// src/App.js — VerifyTrack BGV Portal with Firebase

import React, {
  useState, useEffect, useRef, useCallback,
  createContext, useContext
} from "react";
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie,
  Cell, XAxis, YAxis, Tooltip, ResponsiveContainer
} from "recharts";
import {
  signIn, signOutUser, onAuthChange, getUserProfile,
  saveUserProfile, getCompanies, getCompany, createCompany,
  updateCompany, toggleCompanyStatus, setCompanyVerified,
  addCompanyCredits, addUserCredits, getAllUsers, createUser,
  resetUserPassword,
  getEmployees, searchByAadhaar, searchByName,
  addEmployee, bulkAddEmployees, getUserSearchHistory,
  getAllSearchHistory, addSearchHistory, uploadCompanyLogo,
  uploadBulkCSV, triggerLowCreditAlert, subscribeToUser,
  subscribeToCompanies, subscribeToEmployees, subscribeToHistory, subscribeToAllHistory,
  sendNotification,
  getWallet, getAllWallets, topUpWallet, deductWallet, recordFailedTopup,
  deductHRWallet, getHRAllocation, getCompanyHRAllocations,
  setHRAllocation, reverseHRAllocation,
  getCompanyTransactions, getAllTransactions, manualWalletAdjust,
  uploadResume, saveEmployeeFileResume, getEmployeeFile,
  uploadOnboardingDoc, saveEmployeeFileDocuments,
  getCompanyEmployeeFiles, getAllEmployeeFiles,
  uploadAddressSelfie, uploadRefDoc, createNotification
} from "./firebase/services";

import { collection, doc, getDoc, getDocs, addDoc, updateDoc, setDoc, deleteDoc, onSnapshot, query, where, orderBy, arrayUnion, increment, serverTimestamp, runTransaction } from "firebase/firestore";
import EmployeeApp from './Employee';
import PublicProfile from './PublicProfile';
import DemoPage from './Demo';
import { db, auth } from "./firebase/config";
// Guard Firebase usage on public form pages
const _isPublicPage = /\/refcheck\//.test(window.location.pathname);

/* ══ INLINE SUPPORT SERVICES ══ */
const createSupportTicket = async (data) => {
  const ref = await addDoc(collection(db, "supportTickets"), {
    ...data, status:"open", createdAt:serverTimestamp(), updatedAt:serverTimestamp(),
  });
  createNotification({
    companyId:null,targetRole:"superadmin",type:"support_ticket",
    title:"New support ticket raised",
    message:`${data.contactName||data.userRole||"A user"} raised: ${data.subject||"New ticket"}`,
    link:"support",
  });
  return ref.id;
};
const addTicketMessage = async (ticketId, message) => {
  const ticketRef = doc(db, "supportTickets", ticketId);
  const snap = await getDoc(ticketRef);
  const existing = snap.data()?.messages || [];
  await updateDoc(ticketRef, {
    messages:[...existing, {...message, timestamp:new Date().toISOString()}],
    updatedAt:serverTimestamp(),
    status:message.role==="admin"?"replied":"open",
  });
  if(message.role==="admin"){
    // Support replied — notify the company that raised the ticket
    createNotification({
      companyId:snap.data()?.companyId||null,targetRole:"all",type:"support_reply",
      title:"Support replied to your ticket",
      message:message.text?.slice(0,120)||"New reply from Meitho Support",
      link:"support",
    });
  }else if(message.role==="user"){
    // Company added a follow-up — notify Super Admin
    createNotification({
      companyId:null,targetRole:"superadmin",type:"support_reply",
      title:"New reply on a support ticket",
      message:(message.sender||"A user")+": "+(message.text?.slice(0,100)||""),
      link:"support",
    });
  }
};
const updateTicketStatus = async (ticketId, status) => {
  await updateDoc(doc(db, "supportTickets", ticketId), {status, updatedAt:serverTimestamp()});
};
const subscribeToAllTickets = (callback) =>
  onSnapshot(collection(db,"supportTickets"), (snap)=>{
    const all=snap.docs.map(d=>({id:d.id,...d.data()}));
    all.sort((a,b)=>(b.createdAt?.toDate?.()?.getTime()||0)-(a.createdAt?.toDate?.()?.getTime()||0));
    callback(all);
  });
const subscribeToUserTickets = (companyId, callback) =>
  onSnapshot(collection(db,"supportTickets"), (snap)=>{
    const all=snap.docs.map(d=>({id:d.id,...d.data()}));
    const filtered=all.filter(t=>
      t.companyId===companyId||
      t.userId===companyId||
      t.companyId===companyId||
      (companyId&&t.companyId&&t.companyId.toLowerCase()===companyId.toLowerCase())
    );
    filtered.sort((a,b)=>(b.createdAt?.toDate?.()?.getTime()||0)-(a.createdAt?.toDate?.()?.getTime()||0));
    callback(filtered);
  });

/* ══════════════════════════════════════════════════════════════
   THEME + INJECT CSS
══════════════════════════════════════════════════════════════ */
const ThemeCtx = createContext();
const useTheme = () => useContext(ThemeCtx);

(() => {
  if (document.getElementById("bgv-css")) return;
  const l = document.createElement("link");
  l.rel = "stylesheet";
  l.href = "https://fonts.googleapis.com/css2?family=Sora:wght@400;500;600;700;800;900&family=Inter:wght@300;400;500;600;700&family=JetBrains+Mono:wght@400;500;600&display=swap";
  document.head.appendChild(l);
  const s = document.createElement("style");
  s.id = "bgv-css";
  s.textContent = `
    .dark{--bg:#06080a;--s1:#0a0f12;--s2:#0f161a;--b1:#1a2730;--b2:#213240;--text:#f0f8fa;--muted:#4a7a85;--sub:#7ab8c4;--acc:#0cc0df;--acc2:#0dd4f5;--glow:#0cc0df22;--green:#ff3131;--red:#ff3131;--amber:#ff914d;--purple:#0cc0df;--shadow:0 4px 32px #00000088;--ibg:#0a0f12;--border-r:8px;--brand:#0cc0df;--btn-grad:linear-gradient(90deg,#ff3131,#ff914d);}
    .light{--bg:#f5feff;--s1:#ffffff;--s2:#e8fafb;--b1:#c8eef3;--b2:#a8dde5;--text:#020d0f;--muted:#4a8090;--sub:#2d6570;--acc:#0cc0df;--acc2:#09a8c2;--glow:#0cc0df18;--green:#059669;--red:#ff3131;--amber:#ff914d;--purple:#0cc0df;--shadow:0 2px 20px #0cc0df18;--ibg:#ffffff;--border-r:8px;--brand:#0cc0df;--btn-grad:linear-gradient(90deg,#ff3131,#ff914d);}
    *{box-sizing:border-box;margin:0;padding:0;}
    body{background:var(--bg);font-family:'Inter',system-ui,sans-serif;font-size:14px;-webkit-font-smoothing:antialiased;}
    ::-webkit-scrollbar{width:4px;height:4px;}
    ::-webkit-scrollbar-track{background:transparent;}
    ::-webkit-scrollbar-thumb{background:var(--b2);border-radius:99px;}
    input,select,textarea{font-family:'Inter',system-ui,sans-serif;}

    /* ── Core animations ── */
    @keyframes fadeUp{from{opacity:0;transform:translateY(20px)}to{opacity:1;transform:translateY(0)}}
    @keyframes fadeDown{from{opacity:0;transform:translateY(-12px)}to{opacity:1;transform:translateY(0)}}
    @keyframes fadeIn{from{opacity:0}to{opacity:1}}
    @keyframes fadeLeft{from{opacity:0;transform:translateX(20px)}to{opacity:1;transform:translateX(0)}}
    @keyframes scaleIn{from{opacity:0;transform:scale(.94)}to{opacity:1;transform:scale(1)}}
    @keyframes spin{to{transform:rotate(360deg)}}
    @keyframes pulse{0%,100%{opacity:1}50%{opacity:.5}}
    @keyframes slideRight{from{transform:translateX(-100%)}to{transform:translateX(0)}}
    @keyframes shimmer{0%{background-position:-200% 0}100%{background-position:200% 0}}
    @keyframes bounceIn{0%{opacity:0;transform:scale(.6)}60%{transform:scale(1.08)}80%{transform:scale(.97)}100%{opacity:1;transform:scale(1)}}
    @keyframes countUp{from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:translateY(0)}}
    @keyframes glow{0%,100%{box-shadow:0 0 0 0 var(--glow)}50%{box-shadow:0 0 0 6px var(--glow)}}
    @keyframes progressFill{from{width:0}to{width:var(--pct)}}

    /* ── Animation classes ── */
    .fu{animation:fadeUp .35s cubic-bezier(.22,.68,0,1.2) both}
    .fu1{animation:fadeUp .35s .07s cubic-bezier(.22,.68,0,1.2) both}
    .fu2{animation:fadeUp .35s .14s cubic-bezier(.22,.68,0,1.2) both}
    .fu3{animation:fadeUp .35s .21s cubic-bezier(.22,.68,0,1.2) both}
    .fu4{animation:fadeUp .35s .28s cubic-bezier(.22,.68,0,1.2) both}
    .fi{animation:fadeIn .25s ease both}
    .si{animation:scaleIn .25s cubic-bezier(.22,.68,0,1.2) both}
    .bi{animation:bounceIn .4s cubic-bezier(.22,.68,0,1.2) both}
    .fl{animation:fadeLeft .3s cubic-bezier(.22,.68,0,1.2) both}

    /* ── Card hover effects ── */
    .card-hover{transition:transform .18s ease,box-shadow .18s ease,border-color .18s ease;}
    .card-hover:hover{transform:translateY(-2px);box-shadow:0 8px 32px #00000022;}
    .card-hover-acc:hover{border-color:var(--brand)!important;}

    /* ── Sidebar nav button ── */
    .nav-btn{transition:background .15s,color .15s,transform .1s;}
    .nav-btn:hover{transform:translateX(2px);}
    .nav-btn:active{transform:translateX(0) scale(.97);}

    /* ── Button press ── */
    .btn-press:active{transform:scale(.96)!important;}

    /* ── Skeleton shimmer ── */
    .skeleton{background:linear-gradient(90deg,var(--s2) 25%,var(--b1) 50%,var(--s2) 75%);background-size:200% 100%;animation:shimmer 1.4s infinite;}

    /* ── Badge pop ── */
    .badge-pop{animation:bounceIn .3s cubic-bezier(.22,.68,0,1.2) both;}

    /* ── Page content ── */
    .page-enter{animation:fadeUp .3s cubic-bezier(.22,.68,0,1.2) both;}

    /* ── Expand panel ── */
    .expand-panel{animation:fadeDown .25s cubic-bezier(.22,.68,0,1.2) both;}

    /* ── Stat counter ── */
    .stat-num{animation:countUp .4s cubic-bezier(.22,.68,0,1.2) both;}

    /* ── Glow ping for alerts ── */
    .glow-pulse{animation:glow 2s ease-in-out infinite;}

    /* ── Progress bar ── */
    .progress-fill{animation:progressFill .8s .2s cubic-bezier(.22,.68,0,1.2) both;}

    /* ── Input focus ── */
    input:focus,select:focus{transform:none;transition:border-color .15s,box-shadow .15s;}
    input:focus{box-shadow:0 0 0 3px rgba(12,192,223,0.15);}

    /* ── Tooltip ── */
    [data-tip]{position:relative;}
    [data-tip]:hover::after{content:attr(data-tip);position:absolute;bottom:calc(100% + 6px);left:50%;transform:translateX(-50%);background:#06080a;color:#f0f8fa;font-size:11px;font-weight:500;padding:4px 8px;border-radius:5px;white-space:nowrap;pointer-events:none;animation:fadeIn .15s ease;z-index:999;font-family:'Inter',system-ui,sans-serif;}

    .show-sm{display:none!important}
    @media(max-width:768px){.hide-sm{display:none!important}.show-sm{display:flex!important}.mg1{grid-template-columns:1fr!important}.mg2{grid-template-columns:1fr 1fr!important}.main-content{padding:72px 14px 20px!important}}

    /* ── Mesh gradient backgrounds ── */
    .bg-mesh-dark{background:radial-gradient(ellipse at 20% 20%,#0a1f25 0%,transparent 55%),radial-gradient(ellipse at 80% 80%,#0cc0df08 0%,transparent 55%),radial-gradient(ellipse at 50% 10%,#0cc0df05 0%,transparent 40%),#06080a;}
    .bg-mesh-light{background:radial-gradient(ellipse at 20% 20%,#cdf4f9 0%,transparent 55%),radial-gradient(ellipse at 80% 80%,#e0f9fc 0%,transparent 55%),radial-gradient(ellipse at 50% 50%,#f5feff 0%,transparent 100%);}

    /* ── Gradient cards ── */
    .card-gradient-blue{background:linear-gradient(135deg,#0a2030 0%,#071820 100%)!important;border-color:#0cc0df20!important;}
    .card-gradient-purple{background:linear-gradient(135deg,#0a2530 0%,#061820 100%)!important;border-color:#0cc0df20!important;}
    .card-gradient-green{background:linear-gradient(135deg,#042e22 0%,#021a14 100%)!important;border-color:#ff313120!important;}
    .card-gradient-amber{background:linear-gradient(135deg,#2a1000 0%,#1a0800 100%)!important;border-color:#ff914d20!important;}
    .light .card-gradient-blue{background:linear-gradient(135deg,#cef6fb 0%,#a8edf5 100%)!important;}
    .light .card-gradient-purple{background:linear-gradient(135deg,#d8f7fc 0%,#b8eef5 100%)!important;}
    .light .card-gradient-green{background:linear-gradient(135deg,#d1fae5 0%,#a7f3d0 100%)!important;}
    .light .card-gradient-amber{background:linear-gradient(135deg,#fff0e8 0%,#ffe0cc 100%)!important;}

    /* ── Glassmorphism ── */
    .glass{backdrop-filter:blur(12px);-webkit-backdrop-filter:blur(12px);}
    .dark .glass{background:rgba(10,15,18,0.75)!important;border:1px solid rgba(12,192,223,0.12)!important;}
    .light .glass{background:rgba(255,255,255,0.82)!important;border:1px solid rgba(12,192,223,0.18)!important;}

    /* ── Neon glow on hover ── */
    .neon-hover{transition:all .2s ease;}
    .dark .neon-hover:hover{box-shadow:0 0 24px rgba(12,192,223,0.2),0 4px 24px rgba(0,0,0,0.5)!important;border-color:rgba(12,192,223,0.25)!important;}
    .light .neon-hover:hover{box-shadow:0 0 20px rgba(12,192,223,0.18),0 4px 20px rgba(12,192,223,0.08)!important;border-color:rgba(12,192,223,0.3)!important;}

    /* ── Stat card ── */
    .stat-card{position:relative;overflow:hidden;}
    .stat-card::before{content:'';position:absolute;top:-30px;right:-30px;width:80px;height:80px;border-radius:50%;opacity:.08;background:currentColor;}

    /* ── Quota bar ── */
    @keyframes quotaFill{from{width:0}to{width:var(--qw)}}
    .quota-fill{animation:quotaFill .9s .3s cubic-bezier(.22,.68,0,1.2) both;}

    /* ── Float animation ── */
    @keyframes float{0%,100%{transform:translateY(0)}50%{transform:translateY(-6px)}}
    .float{animation:float 3s ease-in-out infinite;}

    /* ── Ripple ── */
    @keyframes ripple{0%{transform:scale(0);opacity:.4}100%{transform:scale(2.5);opacity:0}}

    /* ── Number tick ── */
    @keyframes tick{from{transform:translateY(-100%);opacity:0}to{transform:translateY(0);opacity:1}}
    .tick{animation:tick .3s cubic-bezier(.22,.68,0,1.2) both;}

    /* ── Border glow ── */
    .border-glow-acc{box-shadow:0 0 0 1px var(--acc),0 0 12px var(--glow);}
    .border-glow-green{box-shadow:0 0 0 1px var(--green),0 0 12px rgba(255,49,49,0.15);}

    /* ── Animated gradient text ── */
    @keyframes gradShift{0%{background-position:0% 50%}50%{background-position:100% 50%}100%{background-position:0% 50%}}
    .grad-text{background:linear-gradient(90deg,#ff3131,#ff914d,#0cc0df,#ff3131);background-size:300%;-webkit-background-clip:text;-webkit-text-fill-color:transparent;background-clip:text;animation:gradShift 6s ease infinite;}

    /* ── Dot ping ── */
    @keyframes ping{0%{transform:scale(1);opacity:1}75%,100%{transform:scale(2);opacity:0}}
    .ping{animation:ping 1.5s cubic-bezier(0,0,.2,1) infinite;}

    /* ══════════════════════════════════════════════════════════
       GLASS MORPHISM SYSTEM
    ══════════════════════════════════════════════════════════════ */
    @keyframes glassShimmer{0%{background-position:-200% 0}100%{background-position:200% 0}}
    @keyframes glassFloat{0%,100%{transform:translateY(0)}50%{transform:translateY(-8px)}}
    @keyframes glassPulse{0%,100%{opacity:1}50%{opacity:.6}}
    @keyframes gradientBorder{0%{background-position:0% 50%}50%{background-position:100% 50%}100%{background-position:0% 50%}}
    @keyframes glassIn{from{opacity:0;transform:scale(.95) translateY(10px)}to{opacity:1;transform:scale(1) translateY(0)}}

    .glass{background:rgba(255,255,255,0.06);backdrop-filter:blur(20px) saturate(180%);-webkit-backdrop-filter:blur(20px) saturate(180%);border:1px solid rgba(255,255,255,0.10);}
    .glass-dark{background:rgba(5,10,20,0.55);backdrop-filter:blur(24px) saturate(180%);-webkit-backdrop-filter:blur(24px) saturate(180%);border:1px solid rgba(255,255,255,0.08);}
    .glass-card{background:rgba(255,255,255,0.045)!important;backdrop-filter:blur(18px) saturate(160%)!important;-webkit-backdrop-filter:blur(18px) saturate(160%)!important;border:1px solid rgba(255,255,255,0.09)!important;box-shadow:0 8px 32px rgba(0,0,0,0.28),inset 0 1px 0 rgba(255,255,255,0.06)!important;transition:transform .25s cubic-bezier(.22,.68,0,1.2),box-shadow .25s ease,border-color .25s ease;}
    .glass-card:hover{transform:translateY(-3px);border-color:rgba(255,49,49,0.35)!important;box-shadow:0 16px 48px rgba(0,0,0,0.4),0 0 0 1px rgba(255,49,49,0.12),inset 0 1px 0 rgba(255,255,255,0.08)!important;}

    .btn-glass{background:rgba(255,255,255,0.06);backdrop-filter:blur(14px) saturate(160%);-webkit-backdrop-filter:blur(14px) saturate(160%);border:1px solid rgba(255,255,255,0.14);border-radius:10px;color:var(--text,#fff);padding:10px 20px;font-weight:600;font-size:14px;cursor:pointer;font-family:inherit;transition:all .2s cubic-bezier(.34,1.56,.64,1);display:inline-flex;align-items:center;gap:6px;}
    .btn-glass:hover{background:rgba(255,255,255,0.11);border-color:rgba(255,255,255,0.24);transform:translateY(-2px);box-shadow:0 8px 24px rgba(0,0,0,0.25);}
    .btn-glass:active{transform:translateY(0) scale(.97);}

    .btn-glass-red{background:rgba(255,49,49,0.12);backdrop-filter:blur(14px) saturate(160%);-webkit-backdrop-filter:blur(14px) saturate(160%);border:1px solid rgba(255,49,49,0.28);border-radius:10px;color:#ff6b6b;padding:10px 20px;font-weight:700;font-size:14px;cursor:pointer;font-family:inherit;transition:all .2s cubic-bezier(.34,1.56,.64,1);display:inline-flex;align-items:center;gap:6px;}
    .btn-glass-red:hover{background:rgba(255,49,49,0.2);border-color:rgba(255,49,49,0.5);transform:translateY(-2px);box-shadow:0 8px 28px rgba(255,49,49,0.25);}
    .btn-glass-red:active{transform:translateY(0) scale(.97);}

    .btn-gradient{background:linear-gradient(90deg,#ff3131,#ff914d);backdrop-filter:blur(6px);border:none;border-radius:10px;color:#fff;padding:10px 22px;font-weight:700;font-size:14px;cursor:pointer;font-family:inherit;transition:all .2s cubic-bezier(.34,1.56,.64,1);display:inline-flex;align-items:center;gap:6px;box-shadow:0 6px 20px rgba(255,49,49,0.35),inset 0 1px 0 rgba(255,255,255,0.25);white-space:nowrap;}
    .btn-gradient:hover{box-shadow:0 10px 32px rgba(255,49,49,0.55),inset 0 1px 0 rgba(255,255,255,0.3);transform:translateY(-2px) scale(1.02);}
    .btn-gradient:active{transform:translateY(0) scale(.97);}

    .glass-overlay{position:fixed;inset:0;background:rgba(2,4,8,0.65);backdrop-filter:blur(6px);-webkit-backdrop-filter:blur(6px);z-index:1000;display:flex;align-items:center;justify-content:center;padding:16px;animation:glassIn .2s ease both;}
    .glass-modal{background:rgba(12,18,28,0.72);backdrop-filter:blur(28px) saturate(180%);-webkit-backdrop-filter:blur(28px) saturate(180%);border:1px solid rgba(255,255,255,0.12);border-radius:18px;box-shadow:0 32px 80px rgba(0,0,0,0.6),inset 0 1px 0 rgba(255,255,255,0.08);animation:glassIn .25s cubic-bezier(.22,.68,0,1.2) both;}
    .light .glass-modal{background:rgba(255,255,255,0.75);border-color:rgba(0,0,0,0.08);box-shadow:0 24px 64px rgba(0,0,0,0.18),inset 0 1px 0 rgba(255,255,255,0.5);}

    .glass-input{width:100%;background:rgba(255,255,255,0.08);backdrop-filter:blur(10px);-webkit-backdrop-filter:blur(10px);border:1.5px solid rgba(255,255,255,0.16);border-radius:8px;padding:10px 13px;color:var(--text,#0a1418);font-size:14px;font-family:inherit;outline:none;transition:border-color .15s,box-shadow .15s,background .15s;}
    .glass-input::placeholder{color:var(--muted,rgba(10,20,24,0.45));}
    .glass-input:focus{border-color:var(--brand,#0cc0df);background:rgba(255,255,255,0.11);box-shadow:0 0 0 3px rgba(12,192,223,0.15);}
    .light .glass-input{background:rgba(0,0,0,0.045);border-color:rgba(0,0,0,0.16);color:#0a1418;}
    .light .glass-input::placeholder{color:rgba(10,20,24,0.45);}

    .glass-nav{background:rgba(5,10,20,0.55)!important;backdrop-filter:blur(24px) saturate(180%)!important;-webkit-backdrop-filter:blur(24px) saturate(180%)!important;border-bottom:1px solid rgba(255,255,255,0.08)!important;}
    .light .glass-nav{background:rgba(255,255,255,0.65)!important;border-bottom-color:rgba(0,0,0,0.06)!important;}

    .sidebar-glass{background:rgba(8,12,20,0.6)!important;backdrop-filter:blur(24px) saturate(180%)!important;-webkit-backdrop-filter:blur(24px) saturate(180%)!important;border-right:1px solid rgba(255,255,255,0.08)!important;}
    .light .sidebar-glass{background:rgba(255,255,255,0.7)!important;border-right-color:rgba(0,0,0,0.06)!important;}

    .gradient-border{position:relative;border-radius:14px;}
    .gradient-border::before{content:'';position:absolute;inset:0;border-radius:inherit;padding:1px;background:linear-gradient(120deg,#ff3131,#ff914d,#0cc0df,#ff3131);background-size:300% 300%;animation:gradientBorder 6s ease infinite;-webkit-mask:linear-gradient(#fff 0 0) content-box,linear-gradient(#fff 0 0);-webkit-mask-composite:xor;mask-composite:exclude;pointer-events:none;}

    .glass-shimmer{position:relative;overflow:hidden;}
    .glass-shimmer::after{content:'';position:absolute;inset:0;background:linear-gradient(110deg,transparent 30%,rgba(255,255,255,0.12) 50%,transparent 70%);background-size:200% 100%;animation:glassShimmer 2.5s linear infinite;}

    /* ── Retrofit existing app-wide surfaces with glass, minimal invasive edits ── */
    .fu.glass-card, .card-hover.glass-card{background:rgba(255,255,255,0.045)!important;}
    table.glass-table, .glass-table table{background:rgba(255,255,255,0.03)!important;backdrop-filter:blur(16px) saturate(150%);}
  `;
  document.head.appendChild(s);
})();

/* ══════════════════════════════════════════════════════════════
   CONSTANTS & HELPERS
══════════════════════════════════════════════════════════════ */
const FF = "'Inter',system-ui,sans-serif";
const FFD = "'Sora',sans-serif";
const FFM = "'JetBrains Mono',monospace";


const REASON_COLOR = {"Better Opportunity":"#4f7cff","Termination":"#f04f6a","Personal Reasons":"#f5a623","Relocation":"#9b7aff","Higher Studies":"#0fba81","Retirement":"#22d3ee","Other":"#4e6a94"};
const PLAN_COLOR = {credits:"#0cc0df",subscription:"#0cc0df",enterprise:"#ff914d"};
const fmtAadhaar = v => {const d=v.replace(/\D/g,"").slice(0,12);if(d.length<=4)return d;if(d.length<=8)return d.slice(0,4)+"-"+d.slice(4);return d.slice(0,4)+"-"+d.slice(4,8)+"-"+d.slice(8);};
const validAadhaar = v => /^\d{4}-\d{4}-\d{4}$/.test(v.trim());
const maskAadhaar = v => {const p=v.split("-");return p.length===3?p[0]+"-****-"+p[2]:v;};
const fmtSalary = v => `₹${parseInt(v||0).toLocaleString("en-IN")}/mo`;
const todayStr = () => new Date().toISOString().split("T")[0];

// Search pricing & wallet config
// Cloud Function URLs for emails
const CF_SEND_CANDIDATE = "https://sendcandidateemail-mtvra6khyq-uc.a.run.app";
const CF_RZP_ORDER = "https://us-central1-meitho.cloudfunctions.net/razorpayCreateOrder";
const CF_RZP_VERIFY = "https://us-central1-meitho.cloudfunctions.net/razorpayVerify";
const CF_SEND_REFERENCE = "https://sendreferenceemail-mtvra6khyq-uc.a.run.app";
const CF_SEND_HR_NOTIF  = "https://sendhrnotification-mtvra6khyq-uc.a.run.app";
const CF_SEND_REMINDER  = "https://sendreminderemail-mtvra6khyq-uc.a.run.app";
const CF_SAVE_REFCHECK  = "https://saverefcheckresponse-mtvra6khyq-uc.a.run.app";

// Helper: send WhatsApp message via wa.me link (opens WhatsApp)
function sendWhatsAppMessage(phone, message) {
  if(!phone) return false;
  // Clean phone number
  let cleaned = phone.replace(/\D/g, '');
  if(cleaned.startsWith('0')) cleaned = '91' + cleaned.slice(1);
  if(!cleaned.startsWith('91') && cleaned.length === 10) cleaned = '91' + cleaned;
  const encoded = encodeURIComponent(message);
  window.open(`https://wa.me/${cleaned}?text=${encoded}`, '_blank');
  return true;
}

// Helper: generate WhatsApp BGV report message
function getBGVWhatsAppMsg(empName, searchType, found, reportId, companyName) {
  const typeLabel = {uan:'Employment History',pan:'PAN Verification',aadhaar:'Aadhaar Verification',dl:'Driving License',ecourt:'Criminal Check',full_bgv:'Full BGV',refcheck:'Reference Check'}[searchType] || searchType;
  return `*Meitho BGV Report*

` +
    `*Candidate:* ${empName || 'Candidate'}
` +
    `*Verification:* ${typeLabel}
` +
    `*Status:* ${found ? '✅ Verified & Found' : '❌ Not Found'}
` +
    `*Report ID:* ${reportId || 'BGV-' + Date.now().toString(36).toUpperCase()}
` +
    `*Verified by:* ${companyName || 'Meitho BGV'}

` +
    `Powered by Meitho BGV · meitho.com`;
}

// Helper: generate WhatsApp reference check message for candidate
function getRefCheckCandidateMsg(candidateName, formLink, companyName) {
  return `*Reference Check Request*

` +
    `Hi ${candidateName || 'there'}! 👋

` +
    `*${companyName || 'A company'}* has initiated a background verification for you.

` +
    `Please fill in your employment details by clicking the link below:
` +
    `${formLink}

` +
    `⏰ Please complete this within *24 hours*

` +
    `This is an automated message from Meitho BGV · meitho.com`;
}

// Helper: generate WhatsApp message for references
function getRefCheckRefMsg(refName, candidateName, formLink, companyName) {
  return `*Reference Verification Request*

` +
    `Hi ${refName || 'there'}! 👋

` +
    `You have been listed as a reference for *${candidateName || 'a candidate'}* who is being verified by *${companyName || 'a company'}*.

` +
    `Please verify their details by clicking:
` +
    `${formLink}

` +
    `This takes only *2 minutes* to complete.

` +
    `Thank you!
Meitho BGV · meitho.com`;
}

// Helper: send email via Cloud Function
async function sendCloudEmail(url, payload) {
  try {
    const r = await fetch(url, {
      method: "POST",
      headers: {"Content-Type":"application/json"},
      body: JSON.stringify(payload)
    });
    const data = await r.json();
    console.info("Email sent:", url.split("/").pop(), data?.message||"ok");
    return data;
  } catch(e) {
    console.error("Email failed:", url, e.message);
    return null;
  }
}

const SEARCH_PRICES={uan:100,pan:50,aadhaar:50,dl:50,ecourt:75,full_bgv:450,refcheck:125,education:75,criminal:75,address_verify:175};
// Distance in meters between two lat/lng points (Haversine formula) —
// used to check whether a candidate's captured GPS selfie location is
// close enough to the address HR entered for address verification.
const distanceMeters=(lat1,lng1,lat2,lng2)=>{
  const R=6371000;
  const toRad=d=>d*Math.PI/180;
  const dLat=toRad(lat2-lat1), dLng=toRad(lng2-lng1);
  const a=Math.sin(dLat/2)**2+Math.cos(toRad(lat1))*Math.cos(toRad(lat2))*Math.sin(dLng/2)**2;
  return R*2*Math.atan2(Math.sqrt(a),Math.sqrt(1-a));
};
// Free geocoding via OpenStreetMap Nominatim — no API key needed. Can be
// swapped for Google Maps Geocoding later for better Indian-address
// accuracy; only this one function would need to change.
const geocodeAddress=async(address)=>{
  const res=await fetch(`https://nominatim.openstreetmap.org/search?format=json&limit=1&q=${encodeURIComponent(address)}`,{
    headers:{"Accept-Language":"en"},signal:AbortSignal.timeout?AbortSignal.timeout(8000):undefined,
  });
  const data=await res.json();
  if(!data||data.length===0)return null;
  return {lat:parseFloat(data[0].lat),lng:parseFloat(data[0].lon)};
};
// Reverse geocode a captured GPS point to find its postal code — more
// reliable than forward-geocoding a free-text address string, and used as
// a secondary match check alongside the raw distance calculation.
const reverseGeocodePincode=async(lat,lng)=>{
  try{
    const res=await fetch(`https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&zoom=18&addressdetails=1`,{
      headers:{"Accept-Language":"en"},signal:AbortSignal.timeout?AbortSignal.timeout(8000):undefined,
    });
    const data=await res.json();
    return data?.address?.postcode||null;
  }catch(e){return null;}
};
// Extract a 6-digit Indian PIN code from a free-text address string
// Get GPS with a hard timeout. Some phones (and in-app browsers such as the
// WhatsApp browser) never call back, so we race a timer and retry once with
// low accuracy before giving up with a clear message.
const getPositionSafe=()=>new Promise((resolve,reject)=>{
  if(!navigator.geolocation){reject(Object.assign(new Error("Geolocation not supported"),{code:0}));return;}
  let done=false;
  const fin=(fn,v)=>{if(done)return;done=true;clearTimeout(hard);fn(v);};
  const hard=setTimeout(()=>fin(reject,Object.assign(new Error("Location timed out"),{code:3})),30000);
  navigator.geolocation.getCurrentPosition(p=>fin(resolve,p),()=>{
    navigator.geolocation.getCurrentPosition(p=>fin(resolve,p),e=>fin(reject,e),{enableHighAccuracy:false,timeout:12000,maximumAge:300000});
  },{enableHighAccuracy:true,timeout:10000,maximumAge:60000});
});
const IN_STATES=["Andaman and Nicobar Islands","Andhra Pradesh","Arunachal Pradesh","Assam","Bihar","Chandigarh","Chhattisgarh","Dadra and Nagar Haveli and Daman and Diu","Delhi","Goa","Gujarat","Haryana","Himachal Pradesh","Jammu and Kashmir","Jharkhand","Karnataka","Kerala","Ladakh","Lakshadweep","Madhya Pradesh","Maharashtra","Manipur","Meghalaya","Mizoram","Nagaland","Odisha","Puducherry","Punjab","Rajasthan","Sikkim","Tamil Nadu","Telangana","Tripura","Uttar Pradesh","Uttarakhand","West Bengal"];
const cvComposeAddr=(p)=>[p?.line1,p?.line2,p?.city,p?.state,p?.pin].map(x=>String(x||"").trim()).filter(Boolean).join(", ");
const cvNormPlace=(v)=>String(v||"").normalize("NFD").replace(/[\u0300-\u036f]/g,"").toLowerCase().replace(/&/g,"and").replace(/[^a-z]+/g,"");
const cvCopy=async(t)=>{
  try{await navigator.clipboard.writeText(t);return true;}
  catch(e){
    try{const ta=document.createElement("textarea");ta.value=t;ta.style.position="fixed";ta.style.opacity="0";document.body.appendChild(ta);ta.focus();ta.select();const ok=document.execCommand("copy");document.body.removeChild(ta);return ok;}
    catch(e2){return false;}
  }
};
// Reverse-geocode a GPS point to its PIN code, state and city
const reverseGeocodeFull=async(lat,lng)=>{
  try{
    const res=await fetch(`https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&zoom=18&addressdetails=1`,{headers:{"Accept-Language":"en"},signal:AbortSignal.timeout?AbortSignal.timeout(8000):undefined});
    const d=await res.json();const a=d?.address||{};
    return {pincode:a.postcode?String(a.postcode).replace(/\D/g,"").slice(0,6):null,state:a.state||a.state_district||"",city:a.city||a.town||a.village||a.county||a.suburb||""};
  }catch(e){return {pincode:null,state:"",city:""};}
};
const extractPincode=(address)=>{
  const match=(address||"").match(/\b(\d{6})\b/);
  return match?match[1]:null;
};
// Returns a company's custom price for a search type, falling back to the platform default.
// company.pricing is an optional map set by super admin per company, e.g. {uan:80,pan:40}
const getCompanyPrice=(company,type)=>{
  const custom=company?.pricing?.[type];
  return (custom!==undefined&&custom!==null&&custom!=="")?Number(custom):(SEARCH_PRICES[type]||0);
};
// Cost of a past search: what was actually charged (stored on the record),
// else the company's own price, else the platform default.
const histCost=(h,companies)=>{
  if(h&&h.cost!==undefined&&h.cost!==null&&h.cost!==""&&!isNaN(Number(h.cost)))return Number(h.cost);
  const co=(companies||[]).find(c=>c.id===h?.companyId);
  return getCompanyPrice(co,h?.searchType);
};
// Verification Cloud Functions (uanVerify, panVerify, etc.) require a valid
// Firebase Auth token — this attaches it so the server can verify the caller
// and apply per-user rate limiting. Never call those endpoints without this.
// Wallet charges and refunds run on the server — the browser cannot change a balance.
const CF_WALLET_CHARGE="https://us-central1-meitho.cloudfunctions.net/walletCharge";
const CF_WALLET_REFUND="https://us-central1-meitho.cloudfunctions.net/walletRefund";
const walletCall=async(url,body)=>{
  const r=await fetch(url,{method:"POST",headers:await getAuthedHeaders(),body:JSON.stringify(body)});
  let d={};try{d=await r.json();}catch(e){}
  if(!r.ok)throw new Error(d.error||"Wallet request failed ("+r.status+")");
  return d;
};
// items: [{type,qty}] — price is decided by the server. Returns {charged,cost,txnId,before,after}.
const walletChargeSrv=(items,description,searchType)=>walletCall(CF_WALLET_CHARGE,{items,description,searchType});
const walletRefundSrv=(txnId)=>walletCall(CF_WALLET_REFUND,{txnId});
const getAuthedHeaders=async()=>{
  const headers={"Content-Type":"application/json"};
  try{
    const token=await auth.currentUser?.getIdToken();
    if(token)headers["Authorization"]="Bearer "+token;
  }catch(e){console.warn("Could not attach auth token to request:",e);}
  return headers;
};
// Per-module search + report access. Company Admins and Super Admins always
// have full access. Plain HR staff start with NO access at all — Company
// Admin must explicitly grant each module (see PermissionsModal). Modules:
// uan, pan, aadhaar, dl, ecourt (criminal), reports.
const SEARCH_MODULES=[
  {key:"uan",label:"UAN / Employment"},
  {key:"pan",label:"PAN Verification"},
  {key:"aadhaar",label:"Aadhaar Verification"},
  {key:"dl",label:"Driving License"},
  {key:"ecourt",label:"Criminal Check"},
  {key:"address",label:"Address Verification"},
];
const hasModuleAccess=(user,isSuperAdmin,moduleKey)=>{
  const isCompanyAdmin=user?.role==="admin"||user?.companyAdmin===true;
  if(isSuperAdmin||isCompanyAdmin)return true;
  return user?.searchPermissions?.[moduleKey]===true;
};
// Fires low-wallet notifications after a deduction — company-level warning
// under ₹500, and a separate Super Admin "critical" alert under ₹100. Only
// fires when CROSSING the threshold (was above, now below) so it doesn't
// spam a notification on every single search once already low.
const checkLowWalletAndNotify=(companyId,companyName,currentBal,newBal)=>{
  if(newBal<500&&currentBal>=500){
    createNotification({
      companyId,targetRole:"all",type:"low_wallet",
      title:"Wallet balance running low",
      message:`${companyName||"Your company"}'s wallet is down to ₹${newBal}. Top up to avoid interrupted searches.`,
      link:"wallet",
    });
  }
  if(newBal<100&&currentBal>=100){
    createNotification({
      companyId:null,targetRole:"superadmin",type:"low_wallet",
      title:"Company wallet critically low",
      message:`${companyName||"A company"}'s wallet has dropped to ₹${newBal}.`,
      link:"companies",
    });
  }
};
const LOGO1="data:image/png;base64,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";

const PLATFORM_FEE_PCT=10;
const MIN_WALLET_LOAD=500;
const GST_PCT=18; // IGST on wallet recharges
const gstOf=(amt)=>Math.round((Number(amt)||0)*GST_PCT)/100;
const money2=(n)=>"₹"+(Number(n)||0).toLocaleString("en-IN",{minimumFractionDigits:2,maximumFractionDigits:2});
const isRechargeTxn=(t)=>!!t&&(t.type==="topup"||t.type==="topup_failed"||(t.type==="manual"&&(t.amount||0)>0));
// Normalised view of a recharge record (new GST-based, or legacy with platform fee / no tax)
const rechargeParts=(t)=>{
  const amt=Math.abs(Number(t.amount)||0);
  const failed=t.type==="topup_failed"||t.status==="failed";
  const hasGst=t.gstAmount!==undefined&&t.gstAmount!==null;
  const gst=hasGst?Number(t.gstAmount)||0:0;
  const fee=(!hasGst&&Number(t.platformFee)>0)?Number(t.platformFee):0;
  return {amt,failed,gst,fee,hasGst,total:failed?0:amt+gst+fee};
};

/* ══════════════════════════════════════════════════════════════
   UI PRIMITIVES
══════════════════════════════════════════════════════════════ */
const Card = ({children,style={},className=""}) => <div className={`card-hover glass-card ${className}`} style={{borderRadius:12,padding:20,...style}}>{children}</div>;
const Inp = ({style={},err,id,name,placeholder,className="",...props}) => {
  const fieldId = id || name || (placeholder ? placeholder.toLowerCase().replace(/[^a-z0-9]/g,"_").slice(0,30) : "field_"+Math.random().toString(36).slice(2,7));
  return <input id={fieldId} name={fieldId} placeholder={placeholder} className={`glass-input ${className}`} style={{borderColor:err?"var(--red)":undefined,...style}} onFocus={e=>{if(!err){e.target.style.borderColor="var(--brand)";e.target.style.boxShadow="0 0 0 3px rgba(12,192,223,0.15)";}}} onBlur={e=>{if(!err){e.target.style.borderColor="rgba(255,255,255,0.12)";e.target.style.boxShadow="none";}}} {...props}/>;
};
const Sel = ({children,style={},...props}) => <select style={{width:"100%",background:"var(--ibg)",border:"1.5px solid var(--b1)",borderRadius:8,padding:"10px 13px",color:"var(--text)",fontSize:14,fontFamily:FF,outline:"none",cursor:"pointer",transition:"border-color .15s",...style}} onFocus={e=>e.target.style.borderColor="var(--brand)"} onBlur={e=>e.target.style.borderColor="var(--b1)"} {...props}>{children}</select>;
const Btn = ({children,style={},className="",...props}) => <button className={`btn-press btn-gradient ${className}`} style={{fontFamily:FF,...style}} {...props}>{children}</button>;
const BtnGhost = ({children,style={},className="",...props}) => <button className={`btn-press btn-glass ${className}`} style={{fontFamily:FF,...style}} {...props}>{children}</button>;
const Badge = ({children,color="var(--brand)",style={}}) => <span style={{display:"inline-flex",alignItems:"center",gap:4,background:color+"18",color,border:"1px solid "+color+"35",borderRadius:5,padding:"2px 8px",fontSize:11,fontWeight:600,fontFamily:FF,transition:"transform .15s",...style}}>{children}</span>;
const Label = ({children}) => <div style={{fontSize:11,fontWeight:600,color:"var(--muted)",letterSpacing:.6,textTransform:"uppercase",marginBottom:5}}>{children}</div>;
const ErrMsg = ({msg}) => msg?<div className="fi" style={{fontSize:11,color:"var(--red)",marginTop:4,fontWeight:500,display:"flex",alignItems:"center",gap:4}}>⚠ {msg}</div>:null;
const Spin = () => <div style={{width:15,height:15,border:"2px solid rgba(255,255,255,.3)",borderTop:"2px solid #fff",borderRadius:"50%",animation:"spin .7s linear infinite",display:"inline-block"}}/>;
const Divider = () => <div style={{height:1,background:"var(--b1)",margin:"14px 0"}}/>;

/* ══════════════════════════════════════════════════════════════
   RESUME UPLOAD — shared by ATS application form, reference check
   form, and HR manual upload from candidate/employee profile.
   Handles the actual file -> Firebase Storage -> download URL flow.
══════════════════════════════════════════════════════════════ */
function ResumeUploadField({companyId,candidateId,value,fileName,onUploaded,dark=true,label="Resume"}){
  const [uploading,setUploading]=useState(false);
  const [progress,setProgress]=useState(0);
  const [err,setErr]=useState("");
  const inputRef=React.useRef(null);

  const pick=()=>inputRef.current?.click();

  const handleFile=async(e)=>{
    const file=e.target.files?.[0];
    if(!file)return;
    setErr("");
    if(file.size>10*1024*1024){setErr("File too large — max 10MB");return;}
    const okTypes=[".pdf",".doc",".docx"];
    const lower=file.name.toLowerCase();
    if(!okTypes.some(ext=>lower.endsWith(ext))){setErr("Please upload a PDF or Word document");return;}
    setUploading(true);setProgress(0);
    try{
      const result=await uploadResume(companyId,candidateId,file,pct=>setProgress(pct));
      onUploaded(result);
    }catch(e2){setErr("Upload failed — please try again");}
    finally{setUploading(false);}
  };

  const textColor=dark?"rgba(255,255,255,0.85)":"var(--text)";
  const mutedColor=dark?"rgba(255,255,255,0.4)":"var(--muted)";
  const borderColor=dark?"rgba(255,255,255,0.12)":"var(--b1)";
  const bg=dark?"rgba(255,255,255,0.04)":"var(--s2)";
  const brand="#0cc0df";

  return(
    <div>
      <label style={{fontSize:11,fontWeight:600,color:mutedColor,display:"block",marginBottom:4,textTransform:"uppercase"}}>{label}</label>
      <input ref={inputRef} type="file" accept=".pdf,.doc,.docx" onChange={handleFile} style={{display:"none"}}/>
      {value?(
        <div style={{display:"flex",alignItems:"center",gap:10,padding:"10px 12px",borderRadius:8,border:`1px solid ${borderColor}`,background:bg}}>
          <div style={{fontSize:18}}>📄</div>
          <div style={{flex:1,minWidth:0}}>
            <div style={{fontSize:12,fontWeight:600,color:textColor,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{fileName||"Resume uploaded"}</div>
            <a href={value} target="_blank" rel="noopener noreferrer" style={{fontSize:11,color:brand,textDecoration:"none"}}>View / Download →</a>
          </div>
          <button type="button" onClick={pick} disabled={uploading} style={{background:"transparent",border:`1px solid ${borderColor}`,borderRadius:6,color:textColor,padding:"5px 10px",cursor:"pointer",fontSize:11,fontFamily:"inherit"}}>{uploading?"...":"Replace"}</button>
        </div>
      ):(
        <button type="button" onClick={pick} disabled={uploading}
          style={{width:"100%",padding:"14px",borderRadius:8,border:`1.5px dashed ${borderColor}`,background:bg,color:mutedColor,cursor:"pointer",fontSize:12,fontFamily:"inherit",textAlign:"center"}}>
          {uploading?`Uploading… ${progress}%`:"📤 Click to upload PDF or Word (max 10MB)"}
        </button>
      )}
      {uploading&&<div style={{height:4,background:borderColor,borderRadius:2,overflow:"hidden",marginTop:6}}><div style={{height:"100%",width:progress+"%",background:brand,transition:"width .2s"}}/></div>}
      {err&&<div style={{fontSize:11,color:"#ff3131",marginTop:4}}>⚠ {err}</div>}
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════
   ONBOARDING DOCUMENT UPLOAD — candidate-facing, per checklist item.
   Tracks per-document status (none/pending/approved/rejected). Once
   approved, upload is locked; rejected documents can be re-uploaded.
══════════════════════════════════════════════════════════════ */
function DocumentUploadField({companyId,candidateId,docKey,label,docData,onUploaded,dark=true}){
  const [uploading,setUploading]=useState(false);
  const [progress,setProgress]=useState(0);
  const [err,setErr]=useState("");
  const inputRef=React.useRef(null);
  const status=docData?.status||"none"; // none | pending | approved | rejected

  const pick=()=>inputRef.current?.click();

  const handleFile=async(e)=>{
    const file=e.target.files?.[0];
    if(!file)return;
    setErr("");
    if(file.size>10*1024*1024){setErr("File too large — max 10MB");return;}
    setUploading(true);setProgress(0);
    try{
      const result=await uploadOnboardingDoc(companyId,candidateId,docKey,file,pct=>setProgress(pct));
      onUploaded(docKey,{...result,status:"pending",reviewedBy:null,reviewedAt:null,rejectReason:null});
    }catch(e2){setErr("Upload failed — please try again");}
    finally{setUploading(false);}
  };

  const textColor=dark?"rgba(255,255,255,0.85)":"var(--text)";
  const mutedColor=dark?"rgba(255,255,255,0.4)":"var(--muted)";
  const borderColor=dark?"rgba(255,255,255,0.12)":"var(--b1)";
  const bg=dark?"rgba(255,255,255,0.04)":"var(--s2)";

  const statusBadge={
    none:{label:"Not uploaded",color:mutedColor,bg:"transparent"},
    pending:{label:"⏳ Pending Review",color:"#f59e0b",bg:"rgba(245,158,11,0.1)"},
    approved:{label:"✅ Approved",color:"#10b981",bg:"rgba(16,185,129,0.1)"},
    rejected:{label:"✕ Rejected",color:"#ef4444",bg:"rgba(239,68,68,0.1)"},
  }[status];

  return(
    <div>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:4}}>
        <label style={{fontSize:11,fontWeight:600,color:mutedColor,textTransform:"uppercase"}}>{label}</label>
        <span style={{fontSize:10,fontWeight:700,color:statusBadge.color,background:statusBadge.bg,padding:"2px 8px",borderRadius:99}}>{statusBadge.label}</span>
      </div>
      <input ref={inputRef} type="file" accept=".pdf,.jpg,.jpeg,.png,.doc,.docx" onChange={handleFile} style={{display:"none"}}/>
      {docData?.url?(
        <div style={{display:"flex",alignItems:"center",gap:10,padding:"10px 12px",borderRadius:8,border:`1px solid ${status==="rejected"?"rgba(239,68,68,0.3)":borderColor}`,background:bg}}>
          <div style={{fontSize:18}}>📄</div>
          <div style={{flex:1,minWidth:0}}>
            <div style={{fontSize:12,fontWeight:600,color:textColor,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{docData.name||"Document uploaded"}</div>
            <a href={docData.url} target="_blank" rel="noopener noreferrer" style={{fontSize:11,color:"#0cc0df",textDecoration:"none"}}>View →</a>
            {status==="rejected"&&docData.rejectReason&&<div style={{fontSize:11,color:"#ef4444",marginTop:2}}>Reason: {docData.rejectReason}</div>}
          </div>
          {status!=="approved"&&<button type="button" onClick={pick} disabled={uploading} style={{background:"transparent",border:`1px solid ${borderColor}`,borderRadius:6,color:textColor,padding:"5px 10px",cursor:"pointer",fontSize:11,fontFamily:"inherit"}}>{uploading?"...":"Replace"}</button>}
        </div>
      ):(
        <button type="button" onClick={pick} disabled={uploading}
          style={{width:"100%",padding:"12px",borderRadius:8,border:`1.5px dashed ${borderColor}`,background:bg,color:mutedColor,cursor:"pointer",fontSize:12,fontFamily:"inherit",textAlign:"center"}}>
          {uploading?`Uploading… ${progress}%`:"📤 Click to upload"}
        </button>
      )}
      {uploading&&<div style={{height:4,background:borderColor,borderRadius:2,overflow:"hidden",marginTop:6}}><div style={{height:"100%",width:progress+"%",background:"#0cc0df",transition:"width .2s"}}/></div>}
      {err&&<div style={{fontSize:11,color:"#ff3131",marginTop:4}}>⚠ {err}</div>}
    </div>
  );
}


function ThemeToggle() {
  const {dark,toggle} = useTheme();
  return (
    <button onClick={toggle} title={dark?"Switch to Light":"Switch to Dark"} style={{width:32,height:32,borderRadius:7,border:"1px solid var(--b1)",background:"var(--s2)",cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",color:"var(--muted)",flexShrink:0,transition:"all .15s"}}
      onMouseEnter={e=>{e.currentTarget.style.borderColor="var(--brand)";e.currentTarget.style.color="var(--brand)";}}
      onMouseLeave={e=>{e.currentTarget.style.borderColor="var(--b1)";e.currentTarget.style.color="var(--muted)";}}>
      {dark?<svg width="14" height="14" viewBox="0 0 14 14" fill="none"><circle cx="7" cy="7" r="3" fill="currentColor"/><path d="M7 1v1M7 12v1M1 7h1M12 7h1M2.93 2.93l.7.7M10.37 10.37l.7.7M10.37 3.63l-.7.7M3.63 10.37l-.7.7" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/></svg>
      :<svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M11 7.5A4.5 4.5 0 016.5 3c0-.28.03-.56.08-.83A4.5 4.5 0 1011 7.5z" fill="currentColor"/></svg>}
    </button>
  );
}

/* ══════════════════════════════════════════════════════════════
   TOAST
══════════════════════════════════════════════════════════════ */
function Toast({toasts,remove}) {
  return <div style={{position:"fixed",bottom:20,right:20,zIndex:9999,display:"flex",flexDirection:"column",gap:6}}>
    {toasts.map(t=><div key={t.id} className="fu" style={{background:"var(--s1)",border:`1px solid var(--b1)`,borderLeft:`3px solid ${t.color}`,borderRadius:8,padding:"11px 14px",display:"flex",gap:10,alignItems:"center",boxShadow:"var(--shadow)",minWidth:260,maxWidth:360}}>
      <span style={{width:22,height:22,borderRadius:5,background:t.color+"18",color:t.color,display:"flex",alignItems:"center",justifyContent:"center",fontWeight:700,fontSize:11,flexShrink:0}}>{t.icon}</span>
      <span style={{fontSize:13,color:"var(--text)",flex:1,fontWeight:500}}>{t.msg}</span>
      <button onClick={()=>remove(t.id)} style={{background:"none",border:"none",color:"var(--muted)",cursor:"pointer",fontSize:16,lineHeight:1,padding:0}}>×</button>
    </div>)}
  </div>;
}

/* ══════════════════════════════════════════════════════════════
   MODAL
══════════════════════════════════════════════════════════════ */
function Modal({title,onClose,children,width=520,dark=false}) {
  return <div className={`glass-overlay ${dark?"dark":"light"}`}>
    <div className="glass-modal" style={{width:"100%",maxWidth:width,maxHeight:"90vh",overflow:"auto"}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"20px 24px",borderBottom:"1px solid rgba(255,255,255,0.08)"}}>
        <h3 style={{fontFamily:FFD,fontSize:18,fontWeight:700,color:"var(--text)"}}>{title}</h3>
        <button onClick={onClose} className="btn-glass" style={{width:30,height:30,padding:0,justifyContent:"center",fontSize:18}}>×</button>
      </div>
      <div style={{padding:24}}>{children}</div>
    </div>
  </div>;
}

/* ══════════════════════════════════════════════════════════════
   LOGIN PAGE
══════════════════════════════════════════════════════════════ */
/* ══════════════════════════════════════════════════════════════
   DEFAULT LANDING CONTENT (editable from Super Admin)
══════════════════════════════════════════════════════════════ */
const DEFAULT_LANDING = {
  hero:{
    badge:"🚀 India's #1 Aadhaar-Based BGV Platform",
    headline:"Go Beyond\nthe Resume",
    subheadline:"Verify employment history in seconds",
    desc:"Meitho connects companies across India to verify candidate employment history using UAN, Aadhaar & PAN — instantly, accurately, and with full audit trail.",
    cta1:"Get Started Free",cta2:"Watch Demo",
  },
  stats:[
    {value:"50,000+",label:"Employees Verified"},
    {value:"500+",label:"Companies Trust Us"},
    {value:"99.2%",label:"Verification Accuracy"},
    {value:"<2s",label:"Average Response Time"},
  ],
  howItWorks:[
    {step:"01",icon:"📤",title:"Upload Exit Data",desc:"HR teams upload employee exit records and earn search credits automatically."},
    {step:"02",icon:"🔍",title:"Search & Verify",desc:"Search by UAN, Aadhaar or PAN — we check EPFO records and return verified employment history."},
    {step:"03",icon:"📄",title:"Download BGV Report",desc:"Get a professionally formatted verification report instantly for any candidate."},
  ],
  features:[
    {icon:"🏦",title:"UAN Verification",desc:"Direct EPFO integration — verify complete PF contribution history and employment dates instantly.",color:"#0cc0df"},
    {icon:"🪪",title:"Aadhaar Identity Check",desc:"Government-backed identity verification with UIDAI — confirm candidate identity with consent.",color:"#ff3131"},
    {icon:"📋",title:"PAN Verification",desc:"Instant PAN card verification against Income Tax database for complete KYC compliance.",color:"#ff914d"},
    {icon:"⚡",title:"Bulk Verification",desc:"Verify 500 employees in one batch — perfect for large-scale onboarding and BGV campaigns.",color:"#ff3131"},
    {icon:"🔗",title:"HRMS Integration",desc:"Connect Zoho People, Keka, Darwinbox & more — auto-sync employee data for seamless BGV.",color:"#8b5cf6"},
    {icon:"📊",title:"Analytics & Reports",desc:"Full audit trail, search history, company-wise usage reports and BGV certificates.",color:"#0cc0df"},
  ],
  testimonials:[
    {name:"Priya Sharma",role:"Head of HR, TechCorp India",avatar:"PS",text:"Meitho cut our BGV time from 2 weeks to 2 minutes. The EPFO integration is a game changer for our hiring process.",rating:5},
    {name:"Rahul Verma",role:"Talent Acquisition, FinServ Co",avatar:"RV",text:"We onboard 200+ employees monthly. Meitho's bulk verification saved us ₹4 lakhs in third-party BGV costs last quarter.",rating:5},
    {name:"Anitha Krishnan",role:"CHRO, LogisticsPro",avatar:"AK",text:"The employment timeline view is brilliant. We can see a candidate's entire career history across all companies in one screen.",rating:5},
  ],
  footer:{
    tagline:"Go Beyond the Trace",
    desc:"India's most trusted Aadhaar-based background verification platform.",
  },
};

function getLandingContent() {
  try{return {...DEFAULT_LANDING,...JSON.parse(localStorage.getItem("meitho_landing")||"{}")};}
  catch{return DEFAULT_LANDING;}
}

/* ══════════════════════════════════════════════════════════════
   LANDING PAGE
══════════════════════════════════════════════════════════════ */

// ── 3D candidate wave — module-level + memoised so page re-renders (scroll etc.) never rebuild it ──
const LP_PEOPLE=[
  {n:"Rajesh Kumar",r:"Senior Developer",c:"#ff3131",i:"RK",chips:["UAN","PAN","Aadhaar"]},
  {n:"Priya Nair",r:"HR Executive",c:"#8b5cf6",i:"PN",chips:["PAN","Reference","Address"]},
  {n:"Imran Sheikh",r:"Delivery Partner",c:"#0cc0df",i:"IS",chips:["DL","Aadhaar","Criminal"]},
  {n:"Ananya Rao",r:"Accountant",c:"#ff914d",i:"AR",chips:["UAN","PAN","Reference"]},
  {n:"Vikram Singh",r:"Sales Manager",c:"#10b981",i:"VS",chips:["UAN","Criminal","Address"]},
  {n:"Meera Iyer",r:"Data Analyst",c:"#ec4899",i:"MI",chips:["PAN","Aadhaar","UAN"]},
  {n:"Arjun Das",r:"Field Engineer",c:"#f59e0b",i:"AD",chips:["DL","PAN","Address"]},
  {n:"Sana Khan",r:"Product Designer",c:"#14b8a6",i:"SK",chips:["UAN","Reference","PAN"]},
];
const lpRot=(arr,k)=>[...arr.slice(k),...arr.slice(0,k)];
const LpTile=React.memo(function LpTile({p,i,dark}){
  const G="#10b981";
  return(
    <div className="m-tile" style={{"--i":i,background:dark?"linear-gradient(160deg,rgba(255,255,255,0.09),rgba(255,255,255,0.03))":"#fff",border:`1px solid ${dark?"rgba(255,255,255,0.12)":"rgba(15,23,42,0.08)"}`,boxShadow:dark?"0 14px 30px rgba(0,0,0,0.5)":"0 14px 30px rgba(15,23,42,0.12)"}}>
      <div style={{display:"flex",alignItems:"center",gap:11,marginBottom:12}}>
        <div style={{width:38,height:38,borderRadius:"50%",background:`linear-gradient(135deg,${p.c},${p.c}aa)`,color:"#fff",fontWeight:800,fontSize:13,display:"flex",alignItems:"center",justifyContent:"center",fontFamily:"'Sora',sans-serif",flexShrink:0}}>{p.i}</div>
        <div style={{minWidth:0}}>
          <div style={{fontSize:13,fontWeight:700,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis",color:dark?"#f0f4ff":"#0f172a"}}>{p.n}</div>
          <div style={{fontSize:10.5,color:dark?"rgba(255,255,255,0.4)":"rgba(0,0,0,0.4)",whiteSpace:"nowrap"}}>{p.r}</div>
        </div>
      </div>
      <div style={{display:"flex",gap:5,flexWrap:"nowrap"}}>
        {p.chips.map((c,k)=>(
          <span key={c} className="m-chip" style={{"--d":`${k*0.5+i*0.3}s`,display:"inline-flex",alignItems:"center",gap:3,fontSize:9.5,fontWeight:700,color:G,background:`${G}18`,border:`1px solid ${G}33`,borderRadius:99,padding:"3px 7px",whiteSpace:"nowrap"}}>
            <svg width="9" height="9" viewBox="0 0 24 24" fill="none"><path d="M20 6L9 17l-5-5" stroke={G} strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round"/></svg>{c}
          </span>
        ))}
      </div>
    </div>
  );
});
const HeroWave=React.memo(function HeroWave({dark,orient,onToggle}){
  const stageRef=React.useRef(null);
  const v=orient==="v";
  // Mouse parallax via CSS variables — no React re-render, throttled with rAF
  useEffect(()=>{
    let raf=0,nx=0,ny=0;
    const apply=()=>{raf=0;const el=stageRef.current;if(!el)return;el.style.setProperty("--px",nx.toFixed(2));el.style.setProperty("--py",ny.toFixed(2));};
    const onMove=e=>{nx=(e.clientX/window.innerWidth-.5)*20;ny=(e.clientY/window.innerHeight-.5)*20;if(!raf)raf=requestAnimationFrame(apply);};
    window.addEventListener("mousemove",onMove,{passive:true});
    return()=>{window.removeEventListener("mousemove",onMove);if(raf)cancelAnimationFrame(raf);};
  },[]);
  return(
    <div style={{position:"relative",marginTop:10}}>
      <div ref={stageRef} className={"m-stage "+(v?"v":"h")} aria-hidden="true">
        <div className={"m-plane "+(v?"pv":"ph")}>
          {v?(
            <div className="m-cols">
              {[0,1,2,3].map(ci=>(
                <div key={ci} className={"m-col "+(ci%2?"b":"a")} style={{marginTop:ci%2?-120:0}}>
                  {[...lpRot(LP_PEOPLE,ci*2),...lpRot(LP_PEOPLE,ci*2)].map((p,i)=><LpTile key={i} p={p} i={i+ci*3} dark={dark}/>)}
                </div>
              ))}
            </div>
          ):(
            [0,1,2].map(ri=>(
              <div key={ri} className={"m-row "+(ri%2?"b":"a")} style={{marginLeft:ri%2?-120:0}}>
                {[...lpRot(LP_PEOPLE,ri*3),...lpRot(LP_PEOPLE,ri*3)].map((p,i)=><LpTile key={i} p={p} i={i+ri*2} dark={dark}/>)}
              </div>
            ))
          )}
        </div>
      </div>
      <div style={{position:"absolute",left:0,right:0,bottom:6,display:"flex",justifyContent:"center",alignItems:"center",gap:12,zIndex:3,flexWrap:"wrap",padding:"0 5%"}}>
        <button onClick={onToggle} style={{display:"inline-flex",alignItems:"center",gap:8,background:dark?"rgba(255,255,255,0.08)":"#fff",border:`1px solid ${dark?"rgba(255,255,255,0.08)":"rgba(0,0,0,0.08)"}`,borderRadius:99,color:dark?"#f0f4ff":"#0f172a",padding:"9px 16px",fontSize:12.5,fontWeight:600,cursor:"pointer",fontFamily:"inherit"}}>
          <span style={{display:"inline-block",transition:"transform .4s",transform:v?"rotate(90deg)":"none"}}>↔</span> {v?"Switch to horizontal":"Switch to vertical"}
        </button>
        <span style={{fontSize:11,color:dark?"rgba(255,255,255,0.4)":"rgba(0,0,0,0.4)"}}>Sample profiles for illustration · hover to pause</span>
      </div>
    </div>
  );
});

function LandingPage({onRegister,gateMessage}) {
  const [dark,setDark]=useState(()=>{try{return localStorage.getItem("meitho_theme")==="dark"}catch{return true}});
  const [showModal,setShowModal]=useState(null);
  const [orient,setOrient]=useState(()=>{try{return window.innerWidth<769?"v":"h";}catch{return "h";}});
  const [orientTouched,setOrientTouched]=useState(false);
  useEffect(()=>{
    const onR=()=>{if(!orientTouched)setOrient(window.innerWidth<769?"v":"h");};
    window.addEventListener("resize",onR);
    return()=>window.removeEventListener("resize",onR);
  },[orientTouched]);
  const [scrollY,setScrollY]=useState(0);
  const [visible,setVisible]=useState({});
  const [mousePos,setMousePos]=useState({x:0,y:0});
  const [activeStep,setActiveStep]=useState(0);
  const [typedText,setTypedText]=useState("");
  const [counters,setCounters]=useState({searches:0,companies:0,time:0});
  const [tilt,setTilt]=useState({x:0,y:0,glowX:50,glowY:50});
  const heroCardRef=React.useRef(null);

  const fullText="Know who you hire.";

  // Typewriter effect
  useEffect(()=>{
    let i=0;
    const interval=setInterval(()=>{
      if(i<=fullText.length){setTypedText(fullText.slice(0,i));i++;}
      else clearInterval(interval);
    },60);
    return()=>clearInterval(interval);
  },[]);

  // Scroll tracking
  const [scrollProgress,setScrollProgress]=useState(0);
  useEffect(()=>{
    const onScroll=()=>{
      const y=window.scrollY;setScrollY(y>700?800:y>40?100:0); // bucketed: re-renders only when a threshold is crossed
      // Intersection-based visibility
      document.querySelectorAll("[data-reveal]").forEach(el=>{
        const rect=el.getBoundingClientRect();
        const id=el.dataset.reveal;
        if(rect.top<window.innerHeight*0.85){
          setVisible(v=>v[id]?v:({...v,[id]:true}));
        }
      });
    };
    window.addEventListener("scroll",onScroll,{passive:true});
    onScroll();
    return()=>window.removeEventListener("scroll",onScroll);
  },[]);

  // Step cycle
  useEffect(()=>{
    const t=setInterval(()=>setActiveStep(s=>(s+1)%5),2200);
    return()=>clearInterval(t);
  },[]);

  // Counter animation
  useEffect(()=>{
    if(!visible.stats)return;
    let start=Date.now();
    const animate=()=>{
      const elapsed=(Date.now()-start)/1500;
      const t=Math.min(elapsed,1);
      const ease=1-Math.pow(1-t,3);
      setCounters({searches:Math.round(ease*50000),companies:Math.round(ease*500),time:Math.round(ease*3)});
      if(t<1)requestAnimationFrame(animate);
    };
    requestAnimationFrame(animate);
  },[visible.stats]);

  const reveal=id=>({
    "data-reveal":id,
    style:{
      opacity:visible[id]?1:0,
      transform:visible[id]?"translateY(0)":"translateY(40px)",
      transition:"opacity .7s ease, transform .7s ease",
    }
  });

  const revealDelay=(id,delay)=>({
    "data-reveal":id,
    style:{
      opacity:visible[id]?1:0,
      transform:visible[id]?"translateY(0)":"translateY(40px)",
      transition:`opacity .7s ease ${delay}s, transform .7s ease ${delay}s`,
    }
  });

  // Colors - dark/light responsive
  const BG=dark?"#020304":"#f8fafc";
  const CARD=dark?"rgba(255,255,255,0.03)":"rgba(255,255,255,0.9)";
  const BORDER=dark?"rgba(255,255,255,0.07)":"rgba(0,0,0,0.08)";
  const TEXT=dark?"#f0f4ff":"#0f172a";
  const MUTED=dark?"rgba(255,255,255,0.4)":"rgba(0,0,0,0.4)";
  const SUB=dark?"rgba(255,255,255,0.6)":"rgba(0,0,0,0.6)";
  const RED="#ff3131";
  const ORANGE="#ff914d";
  const GREEN="#10b981";
  const BRAND=`linear-gradient(135deg,${RED},${ORANGE})`;
  const GTEXT=`linear-gradient(135deg,${RED},${ORANGE})`;

  const STEPS=[
    {key:"uan",label:"Employment",icon:<svg width="20" height="20" viewBox="0 0 24 24" fill="none"><rect x="3" y="7" width="18" height="13" rx="2" stroke={RED} strokeWidth="1.5"/><path d="M8 7V5a2 2 0 012-2h4a2 2 0 012 2v2" stroke={ORANGE} strokeWidth="1.5"/><path d="M12 12v4M10 14h4" stroke="white" strokeWidth="1.2" strokeLinecap="round"/></svg>,color:RED,desc:"Full EPFO history verified"},
    {key:"pan",label:"PAN Card",icon:<svg width="20" height="20" viewBox="0 0 24 24" fill="none"><rect x="2" y="5" width="20" height="14" rx="2" stroke={RED} strokeWidth="1.5"/><path d="M6 9h4M6 12h8M6 15h5" stroke={ORANGE} strokeWidth="1.2" strokeLinecap="round"/></svg>,color:"#f59e0b",desc:"Income Tax dept validated"},
    {key:"aadhaar",label:"Aadhaar",icon:<svg width="20" height="20" viewBox="0 0 24 24" fill="none"><path d="M12 2a5 5 0 100 10A5 5 0 0012 2z" stroke={RED} strokeWidth="1.5"/><path d="M4 20c0-4 3.6-7 8-7s8 3 8 7" stroke={ORANGE} strokeWidth="1.5" strokeLinecap="round"/></svg>,color:GREEN,desc:"DigiLocker verified"},
    {key:"dl",label:"Driving License",icon:<svg width="20" height="20" viewBox="0 0 24 24" fill="none"><rect x="1" y="4" width="22" height="16" rx="3" stroke={RED} strokeWidth="1.5"/><circle cx="8" cy="13" r="3" stroke={ORANGE} strokeWidth="1.2"/><path d="M14 10h5M14 13h3M14 16h4" stroke="white" strokeWidth="1" strokeLinecap="round"/></svg>,color:"#0cc0df",desc:"RTO database checked"},
    {key:"ecourt",label:"Criminal Check",icon:<svg width="20" height="20" viewBox="0 0 24 24" fill="none"><path d="M12 3l8 4v5c0 5-3.5 9.7-8 11C7.5 21.7 4 17 4 12V7l8-4z" stroke={RED} strokeWidth="1.5"/><path d="M9 12l2 2 4-4" stroke={ORANGE} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>,color:"#8b5cf6",desc:"All eCourts searched"},
  ];

  const Seal=({type,size=22,color=RED,strokeWidth=1.5})=>{
    const paths={
      uan:<><rect x="4" y="7" width="16" height="12" rx="1.5"/><path d="M8 7V5a2 2 0 012-2h4a2 2 0 012 2v2"/><path d="M9 13l2 2 4-4"/></>,
      pan:<><rect x="3" y="6" width="18" height="13" rx="1.5"/><path d="M7 10.5h4M7 13.5h7M7 16h5"/></>,
      aadhaar:<><circle cx="12" cy="8.5" r="3.3"/><path d="M5.2 20c0-3.4 3-6.2 6.8-6.2s6.8 2.8 6.8 6.2"/></>,
      dl:<><rect x="2" y="6" width="20" height="13" rx="2"/><circle cx="8" cy="12.5" r="2.2"/><path d="M13 10h6M13 13h4M13 16h5"/></>,
      court:<><path d="M12 3l8 4v5c0 5-3.5 9.5-8 10.5C7.5 21.5 4 17 4 12V7l8-4z"/><path d="M9 12l2 2 4-4.5"/></>,
      refcheck:<><path d="M20 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h13a2 2 0 012 2z"/><path d="M8 9h8M8 12h5"/></>,
      briefcase:<><rect x="2" y="7" width="20" height="13" rx="2"/><path d="M8 7V5a2 2 0 012-2h4a2 2 0 012 2v2"/><path d="M2 13h20"/></>,
      pipeline:<><circle cx="6" cy="6" r="2.3"/><circle cx="6" cy="18" r="2.3"/><circle cx="18" cy="12" r="2.3"/><path d="M6 8.3v7.4M8.1 7.1l7.5 3.5M8.1 16.9l7.5-3.5"/></>,
      calendar:<><rect x="3" y="5" width="18" height="16" rx="2"/><path d="M3 10h18M8 3v4M16 3v4"/></>,
      star:<path d="M12 3l2.6 5.9 6.4.6-4.8 4.3 1.4 6.3L12 17l-5.6 3.1 1.4-6.3L3 9.5l6.4-.6z"/>,
      approve:<><circle cx="12" cy="12" r="9"/><path d="M8 12.3l2.6 2.6L16 9"/></>,
      folder:<path d="M3 7a2 2 0 012-2h4l2 2h8a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2V7z"/>,
      upload:<><path d="M12 16V4M8 8l4-4 4 4"/><path d="M4 16v3a2 2 0 002 2h12a2 2 0 002-2v-3"/></>,
      pin:<><path d="M12 21s7-6.5 7-12a7 7 0 00-14 0c0 5.5 7 12 7 12z"/><circle cx="12" cy="9" r="2.3"/></>,
      stamp:<><path d="M12 3a3.6 3.6 0 013.6 3.6c0 1.8-1.35 2.7-1.35 4.5H9.75c0-1.8-1.35-2.7-1.35-4.5A3.6 3.6 0 0112 3z"/><path d="M6.5 15.5h11l1 5.5h-13z"/><path d="M9.3 12h5.4"/></>,
      shield:<><path d="M12 3l7 3v5.5c0 5-3 9-7 10.5-4-1.5-7-5.5-7-10.5V6z"/><path d="M9 12l2.2 2.2L15.5 9.5"/></>,
    };
    return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round">{paths[type]}</svg>;
  };

  const BGV_ITEMS=[
    {type:"uan",label:"UAN / Employment",desc:"Complete EPFO history — every employer, tenure and PF status, straight from government records.",tag:"EPFO"},
    {type:"pan",label:"PAN Verification",desc:"Real-time Income Tax department validation. Catch a forged PAN before it costs you.",tag:"Instant"},
    {type:"aadhaar",label:"Aadhaar Check",desc:"DigiLocker-verified identity — the candidate confirms it themselves, nothing stored beyond the check.",tag:"DigiLocker"},
    {type:"dl",label:"Driving License",desc:"RTO database lookup for license validity, class and expiry — built for logistics and delivery hiring.",tag:"RTO"},
    {type:"court",label:"Criminal / eCourt",desc:"Searched across every Indian eCourt. Pan-India records, back in seconds.",tag:"Pan-India"},
    {type:"refcheck",label:"Reference Check",desc:"Digital reference form sent automatically. Responses land straight in the candidate's file.",tag:"Automated"},
  ];

  const SELECT_ITEMS=[
    {type:"briefcase",label:"Job Postings",desc:"A public careers page for every company, generated automatically — no separate site to maintain."},
    {type:"pipeline",label:"Candidate Pipeline",desc:"Drag candidates through your stages. Applied to Joined, all in one board."},
    {type:"calendar",label:"Interviews & Panel Feedback",desc:"Schedule rounds, send panel members a rating link, and see every score land on the candidate's timeline."},
    {type:"approve",label:"Offer Approval",desc:"HR drafts the offer, a Company Admin signs off before it ever reaches the candidate."},
    {type:"upload",label:"Onboarding Documents",desc:"Candidates upload straight from their phone. HR approves each file before the offer releases."},
    {type:"folder",label:"Employee Files",desc:"The moment someone joins, their approved documents are already filed and ready."},
    {type:"pin",label:"Address Verification",desc:"A selfie and a GPS pin, checked against the address on file — present and permanent."},
    {type:"shield",label:"Candidate Tracker",desc:"Candidates check their own status, upload documents, and accept offers — no phone calls needed."},
  ];

  const FAQS=[
    {q:"Who is Meitho built for?",a:"Meitho is strictly B2B — built for HR teams and founders who need to verify who they're hiring. There's no candidate sign-up; candidates only ever interact through a one-time secure link."},
    {q:"How fast are the checks?",a:"UAN, PAN, Aadhaar and Driving License checks return in minutes, pulled live from government databases. Criminal/eCourt and Reference Checks are automated but can take longer, depending on how quickly a reference responds."},
    {q:"Is Meitho Select really included?",a:"Yes. The full recruitment platform — job postings, pipeline, interviews, offer approval, onboarding — comes with your Meitho account. It's not a separate product or a separate bill."},
    {q:"Is candidate data handled compliantly?",a:"Meitho follows the DPDP Act, 2023. Candidates consent explicitly before any Aadhaar or identity check runs, and data is scoped to the company that requested it."},
    {q:"How do I get set up?",a:"Register your company, and a Meitho admin approves the account — usually within a day. Once approved, load your wallet and you're ready to search or post your first job."},
    {q:"What does it cost?",a:"Pay per check, no subscriptions — your wallet balance never expires. Reach out and we'll walk you through pricing for your hiring volume."},
  ];

  const spot=e=>{const r=e.currentTarget.getBoundingClientRect();e.currentTarget.style.setProperty("--mx",(e.clientX-r.left)+"px");e.currentTarget.style.setProperty("--my",(e.clientY-r.top)+"px");};
  const Eyebrow=({children})=><div style={{fontSize:12,fontWeight:700,color:RED,letterSpacing:2,textTransform:"uppercase",marginBottom:12}}>{children}</div>;
  const H2=({children,center})=><h2 style={{fontFamily:"'Sora',sans-serif",fontSize:"clamp(28px,3.8vw,44px)",fontWeight:800,letterSpacing:"-1.3px",lineHeight:1.1,marginBottom:14,textAlign:center?"center":"left"}}>{children}</h2>;
  const Lead=({children,center})=><p style={{fontSize:16.5,color:SUB,lineHeight:1.65,maxWidth:640,marginBottom:38,textAlign:center?"center":"left",marginLeft:center?"auto":0,marginRight:center?"auto":0}}>{children}</p>;

  const FLOW=[
    {n:"01",title:"Send a secure link",desc:"Name, email, phone, and the checks you need. Nothing is charged yet.",ev:"✉️ Link sent to Priya Nair",sub:"PAN · Reference · Address",c:"#0cc0df"},
    {n:"02",title:"Candidate fills the form",desc:"From their phone, in two minutes, with consent recorded.",ev:"👀 Priya opened the link",sub:"Filling PAN and reference details…",c:"#8b5cf6"},
    {n:"03",title:"You get notified",desc:"Review what was submitted, including payslips and offer letters.",ev:"🔔 Priya submitted her details",sub:"Review before you spend a rupee",c:ORANGE},
    {n:"04",title:"Run each check",desc:"One click per check. Status updates live.",ev:"✅ PAN verified · Name matched",sub:"Reference 1 of 2 responded",c:GREEN},
    {n:"05",title:"Download the report",desc:"One clean PDF with references' verdicts and documents.",ev:"📄 Report ready to download",sub:"File documents to Employee Files",c:RED},
  ];
  const TICK=["UAN","PAN","AADHAAR","DRIVING LICENCE","CRIMINAL / ECOURT","REFERENCE CHECK","ADDRESS CHECK"];

  return(
    <div style={{background:BG,minHeight:"100vh",fontFamily:"'Inter',system-ui,sans-serif",color:TEXT,overflowX:"hidden"}}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&family=Sora:wght@700;800;900&display=swap');
        *{box-sizing:border-box;margin:0;padding:0;-webkit-font-smoothing:antialiased;}
        html{scroll-behavior:smooth;}
        ::selection{background:rgba(255,49,49,0.3);color:#fff;}
        @keyframes mqL{from{transform:translateX(0)}to{transform:translateX(-50%)}}
        @keyframes mqR{from{transform:translateX(-50%)}to{transform:translateX(0)}}
        @keyframes mqU{from{transform:translateY(0)}to{transform:translateY(-50%)}}
        @keyframes mqD{from{transform:translateY(-50%)}to{transform:translateY(0)}}
        @keyframes bob{0%,100%{transform:translateY(0) rotate(0deg)}50%{transform:translateY(-16px) rotate(1.2deg)}}
        @keyframes chipPop{0%,70%,100%{transform:scale(1)}80%{transform:scale(1.18)}}
        @keyframes orb{0%,100%{transform:translate(0,0) scale(1)}33%{transform:translate(40px,-30px) scale(1.12)}66%{transform:translate(-30px,25px) scale(.94)}}
        @keyframes gshift{0%{background-position:0% 50%}100%{background-position:200% 50%}}
        @keyframes tickL{from{transform:translateX(0)}to{transform:translateX(-50%)}}
        @keyframes fadeUp{from{opacity:0;transform:translateY(14px)}to{opacity:1;transform:none}}
        @keyframes ctaSlideIn{from{opacity:0;transform:translateY(20px) scale(.9);}to{opacity:1;transform:translateY(0) scale(1);}}
        @keyframes ring{0%{transform:scale(.9);opacity:.7}100%{transform:scale(1.6);opacity:0}}
        @keyframes drift{0%,100%{transform:translateY(0)}50%{transform:translateY(-10px)}}
        .m-nav-link{position:relative;color:${MUTED};text-decoration:none;font-size:13.5px;font-weight:500;transition:color .2s;}
        .m-nav-link:hover{color:${TEXT};}
        .m-nav-link::after{content:'';position:absolute;left:0;right:100%;bottom:-4px;height:1.5px;background:${RED};transition:right .25s ease;}
        .m-nav-link:hover::after{right:0;}
        .m-btn-primary{position:relative;overflow:hidden;}
        .m-btn-primary::before{content:'';position:absolute;inset:0;background:linear-gradient(120deg,transparent,rgba(255,255,255,0.28),transparent);transform:translateX(-100%);transition:transform .6s;}
        .m-btn-primary:hover::before{transform:translateX(100%);}
        .m-grad{background:linear-gradient(90deg,${RED},${ORANGE},#ffd36e,${ORANGE},${RED});background-size:200% auto;-webkit-background-clip:text;background-clip:text;-webkit-text-fill-color:transparent;animation:gshift 6s linear infinite;}
        .m-gridbg{position:absolute;inset:0;background-image:linear-gradient(${dark?"rgba(255,255,255,0.045)":"rgba(15,23,42,0.05)"} 1px,transparent 1px),linear-gradient(90deg,${dark?"rgba(255,255,255,0.045)":"rgba(15,23,42,0.05)"} 1px,transparent 1px);background-size:56px 56px;-webkit-mask-image:radial-gradient(ellipse at 50% 30%,#000 10%,transparent 70%);mask-image:radial-gradient(ellipse at 50% 30%,#000 10%,transparent 70%);}
        .m-orb{position:absolute;border-radius:50%;filter:blur(50px);will-change:transform;animation:orb 18s ease-in-out infinite;}
        /* 3D wave stage */
        .m-stage{position:relative;perspective:1500px;margin:0 auto;max-width:1500px;overflow:hidden;-webkit-mask-image:radial-gradient(ellipse 78% 72% at 50% 50%,#000 55%,transparent 100%);mask-image:radial-gradient(ellipse 78% 72% at 50% 50%,#000 55%,transparent 100%);}
        .m-stage.h{height:470px;}
        .m-stage.v{height:560px;}
        .m-plane{position:absolute;left:50%;top:50%;transform-style:preserve-3d;transition:transform .35s ease-out;will-change:transform;}
        .m-plane.ph{transform:rotateX(calc(52deg - var(--py,0) * 0.4deg)) rotateZ(calc(-16deg + var(--px,0) * 0.35deg)) translateZ(-40px);}
        .m-plane.pv{transform:rotateX(calc(14deg - var(--py,0) * 0.3deg)) rotateY(calc(-26deg + var(--px,0) * 0.5deg)) rotateZ(7deg);}
        .m-stage.h .m-plane{width:1900px;margin-left:-950px;margin-top:-200px;}
        .m-stage.v .m-plane{width:980px;margin-left:-490px;margin-top:-620px;height:1240px;}
        .m-row{display:flex;gap:16px;width:max-content;margin-bottom:16px;}
        .m-col{display:flex;flex-direction:column;gap:16px;width:224px;height:max-content;}
        .m-cols{display:flex;gap:16px;justify-content:center;}
        .m-tile{position:relative;width:224px;flex-shrink:0;border-radius:16px;padding:14px;will-change:transform;animation:bob 4.2s ease-in-out infinite;animation-delay:calc(var(--i) * -0.55s);}
        .m-chip{animation:chipPop 4s ease-in-out infinite;animation-delay:var(--d);}
        .m-row{will-change:transform;}.m-col{will-change:transform;}
        .m-row.a{animation:mqL 36s linear infinite;}
        .m-row.b{animation:mqR 42s linear infinite;}
        .m-col.a{animation:mqU 30s linear infinite;}
        .m-col.b{animation:mqD 34s linear infinite;}
        .m-stage:hover .m-row,.m-stage:hover .m-col,.m-stage:hover .m-tile{animation-play-state:paused;}
        .m-ticker{display:flex;width:max-content;animation:tickL 40s linear infinite;}
        .m-spot{position:relative;overflow:hidden;transition:transform .35s cubic-bezier(.22,.68,0,1.1),border-color .3s,box-shadow .35s;}
        .m-spot::before{content:'';position:absolute;inset:0;background:radial-gradient(280px circle at var(--mx,-999px) var(--my,-999px),${RED}26,transparent 60%);opacity:0;transition:opacity .25s;pointer-events:none;}
        .m-spot:hover::before{opacity:1;}
        .m-spot:hover{transform:translateY(-6px);border-color:${RED}55 !important;box-shadow:0 22px 50px ${dark?"rgba(255,49,49,0.12)":"rgba(255,49,49,0.12)"};}
        .faq-item{cursor:pointer;transition:background .2s;}
        .faq-item:hover{background:${dark?"rgba(255,255,255,0.02)":"rgba(0,0,0,0.015)"};}
        .m-bento{display:grid;grid-template-columns:repeat(4,1fr);gap:16px;}
        @media(prefers-reduced-motion:reduce){.m-row,.m-col,.m-tile,.m-chip,.m-orb,.m-ticker,.m-grad{animation:none !important;}}
        @media(max-width:1024px){.mg4{grid-template-columns:repeat(2,1fr) !important;}}
        @media(max-width:900px){.mg1,.mg2{grid-template-columns:repeat(2,1fr) !important;}.m-bento{grid-template-columns:repeat(2,1fr);}.m-bento-big{grid-column:span 2 !important;grid-row:span 1 !important;}.m-flowgrid{grid-template-columns:1fr !important;}}
        @media(max-width:768px){
          .mg1,.mg2,.mg4{grid-template-columns:1fr !important;}
          .m-nav-links{display:none !important;}
          .m-stage.v{height:480px;}
          .m-stage.v .m-plane{width:760px;margin-left:-380px;}
          .m-stage.h{height:380px;}
        }
        @media(max-width:480px){.m-hero-actions{flex-direction:column;align-items:stretch !important;}.m-nav-long{display:none;}.m-bento{grid-template-columns:1fr;}.m-bento-big{grid-column:span 1 !important;}}
      `}</style>

      {gateMessage&&(
        <div style={{position:"fixed",top:0,left:0,right:0,zIndex:200,background:BRAND,color:"#fff",padding:"12px 20px",textAlign:"center",fontSize:13,fontWeight:600,boxShadow:"0 4px 20px rgba(0,0,0,0.2)"}}>⏳ {gateMessage}</div>
      )}

      {/* NAV */}
      <nav style={{position:"sticky",top:0,zIndex:100,padding:"14px 5%",display:"flex",justifyContent:"space-between",alignItems:"center",background:scrollY>40?(dark?"rgba(2,3,4,0.78)":"rgba(248,250,252,0.88)"):"transparent",backdropFilter:scrollY>40?"blur(16px) saturate(160%)":"none",WebkitBackdropFilter:scrollY>40?"blur(16px) saturate(160%)":"none",borderBottom:scrollY>40?`1px solid ${BORDER}`:"1px solid transparent",transition:"all .3s ease"}}>
        <div style={{display:"flex",alignItems:"center",gap:9}}>
          <div style={{width:30,height:30,borderRadius:8,background:BRAND,display:"flex",alignItems:"center",justifyContent:"center",fontFamily:"'Sora',sans-serif",fontWeight:900,color:"#fff",fontSize:15}}>M</div>
          <span style={{fontFamily:"'Sora',sans-serif",fontWeight:800,fontSize:17,letterSpacing:"-0.3px"}}>Meitho</span>
        </div>
        <div style={{display:"flex",alignItems:"center",gap:30}} className="m-nav-links">
          <a href="#verification" className="m-nav-link">Verification</a>
          <a href="#flow" className="m-nav-link">Candidate link</a>
          <a href="#select" className="m-nav-link">Hiring (ATS)</a>
          <a href="#faq" className="m-nav-link">FAQ</a>
        </div>
        <div style={{display:"flex",alignItems:"center",gap:10}}>
          <button aria-label="Toggle theme" onClick={()=>setDark(d=>{try{localStorage.setItem("meitho_theme",!d?"dark":"light")}catch{};return !d;})} style={{background:"none",border:`1px solid ${BORDER}`,borderRadius:8,width:34,height:34,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",color:TEXT,fontSize:14}}>{dark?"☀️":"🌙"}</button>
          <button onClick={()=>setShowModal("login")} style={{background:"none",border:"none",color:TEXT,fontSize:13.5,fontWeight:600,cursor:"pointer",padding:"8px 6px"}}>Sign In</button>
          <button onClick={()=>setShowModal("register")} className="m-btn-primary" style={{background:BRAND,border:"none",borderRadius:9,color:"#fff",padding:"10px 16px",fontSize:13.5,fontWeight:700,cursor:"pointer",fontFamily:"inherit",whiteSpace:"nowrap"}}>Register<span className="m-nav-long"> Company</span></button>
        </div>
      </nav>

      {/* HERO */}
      <header style={{position:"relative",zIndex:1,overflow:"hidden"}}>
        <div style={{position:"absolute",inset:0,pointerEvents:"none"}}>
          <div className="m-gridbg"/>
          <div className="m-orb" style={{top:"-10%",left:"8%",width:420,height:420,background:`${RED}${dark?"40":"26"}`}}/>
          <div className="m-orb" style={{top:"10%",right:"6%",width:380,height:380,background:`${ORANGE}${dark?"38":"24"}`,animationDelay:"-6s"}}/>
          <div className="m-orb" style={{top:"45%",left:"40%",width:340,height:340,background:`#8b5cf6${dark?"30":"1a"}`,animationDelay:"-11s"}}/>
        </div>
        <div style={{position:"relative",padding:"64px 5% 8px",maxWidth:980,margin:"0 auto",textAlign:"center"}}>
          <div style={{display:"inline-flex",alignItems:"center",gap:8,padding:"7px 15px",borderRadius:99,border:`1px solid ${BORDER}`,background:dark?"rgba(255,255,255,0.04)":"#fff",marginBottom:24,fontSize:12.5,fontWeight:600,color:SUB,animation:"fadeUp .7s ease both"}}>
            <span style={{position:"relative",width:7,height:7,borderRadius:"50%",background:GREEN,display:"inline-block"}}><span style={{position:"absolute",inset:0,borderRadius:"50%",background:GREEN,animation:"ring 1.8s ease-out infinite"}}/></span>
            Background verification + hiring, built for Indian HR teams
          </div>
          <h1 style={{fontFamily:"'Sora',sans-serif",fontSize:"clamp(40px,7vw,84px)",fontWeight:900,letterSpacing:"-3px",lineHeight:1.0,marginBottom:22,animation:"fadeUp .8s .1s ease both"}}>
            Hire with <span className="m-grad">proof</span>,<br/>not promises.
          </h1>
          <p style={{fontSize:18,color:SUB,lineHeight:1.65,margin:"0 auto 32px",maxWidth:660,animation:"fadeUp .8s .2s ease both"}}>
            Verify UAN, PAN, Aadhaar, licence, criminal record, address and references from official sources. Send candidates one secure link and run each check when you are ready. The hiring platform comes with your account.
          </p>
          <div style={{display:"flex",gap:12,justifyContent:"center",flexWrap:"wrap",marginBottom:26,animation:"fadeUp .8s .3s ease both"}} className="m-hero-actions">
            <button onClick={()=>setShowModal("register")} className="m-btn-primary" style={{background:BRAND,border:"none",borderRadius:13,color:"#fff",padding:"16px 32px",fontSize:15.5,fontWeight:700,cursor:"pointer",fontFamily:"inherit",display:"flex",alignItems:"center",gap:8,boxShadow:`0 14px 40px ${RED}44`,justifyContent:"center"}}>
              Register Company
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none"><path d="M5 12h14M12 5l7 7-7 7" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
            </button>
            <button onClick={()=>setShowModal("login")} style={{background:dark?"rgba(255,255,255,0.05)":"#fff",border:`1px solid ${BORDER}`,borderRadius:13,color:TEXT,padding:"16px 28px",fontSize:15.5,fontWeight:600,cursor:"pointer",fontFamily:"inherit"}}>Sign In</button>
          </div>
          <div style={{display:"flex",gap:20,flexWrap:"wrap",justifyContent:"center",animation:"fadeUp .8s .4s ease both"}}>
            {["DPDP Act, 2023 compliant","Candidates never create an account","Pay per check, wallet never expires"].map((t,i)=>(
              <div key={i} style={{display:"flex",alignItems:"center",gap:6,fontSize:12.5,color:MUTED}}>
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none"><path d="M20 6L9 17l-5-5" stroke={GREEN} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/></svg>{t}
              </div>
            ))}
          </div>
        </div>

        <HeroWave dark={dark} orient={orient} onToggle={()=>{setOrientTouched(true);setOrient(o=>o==="h"?"v":"h");}}/>
      </header>

      {/* TICKER */}
      <section aria-hidden="true" style={{position:"relative",zIndex:1,padding:"34px 0",overflow:"hidden",borderTop:`1px solid ${BORDER}`,borderBottom:`1px solid ${BORDER}`,marginTop:20}}>
        <div className="m-ticker">
          {[...TICK,...TICK,...TICK,...TICK].map((t,i)=>(
            <span key={i} style={{display:"inline-flex",alignItems:"center",gap:28,paddingRight:28,fontFamily:"'Sora',sans-serif",fontWeight:900,fontSize:"clamp(22px,3vw,34px)",letterSpacing:"-0.5px",whiteSpace:"nowrap",color:i%2?"transparent":TEXT,WebkitTextStroke:i%2?`1.2px ${dark?"rgba(255,255,255,0.35)":"rgba(15,23,42,0.35)"}`:"0"}}>
              {t}<span style={{width:8,height:8,borderRadius:"50%",background:BRAND,display:"inline-block"}}/>
            </span>
          ))}
        </div>
      </section>

      {/* VERIFICATION */}
      <section id="verification" {...reveal("bgv-title")} style={{position:"relative",zIndex:1,padding:"90px 5% 60px",maxWidth:1300,margin:"0 auto"}}>
        <Eyebrow>Background verification</Eyebrow>
        <H2>Every check an Indian employer needs, in one place.</H2>
        <Lead>Run a check on its own or build a package. Most results come back in minutes, and every one is saved to the candidate's record.</Lead>
        <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:18}} className="mg1">
          {BGV_ITEMS.map((it,i)=>(
            <div key={it.type} {...revealDelay("bgv-"+i,i*0.07)} onMouseMove={spot} className="m-spot" style={{...revealDelay("bgv-"+i,i*0.07).style,background:dark?"rgba(255,255,255,0.03)":"#fff",border:`1px solid ${BORDER}`,borderRadius:20,padding:"26px 24px"}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:18}}>
                <div style={{width:46,height:46,borderRadius:13,background:`${RED}14`,display:"flex",alignItems:"center",justifyContent:"center"}}><Seal type={it.type} size={23}/></div>
                <span style={{fontSize:10.5,fontWeight:700,color:SUB,border:`1px solid ${BORDER}`,borderRadius:99,padding:"3px 10px"}}>{it.tag}</span>
              </div>
              <div style={{fontSize:17,fontWeight:700,marginBottom:6}}>{it.label}</div>
              <div style={{fontSize:13.5,color:SUB,lineHeight:1.65}}>{it.desc}</div>
            </div>
          ))}
        </div>
      </section>

      {/* CANDIDATE LINK FLOW — live stepper */}
      <section id="flow" {...reveal("flow-title")} style={{position:"relative",zIndex:1,padding:"80px 5%",maxWidth:1300,margin:"0 auto"}}>
        <Eyebrow>Candidate verification</Eyebrow>
        <H2>You send a link. The candidate does the typing.</H2>
        <Lead>No chasing documents on WhatsApp. Candidates submit once, you review, and you choose which checks to pay for.</Lead>
        <div className="m-flowgrid" style={{display:"grid",gridTemplateColumns:"1.1fr 0.9fr",gap:28,alignItems:"center"}}>
          <div>
            {FLOW.map((f,i)=>{const on=i===activeStep,past=i<activeStep;return(
              <div key={f.n} onClick={()=>setActiveStep(i)} style={{display:"flex",gap:16,padding:"14px 16px",borderRadius:16,cursor:"pointer",marginBottom:6,border:`1px solid ${on?f.c+"55":"transparent"}`,background:on?(dark?"rgba(255,255,255,0.04)":"#fff"):"transparent",transition:"all .35s"}}>
                <div style={{position:"relative",width:40,height:40,borderRadius:"50%",flexShrink:0,display:"flex",alignItems:"center",justifyContent:"center",fontFamily:"'Sora',sans-serif",fontWeight:800,fontSize:13,color:on||past?"#fff":MUTED,background:on||past?f.c:"transparent",border:`1.5px solid ${on||past?f.c:BORDER}`,transition:"all .35s"}}>
                  {past?"✓":f.n}
                  {on&&<span style={{position:"absolute",inset:-1,borderRadius:"50%",border:`2px solid ${f.c}`,animation:"ring 1.6s ease-out infinite"}}/>}
                </div>
                <div>
                  <div style={{fontSize:15.5,fontWeight:700,marginBottom:3}}>{f.title}</div>
                  <div style={{fontSize:13,color:SUB,lineHeight:1.55,maxHeight:on?60:0,opacity:on?1:0.0,overflow:"hidden",transition:"all .4s"}}>{f.desc}</div>
                </div>
              </div>);})}
          </div>
          <div style={{position:"relative",borderRadius:26,padding:"26px 22px",background:dark?"linear-gradient(160deg,rgba(255,255,255,0.07),rgba(255,255,255,0.02))":"#fff",border:`1px solid ${BORDER}`,boxShadow:dark?"0 40px 90px rgba(0,0,0,0.5)":"0 40px 90px rgba(15,23,42,0.12)",animation:"drift 6s ease-in-out infinite"}}>
            <div style={{fontSize:11,fontWeight:700,letterSpacing:1.5,color:MUTED,textTransform:"uppercase",marginBottom:14,display:"flex",justifyContent:"space-between"}}><span>Live status</span><span style={{color:GREEN}}>● LIVE</span></div>
            <div key={activeStep} style={{animation:"fadeUp .5s ease both",padding:"18px 16px",borderRadius:16,background:`${FLOW[activeStep].c}14`,border:`1px solid ${FLOW[activeStep].c}40`}}>
              <div style={{fontSize:16,fontWeight:700,marginBottom:4}}>{FLOW[activeStep].ev}</div>
              <div style={{fontSize:12.5,color:SUB}}>{FLOW[activeStep].sub}</div>
            </div>
            <div style={{display:"flex",gap:6,marginTop:16}}>
              {FLOW.map((f,i)=><div key={i} style={{flex:1,height:5,borderRadius:99,background:i<=activeStep?f.c:BORDER,transition:"background .4s"}}/>)}
            </div>
          </div>
        </div>
      </section>

      {/* ATS BENTO */}
      <section id="select" {...reveal("select-title")} style={{position:"relative",zIndex:1,padding:"70px 5%",maxWidth:1300,margin:"0 auto"}}>
        <Eyebrow>Meitho Select · hiring platform</Eyebrow>
        <H2>From job post to joined, without leaving Meitho.</H2>
        <Lead>Everything after the resume lives with verification, so a candidate's checks, interview scores and documents stay on one record.</Lead>
        <div className="m-bento">
          <div className="m-bento-big m-spot" onMouseMove={spot} style={{gridColumn:"span 2",gridRow:"span 2",background:dark?"rgba(255,255,255,0.03)":"#fff",border:`1px solid ${BORDER}`,borderRadius:22,padding:"24px 22px"}}>
            <div style={{fontSize:12,fontWeight:700,color:MUTED,letterSpacing:1.5,textTransform:"uppercase",marginBottom:14}}>Candidate pipeline</div>
            <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:10}}>
              {["Applied","Interview","Offer"].map((col,ci)=>(
                <div key={col} style={{background:dark?"rgba(255,255,255,0.03)":"#f8fafc",borderRadius:14,padding:10,minHeight:210}}>
                  <div style={{fontSize:11.5,fontWeight:700,marginBottom:10,color:SUB}}>{col}</div>
                  {["Static A","Static B"].slice(0,ci===1?1:2).map((s,k)=>(
                    <div key={k} style={{height:34,borderRadius:9,background:dark?"rgba(255,255,255,0.05)":"#fff",border:`1px solid ${BORDER}`,marginBottom:8}}/>
                  ))}
                  {activeStep%3===ci&&(
                    <div key={activeStep} style={{padding:"9px 10px",borderRadius:10,background:BRAND,color:"#fff",fontSize:11.5,fontWeight:700,animation:"fadeUp .5s ease both",boxShadow:`0 10px 24px ${RED}40`}}>Rajesh K.<div style={{fontSize:9.5,fontWeight:500,opacity:.85}}>PAN ✓ UAN ✓</div></div>
                  )}
                </div>
              ))}
            </div>
            <div style={{fontSize:13.5,color:SUB,marginTop:14,lineHeight:1.6}}>Drag candidates through stages, from Applied to Joined, with their verification status beside them.</div>
          </div>
          {SELECT_ITEMS.filter(x=>x.type!=="pipeline").slice(0,7).map((it,i)=>(
            <div key={it.type} {...revealDelay("sel-"+i,i*0.05)} onMouseMove={spot} className="m-spot" style={{...revealDelay("sel-"+i,i*0.05).style,background:dark?"rgba(255,255,255,0.03)":"#fff",border:`1px solid ${BORDER}`,borderRadius:18,padding:"18px 16px"}}>
              <div style={{width:36,height:36,borderRadius:10,background:`${ORANGE}1a`,display:"flex",alignItems:"center",justifyContent:"center",marginBottom:10}}><Seal type={it.type} size={18} color={ORANGE}/></div>
              <div style={{fontSize:14,fontWeight:700,marginBottom:4}}>{it.label}</div>
              <div style={{fontSize:12,color:SUB,lineHeight:1.55}}>{it.desc}</div>
            </div>
          ))}
        </div>
      </section>

      {/* WHY */}
      <section {...reveal("why")} style={{position:"relative",zIndex:1,padding:"60px 5%",maxWidth:1300,margin:"0 auto"}}>
        <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:18}} className="mg1">
          {[
            {t:"Pay only for what you run",d:"Load your wallet and pay per check. Sending a link costs nothing. You are charged only when you run a check.",c:RED,i:"approve"},
            {t:"Consent built in",d:"Candidates agree explicitly before any identity check runs. Data stays scoped to the company that requested it.",c:GREEN,i:"shield"},
            {t:"One record per candidate",d:"Checks, reference verdicts, documents and interview feedback sit together, and can be filed to Employee Files in a click.",c:ORANGE,i:"folder"},
          ].map((c,i)=>(
            <div key={i} {...revealDelay("why-"+i,i*0.08)} onMouseMove={spot} className="m-spot" style={{...revealDelay("why-"+i,i*0.08).style,background:dark?"rgba(255,255,255,0.025)":"#fff",border:`1px solid ${BORDER}`,borderRadius:20,padding:"28px 24px"}}>
              <div style={{width:44,height:44,borderRadius:13,background:`${c.c}18`,display:"flex",alignItems:"center",justifyContent:"center",marginBottom:14}}><Seal type={c.i} size={22} color={c.c}/></div>
              <div style={{fontSize:17,fontWeight:700,marginBottom:6}}>{c.t}</div>
              <div style={{fontSize:13.5,color:SUB,lineHeight:1.65}}>{c.d}</div>
            </div>
          ))}
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" {...reveal("faq-title")} style={{position:"relative",zIndex:1,padding:"60px 5%",maxWidth:820,margin:"0 auto"}}>
        <div style={{textAlign:"center"}}><Eyebrow>FAQ</Eyebrow><H2 center>Questions HR teams ask us</H2></div>
        <div style={{marginTop:28,background:dark?"rgba(255,255,255,0.025)":"#fff",border:`1px solid ${BORDER}`,borderRadius:18,overflow:"hidden"}}>
          {FAQS.map((f,i)=>(
            <FaqRow key={i} q={f.q} a={f.a} idx={i} border={BORDER} text={TEXT} sub={SUB} muted={MUTED} last={i===FAQS.length-1}/>
          ))}
        </div>
      </section>

      {/* FINAL CTA */}
      <section {...reveal("final-cta")} style={{position:"relative",zIndex:1,padding:"40px 5% 90px",maxWidth:1300,margin:"0 auto",textAlign:"center"}}>
        <div style={{background:`linear-gradient(120deg,${RED},${ORANGE},#ff5e62,${RED})`,backgroundSize:"300% 300%",animation:"gshift 10s linear infinite",borderRadius:28,padding:"72px 6%",position:"relative",overflow:"hidden"}}>
          <div className="m-orb" style={{top:-80,right:-60,width:260,height:260,background:"rgba(255,255,255,0.18)"}}/>
          <div className="m-orb" style={{bottom:-100,left:-60,width:300,height:300,background:"rgba(255,255,255,0.14)",animationDelay:"-7s"}}/>
          <h2 style={{fontFamily:"'Sora',sans-serif",fontSize:"clamp(30px,4.4vw,52px)",fontWeight:900,color:"#fff",letterSpacing:"-1.8px",marginBottom:14,position:"relative"}}>Know who you are hiring.</h2>
          <p style={{fontSize:16.5,color:"rgba(255,255,255,0.92)",marginBottom:32,position:"relative"}}>Register your company and run your first verification this week.</p>
          <button onClick={()=>setShowModal("register")} style={{position:"relative",background:"#fff",border:"none",borderRadius:13,color:RED,padding:"16px 34px",fontSize:15.5,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>Register Company →</button>
        </div>
      </section>

      {/* FOOTER */}
      <footer style={{position:"relative",zIndex:1,padding:"34px 5%",maxWidth:1300,margin:"0 auto",borderTop:`1px solid ${BORDER}`,display:"flex",justifyContent:"space-between",alignItems:"center",flexWrap:"wrap",gap:16}}>
        <div style={{display:"flex",alignItems:"center",gap:9}}>
          <div style={{width:24,height:24,borderRadius:6,background:BRAND,display:"flex",alignItems:"center",justifyContent:"center",fontFamily:"'Sora',sans-serif",fontWeight:900,color:"#fff",fontSize:11}}>M</div>
          <span style={{fontFamily:"'Sora',sans-serif",fontWeight:800,fontSize:14}}>Meitho</span>
          <span style={{fontSize:11,color:MUTED,marginLeft:6}}>© {new Date().getFullYear()}</span>
        </div>
        <div style={{display:"flex",gap:20,flexWrap:"wrap"}}>
          <a href="mailto:sales@meitho.com" className="m-nav-link">sales@meitho.com</a>
          <a href="mailto:help@meitho.com" className="m-nav-link">help@meitho.com</a>
          <a href="tel:+918300016265" className="m-nav-link">+91 83000 16265</a>
          <a href="/terms" className="m-nav-link">Terms</a>
          <a href="/privacy" className="m-nav-link">Privacy</a>
        </div>
        <div style={{fontSize:11,color:MUTED}}>DPDP 2023 · Made in India 🇮🇳</div>
      </footer>

      {scrollY>700&&(
        <button onClick={()=>setShowModal("register")} className="m-btn-primary" style={{position:"fixed",bottom:24,right:24,zIndex:200,background:BRAND,border:"none",borderRadius:99,color:"#fff",padding:"14px 24px",fontSize:13.5,fontWeight:700,cursor:"pointer",fontFamily:"inherit",display:"flex",alignItems:"center",gap:8,boxShadow:`0 12px 34px ${RED}45`,animation:"ctaSlideIn .35s cubic-bezier(.22,1.2,.4,1)"}}>
          Register Company
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none"><path d="M5 12h14M12 5l7 7-7 7" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
        </button>
      )}
      {showModal&&(
        <div style={{position:"fixed",inset:0,background:"rgba(2,4,8,0.72)",backdropFilter:"blur(8px)",WebkitBackdropFilter:"blur(8px)",zIndex:1000,display:"flex",alignItems:"center",justifyContent:"center",padding:16}} onClick={()=>setShowModal(null)}>
          <div onClick={e=>e.stopPropagation()} className="light" style={{background:"rgba(255,255,255,0.97)",border:"1px solid rgba(0,0,0,0.08)",borderRadius:20,boxShadow:"0 32px 80px rgba(0,0,0,0.35)",padding:32,width:"100%",maxWidth:420,maxHeight:"90vh",overflow:"auto","--text":"#0a1418","--muted":"rgba(10,20,24,0.55)","--brand":"#0cc0df","--s2":"rgba(0,0,0,0.04)","--b1":"rgba(0,0,0,0.12)","--red":"#ff3131"}}>
            <ModalContent mode={showModal} setMode={setShowModal} onClose={()=>setShowModal(null)} setShowModal={setShowModal} onSwitch={setShowModal} onRegister={onRegister}/>
          </div>
        </div>
      )}
    </div>
  );
}

function FaqRow({q,a,idx,border,text,sub,muted,last}){
  const [open,setOpen]=useState(false);
  return(
    <div className="faq-item" onClick={()=>setOpen(o=>!o)} style={{padding:"20px 24px",borderBottom:last?"none":`1px solid ${border}`}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",gap:16}}>
        <div style={{display:"flex",alignItems:"baseline",gap:14}}>
          <span style={{fontSize:11,fontWeight:700,color:muted,fontFamily:"monospace"}}>Q{String(idx+1).padStart(2,"0")}</span>
          <span style={{fontSize:14.5,fontWeight:600,color:text}}>{q}</span>
        </div>
        <span style={{fontSize:16,color:muted,transform:open?"rotate(45deg)":"none",transition:"transform .25s ease",flexShrink:0}}>+</span>
      </div>
      <div style={{maxHeight:open?200:0,overflow:"hidden",transition:"max-height .3s ease"}}>
        <div style={{fontSize:13.5,color:sub,lineHeight:1.65,fontWeight:300,paddingTop:14,paddingLeft:38}}>{a}</div>
      </div>
    </div>
  );
}




function ModalContent({mode,onRegister,onSwitch}) {
  const [email,setEmail]=useState("");const [pass,setPass]=useState("");
  const [showPass,setShowPass]=useState(false);
  const [err,setErr]=useState("");const [loading,setLoading]=useState(false);
  const [reg,setReg]=useState({companyName:"",email:"",password:"",confirm:"",phone:"",gst:"",address:"",industry:"",pocName:""});
  const [regErr,setRegErr]=useState("");const [regOk,setRegOk]=useState(false);
  const [regStep,setRegStep]=useState(1);
  const [forgotMode,setForgotMode]=useState(false);
  const [forgotEmail,setForgotEmail]=useState("");const [forgotSent,setForgotSent]=useState(false);

  const [otpMode2,setOtpMode2]=useState(false);
  const [otp2,setOtp2]=useState("");
  const [genOtp2,setGenOtp2]=useState("");
  const [otpExp2,setOtpExp2]=useState(null);
  const [pendCred2,setPendCred2]=useState(null);
  const [otpErr2,setOtpErr2]=useState("");
  const [otpLoad2,setOtpLoad2]=useState(false);
  const [otpTimer2,setOtpTimer2]=useState(0);
  const [resend2,setResend2]=useState(0);
  useEffect(()=>{if(otpTimer2>0){const t=setTimeout(()=>setOtpTimer2(s=>s-1),1000);return()=>clearTimeout(t);}},[otpTimer2]);
  useEffect(()=>{if(resend2>0){const t=setTimeout(()=>setResend2(s=>s-1),1000);return()=>clearTimeout(t);}},[resend2]);

  useEffect(()=>{if(otpTimer2>0){const t=setTimeout(()=>setOtpTimer2(s=>s-1),1000);return()=>clearTimeout(t);}},[otpTimer2]);
  useEffect(()=>{if(resend2>0){const t=setTimeout(()=>setResend2(s=>s-1),1000);return()=>clearTimeout(t);}},[resend2]);

  const doLogin=async()=>{
    if(!email||!pass){setErr("Enter email and password.");return;}
    setLoading(true);setErr("");
    try{await signIn(email,pass);}
    catch(e){setErr(e.code==="auth/invalid-credential"?"Invalid email or password.":"Login failed. Try again.");}
    setLoading(false);
  };
  // 2FA OTP sending - to be rebuilt

  const verifyOTP2=async()=>{
    if(!otp2||otp2.length!==6){setOtpErr2("Enter 6-digit OTP");return;}
    if(Date.now()>otpExp2){setOtpErr2("OTP expired. Resend.");return;}
    if(otp2!==genOtp2){setOtpErr2("Incorrect OTP.");return;}
    setOtpLoad2(true);
    try{await signIn(pendCred2.email,pendCred2.pass);setOtpMode2(false);}
    catch(e){setOtpErr2("Login failed.");}
    setOtpLoad2(false);
  };

  const doRegister=async()=>{
    if(!reg.companyName||!reg.email||!reg.password||!reg.phone||!reg.pocName){setRegErr("Please fill all required fields (*).");return;}
    if(reg.password!==reg.confirm){setRegErr("Passwords don't match.");return;}
    if(reg.password.length<8){setRegErr("Password must be at least 8 characters.");return;}
    if(!reg.terms){setRegErr("Please accept the Terms & Conditions to continue.");return;}
    setLoading(true);
    try{await onRegister(reg);setRegOk(true);}
    catch(e){setRegErr(e.message);}
    setLoading(false);
  };

  const doForgot=async()=>{
    if(!forgotEmail.trim()){setErr("Enter your email.");return;}
    setLoading(true);
    try{await resetUserPassword(forgotEmail);setForgotSent(true);}
    catch(e){setErr("No account found. Contact help@meitho.com");}
    setLoading(false);
  };

  if(forgotMode) return(
    <div>
      <div style={{fontFamily:FFD,fontSize:20,fontWeight:700,color:"var(--text)",marginBottom:4}}>Reset Password</div>
      <div style={{fontSize:13,color:"var(--muted)",marginBottom:20}}>Enter your registered email address</div>
      {forgotSent?(
        <div>
          <div style={{background:"rgba(255,49,49,0.1)",border:"1px solid rgba(255,49,49,0.3)",borderRadius:10,padding:16,fontSize:13,color:"#ff3131",marginBottom:16}}>✓ Reset link sent to {forgotEmail}</div>
          <BtnGhost onClick={()=>setForgotMode(false)} style={{width:"100%",justifyContent:"center"}}>Back to Sign In</BtnGhost>
        </div>
      ):(
        <div>
          <Label>Email Address</Label>
          <Inp value={forgotEmail} onChange={e=>setForgotEmail(e.target.value)} placeholder="you@company.com" style={{marginBottom:err?8:16}}/>
          {err&&<div style={{fontSize:12,color:"#ff3131",marginBottom:12}}>{err}</div>}
          <div style={{display:"flex",gap:10}}>
            <Btn onClick={doForgot} style={{flex:1,justifyContent:"center"}}>{loading?<Spin/>:"Send Reset Link"}</Btn>
            <BtnGhost onClick={()=>setForgotMode(false)}>Back</BtnGhost>
          </div>
        </div>
      )}
    </div>
  );

  if(otpMode2) return(
    <div style={{textAlign:"center",padding:"8px 0"}}>
      <div style={{width:56,height:56,borderRadius:14,background:"linear-gradient(135deg,#ff3131,#ff914d)",display:"flex",alignItems:"center",justifyContent:"center",margin:"0 auto 16px"}}>
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none"><rect x="5" y="11" width="14" height="10" rx="2" stroke="white" strokeWidth="1.5"/><path d="M8 11V7a4 4 0 018 0v4" stroke="white" strokeWidth="1.5" strokeLinecap="round"/></svg>
      </div>
      <div style={{fontFamily:FFD,fontSize:20,fontWeight:800,color:"var(--text)",marginBottom:4}}>2-Step Verification</div>
      <div style={{fontSize:12,color:"var(--muted)",marginBottom:4}}>OTP sent to</div>
      <div style={{fontSize:13,fontWeight:700,color:"var(--brand)",marginBottom:20}}>{pendCred2?.email}</div>
      <div style={{marginBottom:14}}>
        <input value={otp2} onChange={e=>setOtp2(e.target.value.replace(/\D/g,"").slice(0,6))} onKeyDown={e=>e.key==="Enter"&&verifyOTP2()} placeholder="000000" maxLength={6}
          style={{width:"100%",padding:"14px",background:"var(--ibg)",border:"1.5px solid "+(otpErr2?"var(--red)":"var(--b1)"),borderRadius:10,color:"var(--text)",fontSize:24,fontFamily:"monospace",outline:"none",textAlign:"center",letterSpacing:10,boxSizing:"border-box"}}/>
      </div>
      {otpTimer2>0&&<div style={{fontSize:12,color:"var(--muted)",marginBottom:10}}>Expires in <span style={{color:"var(--brand)",fontWeight:700}}>{Math.floor(otpTimer2/60)}:{String(otpTimer2%60).padStart(2,"0")}</span></div>}
      {otpErr2&&<div style={{background:"rgba(255,49,49,0.1)",borderRadius:8,padding:"8px 12px",fontSize:12,color:"#ff3131",marginBottom:12}}>{otpErr2}</div>}
      <Btn onClick={verifyOTP2} style={{width:"100%",justifyContent:"center",marginBottom:12}}>{otpLoad2?<Spin/>:"✓ Verify & Login"}</Btn>
      <div style={{fontSize:12,color:"var(--muted)"}}>
        <button onClick={()=>{if(resend2>0)return;const c=String(Math.floor(100000+Math.random()*900000));setGenOtp2(c);setOtpExp2(Date.now()+300000);setOtpTimer2(300);setResend2(30);setOtp2("");setOtpErr2("");fetch(CF_SEND_CANDIDATE,{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({to:pendCred2.email,subject:"Meitho OTP — "+c,html:`<div style="text-align:center;font-family:Arial;padding:32px;"><h2>Your Meitho OTP</h2><div style="font-size:48px;font-weight:900;color:#ff914d;letter-spacing:8px;font-family:monospace;padding:16px;background:#f8fafc;border-radius:12px;">${c}</div><p>Valid 5 minutes</p></div>`,name:"User"})}).catch(()=>{});}}
          disabled={resend2>0} style={{background:"none",border:"none",color:resend2>0?"var(--muted)":"var(--brand)",cursor:resend2>0?"default":"pointer",fontWeight:700,fontFamily:FF,fontSize:12}}>
          {resend2>0?"Resend in "+resend2+"s":"Resend OTP"}
        </button>
      </div>
      <button onClick={()=>{setOtpMode2(false);setOtp2("");setOtpErr2("");}} style={{background:"none",border:"none",color:"var(--muted)",cursor:"pointer",fontSize:12,fontFamily:FF,marginTop:12}}>← Back to Login</button>
    </div>
  );

  if(mode==="login") return(
    <div>
      <div style={{fontFamily:FFD,fontSize:22,fontWeight:800,color:"var(--text)",marginBottom:2}}>Welcome back</div>
      <div style={{fontSize:13,color:"var(--muted)",marginBottom:24}}>Sign in to your Meitho account</div>
      <div style={{marginBottom:14}}><Label>Email Address</Label><Inp value={email} onChange={e=>setEmail(e.target.value)} placeholder="you@company.com" onKeyDown={e=>e.key==="Enter"&&doLogin()}/></div>
      <div style={{marginBottom:6}}>
        <Label>Password</Label>
        <div style={{position:"relative"}}>
          <Inp type={showPass?"text":"password"} value={pass} onChange={e=>setPass(e.target.value)} placeholder="Password" onKeyDown={e=>e.key==="Enter"&&doLogin()} style={{paddingRight:40}}/>
          <button onClick={()=>setShowPass(s=>!s)} style={{position:"absolute",right:12,top:"50%",transform:"translateY(-50%)",background:"none",border:"none",cursor:"pointer",color:"var(--muted)",padding:0}}>👁</button>
        </div>
      </div>
      <div style={{textAlign:"right",marginBottom:16}}>
        <button onClick={()=>setForgotMode(true)} style={{background:"none",border:"none",color:"#0cc0df",cursor:"pointer",fontSize:12,fontWeight:600,fontFamily:FF}}>Forgot Password?</button>
      </div>
      {err&&<div style={{background:"rgba(255,49,49,0.1)",border:"1px solid rgba(255,49,49,0.3)",borderRadius:8,padding:"10px 12px",fontSize:13,color:"#ff3131",marginBottom:14}}>{err}</div>}
      <Btn onClick={doLogin} style={{width:"100%",justifyContent:"center",marginBottom:16}}>{loading?<Spin/>:"Sign In →"}</Btn>
      <div style={{textAlign:"center",fontSize:13,color:"var(--muted)"}}>
        No account? <button onClick={()=>{onSwitch("register");setRegStep(1);}} style={{background:"none",border:"none",color:"#0cc0df",cursor:"pointer",fontWeight:700,fontFamily:FF,fontSize:13}}>Register Company</button>
      </div>
    </div>
  );

  if(regOk) return(
    <div style={{textAlign:"center",padding:"20px 0"}}>
      <div style={{fontSize:48,marginBottom:12}}>✅</div>
      <div style={{fontFamily:FFD,fontSize:20,fontWeight:700,color:"var(--text)",marginBottom:8}}>Registration Submitted!</div>
      <p style={{fontSize:13,color:"var(--muted)",marginBottom:16,lineHeight:1.7}}>Check your email for confirmation. Account activated within 24 hours.</p>
      <Btn onClick={()=>onSwitch("login")} style={{justifyContent:"center"}}>Back to Sign In</Btn>
    </div>
  );

  const StepBar=()=>(
    <div style={{display:"flex",alignItems:"center",marginBottom:20}}>
      {[{n:1,l:"Company Info"},{n:2,l:"Contact & Login"}].map((s,i)=>(
        <div key={s.n} style={{display:"flex",alignItems:"center",flex:1}}>
          <div style={{display:"flex",flexDirection:"column",alignItems:"center",flex:1}}>
            <div style={{width:26,height:26,borderRadius:"50%",background:regStep>=s.n?"linear-gradient(135deg,#ff3131,#ff914d)":"var(--s2)",border:`2px solid ${regStep>=s.n?"#ff3131":"var(--b1)"}`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:11,fontWeight:700,color:regStep>=s.n?"#fff":"var(--muted)"}}>
              {regStep>s.n?"✓":s.n}
            </div>
            <div style={{fontSize:10,color:regStep>=s.n?"var(--text)":"var(--muted)",fontWeight:600,marginTop:3,whiteSpace:"nowrap"}}>{s.l}</div>
          </div>
          {i===0&&<div style={{height:2,width:40,background:regStep>1?"#ff3131":"var(--b1)",marginBottom:14,flexShrink:0}}/>}
        </div>
      ))}
    </div>
  );

  return(
    <div>
      <div style={{fontFamily:FFD,fontSize:20,fontWeight:800,color:"var(--text)",marginBottom:2}}>Register Company</div>
      <div style={{fontSize:12,color:"var(--muted)",marginBottom:14}}>Step {regStep} of 2 · Join Meitho BGV</div>
      <StepBar/>
      {regStep===1&&(
        <div>
          <div style={{fontSize:10,fontWeight:700,color:"#0cc0df",letterSpacing:1,textTransform:"uppercase",marginBottom:10}}>Company Details</div>
          {[
            {k:"companyName",l:"Company Name *",p:"Acme Corp Pvt Ltd"},
            {k:"industry",l:"Industry",p:"IT / Healthcare / Manufacturing"},
            {k:"gst",l:"GST Number",p:"22AAAAA0000A1Z5"},
            {k:"address",l:"Address *",p:"123, MG Road, Chennai - 600001"},
          ].map(f=>(
            <div key={f.k} style={{marginBottom:10}}>
              <Label>{f.l}</Label>
              <Inp value={reg[f.k]||""} placeholder={f.p} onChange={e=>setReg(p=>({...p,[f.k]:e.target.value}))}/>
            </div>
          ))}
          {regErr&&<div style={{fontSize:12,color:"#ff3131",marginBottom:10}}>{regErr}</div>}
          <Btn onClick={()=>{
            if(!reg.companyName||!reg.address){setRegErr("Company name and address are required.");return;}
            setRegErr("");setRegStep(2);
          }} style={{width:"100%",justifyContent:"center"}}>Next → Contact Details</Btn>
          <div style={{textAlign:"center",fontSize:12,color:"var(--muted)",marginTop:12}}>
            Already registered? <button onClick={()=>onSwitch("login")} style={{background:"none",border:"none",color:"#0cc0df",cursor:"pointer",fontWeight:700,fontFamily:FF,fontSize:12}}>Sign In</button>
          </div>
        </div>
      )}
      {regStep===2&&(
        <div>
          <div style={{fontSize:10,fontWeight:700,color:"#0cc0df",letterSpacing:1,textTransform:"uppercase",marginBottom:10}}>Point of Contact & Login</div>
          {[
            {k:"pocName",l:"Contact Person *",p:"Rajesh Kumar"},
            {k:"phone",l:"Phone *",p:"9876543210"},
            {k:"email",l:"Work Email *",p:"admin@company.com"},
            {k:"password",l:"Password *",p:"Min 8 chars",t:"password"},
            {k:"confirm",l:"Confirm Password *",p:"Re-enter password",t:"password"},
          ].map(f=>(
            <div key={f.k} style={{marginBottom:10}}>
              <Label>{f.l}</Label>
              <Inp type={f.t||"text"} value={reg[f.k]||""} placeholder={f.p} onChange={e=>setReg(p=>({...p,[f.k]:e.target.value}))}/>
            </div>
          ))}
          {/* T&C Checkbox */}
          <div onClick={()=>setReg(p=>({...p,terms:!p.terms}))} style={{display:"flex",gap:10,alignItems:"flex-start",cursor:"pointer",padding:"10px 12px",background:"var(--s2)",border:"1px solid "+(reg.terms?"rgba(255,49,49,0.3)":"var(--b1)"),borderRadius:8,marginBottom:10,userSelect:"none"}}>
            <div style={{width:18,height:18,borderRadius:4,border:"2px solid "+(reg.terms?"#ff3131":"var(--b1)"),background:reg.terms?"#ff3131":"transparent",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,marginTop:1}}>
              {reg.terms&&<svg width="10" height="10" viewBox="0 0 10 10"><path d="M2 5l2.5 2.5L8 3" stroke="white" strokeWidth="1.5" strokeLinecap="round"/></svg>}
            </div>
            <div style={{fontSize:12,color:"var(--muted)",lineHeight:1.6}}>
              I agree to Meitho's <a href="/terms" target="_blank" style={{color:"#0cc0df",textDecoration:"none",fontWeight:600}}>Terms & Conditions</a> and <a href="/privacy" target="_blank" style={{color:"#0cc0df",textDecoration:"none",fontWeight:600}}>Privacy Policy</a>. I consent to verification of candidate data as per DPDP Act 2023.
            </div>
          </div>
          {regErr&&<div style={{background:"rgba(255,49,49,0.1)",borderRadius:8,padding:"8px 12px",fontSize:12,color:"#ff3131",marginBottom:10}}>{regErr}</div>}
          <div style={{display:"flex",gap:8}}>
            <BtnGhost onClick={()=>{setRegErr("");setRegStep(1);}}>← Back</BtnGhost>
            <Btn onClick={doRegister} style={{flex:1,justifyContent:"center"}}>{loading?<Spin/>:"Submit →"}</Btn>
          </div>
        </div>
      )}
    </div>
  );
}

function LandingPageEditor({addToast}) {
  return(
    <div style={{padding:"20px"}}>
      <h2 style={{fontFamily:FFD,fontSize:22,fontWeight:700,color:"var(--text)",marginBottom:8}}>Landing Page Editor</h2>
      <p style={{color:"var(--muted)",fontSize:14}}>Landing page content is managed directly in the code. Contact Meitho support to make changes.</p>
    </div>
  );
}


function Login({onRegister}) {
  const {dark,toggle} = useTheme();
  const [tab,setTab] = useState("login");
  const [email,setEmail] = useState(""); const [pass,setPass] = useState("");
  const [showPass,setShowPass] = useState(false);
  const [err,setErr] = useState(""); const [loading,setLoading] = useState(false);
  const [reg,setReg] = useState({companyName:"",email:"",password:"",confirm:"",plan:"credits",phone:""});
  const [regErr,setRegErr] = useState(""); const [regOk,setRegOk] = useState(false);
  const [showForgot,setShowForgot] = useState(false);
  const [forgotEmail,setForgotEmail] = useState("");
  const [forgotSent,setForgotSent] = useState(false);
  const [forgotErr,setForgotErr] = useState("");
  const [forgotLoading,setForgotLoading] = useState(false);

  const [otpMode,setOtpMode]=useState(false);
  const [otp,setOtp]=useState("");
  const [generatedOtp,setGeneratedOtp]=useState("");
  const [otpExpiry,setOtpExpiry]=useState(null);
  const [otpTimer,setOtpTimer]=useState(0);
  const [pendingCred,setPendingCred]=useState(null);
  const [otpErr,setOtpErr]=useState("");
  const [otpLoading,setOtpLoading]=useState(false);
  const [resendCooldown,setResendCooldown]=useState(0);

  // Timer for OTP
  useEffect(()=>{
    if(otpTimer>0){const t=setTimeout(()=>setOtpTimer(s=>s-1),1000);return()=>clearTimeout(t);}
  },[otpTimer]);
  useEffect(()=>{
    if(resendCooldown>0){const t=setTimeout(()=>setResendCooldown(s=>s-1),1000);return()=>clearTimeout(t);}
  },[resendCooldown]);

  const generate2FAOTP=()=>{
    const otp=String(Math.floor(100000+Math.random()*900000));
    const expiry=Date.now()+5*60*1000; // 5 min
    setGeneratedOtp(otp);
    setOtpExpiry(expiry);
    setOtpTimer(300);
    return otp;
  };

  const send2FAEmail=async(emailAddr,otpCode,name)=>{
    try{
      await fetch(CF_SEND_CANDIDATE,{
        method:"POST",
        headers:{"Content-Type":"application/json"},
        body:JSON.stringify({
          to:emailAddr,
          subject:"Meitho Login OTP — "+otpCode,
          html:`<div style="font-family:Arial,sans-serif;max-width:480px;margin:0 auto;background:#0a0a1a;border-radius:16px;overflow:hidden;">
            <div style="background:linear-gradient(135deg,#ff3131,#ff914d);padding:24px 32px;text-align:center;">
              <div style="font-size:24px;font-weight:900;color:#fff;">Meitho</div>
              <div style="font-size:12px;color:rgba(255,255,255,0.7);margin-top:4px;">Secure Login Verification</div>
            </div>
            <div style="padding:32px;text-align:center;">
              <div style="font-size:14px;color:rgba(255,255,255,0.7);margin-bottom:16px;">Hi ${name||emailAddr},</div>
              <div style="font-size:14px;color:rgba(255,255,255,0.6);margin-bottom:24px;">Your one-time password (OTP) for Meitho login:</div>
              <div style="font-size:48px;font-weight:900;color:#ff914d;letter-spacing:12px;font-family:monospace;background:rgba(255,145,77,0.1);border-radius:12px;padding:16px;margin:0 auto 24px;">${otpCode}</div>
              <div style="font-size:12px;color:rgba(255,255,255,0.4);">Valid for 5 minutes · Do not share this OTP with anyone</div>
              <div style="margin-top:24px;font-size:12px;color:rgba(255,255,255,0.3);">If you did not request this, please ignore this email or contact help@meitho.com</div>
            </div>
            <div style="background:rgba(255,255,255,0.04);padding:16px 32px;text-align:center;font-size:11px;color:rgba(255,255,255,0.3);">
              © 2026 Meitho BGV · meitho.com · help@meitho.com
            </div>
          </div>`,
          name:name||"User",
        })
      });
    }catch(e){console.warn("OTP email failed:",e);}
  };

  // Step 1: User enters email → request OTP
  const doSimpleLogin = async () => {
    if(!email||!pass){setErr("Enter email and password.");return;}
    setLoading(true);setErr("");
    try{await signIn(email,pass);}
    catch(e){setErr(e.code==="auth/invalid-credential"?"Invalid email or password.":"Login failed. Try again.");}
    setLoading(false);
  };

  const requestOTP = async () => {
    if(!email||!email.includes("@")){setErr("Enter a valid email address.");return;}
    setLoading(true);setErr("");
    try{
      // Generate OTP and send directly - no email check needed
      const otpCode=String(Math.floor(100000+Math.random()*900000));
      setGeneratedOtp(otpCode);
      setOtpExpiry(Date.now()+5*60*1000);
      setOtpTimer(300);
      setOtpMode(true);
      setResendCooldown(30);
      // Send OTP email
      fetch(CF_SEND_CANDIDATE,{
        method:"POST",headers:{"Content-Type":"application/json"},
        body:JSON.stringify({
          to:email,
          subject:"Meitho Login OTP — "+otpCode,
          html:`<div style="font-family:Arial,sans-serif;max-width:480px;margin:0 auto;">
            <div style="background:linear-gradient(135deg,#ff3131,#ff914d);padding:24px 32px;text-align:center;border-radius:12px 12px 0 0;">
              <div style="font-size:26px;font-weight:900;color:#fff;">Meitho</div>
              <div style="font-size:12px;color:rgba(255,255,255,0.8);margin-top:4px;">Secure Login Verification</div>
            </div>
            <div style="background:#0a0a1a;padding:32px;text-align:center;border-radius:0 0 12px 12px;">
              <div style="font-size:14px;color:rgba(255,255,255,0.7);margin-bottom:20px;">Your one-time password for Meitho login:</div>
              <div style="font-size:52px;font-weight:900;color:#ff914d;letter-spacing:14px;font-family:monospace;background:rgba(255,145,77,0.1);border-radius:12px;padding:20px;margin-bottom:20px;">${otpCode}</div>
              <div style="font-size:12px;color:rgba(255,255,255,0.4);">Valid for 5 minutes · Do not share this OTP</div>
            </div>
          </div>`,
          name:"User"
        })
      }).catch(()=>{});
    }catch(e){setErr("Failed to send OTP. Try again.");}
    setLoading(false);
  };

  // Step 2: User enters OTP + password → verify both → login
  const doLogin = async () => {
    if(!otp||otp.length!==6){setOtpErr("Enter 6-digit OTP");return;}
    if(Date.now()>otpExpiry){setOtpErr("OTP expired. Click Resend.");return;}
    if(otp!==generatedOtp){setOtpErr("Incorrect OTP. Try again.");return;}
    if(!pass){setOtpErr("Enter your password.");return;}
    setOtpLoading(true);setOtpErr("");
    try{
      await signIn(email,pass);
      setOtpMode(false);
    }catch(e){
      setOtpErr(e.code==="auth/invalid-credential"?"Incorrect password.":"Login failed. Try again.");
    }
    setOtpLoading(false);
  };

  const resendOTP=async()=>{
    if(resendCooldown>0)return;
    const otpCode=String(Math.floor(100000+Math.random()*900000));
    setGeneratedOtp(otpCode);setOtpExpiry(Date.now()+5*60*1000);setOtpTimer(300);setResendCooldown(30);setOtpErr("");setOtp("");
    fetch(CF_SEND_CANDIDATE,{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({to:email,subject:"Meitho OTP — "+otpCode,html:`<div style="text-align:center;font-family:Arial;padding:32px;background:#0a0a1a;border-radius:12px;"><h2 style="color:#fff;">Meitho OTP</h2><div style="font-size:52px;font-weight:900;color:#ff914d;letter-spacing:14px;font-family:monospace;padding:20px;background:rgba(255,145,77,0.1);border-radius:12px;margin:16px 0;">${otpCode}</div><p style="color:rgba(255,255,255,0.5);">Valid 5 minutes · Do not share</p></div>`,name:"User"})}).catch(()=>{});
  };

  const doRegister = async () => {
    if(!reg.companyName||!reg.email||!reg.password){setRegErr("All fields required.");return;}
    if(reg.password!==reg.confirm){setRegErr("Passwords don't match.");return;}
    setLoading(true);
    try { await onRegister(reg); setRegOk(true); }
    catch(e) { setRegErr(e.message); }
    setLoading(false);
  };

  const doForgotPassword = async () => {
    if(!forgotEmail.trim()){setForgotErr("Enter your registered email address.");return;}
    setForgotLoading(true);setForgotErr("");
    try{
      await resetUserPassword(forgotEmail.trim());
      setForgotSent(true);
    }catch(e){
      if(e.code==="auth/user-not-found")setForgotErr("No account found with this email. Contact help@meitho.com");
      else setForgotErr("Failed to send reset email. Try again.");
    }
    setForgotLoading(false);
  };

  const ForgotView = ()=>(
    <div className="fu">
      <button onClick={()=>{setShowForgot(false);setForgotEmail("");setForgotSent(false);setForgotErr("");}} style={{background:"none",border:"none",color:"var(--muted)",cursor:"pointer",fontSize:13,fontFamily:FF,display:"flex",alignItems:"center",gap:6,marginBottom:20,padding:0}}>
        <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M9 3L5 7l4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>Back to Sign In
      </button>
      {!forgotSent?(
        <>
          <div style={{marginBottom:20}}>
            <div style={{width:48,height:48,borderRadius:12,background:"var(--brand)15",border:"1px solid var(--brand)30",display:"flex",alignItems:"center",justifyContent:"center",marginBottom:12}}>
              <svg width="22" height="22" viewBox="0 0 22 22" fill="none"><rect x="2" y="5" width="18" height="13" rx="2" stroke="var(--brand)" strokeWidth="1.5"/><path d="M2 8l9 5 9-5" stroke="var(--brand)" strokeWidth="1.5" strokeLinecap="round"/></svg>
            </div>
            <div style={{fontFamily:FFD,fontWeight:700,fontSize:20,color:"var(--text)",marginBottom:6}}>Forgot Password?</div>
            <div style={{fontSize:13,color:"var(--muted)",lineHeight:1.7}}>Enter the email address registered with your Meitho account. We'll send a password reset link.</div>
          </div>
          <div style={{marginBottom:14}}><Label>Registered Email Address</Label><Inp value={forgotEmail} onChange={e=>{setForgotEmail(e.target.value);setForgotErr("");}} placeholder="you@company.com" onKeyDown={e=>e.key==="Enter"&&doForgotPassword()}/></div>
          {forgotErr&&<div style={{background:"var(--red)10",border:"1px solid var(--red)30",borderRadius:8,padding:"10px 14px",fontSize:13,color:"var(--red)",marginBottom:14}}>{forgotErr}</div>}
          <button onClick={doForgotPassword} style={{width:"100%",padding:"12px",fontSize:14,fontWeight:700,cursor:"pointer",fontFamily:FFD,background:"linear-gradient(90deg,#ff3131,#ff914d)",border:"none",borderRadius:9,color:"#fff",display:"flex",alignItems:"center",justifyContent:"center",gap:8,marginBottom:14}}>
            {forgotLoading?<Spin/>:"Send Reset Link →"}
          </button>
          <div style={{background:"var(--amber)10",border:"1px solid var(--amber)30",borderRadius:8,padding:"12px 14px",fontSize:12,color:"var(--muted)",lineHeight:1.7}}>
            <div style={{fontWeight:600,color:"var(--amber)",marginBottom:4}}>⚠ Don't remember your email?</div>
            Contact us at <a href="mailto:help@meitho.com" style={{color:"var(--brand)",fontWeight:600,textDecoration:"none"}}>help@meitho.com</a> and we'll help you recover your account.
          </div>
        </>
      ):(
        <div style={{textAlign:"center",padding:"16px 0"}}>
          <div style={{width:60,height:60,background:"var(--green)15",border:"2px solid var(--green)40",borderRadius:16,display:"flex",alignItems:"center",justifyContent:"center",margin:"0 auto 16px"}}>
            <svg width="28" height="28" viewBox="0 0 28 28" fill="none"><path d="M6 14l6 6L24 9" stroke="var(--green)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
          </div>
          <div style={{fontFamily:FFD,fontWeight:700,fontSize:18,color:"var(--text)",marginBottom:8}}>Reset Link Sent!</div>
          <p style={{fontSize:13,color:"var(--muted)",lineHeight:1.7,marginBottom:16}}>Check your inbox at <strong style={{color:"var(--text)"}}>{forgotEmail}</strong> — also check spam folder.</p>
          <div style={{background:"var(--s2)",borderRadius:8,padding:"12px 14px",fontSize:12,color:"var(--muted)",lineHeight:1.7,marginBottom:14,textAlign:"left"}}>
            Still not received? Contact <a href="mailto:help@meitho.com" style={{color:"var(--brand)",fontWeight:600,textDecoration:"none"}}>help@meitho.com</a>
          </div>
          <BtnGhost onClick={()=>{setShowForgot(false);setForgotEmail("");setForgotSent(false);}}>Back to Sign In</BtnGhost>
        </div>
      )}
    </div>
  );

  return (
    <div className={dark?"dark":"light"} style={{minHeight:"100vh",background:dark?"#080d14":"#f0f4ff",display:"flex",fontFamily:FF,position:"relative",overflow:"hidden"}}>
      <div style={{position:"fixed",top:-200,left:-200,width:600,height:600,borderRadius:"50%",background:dark?"#2563eb08":"#2563eb0a",pointerEvents:"none"}}/>
      <div style={{position:"fixed",bottom:-150,right:-100,width:500,height:500,borderRadius:"50%",background:dark?"#8b5cf608":"#8b5cf60a",pointerEvents:"none"}}/>
      <div style={{position:"fixed",top:20,right:20,zIndex:10}}><ThemeToggle/></div>

      {/* Left branding */}
      <div className="hide-sm" style={{flex:1,display:"flex",flexDirection:"column",justifyContent:"center",padding:"60px 80px",position:"relative"}}>
        <div style={{maxWidth:500}}>
          <div style={{marginBottom:32}}>
            <div style={{fontFamily:FFD,fontWeight:800,fontSize:72,color:"var(--text)",letterSpacing:-3,lineHeight:.9,marginBottom:8}}>Meitho</div>
            <div style={{display:"flex",alignItems:"center",gap:8}}>
              <div style={{width:40,height:3,background:"linear-gradient(90deg,#ff3131,#ff914d)",borderRadius:2}}/>
              <span style={{fontSize:13,color:"var(--brand)",fontWeight:600,letterSpacing:2,textTransform:"uppercase"}}>Go Beyond the Trace</span>
            </div>
          </div>
          <h1 style={{fontFamily:FFD,fontWeight:700,fontSize:36,color:"var(--text)",lineHeight:1.15,marginBottom:14,letterSpacing:-.5}}>
            Aadhaar Based BGV<br/>
            <span style={{background:"linear-gradient(90deg,#ff3131,#ff914d)",WebkitBackgroundClip:"text",WebkitTextFillColor:"transparent",backgroundClip:"text"}}>Verified by Professionals</span>
          </h1>
          <p style={{color:"var(--muted)",fontSize:15,lineHeight:1.8,marginBottom:40,maxWidth:400}}>All employee records verified by real professionals — trusted, accurate and beyond the resume.</p>
          <div style={{display:"flex",gap:0,marginBottom:36,background:"var(--s1)",borderRadius:14,border:"1px solid var(--b1)",overflow:"hidden"}}>
            {[["100%","Data Verified","var(--brand)"],["2 Credits","Per Search","var(--purple)"],["Real-time","Results","var(--green)"]].map(([v,l,c],i)=>(
              <div key={l} style={{flex:1,padding:"14px 16px",borderRight:i<2?"1px solid var(--b1)":"none",textAlign:"center"}}>
                <div style={{fontFamily:FFD,fontSize:20,fontWeight:700,color:c,marginBottom:2}}>{v}</div>
                <div style={{fontSize:11,color:"var(--muted)",fontWeight:500}}>{l}</div>
              </div>
            ))}
          </div>
          {[{icon:"🕐",title:"Employment Timeline",desc:"Full work history across all companies"},
            {icon:"⭐",title:"Credit System",desc:"Upload records to earn search credits"},
            {icon:"📄",title:"Instant BGV Reports",desc:"Download professional reports instantly"}].map(f=>(
            <div key={f.title} style={{display:"flex",gap:12,alignItems:"center",padding:"10px 14px",background:"var(--s1)",borderRadius:10,border:"1px solid var(--b1)",marginBottom:8}}>
              <span style={{fontSize:18,flexShrink:0}}>{f.icon}</span>
              <div><div style={{fontSize:13,fontWeight:600,color:"var(--text)",marginBottom:1}}>{f.title}</div><div style={{fontSize:12,color:"var(--muted)"}}>{f.desc}</div></div>
            </div>
          ))}
        </div>
      </div>

      {/* Right form */}
      <div style={{width:480,display:"flex",alignItems:"center",justifyContent:"center",padding:"32px 40px"}}>
        <div style={{width:"100%",maxWidth:400}}>
          <div style={{background:"var(--s1)",border:"1px solid var(--b1)",borderRadius:16,padding:28,boxShadow:dark?"0 24px 64px #00000055":"0 8px 48px #1a234412"}}>
            {showForgot?<ForgotView/>:otpMode?(
              <div style={{padding:"8px 0"}}>
                <div style={{textAlign:"center",marginBottom:20}}>
                  <div style={{width:52,height:52,borderRadius:14,background:"linear-gradient(135deg,#ff3131,#ff914d)",display:"flex",alignItems:"center",justifyContent:"center",margin:"0 auto 12px"}}>
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none"><rect x="5" y="11" width="14" height="10" rx="2" stroke="white" strokeWidth="1.5"/><path d="M8 11V7a4 4 0 018 0v4" stroke="white" strokeWidth="1.5" strokeLinecap="round"/></svg>
                  </div>
                  <div style={{fontFamily:FFD,fontSize:18,fontWeight:800,color:"var(--text)",marginBottom:4}}>2-Step Verification</div>
                  <div style={{fontSize:12,color:"var(--muted)"}}>OTP sent to <span style={{color:"var(--brand)",fontWeight:700}}>{email}</span></div>
                </div>
                <div style={{marginBottom:12}}>
                  <Label>Enter 6-digit OTP *</Label>
                  <input value={otp} onChange={e=>setOtp(e.target.value.replace(/\D/g,"").slice(0,6))} placeholder="000000" maxLength={6} autoFocus
                    style={{width:"100%",padding:"14px",background:"var(--ibg)",border:"1.5px solid "+(otpErr?"#ff3131":"var(--b1)"),borderRadius:10,color:"var(--text)",fontSize:26,fontFamily:"monospace",outline:"none",textAlign:"center",letterSpacing:12,boxSizing:"border-box"}}/>
                </div>
                <div style={{marginBottom:8}}>
                  <Label>Password *</Label>
                  <div style={{position:"relative"}}>
                    <Inp type={showPass?"text":"password"} value={pass} onChange={e=>setPass(e.target.value)} placeholder="Enter your password" onKeyDown={e=>e.key==="Enter"&&doLogin()} style={{paddingRight:40}}/>
                    <button onClick={()=>setShowPass(s=>!s)} style={{position:"absolute",right:12,top:"50%",transform:"translateY(-50%)",background:"none",border:"none",cursor:"pointer",color:"var(--muted)",padding:0}}>👁</button>
                  </div>
                </div>
                {otpTimer>0&&<div style={{fontSize:11,color:"var(--muted)",marginBottom:8,textAlign:"center"}}>OTP expires in <span style={{color:"var(--brand)",fontWeight:700}}>{Math.floor(otpTimer/60)}:{String(otpTimer%60).padStart(2,"0")}</span></div>}
                {otpErr&&<div style={{background:"rgba(255,49,49,0.1)",borderRadius:8,padding:"8px 12px",fontSize:12,color:"#ff3131",marginBottom:10}}>{otpErr}</div>}
                <Btn onClick={doLogin} style={{width:"100%",justifyContent:"center",marginBottom:10}}>{otpLoading?<Spin/>:"✓ Verify & Login →"}</Btn>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                  <button onClick={()=>{setOtpMode(false);setOtp("");setOtpErr("");setPass("");}} style={{background:"none",border:"none",color:"var(--muted)",cursor:"pointer",fontSize:12,fontFamily:FF}}>← Back</button>
                  <button onClick={resendOTP} disabled={resendCooldown>0} style={{background:"none",border:"none",color:resendCooldown>0?"var(--muted)":"var(--brand)",cursor:resendCooldown>0?"default":"pointer",fontWeight:700,fontFamily:FF,fontSize:12}}>
                    {resendCooldown>0?"Resend in "+resendCooldown+"s":"Resend OTP"}
                  </button>
                </div>
              </div>
            ):(
              <>
                <div style={{display:"flex",background:"var(--s2)",border:"1px solid var(--b1)",borderRadius:9,padding:3,marginBottom:24}}>
                  {[["login","Sign In"],["register","Register Company"]].map(([t,l])=>(
                    <button key={t} onClick={()=>{setTab(t);setErr("");setRegErr("");}} style={{flex:1,padding:"9px 4px",border:"none",borderRadius:7,cursor:"pointer",fontFamily:FF,fontWeight:600,fontSize:13,transition:"all .2s",background:tab===t?"linear-gradient(90deg,#ff3131,#ff914d)":"transparent",color:tab===t?"#fff":"var(--muted)"}}>{l}</button>
                  ))}
                </div>
                {tab==="login"?(
                  <div className="fu">
                    <div style={{marginBottom:20}}><div style={{fontFamily:FFD,fontWeight:700,fontSize:22,color:"var(--text)",marginBottom:3}}>Welcome back</div><div style={{fontSize:13,color:"var(--muted)"}}>Sign in securely with 2-step verification</div></div>
                    <div style={{marginBottom:12}}><Label>Email Address</Label><Inp value={email} onChange={e=>setEmail(e.target.value)} placeholder="you@company.com" onKeyDown={e=>e.key==="Enter"&&doSimpleLogin()}/></div>
                    <div style={{marginBottom:6}}>
                      <Label>Password</Label>
                      <div style={{position:"relative"}}>
                        <Inp type={showPass?"text":"password"} value={pass} onChange={e=>setPass(e.target.value)} placeholder="Enter your password" onKeyDown={e=>e.key==="Enter"&&doSimpleLogin()} style={{paddingRight:40}}/>
                        <button onClick={()=>setShowPass(s=>!s)} style={{position:"absolute",right:12,top:"50%",transform:"translateY(-50%)",background:"none",border:"none",cursor:"pointer",color:"var(--muted)",padding:0}}>👁</button>
                      </div>
                    </div>
                    <div style={{textAlign:"right",marginBottom:14}}>
                      <button onClick={()=>{setShowForgot(true);setForgotEmail(email);}} style={{background:"none",border:"none",color:"var(--brand)",cursor:"pointer",fontSize:12,fontWeight:600,fontFamily:FF,padding:0}}>Forgot Password?</button>
                    </div>
                    {err&&<div style={{background:"var(--red)10",border:"1px solid var(--red)30",borderRadius:8,padding:"10px 12px",fontSize:13,color:"var(--red)",marginBottom:14,display:"flex",gap:8,alignItems:"center"}}><svg width="13" height="13" viewBox="0 0 14 14" fill="none"><circle cx="7" cy="7" r="6" stroke="currentColor" strokeWidth="1.3"/><path d="M7 4v3.5M7 9.5v.5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/></svg>{err}</div>}
                    <button onClick={doSimpleLogin} style={{width:"100%",padding:"13px",fontSize:14,fontWeight:700,cursor:"pointer",fontFamily:FFD,background:"linear-gradient(90deg,#ff3131,#ff914d)",border:"none",borderRadius:9,color:"#fff",display:"flex",alignItems:"center",justifyContent:"center",gap:8}}>
                      {loading?<Spin/>:<>Sign In <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M3 7h8M7 3l4 4-4 4" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg></>}
                    </button>
                  </div>
                ):regOk?(
                  <div className="fu" style={{textAlign:"center",padding:"28px 10px"}}>
                    <div style={{width:56,height:56,background:"var(--green)15",border:"2px solid var(--green)40",borderRadius:16,display:"flex",alignItems:"center",justifyContent:"center",margin:"0 auto 16px"}}>
                      <svg width="26" height="26" viewBox="0 0 26 26" fill="none"><path d="M5 13l5.5 5.5L21 8" stroke="var(--green)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                    </div>
                    <div style={{fontFamily:FFD,fontWeight:700,fontSize:18,color:"var(--text)",marginBottom:6}}>Registration Submitted!</div>
                    <p style={{color:"var(--muted)",fontSize:13,marginBottom:20,lineHeight:1.7}}>Pending admin approval. You'll receive an email once activated.</p>
                    <Btn onClick={()=>{setTab("login");setRegOk(false);}}>Back to Sign In</Btn>
                  </div>
                ):(
                  <div className="fu">
                    <div style={{marginBottom:16}}><div style={{fontFamily:FFD,fontWeight:700,fontSize:20,color:"var(--text)",marginBottom:3}}>Register Company</div><div style={{fontSize:13,color:"var(--muted)"}}>Create your company account</div></div>
                    {[{k:"companyName",l:"Company Name",p:"Acme Corp Pvt Ltd"},{k:"email",l:"Work Email",p:"admin@company.com"},{k:"phone",l:"Phone Number",p:"9876543210"},{k:"password",l:"Password",p:"Min 8 chars",t:"password"},{k:"confirm",l:"Confirm Password",p:"Re-enter password",t:"password"}].map(f=>(
                      <div key={f.k} style={{marginBottom:10}}><Label>{f.l}</Label><Inp type={f.t||"text"} value={reg[f.k]} placeholder={f.p} onChange={e=>setReg(p=>({...p,[f.k]:e.target.value}))}/></div>
                    ))}
                    <div style={{marginBottom:14}}><Label>Plan</Label>
                      <Sel value={reg.plan} onChange={e=>setReg(p=>({...p,plan:e.target.value}))}>
                        <option value="credits">Pay-as-you-go (Credits)</option>
                        <option value="subscription">Subscription — ₹2,999/mo</option>
                        <option value="enterprise">Enterprise</option>
                      </Sel>
                    </div>
                    {regErr&&<div style={{background:"var(--red)10",border:"1px solid var(--red)30",borderRadius:7,padding:"10px 12px",fontSize:13,color:"var(--red)",marginBottom:12}}>{regErr}</div>}
                    <button onClick={doRegister} style={{width:"100%",padding:"13px",fontSize:14,fontWeight:700,cursor:"pointer",fontFamily:FFD,background:"linear-gradient(90deg,#ff3131,#ff914d)",border:"none",borderRadius:9,color:"#fff",display:"flex",alignItems:"center",justifyContent:"center",gap:8}} onMouseEnter={e=>e.currentTarget.style.opacity=".9"} onMouseLeave={e=>e.currentTarget.style.opacity="1"}>
                      {loading?<Spin/>:"Register Company →"}
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
          <div style={{textAlign:"center",marginTop:16,fontSize:11,color:"var(--muted)"}}>
            © 2026 Meitho · <a href="mailto:help@meitho.com" style={{color:"var(--brand)",textDecoration:"none"}}>help@meitho.com</a>
          </div>
        </div>
      </div>
    </div>
  );
}
/* ══════════════════════════════════════════════════════════════
   SIDEBAR
══════════════════════════════════════════════════════════════ */
function timeAgo(iso){
  if(!iso)return "";
  const diff=(Date.now()-new Date(iso).getTime())/1000;
  if(diff<60)return "just now";
  if(diff<3600)return Math.floor(diff/60)+"m ago";
  if(diff<86400)return Math.floor(diff/3600)+"h ago";
  if(diff<604800)return Math.floor(diff/86400)+"d ago";
  return new Date(iso).toLocaleDateString("en-IN",{day:"numeric",month:"short"});
}

const NOTIF_ICONS={
  new_application:"📄",feedback_submitted:"⭐",offer_pending:"📝",document_uploaded:"📎",
  low_wallet:"💰",support_reply:"💬",hr_created:"👤",offer_approved:"✅",offer_rejected:"❌",
  company_pending:"🏢",candidate_submitted:"🧾",demo_request:"📞",support_ticket:"🎫",default:"🔔",
};

function NotificationBell({user,company,isSuperAdmin,setPage}){
  const companyId=user?.companyId||company?.id||"";
  const [notifs,setNotifs]=useState([]);
  const [open,setOpen]=useState(false);
  const boxRef=React.useRef(null);

  useEffect(()=>{
    if(!user)return;
    let unsub=null;
    try{
      const q=isSuperAdmin
        ? query(collection(db,"notifications"),where("targetRole","==","superadmin"))
        : query(collection(db,"notifications"),where("companyId","==",companyId));
      unsub=onSnapshot(q,(snap)=>{
        let data=snap.docs.map(d=>({id:d.id,...d.data()}));
        if(!isSuperAdmin){
          const myRole=(user.role==="admin"||user.companyAdmin===true)?"admin":"hr";
          data=data.filter(n=>n.targetUserId===user.id||n.targetRole==="all"||n.targetRole===myRole);
        }
        data.sort((a,b)=>new Date(b.createdAt||0)-new Date(a.createdAt||0));
        setNotifs(data.slice(0,40));
      },(e)=>console.warn("Notifications load error:",e.message));
    }catch(e){console.warn("Notifications query error:",e);}
    return()=>{if(unsub)unsub();};
  },[companyId,isSuperAdmin,user?.id]);

  useEffect(()=>{
    const onClick=(e)=>{if(boxRef.current&&!boxRef.current.contains(e.target))setOpen(false);};
    document.addEventListener("mousedown",onClick);
    return()=>document.removeEventListener("mousedown",onClick);
  },[]);

  const unreadCount=notifs.filter(n=>!n.read).length;

  const markRead=async(n)=>{
    if(!n.read){try{await updateDoc(doc(db,"notifications",n.id),{read:true});}catch(e){}}
    if(n.link&&setPage){setPage(n.link);setOpen(false);}
  };

  const markAllRead=async()=>{
    const unread=notifs.filter(n=>!n.read);
    for(const n of unread){try{await updateDoc(doc(db,"notifications",n.id),{read:true});}catch(e){}}
  };

  return(
    <div ref={boxRef} style={{position:"relative"}}>
      <button onClick={()=>setOpen(o=>!o)} title="Notifications" style={{position:"relative",background:"var(--s1)",backdropFilter:"blur(14px) saturate(180%)",WebkitBackdropFilter:"blur(14px) saturate(180%)",border:"1px solid var(--b1)",borderRadius:99,width:38,height:38,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",color:"var(--text)",boxShadow:"0 8px 24px rgba(0,0,0,0.12), inset 0 1px 0 rgba(255,255,255,0.15)",transition:"transform .15s ease, box-shadow .15s ease"}}
        onMouseEnter={e=>{e.currentTarget.style.transform="translateY(-1px)";e.currentTarget.style.boxShadow="0 12px 30px rgba(0,0,0,0.18), inset 0 1px 0 rgba(255,255,255,0.2)";}}
        onMouseLeave={e=>{e.currentTarget.style.transform="translateY(0)";e.currentTarget.style.boxShadow="0 8px 24px rgba(0,0,0,0.12), inset 0 1px 0 rgba(255,255,255,0.15)";}}>
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none"><path d="M18 8a6 6 0 10-12 0c0 7-3 9-3 9h18s-3-2-3-9" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/><path d="M13.7 21a2 2 0 01-3.4 0" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/></svg>
        {unreadCount>0&&<span style={{position:"absolute",top:-3,right:-3,background:"#ff3131",color:"#fff",fontSize:9,fontWeight:800,borderRadius:99,minWidth:16,height:16,display:"flex",alignItems:"center",justifyContent:"center",padding:"0 3px",border:"2px solid var(--bg)"}}>{unreadCount>9?"9+":unreadCount}</span>}
      </button>
      {open&&(
        <div style={{position:"absolute",top:46,right:0,width:340,maxHeight:440,background:"var(--s1)",border:"1px solid var(--b1)",borderRadius:12,boxShadow:"0 20px 50px rgba(0,0,0,0.25)",zIndex:500,overflow:"hidden",display:"flex",flexDirection:"column"}}>
          <div style={{padding:"12px 14px",borderBottom:"1px solid var(--b1)",display:"flex",justifyContent:"space-between",alignItems:"center"}}>
            <div style={{fontSize:13,fontWeight:700,color:"var(--text)"}}>Notifications</div>
            {unreadCount>0&&<button onClick={markAllRead} style={{background:"none",border:"none",color:"var(--brand)",fontSize:11,fontWeight:600,cursor:"pointer",fontFamily:FF}}>Mark all read</button>}
          </div>
          <div style={{overflowY:"auto",flex:1}}>
            {notifs.length===0?(
              <div style={{padding:"32px 16px",textAlign:"center",color:"var(--muted)",fontSize:12}}>No notifications yet</div>
            ):notifs.map(n=>(
              <div key={n.id} onClick={()=>markRead(n)} style={{display:"flex",gap:10,padding:"11px 14px",borderBottom:"1px solid var(--b1)",cursor:"pointer",background:n.read?"transparent":"var(--s2)"}}>
                <div style={{fontSize:16,flexShrink:0}}>{NOTIF_ICONS[n.type]||NOTIF_ICONS.default}</div>
                <div style={{flex:1,minWidth:0}}>
                  <div style={{fontSize:12.5,fontWeight:n.read?500:700,color:"var(--text)"}}>{n.title}</div>
                  <div style={{fontSize:11.5,color:"var(--muted)",marginTop:2,overflow:"hidden",textOverflow:"ellipsis",display:"-webkit-box",WebkitLineClamp:2,WebkitBoxOrient:"vertical"}}>{n.message}</div>
                  <div style={{fontSize:10,color:"var(--muted)",marginTop:4}}>{timeAgo(n.createdAt)}</div>
                </div>
                {!n.read&&<div style={{width:7,height:7,borderRadius:"50%",background:"var(--brand)",flexShrink:0,marginTop:5}}/>}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/* Text-only sidebar navigation with a liquid highlight that glides between items */
const NAV_GROUPS=[
  ["Overview",["dashboard"]],
  ["Verification",["search","candverify","refcheck","addressverify","bulkverify"]],
  ["Meitho Select",["ats_"]],
  ["Records",["history","analytics","reports","data","empladmin"]],
  ["Money",["wallet","walletadmin","plans"]],
  ["Manage",["profile","hrms","approvals","companies","admins","apiconfig","integrations","landing","setup","support"]],
];
const navGroupOf=(id)=>{
  for(const [g,ids] of NAV_GROUPS){if(ids.some(x=>x.endsWith("_")?id.startsWith(x):x===id))return g;}
  return "Manage";
};
const navPlain=(label)=>String(label||"").replace(/[\u{1F300}-\u{1FAFF}\u{2600}-\u{27BF}\u{FE0F}]/gu,"").replace(/^↳\s*/,"").trim();
function SidebarNav({nav,page,onPick,roomy=false}){
  const refs=useRef({});
  const prev=useRef(null);
  const timer=useRef(null);
  const [pill,setPill]=useState({top:0,height:0,show:false,instant:true});
  const groups=NAV_GROUPS.map(([g])=>[g,nav.filter(n=>navGroupOf(n.id)===g)]).filter(([,items])=>items.length);
  const navKey=nav.map(n=>n.id).join("|");
  const move=()=>{
    const el=refs.current[page];
    if(!el){setPill(p=>({...p,show:false}));prev.current=null;return;}
    const t=el.offsetTop,h=el.offsetHeight;
    const reduce=window.matchMedia&&window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    clearTimeout(timer.current);
    if(!prev.current||reduce){setPill({top:t,height:h,show:true,instant:true});}
    else{
      // stretch toward the new item first, then settle: gives the liquid feel
      const pT=prev.current.top,pB=prev.current.top+prev.current.h;
      const down=t>=pT;
      const top=down?pT:Math.max(t,pT-60),bottom=down?Math.min(t+h,pB+60):pB;
      setPill({top,height:bottom-top,show:true,instant:false});
      timer.current=setTimeout(()=>setPill({top:t,height:h,show:true,instant:false}),150);
    }
    prev.current={top:t,h};
  };
  React.useLayoutEffect(()=>{move();},[page,navKey]);
  useEffect(()=>{
    const onR=()=>{const el=refs.current[page];if(el){prev.current={top:el.offsetTop,h:el.offsetHeight};setPill({top:el.offsetTop,height:el.offsetHeight,show:true,instant:true});}};
    window.addEventListener("resize",onR);
    return()=>{window.removeEventListener("resize",onR);};
  },[page]);
  useEffect(()=>()=>clearTimeout(timer.current),[]);
  return(
    <div style={{position:"relative"}}>
      <style>{`.liq-item{transition:color .2s,background .2s}.liq-item:hover:not(.liq-on){background:var(--s2)!important;color:var(--text)!important}.liq-item:focus-visible{outline:2px solid #ff914d;outline-offset:-2px}`}</style>
      <div aria-hidden="true" style={{position:"absolute",left:0,right:0,top:pill.top,height:pill.height,borderRadius:10,background:"linear-gradient(90deg,#ff3131,#ff914d)",boxShadow:"0 6px 18px rgba(255,49,49,0.28)",opacity:pill.show?1:0,pointerEvents:"none",transition:pill.instant?"none":"top .3s cubic-bezier(.34,1.45,.5,1),height .3s cubic-bezier(.34,1.45,.5,1),opacity .2s"}}/>
      {groups.map(([g,items])=>(
        <div key={g} style={{marginBottom:6}}>
          <div style={{fontSize:9.5,fontWeight:700,letterSpacing:1,textTransform:"uppercase",color:"var(--muted)",opacity:.75,padding:"10px 12px 4px"}}>{g}</div>
          {items.map(n=>{
            const on=page===n.id;
            const sub=String(n.label||"").startsWith("↳");
            return(
              <button key={n.id} ref={el=>{refs.current[n.id]=el;}} className={"liq-item"+(on?" liq-on":"")} onClick={()=>onPick(n.id)}
                style={{position:"relative",zIndex:1,display:"block",width:"100%",textAlign:"left",border:"none",background:"transparent",cursor:"pointer",borderRadius:10,fontFamily:FF,
                  padding:roomy?(sub?"9px 12px 9px 26px":"10px 12px"):(sub?"7px 12px 7px 26px":"8px 12px"),
                  fontSize:roomy?(sub?12.5:13.5):(sub?11.5:12.5),fontWeight:on?700:500,letterSpacing:.1,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis",
                  color:on?"#fff":"var(--muted)"}}>
                {navPlain(n.label)}
              </button>
            );
          })}
        </div>
      ))}
    </div>
  );
}

function Sidebar({user,page,setPage,onLogout,company}) {
  const {dark,toggle} = useTheme();
  const [collapsed,setCollapsed]=useState(false);
  const [mobileOpen,setMobileOpen]=useState(false);
  const isSuperAdmin=user.role==="superadmin";

  const ICONS = {
    dashboard: <svg width="17" height="17" viewBox="0 0 24 24" fill="none"><rect x="3" y="3" width="7" height="7" rx="2" fill="currentColor" fillOpacity=".15" stroke="currentColor" strokeWidth="1.5"/><rect x="14" y="3" width="7" height="7" rx="2" fill="currentColor" fillOpacity=".3" stroke="currentColor" strokeWidth="1.5"/><rect x="3" y="14" width="7" height="7" rx="2" fill="currentColor" fillOpacity=".3" stroke="currentColor" strokeWidth="1.5"/><rect x="14" y="14" width="7" height="7" rx="2" fill="currentColor" fillOpacity=".15" stroke="currentColor" strokeWidth="1.5"/></svg>,
    search: <svg width="17" height="17" viewBox="0 0 24 24" fill="none"><circle cx="11" cy="11" r="7" fill="currentColor" fillOpacity=".1" stroke="currentColor" strokeWidth="1.5"/><circle cx="11" cy="11" r="3" fill="currentColor" fillOpacity=".2"/><path d="M16.5 16.5L21 21" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/></svg>,
    upload: <svg width="17" height="17" viewBox="0 0 24 24" fill="none"><path d="M12 15V4M8 8l4-4 4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/><path d="M4 17v1a2 2 0 002 2h12a2 2 0 002-2v-1" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" opacity=".5"/></svg>,
    bulk: <svg width="17" height="17" viewBox="0 0 24 24" fill="none"><path d="M3 5h18" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/><path d="M3 10h18" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" opacity=".7"/><path d="M3 15h12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" opacity=".5"/><path d="M3 20h8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" opacity=".3"/></svg>,
    history: <svg width="17" height="17" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="9" fill="currentColor" fillOpacity=".08" stroke="currentColor" strokeWidth="1.5"/><path d="M12 7v5.5l3.5 2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>,
    profile: <svg width="17" height="17" viewBox="0 0 24 24" fill="none"><rect x="3" y="3" width="18" height="18" rx="3" fill="currentColor" fillOpacity=".08" stroke="currentColor" strokeWidth="1.5"/><path d="M7 10h6M7 14h10M7 18h4" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" opacity=".7"/></svg>,
    integrations: <svg width="17" height="17" viewBox="0 0 24 24" fill="none"><circle cx="5" cy="12" r="3" fill="currentColor" fillOpacity=".3" stroke="currentColor" strokeWidth="1.3"/><circle cx="19" cy="6" r="3" fill="currentColor" fillOpacity=".15" stroke="currentColor" strokeWidth="1.3"/><circle cx="19" cy="18" r="3" fill="currentColor" fillOpacity=".15" stroke="currentColor" strokeWidth="1.3"/><path d="M8 12h4M12 12l5-5M12 12l5 5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/></svg>,
    approvals: <svg width="17" height="17" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="9" fill="currentColor" fillOpacity=".1" stroke="currentColor" strokeWidth="1.5"/><path d="M8 12l3 3 5-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>,
    companies: <svg width="17" height="17" viewBox="0 0 24 24" fill="none"><rect x="3" y="8" width="18" height="13" rx="2" fill="currentColor" fillOpacity=".1" stroke="currentColor" strokeWidth="1.5"/><path d="M7 8V5.5A2.5 2.5 0 019.5 3h5A2.5 2.5 0 0117 5.5V8" stroke="currentColor" strokeWidth="1.5" opacity=".6"/><path d="M9 13h2M13 13h2M9 17h6" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" opacity=".5"/></svg>,
    users: <svg width="17" height="17" viewBox="0 0 24 24" fill="none"><circle cx="8" cy="7" r="3.5" fill="currentColor" fillOpacity=".15" stroke="currentColor" strokeWidth="1.5"/><path d="M2 20c0-3.3 2.7-6 6-6s6 2.7 6 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" opacity=".8"/><circle cx="17" cy="8" r="2.5" stroke="currentColor" strokeWidth="1.3" opacity=".5"/><path d="M20 19c0-2.2-1.3-4-3-4" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" opacity=".5"/></svg>,
    admins: <svg width="17" height="17" viewBox="0 0 24 24" fill="none"><path d="M12 3l2.4 5 5.6.8-4 4 1 5.5L12 16l-5 2.3 1-5.5-4-4 5.6-.8z" fill="currentColor" fillOpacity=".15" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/></svg>,
    data: <svg width="17" height="17" viewBox="0 0 24 24" fill="none"><ellipse cx="12" cy="6" rx="8" ry="3" fill="currentColor" fillOpacity=".2" stroke="currentColor" strokeWidth="1.5"/><path d="M4 6v5c0 1.66 3.58 3 8 3s8-1.34 8-3V6" stroke="currentColor" strokeWidth="1.5" opacity=".7"/><path d="M4 11v5c0 1.66 3.58 3 8 3s8-1.34 8-3v-5" stroke="currentColor" strokeWidth="1.5" opacity=".4"/></svg>,
    analytics: <svg width="17" height="17" viewBox="0 0 24 24" fill="none"><path d="M3 17l4.5-5 4 3 4.5-7L21 13" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/><circle cx="7.5" cy="12" r="1.5" fill="currentColor" opacity=".6"/><circle cx="11.5" cy="15" r="1.5" fill="currentColor" opacity=".6"/><circle cx="16" cy="8" r="1.5" fill="currentColor" opacity=".6"/></svg>,
    plans: <svg width="17" height="17" viewBox="0 0 24 24" fill="none"><rect x="3" y="6" width="18" height="14" rx="2.5" fill="currentColor" fillOpacity=".08" stroke="currentColor" strokeWidth="1.5"/><path d="M3 10h18" stroke="currentColor" strokeWidth="1.3" opacity=".4"/><path d="M7 6V4M17 6V4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" opacity=".5"/><path d="M8 14h3M13 14h3" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" opacity=".6"/></svg>,
    setup: <svg width="17" height="17" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="3" fill="currentColor" fillOpacity=".2" stroke="currentColor" strokeWidth="1.5"/><path d="M12 2v2M12 20v2M2 12h2M20 12h2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" opacity=".6"/></svg>,
    support: <svg width="17" height="17" viewBox="0 0 24 24" fill="none"><path d="M20 12c0 4.4-3.6 8-8 8H4l-1 1V8a8 8 0 0116 0v4z" fill="currentColor" fillOpacity=".1" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/><path d="M8 10h8M8 14h5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" opacity=".6"/></svg>,
    apiconfig: <svg width="17" height="17" viewBox="0 0 24 24" fill="none"><path d="M4 6h16M4 10h10M4 14h7" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/><circle cx="18" cy="15" r="3.5" fill="currentColor" fillOpacity=".15" stroke="currentColor" strokeWidth="1.3"/><path d="M18 13.5v1.5l1 1" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/></svg>,
    bulkverify: <svg width="17" height="17" viewBox="0 0 24 24" fill="none"><path d="M3 5h18M3 10h18M3 15h11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/><path d="M16 17l2 2 4-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>,
    wallet: <svg width="17" height="17" viewBox="0 0 24 24" fill="none"><rect x="2" y="6" width="20" height="14" rx="2.5" fill="currentColor" fillOpacity=".08" stroke="currentColor" strokeWidth="1.5"/><path d="M2 10h20" stroke="currentColor" strokeWidth="1.3" opacity=".4"/><circle cx="17" cy="15" r="2" fill="currentColor" fillOpacity=".4" stroke="currentColor" strokeWidth="1.2"/></svg>,
    walletadmin: <svg width="17" height="17" viewBox="0 0 24 24" fill="none"><rect x="2" y="6" width="20" height="14" rx="2.5" fill="currentColor" fillOpacity=".08" stroke="currentColor" strokeWidth="1.5"/><path d="M2 10h20" stroke="currentColor" strokeWidth="1.3" opacity=".4"/><circle cx="17" cy="15" r="2" fill="currentColor" fillOpacity=".4" stroke="currentColor" strokeWidth="1.2"/><path d="M7 3l1 3M11 2v3M15 3l-1 3" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" opacity=".5"/></svg>,
    fullbgv: <svg width="17" height="17" viewBox="0 0 24 24" fill="none"><path d="M12 2L4 6v6c0 5.5 3.5 10.7 8 12 4.5-1.3 8-6.5 8-12V6l-8-4z" fill="currentColor" fillOpacity=".12" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/><path d="M8.5 12l2.5 2.5L16 9" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>,
    candverify: <svg width="17" height="17" viewBox="0 0 24 24" fill="none"><rect x="3" y="5" width="18" height="14" rx="2.5" fill="currentColor" fillOpacity=".1" stroke="currentColor" strokeWidth="1.5"/><path d="M3 8l9 6 9-6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" opacity=".7"/><path d="M15 17l1.8 1.8L20 15" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>,
    refcheck: <svg width="17" height="17" viewBox="0 0 24 24" fill="none"><path d="M20 12c0 4.4-3.6 8-8 8H4l-1 1V8a8 8 0 0116 0v4z" fill="currentColor" fillOpacity=".1" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/><circle cx="9" cy="11" r="1" fill="currentColor"/><circle cx="12" cy="11" r="1" fill="currentColor"/><circle cx="15" cy="11" r="1" fill="currentColor"/></svg>,
    landing: <svg width="17" height="17" viewBox="0 0 24 24" fill="none"><rect x="2" y="3" width="20" height="16" rx="2.5" fill="currentColor" fillOpacity=".08" stroke="currentColor" strokeWidth="1.5"/><path d="M2 7h20" stroke="currentColor" strokeWidth="1.3" opacity=".4"/><circle cx="5" cy="5" r="1" fill="currentColor" opacity=".5"/><circle cx="8.5" cy="5" r="1" fill="currentColor" opacity=".4"/><path d="M6 12h12M6 15h8" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" opacity=".5"/></svg>,
    empladmin: <svg width="17" height="17" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="8" r="4" fill="currentColor" fillOpacity=".15" stroke="currentColor" strokeWidth="1.5"/><path d="M4 20c0-4 3.6-7 8-7s8 3 8 7" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" opacity=".7"/><path d="M15 5l1.5 1.5-3 3" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" opacity=".6"/></svg>,
    hrms: <svg width="17" height="17" viewBox="0 0 24 24" fill="none"><rect x="3" y="3" width="18" height="18" rx="3" fill="currentColor" fillOpacity=".08" stroke="currentColor" strokeWidth="1.5"/><path d="M9 12h6M12 9v6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" opacity=".7"/><circle cx="12" cy="12" r="2" fill="currentColor" fillOpacity=".2"/></svg>,
  };

  // Admin nav (company admin sees everything)
  const atsEnabled=company?.atsEnabled||false;
  const adminNav2=[
    {id:"dashboard",label:"Dashboard"},
    {id:"search",label:"Search"},
    {id:"wallet",label:"Wallet 💰"},
    {id:"refcheck",label:"Reference Check 📧"},
    {id:"candverify",label:"Candidate Verification 🧾"},
    {id:"addressverify",label:"Address Records 🏠"},
    {id:"bulkverify",label:"Bulk Verify"},
    ...(atsEnabled?[
      {id:"ats_dashboard",label:"Meitho Select 🎯"},
      {id:"ats_jobs",label:"↳ Jobs"},
      {id:"ats_applications",label:"↳ Applications"},
      {id:"ats_pipeline",label:"↳ Pipeline"},
      {id:"ats_approvals",label:"↳ Offer Approvals ✅"},
      {id:"ats_employee_files",label:"↳ Employee Files 🗂️"},
      {id:"ats_offer_template",label:"↳ Offer Template 📄"},
    ]:[]),
    {id:"history",label:"History"},
    {id:"analytics",label:"Analytics 📊"},
    {id:"reports",label:"Reports 📑"},
    {id:"profile",label:"Company Profile"},
    {id:"hrms",label:"HRMS Integration"},
    {id:"support",label:"Support"},
  ];
  // HR user nav (limited access)
  const hrUserNav=[
    {id:"dashboard",label:"Dashboard"},
    {id:"search",label:"Search"},
    {id:"refcheck",label:"Reference Check 📧"},
    {id:"candverify",label:"Candidate Verification 🧾"},
    {id:"addressverify",label:"Address Records 🏠"},
    {id:"bulkverify",label:"Bulk Verify"},
    ...(atsEnabled?[
      {id:"ats_dashboard",label:"Meitho Select 🎯"},
      {id:"ats_jobs",label:"↳ Jobs"},
      {id:"ats_applications",label:"↳ Applications"},
      {id:"ats_pipeline",label:"↳ Pipeline"},
    ]:[]),
    {id:"history",label:"History"},
    {id:"reports",label:"Reports 📑"},
    {id:"support",label:"Support"},
  ];
  const isAdminNav=user?.role==="admin"||user?.companyAdmin===true||user?.role==="superadmin";
  const hrNav=isAdminNav?adminNav2:hrUserNav;

  const adminNav=[
    {id:"dashboard",label:"Overview"},
    {id:"approvals",label:"Approvals"},
    {id:"companies",label:"Companies"},
    {id:"admins",label:"Admin Users"},
    {id:"search",label:"Search Employee"},
    {id:"refcheck",label:"Reference Check 📧"},
    {id:"candverify",label:"Candidate Verification 🧾"},
    {id:"addressverify",label:"Address Records 🏠"},
    {id:"history",label:"Search History"},
    {id:"walletadmin",label:"Wallet Mgmt 💰"},
    {id:"data",label:"Employee DB"},
    {id:"empladmin",label:"Employee Admin 👑"},
    {id:"bulkverify",label:"Bulk Verify"},
    {id:"ats_overview",label:"Meitho Select 🎯"},
    {id:"analytics",label:"Analytics"},
    {id:"reports",label:"Reports 📑"},
    {id:"plans",label:"Plans"},
    {id:"apiconfig",label:"API Config"},
    {id:"integrations",label:"Integrations"},
    {id:"support",label:"Support Inbox"},
    {id:"landing",label:"Landing Page"},
    {id:"setup",label:"Setup Guide"},
  ];

  const nav=isSuperAdmin?adminNav:hrNav;
  const w=collapsed?64:220;

  const SidebarContent=()=>(
    <div style={{display:"flex",flexDirection:"column",height:"100%"}}>
      {/* Logo + Toggle */}
      <div style={{padding:"12px 10px",borderBottom:"1px solid var(--b1)",display:"flex",alignItems:"center",justifyContent:collapsed?"center":"space-between",flexShrink:0,minHeight:56}}>
        {!collapsed&&<div style={{display:"flex",alignItems:"center",gap:8}}>
          <div style={{width:28,height:28,background:"linear-gradient(135deg,#ff3131,#ff914d)",borderRadius:7,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,boxShadow:"0 3px 10px rgba(255,49,49,0.3)"}}>
            <svg width="15" height="15" viewBox="0 0 20 20" fill="none"><path d="M10 2L4 5v5c0 3.55 2.57 6.87 6 7.74C13.43 16.87 16 13.55 16 10V5l-6-3z" fill="white"/><path d="M7.5 10l2 2 3-3" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
          </div>
          <div>
            <div style={{fontFamily:FFD,fontWeight:900,fontSize:15,color:"var(--text)",letterSpacing:-.5,lineHeight:1}}>Meitho</div>
            <div style={{fontSize:9.5,color:"#0cc0df",fontWeight:700,letterSpacing:.3,marginTop:2}}>Go beyond trace</div>
          </div>
        </div>}
        {collapsed&&<div style={{width:28,height:28,background:"linear-gradient(135deg,#ff3131,#ff914d)",borderRadius:7,display:"flex",alignItems:"center",justifyContent:"center",boxShadow:"0 3px 10px rgba(255,49,49,0.3)"}}>
          <svg width="15" height="15" viewBox="0 0 20 20" fill="none"><path d="M10 2L4 5v5c0 3.55 2.57 6.87 6 7.74C13.43 16.87 16 13.55 16 10V5l-6-3z" fill="white"/></svg>
        </div>}
        {collapsed&&<div/>}
      </div>

      {/* Collapse toggle when collapsed */}

      {/* Nav */}
      <nav style={{flex:1,padding:"4px 8px 8px",overflowY:"auto",overflowX:"hidden"}}>
        <SidebarNav nav={nav} page={page} onPick={(id)=>{setPage(id);setMobileOpen(false);}}/>
      </nav>

      {/* Bottom */}
      <div style={{padding:"6px 6px 8px",borderTop:"1px solid var(--b1)",flexShrink:0}}>
        {!collapsed&&<div style={{display:"flex",alignItems:"center",gap:7,padding:"7px 8px",marginBottom:6,background:"var(--s2)",borderRadius:8}}>
          <div style={{width:26,height:26,background:isSuperAdmin?"linear-gradient(135deg,#ff3131,#ff914d)":"var(--brand)",borderRadius:6,display:"flex",alignItems:"center",justifyContent:"center",fontSize:11,color:"#fff",fontWeight:700,fontFamily:FFD,flexShrink:0}}>{(user.name||"U")[0].toUpperCase()}</div>
          <div style={{overflow:"hidden",flex:1,minWidth:0}}>
            <div style={{fontSize:11,fontWeight:600,color:"var(--text)",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{user.name||user.email}</div>
            <div style={{fontSize:9,color:isSuperAdmin?"#ff3131":"#0cc0df",fontWeight:700,letterSpacing:.3}}>{isSuperAdmin?"👑 Super Admin":"🏢 HR Staff"}</div>
          </div>
        </div>}
        {collapsed&&<div style={{width:26,height:26,background:isSuperAdmin?"linear-gradient(135deg,#ff3131,#ff914d)":"var(--brand)",borderRadius:6,display:"flex",alignItems:"center",justifyContent:"center",fontSize:11,color:"#fff",fontWeight:700,fontFamily:FFD,margin:"0 auto 6px"}}>{(user.name||"U")[0].toUpperCase()}</div>}
        <div style={{display:"flex",gap:4,flexDirection:collapsed?"column":"row",alignItems:"center"}}>
          <button onClick={toggle} title="Toggle theme" style={{flex:1,background:"var(--s2)",border:"1px solid var(--b1)",borderRadius:6,color:"var(--muted)",padding:"5px 0",cursor:"pointer",fontSize:10,fontFamily:FF,display:"flex",alignItems:"center",justifyContent:"center",gap:3,width:collapsed?28:undefined,height:collapsed?24:undefined}}>
            {dark?"☀":"🌙"}{!collapsed&&(dark?" Light":" Dark")}
          </button>
          <button onClick={onLogout} title="Sign out" style={{flex:1,background:"var(--s2)",border:"1px solid var(--b1)",borderRadius:6,color:"var(--muted)",padding:"5px 0",cursor:"pointer",fontSize:10,fontFamily:FF,display:"flex",alignItems:"center",justifyContent:"center",gap:3,width:collapsed?28:undefined,height:collapsed?24:undefined}}>
            <svg width="11" height="11" viewBox="0 0 16 16" fill="none"><path d="M6 2H3a1 1 0 00-1 1v10a1 1 0 001 1h3M10 11l3-3-3-3M13 8H6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
            {!collapsed&&" Out"}
          </button>
        </div>
      </div>
    </div>
  );

  return(
    <>
      {/* Floating notification bell — glossy, pinned top-right of the viewport, same on mobile and desktop. Offset from the far corner so it doesn't collide with the theme toggle already living there in the mobile top bar. */}
      <div style={{position:"fixed",top:12,right:66,zIndex:600}}>
        <NotificationBell user={user} company={company} isSuperAdmin={isSuperAdmin} setPage={setPage}/>
      </div>

      {/* Mobile top bar */}
      <div className="show-sm" style={{position:"fixed",top:0,left:0,right:0,height:56,background:"var(--s1)",borderBottom:"1px solid var(--b1)",display:"flex",alignItems:"center",justifyContent:"space-between",padding:"0 16px",zIndex:300}}>
        <button onClick={()=>setMobileOpen(o=>!o)} style={{background:"transparent",border:"none",cursor:"pointer",color:"var(--text)",fontSize:22,display:"flex",alignItems:"center"}}>☰</button>
        <div style={{display:"flex",alignItems:"center",gap:8}}>
          <div style={{width:26,height:26,background:"linear-gradient(135deg,#ff3131,#ff914d)",borderRadius:6,display:"flex",alignItems:"center",justifyContent:"center"}}>
            <svg width="14" height="14" viewBox="0 0 20 20" fill="none"><path d="M10 2L4 5v5c0 3.55 2.57 6.87 6 7.74C13.43 16.87 16 13.55 16 10V5l-6-3z" fill="white"/></svg>
          </div>
          <span style={{fontFamily:FFD,fontWeight:800,fontSize:16,color:"var(--text)"}}>Meitho</span>
        </div>
        <div style={{display:"flex",alignItems:"center",gap:8}}>
          <ThemeToggle/>
        </div>
      </div>

      {/* Mobile overlay */}
      {mobileOpen&&<div onClick={()=>setMobileOpen(false)} className="show-sm" style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.5)",zIndex:400,backdropFilter:"blur(4px)"}}/>}

      {/* Mobile sidebar */}
      <div className="show-sm sidebar-glass" style={{position:"fixed",top:0,left:0,bottom:0,width:260,zIndex:500,transform:mobileOpen?"translateX(0)":"translateX(-100%)",transition:"transform .3s cubic-bezier(.22,.68,0,1.2)"}}>
        <div style={{height:"100%",overflowY:"auto"}}>
          <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"14px 14px",borderBottom:"1px solid var(--b1)"}}>
            <div style={{display:"flex",alignItems:"center",gap:8}}>
              <div style={{width:30,height:30,background:"linear-gradient(135deg,#ff3131,#ff914d)",borderRadius:8,display:"flex",alignItems:"center",justifyContent:"center"}}>
                <svg width="16" height="16" viewBox="0 0 20 20" fill="none"><path d="M10 2L4 5v5c0 3.55 2.57 6.87 6 7.74C13.43 16.87 16 13.55 16 10V5l-6-3z" fill="white"/></svg>
              </div>
              <div><div style={{fontFamily:FFD,fontWeight:800,fontSize:16,color:"var(--text)",lineHeight:1}}>Meitho</div><div style={{fontSize:9.5,color:"#0cc0df",fontWeight:700,letterSpacing:.3,marginTop:2}}>Go beyond trace</div></div>
            </div>
            <button onClick={()=>setMobileOpen(false)} style={{background:"transparent",border:"none",cursor:"pointer",color:"var(--muted)",fontSize:20}}>✕</button>
          </div>

          <nav style={{padding:"6px 10px"}}>
            <SidebarNav roomy nav={nav} page={page} onPick={(id)=>{setPage(id);setMobileOpen(false);}}/>
          </nav>
          <div style={{padding:"8px",borderTop:"1px solid var(--b1)",margin:"8px"}}>
            <button onClick={onLogout} style={{width:"100%",background:"rgba(255,49,49,0.08)",border:"1px solid rgba(255,49,49,0.2)",borderRadius:8,color:"#ff3131",padding:"10px",cursor:"pointer",fontSize:13,fontWeight:600,fontFamily:FF}}>Sign Out</button>
          </div>
        </div>
      </div>

      {/* Desktop sidebar */}
      <div className="hide-sm sidebar-glass" style={{width:w,display:"flex",flexDirection:"column",minHeight:"100vh",flexShrink:0,position:"sticky",top:0,height:"100vh",overflowY:"auto",transition:"width .3s cubic-bezier(.22,.68,0,1.2)"}}>
        {SidebarContent()}
      </div>
    </>
  );
}

/* ══════════════════════════════════════════════════════════════
   SETUP GUIDE PAGE
══════════════════════════════════════════════════════════════ */
/* ══════════════════════════════════════════════════════════════
   SUPER ADMIN — API INTEGRATIONS CONFIG
══════════════════════════════════════════════════════════════ */
function APIConfigPage({addToast}) {
  const {dark}=useTheme();
  const [config,setConfig]=useState(()=>{
    try{return JSON.parse(localStorage.getItem("meitho_api_config"))||{};}catch{return {};}
  });
  const [modal,setModal]=useState(null);
  const [form,setForm]=useState({});
  const [testing,setTesting]=useState(null);
  const [testResult,setTestResult]=useState({});

  const providers=[
    {id:"surepass",name:"Surepass",logo:"🔐",color:"#0cc0df",website:"https://console.surepass.app",desc:"Production API active — UAN Employment History V2, PAN Comprehensive, DigiLocker",fields:[{k:"token",l:"API Bearer Token",p:"eyJhbGciOiJIUzI1NiIsInR5cCI6...",secret:true}],usedFor:["UAN","PAN","DigiLocker","eCourt"]},
    {id:"setu",name:"SETU / DigiLocker",logo:"🏛️",color:"#f5a623",website:"https://setu.co",desc:"Government-backed DigiLocker API — Aadhaar eKYC",fields:[{k:"clientId",l:"Client ID",p:"your-client-id"},{k:"clientSecret",l:"Client Secret",p:"your-client-secret",secret:true},{k:"productInstanceId",l:"Product Instance ID",p:"your-product-instance-id"}],usedFor:["Aadhaar eKYC","DigiLocker"]},
    {id:"zoop",name:"ZOOP",logo:"⚡",color:"#0fba81",website:"https://zoop.one",desc:"EPFO verification and employment history API",fields:[{k:"apiKey",l:"API Key",p:"your-api-key",secret:true},{k:"merchantId",l:"Merchant ID",p:"your-merchant-id"}],usedFor:["UAN","EPFO","PAN"]},
    {id:"decentro",name:"Decentro",logo:"🔵",color:"#9b7aff",website:"https://decentro.tech",desc:"Employment verification via EPFO database",fields:[{k:"clientId",l:"Client ID",p:"your-client-id"},{k:"clientSecret",l:"Client Secret",p:"your-client-secret",secret:true},{k:"moduleCode",l:"Module Code",p:"EMPLOYMENT_VERIFICATION"}],usedFor:["UAN","EPFO"]},
    {id:"razorpay",name:"Razorpay",logo:"💳",color:"#2563eb",website:"https://razorpay.com",desc:"Payment gateway for credit purchases and plan payments",fields:[{k:"keyId",l:"Key ID",p:"rzp_live_xxxxxxxxxxxx"},{k:"keySecret",l:"Key Secret",p:"your-key-secret",secret:true},{k:"webhookSecret",l:"Webhook Secret",p:"your-webhook-secret",secret:true}],usedFor:["Payments","Credit Purchase"]},
  ];

  // Active provider per search type
  const [activeProviders,setActiveProviders]=useState(()=>{
    try{return JSON.parse(localStorage.getItem("meitho_active_providers"))||{uan:"surepass",aadhaar:"surepass",pan:"surepass",payment:"razorpay"};}catch{return {uan:"surepass",aadhaar:"surepass",pan:"surepass",payment:"razorpay"};}
  });

  const saveConfig=(providerId,data)=>{
    const updated={...config,[providerId]:{...data,savedAt:new Date().toISOString()}};
    setConfig(updated);
    localStorage.setItem("meitho_api_config",JSON.stringify(updated));
    addToast("API configuration saved!","success");
    setModal(null);setForm({});
  };

  const setActiveProvider=(type,providerId)=>{
    const updated={...activeProviders,[type]:providerId};
    setActiveProviders(updated);
    localStorage.setItem("meitho_active_providers",JSON.stringify(updated));
    addToast("Active provider updated!","success");
  };

  const testConnection=async(providerId)=>{
    setTesting(providerId);
    await new Promise(r=>setTimeout(r,1500));
    const cfg=config[providerId];
    const hasKeys=cfg&&Object.keys(cfg).length>1;
    setTestResult(p=>({...p,[providerId]:hasKeys?"success":"no_config"}));
    setTesting(null);
    addToast(hasKeys?"✓ Connection test passed!":"⚠ No API keys configured yet","info");
  };

  const isConfigured=(id)=>config[id]&&Object.keys(config[id]).filter(k=>k!=="savedAt").length>0;

  return(
    <div className={dark?"bg-mesh-dark":"bg-mesh-light"} style={{minHeight:"100%",padding:"4px 0"}}>
      {modal&&(
        <Modal title={"Configure — "+modal.name} onClose={()=>{setModal(null);setForm({});}} width={500}>
          <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:20,padding:"12px 14px",background:"var(--brand)08",borderRadius:10,border:"1px solid var(--brand)20"}}>
            <span style={{fontSize:28}}>{modal.logo}</span>
            <div>
              <div style={{fontSize:13,fontWeight:600,color:"var(--text)",marginBottom:2}}>{modal.name}</div>
              <div style={{fontSize:12,color:"var(--muted)"}}>{modal.desc}</div>
              <a href={modal.website} target="_blank" rel="noreferrer" style={{fontSize:11,color:"var(--brand)",textDecoration:"none"}}>{modal.website} →</a>
            </div>
          </div>
          <div style={{display:"flex",flexDirection:"column",gap:12,marginBottom:16}}>
            {modal.fields.map(f=>(
              <div key={f.k}>
                <Label>{f.l}</Label>
                <Inp type={f.secret?"password":"text"} value={form[f.k]||""} onChange={e=>setForm(p=>({...p,[f.k]:e.target.value}))} placeholder={f.p} style={{fontFamily:f.secret?FFM:FF}}/>
              </div>
            ))}
          </div>
          <div style={{background:"var(--amber)10",border:"1px solid var(--amber)30",borderRadius:8,padding:"10px 12px",fontSize:12,color:"var(--amber)",marginBottom:16}}>
            ⚠ API keys are stored locally. For production, use environment variables.
          </div>
          <div style={{display:"flex",gap:10}}>
            <Btn onClick={()=>saveConfig(modal.id,form)} style={{background:"linear-gradient(90deg,#ff3131,#ff914d)"}}>Save Configuration →</Btn>
            <BtnGhost onClick={()=>{setModal(null);setForm({});}}>Cancel</BtnGhost>
          </div>
        </Modal>
      )}

      <div className="fu" style={{marginBottom:24}}>
        <h2 style={{fontFamily:FFD,fontWeight:800,fontSize:26,color:"var(--text)",marginBottom:4,letterSpacing:-.5}}>API Integrations</h2>
        <p style={{color:"var(--muted)",fontSize:14}}>Configure 3rd party APIs for verification and payments</p>
      </div>

      {/* Active Provider Routing */}
      <div className="fu1 glass neon-hover" style={{borderRadius:14,padding:20,border:"1px solid var(--b1)",marginBottom:24}}>
        <div style={{fontFamily:FFD,fontSize:14,fontWeight:700,color:"var(--text)",marginBottom:4}}>Active Provider Routing</div>
        <div style={{fontSize:12,color:"var(--muted)",marginBottom:16}}>Set which provider handles each verification type</div>
        <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(200px,1fr))",gap:12}}>
          {[
            {type:"uan",label:"UAN / EPFO",options:["surepass","zoop","decentro"],icon:"🏦"},
            {type:"aadhaar",label:"Aadhaar",options:["surepass","setu"],icon:"🪪"},
            {type:"pan",label:"PAN",options:["surepass","zoop"],icon:"📋"},
            {type:"payment",label:"Payment Gateway",options:["razorpay"],icon:"💳"},
          ].map(r=>(
            <div key={r.type} style={{background:"var(--s2)",borderRadius:10,padding:"12px 14px",border:"1px solid var(--b1)"}}>
              <div style={{fontSize:11,color:"var(--muted)",fontWeight:700,marginBottom:6}}>{r.icon} {r.label}</div>
              <Sel value={activeProviders[r.type]||r.options[0]} onChange={e=>setActiveProvider(r.type,e.target.value)} style={{fontSize:12}}>
                {r.options.map(o=><option key={o} value={o}>{providers.find(p=>p.id===o)?.name||o}</option>)}
              </Sel>
              <div style={{fontSize:10,marginTop:4,color:isConfigured(activeProviders[r.type]||r.options[0])?"var(--green)":"var(--amber)",fontWeight:600}}>
                {isConfigured(activeProviders[r.type]||r.options[0])?"✓ Configured":"⚠ Not configured"}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Provider Cards */}
      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(300px,1fr))",gap:16}}>
        {providers.map((p,i)=>{
          const cfg=isConfigured(p.id);
          const tr=testResult[p.id];
          return(
            <div key={p.id} className={"fu"+i+" glass neon-hover"} style={{borderRadius:14,padding:20,border:"1px solid var(--b1)",borderTop:"3px solid "+p.color}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:14}}>
                <div style={{display:"flex",gap:10,alignItems:"center"}}>
                  <span style={{fontSize:28}}>{p.logo}</span>
                  <div>
                    <div style={{fontFamily:FFD,fontSize:15,fontWeight:700,color:"var(--text)"}}>{p.name}</div>
                    <div style={{fontSize:11,color:"var(--muted)"}}>{p.website}</div>
                  </div>
                </div>
                <Badge color={cfg?"var(--green)":"var(--muted)"} style={{fontSize:10}}>{cfg?"✓ Configured":"Not Set"}</Badge>
              </div>
              <div style={{fontSize:12,color:"var(--muted)",marginBottom:12,lineHeight:1.6}}>{p.desc}</div>
              <div style={{display:"flex",gap:6,flexWrap:"wrap",marginBottom:14}}>
                {p.usedFor.map(u=><span key={u} style={{background:p.color+"15",color:p.color,border:"1px solid "+p.color+"30",borderRadius:99,padding:"2px 8px",fontSize:10,fontWeight:600}}>{u}</span>)}
              </div>
              {cfg&&config[p.id]?.savedAt&&(
                <div style={{fontSize:10,color:"var(--muted)",marginBottom:10}}>
                  Last updated: {new Date(config[p.id].savedAt).toLocaleDateString("en-IN")}
                </div>
              )}
              {tr&&(
                <div style={{background:tr==="success"?"var(--green)10":"var(--amber)10",border:"1px solid "+(tr==="success"?"var(--green)30":"var(--amber)30"),borderRadius:7,padding:"6px 10px",fontSize:11,color:tr==="success"?"var(--green)":"var(--amber)",marginBottom:10}}>
                  {tr==="success"?"✓ Connection successful!":"⚠ Configure API keys first"}
                </div>
              )}
              <div style={{display:"flex",gap:8}}>
                <Btn onClick={()=>{setModal(p);setForm(config[p.id]||{});}} style={{flex:1,justifyContent:"center",fontSize:12,padding:"8px",background:"linear-gradient(135deg,"+p.color+","+p.color+"cc)"}}>
                  {cfg?"Update Keys":"Configure"}
                </Btn>
                <button onClick={()=>testConnection(p.id)} disabled={testing===p.id} style={{padding:"8px 12px",background:"var(--s2)",border:"1px solid var(--b2)",borderRadius:8,color:"var(--muted)",cursor:"pointer",fontSize:11,fontWeight:600,fontFamily:FF,display:"flex",alignItems:"center",gap:4}}>
                  {testing===p.id?<Spin/>:"Test"}
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {/* Razorpay Setup Guide */}
      <div className="fu4 glass" style={{borderRadius:14,padding:20,border:"1px solid var(--b1)",marginTop:20}}>
        <div style={{fontFamily:FFD,fontSize:14,fontWeight:700,color:"var(--text)",marginBottom:12}}>
          💳 Razorpay Setup Guide
        </div>
        <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(200px,1fr))",gap:12}}>
          {[
            {step:"1",title:"Create Account",desc:"Go to razorpay.com → Sign Up → Verify business"},
            {step:"2",title:"Get API Keys",desc:"Dashboard → Settings → API Keys → Generate Live Key"},
            {step:"3",title:"Add Keys Here",desc:'Click "Configure" on Razorpay card above → paste Key ID & Secret'},
            {step:"4",title:"Test Payment",desc:"Use test mode first: rzp_test_xxxx → verify payment flow"},
          ].map(s=>(
            <div key={s.step} style={{display:"flex",gap:10}}>
              <div style={{width:24,height:24,borderRadius:"50%",background:"var(--brand)",color:"#fff",display:"flex",alignItems:"center",justifyContent:"center",fontSize:12,fontWeight:700,flexShrink:0}}>{s.step}</div>
              <div>
                <div style={{fontSize:12,fontWeight:700,color:"var(--text)",marginBottom:2}}>{s.title}</div>
                <div style={{fontSize:11,color:"var(--muted)",lineHeight:1.5}}>{s.desc}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════
   HRMS BULK VERIFICATION PAGE (Company/HR)
══════════════════════════════════════════════════════════════ */
/* ══════════════════════════════════════════════════════════════
   HRMS BULK VERIFICATION PAGE
══════════════════════════════════════════════════════════════ */

// ============================================================
// ATS COMPONENTS
// ============================================================

// ── ATS Dashboard ──
function ATSDashboard({user,company,addToast,isSuperAdmin,setPage}) {
  const {dark}=useTheme();
  const [jobs,setJobs]=useState([]);
  const [applications,setApplications]=useState([]);
  const [loading,setLoading]=useState(true);
  const companyId=user?.companyId||company?.id||"";

  useEffect(()=>{
    const load=async()=>{
      try{
        const [jobsSnap,appsSnap]=await Promise.all([
          isSuperAdmin?getDocs(collection(db,"ats_jobs")):getDocs(query(collection(db,"ats_jobs"),where("companyId","==",companyId))),
          isSuperAdmin?getDocs(collection(db,"ats_applications")):getDocs(query(collection(db,"ats_applications"),where("companyId","==",companyId))),
        ]);
        setJobs(jobsSnap.docs.map(d=>({id:d.id,...d.data()})));
        setApplications(appsSnap.docs.map(d=>({id:d.id,...d.data()})));
      }catch(e){console.error(e);}
      finally{setLoading(false);}
    };
    load();
  },[companyId]);

  if(loading)return(
    <div style={{textAlign:"center",padding:60,color:"var(--muted)"}}>
      <div style={{fontSize:32,marginBottom:12}}>🎯</div>
      <div>Loading Meitho Select...</div>
    </div>
  );

  // Analytics calculations
  const activeJobs=jobs.filter(j=>j.status==="active");
  const draftJobs=jobs.filter(j=>j.status==="draft");
  const totalApps=applications.length;
  const todayApps=applications.filter(a=>new Date(a.appliedAt||"").toDateString()===new Date().toDateString()).length;
  const thisWeekApps=applications.filter(a=>{
    const d=new Date(a.appliedAt||"");
    const now=new Date();
    return (now-d)/(86400000)<=7;
  }).length;

  const STAGES=["applied","shortlisted","aptitude","interview","selected","document","bgv","offer","joined","rejected"];
  const stageCount={};
  STAGES.forEach(s=>stageCount[s]=applications.filter(a=>(a.stage||"applied")===s).length);

  const stuck=applications.filter(a=>{
    if(!a.stageUpdatedAt||["joined","rejected"].includes(a.stage))return false;
    return (Date.now()-new Date(a.stageUpdatedAt).getTime())/(86400000)>7;
  });

  const bgvInitiated=applications.filter(a=>a.bgvInitiated).length;
  const hired=applications.filter(a=>a.stage==="joined").length;
  const conversionRate=totalApps>0?Math.round((hired/totalApps)*100):0;

  // TAT per stage (avg days spent in each stage)
  const stageTAT={};
  STAGES.forEach(stage=>{
    const stageApps=applications.filter(a=>a.stageHistory?.some(h=>h.stage===stage));
    if(stageApps.length>0){
      const total=stageApps.reduce((sum,a)=>{
        const hist=a.stageHistory||[];
        const entry=hist.find(h=>h.stage===stage);
        const entryIdx=hist.indexOf(entry);
        const next=hist[entryIdx+1];
        if(entry&&next){
          return sum+(new Date(next.timestamp)-new Date(entry.timestamp))/(86400000);
        }
        if(entry&&(a.stage===stage)){
          return sum+(Date.now()-new Date(entry.timestamp))/(86400000);
        }
        return sum;
      },0);
      stageTAT[stage]=Math.round(total/stageApps.length);
    }
  });

  // Job-wise application count
  const jobStats=jobs.map(j=>({
    ...j,
    appCount:applications.filter(a=>a.jobId===j.id).length,
    hired:applications.filter(a=>a.jobId===j.id&&a.stage==="joined").length,
  })).sort((a,b)=>(b.appCount||0)-(a.appCount||0));

  const stageColors={
    applied:"#64748b",shortlisted:"#0cc0df",aptitude:"#8b5cf6",
    interview:"#f59e0b",selected:"#10b981",document:"#ff914d",
    bgv:"#ff3131",offer:"#06b6d4",joined:"#10b981",rejected:"#ef4444"
  };

  return(
    <div>
      {/* Header */}
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:24,flexWrap:"wrap",gap:12}}>
        <div>
          <h2 style={{fontFamily:FFD,fontWeight:900,fontSize:26,color:"var(--text)",marginBottom:4}}>🎯 Meitho Select</h2>
          <p style={{color:"var(--muted)",fontSize:13}}>Recruitment analytics & pipeline overview</p>
        </div>
        <div style={{display:"flex",gap:8}}>
          <button onClick={()=>setPage("ats_jobs")} style={{background:"var(--s2)",border:"1px solid var(--b1)",borderRadius:9,color:"var(--text)",padding:"9px 16px",cursor:"pointer",fontSize:12,fontWeight:600,fontFamily:FF}}>📋 Jobs</button>
          <button onClick={()=>setPage("ats_applications")} style={{background:"var(--s2)",border:"1px solid var(--b1)",borderRadius:9,color:"var(--text)",padding:"9px 16px",cursor:"pointer",fontSize:12,fontWeight:600,fontFamily:FF}}>👥 Applications</button>
          <button onClick={()=>setPage("ats_pipeline")} style={{background:"var(--s2)",border:"1px solid var(--b1)",borderRadius:9,color:"var(--text)",padding:"9px 16px",cursor:"pointer",fontSize:12,fontWeight:600,fontFamily:FF}}>🔄 Pipeline</button>
          {!isSuperAdmin&&<button onClick={()=>setPage("ats_job_create")} style={{background:"linear-gradient(90deg,#ff3131,#ff914d)",border:"none",borderRadius:9,color:"#fff",padding:"9px 18px",cursor:"pointer",fontSize:12,fontWeight:700,fontFamily:FF}}>+ Post Job</button>}
        </div>
      </div>

      {/* KPI Row 1 */}
      <div style={{display:"grid",gridTemplateColumns:"repeat(5,1fr)",gap:12,marginBottom:16}} className="mg2">
        {[
          ["📋","Active Jobs",activeJobs.length,"var(--brand)"],
          ["👥","Total Applications",totalApps,"#ff914d"],
          ["📅","This Week",thisWeekApps,"#0cc0df"],
          ["✅","Hired",hired,"var(--green)"],
          ["📊","Conversion",conversionRate+"%","#8b5cf6"],
        ].map(([icon,label,val,color],i)=>(
          <div key={i} className="fu" style={{padding:"16px",borderRadius:12,textAlign:"center"}}>
            <div style={{fontSize:22,marginBottom:6}}>{icon}</div>
            <div style={{fontFamily:FFD,fontSize:22,fontWeight:900,color,marginBottom:2}}>{val}</div>
            <div style={{fontSize:10,color:"var(--muted)",fontWeight:600,textTransform:"uppercase"}}>{label}</div>
          </div>
        ))}
      </div>

      {/* Row 2: Pipeline + TAT */}
      <div style={{display:"grid",gridTemplateColumns:"1.2fr 1fr",gap:16,marginBottom:16}} className="mg1">
        
        {/* Pipeline Funnel */}
        <div className="fu" style={{borderRadius:14,padding:"18px 20px"}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16}}>
            <div style={{fontFamily:FFD,fontSize:13,fontWeight:700,color:"var(--text)"}}>📊 Hiring Pipeline</div>
            <button onClick={()=>setPage("ats_pipeline")} style={{background:"transparent",border:"none",color:"var(--brand)",fontSize:11,cursor:"pointer",fontFamily:FF,fontWeight:600}}>View Kanban →</button>
          </div>
          {STAGES.filter(s=>s!=="rejected").map(stage=>{
            const count=stageCount[stage]||0;
            const pct=totalApps>0?Math.round((count/totalApps)*100):0;
            return(
              <div key={stage} style={{marginBottom:10}}>
                <div style={{display:"flex",justifyContent:"space-between",marginBottom:4}}>
                  <div style={{fontSize:12,color:"var(--text)",textTransform:"capitalize",fontWeight:500}}>{stage}</div>
                  <div style={{fontSize:12,fontWeight:700,color:stageColors[stage]||"var(--muted)"}}>{count}</div>
                </div>
                <div style={{height:6,background:"var(--s2)",borderRadius:99,overflow:"hidden"}}>
                  <div style={{height:"100%",width:pct+"%",background:stageColors[stage]||"var(--muted)",borderRadius:99,transition:"width .3s"}}/>
                </div>
              </div>
            );
          })}
          {/* Rejected separate */}
          <div style={{marginTop:12,paddingTop:12,borderTop:"1px solid var(--b1)",display:"flex",justifyContent:"space-between"}}>
            <div style={{fontSize:12,color:"var(--muted)"}}>❌ Rejected</div>
            <div style={{fontSize:12,fontWeight:700,color:"#ef4444"}}>{stageCount["rejected"]||0}</div>
          </div>
        </div>

        {/* TAT per Stage */}
        <div className="fu" style={{borderRadius:14,padding:"18px 20px"}}>
          <div style={{fontFamily:FFD,fontSize:13,fontWeight:700,color:"var(--text)",marginBottom:16}}>⏱️ Avg TAT per Stage</div>
          {Object.keys(stageTAT).length>0?(
            Object.entries(stageTAT).map(([stage,days])=>(
              <div key={stage} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"8px 0",borderBottom:"1px solid var(--b1)"}}>
                <div style={{fontSize:12,color:"var(--text)",textTransform:"capitalize"}}>{stage}</div>
                <div style={{display:"flex",alignItems:"center",gap:8}}>
                  <div style={{width:50,height:4,background:"var(--s2)",borderRadius:99,overflow:"hidden"}}>
                    <div style={{height:"100%",width:Math.min(100,days*10)+"%",background:days>7?"#ff3131":days>3?"#f59e0b":"var(--green)",borderRadius:99}}/>
                  </div>
                  <Badge color={days>7?"#ff3131":days>3?"#f59e0b":"var(--green)"} style={{fontSize:9,minWidth:32}}>{days}d</Badge>
                </div>
              </div>
            ))
          ):(
            <div style={{textAlign:"center",padding:"20px 0",color:"var(--muted)",fontSize:12}}>
              TAT data will appear as candidates move through stages
            </div>
          )}
        </div>
      </div>

      {/* Row 3: Stuck Candidates + Jobs Performance */}
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:16,marginBottom:16}} className="mg1">
        
        {/* Stuck Candidates */}
        <div className="fu" style={{borderRadius:14,padding:"18px 20px",border:stuck.length>0?"1px solid rgba(255,49,49,0.2)":"1px solid var(--b1)"}}>
          <div style={{fontFamily:FFD,fontSize:13,fontWeight:700,color:stuck.length>0?"#ff3131":"var(--text)",marginBottom:14}}>
            {stuck.length>0?"🔴":"🟢"} Stuck Candidates ({stuck.length})
          </div>
          {stuck.length>0?(
            stuck.slice(0,5).map((a,i)=>{
              const days=Math.round((Date.now()-new Date(a.stageUpdatedAt||a.appliedAt).getTime())/86400000);
              return(
                <div key={i} onClick={()=>setPage("ats_candidate_"+a.id)} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"10px 0",borderBottom:"1px solid var(--b1)",cursor:"pointer"}}>
                  <div>
                    <div style={{fontSize:12,fontWeight:600,color:"var(--text)"}}>{a.candidateName}</div>
                    <div style={{fontSize:10,color:"var(--muted)"}}>{a.jobTitle} · {a.stage}</div>
                  </div>
                  <Badge color="#ff3131" style={{fontSize:9}}>{days}d</Badge>
                </div>
              );
            })
          ):(
            <div style={{textAlign:"center",padding:"20px 0",color:"var(--muted)",fontSize:12}}>
              ✅ No stuck candidates! Pipeline is moving well.
            </div>
          )}
          {stuck.length>5&&<div style={{fontSize:11,color:"#ff3131",marginTop:8,textAlign:"center"}}>+{stuck.length-5} more stuck</div>}
        </div>

        {/* Today's Activity */}
        <div className="fu" style={{borderRadius:14,padding:"18px 20px"}}>
          <div style={{fontFamily:FFD,fontSize:13,fontWeight:700,color:"var(--text)",marginBottom:14}}>📅 Today's Activity</div>
          {[
            ["New Applications",todayApps,"#0cc0df"],
            ["BGV Initiated",applications.filter(a=>a.bgvInitiatedAt&&new Date(a.bgvInitiatedAt).toDateString()===new Date().toDateString()).length,"#ff3131"],
            ["Interviews Scheduled",applications.filter(a=>(a.interviews||[]).some(i=>i.date===new Date().toISOString().slice(0,10))).length,"#f59e0b"],
            ["Stage Updates",applications.filter(a=>a.stageUpdatedAt&&new Date(a.stageUpdatedAt).toDateString()===new Date().toDateString()).length,"var(--green)"],
          ].map(([label,val,color],i)=>(
            <div key={i} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"10px 0",borderBottom:"1px solid var(--b1)"}}>
              <div style={{fontSize:12,color:"var(--text)"}}>{label}</div>
              <div style={{fontFamily:FFD,fontSize:16,fontWeight:900,color}}>{val}</div>
            </div>
          ))}
          <div style={{marginTop:12,padding:"10px 12px",background:"var(--s2)",borderRadius:8,display:"flex",justifyContent:"space-between"}}>
            <div style={{fontSize:11,color:"var(--muted)"}}>Draft Jobs</div>
            <div style={{fontSize:13,fontWeight:700,color:"#f59e0b"}}>{draftJobs.length}</div>
          </div>
        </div>
      </div>

      {/* Row 4: Jobs Performance Table */}
      <div className="fu" style={{borderRadius:14,padding:0,overflow:"hidden",marginBottom:16}}>
        <div style={{padding:"12px 16px",borderBottom:"1px solid var(--b1)",display:"flex",justifyContent:"space-between",alignItems:"center"}}>
          <div style={{fontFamily:FFD,fontSize:13,fontWeight:700,color:"var(--text)"}}>📋 Jobs Performance</div>
          <button onClick={()=>setPage("ats_jobs")} style={{background:"transparent",border:"none",color:"var(--brand)",fontSize:11,cursor:"pointer",fontFamily:FF,fontWeight:600}}>View All →</button>
        </div>
        <table style={{width:"100%",borderCollapse:"collapse"}}>
          <thead><tr style={{background:"var(--s2)"}}>
            {["Job Title","Status","Applications","Shortlisted","Hired","Posted"].map(h=>(
              <th key={h} style={{padding:"8px 12px",textAlign:"left",fontSize:10,fontWeight:700,color:"var(--muted)",textTransform:"uppercase"}}>{h}</th>
            ))}
          </tr></thead>
          <tbody>
            {jobStats.slice(0,8).map((j,i)=>(
              <tr key={i} style={{borderBottom:"1px solid var(--b1)"}}>
                <td style={{padding:"10px 12px",fontSize:13,fontWeight:600,color:"var(--text)"}}>{j.title}</td>
                <td style={{padding:"10px 12px"}}><Badge color={j.status==="active"?"var(--green)":j.status==="draft"?"#f59e0b":"var(--muted)"} style={{fontSize:9,textTransform:"capitalize"}}>{j.status}</Badge></td>
                <td style={{padding:"10px 12px",fontSize:12,color:"var(--text)",fontWeight:600}}>{j.appCount}</td>
                <td style={{padding:"10px 12px",fontSize:12,color:"#0cc0df"}}>{applications.filter(a=>a.jobId===j.id&&["shortlisted","interview","selected","bgv","offer","joined"].includes(a.stage)).length}</td>
                <td style={{padding:"10px 12px",fontSize:12,color:"var(--green)"}}>{j.hired}</td>
                <td style={{padding:"10px 12px",fontSize:11,color:"var(--muted)"}}>{(j.createdAt||"").slice(0,10)}</td>
              </tr>
            ))}
            {jobs.length===0&&(
              <tr><td colSpan={6} style={{padding:"32px",textAlign:"center",color:"var(--muted)",fontSize:12}}>
                No jobs posted yet · <button onClick={()=>setPage("ats_job_create")} style={{background:"none",border:"none",color:"var(--brand)",cursor:"pointer",fontSize:12,fontFamily:FF,fontWeight:600}}>Post your first job →</button>
              </td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Row 5: Recent Applications */}
      <div className="fu" style={{borderRadius:14,padding:0,overflow:"hidden"}}>
        <div style={{padding:"12px 16px",borderBottom:"1px solid var(--b1)",display:"flex",justifyContent:"space-between",alignItems:"center"}}>
          <div style={{fontFamily:FFD,fontSize:13,fontWeight:700,color:"var(--text)"}}>👥 Recent Applications</div>
          <button onClick={()=>setPage("ats_applications")} style={{background:"transparent",border:"none",color:"var(--brand)",fontSize:11,cursor:"pointer",fontFamily:FF,fontWeight:600}}>View All →</button>
        </div>
        <table style={{width:"100%",borderCollapse:"collapse"}}>
          <thead><tr style={{background:"var(--s2)"}}>
            {["Candidate","Job","Stage","Applied","TAT"].map(h=>(
              <th key={h} style={{padding:"8px 12px",textAlign:"left",fontSize:10,fontWeight:700,color:"var(--muted)",textTransform:"uppercase"}}>{h}</th>
            ))}
          </tr></thead>
          <tbody>
            {[...applications].sort((a,b)=>new Date(b.appliedAt||"")-new Date(a.appliedAt||"")).slice(0,8).map((a,i)=>{
              const days=Math.round((Date.now()-new Date(a.appliedAt||"").getTime())/86400000);
              return(
                <tr key={i} style={{borderBottom:"1px solid var(--b1)",cursor:"pointer"}} onClick={()=>setPage("ats_candidate_"+a.id)}>
                  <td style={{padding:"10px 12px"}}>
                    <div style={{fontSize:13,fontWeight:600,color:"var(--text)"}}>{a.candidateName}</div>
                    <div style={{fontSize:10,color:"var(--muted)"}}>{a.email}</div>
                  </td>
                  <td style={{padding:"10px 12px",fontSize:12,color:"var(--muted)"}}>{a.jobTitle}</td>
                  <td style={{padding:"10px 12px"}}><Badge color={stageColors[a.stage||"applied"]||"var(--muted)"} style={{fontSize:9,textTransform:"capitalize"}}>{a.stage||"applied"}</Badge></td>
                  <td style={{padding:"10px 12px",fontSize:11,color:"var(--muted)"}}>{(a.appliedAt||"").slice(0,10)}</td>
                  <td style={{padding:"10px 12px"}}><Badge color={days>14?"#ff3131":days>7?"#f59e0b":"var(--green)"} style={{fontSize:9}}>{days}d</Badge></td>
                </tr>
              );
            })}
            {applications.length===0&&(
              <tr><td colSpan={5} style={{padding:"32px",textAlign:"center",color:"var(--muted)",fontSize:12}}>
                No applications yet · Share job links to start receiving applications!
              </td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}



function ATSJobsPage({user,company,addToast,setPage,isSuperAdmin}) {
  const {dark}=useTheme();
  const [jobs,setJobs]=useState([]);
  const [loading,setLoading]=useState(true);
  const [filter,setFilter]=useState("all");
  const companyId=user?.companyId||company?.id||"";
  const companySlug=(company?.name||"").toLowerCase().replace(/[^a-z0-9]/g,"-").replace(/-+/g,"-");

  useEffect(()=>{loadJobs();},[companyId]);

  const loadJobs=async()=>{
    try{
      const snap=isSuperAdmin
        ?await getDocs(collection(db,"ats_jobs"))
        :await getDocs(query(collection(db,"ats_jobs"),where("companyId","==",companyId)));
      setJobs(snap.docs.map(d=>({id:d.id,...d.data()})));
    }catch(e){addToast("Failed to load jobs","error");}
    finally{setLoading(false);}
  };

  const toggleStatus=async(job)=>{
    try{
      
      const newStatus=job.status==="active"?"paused":"active";
      await updateDoc(doc(db,"ats_jobs",job.id),{status:newStatus});
      setJobs(p=>p.map(j=>j.id===job.id?{...j,status:newStatus}:j));
      addToast("Job "+newStatus,"success");
    }catch(e){addToast("Failed","error");}
  };

  const copyLink=(job)=>{
    const slug=(job.companyName||company?.name||"").toLowerCase().replace(/[^a-z0-9]/g,"-").replace(/-+/g,"-");
    const link=`${window.location.origin}/ats/${slug}/${job.id}`;
    navigator.clipboard.writeText(link);
    addToast("Link copied!","success");
  };

  const filtered=jobs.filter(j=>filter==="all"||j.status===filter);

  return(
    <div>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:20,flexWrap:"wrap",gap:10}}>
        <div>
          <h2 style={{fontFamily:FFD,fontWeight:800,fontSize:24,color:"var(--text)",marginBottom:4}}>📋 Jobs</h2>
          <p style={{color:"var(--muted)",fontSize:13}}>{jobs.length} total · {jobs.filter(j=>j.status==="active").length} active · {jobs.filter(j=>j.status==="draft").length} drafts</p>
        </div>
        {!isSuperAdmin&&<Btn onClick={()=>setPage("ats_job_create")}>+ Post New Job</Btn>}
      </div>

      {/* Filter tabs */}
      <div style={{display:"flex",gap:4,marginBottom:16,background:"var(--s2)",borderRadius:10,padding:4,width:"fit-content"}}>
        {["all","active","draft","paused","closed"].map(f=>(
          <button key={f} onClick={()=>setFilter(f)} style={{padding:"6px 14px",borderRadius:7,border:"none",background:filter===f?"var(--bg)":"transparent",color:filter===f?"var(--text)":"var(--muted)",cursor:"pointer",fontSize:12,fontWeight:filter===f?700:500,fontFamily:FF,textTransform:"capitalize"}}>
            {f}{f==="draft"?` (${jobs.filter(j=>j.status==="draft").length})`:""}
          </button>
        ))}
      </div>

      {loading?<div style={{textAlign:"center",padding:40,color:"var(--muted)"}}>Loading...</div>:(
        <div style={{display:"grid",gap:12}}>
          {filtered.map((job,i)=>{
            const slug=(job.companyName||company?.name||"").toLowerCase().replace(/[^a-z0-9]/g,"-").replace(/-+/g,"-");
            const link=`${window.location.origin}/ats/${slug}/${job.id}`;
            return(
              <div key={i} className="fu" style={{borderRadius:14,padding:"18px 20px"}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",flexWrap:"wrap",gap:12}}>
                  <div style={{flex:1}}>
                    <div style={{display:"flex",gap:8,alignItems:"center",marginBottom:6,flexWrap:"wrap"}}>
                      <div style={{fontFamily:FFD,fontSize:16,fontWeight:800,color:"var(--text)"}}>{job.title}</div>
                      <Badge color={job.status==="active"?"var(--green)":job.status==="paused"?"#f59e0b":"var(--muted)"} style={{fontSize:9,textTransform:"capitalize"}}>{job.status}</Badge>
                    </div>
                    <div style={{display:"flex",gap:16,flexWrap:"wrap",marginBottom:8}}>
                      {[["📍",job.location],["💼",job.department],["💰",job.salaryRange],["👥",job.openings+" openings"],["🔄",(job.rounds||[]).length+" rounds"]].filter(([,v])=>v).map(([icon,val],j)=>(
                        <div key={j} style={{fontSize:12,color:"var(--muted)",display:"flex",gap:4,alignItems:"center"}}><span>{icon}</span><span>{val}</span></div>
                      ))}
                    </div>
                    <div style={{fontSize:11,color:"var(--muted)"}}>Posted: {(job.createdAt||"").slice(0,10)} · {job.applicationsCount||0} applications</div>
                  </div>
                  <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
                    <button onClick={()=>copyLink(job)} style={{background:"var(--s2)",border:"1px solid var(--b1)",borderRadius:8,color:"var(--text)",padding:"6px 12px",cursor:"pointer",fontSize:11,fontWeight:600,fontFamily:FF}}>🔗 Copy Link</button>
                    <button onClick={()=>window.open(link,"_blank")} style={{background:"var(--s2)",border:"1px solid var(--b1)",borderRadius:8,color:"var(--text)",padding:"6px 12px",cursor:"pointer",fontSize:11,fontWeight:600,fontFamily:FF}}>👁️ Preview</button>
                    {!isSuperAdmin&&<>
                      {job.status==="draft"?<>
                        <button onClick={()=>setPage("ats_job_edit_"+job.id)} style={{background:"var(--s2)",border:"1px solid var(--b1)",borderRadius:8,color:"var(--text)",padding:"6px 12px",cursor:"pointer",fontSize:11,fontWeight:600,fontFamily:FF}}>✏️ Continue Edit</button>
                        <button onClick={async()=>{
                          try{
                            await updateDoc(doc(db,"ats_jobs",job.id),{status:"active"});
                            setJobs(p=>p.map(j=>j.id===job.id?{...j,status:"active"}:j));
                            addToast("Job published!","success");
                          }catch(e){addToast("Failed","error");}
                        }} style={{background:"linear-gradient(90deg,#ff3131,#ff914d)",border:"none",borderRadius:8,color:"#fff",padding:"6px 12px",cursor:"pointer",fontSize:11,fontWeight:700,fontFamily:FF}}>🚀 Publish</button>
                        <button onClick={async()=>{
                          if(!window.confirm("Delete this draft?"))return;
                          try{
                            await deleteDoc(doc(db,"ats_jobs",job.id));
                            setJobs(p=>p.filter(j=>j.id!==job.id));
                            addToast("Draft deleted","success");
                          }catch(e){addToast("Failed","error");}
                        }} style={{background:"rgba(255,49,49,0.1)",border:"1px solid rgba(255,49,49,0.2)",borderRadius:8,color:"#ff3131",padding:"6px 12px",cursor:"pointer",fontSize:11,fontWeight:600,fontFamily:FF}}>🗑️ Delete</button>
                      </>:<>
                        <button onClick={()=>toggleStatus(job)} style={{background:"var(--s2)",border:"1px solid var(--b1)",borderRadius:8,color:"var(--text)",padding:"6px 12px",cursor:"pointer",fontSize:11,fontWeight:600,fontFamily:FF}}>{job.status==="active"?"⏸ Pause":"▶ Activate"}</button>
                        <button onClick={()=>setPage("ats_applications_"+job.id)} style={{background:"linear-gradient(90deg,#ff3131,#ff914d)",border:"none",borderRadius:8,color:"#fff",padding:"6px 12px",cursor:"pointer",fontSize:11,fontWeight:700,fontFamily:FF}}>View Applications ({job.applicationsCount||0})</button>
                      </>}
                    </>}
                  </div>
                </div>
              </div>
            );
          })}
          {filtered.length===0&&(
            <div className="fu" style={{borderRadius:14,padding:"40px",textAlign:"center"}}>
              <div style={{fontSize:40,marginBottom:12}}>📋</div>
              <div style={{fontFamily:FFD,fontSize:16,fontWeight:700,color:"var(--text)",marginBottom:8}}>No jobs yet</div>
              <div style={{fontSize:13,color:"var(--muted)",marginBottom:16}}>Post your first job to start receiving applications</div>
              {!isSuperAdmin&&<Btn onClick={()=>setPage("ats_job_create")}>+ Post New Job</Btn>}
            </div>
          )}
        </div>
      )}
    </div>
  );
}


// ============================================================
// ATS FINAL - COMPLETE REWRITE
// ============================================================

// ── Memoized Panel Row (no re-render on sibling changes) ──
const PanelRow=React.memo(function PanelRow({panel,ri,pi,total,onRemove,onUpdate}){
  return(
    <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr auto",gap:8,marginBottom:8,alignItems:"end"}}>
      <div>
        <label style={{fontSize:11,fontWeight:600,color:"var(--muted)",display:"block",marginBottom:4}}>Name</label>
        <input defaultValue={panel.name} onBlur={e=>onUpdate(ri,pi,"name",e.target.value)} placeholder="Panel name"
          style={{width:"100%",padding:"9px 12px",borderRadius:8,border:"1px solid var(--b1)",background:"var(--s2)",color:"var(--text)",fontSize:13,fontFamily:"inherit",outline:"none"}}/>
      </div>
      <div>
        <label style={{fontSize:11,fontWeight:600,color:"var(--muted)",display:"block",marginBottom:4}}>Designation</label>
        <input defaultValue={panel.designation} onBlur={e=>onUpdate(ri,pi,"designation",e.target.value)} placeholder="Tech Lead"
          style={{width:"100%",padding:"9px 12px",borderRadius:8,border:"1px solid var(--b1)",background:"var(--s2)",color:"var(--text)",fontSize:13,fontFamily:"inherit",outline:"none"}}/>
      </div>
      <div>
        <label style={{fontSize:11,fontWeight:600,color:"var(--muted)",display:"block",marginBottom:4}}>Email</label>
        <input defaultValue={panel.email} onBlur={e=>onUpdate(ri,pi,"email",e.target.value)} placeholder="panel@company.com"
          style={{width:"100%",padding:"9px 12px",borderRadius:8,border:"1px solid var(--b1)",background:"var(--s2)",color:"var(--text)",fontSize:13,fontFamily:"inherit",outline:"none"}}/>
      </div>
      {total>1&&<button onClick={()=>onRemove(ri,pi)} style={{background:"rgba(255,49,49,0.1)",border:"none",borderRadius:6,color:"#ff3131",padding:"9px 10px",cursor:"pointer",marginBottom:1,fontFamily:"inherit"}}>✕</button>}
    </div>
  );
});

// ── Memoized Round Card ──
const RoundCard=React.memo(function RoundCard({round,ri,total,onRemove,onUpdate,onAddPanel,onRemovePanel,onUpdatePanel}){
  return(
    <div style={{background:"var(--s2)",borderRadius:12,padding:"16px",marginBottom:12,border:"1px solid var(--b1)"}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12}}>
        <div style={{fontFamily:"'Sora',sans-serif",fontSize:13,fontWeight:700,color:"var(--text)"}}>Round {ri+1}</div>
        {total>1&&<button onClick={()=>onRemove(ri)} style={{background:"rgba(255,49,49,0.1)",border:"none",borderRadius:6,color:"#ff3131",padding:"4px 10px",cursor:"pointer",fontSize:11,fontWeight:600,fontFamily:"inherit"}}>Remove</button>}
      </div>
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:10,marginBottom:12}}>
        <div>
          <label style={{fontSize:11,fontWeight:600,color:"var(--muted)",display:"block",marginBottom:4}}>Round Name</label>
          <input defaultValue={round.name} onBlur={e=>onUpdate(ri,"name",e.target.value)} placeholder="e.g. Technical Round"
            style={{width:"100%",padding:"9px 12px",borderRadius:8,border:"1px solid var(--b1)",background:"var(--bg)",color:"var(--text)",fontSize:13,fontFamily:"inherit",outline:"none"}}/>
        </div>
        <div>
          <label style={{fontSize:11,fontWeight:600,color:"var(--muted)",display:"block",marginBottom:4}}>Type</label>
          <select value={round.type} onChange={e=>onUpdate(ri,"type",e.target.value)}
            style={{width:"100%",padding:"9px 12px",borderRadius:8,border:"1px solid var(--b1)",background:"var(--bg)",color:"var(--text)",fontSize:13,fontFamily:"inherit"}}>
            <option value="telephonic">📞 Telephonic</option>
            <option value="google_meet">🖥️ Google Meet</option>
            <option value="face_to_face">🤝 Face to Face</option>
          </select>
        </div>
        <div>
          <label style={{fontSize:11,fontWeight:600,color:"var(--muted)",display:"block",marginBottom:4}}>Duration (mins)</label>
          <input defaultValue={round.duration} onBlur={e=>onUpdate(ri,"duration",e.target.value)} placeholder="45" type="number"
            style={{width:"100%",padding:"9px 12px",borderRadius:8,border:"1px solid var(--b1)",background:"var(--bg)",color:"var(--text)",fontSize:13,fontFamily:"inherit",outline:"none"}}/>
        </div>
      </div>
      {round.type==="face_to_face"&&(
        <div style={{marginBottom:12}}>
          <label style={{fontSize:11,fontWeight:600,color:"var(--muted)",display:"block",marginBottom:4}}>Venue</label>
          <input defaultValue={round.location||""} onBlur={e=>onUpdate(ri,"location",e.target.value)} placeholder="Office address"
            style={{width:"100%",padding:"9px 12px",borderRadius:8,border:"1px solid var(--b1)",background:"var(--bg)",color:"var(--text)",fontSize:13,fontFamily:"inherit",outline:"none"}}/>
        </div>
      )}
      <div style={{fontSize:11,fontWeight:700,color:"var(--muted)",marginBottom:8,textTransform:"uppercase",letterSpacing:.5}}>Panel Members</div>
      {round.panels.map((panel,pi)=>(
        <PanelRow key={pi} panel={panel} ri={ri} pi={pi} total={round.panels.length}
          onRemove={onRemovePanel} onUpdate={onUpdatePanel}/>
      ))}
      <button onClick={()=>onAddPanel(ri)} style={{background:"transparent",border:"1px dashed var(--b1)",borderRadius:8,color:"var(--muted)",padding:"6px 14px",cursor:"pointer",fontSize:11,fontWeight:600,fontFamily:"inherit"}}>+ Add Panel</button>
    </div>
  );
},
(prev,next)=>prev.round===next.round&&prev.total===next.total);

// ── ATS Dashboard ──


function ATSJobCreate({user,company,addToast,setPage,editJobId}) {
  const {dark}=useTheme();
  const companyId=user?.companyId||company?.id||"";
  const [saving,setSaving]=useState(false);
  const [locationType,setLocationType]=useState("office");
  const [aptitudeTest,setAptitudeTest]=useState("");
  const [bgvChecks,setBgvChecks]=useState({uan:true,pan:true,dl:false,criminal:true,refcheck:false});
  const [loadingDraft,setLoadingDraft]=useState(!!editJobId);
  const [draftData,setDraftData]=useState(null);
  // Use single forceUpdate to re-render when rounds structure changes
  const [,forceUpdate]=useState(0);
  const rerender=()=>forceUpdate(n=>n+1);
  const [extractingJD,setExtractingJD]=useState(false);
  const jdFileRef=useRef(null);

  // Extract plain text from an uploaded JD file (PDF or Word) and drop it
  // straight into the Job Description field. Uses dynamic imports so these
  // libraries only load when someone actually uses this button, not on
  // every page load. Requires `npm install pdfjs-dist mammoth` locally —
  // this is a one-time setup step, not something that ships automatically.
  const handleJDUpload=async(e)=>{
    const file=e.target.files?.[0];
    if(!file)return;
    setExtractingJD(true);
    try{
      const name=file.name.toLowerCase();
      let text="";
      if(name.endsWith(".pdf")){
        const pdfjsLib=await import("pdfjs-dist");
        pdfjsLib.GlobalWorkerOptions.workerSrc=`https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.mjs`;
        const buf=await file.arrayBuffer();
        const pdf=await pdfjsLib.getDocument({data:buf}).promise;
        const pageTexts=[];
        for(let i=1;i<=pdf.numPages;i++){
          const page=await pdf.getPage(i);
          const content=await page.getTextContent();
          pageTexts.push(content.items.map(it=>it.str).join(" "));
        }
        text=pageTexts.join("\n\n");
      }else if(name.endsWith(".docx")||name.endsWith(".doc")){
        const mammoth=await import("mammoth");
        const buf=await file.arrayBuffer();
        const result=await mammoth.extractRawText({arrayBuffer:buf});
        text=result.value;
      }else{
        addToast("Please upload a PDF or Word document","error");
        setExtractingJD(false);
        if(jdFileRef.current)jdFileRef.current.value="";
        return;
      }
      text=text.trim();
      if(!text){
        addToast("Couldn't find any text in that file — please paste the description manually","error");
      }else{
        if(r.desc.current)r.desc.current.value=text;
        addToast(`Extracted from ${file.name} — please review before publishing`,"success");
      }
    }catch(err){
      addToast("Failed to read that file: "+err.message,"error");
    }finally{
      setExtractingJD(false);
      if(jdFileRef.current)jdFileRef.current.value="";
    }
  };

  // Load existing draft if editJobId provided
  useEffect(()=>{
    if(!editJobId)return;
    getDoc(doc(db,"ats_jobs",editJobId)).then(snap=>{
      if(snap.exists()){
        const d=snap.data();
        setDraftData(d);
        setLocationType(d.locationType||"office");
        setAptitudeTest(d.aptitudeTest||"");
        setBgvChecks(d.bgvChecks||{uan:true,pan:true,dl:false,criminal:true,refcheck:false});
        if(d.rounds?.length>0)roundsRef.current=d.rounds;
        if(d.screeningQuestions?.length>0)questionsRef.current=d.screeningQuestions;
        rerender();
        // Set ref values after render
        setTimeout(()=>{
          if(r.title.current)r.title.current.value=d.title||"";
          if(r.dept.current)r.dept.current.value=d.department||"";
          if(r.loc.current)r.loc.current.value=d.location||"";
          if(r.salMin.current)r.salMin.current.value=d.salaryMin||"";
          if(r.salMax.current)r.salMax.current.value=d.salaryMax||"";
          if(r.exp.current)r.exp.current.value=d.experience||"";
          if(r.openings.current)r.openings.current.value=d.openings||"1";
          if(r.desc.current)r.desc.current.value=d.description||"";
          if(r.req.current)r.req.current.value=d.requirements||"";
        },100);
      }
    }).catch(e=>console.error(e))
    .finally(()=>setLoadingDraft(false));
  },[editJobId]);

  // All text inputs via refs
  const r={
    title:useRef(null),dept:useRef(null),loc:useRef(null),
    salMin:useRef(null),salMax:useRef(null),exp:useRef(null),
    openings:useRef(null),desc:useRef(null),req:useRef(null),
    aptLink:useRef(null),aptDur:useRef(null),
  };

  // Rounds stored in a ref - NO re-render on field changes
  const roundsRef=useRef([
    {name:"HR Screening",type:"telephonic",duration:"30",location:"",
     panels:[{name:"",designation:"",email:""}]}
  ]);

  // Round field refs: roundFieldRefs[ri][fieldName] = ref
  const roundFieldRefs=useRef({});
  const panelFieldRefs=useRef({});

  const getRoundRef=(ri,field)=>{
    if(!roundFieldRefs.current[ri])roundFieldRefs.current[ri]={};
    if(!roundFieldRefs.current[ri][field])roundFieldRefs.current[ri][field]=React.createRef();
    return roundFieldRefs.current[ri][field];
  };
  const getPanelRef=(ri,pi,field)=>{
    const key=`${ri}_${pi}_${field}`;
    if(!panelFieldRefs.current[key])panelFieldRefs.current[key]=React.createRef();
    return panelFieldRefs.current[key];
  };

  const addRound=()=>{
    roundsRef.current=[...roundsRef.current,{name:"",type:"google_meet",duration:"45",location:"",panels:[{name:"",designation:"",email:""}]}];
    rerender();
  };
  const removeRound=i=>{
    roundsRef.current=roundsRef.current.filter((_,j)=>j!==i);
    rerender();
  };
  const setRoundType=(ri,val)=>{
    roundsRef.current=roundsRef.current.map((r,j)=>j===ri?{...r,type:val}:r);
    rerender();
  };
  const addPanel=ri=>{
    roundsRef.current=roundsRef.current.map((r,j)=>j===ri?{...r,panels:[...r.panels,{name:"",designation:"",email:""}]}:r);
    rerender();
  };
  const removePanel=(ri,pi)=>{
    roundsRef.current=roundsRef.current.map((r,j)=>j===ri?{...r,panels:r.panels.filter((_,k)=>k!==pi)}:r);
    rerender();
  };

  // Screening questions builder — same ref-based pattern as rounds, to avoid
  // re-rendering the whole form on every keystroke. HR defines these per job;
  // candidates answer them on their tracker page once shortlisted.
  const questionsRef=useRef([]);
  const questionFieldRefs=useRef({});
  const getQuestionRef=(qi,field)=>{
    const key=`${qi}_${field}`;
    if(!questionFieldRefs.current[key])questionFieldRefs.current[key]=React.createRef();
    return questionFieldRefs.current[key];
  };
  const addQuestion=()=>{
    questionsRef.current=[...questionsRef.current,{id:"q"+Date.now()+Math.random().toString(36).slice(2,6),type:"text",options:["",""]}];
    rerender();
  };
  const removeQuestion=i=>{
    questionsRef.current=questionsRef.current.filter((_,j)=>j!==i);
    rerender();
  };
  const setQuestionType=(qi,val)=>{
    questionsRef.current=questionsRef.current.map((q,j)=>j===qi?{...q,type:val,options:val==="mcq"?(q.options?.length?q.options:["",""]):q.options}:q);
    rerender();
  };
  const addOption=qi=>{
    questionsRef.current=questionsRef.current.map((q,j)=>j===qi?{...q,options:[...(q.options||[]),""]}:q);
    rerender();
  };
  const removeOption=(qi,oi)=>{
    questionsRef.current=questionsRef.current.map((q,j)=>j===qi?{...q,options:q.options.filter((_,k)=>k!==oi)}:q);
    rerender();
  };
  const collectQuestions=()=>{
    return questionsRef.current.map((q,qi)=>({
      id:q.id,
      text:getQuestionRef(qi,"text")?.current?.value?.trim()||"",
      type:q.type,
      options:q.type==="yesno"?["Yes","No"]:q.type==="mcq"?(q.options||[]).map((_,oi)=>getQuestionRef(qi,"opt"+oi)?.current?.value?.trim()||"").filter(Boolean):[],
    })).filter(q=>q.text); // drop any question left blank
  };

  // Read all current values from refs before saving
  const collectRounds=()=>{
    return roundsRef.current.map((round,ri)=>({
      name:roundFieldRefs.current[ri]?.name?.current?.value||round.name||"",
      type:round.type,
      duration:roundFieldRefs.current[ri]?.duration?.current?.value||round.duration||"",
      location:roundFieldRefs.current[ri]?.location?.current?.value||round.location||"",
      panels:round.panels.map((panel,pi)=>({
        name:panelFieldRefs.current[`${ri}_${pi}_name`]?.current?.value||panel.name||"",
        designation:panelFieldRefs.current[`${ri}_${pi}_designation`]?.current?.value||panel.designation||"",
        email:panelFieldRefs.current[`${ri}_${pi}_email`]?.current?.value||panel.email||"",
      }))
    }));
  };

  const save=async(saveStatus)=>{
    const title=r.title.current?.value?.trim()||"";
    const dept=r.dept.current?.value?.trim()||"";
    const loc=r.loc.current?.value?.trim()||"";
    if(!title){addToast("Enter job title","error");return;}
    if(!dept){addToast("Enter department","error");return;}
    if(!loc){addToast("Enter location","error");return;}
    const rounds=collectRounds();
    const screeningQuestions=collectQuestions();
    setSaving(true);
    try{
      const slug=(company?.name||"").toLowerCase().replace(/[^a-z0-9]/g,"-").replace(/-+/g,"-");
      const salMin=r.salMin.current?.value||"";
      const salMax=r.salMax.current?.value||"";
      const jobData={
        title,department:dept,location:loc,locationType,
        salaryMin:salMin,salaryMax:salMax,
        salaryRange:salMin&&salMax?`₹${salMin}L - ₹${salMax}L`:"",
        experience:r.exp.current?.value||"",
        openings:r.openings.current?.value||"1",
        description:r.desc.current?.value||"",
        requirements:r.req.current?.value||"",
        aptitudeTest,
        aptitudeLink:r.aptLink.current?.value||"",
        aptitudeDuration:r.aptDur.current?.value||"",
        bgvChecks,rounds,screeningQuestions,
        companyId,companyName:company?.name||"",
        companySlug:slug,companyEmail:company?.email||"",
        status:saveStatus,
      };
      if(editJobId){
        // Update existing draft
        await updateDoc(doc(db,"ats_jobs",editJobId),jobData);
        addToast(saveStatus==="draft"?"✅ Draft updated!":"🚀 Job published!","success");
      }else{
        // Create new job
        await addDoc(collection(db,"ats_jobs"),{
          ...jobData,
          createdAt:new Date().toISOString(),
          createdBy:user?.uid||"",applicationsCount:0,
        });
        addToast(saveStatus==="draft"?"✅ Draft saved!":"🚀 Job published!","success");
      }
      setPage("ats_jobs");
    }catch(e){addToast("Failed: "+e.message,"error");}
    finally{setSaving(false);}
  };

  const IS={width:"100%",padding:"9px 12px",borderRadius:8,border:"1px solid var(--b1)",background:"var(--s2)",color:"var(--text)",fontSize:13,fontFamily:"inherit",outline:"none",boxSizing:"border-box"};
  const TA={...IS,resize:"vertical"};
  const L=({ch})=><label style={{fontSize:11,fontWeight:600,color:"var(--muted)",display:"block",marginBottom:4,textTransform:"uppercase"}}>{ch}</label>;

  const rounds=roundsRef.current;

  return(
    <div>
      <div style={{display:"flex",gap:10,alignItems:"center",marginBottom:20}}>
        <button onClick={()=>setPage("ats_jobs")} style={{background:"var(--s2)",border:"1px solid var(--b1)",borderRadius:8,color:"var(--text)",padding:"7px 14px",cursor:"pointer",fontSize:12,fontWeight:600,fontFamily:"inherit"}}>← Back</button>
        <h2 style={{fontFamily:"'Sora',sans-serif",fontWeight:800,fontSize:22,color:"var(--text)"}}>{editJobId?"✏️ Edit Draft":"Post New Job"}</h2>
      </div>
      {loadingDraft&&<div style={{textAlign:"center",padding:40,color:"var(--muted)"}}>Loading draft...</div>}

      {/* Job Details */}
      <div className="fu" style={{borderRadius:14,padding:"20px 24px",marginBottom:16}}>
        <div style={{fontFamily:"'Sora',sans-serif",fontSize:13,fontWeight:700,color:"var(--text)",marginBottom:16,paddingBottom:10,borderBottom:"1px solid var(--b1)"}}>📋 Job Details</div>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:14,marginBottom:14}} className="mg1">
          <div><L ch="Job Title *"/><input ref={r.title} placeholder="e.g. Senior React Developer" style={IS}/></div>
          <div><L ch="Department *"/><input ref={r.dept} placeholder="e.g. Engineering" style={IS}/></div>
          <div><L ch="Location *"/><input ref={r.loc} placeholder="e.g. Chennai, Tamil Nadu" style={IS}/></div>
          <div>
            <L ch="Work Type"/>
            <select value={locationType} onChange={e=>setLocationType(e.target.value)} style={IS}>
              <option value="office">On-site</option>
              <option value="remote">Remote</option>
              <option value="hybrid">Hybrid</option>
            </select>
          </div>
          <div><L ch="Min Salary (LPA)"/><input ref={r.salMin} placeholder="8" type="number" style={IS}/></div>
          <div><L ch="Max Salary (LPA)"/><input ref={r.salMax} placeholder="12" type="number" style={IS}/></div>
          <div><L ch="Experience"/><input ref={r.exp} placeholder="e.g. 3-5 years" style={IS}/></div>
          <div><L ch="Openings"/><input ref={r.openings} placeholder="1" type="number" style={IS}/></div>
        </div>
        <div style={{marginBottom:14}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:4}}>
            <L ch="Job Description"/>
            <div>
              <input ref={jdFileRef} type="file" accept=".pdf,.doc,.docx" onChange={handleJDUpload} style={{display:"none"}}/>
              <button type="button" onClick={()=>jdFileRef.current?.click()} disabled={extractingJD}
                style={{background:"rgba(12,192,223,0.08)",border:"1px solid rgba(12,192,223,0.25)",borderRadius:7,color:"var(--brand)",padding:"5px 12px",cursor:"pointer",fontSize:11,fontWeight:700,fontFamily:"inherit"}}>
                {extractingJD?"Reading file...":"📄 Upload JD (PDF/Word)"}
              </button>
            </div>
          </div>
          <textarea ref={r.desc} placeholder="Describe the role..." rows={5} style={TA}/>
        </div>
        <div><L ch="Requirements"/><textarea ref={r.req} placeholder="Skills needed..." rows={4} style={TA}/></div>
      </div>

      {/* Interview Rounds */}
      <div className="fu" style={{borderRadius:14,padding:"20px 24px",marginBottom:16}}>
        <div style={{fontFamily:"'Sora',sans-serif",fontSize:13,fontWeight:700,color:"var(--text)",marginBottom:16,paddingBottom:10,borderBottom:"1px solid var(--b1)"}}>🎯 Interview Rounds</div>
        {rounds.map((round,ri)=>(
          <div key={ri} style={{background:"var(--s2)",borderRadius:12,padding:"16px",marginBottom:12,border:"1px solid var(--b1)"}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12}}>
              <div style={{fontFamily:"'Sora',sans-serif",fontSize:13,fontWeight:700,color:"var(--text)"}}>Round {ri+1}</div>
              {rounds.length>1&&<button onClick={()=>removeRound(ri)} style={{background:"rgba(255,49,49,0.1)",border:"none",borderRadius:6,color:"#ff3131",padding:"4px 10px",cursor:"pointer",fontSize:11,fontFamily:"inherit"}}>Remove</button>}
            </div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:10,marginBottom:12}} className="mg1">
              <div><L ch="Round Name"/><input ref={getRoundRef(ri,"name")} defaultValue={round.name} placeholder="e.g. Technical Round" style={{...IS,background:"var(--bg)"}}/></div>
              <div>
                <L ch="Type"/>
                <select value={round.type} onChange={e=>setRoundType(ri,e.target.value)} style={{...IS,background:"var(--bg)"}}>
                  <option value="telephonic">📞 Telephonic</option>
                  <option value="google_meet">🖥️ Google Meet</option>
                  <option value="face_to_face">🤝 Face to Face</option>
                </select>
              </div>
              <div><L ch="Duration (mins)"/><input ref={getRoundRef(ri,"duration")} defaultValue={round.duration} placeholder="45" type="number" style={{...IS,background:"var(--bg)"}}/></div>
            </div>
            {round.type==="face_to_face"&&(
              <div style={{marginBottom:12}}><L ch="Venue"/><input ref={getRoundRef(ri,"location")} defaultValue={round.location||""} placeholder="Office address" style={{...IS,background:"var(--bg)"}}/></div>
            )}
            <div style={{fontSize:11,fontWeight:700,color:"var(--muted)",marginBottom:8,textTransform:"uppercase"}}>Panel Members</div>
            {round.panels.map((panel,pi)=>(
              <div key={pi} style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr auto",gap:8,marginBottom:8,alignItems:"end"}} className="mg1">
                <div><L ch="Name"/><input ref={getPanelRef(ri,pi,"name")} defaultValue={panel.name} placeholder="Panel name" style={IS}/></div>
                <div><L ch="Designation"/><input ref={getPanelRef(ri,pi,"designation")} defaultValue={panel.designation} placeholder="Tech Lead" style={IS}/></div>
                <div><L ch="Email"/><input ref={getPanelRef(ri,pi,"email")} defaultValue={panel.email} placeholder="panel@co.com" style={IS}/></div>
                {round.panels.length>1&&<button onClick={()=>removePanel(ri,pi)} style={{background:"rgba(255,49,49,0.1)",border:"none",borderRadius:6,color:"#ff3131",padding:"9px 10px",cursor:"pointer",marginBottom:1}}>✕</button>}
              </div>
            ))}
            <button onClick={()=>addPanel(ri)} style={{background:"transparent",border:"1px dashed var(--b1)",borderRadius:8,color:"var(--muted)",padding:"6px 14px",cursor:"pointer",fontSize:11,fontWeight:600,fontFamily:"inherit"}}>+ Add Panel</button>
          </div>
        ))}
        <button onClick={addRound} style={{background:"transparent",border:"1px dashed var(--b1)",borderRadius:10,color:"var(--muted)",padding:"10px 20px",cursor:"pointer",fontSize:12,fontWeight:600,fontFamily:"inherit",width:"100%"}}>+ Add Interview Round</button>
      </div>

      {/* Aptitude Test */}
      <div className="fu" style={{borderRadius:14,padding:"20px 24px",marginBottom:16}}>
        <div style={{fontFamily:"'Sora',sans-serif",fontSize:13,fontWeight:700,color:"var(--text)",marginBottom:16,paddingBottom:10,borderBottom:"1px solid var(--b1)"}}>📝 Aptitude Test (Optional)</div>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:14}} className="mg1">
          <div>
            <L ch="Platform"/>
            <select value={aptitudeTest} onChange={e=>setAptitudeTest(e.target.value)} style={IS}>
              <option value="">None</option>
              <option value="mettl">Mettl</option>
              <option value="hackerrank">HackerRank</option>
              <option value="other">Other</option>
            </select>
          </div>
          {aptitudeTest&&<>
            <div><L ch="Test Link"/><input ref={r.aptLink} placeholder="https://..." style={IS}/></div>
            <div><L ch="Duration (mins)"/><input ref={r.aptDur} placeholder="60" type="number" style={IS}/></div>
          </>}
        </div>
      </div>

      {/* BGV Checks */}
      <div className="fu" style={{borderRadius:14,padding:"20px 24px",marginBottom:16}}>
        <div style={{fontFamily:"'Sora',sans-serif",fontSize:13,fontWeight:700,color:"var(--text)",marginBottom:16,paddingBottom:10,borderBottom:"1px solid var(--b1)"}}>🔍 BGV Checks Required</div>
        <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:10}} className="mg1">
          {[["uan","UAN","🏦",100],["pan","PAN","📋",50],["dl","DL","🚗",50],["criminal","Criminal","⚖️",75],["refcheck","Reference","📧",125]].map(([key,label,icon,price])=>(
            <div key={key} onClick={()=>setBgvChecks(p=>({...p,[key]:!p[key]}))} style={{padding:"12px",borderRadius:10,border:`2px solid ${bgvChecks[key]?"#ff3131":"var(--b1)"}`,background:bgvChecks[key]?"rgba(255,49,49,0.05)":"var(--s2)",cursor:"pointer",transition:"all .2s"}}>
              <div style={{display:"flex",justifyContent:"space-between",marginBottom:6}}>
                <span style={{fontSize:18}}>{icon}</span>
                <div style={{width:16,height:16,borderRadius:"50%",background:bgvChecks[key]?"#ff3131":"transparent",border:`2px solid ${bgvChecks[key]?"#ff3131":"var(--b1)"}`,display:"flex",alignItems:"center",justifyContent:"center"}}>
                  {bgvChecks[key]&&<svg width="8" height="8" viewBox="0 0 8 8"><path d="M1.5 4l2 2 3-3" stroke="white" strokeWidth="1.5" strokeLinecap="round" fill="none"/></svg>}
                </div>
              </div>
              <div style={{fontSize:11,fontWeight:700,color:"var(--text)",marginBottom:2}}>{label}</div>
              <div style={{fontSize:10,color:"#ff3131",fontWeight:600}}>₹{price}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Screening Questions — asked to candidates once they're Shortlisted */}
      <div className="fu" style={{borderRadius:14,padding:"20px 24px",marginBottom:16}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:6,paddingBottom:10,borderBottom:"1px solid var(--b1)"}}>
          <div style={{fontFamily:"'Sora',sans-serif",fontSize:13,fontWeight:700,color:"var(--text)"}}>📋 Screening Questions</div>
          <button onClick={addQuestion} style={{background:"rgba(255,49,49,0.08)",border:"1px solid rgba(255,49,49,0.25)",borderRadius:8,color:"#ff3131",padding:"6px 14px",cursor:"pointer",fontSize:12,fontWeight:700,fontFamily:"inherit"}}>+ Add Question</button>
        </div>
        <div style={{fontSize:12,color:"var(--muted)",marginBottom:16}}>Shown to candidates on their tracker page once shortlisted — answers appear on the candidate's Screening tab for HR to review.</div>
        {questionsRef.current.length===0?(
          <div style={{textAlign:"center",padding:"20px 0",color:"var(--muted)",fontSize:12}}>No screening questions yet — optional, click "+ Add Question" to add one.</div>
        ):questionsRef.current.map((q,qi)=>(
          <div key={q.id} style={{background:"var(--s2)",border:"1px solid var(--b1)",borderRadius:10,padding:14,marginBottom:10}}>
            <div style={{display:"flex",gap:8,alignItems:"flex-start",marginBottom:10}}>
              <input ref={getQuestionRef(qi,"text")} defaultValue={q.text||""} placeholder={`Question ${qi+1} — e.g. What's your notice period?`} style={{...IS,flex:1}}/>
              <select value={q.type} onChange={e=>setQuestionType(qi,e.target.value)} style={{...IS,width:120,flexShrink:0,cursor:"pointer"}}>
                <option value="text">Text</option>
                <option value="yesno">Yes/No</option>
                <option value="mcq">Multiple Choice</option>
              </select>
              <button onClick={()=>removeQuestion(qi)} style={{background:"transparent",border:"1px solid var(--b1)",borderRadius:8,color:"var(--muted)",width:36,height:36,cursor:"pointer",flexShrink:0,fontSize:14}}>✕</button>
            </div>
            {q.type==="mcq"&&(
              <div style={{paddingLeft:8}}>
                {(q.options||["",""]).map((opt,oi)=>(
                  <div key={oi} style={{display:"flex",gap:8,marginBottom:6,alignItems:"center"}}>
                    <span style={{fontSize:11,color:"var(--muted)",width:16}}>{oi+1}.</span>
                    <input ref={getQuestionRef(qi,"opt"+oi)} defaultValue={opt||""} placeholder={`Option ${oi+1}`} style={{...IS,flex:1,padding:"6px 10px",fontSize:12}}/>
                    {(q.options||[]).length>2&&<button onClick={()=>removeOption(qi,oi)} style={{background:"transparent",border:"none",color:"var(--muted)",cursor:"pointer",fontSize:12,padding:"0 6px"}}>✕</button>}
                  </div>
                ))}
                <button onClick={()=>addOption(qi)} style={{background:"none",border:"none",color:"var(--brand)",cursor:"pointer",fontSize:11,fontWeight:600,fontFamily:"inherit",padding:0}}>+ Add option</button>
              </div>
            )}
            {q.type==="yesno"&&<div style={{fontSize:11,color:"var(--muted)",paddingLeft:8}}>Candidate will answer Yes or No.</div>}
          </div>
        ))}
      </div>

      <div style={{display:"flex",gap:10,flexWrap:"wrap",paddingBottom:40}}>
        <button onClick={()=>save("active")} disabled={saving} style={{background:"linear-gradient(90deg,#ff3131,#ff914d)",border:"none",borderRadius:10,color:"#fff",padding:"12px 28px",cursor:"pointer",fontSize:14,fontWeight:700,fontFamily:"inherit",opacity:saving?.6:1}}>
          {saving?"Saving...":"🚀 Publish Job"}
        </button>
        <button onClick={()=>save("draft")} disabled={saving} style={{background:"var(--s2)",border:"1px solid var(--b1)",borderRadius:10,color:"var(--text)",padding:"12px 22px",cursor:"pointer",fontSize:14,fontWeight:600,fontFamily:"inherit"}}>
          💾 Save as Draft
        </button>
        <button onClick={()=>setPage("ats_jobs")} style={{background:"transparent",border:"1px solid var(--b1)",borderRadius:10,color:"var(--muted)",padding:"12px 22px",cursor:"pointer",fontSize:14,fontFamily:"inherit"}}>
          Cancel
        </button>
      </div>
    </div>
  );
}


function ATSApplications({user,company,addToast,setPage,jobId,isSuperAdmin}) {
  const {dark}=useTheme();
  const [applications,setApplications]=useState([]);
  const [jobs,setJobs]=useState([]);
  const [loading,setLoading]=useState(true);
  const [filterJob,setFilterJob]=useState(jobId||"all");
  const [filterStage,setFilterStage]=useState("all");
  const [search,setSearch]=useState("");
  const companyId=user?.companyId||company?.id||"";

  const STAGES=["applied","shortlisted","aptitude","interview","selected","document","bgv","offer","joined","rejected"];

  useEffect(()=>{loadData();},[companyId]);

  const loadData=async()=>{
    try{
      
      const appsQ=isSuperAdmin
        ?query(collection(db,"ats_applications"),orderBy("appliedAt","desc"))
        :query(collection(db,"ats_applications"),where("companyId","==",companyId),orderBy("appliedAt","desc"));
      const jobsQ=isSuperAdmin
        ?query(collection(db,"ats_jobs"),orderBy("createdAt","desc"))
        :query(collection(db,"ats_jobs"),where("companyId","==",companyId),orderBy("createdAt","desc"));
      const [appsSnap,jobsSnap]=await Promise.all([getDocs(appsQ),getDocs(jobsQ)]);
      setApplications(appsSnap.docs.map(d=>({id:d.id,...d.data()})));
      setJobs(jobsSnap.docs.map(d=>({id:d.id,...d.data()})));
    }catch(e){addToast("Failed to load","error");}
    finally{setLoading(false);}
  };

  const moveStage=async(appId,newStage,app)=>{
    try{
      
      const now=new Date().toISOString();
      await updateDoc(doc(db,"ats_applications",appId),{
        stage:newStage,
        stageUpdatedAt:now,
        stageHistory:arrayUnion({stage:newStage,timestamp:now,updatedBy:user?.uid||""}),
      });
      setApplications(p=>p.map(a=>a.id===appId?{...a,stage:newStage,stageUpdatedAt:now,stageHistory:[...(a.stageHistory||[]),{stage:newStage,timestamp:now,updatedBy:user?.uid||""}]}:a));
      addToast("Moved to "+newStage,"success");

      // Send emails based on stage
      if(newStage==="shortlisted"){
        await sendStageEmail(app,"shortlisted");
      }else if(newStage==="rejected"){
        await sendRejectionEmail(app);
      }else if(newStage==="selected"){
        await sendStageEmail(app,"selected");
        await sendDocumentCollectionEmail(app);
        // Employee File is intentionally NOT created here anymore — per the
        // stricter onboarding flow, it's only created once documents are
        // approved, offer is accepted, and the candidate reaches "joined"
        // (see respondToOffer in CandidateTrackPage).
      }
    }catch(e){addToast("Failed","error");}
  };

  const sendStageEmail=async(app,stage)=>{
    const subjects={
      shortlisted:`You've been shortlisted — ${app.jobTitle} at ${app.companyName}`,
      selected:`Congratulations! You've been selected — ${app.jobTitle} at ${app.companyName}`,
    };
    const bodies={
      shortlisted:`<p>Dear ${app.candidateName},</p><p>Congratulations! You have been <strong>shortlisted</strong> for the position of <strong>${app.jobTitle}</strong> at <strong>${app.companyName}</strong>.</p><p>Our HR team will contact you shortly to schedule the next steps.</p><p>Best regards,<br/>${app.companyName} HR Team</p>`,
      selected:`<p>Dear ${app.candidateName},</p><p>Congratulations! We are pleased to inform you that you have been <strong>selected</strong> for the position of <strong>${app.jobTitle}</strong> at <strong>${app.companyName}</strong>.</p><p>Our HR team will contact you shortly regarding the next steps including document collection and background verification.</p><p>Best regards,<br/>${app.companyName} HR Team</p>`,
    };
    try{
      await fetch("https://sendcandidateemail-mtvra6khyq-uc.a.run.app",{
        method:"POST",headers:{"Content-Type":"application/json"},
        body:JSON.stringify({to:app.email,subject:subjects[stage],html:bodies[stage],name:app.candidateName}),
      });
    }catch(e){}
  };

  const sendRejectionEmail=async(app)=>{
    try{
      await fetch("https://sendcandidateemail-mtvra6khyq-uc.a.run.app",{
        method:"POST",headers:{"Content-Type":"application/json"},
        body:JSON.stringify({
          to:app.email,
          subject:`Application Update — ${app.jobTitle} at ${app.companyName}`,
          html:`<p>Dear ${app.candidateName},</p><p>Thank you for your interest in the <strong>${app.jobTitle}</strong> position at <strong>${app.companyName}</strong>.</p><p>After careful consideration, we regret to inform you that we will not be moving forward with your application at this time.</p><p>We appreciate the time you invested in the application process and wish you all the best in your career journey.</p><p>Best regards,<br/>${app.companyName} HR Team</p>`,
          name:app.candidateName,
        }),
      });
    }catch(e){}
  };

  const sendDocumentCollectionEmail=async(app)=>{
    try{
      await fetch("https://sendcandidateemail-mtvra6khyq-uc.a.run.app",{
        method:"POST",headers:{"Content-Type":"application/json"},
        body:JSON.stringify({
          to:app.email,
          subject:`Document Submission Required — ${app.jobTitle} at ${app.companyName}`,
          html:`<p>Dear ${app.candidateName},</p><p>Congratulations on being selected for <strong>${app.jobTitle}</strong> at <strong>${app.companyName}</strong>!</p><p>Please upload the following documents using your application tracker so our HR team can review them:</p><ul><li>Last 3 months salary slips</li><li>Relieving letter from previous employer</li><li>Experience letters</li><li>Educational certificates</li><li>PAN card copy</li><li>Aadhaar card copy</li><li>Bank account details</li><li>Passport size photograph</li></ul><p><a href="https://meitho.com/track/${app.id}">Upload your documents here →</a></p><p>Best regards,<br/>${app.companyName} HR Team</p>`,
          name:app.candidateName,
        }),
      });
    }catch(e){}
  };

  const filtered=applications.filter(a=>{
    const matchJob=filterJob==="all"||a.jobId===filterJob;
    const matchStage=filterStage==="all"||a.stage===filterStage;
    const matchSearch=!search||a.candidateName?.toLowerCase().includes(search.toLowerCase())||a.email?.toLowerCase().includes(search.toLowerCase());
    return matchJob&&matchStage&&matchSearch;
  });

  if(loading)return <div style={{textAlign:"center",padding:40,color:"var(--muted)"}}>Loading...</div>;

  return(
    <div>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:20,flexWrap:"wrap",gap:10}}>
        <div>
          <h2 style={{fontFamily:FFD,fontWeight:800,fontSize:24,color:"var(--text)",marginBottom:4}}>👥 Applications</h2>
          <p style={{color:"var(--muted)",fontSize:13}}>{filtered.length} applications</p>
        </div>
        <button onClick={()=>setPage("ats_pipeline")} style={{background:"linear-gradient(90deg,#ff3131,#ff914d)",border:"none",borderRadius:9,color:"#fff",padding:"9px 18px",cursor:"pointer",fontSize:13,fontWeight:700,fontFamily:FF}}>🔄 Pipeline View</button>
      </div>

      {/* Filters */}
      <div style={{display:"flex",gap:10,marginBottom:16,flexWrap:"wrap"}}>
        <Inp value={search} onChange={e=>setSearch(e.target.value)} placeholder="Search by name or email..." style={{maxWidth:240}}/>
        <select value={filterJob} onChange={e=>setFilterJob(e.target.value)} style={{padding:"9px 12px",borderRadius:8,border:"1px solid var(--b1)",background:"var(--s2)",color:"var(--text)",fontSize:13,fontFamily:FF}}>
          <option value="all">All Jobs</option>
          {jobs.map(j=><option key={j.id} value={j.id}>{j.title}</option>)}
        </select>
        <select value={filterStage} onChange={e=>setFilterStage(e.target.value)} style={{padding:"9px 12px",borderRadius:8,border:"1px solid var(--b1)",background:"var(--s2)",color:"var(--text)",fontSize:13,fontFamily:FF}}>
          <option value="all">All Stages</option>
          {STAGES.map(s=><option key={s} value={s} style={{textTransform:"capitalize"}}>{s}</option>)}
        </select>
      </div>

      {/* Applications Table */}
      <div className="fu" style={{borderRadius:14,padding:0,overflow:"hidden"}}>
        <table style={{width:"100%",borderCollapse:"collapse"}}>
          <thead><tr style={{background:"var(--s2)"}}>
            {["Candidate","Job","Stage","CTC","Notice","Applied","Action"].map(h=><th key={h} style={{padding:"10px 12px",textAlign:"left",fontSize:10,fontWeight:700,color:"var(--muted)",textTransform:"uppercase"}}>{h}</th>)}
          </tr></thead>
          <tbody>
            {filtered.map((app,i)=>{
              const days=Math.round((Date.now()-new Date(app.appliedAt||"").getTime())/(1000*60*60*24));
              return(
                <tr key={i} style={{borderBottom:"1px solid var(--b1)",cursor:"pointer"}} onClick={()=>setPage("ats_candidate_"+app.id)}>
                  <td style={{padding:"12px 12px"}}>
                    <div style={{fontSize:13,fontWeight:600,color:"var(--text)"}}>{app.candidateName}</div>
                    <div style={{fontSize:11,color:"var(--muted)"}}>{app.email}</div>
                  </td>
                  <td style={{padding:"12px 12px",fontSize:12,color:"var(--muted)"}}>{app.jobTitle}</td>
                  <td style={{padding:"12px 12px"}}>
                    <select value={app.stage||"applied"} onClick={e=>e.stopPropagation()} onChange={e=>moveStage(app.id,e.target.value,app)} style={{padding:"4px 8px",borderRadius:6,border:"1px solid var(--b1)",background:"var(--s2)",color:"var(--text)",fontSize:11,fontFamily:FF,cursor:"pointer"}}>
                      {STAGES.map(s=><option key={s} value={s} style={{textTransform:"capitalize"}}>{s}</option>)}
                    </select>
                  </td>
                  <td style={{padding:"12px 12px",fontSize:12,color:"var(--muted)"}}>{app.currentCTC?`₹${app.currentCTC}L`:"-"}</td>
                  <td style={{padding:"12px 12px",fontSize:12,color:"var(--muted)"}}>{app.noticePeriod||"-"}</td>
                  <td style={{padding:"12px 12px"}}>
                    <div style={{fontSize:11,color:"var(--muted)"}}>{(app.appliedAt||"").slice(0,10)}</div>
                    <Badge color={days>14?"#ff3131":days>7?"#f59e0b":"var(--green)"} style={{fontSize:8,marginTop:2}}>{days}d</Badge>
                  </td>
                  <td style={{padding:"12px 12px"}} onClick={e=>e.stopPropagation()}>
                    <div style={{display:"flex",gap:6}}>
                      {app.resumeUrl&&<a href={app.resumeUrl} target="_blank" rel="noopener noreferrer" style={{background:"var(--s2)",border:"1px solid var(--b1)",borderRadius:6,color:"var(--text)",padding:"4px 8px",fontSize:10,fontWeight:600,textDecoration:"none"}}>📄 CV</a>}
                      <button onClick={()=>moveStage(app.id,"rejected",app)} style={{background:"rgba(255,49,49,0.1)",border:"none",borderRadius:6,color:"#ff3131",padding:"4px 8px",cursor:"pointer",fontSize:10,fontWeight:600,fontFamily:FF}}>Reject</button>
                    </div>
                  </td>
                </tr>
              );
            })}
            {filtered.length===0&&<tr><td colSpan={7} style={{padding:32,textAlign:"center",color:"var(--muted)",fontSize:12}}>No applications found</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── ATS Pipeline/Kanban ──
function ATSPipeline({user,company,addToast,setPage,isSuperAdmin}) {
  const {dark}=useTheme();
  const [applications,setApplications]=useState([]);
  const [jobs,setJobs]=useState([]);
  const [selectedJob,setSelectedJob]=useState("all");
  const [loading,setLoading]=useState(true);
  const companyId=user?.companyId||company?.id||"";

  const STAGES=[
    {id:"applied",label:"Applied",color:"#64748b"},
    {id:"shortlisted",label:"Shortlisted",color:"#0cc0df"},
    {id:"aptitude",label:"Aptitude",color:"#8b5cf6"},
    {id:"interview",label:"Interview",color:"#f59e0b"},
    {id:"selected",label:"Selected",color:"#10b981"},
    {id:"document",label:"Documents",color:"#ff914d"},
    {id:"bgv",label:"BGV",color:"#ff3131"},
    {id:"offer",label:"Offer",color:"#06b6d4"},
    {id:"joined",label:"Joined",color:"#10b981"},
    {id:"rejected",label:"Rejected",color:"#ef4444"},
  ];

  useEffect(()=>{loadData();},[companyId]);

  const loadData=async()=>{
    try{
      
      const [appsSnap,jobsSnap]=await Promise.all([
        isSuperAdmin?getDocs(collection(db,"ats_applications")):getDocs(query(collection(db,"ats_applications"),where("companyId","==",companyId))),
        isSuperAdmin?getDocs(collection(db,"ats_jobs")):getDocs(query(collection(db,"ats_jobs"),where("companyId","==",companyId))),
      ]);
      setApplications(appsSnap.docs.map(d=>({id:d.id,...d.data()})));
      setJobs(jobsSnap.docs.map(d=>({id:d.id,...d.data()})));
    }catch(e){}
    finally{setLoading(false);}
  };

  const moveStage=async(appId,newStage)=>{
    try{
      
      const now=new Date().toISOString();
      await updateDoc(doc(db,"ats_applications",appId),{
        stage:newStage,stageUpdatedAt:now,
        stageHistory:arrayUnion({stage:newStage,timestamp:now}),
      });
      setApplications(p=>p.map(a=>a.id===appId?{...a,stage:newStage,stageUpdatedAt:now,stageHistory:[...(a.stageHistory||[]),{stage:newStage,timestamp:now}]}:a));
      addToast("Moved to "+newStage,"success");
      // Employee File creation moved to offer acceptance (see CandidateTrackPage.respondToOffer)
    }catch(e){addToast("Failed","error");}
  };

  const filtered=selectedJob==="all"?applications:applications.filter(a=>a.jobId===selectedJob);

  if(loading)return <div style={{textAlign:"center",padding:40,color:"var(--muted)"}}>Loading...</div>;

  return(
    <div>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:20,flexWrap:"wrap",gap:10}}>
        <div>
          <h2 style={{fontFamily:FFD,fontWeight:800,fontSize:24,color:"var(--text)",marginBottom:4}}>🔄 Pipeline</h2>
          <p style={{color:"var(--muted)",fontSize:13}}>{filtered.length} candidates</p>
        </div>
        <div style={{display:"flex",gap:10,flexWrap:"wrap"}}>
          <select value={selectedJob} onChange={e=>setSelectedJob(e.target.value)} style={{padding:"8px 12px",borderRadius:8,border:"1px solid var(--b1)",background:"var(--s2)",color:"var(--text)",fontSize:13,fontFamily:FF}}>
            <option value="all">All Jobs</option>
            {jobs.map(j=><option key={j.id} value={j.id}>{j.title}</option>)}
          </select>
          <button onClick={()=>setPage("ats_applications")} style={{background:"var(--s2)",border:"1px solid var(--b1)",borderRadius:8,color:"var(--text)",padding:"8px 14px",cursor:"pointer",fontSize:12,fontWeight:600,fontFamily:FF}}>📋 List View</button>
        </div>
      </div>

      {/* Kanban Board */}
      <div style={{display:"flex",gap:12,overflowX:"auto",paddingBottom:16}}>
        {STAGES.map(stage=>{
          const stageCandidates=filtered.filter(a=>(a.stage||"applied")===stage.id);
          return(
            <div key={stage.id} style={{minWidth:220,flexShrink:0}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:10,padding:"8px 12px",background:`rgba(${stage.color==="var(--green)"?"16,185,129":stage.color==="#ff3131"?"255,49,49":stage.color==="#f59e0b"?"245,158,11":stage.color==="#0cc0df"?"12,192,223":stage.color==="#8b5cf6"?"139,92,246":"100,116,139"},0.1)`,borderRadius:10,border:`1px solid ${stage.color}22`}}>
                <div style={{fontSize:12,fontWeight:700,color:stage.color}}>{stage.label}</div>
                <Badge color={stage.color} style={{fontSize:9}}>{stageCandidates.length}</Badge>
              </div>
              <div style={{display:"flex",flexDirection:"column",gap:8,minHeight:100}}>
                {stageCandidates.map((app,i)=>{
                  const days=Math.round((Date.now()-new Date(app.stageUpdatedAt||app.appliedAt||"").getTime())/(1000*60*60*24));
                  return(
                    <div key={i} className="fu" style={{borderRadius:10,padding:"12px",cursor:"pointer",borderLeft:`3px solid ${stage.color}`}} onClick={()=>setPage("ats_candidate_"+app.id)}>
                      <div style={{fontSize:12,fontWeight:700,color:"var(--text)",marginBottom:4}}>{app.candidateName}</div>
                      <div style={{fontSize:10,color:"var(--muted)",marginBottom:6}}>{app.jobTitle}</div>
                      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                        <Badge color={days>7?"#ff3131":days>3?"#f59e0b":"var(--green)"} style={{fontSize:8}}>{days}d</Badge>
                        {app.currentCTC&&<div style={{fontSize:10,color:"var(--muted)"}}>₹{app.currentCTC}L</div>}
                      </div>
                      {/* Quick move */}
                      <select value={app.stage||"applied"} onClick={e=>e.stopPropagation()} onChange={e=>{e.stopPropagation();moveStage(app.id,e.target.value);}} style={{marginTop:8,width:"100%",padding:"4px 6px",borderRadius:6,border:"1px solid var(--b1)",background:"var(--s2)",color:"var(--muted)",fontSize:10,fontFamily:FF,cursor:"pointer"}}>
                        {STAGES.map(s=><option key={s.id} value={s.id}>{s.label}</option>)}
                      </select>
                    </div>
                  );
                })}
                {stageCandidates.length===0&&<div style={{padding:"20px",textAlign:"center",color:"var(--muted)",fontSize:11,border:"1px dashed var(--b1)",borderRadius:10}}>Empty</div>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── ATS Candidate Detail ──
function ATSCandidateDetail({user,company,addToast,setPage,candidateId,wallets,isSuperAdmin}) {
  const {dark}=useTheme();
  const [app,setApp]=useState(null);
  const [job,setJob]=useState(null);
  const [loading,setLoading]=useState(true);
  const [activeTab,setActiveTab]=useState("overview");
  const [note,setNote]=useState("");
  const [showBGVModal,setShowBGVModal]=useState(false);
  const [showInterviewModal,setShowInterviewModal]=useState(false);
  const [showOfferModal,setShowOfferModal]=useState(false);
  const [bgvChecks,setBgvChecks]=useState({uan:true,pan:true,dl:false,criminal:true});
  const [interviewForm,setInterviewForm]=useState({round:0,date:"",time:"",meetLink:"",location:"",notes:""});
  const companyId=user?.companyId||company?.id||"";
  const walletBalance=(wallets||{})[companyId]?.balance||0;

  useEffect(()=>{loadData();},[candidateId]);

  const loadData=async()=>{
    try{
      const appSnap=await getDoc(doc(db,"ats_applications",candidateId));
      if(appSnap.exists()){
        const appData={id:appSnap.id,...appSnap.data()};
        setApp(appData);
        // Load job in its own try/catch — a job-read hiccup (deleted job, stale
        // reference, permission edge case) shouldn't block the candidate page
        // itself from showing, which was previously triggering a false "Failed to load".
        if(appData.jobId){
          try{
            const jobSnap=await getDoc(doc(db,"ats_jobs",appData.jobId));
            if(jobSnap.exists())setJob({id:jobSnap.id,...jobSnap.data()});
          }catch(jobErr){console.warn("Could not load job details:",jobErr);}
        }
        if(appData.bgvChecks)setBgvChecks(appData.bgvChecks);
      }else{
        addToast("Application not found","error");
      }
    }catch(e){addToast("Failed to load application","error");}
    finally{setLoading(false);}
  };

  const updateStage=async(newStage)=>{
    try{
      const now=new Date().toISOString();
      await updateDoc(doc(db,"ats_applications",candidateId),{
        stage:newStage,stageUpdatedAt:now,
        stageHistory:arrayUnion({stage:newStage,timestamp:now,updatedBy:user?.uid||""}),
      });
      setApp(p=>({...p,stage:newStage,stageUpdatedAt:now,stageHistory:[...(p.stageHistory||[]),{stage:newStage,timestamp:now,updatedBy:user?.uid||""}]}));
      addToast("Moved to "+newStage,"success");

      const sendEmail=async(subject,html)=>{
        try{
          await fetch("https://sendcandidateemail-mtvra6khyq-uc.a.run.app",{
            method:"POST",headers:{"Content-Type":"application/json"},
            body:JSON.stringify({to:app.email,subject,html,name:app.candidateName}),
          });
        }catch(e){console.error("Email failed:",e);}
      };

      const co=app.companyName||company?.name||"";
      const jt=app.jobTitle||"";
      const cn=app.candidateName||"";

      if(newStage==="shortlisted"){
        await sendEmail(
          `You've been Shortlisted! — ${jt} at ${co}`,
          `<p>Dear ${cn},</p><p>Great news! You have been <strong>shortlisted</strong> for <strong>${jt}</strong> at <strong>${co}</strong>.</p><p>Our HR team will contact you shortly to schedule the next steps.</p><p>Best regards,<br/>${co} HR Team</p>`
        );
      }else if(newStage==="aptitude"){
        const aptLink=app.aptitudeLink||"";
        await sendEmail(
          `Aptitude Test — ${jt} at ${co}`,
          `<p>Dear ${cn},</p><p>As part of the selection process for <strong>${jt}</strong> at <strong>${co}</strong>, please complete the aptitude test.</p>${aptLink?`<p><a href="${aptLink}" style="background:#ff3131;color:white;padding:10px 20px;border-radius:8px;text-decoration:none;display:inline-block;">Take Aptitude Test →</a></p>`:"<p>Our HR team will share the test link with you shortly.</p>"}<p>Best regards,<br/>${co} HR Team</p>`
        );
      }else if(newStage==="interview"){
        await sendEmail(
          `Interview Update — ${jt} at ${co}`,
          `<p>Dear ${cn},</p><p>You have been moved to the <strong>Interview</strong> stage for <strong>${jt}</strong> at <strong>${co}</strong>.</p><p>Our HR team will reach out to schedule your interview shortly.</p><p>Best regards,<br/>${co} HR Team</p>`
        );
      }else if(newStage==="selected"){
        await sendEmail(
          `Congratulations! Selected — ${jt} at ${co}`,
          `<p>Dear ${cn},</p><p>🎉 Congratulations! You have been <strong>selected</strong> for <strong>${jt}</strong> at <strong>${co}</strong>.</p><p>Our HR team will contact you shortly regarding the next steps.</p><p>Best regards,<br/>${co} HR Team</p>`
        );
        // Document collection email
        await sendEmail(
          `Documents Required — ${jt} at ${co}`,
          `<p>Dear ${cn},</p><p>Please upload the following documents using your application tracker so our HR team can review them:</p><ul style="line-height:2"><li>Last 3 months salary slips</li><li>Relieving letter from previous employer</li><li>Experience letters</li><li>Educational certificates</li><li>PAN card copy</li><li>Aadhaar card copy</li><li>Bank account details</li><li>Passport size photograph</li></ul><p><a href="https://meitho.com/track/${candidateId}">Upload your documents here →</a></p><p>Best regards,<br/>${co} HR Team</p>`
        );
        // Employee File creation moved to offer acceptance (see CandidateTrackPage.respondToOffer)
      }else if(newStage==="rejected"){
        await sendEmail(
          `Application Update — ${jt} at ${co}`,
          `<p>Dear ${cn},</p><p>Thank you for your interest in <strong>${jt}</strong> at <strong>${co}</strong>.</p><p>After careful consideration, we regret to inform you that we will not be proceeding with your application at this time.</p><p>We appreciate the time you invested and wish you all the best in your career journey.</p><p>Best regards,<br/>${co} HR Team</p>`
        );
      }else if(newStage==="offer"){
        await sendEmail(
          `Offer Letter Coming Soon — ${jt} at ${co}`,
          `<p>Dear ${cn},</p><p>We are pleased to inform you that your offer letter for <strong>${jt}</strong> at <strong>${co}</strong> is being prepared.</p><p>Our HR team will share it with you very shortly. Once ready, you can accept or decline the offer here: <a href="https://meitho.com/track/${candidateId}">https://meitho.com/track/${candidateId}</a></p><p>Best regards,<br/>${co} HR Team</p>`
        );
      }else if(newStage==="joined"){
        await sendEmail(
          `Welcome to ${co}! 🎉`,
          `<p>Dear ${cn},</p><p>Welcome aboard! We are thrilled to have you join us as <strong>${jt}</strong> at <strong>${co}</strong>.</p><p>Wishing you a wonderful journey with us!</p><p>Best regards,<br/>${co} HR Team</p>`
        );
      }
    }catch(e){addToast("Failed","error");}
  };

  const addNote=async()=>{
    if(!note.trim())return;
    try{
      
      const newNote={text:note,timestamp:new Date().toISOString(),by:user?.displayName||user?.email||"HR"};
      await updateDoc(doc(db,"ats_applications",candidateId),{notes:arrayUnion(newNote)});
      setApp(p=>({...p,notes:[...(p.notes||[]),newNote]}));
      setNote("");
      addToast("Note added","success");
    }catch(e){addToast("Failed","error");}
  };

  const scheduleInterview=async()=>{
    if(!interviewForm.date||!interviewForm.time){addToast("Enter date and time","error");return;}
    try{
      
      const round=job?.rounds?.[interviewForm.round]||{};
      const roundName=round.name||`Round ${interviewForm.round+1}`;

      // Create each panelist's feedback request FIRST, so we can embed the
      // resulting feedbackRequestId directly on the panel entry — this is
      // what lets HR copy/share the link straight from the candidate page
      // instead of relying only on the emailed link.
      const enrichedPanels=[];
      for(const panel of (round.panels||[])){
        if(!panel.name){enrichedPanels.push(panel);continue;}
        try{
          const fbRef=await addDoc(collection(db,"ats_feedback_requests"),{
            applicationId:candidateId,
            candidateName:app.candidateName,
            jobTitle:app.jobTitle,
            companyId:app.companyId||company?.id||"",
            companyName:app.companyName||company?.name||"",
            roundName,
            panelName:panel.name,
            panelEmail:panel.email||"",
            panelDesignation:panel.designation||"",
            interviewDate:interviewForm.date,
            interviewTime:interviewForm.time,
            status:"pending",
            createdAt:new Date().toISOString(),
          });
          enrichedPanels.push({...panel,feedbackRequestId:fbRef.id});
        }catch(e){enrichedPanels.push(panel);}
      }

      const interview={
        ...interviewForm,
        roundName,
        type:round.type||"telephonic",
        panels:enrichedPanels,
        scheduledAt:new Date().toISOString(),
        status:"scheduled",
      };
      await updateDoc(doc(db,"ats_applications",candidateId),{
        interviews:arrayUnion(interview),
        stage:"interview",
        stageUpdatedAt:new Date().toISOString(),
        stageHistory:arrayUnion({stage:"interview",timestamp:new Date().toISOString()}),
      });
      setApp(p=>({...p,interviews:[...(p.interviews||[]),interview],stage:"interview",stageUpdatedAt:new Date().toISOString(),stageHistory:[...(p.stageHistory||[]),{stage:"interview",timestamp:new Date().toISOString()}]}));

      // Email candidate
      const typeLabels={telephonic:"📞 Telephonic",google_meet:"🖥️ Google Meet",face_to_face:"🤝 Face to Face"};
      await fetch("https://sendcandidateemail-mtvra6khyq-uc.a.run.app",{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({
        to:app.email,
        subject:`Interview Scheduled — ${interview.roundName} | ${app.jobTitle} at ${app.companyName}`,
        html:`<p>Dear ${app.candidateName},</p><p>Your <strong>${interview.roundName}</strong> has been scheduled.</p><table border="1" cellpadding="8" style="border-collapse:collapse"><tr><td><b>Date</b></td><td>${interview.date}</td></tr><tr><td><b>Time</b></td><td>${interview.time}</td></tr><tr><td><b>Mode</b></td><td>${typeLabels[interview.type]||interview.type}</td></tr>${interview.meetLink?`<tr><td><b>Link</b></td><td><a href="${interview.meetLink}">${interview.meetLink}</a></td></tr>`:""}${interview.location?`<tr><td><b>Location</b></td><td>${interview.location}</td></tr>`:""}</table>${interview.panels?.length>0?`<p><b>You will be meeting:</b><br/>${interview.panels.filter(p=>p.name).map(p=>`${p.name} (${p.designation})`).join("<br/>")}</p>`:""}<p>Best regards,<br/>${app.companyName} HR</p>`,
        name:app.candidateName,
      })});

      // Email each panel their feedback link (request docs already created above)
      for(const panel of enrichedPanels.filter(p=>p.email&&p.feedbackRequestId)){
        const feedbackLink=`${window.location.origin}/feedback/${panel.feedbackRequestId}`;
        await fetch("https://sendcandidateemail-mtvra6khyq-uc.a.run.app",{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({
          to:panel.email,
          subject:`Interview Scheduled — ${app.candidateName} | ${interview.roundName}`,
          html:`<p>Dear ${panel.name},</p><p>You have an interview scheduled.</p><p><b>Candidate:</b> ${app.candidateName}<br/><b>Position:</b> ${app.jobTitle}<br/><b>Date:</b> ${interview.date}<br/><b>Time:</b> ${interview.time}${interview.meetLink?`<br/><b>Meet Link:</b> <a href="${interview.meetLink}">${interview.meetLink}</a>`:""}</p><br/><p><b>Please submit your feedback after the interview:</b><br/><a href="${feedbackLink}" style="background:#ff3131;color:white;padding:10px 20px;border-radius:8px;text-decoration:none;display:inline-block;margin-top:8px;">Submit Feedback →</a></p><p>Best regards,<br/>${app.companyName} HR</p>`,
          name:panel.name,
        })});
      }

      setShowInterviewModal(false);
      addToast("Interview scheduled + emails sent!","success");
    }catch(e){addToast("Failed: "+e.message,"error");}
  };

  const initiateBGV=async()=>{
    const activeChecks=Object.entries(bgvChecks).filter(([,v])=>v);
    const totalCost=activeChecks.reduce((s,[k])=>s+getCompanyPrice(company,k==="criminal"?"ecourt":k),0);

    if(!isSuperAdmin&&walletBalance<totalCost){
      addToast(`Insufficient wallet balance! Need ₹${totalCost}, have ₹${walletBalance}`,"error");
      return;
    }

    try{
      
      await updateDoc(doc(db,"ats_applications",candidateId),{
        bgvInitiated:true,bgvInitiatedAt:new Date().toISOString(),
        bgvChecks,bgvStatus:"running",stage:"bgv",
        stageUpdatedAt:new Date().toISOString(),
      });
      setApp(p=>({...p,bgvInitiated:true,bgvStatus:"running",stage:"bgv"}));

      // Email candidate
      await fetch("https://sendcandidateemail-mtvra6khyq-uc.a.run.app",{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({
        to:app.email,
        subject:`Background Verification Initiated — ${app.companyName}`,
        html:`<p>Dear ${app.candidateName},</p><p>Your background verification has been initiated by <strong>${app.companyName}</strong>.</p><p>Checks being performed: ${activeChecks.map(([k])=>k.toUpperCase()).join(", ")}</p><p>You will be notified once the verification is complete.</p><p>Best regards,<br/>${app.companyName} HR</p>`,
        name:app.candidateName,
      })});

      setShowBGVModal(false);
      addToast("BGV initiated!","success");
    }catch(e){addToast("Failed","error");}
  };

  if(loading)return <div style={{textAlign:"center",padding:40,color:"var(--muted)"}}>Loading candidate...</div>;
  if(!app)return <div style={{textAlign:"center",padding:40,color:"var(--muted)"}}>Candidate not found</div>;

  const STAGES=["applied","shortlisted","aptitude","interview","selected","document","bgv","offer","joined","rejected"];
  const stageIndex=STAGES.indexOf(app.stage||"applied");

  return(
    <div>
      {/* Back + Header */}
      <button onClick={()=>setPage("ats_applications")} style={{background:"var(--s2)",border:"1px solid var(--b1)",borderRadius:8,color:"var(--text)",padding:"7px 14px",cursor:"pointer",fontSize:12,fontWeight:600,fontFamily:FF,marginBottom:16}}>← Back</button>

      <div className="fu" style={{borderRadius:14,padding:"20px 24px",marginBottom:16}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",flexWrap:"wrap",gap:14,marginBottom:16}}>
          <div style={{display:"flex",gap:14,alignItems:"center"}}>
            <div style={{width:52,height:52,borderRadius:14,background:"linear-gradient(135deg,#ff3131,#ff914d)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:22,color:"#fff",fontWeight:900,flexShrink:0}}>
              {(app.candidateName||"?")[0].toUpperCase()}
            </div>
            <div>
              <div style={{fontFamily:FFD,fontSize:20,fontWeight:800,color:"var(--text)",marginBottom:2}}>{app.candidateName}</div>
              <div style={{fontSize:13,color:"var(--muted)"}}>{app.email} · {app.phone}</div>
              <div style={{fontSize:12,color:"var(--muted)",marginTop:2}}>Applied for: <strong>{app.jobTitle}</strong></div>
            </div>
          </div>
          <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
            {!isSuperAdmin&&<>
              <button onClick={()=>setShowInterviewModal(true)} style={{background:"var(--s2)",border:"1px solid var(--b1)",borderRadius:8,color:"var(--text)",padding:"8px 14px",cursor:"pointer",fontSize:12,fontWeight:600,fontFamily:FF}}>📅 Schedule Interview</button>
              <button onClick={()=>setShowBGVModal(true)} style={{background:"linear-gradient(90deg,#ff3131,#ff914d)",border:"none",borderRadius:8,color:"#fff",padding:"8px 14px",cursor:"pointer",fontSize:12,fontWeight:700,fontFamily:FF}}>🔍 Initiate BGV</button>
            </>}
          </div>
        </div>

        {/* Stage Progress */}
        <div style={{display:"flex",gap:2,flexWrap:"wrap",marginBottom:16}}>
          {STAGES.filter(s=>s!=="rejected").map((s,i)=>(
            <div key={s} style={{flex:1,minWidth:60,textAlign:"center"}}>
              <div style={{height:4,background:i<=stageIndex?"linear-gradient(90deg,#ff3131,#ff914d)":"var(--s2)",borderRadius:99,marginBottom:4}}/>
              <div style={{fontSize:9,color:i===stageIndex?"#ff3131":i<stageIndex?"var(--green)":"var(--muted)",fontWeight:i===stageIndex?700:400,textTransform:"capitalize"}}>{s}</div>
            </div>
          ))}
        </div>

        {/* Quick stats */}
        <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:10}} className="mg2">
          {[["Current Stage",(app.stage||"applied").toUpperCase(),"var(--brand)"],["Experience",app.experience||"—","var(--text)"],["Current CTC",app.currentCTC?`₹${app.currentCTC}L`:"—","#ff914d"],["Notice Period",app.noticePeriod||"—","var(--muted)"]].map(([label,val,color],i)=>(
            <div key={i} style={{background:"var(--s2)",borderRadius:10,padding:"10px 12px",border:"1px solid var(--b1)"}}>
              <div style={{fontSize:10,color:"var(--muted)",marginBottom:3,fontWeight:600}}>{label}</div>
              <div style={{fontSize:13,fontWeight:700,color}}>{val}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Tabs */}
      <div style={{display:"flex",gap:4,marginBottom:14,background:"var(--s2)",borderRadius:10,padding:4,width:"fit-content"}}>
        {[["overview","Overview"],["screening","Screening"],["interviews","Interviews"],["bgv","BGV Status"],["notes","Notes"],["timeline","Timeline"]].map(([id,label])=>(
          <button key={id} onClick={()=>setActiveTab(id)} style={{padding:"6px 14px",borderRadius:7,border:"none",background:activeTab===id?"var(--bg)":"transparent",color:activeTab===id?"var(--text)":"var(--muted)",cursor:"pointer",fontSize:12,fontWeight:activeTab===id?700:500,fontFamily:FF}}>{label}</button>
        ))}
      </div>

      {/* Overview Tab */}
      {activeTab==="overview"&&(
        <div className="fu" style={{borderRadius:14,padding:"20px 24px"}}>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:14}} className="mg1">
            {[["Full Name",app.candidateName],["Email",app.email],["Phone",app.phone],["Current CTC",app.currentCTC?`₹${app.currentCTC}L`:"—"],["Expected CTC",app.expectedCTC?`₹${app.expectedCTC}L`:"—"],["Notice Period",app.noticePeriod||"—"],["Experience",app.experience||"—"],["Applied On",(app.appliedAt||"").slice(0,10)]].map(([label,val],i)=>(
              <div key={i}>
                <div style={{fontSize:11,color:"var(--muted)",fontWeight:600,marginBottom:4,textTransform:"uppercase"}}>{label}</div>
                <div style={{fontSize:13,color:"var(--text)",fontWeight:500}}>{val||"—"}</div>
              </div>
            ))}
          </div>

          {/* Candidate tracking link — HR can copy and share directly */}
          {!isSuperAdmin&&(
            <div style={{marginTop:20,paddingTop:16,borderTop:"1px solid var(--b1)",maxWidth:420}}>
              <div style={{fontSize:11,fontWeight:700,color:"var(--muted)",marginBottom:6,textTransform:"uppercase"}}>Candidate Tracking Link</div>
              <div style={{display:"flex",gap:8,alignItems:"center",background:"var(--s2)",border:"1px solid var(--b1)",borderRadius:8,padding:"8px 10px"}}>
                <div style={{flex:1,fontSize:12,color:"var(--text)",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",fontFamily:"monospace"}}>{`meitho.com/track/${candidateId}`}</div>
                <button onClick={()=>{navigator.clipboard?.writeText(`https://meitho.com/track/${candidateId}`);addToast("Link copied!","success");}} style={{background:"rgba(12,192,223,0.15)",border:"none",borderRadius:6,color:"var(--brand)",padding:"5px 10px",cursor:"pointer",fontSize:11,fontWeight:700,fontFamily:FF,flexShrink:0}}>Copy</button>
              </div>
              <div style={{fontSize:11,color:"var(--muted)",marginTop:6}}>Share this with the candidate so they can check status, upload documents, and respond to offers.</div>
            </div>
          )}

          <div style={{marginTop:20,paddingTop:16,borderTop:"1px solid var(--b1)",maxWidth:420}}>
            <ResumeUploadField companyId={companyId} candidateId={candidateId} value={app.resumeUrl} fileName={app.resumeName} dark={false} label="Resume"
              onUploaded={async(res)=>{
                try{
                  await updateDoc(doc(db,"ats_applications",candidateId),{resumeUrl:res.url,resumeName:res.name});
                  setApp(p=>({...p,resumeUrl:res.url,resumeName:res.name}));
                  addToast("Resume uploaded","success");
                }catch(e){addToast("Failed to save resume","error");}
              }}/>
          </div>

          {/* Onboarding Documents — HR review */}
          {!isSuperAdmin&&(app.documents&&Object.keys(app.documents).length>0)&&(
            <div style={{marginTop:20,paddingTop:16,borderTop:"1px solid var(--b1)"}}>
              <div style={{fontSize:12,fontWeight:700,color:"var(--muted)",marginBottom:12,textTransform:"uppercase"}}>Onboarding Documents ({ONBOARDING_DOCS.filter(d=>app.documents?.[d.key]?.status==="approved").length}/{ONBOARDING_DOCS.length} approved)</div>
              <div style={{display:"flex",flexDirection:"column",gap:10}}>
                {ONBOARDING_DOCS.map(d=>{
                  const docData=app.documents?.[d.key];
                  if(!docData?.url)return null;
                  const status=docData.status||"pending";
                  return(
                    <div key={d.key} style={{display:"flex",justifyContent:"space-between",alignItems:"center",background:"var(--s2)",border:"1px solid var(--b1)",borderRadius:10,padding:"10px 14px",flexWrap:"wrap",gap:8}}>
                      <div>
                        <div style={{fontSize:12,fontWeight:600,color:"var(--text)"}}>{d.label}</div>
                        <a href={docData.url} target="_blank" rel="noopener noreferrer" style={{fontSize:11,color:"var(--brand)",textDecoration:"none"}}>{docData.name} — View →</a>
                      </div>
                      <div style={{display:"flex",alignItems:"center",gap:8}}>
                        {status==="approved"&&<Badge color="var(--green)">✅ Approved</Badge>}
                        {status==="rejected"&&<Badge color="#ef4444">✕ Rejected</Badge>}
                        {status!=="approved"&&(
                          <button onClick={async()=>{
                            try{
                              await updateDoc(doc(db,"ats_applications",candidateId),{[`documents.${d.key}.status`]:"approved",[`documents.${d.key}.reviewedBy`]:user?.id||"",[`documents.${d.key}.reviewedAt`]:new Date().toISOString()});
                              setApp(p=>({...p,documents:{...p.documents,[d.key]:{...p.documents[d.key],status:"approved"}}}));
                              addToast(d.label+" approved","success");
                            }catch(e){addToast("Failed","error");}
                          }} style={{background:"rgba(16,185,129,0.1)",border:"1px solid rgba(16,185,129,0.25)",borderRadius:6,color:"var(--green)",padding:"5px 10px",cursor:"pointer",fontSize:11,fontWeight:700,fontFamily:FF}}>✓ Approve</button>
                        )}
                        {status!=="rejected"&&(
                          <button onClick={async()=>{
                            const reason=window.prompt("Reason for rejecting "+d.label+"?")||"";
                            try{
                              await updateDoc(doc(db,"ats_applications",candidateId),{[`documents.${d.key}.status`]:"rejected",[`documents.${d.key}.rejectReason`]:reason,[`documents.${d.key}.reviewedBy`]:user?.id||"",[`documents.${d.key}.reviewedAt`]:new Date().toISOString()});
                              setApp(p=>({...p,documents:{...p.documents,[d.key]:{...p.documents[d.key],status:"rejected",rejectReason:reason}}}));
                              addToast(d.label+" rejected","amber");
                            }catch(e){addToast("Failed","error");}
                          }} style={{background:"rgba(239,68,68,0.1)",border:"1px solid rgba(239,68,68,0.25)",borderRadius:6,color:"#ef4444",padding:"5px 10px",cursor:"pointer",fontSize:11,fontWeight:700,fontFamily:FF}}>✕ Reject</button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Offer Letter — blocked until every onboarding document is HR-approved */}
          {!isSuperAdmin&&(()=>{
            const allDocsApproved=ONBOARDING_DOCS.every(d=>app.documents?.[d.key]?.status==="approved");
            const approvalStatus=app.offerApproval?.status; // undefined | pending | approved | rejected
            return(
              <div style={{marginTop:20,paddingTop:16,borderTop:"1px solid var(--b1)"}}>
                {approvalStatus==="pending"&&(
                  <div style={{background:"rgba(245,158,11,0.08)",border:"1px solid rgba(245,158,11,0.25)",borderRadius:10,padding:"12px 16px",marginBottom:12,fontSize:12,color:"#f59e0b"}}>
                    ⏳ Offer submitted by {app.offerApproval.submittedByName||"HR"} — awaiting Company Admin approval.
                  </div>
                )}
                {approvalStatus==="rejected"&&(
                  <div style={{background:"rgba(239,68,68,0.08)",border:"1px solid rgba(239,68,68,0.25)",borderRadius:10,padding:"12px 16px",marginBottom:12,fontSize:12,color:"#ef4444"}}>
                    ✕ Offer rejected{app.offerApproval.rejectReason?": "+app.offerApproval.rejectReason:""}. You can revise and resubmit.
                  </div>
                )}
                {approvalStatus==="approved"&&(
                  <div style={{background:"rgba(16,185,129,0.08)",border:"1px solid rgba(16,185,129,0.25)",borderRadius:10,padding:"12px 16px",marginBottom:12,fontSize:12,color:"#10b981"}}>
                    ✅ Offer approved by {app.offerApproval.approvedByName||"Company Admin"} — visible to candidate on their tracker.
                  </div>
                )}
                {allDocsApproved?(
                  <button onClick={()=>setShowOfferModal(true)} style={{background:"linear-gradient(90deg,#ff3131,#ff914d)",border:"none",borderRadius:10,color:"#fff",padding:"11px 22px",cursor:"pointer",fontSize:13,fontWeight:700,fontFamily:FF,display:"inline-flex",alignItems:"center",gap:8}}>
                    📄 {approvalStatus==="approved"?"Regenerate Offer Letter":approvalStatus==="pending"?"Edit Submission":"Generate Offer Letter"}
                  </button>
                ):(
                  <div>
                    <button disabled style={{background:"var(--s2)",border:"1px solid var(--b1)",borderRadius:10,color:"var(--muted)",padding:"11px 22px",cursor:"not-allowed",fontSize:13,fontWeight:700,fontFamily:FF,display:"inline-flex",alignItems:"center",gap:8}}>
                      🔒 Generate Offer Letter
                    </button>
                    <div style={{fontSize:11,color:"var(--muted)",marginTop:6}}>All onboarding documents must be approved first ({ONBOARDING_DOCS.filter(d=>app.documents?.[d.key]?.status==="approved").length}/{ONBOARDING_DOCS.length} approved).</div>
                  </div>
                )}
              </div>
            );
          })()}

          {/* Stage Actions */}
          {!isSuperAdmin&&<div style={{marginTop:20,paddingTop:16,borderTop:"1px solid var(--b1)"}}>
            <div style={{fontSize:12,fontWeight:700,color:"var(--muted)",marginBottom:10,textTransform:"uppercase"}}>Move Stage</div>
            <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
              {STAGES.filter(s=>s!==app.stage).map(s=>(
                <button key={s} onClick={()=>updateStage(s)} style={{background:"var(--s2)",border:"1px solid var(--b1)",borderRadius:8,color:s==="rejected"?"#ff3131":"var(--text)",padding:"6px 12px",cursor:"pointer",fontSize:11,fontWeight:600,fontFamily:FF,textTransform:"capitalize"}}>{s}</button>
              ))}
            </div>
          </div>}
        </div>
      )}

      {/* Screening Tab — consolidated view: screening answers, panel feedback,
          HR notes, and the candidate's own post-process feedback, all scoped
          to this one candidate. */}
      {activeTab==="screening"&&(
        <div style={{display:"flex",flexDirection:"column",gap:16}}>
          <div className="fu" style={{borderRadius:14,padding:"20px 24px"}}>
            <div style={{fontFamily:FFD,fontSize:13,fontWeight:700,color:"var(--text)",marginBottom:14}}>📋 Screening Question Answers</div>
            {job?.screeningQuestions?.length>0?(
              app.screeningAnswers?(
                job.screeningQuestions.map((q,qi)=>(
                  <div key={q.id} style={{padding:"10px 0",borderBottom:qi<job.screeningQuestions.length-1?"1px solid var(--b1)":"none"}}>
                    <div style={{fontSize:12,color:"var(--muted)",marginBottom:4}}>{qi+1}. {q.text}</div>
                    <div style={{fontSize:13,fontWeight:600,color:"var(--text)"}}>{app.screeningAnswers.answers?.[q.id]||<span style={{color:"var(--muted)",fontWeight:400,fontStyle:"italic"}}>Skipped</span>}</div>
                  </div>
                ))
              ):(
                <div style={{textAlign:"center",padding:20,color:"var(--muted)",fontSize:12}}>Candidate hasn't answered yet.</div>
              )
            ):(
              <div style={{textAlign:"center",padding:20,color:"var(--muted)",fontSize:12}}>No screening questions were set on this job posting.</div>
            )}
          </div>

          <div className="fu" style={{borderRadius:14,padding:"20px 24px"}}>
            <div style={{fontFamily:FFD,fontSize:13,fontWeight:700,color:"var(--text)",marginBottom:14}}>⭐ Panel Feedback</div>
            {Object.keys(app.interviewFeedback||{}).length>0?(
              Object.values(app.interviewFeedback).sort((a,b)=>new Date(a.submittedAt||0)-new Date(b.submittedAt||0)).map((f,i)=>(
                <div key={i} style={{padding:"12px 0",borderBottom:i<Object.keys(app.interviewFeedback).length-1?"1px solid var(--b1)":"none"}}>
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:4}}>
                    <div style={{fontSize:13,fontWeight:700,color:"var(--text)"}}>{f.panelName}</div>
                    <Badge color={f.decision==="proceed"?"var(--green)":f.decision==="hold"?"#f59e0b":"#ef4444"} style={{fontSize:9}}>{f.decision==="proceed"?"✅ Proceed":f.decision==="hold"?"⏸ Hold":"❌ Reject"}</Badge>
                  </div>
                  <div style={{fontSize:12,color:"var(--muted)",marginBottom:4}}>⭐ {f.overall}/5 overall — {f.roundName}</div>
                  {f.comments&&<div style={{fontSize:12,color:"var(--text)"}}>{f.comments}</div>}
                </div>
              ))
            ):(
              <div style={{textAlign:"center",padding:20,color:"var(--muted)",fontSize:12}}>No panel feedback submitted yet.</div>
            )}
          </div>

          <div className="fu" style={{borderRadius:14,padding:"20px 24px"}}>
            <div style={{fontFamily:FFD,fontSize:13,fontWeight:700,color:"var(--text)",marginBottom:14}}>📝 HR Notes</div>
            {(app.notes||[]).length>0?(app.notes||[]).map((n,i)=>(
              <div key={i} style={{padding:"8px 0",borderBottom:i<(app.notes.length-1)?"1px solid var(--b1)":"none"}}>
                <div style={{fontSize:13,color:"var(--text)"}}>{n.text}</div>
                <div style={{fontSize:11,color:"var(--muted)",marginTop:2}}>{n.by} · {(n.timestamp||"").slice(0,10)}</div>
              </div>
            )):(
              <div style={{textAlign:"center",padding:20,color:"var(--muted)",fontSize:12}}>No notes yet — add one from the Notes tab.</div>
            )}
          </div>

          <div className="fu" style={{borderRadius:14,padding:"20px 24px"}}>
            <div style={{fontFamily:FFD,fontSize:13,fontWeight:700,color:"var(--text)",marginBottom:14}}>💬 Candidate's Own Feedback</div>
            {app.candidateProcessFeedback?(
              <div>
                <div style={{fontSize:13,fontWeight:700,color:"var(--text)",marginBottom:4}}>⭐ {app.candidateProcessFeedback.rating}/5</div>
                {app.candidateProcessFeedback.comments&&<div style={{fontSize:13,color:"var(--text)"}}>{app.candidateProcessFeedback.comments}</div>}
                <div style={{fontSize:11,color:"var(--muted)",marginTop:6}}>{(app.candidateProcessFeedback.submittedAt||"").slice(0,16).replace("T"," ")}</div>
              </div>
            ):(
              <div style={{textAlign:"center",padding:20,color:"var(--muted)",fontSize:12}}>Candidate hasn't submitted this yet — it becomes available after they accept an offer.</div>
            )}
          </div>
        </div>
      )}

      {/* Interviews Tab */}
      {activeTab==="interviews"&&(
        <div className="fu" style={{borderRadius:14,padding:"20px 24px"}}>
          <div style={{fontFamily:FFD,fontSize:13,fontWeight:700,color:"var(--text)",marginBottom:14}}>Interview History</div>
          {(app.interviews||[]).length>0?(app.interviews||[]).map((interview,i)=>(
            <div key={i} style={{background:"var(--s2)",borderRadius:12,padding:"14px 16px",marginBottom:10,border:"1px solid var(--b1)"}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:8}}>
                <div style={{fontFamily:FFD,fontSize:13,fontWeight:700,color:"var(--text)"}}>{interview.roundName}</div>
                <Badge color="var(--brand)" style={{fontSize:9,textTransform:"capitalize"}}>{interview.type?.replace("_"," ")}</Badge>
              </div>
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,marginBottom:8}}>
                {[["📅 Date",interview.date],["⏰ Time",interview.time],["⏱️ Duration",(job?.rounds?.[interview.round]?.duration||"—")+" mins"],["📍 Location",interview.meetLink?"Google Meet":interview.location||"—"]].map(([label,val],j)=>(
                  <div key={j} style={{fontSize:12,color:"var(--muted)"}}><span style={{fontWeight:600}}>{label}:</span> {val}</div>
                ))}
              </div>
              {interview.panels?.length>0&&(
                <div style={{marginTop:8}}>
                  <div style={{fontSize:11,fontWeight:600,color:"var(--muted)",marginBottom:6}}>Panel Feedback:</div>
                  {interview.panels.filter(p=>p.name).map((p,j)=>{
                    const submission=p.feedbackRequestId?app.interviewFeedback?.[p.feedbackRequestId]:null;
                    return(
                      <div key={j} style={{display:"flex",justifyContent:"space-between",alignItems:"center",background:"var(--bg)",border:"1px solid var(--b1)",borderRadius:8,padding:"8px 10px",marginBottom:6,flexWrap:"wrap",gap:6}}>
                        <div>
                          <div style={{fontSize:12,fontWeight:600,color:"var(--text)"}}>{p.name} <span style={{fontWeight:400,color:"var(--muted)"}}>— {p.designation}</span></div>
                          {submission&&(
                            <div style={{fontSize:11,color:"var(--muted)",marginTop:2}}>
                              ⭐ {submission.overall}/5 overall · {submission.decision==="proceed"?"✅ Proceed":submission.decision==="hold"?"⏸ On Hold":"❌ Reject"}
                            </div>
                          )}
                        </div>
                        {submission?(
                          <Badge color="var(--green)" style={{fontSize:9}}>✅ Submitted</Badge>
                        ):p.feedbackRequestId?(
                          <button onClick={()=>{navigator.clipboard?.writeText(`${window.location.origin}/feedback/${p.feedbackRequestId}`);addToast("Feedback link copied!","success");}} style={{background:"rgba(12,192,223,0.12)",border:"none",borderRadius:6,color:"#0cc0df",padding:"4px 10px",cursor:"pointer",fontSize:10,fontWeight:700,fontFamily:FF}}>🔗 Copy Link</button>
                        ):(
                          <Badge color="var(--muted)" style={{fontSize:9}}>⏳ Pending</Badge>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
              {interview.meetLink&&<a href={interview.meetLink} target="_blank" rel="noopener noreferrer" style={{display:"inline-flex",alignItems:"center",gap:4,marginTop:8,fontSize:11,color:"#0cc0df",textDecoration:"none",fontWeight:600}}>🔗 Join Meet</a>}
            </div>
          )):<div style={{textAlign:"center",padding:32,color:"var(--muted)",fontSize:12}}>No interviews scheduled yet</div>}
        </div>
      )}

      {activeTab==="bgv"&&(
        <div className="fu" style={{borderRadius:14,padding:"20px 24px"}}>
          <div style={{fontFamily:FFD,fontSize:13,fontWeight:700,color:"var(--text)",marginBottom:14}}>🔍 BGV Status</div>
          {app.bgvInitiated?(
            <div>
              <div style={{display:"flex",gap:8,alignItems:"center",marginBottom:16,padding:"12px 16px",background:"var(--s2)",borderRadius:10,border:"1px solid var(--b1)"}}>
                <Badge color={app.bgvStatus==="completed"?"var(--green)":app.bgvStatus==="running"?"#f59e0b":"var(--muted)"}>{app.bgvStatus||"running"}</Badge>
                <div style={{fontSize:12,color:"var(--muted)"}}>Initiated: {(app.bgvInitiatedAt||"").slice(0,10)}</div>
                <div style={{fontSize:12,color:"var(--muted)",marginLeft:"auto"}}>by {app.bgvInitiatedBy||user?.displayName||"HR"}</div>
              </div>
              <div style={{display:"grid",gap:8}}>
                {Object.entries(app.bgvChecks||{}).filter(([,v])=>v).map(([check],i)=>{
                  const labels={uan:"UAN / Employment",pan:"PAN Verification",dl:"Driving License",criminal:"Criminal Check",refcheck:"Reference Check"};
                  const icons={uan:"🏦",pan:"📋",dl:"🚗",criminal:"⚖️",refcheck:"📧"};
                  const result=app.bgvResults?.[check];
                  return(
                    <div key={i} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"12px 16px",background:"var(--s2)",borderRadius:10,border:"1px solid var(--b1)"}}>
                      <div style={{display:"flex",gap:10,alignItems:"center"}}>
                        <span style={{fontSize:18}}>{icons[check]||"🔍"}</span>
                        <div>
                          <div style={{fontSize:13,fontWeight:600,color:"var(--text)"}}>{labels[check]||check.toUpperCase()}</div>
                          {result?.summary&&<div style={{fontSize:11,color:"var(--muted)",marginTop:2}}>{result.summary}</div>}
                        </div>
                      </div>
                      <Badge color={result?.status==="clear"?"var(--green)":result?.status==="flagged"?"#ff3131":result?.status==="found"?"var(--green)":"#f59e0b"} style={{fontSize:9}}>
                        {result?.status||"Running"}
                      </Badge>
                    </div>
                  );
                })}
              </div>
              {!isSuperAdmin&&app.bgvStatus!=="completed"&&(
                <div style={{marginTop:12,padding:"10px 14px",background:"rgba(245,158,11,0.06)",border:"1px solid rgba(245,158,11,0.2)",borderRadius:8,fontSize:12,color:"#f59e0b"}}>
                  ⏳ Verification in progress. Results will appear here once complete.
                </div>
              )}
            </div>
          ):(
            <div style={{textAlign:"center",padding:"32px 20px"}}>
              <div style={{fontSize:40,marginBottom:12}}>🔍</div>
              <div style={{fontSize:14,color:"var(--muted)",marginBottom:16}}>BGV not initiated yet</div>
              <div style={{fontSize:12,color:"var(--muted)",marginBottom:16}}>
                Checks configured for this job:<br/>
                {Object.entries(job?.bgvChecks||{uan:true,pan:true}).filter(([,v])=>v).map(([k])=>k.toUpperCase()).join(", ")}
              </div>
              {!isSuperAdmin&&<button onClick={()=>setShowBGVModal(true)} style={{background:"linear-gradient(90deg,#ff3131,#ff914d)",border:"none",borderRadius:9,color:"#fff",padding:"10px 24px",cursor:"pointer",fontSize:13,fontWeight:700,fontFamily:FF}}>🔍 Initiate BGV</button>}
            </div>
          )}
        </div>
      )}

      {/* Notes Tab */}
      {activeTab==="notes"&&(
        <div className="fu" style={{borderRadius:14,padding:"20px 24px"}}>
          {app.candidateProcessFeedback&&(
            <div style={{background:"rgba(12,192,223,0.06)",border:"1px solid rgba(12,192,223,0.2)",borderRadius:10,padding:"14px 16px",marginBottom:20}}>
              <div style={{fontSize:11,fontWeight:700,color:"#0cc0df",textTransform:"uppercase",letterSpacing:1,marginBottom:6}}>💬 Candidate's Interview Process Feedback</div>
              <div style={{fontSize:13,fontWeight:700,color:"var(--text)",marginBottom:4}}>⭐ {app.candidateProcessFeedback.rating}/5</div>
              {app.candidateProcessFeedback.comments&&<div style={{fontSize:12,color:"var(--muted)"}}>{app.candidateProcessFeedback.comments}</div>}
              <div style={{fontSize:10,color:"var(--muted)",marginTop:6}}>{(app.candidateProcessFeedback.submittedAt||"").slice(0,16).replace("T"," ")}</div>
            </div>
          )}
          <div style={{fontFamily:FFD,fontSize:13,fontWeight:700,color:"var(--text)",marginBottom:14}}>Notes</div>
          {!isSuperAdmin&&<div style={{display:"flex",gap:10,marginBottom:16}}>
            <Inp value={note} onChange={e=>setNote(e.target.value)} placeholder="Add a note..." style={{flex:1}}/>
            <Btn onClick={addNote}>Add</Btn>
          </div>}
          {(app.notes||[]).length>0?(app.notes||[]).map((n,i)=>(
            <div key={i} style={{padding:"10px 14px",background:"var(--s2)",borderRadius:10,marginBottom:8,border:"1px solid var(--b1)"}}>
              <div style={{fontSize:13,color:"var(--text)",marginBottom:4}}>{n.text}</div>
              <div style={{fontSize:11,color:"var(--muted)"}}>{n.by} · {(n.timestamp||"").slice(0,10)}</div>
            </div>
          )):<div style={{textAlign:"center",padding:32,color:"var(--muted)",fontSize:12}}>No notes yet</div>}
        </div>
      )}

      {/* Timeline Tab */}
      {activeTab==="timeline"&&(()=>{
        // Merge stage changes with panel feedback submissions into one
        // chronological timeline — this is what makes feedback usable for
        // reporting instead of being buried in a separate data field.
        const stageEvents=(app.stageHistory||[]).map(h=>({type:"stage",timestamp:h.timestamp,stage:h.stage}));
        const feedbackEvents=Object.values(app.interviewFeedback||{}).map(f=>({type:"feedback",timestamp:f.submittedAt,panelName:f.panelName,decision:f.decision,overall:f.overall}));
        const allEvents=[...stageEvents,...feedbackEvents].sort((a,b)=>new Date(b.timestamp||0)-new Date(a.timestamp||0));
        return(
          <div className="fu" style={{borderRadius:14,padding:"20px 24px"}}>
            <div style={{fontFamily:FFD,fontSize:13,fontWeight:700,color:"var(--text)",marginBottom:14}}>Timeline</div>
            {allEvents.length>0?allEvents.map((h,i)=>(
              <div key={i} style={{display:"flex",gap:12,marginBottom:12}}>
                <div style={{width:32,height:32,borderRadius:"50%",background:h.type==="feedback"?"rgba(12,192,223,0.1)":"rgba(255,49,49,0.1)",border:`2px solid ${h.type==="feedback"?"rgba(12,192,223,0.2)":"rgba(255,49,49,0.2)"}`,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,fontSize:13}}>
                  {h.type==="feedback"?"⭐":<div style={{width:8,height:8,borderRadius:"50%",background:"#ff3131"}}/>}
                </div>
                <div style={{paddingTop:6}}>
                  {h.type==="feedback"?(
                    <>
                      <div style={{fontSize:13,fontWeight:600,color:"var(--text)"}}>Panel feedback from {h.panelName}</div>
                      <div style={{fontSize:11,color:"var(--muted)"}}>{h.overall}/5 overall · {h.decision==="proceed"?"✅ Proceed":h.decision==="hold"?"⏸ On Hold":"❌ Reject"}</div>
                    </>
                  ):(
                    <div style={{fontSize:13,fontWeight:600,color:"var(--text)",textTransform:"capitalize"}}>{h.stage}</div>
                  )}
                  <div style={{fontSize:11,color:"var(--muted)"}}>{(h.timestamp||"").slice(0,16).replace("T"," ")}</div>
                </div>
              </div>
            )):<div style={{textAlign:"center",padding:32,color:"var(--muted)",fontSize:12}}>No timeline data</div>}
          </div>
        );
      })()}

      {/* BGV Modal */}
      {showBGVModal&&(
        <Modal title="🔍 Initiate BGV" onClose={()=>setShowBGVModal(false)}>
          <div style={{marginBottom:16}}>
            <div style={{fontSize:13,color:"var(--muted)",marginBottom:12}}>Select checks to run for {app.candidateName}:</div>
            {[["uan","UAN / Employment",100],["pan","PAN Verification",50],["dl","Driving License",50],["criminal","Criminal Check",75],["refcheck","Reference Check",125]].map(([key,label,price])=>(
              <div key={key} onClick={()=>setBgvChecks(p=>({...p,[key]:!p[key]}))} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"10px 12px",borderRadius:8,border:`1px solid ${bgvChecks[key]?"#ff3131":"var(--b1)"}`,background:bgvChecks[key]?"rgba(255,49,49,0.04)":"transparent",cursor:"pointer",marginBottom:8}}>
                <div style={{fontSize:13,fontWeight:500,color:"var(--text)"}}>{label}</div>
                <div style={{display:"flex",gap:10,alignItems:"center"}}>
                  <div style={{fontSize:12,color:"#ff3131",fontWeight:700}}>₹{price}</div>
                  <div style={{width:18,height:18,borderRadius:"50%",background:bgvChecks[key]?"#ff3131":"transparent",border:`2px solid ${bgvChecks[key]?"#ff3131":"var(--b1)"}`,display:"flex",alignItems:"center",justifyContent:"center"}}>
                    {bgvChecks[key]&&<svg width="8" height="8" viewBox="0 0 8 8" fill="none"><path d="M1.5 4l2 2 3-3" stroke="white" strokeWidth="1.5" strokeLinecap="round"/></svg>}
                  </div>
                </div>
              </div>
            ))}
            {!isSuperAdmin&&(
              <div style={{marginTop:12,padding:"10px 14px",background:"var(--s2)",borderRadius:8,border:"1px solid var(--b1)"}}>
                <div style={{display:"flex",justifyContent:"space-between",fontSize:13}}>
                  <span style={{color:"var(--muted)"}}>Total Cost:</span>
                  <span style={{fontWeight:700,color:"#ff3131"}}>₹{Object.entries(bgvChecks).filter(([,v])=>v).reduce((s,[k])=>s+getCompanyPrice(company,k==="criminal"?"ecourt":k),0).toLocaleString("en-IN")}</span>
                </div>
                <div style={{display:"flex",justifyContent:"space-between",fontSize:13,marginTop:4}}>
                  <span style={{color:"var(--muted)"}}>Wallet Balance:</span>
                  <span style={{fontWeight:700,color:walletBalance>0?"var(--green)":"#ff3131"}}>₹{walletBalance.toLocaleString("en-IN")}</span>
                </div>
              </div>
            )}
          </div>
          <div style={{display:"flex",gap:10}}>
            <Btn onClick={initiateBGV}>🔍 Start BGV</Btn>
            <BtnGhost onClick={()=>setShowBGVModal(false)}>Cancel</BtnGhost>
          </div>
        </Modal>
      )}

      {/* Offer Letter Modal */}
      {showOfferModal&&(
        <Modal title="📄 Generate Offer Letter" onClose={()=>setShowOfferModal(false)}>
          <ATSOfferGenerator application={app} company={company} user={user} addToast={addToast} onClose={()=>setShowOfferModal(false)}/>
        </Modal>
      )}

      {/* Schedule Interview Modal */}
      {showInterviewModal&&(
        <Modal title="📅 Schedule Interview" onClose={()=>setShowInterviewModal(false)}>
          <div style={{marginBottom:16}}>
            {job?.rounds?.length>0&&(
              <div style={{marginBottom:12}}>
                <Label>Select Round</Label>
                <select value={interviewForm.round} onChange={e=>setInterviewForm(p=>({...p,round:parseInt(e.target.value)}))} style={{width:"100%",padding:"9px 12px",borderRadius:8,border:"1px solid var(--b1)",background:"var(--s2)",color:"var(--text)",fontSize:13,fontFamily:FF}}>
                  {job.rounds.map((r,i)=><option key={i} value={i}>{r.name||`Round ${i+1}`} ({r.type?.replace("_"," ")})</option>)}
                </select>
              </div>
            )}
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:12}}>
              <div><Label>Date *</Label><Inp type="date" value={interviewForm.date} onChange={e=>setInterviewForm(p=>({...p,date:e.target.value}))}/></div>
              <div><Label>Time *</Label><Inp type="time" value={interviewForm.time} onChange={e=>setInterviewForm(p=>({...p,time:e.target.value}))}/></div>
            </div>
            {job?.rounds?.[interviewForm.round]?.type==="google_meet"&&<div style={{marginBottom:10}}><Label>Google Meet Link</Label><Inp value={interviewForm.meetLink} onChange={e=>setInterviewForm(p=>({...p,meetLink:e.target.value}))} placeholder="paste meet.google.com/xxx link"/></div>}
            {job?.rounds?.[interviewForm.round]?.type==="face_to_face"&&<div style={{marginBottom:10}}><Label>Venue/Location</Label><Inp value={interviewForm.location} onChange={e=>setInterviewForm(p=>({...p,location:e.target.value}))} placeholder="Office address"/></div>}
            <div><Label>Notes (optional)</Label><Inp value={interviewForm.notes} onChange={e=>setInterviewForm(p=>({...p,notes:e.target.value}))} placeholder="Any notes for candidate"/></div>
            {job?.rounds?.[interviewForm.round]?.panels?.length>0&&(
              <div style={{marginTop:12,padding:"10px 14px",background:"var(--s2)",borderRadius:8,border:"1px solid var(--b1)"}}>
                <div style={{fontSize:11,fontWeight:600,color:"var(--muted)",marginBottom:6}}>PANEL MEMBERS (emails will be sent):</div>
                {job.rounds[interviewForm.round].panels.filter(p=>p.name).map((p,i)=>(
                  <div key={i} style={{fontSize:12,color:"var(--text)",marginBottom:2}}>• {p.name} ({p.designation}) — {p.email}</div>
                ))}
              </div>
            )}
          </div>
          <div style={{display:"flex",gap:10}}>
            <Btn onClick={scheduleInterview}>📅 Schedule + Send Emails</Btn>
            <BtnGhost onClick={()=>setShowInterviewModal(false)}>Cancel</BtnGhost>
          </div>
        </Modal>
      )}
    </div>
  );
}




// ── Public Career Page ──
// ── Meitho Select Overview (Super Admin) ──
function ATSOverview({companies,addToast}) {
  const {dark}=useTheme();
  const [jobs,setJobs]=useState([]);
  const [apps,setApps]=useState([]);
  const [loading,setLoading]=useState(true);

  useEffect(()=>{
    Promise.all([
      getDocs(collection(db,"ats_jobs")),
      getDocs(collection(db,"ats_applications")),
    ]).then(([j,a])=>{
      setJobs(j.docs.map(d=>({id:d.id,...d.data()})));
      setApps(a.docs.map(d=>({id:d.id,...d.data()})));
    }).catch(e=>console.error(e))
    .finally(()=>setLoading(false));
  },[]);

  const toggleATS=async(cid,current)=>{
    try{
      await updateDoc(doc(db,"companies",cid),{atsEnabled:!current});
      addToast("ATS "+(current?"disabled":"enabled"),"success");
    }catch(e){addToast("Failed","error");}
  };

  if(loading)return <div style={{textAlign:"center",padding:60,color:"var(--muted)"}}>
    <div style={{fontSize:32,marginBottom:12}}>🎯</div>
    <div>Loading Meitho Select...</div>
  </div>;

  return(
    <div>
      <div style={{marginBottom:24}}>
        <h2 style={{fontFamily:FFD,fontWeight:800,fontSize:24,color:"var(--text)",marginBottom:4}}>🎯 Meitho Select — Overview</h2>
        <p style={{color:"var(--muted)",fontSize:13}}>All companies recruitment management</p>
      </div>
      <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:14,marginBottom:24}} className="mg2">
        {[["📋","Total Jobs",jobs.length,"var(--brand)"],["✅","Active Jobs",jobs.filter(j=>j.status==="active").length,"var(--green)"],["👥","Applications",apps.length,"#ff914d"],["🔍","BGV Triggered",apps.filter(a=>a.bgvInitiated).length,"#ff3131"]].map(([icon,label,val,color],i)=>(
          <div key={i} className="fu" style={{padding:"16px 18px",borderRadius:12}}>
            <div style={{fontSize:22,marginBottom:8}}>{icon}</div>
            <div style={{fontFamily:FFD,fontSize:28,fontWeight:900,color,marginBottom:3}}>{val}</div>
            <div style={{fontSize:11,color:"var(--muted)",fontWeight:600}}>{label}</div>
          </div>
        ))}
      </div>
      <div className="fu" style={{borderRadius:14,padding:0,overflow:"hidden",marginBottom:16}}>
        <div style={{padding:"12px 16px",borderBottom:"1px solid var(--b1)",fontFamily:FFD,fontSize:13,fontWeight:700,color:"var(--text)"}}>Companies ({(companies||[]).length})</div>
        <table style={{width:"100%",borderCollapse:"collapse"}}>
          <thead><tr style={{background:"var(--s2)"}}>
            {["Company","Jobs","Applications","ATS","Action"].map(h=><th key={h} style={{padding:"8px 12px",textAlign:"left",fontSize:10,fontWeight:700,color:"var(--muted)",textTransform:"uppercase"}}>{h}</th>)}
          </tr></thead>
          <tbody>
            {(companies||[]).length===0?<tr><td colSpan={5} style={{padding:32,textAlign:"center",color:"var(--muted)",fontSize:12}}>No companies found</td></tr>:
            (companies||[]).map((c,i)=>(
              <tr key={i} style={{borderBottom:"1px solid var(--b1)"}}>
                <td style={{padding:"10px 12px",fontSize:13,fontWeight:600,color:"var(--text)"}}>{c.name}</td>
                <td style={{padding:"10px 12px",fontSize:12,color:"var(--muted)"}}>{jobs.filter(j=>j.companyId===c.id).length}</td>
                <td style={{padding:"10px 12px",fontSize:12,color:"var(--muted)"}}>{apps.filter(a=>a.companyId===c.id).length}</td>
                <td style={{padding:"10px 12px"}}><Badge color={c.atsEnabled?"var(--green)":"var(--muted)"}>{c.atsEnabled?"On":"Off"}</Badge></td>
                <td style={{padding:"10px 12px"}}>
                  <button onClick={()=>toggleATS(c.id,c.atsEnabled)} style={{background:c.atsEnabled?"rgba(255,49,49,0.1)":"rgba(16,185,129,0.1)",border:"none",borderRadius:6,color:c.atsEnabled?"#ff3131":"var(--green)",padding:"5px 12px",cursor:"pointer",fontSize:11,fontWeight:600,fontFamily:FF}}>
                    {c.atsEnabled?"Disable":"Enable"}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="fu" style={{borderRadius:14,padding:0,overflow:"hidden"}}>
        <div style={{padding:"12px 16px",borderBottom:"1px solid var(--b1)",fontFamily:FFD,fontSize:13,fontWeight:700,color:"var(--text)"}}>All Jobs ({jobs.length})</div>
        <table style={{width:"100%",borderCollapse:"collapse"}}>
          <thead><tr style={{background:"var(--s2)"}}>
            {["Title","Company","Status","Applications","Date"].map(h=><th key={h} style={{padding:"8px 12px",textAlign:"left",fontSize:10,fontWeight:700,color:"var(--muted)",textTransform:"uppercase"}}>{h}</th>)}
          </tr></thead>
          <tbody>
            {jobs.length===0?<tr><td colSpan={5} style={{padding:32,textAlign:"center",color:"var(--muted)",fontSize:12}}>No jobs posted yet</td></tr>:
            jobs.slice(0,25).map((j,i)=>(
              <tr key={i} style={{borderBottom:"1px solid var(--b1)"}}>
                <td style={{padding:"10px 12px",fontSize:13,fontWeight:600,color:"var(--text)"}}>{j.title}</td>
                <td style={{padding:"10px 12px",fontSize:12,color:"var(--muted)"}}>{j.companyName||"—"}</td>
                <td style={{padding:"10px 12px"}}><Badge color={j.status==="active"?"var(--green)":j.status==="draft"?"#f59e0b":"var(--muted)"} style={{fontSize:9,textTransform:"capitalize"}}>{j.status||"draft"}</Badge></td>
                <td style={{padding:"10px 12px",fontSize:12,color:"var(--muted)"}}>{j.applicationsCount||0}</td>
                <td style={{padding:"10px 12px",fontSize:11,color:"var(--muted)"}}>{(j.createdAt||"").slice(0,10)||"—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════
   EMPLOYEE FILES — Meitho Select. Shows candidates who fully joined
   (offer accepted + all onboarding documents approved). Each entry's
   documents were auto-saved at the moment they accepted their offer.
══════════════════════════════════════════════════════════════ */
function EmployeeFilesPage({user,company,companies,isSuperAdmin,addToast}) {
  const {dark}=useTheme();
  const [files,setFiles]=useState([]);
  const [loading,setLoading]=useState(true);
  const [search,setSearch]=useState("");
  const [expanded,setExpanded]=useState(null);
  const companyId=user?.companyId||company?.id||"";

  useEffect(()=>{
    setLoading(true);
    const loader=isSuperAdmin?getAllEmployeeFiles():getCompanyEmployeeFiles(companyId);
    loader.then(setFiles).catch(()=>addToast("Failed to load employee files","error")).finally(()=>setLoading(false));
  },[companyId,isSuperAdmin]);

  const companyName=(cid)=>companies?.find(c=>c.id===cid)?.name||"—";
  const filtered=files.filter(f=>!search||(f.candidateName||"").toLowerCase().includes(search.toLowerCase())||(f.email||"").toLowerCase().includes(search.toLowerCase()));

  if(loading)return <div style={{textAlign:"center",padding:60,color:"var(--muted)"}}>
    <div style={{fontSize:32,marginBottom:12}}>🗂️</div>
    <div>Loading employee files...</div>
  </div>;

  return(
    <div>
      <div style={{marginBottom:20}}>
        <h2 style={{fontFamily:FFD,fontWeight:800,fontSize:24,color:"var(--text)",marginBottom:4}}>🗂️ Employee Files</h2>
        <p style={{color:"var(--muted)",fontSize:13}}>{isSuperAdmin?"All joined employees across every company":"Employees who have fully joined — offer accepted, all documents approved"}</p>
      </div>
      <Inp value={search} onChange={e=>setSearch(e.target.value)} placeholder="Search by name or email..." style={{maxWidth:280,marginBottom:16}}/>

      {filtered.length===0?(
        <div className="fu" style={{borderRadius:14,padding:48,textAlign:"center",color:"var(--muted)"}}>
          <div style={{fontSize:32,marginBottom:10}}>📭</div>
          <div style={{fontSize:13}}>No employee files yet. They're created automatically once a candidate accepts their offer with all documents approved.</div>
        </div>
      ):(
        <div style={{display:"flex",flexDirection:"column",gap:10}}>
          {filtered.map((f,i)=>{
            const isOpen=expanded===f.id;
            const docCount=Object.keys(f.documents||{}).length;
            return(
              <div key={i} className="fu" style={{borderRadius:14,overflow:"hidden"}}>
                <div onClick={()=>setExpanded(isOpen?null:f.id)} style={{padding:"16px 20px",cursor:"pointer",display:"flex",justifyContent:"space-between",alignItems:"center",flexWrap:"wrap",gap:10}}>
                  <div style={{display:"flex",gap:12,alignItems:"center"}}>
                    <div style={{width:40,height:40,borderRadius:10,background:"linear-gradient(135deg,#ff3131,#ff914d)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:16,color:"#fff",fontWeight:900,flexShrink:0}}>
                      {(f.candidateName||"?")[0].toUpperCase()}
                    </div>
                    <div>
                      <div style={{fontSize:13,fontWeight:700,color:"var(--text)"}}>{f.candidateName||"Unknown"}</div>
                      <div style={{fontSize:11,color:"var(--muted)"}}>{f.jobTitle||"—"}{isSuperAdmin?" · "+companyName(f.companyId):""}</div>
                    </div>
                  </div>
                  <div style={{display:"flex",gap:16,alignItems:"center"}}>
                    <div style={{fontSize:11,color:"var(--muted)"}}>{docCount} document{docCount!==1?"s":""}</div>
                    <div style={{fontSize:11,color:"var(--muted)"}}>Joined {(f.joinedAt||"").slice(0,10)||"—"}</div>
                    <div style={{fontSize:14,color:"var(--muted)"}}>{isOpen?"▲":"▼"}</div>
                  </div>
                </div>
                {isOpen&&(
                  <div style={{padding:"0 20px 20px",borderTop:"1px solid var(--b1)",paddingTop:16}}>
                    <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}} className="mg1">
                      {Object.entries(f.documents||{}).map(([key,d])=>(
                        <a key={key} href={d.url} target="_blank" rel="noopener noreferrer" style={{display:"flex",alignItems:"center",gap:10,background:"var(--s2)",border:"1px solid var(--b1)",borderRadius:8,padding:"10px 12px",textDecoration:"none"}}>
                          <div style={{fontSize:16}}>📄</div>
                          <div style={{flex:1,minWidth:0}}>
                            <div style={{fontSize:12,fontWeight:600,color:"var(--text)",whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>
                              {d.label||ONBOARDING_DOCS.find(od=>od.key===key)?.label||(key==="resume"?"Resume":key)}
                            </div>
                            <div style={{fontSize:10,color:"var(--brand)"}}>View →</div>
                          </div>
                        </a>
                      ))}
                      {docCount===0&&<div style={{fontSize:12,color:"var(--muted)",gridColumn:"1/-1"}}>No documents on file.</div>}
                    </div>
                    {f.email&&<div style={{marginTop:14,fontSize:12,color:"var(--muted)"}}>✉️ {f.email}</div>}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════
   OFFER APPROVALS — Company Admin only. HR-submitted offers wait here
   before they're visible to the candidate. Approving copies the letter
   into offerLetterContent and moves the candidate into the "offer" stage;
   rejecting sends it back to HR with a reason to revise.
══════════════════════════════════════════════════════════════ */
function CompanyOfferApprovalsPage({user,company,addToast}) {
  const [apps,setApps]=useState([]);
  const [loading,setLoading]=useState(true);
  const [busyId,setBusyId]=useState(null);
  const companyId=user?.companyId||company?.id||"";

  const load=()=>{
    setLoading(true);
    getDocs(query(collection(db,"ats_applications"),where("companyId","==",companyId)))
      .then(snap=>setApps(snap.docs.map(d=>({id:d.id,...d.data()}))))
      .catch(()=>addToast("Failed to load offers","error"))
      .finally(()=>setLoading(false));
  };
  useEffect(()=>{ if(companyId) load(); },[companyId]);

  const pending=apps.filter(a=>a.offerApproval?.status==="pending");
  const recent=apps.filter(a=>a.offerApproval?.status==="approved"||a.offerApproval?.status==="rejected")
    .sort((a,b)=>new Date(b.offerApproval?.approvedAt||b.offerApproval?.rejectedAt||0)-new Date(a.offerApproval?.approvedAt||a.offerApproval?.rejectedAt||0))
    .slice(0,10);

  const approve=async(app)=>{
    setBusyId(app.id);
    try{
      const now=new Date().toISOString();
      await updateDoc(doc(db,"ats_applications",app.id),{
        offerLetterContent:app.offerApproval.letterContent,
        offerGeneratedAt:now,
        offerApproval:{...app.offerApproval,status:"approved",approvedBy:user?.id||"",approvedByName:user?.name||user?.email||"",approvedAt:now},
        stage:"offer",
        stageUpdatedAt:now,
        stageHistory:arrayUnion({stage:"offer",timestamp:now}),
      });
      fetch("https://sendcandidateemail-mtvra6khyq-uc.a.run.app",{
        method:"POST",headers:{"Content-Type":"application/json"},
        body:JSON.stringify({
          candidateEmail:app.email,candidateName:app.candidateName,
          customSubject:`Your Offer Letter is Ready — ${app.jobTitle} at ${app.companyName||company?.name}`,
          customBody:`<p>Dear ${app.candidateName},</p><p>Great news — your offer letter for <strong>${app.jobTitle}</strong> is ready to view.</p><p><a href="https://meitho.com/track/${app.id}">View and respond to your offer →</a></p><p>Best regards,<br/>${app.companyName||company?.name} HR Team</p>`,
        }),
      }).catch(()=>{});
      setApps(p=>p.map(a=>a.id===app.id?{...a,offerApproval:{...a.offerApproval,status:"approved"},stage:"offer"}:a));
      createNotification({
        companyId:app.companyId,targetRole:null,targetUserId:app.offerApproval.submittedBy,type:"offer_approved",
        title:"Offer approved",
        message:`Your offer for ${app.candidateName} — ${app.jobTitle} — was approved by ${user?.name||user?.email||"Company Admin"}.`,
        link:"ats_applications",
      });
      addToast("Offer approved — candidate has been notified.","success");
    }catch(e){addToast("Failed to approve: "+e.message,"error");}
    finally{setBusyId(null);}
  };

  const reject=async(app)=>{
    const reason=window.prompt("Reason for rejecting this offer? (shown to the HR staff who submitted it)")||"";
    setBusyId(app.id);
    try{
      const now=new Date().toISOString();
      await updateDoc(doc(db,"ats_applications",app.id),{
        offerApproval:{...app.offerApproval,status:"rejected",rejectedBy:user?.id||"",rejectedByName:user?.name||user?.email||"",rejectedAt:now,rejectReason:reason},
      });
      setApps(p=>p.map(a=>a.id===app.id?{...a,offerApproval:{...a.offerApproval,status:"rejected",rejectReason:reason}}:a));
      createNotification({
        companyId:app.companyId,targetRole:null,targetUserId:app.offerApproval.submittedBy,type:"offer_rejected",
        title:"Offer rejected",
        message:`Your offer for ${app.candidateName} — ${app.jobTitle} — was rejected${reason?": "+reason:""}. Revise and resubmit.`,
        link:"ats_applications",
      });
      addToast("Offer rejected — HR will need to revise and resubmit.","amber");
    }catch(e){addToast("Failed to reject: "+e.message,"error");}
    finally{setBusyId(null);}
  };

  if(loading)return <div style={{textAlign:"center",padding:60,color:"var(--muted)"}}>
    <div style={{fontSize:32,marginBottom:12}}>✅</div>
    <div>Loading approvals...</div>
  </div>;

  return(
    <div>
      <div style={{marginBottom:20}}>
        <h2 style={{fontFamily:FFD,fontWeight:800,fontSize:24,color:"var(--text)",marginBottom:4}}>✅ Offer Approvals</h2>
        <p style={{color:"var(--muted)",fontSize:13}}>Offers submitted by HR staff wait here before candidates can see them.</p>
      </div>

      {pending.length===0?(
        <div className="fu" style={{borderRadius:14,padding:48,textAlign:"center",color:"var(--muted)",marginBottom:24}}>
          <div style={{fontSize:32,marginBottom:10}}>📭</div>
          <div style={{fontSize:13}}>No offers waiting for approval right now.</div>
        </div>
      ):(
        <div style={{display:"flex",flexDirection:"column",gap:12,marginBottom:28}}>
          {pending.map(app=>(
            <div key={app.id} className="fu" style={{borderRadius:14,padding:20}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",flexWrap:"wrap",gap:12,marginBottom:14}}>
                <div>
                  <div style={{fontSize:14,fontWeight:700,color:"var(--text)"}}>{app.candidateName}</div>
                  <div style={{fontSize:12,color:"var(--muted)"}}>{app.jobTitle}</div>
                  <div style={{fontSize:11,color:"var(--muted)",marginTop:4}}>Submitted by {app.offerApproval.submittedByName||"HR"} · {(app.offerApproval.submittedAt||"").slice(0,16).replace("T"," ")}</div>
                </div>
                <div style={{display:"flex",gap:8}}>
                  <button onClick={()=>approve(app)} disabled={busyId===app.id} style={{background:"linear-gradient(90deg,#10b981,#0cc0df)",border:"none",borderRadius:8,color:"#fff",padding:"8px 16px",cursor:"pointer",fontSize:12,fontWeight:700,fontFamily:FF}}>{busyId===app.id?"...":"✓ Approve"}</button>
                  <button onClick={()=>reject(app)} disabled={busyId===app.id} style={{background:"rgba(239,68,68,0.1)",border:"1px solid rgba(239,68,68,0.25)",borderRadius:8,color:"#ef4444",padding:"8px 16px",cursor:"pointer",fontSize:12,fontWeight:700,fontFamily:FF}}>✕ Reject</button>
                </div>
              </div>
              <div style={{background:"var(--s2)",border:"1px solid var(--b1)",borderRadius:10,padding:14,fontSize:12,color:"var(--muted)",whiteSpace:"pre-wrap",maxHeight:200,overflow:"auto"}}>
                {app.offerApproval.letterContent}
              </div>
            </div>
          ))}
        </div>
      )}

      {recent.length>0&&(
        <div>
          <div style={{fontSize:12,fontWeight:700,color:"var(--muted)",marginBottom:10,textTransform:"uppercase"}}>Recent Decisions</div>
          <div className="fu" style={{borderRadius:14,padding:0,overflow:"hidden"}}>
            <table style={{width:"100%",borderCollapse:"collapse"}}>
              <thead><tr style={{background:"var(--s2)"}}>{["Candidate","Job","Status","By","When"].map(h=><th key={h} style={{padding:"8px 12px",textAlign:"left",fontSize:10,fontWeight:700,color:"var(--muted)",textTransform:"uppercase"}}>{h}</th>)}</tr></thead>
              <tbody>
                {recent.map((a,i)=>(
                  <tr key={i} style={{borderBottom:"1px solid var(--b1)"}}>
                    <td style={{padding:"10px 12px",fontSize:13,fontWeight:600,color:"var(--text)"}}>{a.candidateName}</td>
                    <td style={{padding:"10px 12px",fontSize:12,color:"var(--muted)"}}>{a.jobTitle}</td>
                    <td style={{padding:"10px 12px"}}><Badge color={a.offerApproval.status==="approved"?"var(--green)":"#ef4444"} style={{fontSize:9}}>{a.offerApproval.status}</Badge></td>
                    <td style={{padding:"10px 12px",fontSize:12,color:"var(--muted)"}}>{a.offerApproval.approvedByName||a.offerApproval.rejectedByName||"—"}</td>
                    <td style={{padding:"10px 12px",fontSize:11,color:"var(--muted)"}}>{(a.offerApproval.approvedAt||a.offerApproval.rejectedAt||"").slice(0,10)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}


// ============================================================
// H7: SCHEDULE INTERVIEW (inside ATSCandidateDetail)
// H8: OFFER LETTER TEMPLATE + GENERATOR  
// H9: PANEL FEEDBACK FORM (public page)
// ============================================================

// ── H9: Panel Feedback Form (Public) ──
function PanelFeedbackPage() {
  const feedbackId = window.location.pathname.split("/feedback/")[1]||"";
  const [data,setData]=useState(null);
  const [loading,setLoading]=useState(true);
  const [submitted,setSubmitted]=useState(false);
  const [form,setForm]=useState({
    technical:4,communication:4,attitude:4,overall:4,
    strengths:"",improvements:"",decision:"proceed",recommendedCTC:"",comments:""
  });
  const [saving,setSaving]=useState(false);

  useEffect(()=>{
    if(!feedbackId)return;
    getDoc(doc(db,"ats_feedback_requests",feedbackId)).then(snap=>{
      if(snap.exists())setData({id:snap.id,...snap.data()});
    }).catch(e=>console.error(e))
    .finally(()=>setLoading(false));
  },[feedbackId]);

  const submit=async()=>{
    setSaving(true);
    try{
      const feedback={
        ...form,
        panelName:data.panelName,
        panelEmail:data.panelEmail,
        panelDesignation:data.panelDesignation,
        submittedAt:new Date().toISOString(),
        feedbackRequestId:feedbackId,
      };
      // Save feedback to application
      await updateDoc(doc(db,"ats_applications",data.applicationId),{
        [`interviewFeedback.${feedbackId}`]:feedback,
      });
      // Mark request as completed
      await updateDoc(doc(db,"ats_feedback_requests",feedbackId),{
        status:"completed",submittedAt:new Date().toISOString()
      });
      createNotification({
        companyId:data.companyId,targetRole:"all",type:"feedback_submitted",
        title:"Interview feedback submitted",
        message:`${data.panelName} submitted feedback for ${data.candidateName} — ${data.roundName}`,
        link:"ats_applications",
      });
      setSubmitted(true);
    }catch(e){alert("Failed to submit: "+e.message);}
    finally{setSaving(false);}
  };

  const G="linear-gradient(135deg,#ff3131,#ff914d)";
  const s={fontFamily:"'Inter',sans-serif"};

  if(loading)return(
    <div style={{...s,minHeight:"100vh",display:"flex",alignItems:"center",justifyContent:"center",background:"#050a14",color:"#fff"}}>
      <div style={{textAlign:"center"}}><div style={{fontSize:32,marginBottom:12}}>⚡</div><div>Loading...</div></div>
    </div>
  );

  if(!data)return(
    <div style={{...s,minHeight:"100vh",display:"flex",alignItems:"center",justifyContent:"center",background:"#050a14",color:"#fff"}}>
      <div style={{textAlign:"center"}}><div style={{fontSize:48,marginBottom:16}}>🔍</div><div style={{fontSize:20,fontWeight:700}}>Invalid feedback link</div></div>
    </div>
  );

  if(submitted||data.status==="completed")return(
    <div style={{...s,minHeight:"100vh",display:"flex",alignItems:"center",justifyContent:"center",background:"#050a14",color:"#fff",padding:20}}>
      <div style={{textAlign:"center",maxWidth:480}}>
        <div style={{width:80,height:80,borderRadius:"50%",background:"rgba(16,185,129,0.1)",border:"2px solid rgba(16,185,129,0.3)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:36,margin:"0 auto 24px"}}>✅</div>
        <h1 style={{fontFamily:"'Sora',sans-serif",fontSize:28,fontWeight:900,marginBottom:12}}>Feedback Submitted!</h1>
        <p style={{color:"rgba(255,255,255,0.6)",lineHeight:1.7}}>Thank you {data.panelName}! Your feedback for <strong>{data.candidateName}</strong> has been recorded successfully.</p>
      </div>
    </div>
  );

  const Star=({field,val})=>(
    <div style={{display:"flex",gap:8,marginTop:8}}>
      {[1,2,3,4,5].map(n=>(
        <button key={n} onClick={()=>setForm(p=>({...p,[field]:n}))} style={{fontSize:24,background:"none",border:"none",cursor:"pointer",opacity:form[field]>=n?1:0.3,transition:"opacity .15s"}}>⭐</button>
      ))}
      <span style={{color:"rgba(255,255,255,0.5)",fontSize:13,marginLeft:4,alignSelf:"center"}}>{form[field]}/5</span>
    </div>
  );

  return(
    <div style={{...s,minHeight:"100vh",background:"#050a14",color:"#f0f4ff",padding:"0 0 60px"}}>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&family=Sora:wght@700;800;900&display=swap');*{box-sizing:border-box;margin:0;padding:0;}`}</style>
      <div style={{background:"linear-gradient(135deg,#ff3131,#ff914d)",padding:"24px 5%",marginBottom:32}}>
        <div style={{maxWidth:680,margin:"0 auto"}}>
          <div style={{fontSize:12,color:"rgba(255,255,255,0.7)",marginBottom:4}}>Interview Feedback · {data.companyName}</div>
          <div style={{fontFamily:"'Sora',sans-serif",fontSize:22,fontWeight:900}}>{data.roundName} — {data.candidateName}</div>
          <div style={{fontSize:13,color:"rgba(255,255,255,0.8)",marginTop:4}}>Position: {data.jobTitle} · Date: {data.interviewDate} {data.interviewTime}</div>
        </div>
      </div>

      <div style={{maxWidth:680,margin:"0 auto",padding:"0 5%"}}>
        {/* Ratings */}
        {[["technical","Technical Skills"],["communication","Communication"],["attitude","Attitude & Culture Fit"],["overall","Overall Rating"]].map(([field,label])=>(
          <div key={field} style={{background:"rgba(255,255,255,0.03)",border:"1px solid rgba(255,255,255,0.07)",borderRadius:14,padding:"18px 20px",marginBottom:12}}>
            <div style={{fontSize:14,fontWeight:600}}>{label}</div>
            <Star field={field}/>
          </div>
        ))}

        {/* Decision */}
        <div style={{background:"rgba(255,255,255,0.03)",border:"1px solid rgba(255,255,255,0.07)",borderRadius:14,padding:"18px 20px",marginBottom:12}}>
          <div style={{fontSize:14,fontWeight:600,marginBottom:12}}>Your Decision</div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:10}}>
            {[["proceed","✅ Proceed","rgba(16,185,129,0.15)","rgba(16,185,129,0.4)","#10b981"],
              ["hold","⏸ On Hold","rgba(245,158,11,0.15)","rgba(245,158,11,0.4)","#f59e0b"],
              ["reject","❌ Reject","rgba(255,49,49,0.15)","rgba(255,49,49,0.4)","#ff3131"]].map(([val,label,bg,border,color])=>(
              <button key={val} onClick={()=>setForm(p=>({...p,decision:val}))} style={{padding:"12px",borderRadius:10,border:`2px solid ${form.decision===val?border:"rgba(255,255,255,0.1)"}`,background:form.decision===val?bg:"transparent",color:form.decision===val?color:"rgba(255,255,255,0.5)",cursor:"pointer",fontSize:13,fontWeight:700,fontFamily:"inherit",transition:"all .2s"}}>
                {label}
              </button>
            ))}
          </div>
        </div>

        {/* Text fields */}
        {[["strengths","Key Strengths","What stood out positively?"],["improvements","Areas of Improvement","What could be better?"],["comments","Additional Comments","Any other observations?"]].map(([field,label,ph])=>(
          <div key={field} style={{background:"rgba(255,255,255,0.03)",border:"1px solid rgba(255,255,255,0.07)",borderRadius:14,padding:"18px 20px",marginBottom:12}}>
            <div style={{fontSize:14,fontWeight:600,marginBottom:8}}>{label}</div>
            <textarea value={form[field]} onChange={e=>setForm(p=>({...p,[field]:e.target.value}))} placeholder={ph} rows={3}
              style={{width:"100%",padding:"10px 12px",borderRadius:8,border:"1px solid rgba(255,255,255,0.1)",background:"rgba(255,255,255,0.05)",color:"#fff",fontSize:13,fontFamily:"inherit",resize:"vertical",outline:"none"}}/>
          </div>
        ))}

        {/* Recommended CTC */}
        <div style={{background:"rgba(255,255,255,0.03)",border:"1px solid rgba(255,255,255,0.07)",borderRadius:14,padding:"18px 20px",marginBottom:24}}>
          <div style={{fontSize:14,fontWeight:600,marginBottom:8}}>Recommended CTC (Optional)</div>
          <input value={form.recommendedCTC} onChange={e=>setForm(p=>({...p,recommendedCTC:e.target.value}))} placeholder="e.g. ₹8.5L - ₹10L"
            style={{width:"100%",padding:"10px 12px",borderRadius:8,border:"1px solid rgba(255,255,255,0.1)",background:"rgba(255,255,255,0.05)",color:"#fff",fontSize:13,fontFamily:"inherit",outline:"none"}}/>
        </div>

        <button onClick={submit} disabled={saving} style={{background:G,border:"none",borderRadius:12,color:"#fff",padding:"14px",cursor:"pointer",fontSize:15,fontWeight:700,fontFamily:"inherit",width:"100%",boxShadow:"0 8px 24px rgba(255,49,49,0.3)"}}>
          {saving?"Submitting...":"Submit Feedback →"}
        </button>
        <div style={{textAlign:"center",marginTop:12,fontSize:11,color:"rgba(255,255,255,0.3)"}}>Powered by Meitho Select</div>
      </div>
    </div>
  );
}

// ── H8: Offer Letter Template (Company Admin) ──
const OFFER_LETTER_DEFAULT_TEMPLATE=`Dear {{candidate_name}},

We are pleased to offer you the position of {{designation}} at {{company_name}}.

COMPENSATION:
Fixed CTC: ₹{{fixed_ctc}} per annum
Variable: ₹{{variable_ctc}} per annum  
Total CTC: ₹{{total_ctc}} per annum

JOINING DETAILS:
Date of Joining: {{joining_date}}
Work Location: {{work_location}}
Reporting To: {{reporting_manager}}
Probation Period: {{probation_period}}

This offer is subject to successful completion of background verification.

Please confirm your acceptance by {{acceptance_deadline}}.

We look forward to having you on our team!

Regards,
{{hr_name}}
HR Department
{{company_name}}`;

function ATSOfferLetterTemplate({user,company,addToast,setPage}) {
  const {dark}=useTheme();
  const companyId=company?.id||user?.companyId||"";
  const [template,setTemplate]=useState("");
  const [saving,setSaving]=useState(false);
  const [loading,setLoading]=useState(true);
  const textareaRef=React.useRef(null);

  const DEFAULT_TEMPLATE=OFFER_LETTER_DEFAULT_TEMPLATE;

  useEffect(()=>{
    if(!companyId){setTemplate(DEFAULT_TEMPLATE);setLoading(false);return;}
    getDoc(doc(db,"companies",companyId)).then(snap=>{
      const d=snap.exists()?snap.data():{};
      setTemplate(d.offerTemplate||DEFAULT_TEMPLATE);
    }).catch(()=>setTemplate(DEFAULT_TEMPLATE))
    .finally(()=>setLoading(false));
  },[companyId]);

  const save=async()=>{
    setSaving(true);
    try{
      await updateDoc(doc(db,"companies",companyId),{offerTemplate:template,offerTemplateUpdatedAt:new Date().toISOString()});
      addToast("Offer letter template saved!","success");
    }catch(e){addToast("Failed: "+e.message,"error");}
    finally{setSaving(false);}
  };

  // Inserts the placeholder at wherever the cursor actually is in the
  // textarea, instead of always appending to the very end — previously,
  // clicking a placeholder on a long template silently added it off-screen
  // below the visible area, making it look like nothing happened.
  const insertPlaceholder=(p)=>{
    const el=textareaRef.current;
    if(!el){setTemplate(t=>t+"\n"+p);return;}
    const start=el.selectionStart??template.length;
    const end=el.selectionEnd??template.length;
    const newValue=template.slice(0,start)+p+template.slice(end);
    setTemplate(newValue);
    // Restore focus + cursor right after the inserted text, and make sure
    // it's actually visible instead of relying on the user to scroll.
    requestAnimationFrame(()=>{
      el.focus();
      const cursorPos=start+p.length;
      el.selectionStart=el.selectionEnd=cursorPos;
      el.scrollTop=Math.max(0,(el.scrollHeight*(cursorPos/newValue.length))-el.clientHeight/2);
    });
  };

  if(loading)return <div style={{textAlign:"center",padding:40,color:"var(--muted)"}}>Loading...</div>;

  return(
    <div>
      <div style={{display:"flex",gap:10,alignItems:"center",marginBottom:20}}>
        <button onClick={()=>setPage("ats_jobs")} style={{background:"var(--s2)",border:"1px solid var(--b1)",borderRadius:8,color:"var(--text)",padding:"7px 14px",cursor:"pointer",fontSize:12,fontWeight:600,fontFamily:FF}}>← Back</button>
        <h2 style={{fontFamily:FFD,fontWeight:800,fontSize:22,color:"var(--text)"}}>📄 Offer Letter Template</h2>
      </div>

      <div className="fu" style={{borderRadius:14,padding:"20px 24px",marginBottom:16}}>
        <div style={{fontFamily:FFD,fontSize:13,fontWeight:700,color:"var(--text)",marginBottom:8}}>Available Placeholders</div>
        <div style={{display:"flex",gap:8,flexWrap:"wrap",marginBottom:4}}>
          {["{{candidate_name}}","{{designation}}","{{company_name}}","{{fixed_ctc}}","{{variable_ctc}}","{{total_ctc}}","{{joining_date}}","{{work_location}}","{{reporting_manager}}","{{probation_period}}","{{acceptance_deadline}}","{{hr_name}}"].map(p=>(
            <span key={p} onClick={()=>insertPlaceholder(p)} style={{background:"rgba(255,49,49,0.08)",border:"1px solid rgba(255,49,49,0.2)",borderRadius:6,padding:"3px 8px",fontSize:11,color:"#ff3131",fontFamily:"monospace",cursor:"pointer"}} title="Click to insert at cursor position">{p}</span>
          ))}
        </div>
        <div style={{fontSize:11,color:"var(--muted)"}}>Click into the template below first to place your cursor, then click a placeholder to insert it there.</div>
      </div>

      <div className="fu" style={{borderRadius:14,padding:"20px 24px",marginBottom:16}}>
        <div style={{fontFamily:FFD,fontSize:13,fontWeight:700,color:"var(--text)",marginBottom:12}}>Letter Template</div>
        <textarea ref={textareaRef} value={template} onChange={e=>setTemplate(e.target.value)} rows={25}
          style={{width:"100%",padding:"12px",borderRadius:8,border:"1px solid var(--b1)",background:"var(--s2)",color:"var(--text)",fontSize:13,fontFamily:"monospace",resize:"vertical",outline:"none",lineHeight:1.8}}/>
      </div>

      <div style={{display:"flex",gap:10}}>
        <button onClick={save} disabled={saving} style={{background:"linear-gradient(90deg,#ff3131,#ff914d)",border:"none",borderRadius:10,color:"#fff",padding:"12px 28px",cursor:"pointer",fontSize:14,fontWeight:700,fontFamily:FF}}>
          {saving?"Saving...":"💾 Save Template"}
        </button>
        <button onClick={()=>setTemplate(DEFAULT_TEMPLATE)} style={{background:"var(--s2)",border:"1px solid var(--b1)",borderRadius:10,color:"var(--text)",padding:"12px 20px",cursor:"pointer",fontSize:14,fontFamily:FF}}>
          Reset to Default
        </button>
      </div>
    </div>
  );
}

// ── H8b: Offer Letter Generator (HR uses) ──
function ATSOfferGenerator({application,company,user,addToast,onClose}) {
  const {dark}=useTheme();
  const [template,setTemplate]=useState("");
  const [form,setForm]=useState({
    designation:application?.jobTitle||"",
    fixed_ctc:"",variable_ctc:"",total_ctc:"",
    joining_date:"",work_location:company?.address||"",
    reporting_manager:"",probation_period:"6 months",
    acceptance_deadline:"",hr_name:""
  });
  const isCompanyAdmin=user?.role==="admin"||user?.companyAdmin===true;

  useEffect(()=>{
    getDoc(doc(db,"companies",company?.id||"")).then(snap=>{
      if(snap.exists())setTemplate(snap.data().offerTemplate||OFFER_LETTER_DEFAULT_TEMPLATE);
      else setTemplate(OFFER_LETTER_DEFAULT_TEMPLATE);
    }).catch(()=>setTemplate(OFFER_LETTER_DEFAULT_TEMPLATE));
  },[company?.id]);

  const [saving,setSaving]=useState(false);

  const buildLetter=()=>{
    let letter=template;
    const replacements={
      candidate_name:application?.candidateName||"",
      company_name:company?.name||"",
      ...form
    };
    Object.entries(replacements).forEach(([key,val])=>{
      letter=letter.replace(new RegExp(`\\{\\{${key}\\}\\}`,"g"),val||`[${key}]`);
    });
    return letter;
  };

  const printLetter=(letter)=>{
    const win=window.open("","_blank","width=800,height=900");
    win.document.write(`<!DOCTYPE html><html><head><title>Offer Letter — ${application?.candidateName}</title>
    <style>body{font-family:Arial,sans-serif;max-width:700px;margin:40px auto;padding:40px;color:#1e293b;line-height:1.8;}
    h1{color:#ff3131;margin-bottom:4px;}
    .logo{display:flex;align-items:center;gap:10px;margin-bottom:32px;padding-bottom:16px;border-bottom:2px solid #ff3131;}
    pre{white-space:pre-wrap;font-family:Arial,sans-serif;font-size:14px;}
    @media print{body{margin:0;}</style></head>
    <body>
    <div class="logo">
      <div style="width:40px;height:40px;background:linear-gradient(135deg,#ff3131,#ff914d);border-radius:10px;display:flex;align-items:center;justify-content:center;color:white;font-weight:900;font-size:18px;">M</div>
      <div><strong style="font-size:18px;">${company?.name||"Company"}</strong><br/><small style="color:#64748b;">Offer Letter</small></div>
    </div>
    <pre>${letter}</pre>
    <script>window.onload=()=>window.print();</script>
    </body></html>`);
    win.document.close();
  };

  // Company Admins publish immediately (no need to approve their own work).
  // Everyone else (HR staff) submits into the approval queue — the offer
  // stays invisible to the candidate until a Company Admin approves it.
  const generate=async()=>{
    const letter=buildLetter();
    setSaving(true);
    try{
      const now=new Date().toISOString();
      if(isCompanyAdmin){
        await updateDoc(doc(db,"ats_applications",application.id),{
          offerLetterContent:letter,
          offerGeneratedAt:now,
          offerApproval:{status:"approved",approvedBy:user?.id||"",approvedByName:user?.name||user?.email||"",approvedAt:now,letterContent:letter,formData:form},
          stage:"offer",
          stageUpdatedAt:now,
          stageHistory:arrayUnion({stage:"offer",timestamp:now}),
        });
        printLetter(letter);
        addToast("Offer letter generated — candidate can now view and accept it from their tracker.","success");
      }else{
        await updateDoc(doc(db,"ats_applications",application.id),{
          offerApproval:{status:"pending",letterContent:letter,formData:form,submittedBy:user?.id||"",submittedByName:user?.name||user?.email||"",submittedAt:now},
        });
        createNotification({
          companyId:application.companyId||company?.id||"",targetRole:"admin",type:"offer_pending",
          title:"Offer awaiting your approval",
          message:`${user?.name||user?.email||"HR"} submitted an offer for ${application.candidateName} — ${application.jobTitle}`,
          link:"ats_approvals",
        });
        addToast("Offer submitted for Company Admin approval — the candidate won't see it until it's approved.","success");
      }
      onClose();
    }catch(e){addToast("Failed to submit offer: "+e.message,"error");}
    finally{setSaving(false);}
  };

  const pendingApproval=application?.offerApproval?.status==="pending";
  const rejectedApproval=application?.offerApproval?.status==="rejected";

  return(
    <div>
      <div style={{fontFamily:FFD,fontSize:16,fontWeight:700,color:"var(--text)",marginBottom:4}}>Generate Offer Letter</div>
      <div style={{fontSize:12,color:"var(--muted)",marginBottom:16}}>For: {application?.candidateName} — {application?.jobTitle}</div>

      {pendingApproval&&(
        <div style={{background:"rgba(245,158,11,0.08)",border:"1px solid rgba(245,158,11,0.25)",borderRadius:10,padding:"10px 14px",marginBottom:16,fontSize:12,color:"#f59e0b"}}>
          ⏳ An offer is already awaiting Company Admin approval for this candidate. Submitting again will replace it.
        </div>
      )}
      {rejectedApproval&&(
        <div style={{background:"rgba(239,68,68,0.08)",border:"1px solid rgba(239,68,68,0.25)",borderRadius:10,padding:"10px 14px",marginBottom:16,fontSize:12,color:"#ef4444"}}>
          ✕ Your last offer was rejected{application.offerApproval.rejectReason?": "+application.offerApproval.rejectReason:""}. Revise and resubmit below.
        </div>
      )}

      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:16}} className="mg1">
        {[["Designation","designation","e.g. Senior Developer"],["Fixed CTC (LPA)","fixed_ctc","e.g. 8.5"],["Variable CTC (LPA)","variable_ctc","e.g. 1.5"],["Total CTC (LPA)","total_ctc","e.g. 10"],["Joining Date","joining_date","",{type:"date"}],["Work Location","work_location","e.g. Chennai"],["Reporting Manager","reporting_manager","Manager name"],["Probation Period","probation_period","e.g. 6 months"],["Acceptance Deadline","acceptance_deadline","",{type:"date"}],["HR Name","hr_name","Your name"]].map(([label,key,ph,opts])=>(
          <div key={key}>
            <label style={{fontSize:11,fontWeight:600,color:"var(--muted)",display:"block",marginBottom:4,textTransform:"uppercase"}}>{label}</label>
            <input type={opts?.type||"text"} value={form[key]} onChange={e=>setForm(p=>({...p,[key]:e.target.value}))} placeholder={ph}
              style={{width:"100%",padding:"8px 12px",borderRadius:8,border:"1px solid var(--b1)",background:"var(--s2)",color:"var(--text)",fontSize:13,fontFamily:FF,outline:"none",colorScheme:opts?.type==="date"?(dark?"dark":"light"):undefined}}/>
          </div>
        ))}
      </div>

      <div style={{display:"flex",gap:10}}>
        <button onClick={generate} disabled={saving} style={{background:"linear-gradient(90deg,#ff3131,#ff914d)",border:"none",borderRadius:10,color:"#fff",padding:"11px 24px",cursor:"pointer",fontSize:13,fontWeight:700,fontFamily:FF}}>
          {saving?"Saving...":isCompanyAdmin?"📄 Generate & Print":"📤 Submit for Approval"}
        </button>
        <button onClick={onClose} style={{background:"var(--s2)",border:"1px solid var(--b1)",borderRadius:10,color:"var(--text)",padding:"11px 20px",cursor:"pointer",fontSize:13,fontFamily:FF}}>Cancel</button>
      </div>
    </div>
  );
}

// ── Candidate Application Tracker (public, meitho.com/track/{applicationId}) ──
// ── Onboarding document checklist ──
// Shared list of required documents for the offer/onboarding flow. Keys are
// stored on ats_applications.documents[key] = {url,name,uploadedAt,status,reviewedBy,reviewedAt,rejectReason}
const ONBOARDING_DOCS=[
  {key:"salary_slips",label:"Last 3 Months Salary Slips"},
  {key:"relieving_letter",label:"Relieving Letter (Previous Employer)"},
  {key:"experience_letter",label:"Experience Letter(s)"},
  {key:"education_certificate",label:"Educational Certificates"},
  {key:"pan_card",label:"PAN Card Copy"},
  {key:"aadhaar_card",label:"Aadhaar Card Copy"},
  {key:"bank_details",label:"Bank Account Details"},
  {key:"photo",label:"Passport Size Photograph"},
];

const TRACK_STAGES=[
  {id:"applied",label:"Applied",color:"#64748b",icon:"📝"},
  {id:"shortlisted",label:"Shortlisted",color:"#0cc0df",icon:"✅"},
  {id:"aptitude",label:"Aptitude Test",color:"#8b5cf6",icon:"🧠"},
  {id:"interview",label:"Interview",color:"#f59e0b",icon:"🗣️"},
  {id:"selected",label:"Selected",color:"#10b981",icon:"🎯"},
  {id:"document",label:"Documents",color:"#ff914d",icon:"📄"},
  {id:"bgv",label:"Background Verification",color:"#ff3131",icon:"🛡️"},
  {id:"offer",label:"Offer",color:"#06b6d4",icon:"🎉"},
  {id:"joined",label:"Joined",color:"#10b981",icon:"🏁"},
];

function CandidateTrackPage(){
  const appId=window.location.pathname.split("/track/")[1]?.split("/")[0]||"";
  const [app,setApp]=useState(null);
  const [job,setJob]=useState(null);
  const [loading,setLoading]=useState(true);
  const [error,setError]=useState("");
  const [responding,setResponding]=useState(false);
  const [processFeedback,setProcessFeedback]=useState({rating:0,comments:""});
  const [feedbackSaving,setFeedbackSaving]=useState(false);
  const [screeningDraft,setScreeningDraft]=useState({});
  const [screeningSaving,setScreeningSaving]=useState(false);

  const submitScreeningAnswers=async()=>{
    setScreeningSaving(true);
    try{
      const payload={answers:screeningDraft,submittedAt:new Date().toISOString()};
      await updateDoc(doc(db,"ats_applications",appId),{screeningAnswers:payload});
      setApp(p=>({...p,screeningAnswers:payload}));
    }catch(e){alert("Failed to submit — please try again.");}
    finally{setScreeningSaving(false);}
  };

  const submitProcessFeedback=async()=>{
    if(!processFeedback.rating){alert("Please select a rating first.");return;}
    setFeedbackSaving(true);
    try{
      const payload={...processFeedback,submittedAt:new Date().toISOString()};
      await updateDoc(doc(db,"ats_applications",appId),{candidateProcessFeedback:payload});
      setApp(p=>({...p,candidateProcessFeedback:payload}));
    }catch(e){alert("Failed to submit feedback — please try again.");}
    finally{setFeedbackSaving(false);}
  };

  const load=async()=>{
    try{
      const snap=await getDoc(doc(db,"ats_applications",appId));
      if(!snap.exists()){setError("Application not found. Please check your link.");setLoading(false);return;}
      const data={id:snap.id,...snap.data()};
      setApp(data);
      if(data.jobId){
        const jobSnap=await getDoc(doc(db,"ats_jobs",data.jobId));
        if(jobSnap.exists())setJob({id:jobSnap.id,...jobSnap.data()});
      }
    }catch(e){setError("Something went wrong loading your application.");}
    finally{setLoading(false);}
  };

  useEffect(()=>{if(appId)load();},[appId]);

  const respondToOffer=async(decision)=>{
    setResponding(true);
    try{
      const now=new Date().toISOString();
      await updateDoc(doc(db,"ats_applications",appId),{
        offerResponse:decision,
        offerRespondedAt:now,
        stage:decision==="accepted"?"joined":"rejected",
        stageUpdatedAt:now,
        stageHistory:arrayUnion({stage:decision==="accepted"?"joined":"rejected",timestamp:now,updatedBy:"candidate"}),
      });
      await fetch("https://sendhrnotification-mtvra6khyq-uc.a.run.app",{
        method:"POST",headers:{"Content-Type":"application/json"},
        body:JSON.stringify({
          companyEmail:app.companyEmail||app.companyHREmail||"",
          subject:`Offer ${decision==="accepted"?"Accepted 🎉":"Declined"} — ${app.candidateName}`,
          html:`<p>${app.candidateName} has <strong>${decision}</strong> the offer for <strong>${app.jobTitle}</strong>.</p>`,
        }),
      }).catch(()=>{});
      // Only now — fully joined, offer accepted, and (per the gate on offer
      // release) every checklist document already HR-approved — create the
      // Employee File with every approved document, not just the resume.
      if(decision==="accepted"){
        try{
          const approvedDocs={};
          Object.entries(app.documents||{}).forEach(([k,v])=>{ if(v?.status==="approved") approvedDocs[k]=v; });
          if(app.resumeUrl) approvedDocs.resume={url:app.resumeUrl,name:app.resumeName||"resume",status:"approved"};
          await saveEmployeeFileDocuments(app.companyId,appId,approvedDocs,{
            candidateName:app.candidateName||"",
            email:app.email||"",
            jobTitle:app.jobTitle||"",
            joinedAt:new Date().toISOString(),
          });
        }catch(e){console.warn("Could not create employee file:",e);}
      }
      setApp(p=>({...p,offerResponse:decision,stage:decision==="accepted"?"joined":"rejected"}));
    }catch(e){alert("Failed to submit your response. Please try again or contact HR directly.");}
    finally{setResponding(false);}
  };

  const uploadDoc=async(docKey,docMeta)=>{
    try{
      await updateDoc(doc(db,"ats_applications",appId),{[`documents.${docKey}`]:docMeta});
      setApp(p=>({...p,documents:{...(p.documents||{}),[docKey]:docMeta}}));
      const docLabel=ONBOARDING_DOCS.find(d=>d.key===docKey)?.label||docKey;
      createNotification({
        companyId:app?.companyId||"",targetRole:"all",type:"document_uploaded",
        title:"Document uploaded — needs review",
        message:`${app?.candidateName||"Candidate"} uploaded ${docLabel}`,
        link:"ats_applications",
      });
    }catch(e){alert("Failed to save document — please try again.");}
  };

  const bg="#050a14",card="rgba(255,255,255,0.03)",border="rgba(255,255,255,0.08)",text="#f0f4ff",sub="rgba(255,255,255,0.5)";

  if(loading)return(
    <div style={{minHeight:"100vh",background:bg,display:"flex",alignItems:"center",justifyContent:"center",fontFamily:"'Inter',sans-serif"}}>
      <div style={{textAlign:"center",color:sub}}><div style={{fontSize:32,marginBottom:12}}>⚡</div><div style={{fontSize:14}}>Loading your application...</div></div>
    </div>
  );

  if(error||!app)return(
    <div style={{minHeight:"100vh",background:bg,display:"flex",alignItems:"center",justifyContent:"center",fontFamily:"'Inter',sans-serif",padding:20}}>
      <div style={{textAlign:"center",maxWidth:400}}>
        <div style={{fontSize:48,marginBottom:16}}>🔍</div>
        <div style={{fontFamily:"'Sora',sans-serif",fontSize:20,fontWeight:800,color:text,marginBottom:8}}>Application Not Found</div>
        <div style={{fontSize:13,color:sub}}>{error||"Please check the link you were sent, or contact HR."}</div>
      </div>
    </div>
  );

  const currentIdx=TRACK_STAGES.findIndex(s=>s.id===app.stage);
  const isRejected=app.stage==="rejected";
  const upcomingInterview=(app.interviews||[]).slice(-1)[0];
  const G="linear-gradient(135deg,#ff3131,#ff914d)";

  return(
    <div style={{minHeight:"100vh",background:bg,color:text,fontFamily:"'Inter',sans-serif"}}>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&family=Sora:wght@700;800;900&display=swap');*{box-sizing:border-box;margin:0;padding:0;}`}</style>

      <nav style={{padding:"16px 5%",borderBottom:`1px solid ${border}`,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
        <div style={{fontFamily:"'Sora',sans-serif",fontWeight:900,fontSize:16,color:"#ff3131"}}>Meitho</div>
        <div style={{fontSize:11,color:sub}}>Application Tracker</div>
      </nav>

      <div style={{maxWidth:640,margin:"0 auto",padding:"40px 5%"}}>
        {/* Header */}
        <div style={{marginBottom:32}}>
          <div style={{fontSize:12,color:sub,marginBottom:6}}>Hi {app.candidateName},</div>
          <h1 style={{fontFamily:"'Sora',sans-serif",fontSize:26,fontWeight:900,marginBottom:8}}>{app.jobTitle}</h1>
          <div style={{fontSize:13,color:sub}}>{app.companyName}</div>
        </div>

        {isRejected?(
          <div style={{background:"rgba(239,68,68,0.08)",border:"1px solid rgba(239,68,68,0.2)",borderRadius:16,padding:24,marginBottom:24,textAlign:"center"}}>
            <div style={{fontSize:32,marginBottom:8}}>—</div>
            <div style={{fontSize:14,fontWeight:700,color:"#ef4444",marginBottom:4}}>Application Not Progressing</div>
            <div style={{fontSize:12,color:sub}}>Thank you for your interest. We encourage you to apply for future openings.</div>
          </div>
        ):(
          <>
            {/* Stage Timeline */}
            <div style={{background:card,border:`1px solid ${border}`,borderRadius:16,padding:24,marginBottom:20}}>
              <div style={{fontSize:11,fontWeight:700,color:sub,textTransform:"uppercase",letterSpacing:1,marginBottom:20}}>Application Status</div>
              {TRACK_STAGES.map((s,i)=>{
                const done=i<currentIdx,active=i===currentIdx;
                return(
                  <div key={s.id} style={{display:"flex",gap:14,marginBottom:i<TRACK_STAGES.length-1?4:0}}>
                    <div style={{display:"flex",flexDirection:"column",alignItems:"center"}}>
                      <div style={{width:32,height:32,borderRadius:"50%",background:done||active?s.color:"rgba(255,255,255,0.06)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:14,flexShrink:0,boxShadow:active?`0 0 0 4px ${s.color}25`:"none"}}>
                        {done?"✓":s.icon}
                      </div>
                      {i<TRACK_STAGES.length-1&&<div style={{width:2,flex:1,minHeight:24,background:done?s.color:"rgba(255,255,255,0.08)"}}/>}
                    </div>
                    <div style={{paddingBottom:24}}>
                      <div style={{fontSize:13,fontWeight:active?700:600,color:done||active?text:sub}}>{s.label}</div>
                      {active&&<div style={{fontSize:11,color:s.color,marginTop:2}}>Current stage</div>}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Screening questions — shown once shortlisted, skippable not blocking */}
            {job?.screeningQuestions?.length>0&&currentIdx>=TRACK_STAGES.findIndex(s=>s.id==="shortlisted")&&(
              app.screeningAnswers?(
                <div style={{background:"rgba(16,185,129,0.06)",border:"1px solid rgba(16,185,129,0.2)",borderRadius:16,padding:20,marginBottom:20}}>
                  <div style={{fontSize:11,fontWeight:700,color:"#10b981",textTransform:"uppercase",letterSpacing:1,marginBottom:6}}>✅ Screening Questions</div>
                  <div style={{fontSize:12,color:sub}}>Thanks — your answers have been shared with the hiring team.</div>
                </div>
              ):(
                <div style={{background:card,border:`1px solid ${border}`,borderRadius:16,padding:24,marginBottom:20}}>
                  <div style={{fontSize:11,fontWeight:700,color:sub,textTransform:"uppercase",letterSpacing:1,marginBottom:6}}>📋 A Few Quick Questions</div>
                  <div style={{fontSize:12,color:sub,marginBottom:18}}>Optional, but it helps the hiring team move faster on your application.</div>
                  {job.screeningQuestions.map((q,qi)=>(
                    <div key={q.id} style={{marginBottom:16}}>
                      <div style={{fontSize:13,color:text,marginBottom:8,fontWeight:600}}>{qi+1}. {q.text}</div>
                      {q.type==="text"&&(
                        <textarea value={screeningDraft[q.id]||""} onChange={e=>setScreeningDraft(p=>({...p,[q.id]:e.target.value}))} rows={2}
                          style={{width:"100%",padding:"10px 12px",borderRadius:8,border:`1px solid ${border}`,background:"rgba(255,255,255,0.04)",color:text,fontSize:13,fontFamily:"inherit",resize:"vertical",outline:"none"}}/>
                      )}
                      {(q.type==="yesno"||q.type==="mcq")&&(
                        <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
                          {(q.options||[]).map((opt,oi)=>(
                            <button key={oi} onClick={()=>setScreeningDraft(p=>({...p,[q.id]:opt}))}
                              style={{background:screeningDraft[q.id]===opt?G:"rgba(255,255,255,0.04)",border:`1px solid ${screeningDraft[q.id]===opt?"transparent":border}`,borderRadius:8,color:screeningDraft[q.id]===opt?"#fff":sub,padding:"8px 16px",cursor:"pointer",fontSize:12,fontWeight:600,fontFamily:"inherit"}}>{opt}</button>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                  <button onClick={submitScreeningAnswers} disabled={screeningSaving} style={{background:G,border:"none",borderRadius:10,color:"#fff",padding:"10px 24px",cursor:"pointer",fontSize:13,fontWeight:700,fontFamily:"inherit",width:"100%",marginTop:6}}>
                    {screeningSaving?"Submitting...":"Submit Answers"}
                  </button>
                </div>
              )
            )}

            {/* Interview details */}
            {upcomingInterview&&app.stage==="interview"&&(
              <div style={{background:"rgba(245,158,11,0.08)",border:"1px solid rgba(245,158,11,0.2)",borderRadius:16,padding:20,marginBottom:20}}>
                <div style={{fontSize:11,fontWeight:700,color:"#f59e0b",textTransform:"uppercase",letterSpacing:1,marginBottom:10}}>🗣️ Interview Scheduled</div>
                <div style={{fontSize:14,fontWeight:700,marginBottom:4}}>{upcomingInterview.roundName||"Interview Round"}</div>
                <div style={{fontSize:13,color:sub,marginBottom:2}}>{upcomingInterview.date} at {upcomingInterview.time}</div>
                {upcomingInterview.meetLink?
                  <a href={upcomingInterview.meetLink} target="_blank" rel="noopener noreferrer" style={{display:"inline-block",marginTop:10,background:G,color:"#fff",padding:"8px 18px",borderRadius:8,fontSize:12,fontWeight:700,textDecoration:"none"}}>Join Google Meet →</a>
                  :upcomingInterview.location&&<div style={{fontSize:12,color:sub,marginTop:6}}>📍 {upcomingInterview.location}</div>}
              </div>
            )}

            {/* Document checklist */}
            {app.stage==="document"&&(
              <div style={{background:card,border:`1px solid ${border}`,borderRadius:16,padding:24,marginBottom:20}}>
                <div style={{fontSize:11,fontWeight:700,color:sub,textTransform:"uppercase",letterSpacing:1,marginBottom:6}}>📄 Documents Required</div>
                <div style={{fontSize:12,color:sub,marginBottom:18}}>Please upload each document below. HR will review and approve them before your offer letter is released.</div>
                <div style={{display:"flex",flexDirection:"column",gap:16}}>
                  {ONBOARDING_DOCS.map(d=>(
                    <DocumentUploadField key={d.key} companyId={app.companyId} candidateId={appId} docKey={d.key} label={d.label}
                      docData={app.documents?.[d.key]} onUploaded={uploadDoc} dark={true}/>
                  ))}
                </div>
              </div>
            )}

            {/* Offer response */}
            {app.stage==="offer"&&!app.offerResponse&&(
              <div style={{background:"rgba(6,182,212,0.08)",border:"1px solid rgba(6,182,212,0.25)",borderRadius:16,padding:24,marginBottom:20,textAlign:"center"}}>
                <div style={{fontSize:32,marginBottom:8}}>🎉</div>
                <div style={{fontSize:16,fontWeight:800,marginBottom:6}}>Congratulations! You have an offer.</div>
                <div style={{fontSize:12,color:sub,marginBottom:20}}>Review the offer below, then respond.</div>
                {app.offerLetterContent&&(
                  <div style={{background:"rgba(255,255,255,0.04)",border:`1px solid ${border}`,borderRadius:10,padding:16,marginBottom:16,textAlign:"left",fontSize:12,color:sub,whiteSpace:"pre-wrap",maxHeight:220,overflow:"auto"}}>{app.offerLetterContent}</div>
                )}
                <div style={{display:"flex",gap:10,justifyContent:"center"}}>
                  <button disabled={responding} onClick={()=>respondToOffer("accepted")} style={{background:"linear-gradient(90deg,#10b981,#0cc0df)",border:"none",borderRadius:10,color:"#fff",padding:"11px 24px",cursor:"pointer",fontSize:13,fontWeight:700,fontFamily:"inherit"}}>{responding?"...":"✅ Accept Offer"}</button>
                  <button disabled={responding} onClick={()=>{if(window.confirm("Are you sure you want to decline this offer?"))respondToOffer("declined");}} style={{background:"transparent",border:`1px solid ${border}`,borderRadius:10,color:sub,padding:"11px 24px",cursor:"pointer",fontSize:13,fontWeight:600,fontFamily:"inherit"}}>Decline</button>
                </div>
              </div>
            )}
            {app.offerResponse&&(
              <div style={{background:app.offerResponse==="accepted"?"rgba(16,185,129,0.08)":"rgba(239,68,68,0.08)",border:`1px solid ${app.offerResponse==="accepted"?"rgba(16,185,129,0.25)":"rgba(239,68,68,0.25)"}`,borderRadius:16,padding:20,marginBottom:20,textAlign:"center"}}>
                <div style={{fontSize:13,fontWeight:700,color:app.offerResponse==="accepted"?"#10b981":"#ef4444",marginBottom:app.offerResponse==="accepted"&&app.offerLetterContent?12:0}}>
                  {app.offerResponse==="accepted"?"✅ You accepted this offer":"You declined this offer"}
                </div>
                {app.offerResponse==="accepted"&&app.offerLetterContent&&(
                  <button onClick={()=>{
                    const w=window.open("","_blank");
                    w.document.write(`<pre style="font-family:Arial,sans-serif;white-space:pre-wrap;padding:40px;max-width:700px;margin:0 auto;line-height:1.7;">${app.offerLetterContent.replace(/</g,"&lt;")}</pre><script>window.onload=()=>window.print();</script>`);
                    w.document.close();
                  }} style={{background:G,border:"none",borderRadius:10,color:"#fff",padding:"9px 20px",cursor:"pointer",fontSize:12,fontWeight:700,fontFamily:"inherit"}}>📥 Download Offer Letter</button>
                )}
              </div>
            )}

            {/* Post-acceptance interview process feedback */}
            {app.offerResponse==="accepted"&&(
              <div style={{background:card,border:`1px solid ${border}`,borderRadius:16,padding:24,marginBottom:20}}>
                {app.candidateProcessFeedback?(
                  <div style={{textAlign:"center"}}>
                    <div style={{fontSize:28,marginBottom:8}}>🙏</div>
                    <div style={{fontSize:13,fontWeight:700,color:text,marginBottom:4}}>Thanks for your feedback!</div>
                    <div style={{fontSize:12,color:sub}}>You rated your interview experience {app.candidateProcessFeedback.rating}/5.</div>
                  </div>
                ):(
                  <>
                    <div style={{fontSize:11,fontWeight:700,color:sub,textTransform:"uppercase",letterSpacing:1,marginBottom:6}}>💬 How was your interview experience?</div>
                    <div style={{fontSize:12,color:sub,marginBottom:16}}>Your feedback helps {app.companyName} improve their hiring process.</div>
                    <div style={{display:"flex",gap:8,marginBottom:16,justifyContent:"center"}}>
                      {[1,2,3,4,5].map(n=>(
                        <button key={n} onClick={()=>setProcessFeedback(p=>({...p,rating:n}))} style={{fontSize:28,background:"none",border:"none",cursor:"pointer",opacity:processFeedback.rating>=n?1:0.25,transition:"opacity .15s"}}>⭐</button>
                      ))}
                    </div>
                    <textarea value={processFeedback.comments} onChange={e=>setProcessFeedback(p=>({...p,comments:e.target.value}))} placeholder="Anything you'd like to share about the interview process? (optional)" rows={3}
                      style={{width:"100%",padding:"10px 12px",borderRadius:8,border:`1px solid ${border}`,background:"rgba(255,255,255,0.04)",color:text,fontSize:13,fontFamily:"inherit",resize:"vertical",outline:"none",marginBottom:14}}/>
                    <button onClick={submitProcessFeedback} disabled={feedbackSaving} style={{background:G,border:"none",borderRadius:10,color:"#fff",padding:"10px 24px",cursor:"pointer",fontSize:13,fontWeight:700,fontFamily:"inherit",width:"100%"}}>
                      {feedbackSaving?"Submitting...":"Submit Feedback"}
                    </button>
                  </>
                )}
              </div>
            )}
          </>
        )}

        {/* Details */}
        <div style={{background:card,border:`1px solid ${border}`,borderRadius:16,padding:20,marginBottom:20}}>
          <div style={{fontSize:11,fontWeight:700,color:sub,textTransform:"uppercase",letterSpacing:1,marginBottom:14}}>Your Application</div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,fontSize:12}}>
            <div><div style={{color:sub,marginBottom:2}}>Applied On</div><div style={{fontWeight:600}}>{(app.appliedAt||"").slice(0,10)}</div></div>
            <div><div style={{color:sub,marginBottom:2}}>Experience</div><div style={{fontWeight:600}}>{app.experience||"—"}</div></div>
          </div>
          {app.resumeUrl&&<a href={app.resumeUrl} target="_blank" rel="noopener noreferrer" style={{display:"inline-block",marginTop:14,fontSize:12,color:"#0cc0df",textDecoration:"none"}}>📄 View your resume →</a>}
        </div>

        <div style={{textAlign:"center",fontSize:11,color:"rgba(255,255,255,0.3)",padding:"20px 0"}}>
          Powered by <strong style={{color:"#ff3131"}}>Meitho</strong> · meitho.com
        </div>
      </div>
    </div>
  );
}

function ATSCareerPage() {
  const [company,setCompany]=useState(null);
  const [jobs,setJobs]=useState([]);
  const [loading,setLoading]=useState(true);
  const slug=window.location.pathname.split("/ats/")[1]?.split("/")[0]||"";

  useEffect(()=>{loadData();},[slug]);

  const loadData=async()=>{
    try{
      
      // Find company by slug
      const compSnap=await getDocs(query(collection(db,"companies"),where("slug","==",slug)));
      if(!compSnap.empty){
        const comp={id:compSnap.docs[0].id,...compSnap.docs[0].data()};
        setCompany(comp);
        // Load active jobs
        const jobsSnap=await getDocs(query(collection(db,"ats_jobs"),where("companyId","==",comp.id),where("status","==","active")));
        setJobs(jobsSnap.docs.map(d=>({id:d.id,...d.data()})));
      }
    }catch(e){console.error(e);}
    finally{setLoading(false);}
  };

  if(loading)return(
    <div style={{minHeight:"100vh",display:"flex",alignItems:"center",justifyContent:"center",background:"#050a14",color:"#fff",fontFamily:"'Inter',sans-serif"}}>
      <div style={{textAlign:"center"}}>
        <div style={{fontSize:32,marginBottom:12}}>⚡</div>
        <div style={{fontSize:14,opacity:.6}}>Loading...</div>
      </div>
    </div>
  );

  if(!company)return(
    <div style={{minHeight:"100vh",display:"flex",alignItems:"center",justifyContent:"center",background:"#050a14",color:"#fff",fontFamily:"'Inter',sans-serif"}}>
      <div style={{textAlign:"center"}}>
        <div style={{fontSize:48,marginBottom:16}}>🔍</div>
        <div style={{fontFamily:"'Sora',sans-serif",fontSize:24,fontWeight:800,marginBottom:8}}>Company not found</div>
        <div style={{fontSize:14,opacity:.6}}>This career page doesn't exist</div>
      </div>
    </div>
  );

  return(
    <div style={{minHeight:"100vh",background:"#050a14",color:"#f0f4ff",fontFamily:"'Inter',sans-serif"}}>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&family=Sora:wght@700;800;900&display=swap');*{box-sizing:border-box;margin:0;padding:0;}`}</style>

      {/* Nav */}
      <nav style={{padding:"16px 5%",display:"flex",justifyContent:"space-between",alignItems:"center",borderBottom:"1px solid rgba(255,255,255,0.06)"}}>
        <div style={{fontFamily:"'Sora',sans-serif",fontWeight:800,fontSize:18}}>{company.name}</div>
        <div style={{fontSize:12,color:"rgba(255,255,255,0.4)"}}>Powered by <strong style={{color:"#ff3131"}}>Meitho</strong></div>
      </nav>

      {/* Hero */}
      <div style={{padding:"60px 5% 40px",textAlign:"center",borderBottom:"1px solid rgba(255,255,255,0.06)"}}>
        <div style={{width:80,height:80,borderRadius:20,background:"linear-gradient(135deg,#ff3131,#ff914d)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:36,margin:"0 auto 20px",fontWeight:900,color:"#fff"}}>
          {(company.name||"?")[0].toUpperCase()}
        </div>
        <h1 style={{fontFamily:"'Sora',sans-serif",fontSize:40,fontWeight:900,marginBottom:12,letterSpacing:"-1px"}}>Join {company.name}</h1>
        <p style={{fontSize:16,color:"rgba(255,255,255,0.6)",maxWidth:500,margin:"0 auto",lineHeight:1.7}}>{company.about||`Explore open positions at ${company.name} and take the next step in your career.`}</p>
        {company.industry&&<div style={{marginTop:12,display:"inline-flex",gap:8,alignItems:"center",background:"rgba(255,255,255,0.04)",border:"1px solid rgba(255,255,255,0.08)",borderRadius:99,padding:"6px 16px",fontSize:12,color:"rgba(255,255,255,0.5)"}}>{company.industry}</div>}
      </div>

      {/* Jobs */}
      <div style={{padding:"40px 5%",maxWidth:900,margin:"0 auto"}}>
        <div style={{fontFamily:"'Sora',sans-serif",fontSize:22,fontWeight:800,marginBottom:24}}>{jobs.length} Open Position{jobs.length!==1?"s":""}</div>
        {jobs.length>0?jobs.map((job,i)=>(
          <div key={i} onClick={()=>window.location.href=`/ats/${slug}/${job.id}`} style={{background:"rgba(255,255,255,0.03)",border:"1px solid rgba(255,255,255,0.08)",borderRadius:16,padding:"22px 24px",marginBottom:12,cursor:"pointer",transition:"all .2s"}}
            onMouseEnter={e=>{e.currentTarget.style.borderColor="rgba(255,49,49,0.3)";e.currentTarget.style.background="rgba(255,49,49,0.04)";}}
            onMouseLeave={e=>{e.currentTarget.style.borderColor="rgba(255,255,255,0.08)";e.currentTarget.style.background="rgba(255,255,255,0.03)";}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",flexWrap:"wrap",gap:12}}>
              <div>
                <div style={{fontFamily:"'Sora',sans-serif",fontSize:18,fontWeight:800,marginBottom:8}}>{job.title}</div>
                <div style={{display:"flex",gap:16,flexWrap:"wrap"}}>
                  {[["📍",job.location],["💼",job.department],["💰",job.salaryRange],["🏢",job.locationType],["👥",job.openings+" openings"]].filter(([,v])=>v).map(([icon,val],j)=>(
                    <div key={j} style={{fontSize:13,color:"rgba(255,255,255,0.5)",display:"flex",gap:4,alignItems:"center"}}><span>{icon}</span><span>{val}</span></div>
                  ))}
                </div>
              </div>
              <div style={{background:"linear-gradient(90deg,#ff3131,#ff914d)",borderRadius:8,color:"#fff",padding:"9px 20px",fontSize:13,fontWeight:700,whiteSpace:"nowrap"}}>Apply Now →</div>
            </div>
          </div>
        )):(
          <div style={{textAlign:"center",padding:"60px 20px",color:"rgba(255,255,255,0.4)"}}>
            <div style={{fontSize:48,marginBottom:16}}>🎯</div>
            <div style={{fontFamily:"'Sora',sans-serif",fontSize:20,fontWeight:700,marginBottom:8}}>No open positions</div>
            <div style={{fontSize:14}}>Check back later for new opportunities</div>
          </div>
        )}
      </div>

      {/* Footer */}
      <div style={{padding:"24px 5%",borderTop:"1px solid rgba(255,255,255,0.06)",textAlign:"center",fontSize:12,color:"rgba(255,255,255,0.3)"}}>
        Powered by <a href="https://meitho.com" style={{color:"#ff3131",textDecoration:"none",fontWeight:700}}>Meitho BGV</a> · Verified hiring platform
      </div>
    </div>
  );
}

// ── Public Job Detail + Application ──
function ATSJobDetail() {
  const path=window.location.pathname;
  const parts=path.split("/ats/")[1]?.split("/")||[];
  const slug=parts[0]||"";
  const jobId=parts[1]||"";

  const [job,setJob]=useState(null);
  const [company,setCompany]=useState(null);
  const [step,setStep]=useState("detail"); // detail | apply | success
  const [loading,setLoading]=useState(true);
  const [submitting,setSubmitting]=useState(false);
  const [form,setForm]=useState({
    candidateName:"",email:"",phone:"",
    currentCTC:"",expectedCTC:"",noticePeriod:"",
    experience:"",resumeUrl:"",resumeName:"",coverLetter:"",
  });
  const [appId]=useState(()=>doc(collection(db,"ats_applications")).id);

  useEffect(()=>{loadJob();},[jobId]);

  const loadJob=async()=>{
    try{
      
      const jobSnap=await getDoc(doc(db,"ats_jobs",jobId));
      if(jobSnap.exists()){
        const jobData={id:jobSnap.id,...jobSnap.data()};
        setJob(jobData);
        // Load company
        const compSnap=await getDocs(query(collection(db,"companies"),where("slug","==",slug)));
        if(!compSnap.empty)setCompany({id:compSnap.docs[0].id,...compSnap.docs[0].data()});
      }
    }catch(e){}
    finally{setLoading(false);}
  };

  const submitApplication=async()=>{
    if(!form.candidateName.trim()){alert("Enter your name");return;}
    if(!form.email.trim()||!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)){alert("Enter valid email");return;}
    if(!form.phone.trim()){alert("Enter phone number");return;}
    setSubmitting(true);
    try{
      
      const urlParams=new URLSearchParams(window.location.search);
      const source=urlParams.get("src")||urlParams.get("ref")||urlParams.get("utm_source")||"Direct";
      await setDoc(doc(db,"ats_applications",appId),{
        ...form,
        jobId,jobTitle:job.title,
        companyId:job.companyId,
        companyName:job.companyName||company?.name||"",
        companyEmail:company?.email||"",
        companyHREmail:company?.email||"",
        source,
        stage:"applied",
        appliedAt:new Date().toISOString(),
        stageUpdatedAt:new Date().toISOString(),
        stageHistory:[{stage:"applied",timestamp:new Date().toISOString()}],
        bgvInitiated:false,
        notes:[],interviews:[],
      });
      // Increment applications count
      await updateDoc(doc(db,"ats_jobs",jobId),{applicationsCount:increment(1)});
      createNotification({
        companyId:job.companyId,targetRole:"all",type:"new_application",
        title:"New application received",
        message:`${form.candidateName} applied for ${job.title}`,
        link:"ats_applications",
      });
      // Move to success and show the tracking link directly on this page.
      // We intentionally do NOT email the candidate here — email delivery isn't
      // reliable enough to depend on, so the copyable link on the success screen
      // is the one guaranteed way candidates get their tracking link.
      setStep("success");
    }catch(e){alert("Failed to submit: "+e.message);}
    finally{setSubmitting(false);}
  };

  if(loading)return(
    <div style={{minHeight:"100vh",display:"flex",alignItems:"center",justifyContent:"center",background:"#050a14",color:"#fff",fontFamily:"'Inter',sans-serif"}}>
      <div style={{textAlign:"center"}}><div style={{fontSize:32,marginBottom:12}}>⚡</div><div style={{fontSize:14,opacity:.6}}>Loading...</div></div>
    </div>
  );

  if(!job)return(
    <div style={{minHeight:"100vh",display:"flex",alignItems:"center",justifyContent:"center",background:"#050a14",color:"#fff",fontFamily:"'Inter',sans-serif"}}>
      <div style={{textAlign:"center"}}><div style={{fontSize:48,marginBottom:16}}>🔍</div><div style={{fontFamily:"'Sora',sans-serif",fontSize:24,fontWeight:800}}>Job not found</div></div>
    </div>
  );

  const G="linear-gradient(135deg,#ff3131,#ff914d)";

  if(step==="success")return(
    <div style={{minHeight:"100vh",display:"flex",alignItems:"center",justifyContent:"center",background:"#050a14",color:"#fff",fontFamily:"'Inter',sans-serif",padding:20}}>
      <div style={{textAlign:"center",maxWidth:480,width:"100%"}}>
        <div style={{width:80,height:80,borderRadius:"50%",background:"rgba(16,185,129,0.1)",border:"2px solid rgba(16,185,129,0.3)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:36,margin:"0 auto 24px"}}>✅</div>
        <h1 style={{fontFamily:"'Sora',sans-serif",fontSize:32,fontWeight:900,marginBottom:12}}>Application Submitted!</h1>
        <p style={{fontSize:15,color:"rgba(255,255,255,0.6)",lineHeight:1.7,marginBottom:20}}>Thank you {form.candidateName}! We've received your application for <strong>{job.title}</strong>.</p>

        {/* Tracking link — shown directly on this page since candidates don't always receive email */}
        <div style={{background:"rgba(12,192,223,0.06)",border:"1px solid rgba(12,192,223,0.25)",borderRadius:14,padding:20,marginBottom:24,textAlign:"left"}}>
          <div style={{fontSize:11,fontWeight:700,color:"#0cc0df",textTransform:"uppercase",letterSpacing:1,marginBottom:10,textAlign:"center"}}>📍 Save this link to track your application</div>
          <div style={{display:"flex",gap:8,alignItems:"center",background:"rgba(255,255,255,0.05)",border:"1px solid rgba(255,255,255,0.1)",borderRadius:8,padding:"8px 10px"}}>
            <div style={{flex:1,fontSize:12,color:"rgba(255,255,255,0.7)",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",fontFamily:"monospace"}}>{`meitho.com/track/${appId}`}</div>
            <button onClick={()=>{navigator.clipboard?.writeText(`https://meitho.com/track/${appId}`);}} style={{background:"rgba(12,192,223,0.15)",border:"none",borderRadius:6,color:"#0cc0df",padding:"5px 10px",cursor:"pointer",fontSize:11,fontWeight:700,fontFamily:"inherit",flexShrink:0}}>Copy</button>
          </div>
          <div style={{fontSize:11,color:"rgba(255,255,255,0.4)",marginTop:8,textAlign:"center"}}>Bookmark this page or copy the link — this is the only way to check your status.</div>
        </div>

        <a href={`/track/${appId}`} style={{display:"inline-block",background:"linear-gradient(90deg,#ff3131,#ff914d)",border:"none",borderRadius:10,color:"#fff",padding:"12px 28px",cursor:"pointer",fontSize:14,fontWeight:700,fontFamily:"inherit",textDecoration:"none",marginBottom:12,marginRight:10}}>📍 Track My Application</a>
        <button onClick={()=>window.location.href=`/ats/${slug}`} style={{background:"transparent",border:"1px solid rgba(255,255,255,0.15)",borderRadius:10,color:"rgba(255,255,255,0.7)",padding:"12px 28px",cursor:"pointer",fontSize:14,fontWeight:700,fontFamily:"inherit"}}>← View More Jobs</button>
      </div>
    </div>
  );

  return(
    <div style={{minHeight:"100vh",background:"#050a14",color:"#f0f4ff",fontFamily:"'Inter',sans-serif"}}>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&family=Sora:wght@700;800;900&display=swap');*{box-sizing:border-box;margin:0;padding:0;}`}</style>

      {/* Nav */}
      <nav style={{padding:"16px 5%",display:"flex",justifyContent:"space-between",alignItems:"center",borderBottom:"1px solid rgba(255,255,255,0.06)"}}>
        <button onClick={()=>window.location.href=`/ats/${slug}`} style={{background:"rgba(255,255,255,0.04)",border:"1px solid rgba(255,255,255,0.08)",borderRadius:8,color:"#fff",padding:"7px 14px",cursor:"pointer",fontSize:12,fontFamily:"inherit"}}>← {job.companyName||company?.name}</button>
        <div style={{fontSize:12,color:"rgba(255,255,255,0.4)"}}>Powered by <strong style={{color:"#ff3131"}}>Meitho</strong></div>
      </nav>

      <div style={{maxWidth:900,margin:"0 auto",padding:"40px 5%",display:"grid",gridTemplateColumns:"1fr 380px",gap:32,alignItems:"start"}} className="pub-grid">
        <style>{`@media(max-width:768px){.pub-grid{grid-template-columns:1fr!important;}}`}</style>

        {/* Job Details */}
        <div>
          <div style={{marginBottom:32}}>
            <h1 style={{fontFamily:"'Sora',sans-serif",fontSize:36,fontWeight:900,marginBottom:12,letterSpacing:"-1px"}}>{job.title}</h1>
            <div style={{display:"flex",gap:16,flexWrap:"wrap",marginBottom:20}}>
              {[["📍",job.location],["💼",job.department],["💰",job.salaryRange],["🏢",job.locationType],["👥",job.openings+" openings"],["📅",job.experience]].filter(([,v])=>v).map(([icon,val],i)=>(
                <div key={i} style={{fontSize:13,color:"rgba(255,255,255,0.5)",display:"flex",gap:4}}><span>{icon}</span><span>{val}</span></div>
              ))}
            </div>
            <button onClick={()=>setStep("apply")} style={{background:G,border:"none",borderRadius:10,color:"#fff",padding:"13px 32px",cursor:"pointer",fontSize:15,fontWeight:700,fontFamily:"inherit",boxShadow:"0 8px 24px rgba(255,49,49,0.3)"}}>Apply Now →</button>
          </div>

          {job.description&&(
            <div style={{marginBottom:28}}>
              <h2 style={{fontFamily:"'Sora',sans-serif",fontSize:18,fontWeight:800,marginBottom:12}}>About the Role</h2>
              <div style={{fontSize:14,color:"rgba(255,255,255,0.7)",lineHeight:1.8,whiteSpace:"pre-wrap"}}>{job.description}</div>
            </div>
          )}

          {job.requirements&&(
            <div style={{marginBottom:28}}>
              <h2 style={{fontFamily:"'Sora',sans-serif",fontSize:18,fontWeight:800,marginBottom:12}}>Requirements</h2>
              <div style={{fontSize:14,color:"rgba(255,255,255,0.7)",lineHeight:1.8,whiteSpace:"pre-wrap"}}>{job.requirements}</div>
            </div>
          )}

          {job.rounds?.length>0&&(
            <div>
              <h2 style={{fontFamily:"'Sora',sans-serif",fontSize:18,fontWeight:800,marginBottom:12}}>Interview Process</h2>
              {job.rounds.map((round,i)=>(
                <div key={i} style={{display:"flex",gap:14,marginBottom:12}}>
                  <div style={{width:28,height:28,borderRadius:"50%",background:G,display:"flex",alignItems:"center",justifyContent:"center",fontSize:12,fontWeight:700,flexShrink:0}}>{i+1}</div>
                  <div>
                    <div style={{fontSize:14,fontWeight:600,color:"#fff"}}>{round.name||`Round ${i+1}`}</div>
                    <div style={{fontSize:12,color:"rgba(255,255,255,0.4)",textTransform:"capitalize"}}>{round.type?.replace("_"," ")} · {round.duration} mins</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Application Form */}
        <div style={{background:"rgba(255,255,255,0.03)",border:"1px solid rgba(255,255,255,0.08)",borderRadius:20,padding:24,position:"sticky",top:20}}>
          {step==="detail"?(
            <div style={{textAlign:"center",padding:"20px 0"}}>
              <div style={{fontFamily:"'Sora',sans-serif",fontSize:18,fontWeight:800,marginBottom:8}}>Ready to apply?</div>
              <div style={{fontSize:13,color:"rgba(255,255,255,0.5)",marginBottom:20}}>Takes less than 2 minutes</div>
              <button onClick={()=>setStep("apply")} style={{background:G,border:"none",borderRadius:10,color:"#fff",padding:"13px 32px",cursor:"pointer",fontSize:14,fontWeight:700,fontFamily:"inherit",width:"100%"}}>Apply Now →</button>
            </div>
          ):(
            <div>
              <div style={{fontFamily:"'Sora',sans-serif",fontSize:16,fontWeight:800,marginBottom:16}}>Apply for {job.title}</div>
              {[
                {key:"candidateName",label:"Full Name *",placeholder:"Your full name",type:"text"},
                {key:"email",label:"Email *",placeholder:"your@email.com",type:"email"},
                {key:"phone",label:"Phone *",placeholder:"10-digit mobile number",type:"tel"},
                {key:"experience",label:"Total Experience",placeholder:"e.g. 4 years",type:"text"},
                {key:"currentCTC",label:"Current CTC (LPA)",placeholder:"e.g. 8",type:"number"},
                {key:"expectedCTC",label:"Expected CTC (LPA)",placeholder:"e.g. 12",type:"number"},
                {key:"noticePeriod",label:"Notice Period",placeholder:"e.g. 30 days",type:"text"},
              ].map(field=>(
                <div key={field.key} style={{marginBottom:12}}>
                  <label style={{fontSize:11,fontWeight:600,color:"rgba(255,255,255,0.5)",display:"block",marginBottom:4,textTransform:"uppercase"}}>{field.label}</label>
                  <input type={field.type} value={form[field.key]} onChange={e=>setForm(p=>({...p,[field.key]:e.target.value}))} placeholder={field.placeholder} style={{width:"100%",padding:"9px 12px",borderRadius:8,border:"1px solid rgba(255,255,255,0.1)",background:"rgba(255,255,255,0.05)",color:"#fff",fontSize:13,fontFamily:"inherit",outline:"none"}}/>
                </div>
              ))}
              <div style={{marginBottom:12}}>
                <ResumeUploadField companyId={job.companyId} candidateId={appId} value={form.resumeUrl} fileName={form.resumeName}
                  onUploaded={res=>setForm(p=>({...p,resumeUrl:res.url,resumeName:res.name}))} dark={true}/>
              </div>
              <div style={{marginBottom:16}}>
                <label style={{fontSize:11,fontWeight:600,color:"rgba(255,255,255,0.5)",display:"block",marginBottom:4,textTransform:"uppercase"}}>Cover Letter (optional)</label>
                <textarea value={form.coverLetter} onChange={e=>setForm(p=>({...p,coverLetter:e.target.value}))} placeholder="Why are you a great fit?" rows={3} style={{width:"100%",padding:"9px 12px",borderRadius:8,border:"1px solid rgba(255,255,255,0.1)",background:"rgba(255,255,255,0.05)",color:"#fff",fontSize:13,fontFamily:"inherit",resize:"vertical",outline:"none"}}/>
              </div>
              <button onClick={submitApplication} disabled={submitting} style={{background:G,border:"none",borderRadius:10,color:"#fff",padding:"13px",cursor:"pointer",fontSize:14,fontWeight:700,fontFamily:"inherit",width:"100%",marginBottom:8}}>{submitting?"Submitting...":"Submit Application"}</button>
              <button onClick={()=>setStep("detail")} style={{background:"transparent",border:"1px solid rgba(255,255,255,0.1)",borderRadius:10,color:"rgba(255,255,255,0.5)",padding:"10px",cursor:"pointer",fontSize:12,fontFamily:"inherit",width:"100%"}}>← Back</button>
              <div style={{fontSize:10,color:"rgba(255,255,255,0.3)",textAlign:"center",marginTop:10}}>🔒 Your data is secure · Powered by Meitho BGV</div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Super Admin ATS Overview ──

function BulkVerificationPage({user,company,wallets,setWallets,addToast,isSuperAdmin}) {
  const {dark}=useTheme();
  const [step,setStep]=useState(1); // 1=upload 2=preview 3=running 4=results
  const [selectedChecks,setSelectedChecks]=useState({uan:true,pan:true,dl:false});
  const [csvRows,setCsvRows]=useState([]);
  const [validRows,setValidRows]=useState([]);
  const [invalidRows,setInvalidRows]=useState([]);
  const [costBreakdown,setCostBreakdown]=useState({});
  const [totalCost,setTotalCost]=useState(0);
  const [walletBalance,setWalletBalance]=useState(0);
  const [progress,setProgress]=useState(0);
  const [progressMsg,setProgressMsg]=useState("");
  const [results,setResults]=useState({uan:[],pan:[],dl:[],summary:[]});
  const [verifying,setVerifying]=useState(false);
  const fileRef=useRef();
  const companyId=user?.companyId||company?.id||"";
  const isHR=user?.role==="hr";

  const PRICES={uan:100,pan:50,dl:50};
  const CHECKS=[
    {id:"uan",label:"UAN Verification",price:100,icon:"🏦",desc:"EPFO employment history"},
    {id:"pan",label:"PAN Verification",price:50,icon:"📋",desc:"Income Tax dept validation"},
    {id:"dl",label:"DL Verification",price:50,icon:"🚗",desc:"RTO database check"},
  ];

  useEffect(()=>{
    const w=(wallets||{})[companyId];
    setWalletBalance(w?.balance||0);
  },[wallets,companyId]);

  const toggleCheck=id=>{
    setSelectedChecks(p=>({...p,[id]:!p[id]}));
  };

  const calcCost=(rows,checks)=>{
    const activeChecks=Object.entries(checks).filter(([,v])=>v);
    const breakdown={};
    let total=0;
    activeChecks.forEach(([id])=>{
      // Only count rows that have the required field
      const count=rows.filter(r=>{
        if(id==="uan")return (r.uan||"").replace(/\D/g,"").length===12;
        if(id==="pan")return (r.pan||"").length>=9;
        if(id==="dl")return (r.dl_number||r.dl||"").length>=6&&(r.dl_dob||r.dob||"").length>=8;
        return false;
      }).length;
      const cost=count*PRICES[id];
      breakdown[id]={count,price:PRICES[id],cost};
      total+=cost;
    });
    return{breakdown,total};
  };

  const parseCSV=text=>{
    const lines=text.trim().split("\n");
    const headers=lines[0].split(",").map(h=>h.trim().toLowerCase().replace(/[\s-]/g,"_"));
    return lines.slice(1).filter(l=>l.trim()).map((line,i)=>{
      // Handle quoted CSV properly
      const vals=[];let cur="";let inQ=false;
      for(const ch of line){
        if(ch==='"'){inQ=!inQ;}
        else if(ch===","&&!inQ){vals.push(cur.trim());cur="";}
        else cur+=ch;
      }
      vals.push(cur.trim());
      const obj={_row:i+2};
      headers.forEach((h,j)=>obj[h]=vals[j]||"");
      return obj;
    });
  };

  const handleCSV=async(file)=>{
    try{
      const text=await file.text();
      const rows=parseCSV(text);
      const valid=[];const invalid=[];
      rows.forEach(r=>{
        const name=r.employee_name||r.name||r.emp_name||"";
        const issues=[];
        if(!name)issues.push("Missing employee name");
        if(selectedChecks.uan&&!(r.uan||"").replace(/\D/g,"").match(/^\d{12}$/))issues.push("Invalid/missing UAN");
        if(selectedChecks.pan&&!(r.pan||"").match(/^[A-Z]{5}[0-9]{4}[A-Z]$/i))issues.push("Invalid/missing PAN");
        if(selectedChecks.dl){
          if(!(r.dl_number||r.dl||""))issues.push("Missing DL number");
          if(!(r.dl_dob||r.dob||""))issues.push("Missing DOB for DL");
        }
        if(issues.length>0)invalid.push({...r,name,issues});
        else valid.push({...r,name,status:"pending"});
      });
      setCsvRows(rows);
      setValidRows(valid);
      setInvalidRows(invalid);
      const{breakdown,total}=calcCost(valid,selectedChecks);
      setCostBreakdown(breakdown);
      setTotalCost(total);
      setStep(2);
      addToast(`${valid.length} valid, ${invalid.length} issues found`,"success");
    }catch(e){addToast("CSV parse error: "+e.message,"error");}
  };

  const downloadTemplate=()=>{
    const headers=["employee_name","uan","pan","dl_number","dl_dob","email","phone"];
    const sample=["Rajesh Kumar","101234567890","ABCDE1234F","TN0120XX234567","1990-01-15","rajesh@company.com","9876543210"];
    const csv=headers.join(",")+"\n"+sample.join(",");
    const a=document.createElement("a");
    a.href="data:text/csv,"+encodeURIComponent(csv);
    a.download="meitho_bulk_template.csv";a.click();
    addToast("Template downloaded!","success");
  };

  const runVerification=async()=>{
    // Balance check for non-super-admin
    if(!isSuperAdmin&&walletBalance<totalCost){
      addToast(`Insufficient balance! Need ₹${totalCost.toLocaleString("en-IN")}, have ₹${walletBalance.toLocaleString("en-IN")}`,"error");
      return;
    }
    // HR quota check
    if(isHR){
      const hrQuota=(user?.searchesAllocated||0)-(user?.searchesUsed||0);
      const totalChecks=Object.values(costBreakdown).reduce((s,v)=>s+v.count,0);
      if(hrQuota<totalChecks){
        addToast(`Insufficient quota! You have ${hrQuota} searches left, need ${totalChecks}`,"error");
        return;
      }
    }

    setStep(3);
    setVerifying(true);
    setProgress(0);

    const headers=await getAuthedHeaders();
    const uanResults=[];
    const panResults=[];
    const dlResults=[];
    const summaryResults=[];

    const total=validRows.length;
    let done=0;
    let anyGenuineResult=false; // becomes true if ANY check across the whole batch actually completed (success or legitimate not-found) rather than erroring out — used to skip charging on total failure

    for(const row of validRows){
      const rowSummary={name:row.name,uan:row.uan||"",pan:row.pan||"",dl:row.dl_number||row.dl||""};
      let uanOk=null,panOk=null,dlOk=null;

      // UAN Check
      if(selectedChecks.uan&&(row.uan||"").replace(/\D/g,"").length===12){
        try{
          setProgressMsg(`Checking UAN for ${row.name}...`);
          const res=await fetch("https://uanverify-mtvra6khyq-uc.a.run.app",{method:"POST",headers,body:JSON.stringify({uan:row.uan.replace(/\D/g,"")})});
          const json=await res.json();
          const d=json.data||{};
          const empHistory=(d.employment_history||[]).map(e=>({
            company:e.establishment_name||"",
            doj:e.date_of_joining||"",
            dor:(e.date_of_exit||"").startsWith("1800")?"Currently Employed":(e.date_of_exit||""),
            memberId:e.member_id||"",
          }));
          const found=json.status_code===200&&empHistory.length>0;
          uanOk=found?"✅ Found":"❌ Not Found";
          anyGenuineResult=true;
          uanResults.push({
            name:row.name,
            uan:row.uan,
            epfo_name:d.employment_history?.[0]?.name||"",
            status:found?"Found":"Not Found",
            total_jobs:empHistory.length,
            ...empHistory.reduce((acc,e,i)=>({
              ...acc,
              [`company_${i+1}`]:e.company,
              [`doj_${i+1}`]:e.doj,
              [`dor_${i+1}`]:e.dor,
            }),{}),
          });
        }catch(e){
          uanOk="❌ Error";
          uanResults.push({name:row.name,uan:row.uan,status:"Error",error:e.message});
        }
      }

      // PAN Check
      if(selectedChecks.pan&&(row.pan||"").length>=9){
        try{
          setProgressMsg(`Checking PAN for ${row.name}...`);
          const res=await fetch("https://panverify-mtvra6khyq-uc.a.run.app",{method:"POST",headers,body:JSON.stringify({pan:(row.pan||"").toUpperCase()})});
          const json=await res.json();
          const d=json.data||{};
          const found=json.status_code===200;
          panOk=found?"✅ Valid":"❌ Invalid";
          anyGenuineResult=true;
          panResults.push({
            name:row.name,
            pan:(row.pan||"").toUpperCase(),
            pan_holder_name:d.full_name||d.name||"",
            pan_status:d.pan_status||d.status||"",
            pan_type:d.pan_type||"",
            aadhaar_linked:d.aadhaar_linked?"Yes":"No",
            status:found?"Valid":"Invalid",
          });
        }catch(e){
          panOk="❌ Error";
          panResults.push({name:row.name,pan:row.pan,status:"Error",error:e.message});
        }
      }

      // DL Check
      if(selectedChecks.dl&&(row.dl_number||row.dl||"")){
        try{
          setProgressMsg(`Checking DL for ${row.name}...`);
          const dlNum=(row.dl_number||row.dl||"").toUpperCase();
          const dlDob=row.dl_dob||row.dob||"";
          const res=await fetch("https://dlverify-mtvra6khyq-uc.a.run.app",{method:"POST",headers,body:JSON.stringify({dl_number:dlNum,dob:dlDob})});
          const json=await res.json();
          const d=json.data||{};
          const found=json.status_code===200;
          dlOk=found?"✅ Valid":"❌ Invalid";
          anyGenuineResult=true;
          dlResults.push({
            name:row.name,
            dl_number:dlNum,
            dl_holder_name:d.name||d.full_name||"",
            dob:d.dob||dlDob,
            valid_from:d.transport?.issue_date||d.non_transport?.issue_date||"",
            valid_till:d.transport?.expiry_date||d.non_transport?.expiry_date||"",
            vehicle_class:(d.transport?.vehicle_class||[]).concat(d.non_transport?.vehicle_class||[]).join(", "),
            status:found?"Valid":"Invalid",
          });
        }catch(e){
          dlOk="❌ Error";
          dlResults.push({name:row.name,dl:row.dl_number||row.dl,status:"Error",error:e.message});
        }
      }

      // Summary row
      summaryResults.push({
        ...rowSummary,
        uan_result:uanOk||"—",
        pan_result:panOk||"—",
        dl_result:dlOk||"—",
        overall:(!uanOk||uanOk.includes("✅"))&&(!panOk||panOk.includes("✅"))&&(!dlOk||dlOk.includes("✅"))?"✅ Clear":"❌ Issues",
      });

      done++;
      setProgress(Math.round((done/total)*100));

      // Small delay to avoid rate limiting
      await new Promise(r=>setTimeout(r,300));
    }

    // Deduct wallet for non-super-admin — but only if at least one check in
    // the whole batch actually completed. Previously this charged the full
    // batch cost unconditionally, even if every single check errored out
    // (which, combined with the missing auth header bug above, meant 100%
    // of Bulk Verify runs were likely being charged despite 100% failure).
    if(!isSuperAdmin&&totalCost>0&&anyGenuineResult){
      try{await walletChargeSrv(Object.entries(selectedChecks).filter(([,v])=>v).map(([k])=>({type:k,qty:validRows.length})),"Bulk verification — "+Object.entries(selectedChecks).filter(([,v])=>v).map(([k])=>k.toUpperCase()).join("+")+" × "+validRows.length+" employees","bulk");}
      catch(e){console.warn("Wallet deduction failed:",e);addToast("Could not deduct the wallet: "+e.message,"error");}
    }else if(!isSuperAdmin&&totalCost>0&&!anyGenuineResult){
      addToast("None of the checks could be completed — you have not been charged for this batch.","amber");
    }

    setResults({uan:uanResults,pan:panResults,dl:dlResults,summary:summaryResults});
    setVerifying(false);
    setProgressMsg("Complete!");
    setStep(4);
    addToast("Bulk verification complete!","success");
  };

  const downloadExcel=()=>{
    // Build multi-sheet Excel using SheetJS-style CSV with sheet markers
    // We'll create a proper XLSX using multiple CSV downloads
    const timestamp=new Date().toISOString().slice(0,10);

    // Summary sheet
    if(results.summary.length>0){
      const headers=Object.keys(results.summary[0]);
      const csv=[headers.join(","),...results.summary.map(r=>headers.map(h=>`"${(r[h]||"").toString().replace(/"/g,'""')}"`).join(","))].join("\n");
      const a=document.createElement("a");
      a.href="data:text/csv;charset=utf-8,"+encodeURIComponent(csv);
      a.download=`meitho_bulk_${timestamp}_summary.csv`;a.click();
    }

    setTimeout(()=>{
      // UAN sheet
      if(results.uan.length>0){
        const headers=Object.keys(results.uan[0]);
        const csv=[headers.join(","),...results.uan.map(r=>headers.map(h=>`"${(r[h]||"").toString().replace(/"/g,'""')}"`).join(","))].join("\n");
        const a=document.createElement("a");
        a.href="data:text/csv;charset=utf-8,"+encodeURIComponent(csv);
        a.download=`meitho_bulk_${timestamp}_UAN.csv`;a.click();
      }
    },500);

    setTimeout(()=>{
      // PAN sheet
      if(results.pan.length>0){
        const headers=Object.keys(results.pan[0]);
        const csv=[headers.join(","),...results.pan.map(r=>headers.map(h=>`"${(r[h]||"").toString().replace(/"/g,'""')}"`).join(","))].join("\n");
        const a=document.createElement("a");
        a.href="data:text/csv;charset=utf-8,"+encodeURIComponent(csv);
        a.download=`meitho_bulk_${timestamp}_PAN.csv`;a.click();
      }
    },1000);

    setTimeout(()=>{
      // DL sheet
      if(results.dl.length>0){
        const headers=Object.keys(results.dl[0]);
        const csv=[headers.join(","),...results.dl.map(r=>headers.map(h=>`"${(r[h]||"").toString().replace(/"/g,'""')}"`).join(","))].join("\n");
        const a=document.createElement("a");
        a.href="data:text/csv;charset=utf-8,"+encodeURIComponent(csv);
        a.download=`meitho_bulk_${timestamp}_DL.csv`;a.click();
      }
      addToast("All result sheets downloaded!","success");
    },1500);
  };

  const Card=({children,style={}})=><div className="fu" style={{borderRadius:14,padding:"20px 24px",...style}}>{children}</div>;

  // ── STEP 1: Upload & Check Selection ──
  if(step===1)return(
    <div>
      <div className="fu" style={{marginBottom:20}}>
        <h2 style={{fontFamily:FFD,fontWeight:800,fontSize:24,color:"var(--text)",marginBottom:4}}>⚡ Bulk Verification</h2>
        <p style={{color:"var(--muted)",fontSize:13}}>Verify multiple employees at once · UAN · PAN · DL</p>
      </div>

      {/* Check selector */}
      <Card style={{marginBottom:16}}>
        <div style={{fontFamily:FFD,fontSize:13,fontWeight:700,color:"var(--text)",marginBottom:14}}>Select Verification Types</div>
        <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:12}} className="mg1">
          {CHECKS.map(c=>(
            <div key={c.id} onClick={()=>toggleCheck(c.id)} style={{padding:"16px",borderRadius:12,border:`2px solid ${selectedChecks[c.id]?"#ff3131":"var(--b1)"}`,background:selectedChecks[c.id]?"rgba(255,49,49,0.05)":"var(--s2)",cursor:"pointer",transition:"all .2s"}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:8}}>
                <span style={{fontSize:22}}>{c.icon}</span>
                <div style={{width:18,height:18,borderRadius:"50%",border:`2px solid ${selectedChecks[c.id]?"#ff3131":"var(--b1)"}`,background:selectedChecks[c.id]?"#ff3131":"transparent",display:"flex",alignItems:"center",justifyContent:"center"}}>
                  {selectedChecks[c.id]&&<svg width="10" height="10" viewBox="0 0 10 10" fill="none"><path d="M2 5l2 2 4-4" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>}
                </div>
              </div>
              <div style={{fontFamily:FFD,fontSize:13,fontWeight:700,color:"var(--text)",marginBottom:3}}>{c.label}</div>
              <div style={{fontSize:11,color:"var(--muted)",marginBottom:6}}>{c.desc}</div>
              <div style={{fontSize:12,fontWeight:700,color:"#ff3131"}}>₹{c.price}/employee</div>
            </div>
          ))}
        </div>
        <div style={{marginTop:12,padding:"10px 14px",background:"rgba(255,145,77,0.06)",border:"1px solid rgba(255,145,77,0.2)",borderRadius:8,fontSize:12,color:"var(--muted)"}}>
          ⚠️ Aadhaar, Education & eCourt require candidate consent — not available in bulk
        </div>
      </Card>

      {/* Wallet balance for non-super-admin */}
      {!isSuperAdmin&&(
        <Card style={{marginBottom:16}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
            <div>
              <div style={{fontFamily:FFD,fontSize:13,fontWeight:700,color:"var(--text)",marginBottom:2}}>Wallet Balance</div>
              <div style={{fontSize:11,color:"var(--muted)"}}>Available for bulk verification</div>
            </div>
            <div style={{fontFamily:FFD,fontSize:24,fontWeight:900,color:walletBalance<500?"#ff3131":"var(--green)"}}>₹{walletBalance.toLocaleString("en-IN")}</div>
          </div>
        </Card>
      )}

      {/* Upload area */}
      <Card style={{marginBottom:16}}>
        <div style={{fontFamily:FFD,fontSize:13,fontWeight:700,color:"var(--text)",marginBottom:14}}>Upload CSV File</div>
        <div onClick={()=>fileRef.current?.click()} style={{border:"2px dashed var(--b1)",borderRadius:12,padding:"40px",textAlign:"center",cursor:"pointer",transition:"all .2s"}}
          onMouseEnter={e=>{e.currentTarget.style.borderColor="#ff3131";e.currentTarget.style.background="rgba(255,49,49,0.02)";}}
          onMouseLeave={e=>{e.currentTarget.style.borderColor="var(--b1)";e.currentTarget.style.background="transparent";}}>
          <div style={{fontSize:36,marginBottom:12}}>📂</div>
          <div style={{fontFamily:FFD,fontSize:15,fontWeight:700,color:"var(--text)",marginBottom:6}}>Click to upload CSV</div>
          <div style={{fontSize:12,color:"var(--muted)"}}>Max 500 employees per file</div>
        </div>
        <input ref={fileRef} type="file" accept=".csv" style={{display:"none"}} onChange={e=>{if(e.target.files[0])handleCSV(e.target.files[0]);}}/>
        <button onClick={downloadTemplate} style={{marginTop:14,background:"var(--s2)",border:"1px solid var(--b1)",borderRadius:8,color:"var(--text)",padding:"8px 16px",cursor:"pointer",fontSize:12,fontWeight:600,fontFamily:FF,display:"flex",alignItems:"center",gap:6}}>
          ↓ Download CSV Template
        </button>
      </Card>

      {/* Template guide */}
      <Card>
        <div style={{fontFamily:FFD,fontSize:13,fontWeight:700,color:"var(--text)",marginBottom:12}}>CSV Format Guide</div>
        <div style={{overflowX:"auto"}}>
          <table style={{width:"100%",borderCollapse:"collapse",fontSize:12}}>
            <thead><tr style={{background:"var(--s2)"}}>
              {["Column","Required For","Format","Example"].map(h=><th key={h} style={{padding:"8px 12px",textAlign:"left",fontSize:10,fontWeight:700,color:"var(--muted)",textTransform:"uppercase"}}>{h}</th>)}
            </tr></thead>
            <tbody>
              {[
                ["employee_name","All checks","Text","Rajesh Kumar"],
                ["uan","UAN check","12 digits","101234567890"],
                ["pan","PAN check","AAAAA9999A","ABCDE1234F"],
                ["dl_number","DL check","State code + number","TN0120XX234567"],
                ["dl_dob","DL check","YYYY-MM-DD","1990-01-15"],
                ["email","Optional","Email","rajesh@company.com"],
                ["phone","Optional","10 digits","9876543210"],
              ].map(([col,req,fmt,ex],i)=>(
                <tr key={i} style={{borderBottom:"1px solid var(--b1)"}}>
                  <td style={{padding:"8px 12px",fontFamily:"monospace",color:"#ff3131",fontWeight:600}}>{col}</td>
                  <td style={{padding:"8px 12px",color:"var(--muted)"}}>{req}</td>
                  <td style={{padding:"8px 12px",color:"var(--muted)"}}>{fmt}</td>
                  <td style={{padding:"8px 12px",color:"var(--text)",fontFamily:"monospace"}}>{ex}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );

  // ── STEP 2: Preview & Balance Check ──
  if(step===2){
    const hasBalance=isSuperAdmin||walletBalance>=totalCost;
    const shortfall=Math.max(0,totalCost-walletBalance);

    return(
      <div>
        <div style={{display:"flex",gap:10,alignItems:"center",marginBottom:20}}>
          <button onClick={()=>{setStep(1);setCsvRows([]);setValidRows([]);setInvalidRows([]);}} style={{background:"var(--s2)",border:"1px solid var(--b1)",borderRadius:8,color:"var(--text)",padding:"7px 14px",cursor:"pointer",fontSize:12,fontWeight:600,fontFamily:FF}}>← Back</button>
          <h2 style={{fontFamily:FFD,fontWeight:800,fontSize:22,color:"var(--text)"}}>Preview & Confirm</h2>
        </div>

        {/* Cost breakdown */}
        <Card style={{marginBottom:16}}>
          <div style={{fontFamily:FFD,fontSize:13,fontWeight:700,color:"var(--text)",marginBottom:14}}>💰 Cost Breakdown</div>
          <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:10,marginBottom:16}} className="mg1">
            {Object.entries(costBreakdown).map(([type,d])=>(
              <div key={type} style={{background:"var(--s2)",borderRadius:10,padding:"12px 14px",border:"1px solid var(--b1)"}}>
                <div style={{fontSize:11,color:"var(--muted)",fontWeight:600,marginBottom:4,textTransform:"uppercase"}}>{type}</div>
                <div style={{fontSize:13,color:"var(--text)",marginBottom:2}}>{d.count} employees × ₹{d.price}</div>
                <div style={{fontFamily:FFD,fontSize:18,fontWeight:800,color:"#ff3131"}}>₹{d.cost.toLocaleString("en-IN")}</div>
              </div>
            ))}
          </div>
          <div style={{borderTop:"1px solid var(--b1)",paddingTop:14,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
            <div style={{fontSize:14,fontWeight:700,color:"var(--text)"}}>Total Cost</div>
            <div style={{fontFamily:FFD,fontSize:24,fontWeight:900,color:"#ff3131"}}>₹{totalCost.toLocaleString("en-IN")}</div>
          </div>
          {!isSuperAdmin&&(
            <div style={{marginTop:12,display:"flex",justifyContent:"space-between",alignItems:"center",padding:"10px 14px",background:hasBalance?"rgba(16,185,129,0.06)":"rgba(255,49,49,0.06)",border:`1px solid ${hasBalance?"rgba(16,185,129,0.2)":"rgba(255,49,49,0.2)"}`,borderRadius:10}}>
              <div>
                <div style={{fontSize:12,color:"var(--muted)"}}>Wallet Balance</div>
                <div style={{fontFamily:FFD,fontSize:18,fontWeight:800,color:hasBalance?"var(--green)":"#ff3131"}}>₹{walletBalance.toLocaleString("en-IN")}</div>
              </div>
              {hasBalance
                ?<div style={{fontSize:13,color:"var(--green)",fontWeight:700}}>✅ Sufficient balance</div>
                :<div style={{textAlign:"right"}}>
                  <div style={{fontSize:13,color:"#ff3131",fontWeight:700}}>❌ Insufficient balance</div>
                  <div style={{fontSize:11,color:"var(--muted)"}}>Shortfall: ₹{shortfall.toLocaleString("en-IN")}</div>
                </div>
              }
            </div>
          )}
          {!hasBalance&&(
            <div style={{marginTop:12,padding:"14px 16px",background:"rgba(255,49,49,0.04)",border:"1px solid rgba(255,49,49,0.15)",borderRadius:10}}>
              <div style={{fontSize:13,fontWeight:700,color:"#ff3131",marginBottom:6}}>⚠️ Please recharge your wallet</div>
              <div style={{fontSize:12,color:"var(--muted)",marginBottom:10}}>Contact Meitho to add ₹{shortfall.toLocaleString("en-IN")} or more to your wallet.</div>
              <a href="https://wa.me/918300016265?text=Hi%20Meitho%2C%20I%20need%20to%20recharge%20my%20wallet%20for%20bulk%20verification" target="_blank" rel="noopener noreferrer" style={{background:"#25D366",border:"none",borderRadius:8,color:"#fff",padding:"8px 16px",cursor:"pointer",fontSize:12,fontWeight:700,textDecoration:"none",display:"inline-flex",alignItems:"center",gap:6}}>
                💬 WhatsApp to Recharge
              </a>
            </div>
          )}
        </Card>

        {/* Valid rows */}
        <Card style={{marginBottom:16}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14}}>
            <div style={{fontFamily:FFD,fontSize:13,fontWeight:700,color:"var(--green)"}}>✅ Ready to verify ({validRows.length})</div>
          </div>
          <div style={{maxHeight:280,overflowY:"auto"}}>
            <table style={{width:"100%",borderCollapse:"collapse"}}>
              <thead><tr style={{background:"var(--s2)"}}>
                {["#","Name","UAN","PAN","DL","Checks"].map(h=><th key={h} style={{padding:"8px 12px",textAlign:"left",fontSize:10,fontWeight:700,color:"var(--muted)",textTransform:"uppercase"}}>{h}</th>)}
              </tr></thead>
              <tbody>
                {validRows.map((r,i)=>(
                  <tr key={i} style={{borderBottom:"1px solid var(--b1)"}}>
                    <td style={{padding:"8px 12px",fontSize:11,color:"var(--muted)"}}>{i+1}</td>
                    <td style={{padding:"8px 12px",fontSize:12,fontWeight:600,color:"var(--text)"}}>{r.name}</td>
                    <td style={{padding:"8px 12px",fontSize:11,fontFamily:"monospace",color:"var(--muted)"}}>{r.uan||"—"}</td>
                    <td style={{padding:"8px 12px",fontSize:11,fontFamily:"monospace",color:"var(--muted)"}}>{r.pan||"—"}</td>
                    <td style={{padding:"8px 12px",fontSize:11,fontFamily:"monospace",color:"var(--muted)"}}>{r.dl_number||r.dl||"—"}</td>
                    <td style={{padding:"8px 12px"}}>
                      <div style={{display:"flex",gap:4}}>
                        {selectedChecks.uan&&r.uan&&<Badge color="#ff3131" style={{fontSize:8}}>UAN</Badge>}
                        {selectedChecks.pan&&r.pan&&<Badge color="#ff914d" style={{fontSize:8}}>PAN</Badge>}
                        {selectedChecks.dl&&(r.dl_number||r.dl)&&<Badge color="#0cc0df" style={{fontSize:8}}>DL</Badge>}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>

        {/* Invalid rows */}
        {invalidRows.length>0&&(
          <Card style={{marginBottom:16}}>
            <div style={{fontFamily:FFD,fontSize:13,fontWeight:700,color:"#ff3131",marginBottom:14}}>⚠️ Issues Found ({invalidRows.length}) — Will be skipped</div>
            <div style={{maxHeight:200,overflowY:"auto"}}>
              {invalidRows.map((r,i)=>(
                <div key={i} style={{display:"flex",justifyContent:"space-between",padding:"8px 0",borderBottom:"1px solid var(--b1)"}}>
                  <div style={{fontSize:12,fontWeight:600,color:"var(--text)"}}>{r.name||`Row ${r._row}`}</div>
                  <div style={{fontSize:11,color:"#ff3131"}}>{(r.issues||[]).join(" · ")}</div>
                </div>
              ))}
            </div>
          </Card>
        )}

        {/* Confirm button */}
        <div style={{display:"flex",gap:10}}>
          <button onClick={runVerification} disabled={!hasBalance||validRows.length===0} style={{background:hasBalance&&validRows.length>0?"linear-gradient(90deg,#ff3131,#ff914d)":"var(--s2)",border:"none",borderRadius:10,color:hasBalance&&validRows.length>0?"#fff":"var(--muted)",padding:"13px 32px",cursor:hasBalance&&validRows.length>0?"pointer":"not-allowed",fontSize:14,fontWeight:700,fontFamily:FF,boxShadow:hasBalance&&validRows.length>0?"0 8px 24px rgba(255,49,49,0.3)":"none"}}>
            ⚡ Start Bulk Verification ({validRows.length} employees)
          </button>
          <button onClick={()=>setStep(1)} style={{background:"var(--s2)",border:"1px solid var(--b1)",borderRadius:10,color:"var(--text)",padding:"13px 20px",cursor:"pointer",fontSize:14,fontWeight:600,fontFamily:FF}}>Cancel</button>
        </div>
      </div>
    );
  }

  // ── STEP 3: Running ──
  if(step===3)return(
    <div style={{display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",minHeight:"60vh",gap:24}}>
      <div style={{width:80,height:80,borderRadius:"50%",background:"rgba(255,49,49,0.1)",border:"2px solid rgba(255,49,49,0.3)",display:"flex",alignItems:"center",justifyContent:"center",animation:"pulse 2s ease infinite"}}>
        <div style={{fontFamily:FFD,fontSize:24,fontWeight:900,color:"#ff3131"}}>{progress}%</div>
      </div>
      <div>
        <div style={{fontFamily:FFD,fontSize:22,fontWeight:800,color:"var(--text)",textAlign:"center",marginBottom:8}}>Verifying employees...</div>
        <div style={{fontSize:13,color:"var(--muted)",textAlign:"center",marginBottom:20}}>{progressMsg}</div>
        <div style={{width:320,height:6,background:"var(--s2)",borderRadius:99,overflow:"hidden"}}>
          <div style={{height:"100%",width:progress+"%",background:"linear-gradient(90deg,#ff3131,#ff914d)",borderRadius:99,transition:"width .3s ease"}}/>
        </div>
        <div style={{fontSize:11,color:"var(--muted)",textAlign:"center",marginTop:8}}>{Math.round(validRows.length*progress/100)}/{validRows.length} employees done</div>
      </div>
      <div style={{fontSize:12,color:"var(--muted)"}}>Please don't close this tab</div>
    </div>
  );

  // ── STEP 4: Results ──
  if(step===4){
    const cleared=results.summary.filter(r=>r.overall?.includes("✅")).length;
    const issues=results.summary.filter(r=>r.overall?.includes("❌")).length;

    return(
      <div>
        <div className="fu" style={{marginBottom:20,padding:"20px 24px"}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",flexWrap:"wrap",gap:12}}>
            <div>
              <h2 style={{fontFamily:FFD,fontWeight:800,fontSize:22,color:"var(--text)",marginBottom:4}}>✅ Bulk Verification Complete</h2>
              <p style={{color:"var(--muted)",fontSize:13}}>{results.summary.length} employees verified · ₹{totalCost.toLocaleString("en-IN")} charged</p>
            </div>
            <button onClick={downloadExcel} style={{background:"linear-gradient(90deg,#ff3131,#ff914d)",border:"none",borderRadius:10,color:"#fff",padding:"10px 24px",cursor:"pointer",fontSize:13,fontWeight:700,fontFamily:FF,display:"flex",alignItems:"center",gap:8}}>
              ↓ Download All Sheets
            </button>
          </div>
        </div>

        {/* Stats */}
        <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:12,marginBottom:20}} className="mg2">
          {[
            ["👥","Total",results.summary.length,"var(--brand)"],
            ["✅","All Clear",cleared,"var(--green)"],
            ["❌","Issues",issues,"#ff3131"],
            ["💰","Cost","₹"+totalCost.toLocaleString("en-IN"),"#ff914d"],
          ].map(([icon,label,val,color],i)=>(
            <div key={i} className="fu" style={{padding:"14px 16px",borderRadius:12}}>
              <div style={{fontSize:20,marginBottom:6}}>{icon}</div>
              <div style={{fontFamily:FFD,fontSize:22,fontWeight:900,color,marginBottom:2}}>{val}</div>
              <div style={{fontSize:11,color:"var(--muted)",fontWeight:600}}>{label}</div>
            </div>
          ))}
        </div>

        {/* Tabs for each sheet */}
        {[
          {label:"📊 Summary",data:results.summary,key:"summary"},
          {label:"🏦 UAN Results",data:results.uan,key:"uan"},
          {label:"📋 PAN Results",data:results.pan,key:"pan"},
          {label:"🚗 DL Results",data:results.dl,key:"dl"},
        ].filter(t=>t.data.length>0).map(t=>(
          <div key={t.key} className="fu" style={{borderRadius:14,padding:0,overflow:"hidden",marginBottom:16}}>
            <div style={{padding:"12px 16px",borderBottom:"1px solid var(--b1)",display:"flex",justifyContent:"space-between",alignItems:"center"}}>
              <div style={{fontFamily:FFD,fontSize:13,fontWeight:700,color:"var(--text)"}}>{t.label} ({t.data.length})</div>
              <button onClick={()=>{
                const headers=Object.keys(t.data[0]);
                const csv=[headers.join(","),...t.data.map(r=>headers.map(h=>`"${(r[h]||"").toString().replace(/"/g,'""')}"`).join(","))].join("\n");
                const a=document.createElement("a");a.href="data:text/csv;charset=utf-8,"+encodeURIComponent(csv);a.download=`meitho_${t.key}_results.csv`;a.click();
              }} style={{background:"var(--s2)",border:"1px solid var(--b1)",borderRadius:7,color:"var(--text)",padding:"5px 12px",cursor:"pointer",fontSize:11,fontWeight:600,fontFamily:FF}}>↓ Download</button>
            </div>
            <div style={{overflowX:"auto",maxHeight:320,overflowY:"auto"}}>
              <table style={{width:"100%",borderCollapse:"collapse",minWidth:600}}>
                <thead><tr style={{background:"var(--s2)"}}>
                  {Object.keys(t.data[0]).map(h=><th key={h} style={{padding:"8px 12px",textAlign:"left",fontSize:10,fontWeight:700,color:"var(--muted)",textTransform:"uppercase",whiteSpace:"nowrap"}}>{h.replace(/_/g," ")}</th>)}
                </tr></thead>
                <tbody>
                  {t.data.map((row,i)=>(
                    <tr key={i} style={{borderBottom:"1px solid var(--b1)",background:row.overall?.includes("❌")||row.status==="Invalid"||row.status==="Not Found"?"rgba(255,49,49,0.02)":"transparent"}}>
                      {Object.entries(row).map(([k,v],j)=>(
                        <td key={j} style={{padding:"8px 12px",fontSize:12,color:String(v).includes("✅")?"var(--green)":String(v).includes("❌")?"#ff3131":"var(--text)",whiteSpace:"nowrap",fontWeight:String(v).includes("✅")||String(v).includes("❌")?700:400}}>
                          {String(v||"—")}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ))}

        <button onClick={()=>{setStep(1);setResults({uan:[],pan:[],dl:[],summary:[]});setCsvRows([]);setValidRows([]);setInvalidRows([]);}} style={{background:"var(--s2)",border:"1px solid var(--b1)",borderRadius:10,color:"var(--text)",padding:"10px 20px",cursor:"pointer",fontSize:13,fontWeight:600,fontFamily:FF}}>
          ← New Bulk Verification
        </button>
      </div>
    );
  }

  return null;
}


function FirebaseSetupPage() {
  const [copied,setCopied]=useState(null);
  const copy=(code,i)=>{navigator.clipboard.writeText(code);setCopied(i);setTimeout(()=>setCopied(null),2000);};
  const steps=[
    {num:"01",title:"Create Project",icon:"🔥",desc:"Go to console.firebase.google.com → Add project → name it meitho-bgv → Create project.",code:`# Go to:\nhttps://console.firebase.google.com`},
    {num:"02",title:"Enable Authentication",icon:"🔐",desc:"Firebase Console → Authentication → Get started → Enable Email/Password. Then add your Super Admin user manually under Users tab.",code:`// In Firestore → users collection\n// Document ID = Firebase Auth UID\n{\n  "name": "Super Admin",\n  "email": "admin@meitho.com",\n  "role": "superadmin",\n  "company": "Meitho BGV",\n  "companyId": null\n}`},
    {num:"03",title:"Create Firestore Database",icon:"🗄",desc:"Firebase Console → Firestore Database → Create database → Production mode → asia-south1 (Mumbai) region.",code:`# Collections (auto-created):\n# users\n# companies\n# employees\n# searchHistory\n# notifications\n# plans`},
    {num:"04",title:"Enable Storage",icon:"☁️",desc:"Firebase Console → Storage → Get started → Production mode → Same region as Firestore.",code:`# Storage structure:\n# /logos/{companyId}/logo.png\n# /bulk-uploads/{userId}/file.csv\n# /reports/{userId}/report.txt`},
    {num:"05",title:"Get Firebase Config",icon:"⚙️",desc:"Project Settings (gear icon) → Your apps → Add Web App → Register → Copy the config object → Paste into src/firebase/config.js",code:`const firebaseConfig = {\n  apiKey: "YOUR_API_KEY",\n  authDomain: "YOUR_PROJECT.firebaseapp.com",\n  projectId: "YOUR_PROJECT_ID",\n  storageBucket: "YOUR_PROJECT.appspot.com",\n  messagingSenderId: "YOUR_SENDER_ID",\n  appId: "YOUR_APP_ID"\n};`},
    {num:"06",title:"Deploy Security Rules",icon:"🔒",desc:"Protect your database — deploy the security rules included in the project.",code:`# Install CLI\nnpm install -g firebase-tools\n\n# Login\nfirebase login\n\n# Deploy rules\nfirebase deploy --only firestore:rules,storage`},
    {num:"07",title:"Run Locally",icon:"💻",desc:"Install dependencies and start development server.",code:`npm install\nnpm start\n# Opens at http://localhost:3000`},
    {num:"08",title:"Setup Email Alerts",icon:"📧",desc:"Enable email notifications for low credits and new registrations using Cloud Functions.",code:`firebase functions:config:set \\\n  email.user="you@gmail.com" \\\n  email.password="app_password"\n\nfirebase deploy --only functions`},
    {num:"09",title:"Deploy Live",icon:"🌐",desc:"Build and deploy to hosting. Your app goes live instantly.",code:`npm run build\nfirebase deploy --only hosting\n\n# Live at:\nhttps://your-project-id.web.app`},
  ];
  return(
    <div>
      <div className="fu" style={{marginBottom:28}}>
        <h2 style={{fontFamily:FFD,fontWeight:700,fontSize:26,color:"var(--text)",marginBottom:4}}>Setup Guide</h2>
        <p style={{color:"var(--muted)",fontSize:14}}>Complete step-by-step deployment guide for Meitho BGV</p>
      </div>
      <div style={{display:"flex",gap:10,marginBottom:24,flexWrap:"wrap"}}>
        {[["🔐","Auth","Email login"],["🗄","Database","NoSQL"],["☁️","Storage","Files"],["📧","Alerts","Email"],["🌐","Hosting","Live"]].map(([ic,nm,desc])=>(
          <div key={nm} style={{background:"var(--s1)",border:"1px solid var(--b1)",borderRadius:12,padding:"10px 14px",display:"flex",gap:8,alignItems:"center"}}>
            <span style={{fontSize:16}}>{ic}</span>
            <div><div style={{fontSize:13,fontWeight:700,color:"var(--text)",fontFamily:FFD}}>{nm}</div><div style={{fontSize:11,color:"var(--muted)"}}>{desc}</div></div>
          </div>
        ))}
      </div>
      <div style={{display:"flex",flexDirection:"column",gap:14}}>
        {steps.map((step,i)=>(
          <Card key={i} className={`fu${Math.min(i,3)}`}>
            <div style={{display:"flex",gap:16,alignItems:"flex-start"}}>
              <div style={{width:44,height:44,background:"linear-gradient(135deg,#4f7cff,#2855dd)",borderRadius:12,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",flexShrink:0}}>
                <div style={{fontSize:15}}>{step.icon}</div>
                <div style={{fontFamily:FFM,fontSize:9,color:"#fff",fontWeight:700}}>{step.num}</div>
              </div>
              <div style={{flex:1,minWidth:0}}>
                <div style={{fontFamily:FFD,fontSize:15,fontWeight:700,color:"var(--text)",marginBottom:6}}>{step.title}</div>
                <p style={{color:"var(--muted)",fontSize:13,lineHeight:1.7,marginBottom:12}}>{step.desc}</p>
                <div style={{position:"relative"}}>
                  <pre style={{background:"var(--bg,#060c18)",border:"1px solid var(--b2)",borderRadius:10,padding:"14px 16px",fontSize:12,color:"var(--sub)",fontFamily:FFM,overflow:"auto",lineHeight:1.7,margin:0}}>{step.code}</pre>
                  <button onClick={()=>copy(step.code,i)} style={{position:"absolute",top:8,right:8,background:copied===i?"#0fba8133":"var(--s2)",border:"1px solid var(--b2)",borderRadius:7,color:copied===i?"var(--green)":"var(--muted)",padding:"4px 10px",cursor:"pointer",fontSize:11,fontWeight:700}}>
                    {copied===i?"✓ Copied":"Copy"}
                  </button>
                </div>
              </div>
            </div>
          </Card>
        ))}
      </div>
      <Card style={{marginTop:16,borderColor:"var(--acc)44",background:"var(--glow)"}}>
        <div style={{display:"flex",gap:14,alignItems:"center",flexWrap:"wrap"}}>
          <span style={{fontSize:36}}>🚀</span>
          <div style={{flex:1}}>
            <div style={{fontFamily:FFD,fontSize:15,fontWeight:700,color:"var(--text)",marginBottom:4}}>Total setup time: ~1 hour</div>
            <div style={{fontSize:13,color:"var(--muted)",lineHeight:1.8}}>Free plan supports 50K reads/day · 20K writes/day · 1GB storage · 10GB hosting. More than enough to get started.</div>
          </div>
        </div>
      </Card>
    </div>
  );
}
/* ══════════════════════════════════════════════════════════════
   APPROVALS PAGE
══════════════════════════════════════════════════════════════ */
function ApprovalsPage({companies,setCompanies,users,setUsers,addToast}) {
  const pending = companies.filter(c=>c.status==="Pending");
  const [loading,setLoading]=useState(null);
  const [modal,setModal]=useState(null);

  const approve = async (company) => {
    setLoading(company.id);
    try {
      // The Firebase Auth account and user profile were already created at
      // registration time (see handleRegister) — the account exists but has
      // been gated out of the dashboard until now. Approving is just a
      // status flip; there is no password to create, store, or email.
      await updateCompany(company.id, { status: "Active" });
      setCompanies(p=>p.map(c=>c.id===company.id?{...c,status:"Active"}:c));

      // Bump their starting credits now that they're approved, if applicable
      if(company.ownerUid && company.plan==="credits"){
        try{ await saveUserProfile(company.ownerUid, { credits: 10 }); }catch(e){}
      }

      // Notify — no password included, they already set their own at registration
      await sendNotification({
        type: "welcome",
        email: company.email,
        subject: `Welcome to Meitho BGV — ${company.name}`,
        message: `Hi ${company.name},\n\nYour account has been approved! Log in at https://meitho.com with the email and password you registered with.\n\nWelcome to Meitho BGV — Beyond the Resume.\n\nTeam Meitho`,
      });

      addToast(`${company.name} approved! Welcome email sent.`, "success");
    } catch(e) {
      addToast("Approval failed: " + e.message, "error");
    }
    setLoading(null);
  };

  const [rejectModal,setRejectModal]=useState(null);
  const [rejectRemark,setRejectRemark]=useState("");

  const reject = async (company,remark) => {
    try {
      await updateCompany(company.id, { status: "Rejected", rejectRemark:remark||"" });
      setCompanies(p=>p.map(c=>c.id===company.id?{...c,status:"Rejected"}:c));
      // Send rejection email with remark
      try{
        await fetch(CF_SEND_CANDIDATE,{
          method:"POST",headers:{"Content-Type":"application/json"},
          body:JSON.stringify({
            to:company.email,
            subject:"Meitho BGV — Registration Update",
            html:`<div style="font-family:Arial,sans-serif;max-width:560px;margin:0 auto;">
              <div style="background:linear-gradient(135deg,#1a1a2e,#0a0a1a);padding:28px 32px;border-radius:12px 12px 0 0;text-align:center;">
                <div style="font-size:28px;font-weight:900;color:#fff;">Meitho</div>
                <div style="font-size:13px;color:rgba(255,255,255,0.5);margin-top:4px;">Background Verification Platform</div>
              </div>
              <div style="background:#0a0a1a;padding:32px;border-radius:0 0 12px 12px;">
                <h2 style="color:#fff;font-size:20px;margin-bottom:8px;">Registration Status Update</h2>
                <p style="color:rgba(255,255,255,0.6);margin-bottom:20px;">Hi ${company.pocName||company.name},</p>
                <p style="color:rgba(255,255,255,0.6);margin-bottom:20px;">Thank you for registering with Meitho BGV. After reviewing your application, we are unable to approve your account at this time.</p>
                ${remark?`<div style="background:rgba(255,49,49,0.08);border:1px solid rgba(255,49,49,0.2);border-radius:8px;padding:16px;margin-bottom:20px;">
                  <div style="font-size:11px;color:rgba(255,255,255,0.4);font-weight:700;text-transform:uppercase;margin-bottom:6px;">Reason</div>
                  <div style="color:rgba(255,255,255,0.7);font-size:13px;line-height:1.6;">${remark}</div>
                </div>`:""}
                <p style="color:rgba(255,255,255,0.5);font-size:13px;line-height:1.7;margin-bottom:20px;">If you believe this is an error or would like to provide additional information, please contact our support team.</p>
                <div style="text-align:center;">
                  <a href="mailto:help@meitho.com" style="background:rgba(12,192,223,0.1);color:#0cc0df;text-decoration:none;padding:12px 24px;border-radius:8px;font-weight:700;font-size:13px;display:inline-block;border:1px solid rgba(12,192,223,0.2);">Contact Support</a>
                </div>
              </div>
              <div style="text-align:center;padding:16px;font-size:11px;color:#64748b;">© 2026 Meitho BGV · meitho.com · help@meitho.com · +91 8300016265</div>
            </div>`,
            name:company.pocName||company.name,
          })
        });
      }catch(e){console.warn("Rejection email failed:",e);}
      addToast(`${company.name} rejected. Email sent.`, "amber");
    } catch(e) {
      addToast("Reject failed: " + e.message, "error");
    }
    setModal(null);setRejectModal(null);setRejectRemark("");
  };

  return (
    <div>
      {modal?.mode==="view"&&<Modal title={`Registration — ${modal.company.name}`} onClose={()=>setModal(null)} width={520}>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:16}}>
          {[["Company Name",modal.company.name],["Email",modal.company.email],["Phone",modal.company.phone],["Plan",modal.company.plan],["Registered On",modal.company.registeredOn],["Status",modal.company.status]].map(([l,v])=>(
            <div key={l} style={{background:"var(--s2)",borderRadius:10,padding:14}}>
              <div style={{fontSize:10,color:"var(--muted)",fontWeight:700,textTransform:"uppercase",letterSpacing:1,marginBottom:4}}>{l}</div>
              <div style={{fontSize:13,fontWeight:700,color:v?"var(--text)":"var(--muted)"}}>{v||"Not provided"}</div>
            </div>
          ))}
        </div>
        <div style={{display:"flex",gap:10,marginTop:8,flexDirection:"column"}}>
          <div style={{display:"flex",gap:10}}>
            <Btn style={{flex:1}} onClick={()=>{setModal(null);approve(modal.company);}}>{loading===modal.company.id?<Spin/>:"✓ Approve & Send Welcome Email"}</Btn>
            <button onClick={()=>setRejectModal(modal.company.id)} style={{flex:1,background:"rgba(255,49,49,0.08)",border:"1px solid rgba(255,49,49,0.3)",borderRadius:10,color:"#ff3131",padding:"11px",cursor:"pointer",fontSize:14,fontWeight:700,fontFamily:FF}}>✕ Reject</button>
          </div>
          {rejectModal===modal.company.id&&(
            <div style={{background:"rgba(255,49,49,0.06)",border:"1px solid rgba(255,49,49,0.2)",borderRadius:10,padding:14}}>
              <div style={{fontSize:11,color:"#ff3131",fontWeight:700,marginBottom:8}}>Rejection Reason *</div>
              <textarea value={rejectRemark} onChange={e=>setRejectRemark(e.target.value)}
                placeholder="e.g. Incomplete information, Invalid GST, Duplicate registration..."
                style={{width:"100%",padding:"10px 12px",background:"var(--ibg)",border:"1px solid rgba(255,49,49,0.3)",borderRadius:8,color:"var(--text)",fontSize:13,fontFamily:FF,resize:"vertical",minHeight:70,outline:"none",boxSizing:"border-box",marginBottom:10}}/>
              <div style={{display:"flex",gap:8}}>
                <button onClick={()=>reject(modal.company,rejectRemark)} style={{background:"#ff3131",border:"none",borderRadius:8,color:"#fff",padding:"9px 18px",cursor:"pointer",fontSize:13,fontWeight:700,fontFamily:FF}}>Confirm Rejection & Send Email</button>
                <BtnGhost onClick={()=>{setRejectModal(null);setRejectRemark("");}}>Cancel</BtnGhost>
              </div>
            </div>
          )}
        </div>
      </Modal>}

      <div className="fu" style={{marginBottom:24}}>
        <h2 style={{fontFamily:FFD,fontWeight:700,fontSize:26,color:"var(--text)",marginBottom:4}}>Company Approvals</h2>
        <p style={{color:"var(--muted)",fontSize:14}}>{pending.length} pending approval{pending.length!==1?"s":""}</p>
      </div>

      {/* Stats row */}
      <div style={{display:"flex",gap:12,marginBottom:24,flexWrap:"wrap"}}>
        {[
          {label:"Pending",value:companies.filter(c=>c.status==="Pending").length,color:"var(--amber)"},
          {label:"Approved",value:companies.filter(c=>c.status==="Active").length,color:"var(--green)"},
          {label:"Rejected",value:companies.filter(c=>c.status==="Rejected").length,color:"var(--red)"},
        ].map(s=>(
          <div key={s.label} style={{background:"var(--s1)",border:`1px solid ${s.color}44`,borderRadius:12,padding:"12px 20px",display:"flex",gap:12,alignItems:"center"}}>
            <div style={{fontSize:22,fontWeight:700,color:s.color,fontFamily:FFD}}>{s.value}</div>
            <div style={{fontSize:12,color:"var(--muted)",fontWeight:600}}>{s.label}</div>
          </div>
        ))}
      </div>

      {/* PENDING */}
      <div style={{marginBottom:24}}>
        <h3 style={{fontFamily:FFD,fontSize:13,fontWeight:700,color:"var(--amber)",marginBottom:12,letterSpacing:.8,textTransform:"uppercase"}}>⏳ Pending ({pending.length})</h3>
        {pending.length===0?(
          <Card style={{textAlign:"center",padding:40}}>
            <div style={{fontSize:40,marginBottom:10}}>✅</div>
            <div style={{fontFamily:FFD,fontSize:16,fontWeight:700,color:"var(--sub)"}}>All caught up! No pending registrations.</div>
          </Card>
        ):(
          <div style={{display:"flex",flexDirection:"column",gap:12}}>
            {pending.map((c,i)=>(
              <Card key={i} style={{padding:20,borderLeft:"3px solid var(--amber)"}}>
                <div style={{display:"flex",gap:16,alignItems:"center",flexWrap:"wrap"}}>
                  <div style={{width:46,height:46,background:"linear-gradient(135deg,var(--amber)44,var(--amber)22)",border:"1px solid var(--amber)44",borderRadius:13,display:"flex",alignItems:"center",justifyContent:"center",fontSize:20,fontFamily:FFD,fontWeight:700,color:"var(--amber)",flexShrink:0}}>{c.name[0]}</div>
                  <div style={{flex:1,minWidth:160}}>
                    <div style={{fontSize:16,fontWeight:700,color:"var(--text)",fontFamily:FFD,marginBottom:4}}>{c.name}</div>
                    <div style={{fontSize:12,color:"var(--muted)",marginBottom:4}}>{c.email} {c.phone&&`· ${c.phone}`}</div>
                    <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
                      <Badge color="var(--amber)">⏳ Pending</Badge>
                      <Badge color={PLAN_COLOR[c.plan]||"var(--brand)"} style={{fontSize:10}}>{c.plan}</Badge>
                      <span style={{fontSize:11,color:"var(--muted)"}}>Registered: {c.registeredOn}</span>
                    </div>
                  </div>
                  <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
                    <Btn style={{fontSize:12,padding:"8px 16px"}} onClick={()=>setModal({mode:"view",company:c})}>View Details</Btn>
                    <Btn style={{fontSize:12,padding:"8px 16px"}} onClick={()=>approve(c)}>{loading===c.id?<Spin/>:"✓ Approve"}</Btn>
                    <button onClick={()=>reject(c)} style={{background:"var(--red)15",border:"1px solid var(--red)44",borderRadius:8,color:"var(--red)",padding:"8px 16px",cursor:"pointer",fontSize:12,fontWeight:700,fontFamily:FF}}>✕ Reject</button>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* APPROVED */}
      {companies.filter(c=>c.status==="Active"&&c.registeredOn).length>0&&(
        <div style={{marginBottom:24}}>
          <h3 style={{fontFamily:FFD,fontSize:13,fontWeight:700,color:"var(--green)",marginBottom:12,letterSpacing:.8,textTransform:"uppercase"}}>✅ Approved ({companies.filter(c=>c.status==="Active"&&c.registeredOn).length})</h3>
          <div style={{display:"flex",flexDirection:"column",gap:10}}>
            {companies.filter(c=>c.status==="Active"&&c.registeredOn).map((c,i)=>(
              <Card key={i} style={{padding:16,borderLeft:"3px solid var(--green)"}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",flexWrap:"wrap",gap:8}}>
                  <div style={{display:"flex",gap:12,alignItems:"center"}}>
                    <div style={{width:38,height:38,background:"var(--green)22",border:"1px solid var(--green)44",borderRadius:10,display:"flex",alignItems:"center",justifyContent:"center",fontSize:16,fontWeight:700,color:"var(--green)"}}>{c.name[0]}</div>
                    <div>
                      <div style={{fontSize:14,fontWeight:700,color:"var(--text)"}}>{c.name}</div>
                      <div style={{fontSize:12,color:"var(--muted)"}}>{c.email} · Registered: {c.registeredOn}</div>
                    </div>
                  </div>
                  <div style={{display:"flex",gap:8,alignItems:"center"}}>
                    <Badge color="var(--green)">✅ Approved</Badge>
                    <Badge color={PLAN_COLOR[c.plan]||"var(--brand)"} style={{fontSize:10}}>{c.plan}</Badge>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* REJECTED */}
      {companies.filter(c=>c.status==="Rejected").length>0&&(
        <div style={{marginBottom:24}}>
          <h3 style={{fontFamily:FFD,fontSize:13,fontWeight:700,color:"var(--red)",marginBottom:12,letterSpacing:.8,textTransform:"uppercase"}}>✕ Rejected ({companies.filter(c=>c.status==="Rejected").length})</h3>
          <div style={{display:"flex",flexDirection:"column",gap:10}}>
            {companies.filter(c=>c.status==="Rejected").map((c,i)=>(
              <Card key={i} style={{padding:16,borderLeft:"3px solid var(--red)",opacity:.8}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",flexWrap:"wrap",gap:8}}>
                  <div style={{display:"flex",gap:12,alignItems:"center"}}>
                    <div style={{width:38,height:38,background:"var(--red)22",border:"1px solid var(--red)44",borderRadius:10,display:"flex",alignItems:"center",justifyContent:"center",fontSize:16,fontWeight:700,color:"var(--red)"}}>{c.name[0]}</div>
                    <div>
                      <div style={{fontSize:14,fontWeight:700,color:"var(--text)"}}>{c.name}</div>
                      <div style={{fontSize:12,color:"var(--muted)"}}>{c.email} · Registered: {c.registeredOn}</div>
                    </div>
                  </div>
                  <div style={{display:"flex",gap:8,alignItems:"center"}}>
                    <Badge color="var(--red)">✕ Rejected</Badge>
                    <Btn style={{fontSize:11,padding:"6px 12px"}} onClick={async()=>{
                      await updateCompany(c.id,{status:"Pending"});
                      setCompanies(p=>p.map(co=>co.id===c.id?{...co,status:"Pending"}:co));
                      addToast(`${c.name} moved back to pending.`,"success");
                    }}>↩ Restore</Btn>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════
   ADMIN USERS PAGE (with permissions)
══════════════════════════════════════════════════════════════ */
const MODULES = ["Dashboard","Companies","HR Users","Employee DB","Analytics","Plans","Integrations","Approvals"];
const PERMS = ["View","Create","Edit"];

function AdminUsersPage({users,setUsers,addToast}) {
  // Platform admins only — company admins (people promoted to manage their
  // own company via role:"admin" + companyId) must not appear here. They're
  // distinguished by NOT having a companyId, since platform admin accounts
  // aren't tied to any single company.
  const adminUsers = users.filter(u=>(u.role==="admin"&&!u.companyId)||u.role==="superadmin");
  const [modal,setModal]=useState(null);
  const [loading,setLoading]=useState(false);
  const blankAdmin={name:"",email:"",password:"",phone:"",designation:"",permissions:{}};
  const [form,setForm]=useState(blankAdmin);
  const [errs,setErrs]=useState({});

  const defaultPerms=()=>{
    const p={};
    MODULES.forEach(m=>{p[m]={View:true,Create:false,Edit:false};});
    return p;
  };

  const togglePerm=(mod,perm)=>{
    setForm(f=>({...f,permissions:{...f.permissions,[mod]:{...f.permissions[mod],[perm]:!f.permissions[mod]?.[perm]}}}));
  };

  const validate=()=>{
    const e={};
    if(!form.name.trim())e.name="Required";
    if(!form.email.trim())e.email="Required";
    if(!form.password||form.password.length<6)e.password="Min 6 characters";
    if(!form.designation.trim())e.designation="Required";
    return e;
  };

  const doAdd=async()=>{
    const e=validate();if(Object.keys(e).length){setErrs(e);return;}
    setLoading(true);
    try{
      const {initializeApp,getApps}=await import("firebase/app");
      const {getAuth,createUserWithEmailAndPassword,signOut}=await import("firebase/auth");
      // Same isolated secondary-app pattern used for HR user creation —
      // createUserWithEmailAndPassword auto-signs-in as the new user on
      // whatever auth instance it runs on, which would otherwise silently
      // swap the current Super Admin's own session over to the new account.
      const firebaseConfig={
        apiKey: "AIzaSyBAwGsTIxPxh-s4UwqJiIXge8Dc-zt6ivI",
        authDomain: "meitho.firebaseapp.com",
        projectId: "meitho",
        storageBucket: "meitho.firebasestorage.app",
      };
      const secondaryApp=getApps().find(a=>a.name==="adminCreate")||initializeApp(firebaseConfig,"adminCreate");
      const secondaryAuth=getAuth(secondaryApp);
      const cred=await createUserWithEmailAndPassword(secondaryAuth,form.email,form.password);
      await signOut(secondaryAuth);

      const newUser={
        id:cred.user.uid,
        name:form.name,email:form.email,phone:form.phone||"",designation:form.designation,
        role:"admin", // platform admin — companyId absent is what distinguishes this from a company admin (see AdminUsersPage filter)
        company:"Meitho BGV",
        permissions:Object.keys(form.permissions).length?form.permissions:defaultPerms(),
        suspended:false,
        createdOn:todayStr(),
      };
      const {saveUserProfile}=await import("./firebase/services");
      await saveUserProfile(cred.user.uid,newUser);
      setUsers(p=>[...p,newUser]);
      addToast(`Admin user ${newUser.name} created`,"success");
      setModal(null);setForm(blankAdmin);setErrs({});
    }catch(e){
      if(e.code==="auth/email-already-in-use")addToast("That email is already registered","error");
      else addToast("Failed: "+e.message,"error");
    }
    setLoading(false);
  };

  const toggleSuspend=async(id)=>{
    const u=users.find(u=>u.id===id);
    if(!u)return;
    try{
      await saveUserProfile(id,{suspended:!u.suspended});
      setUsers(p=>p.map(x=>x.id===id?{...x,suspended:!x.suspended}:x));
      addToast(`${u.name} ${u.suspended?"activated":"suspended"}.`,u.suspended?"success":"amber");
    }catch(e){addToast("Failed to update: "+e.message,"error");}
  };

  return (
    <div>
      {modal?.mode==="add"&&<Modal title="Create Admin User" onClose={()=>{setModal(null);setForm(blankAdmin);setErrs({});}} width={640}>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:14,marginBottom:16}}>
          <div><Label>Full Name *</Label><Inp value={form.name} err={errs.name} onChange={e=>setForm(p=>({...p,name:e.target.value}))}/><ErrMsg msg={errs.name}/></div>
          <div><Label>Email *</Label><Inp value={form.email} err={errs.email} onChange={e=>setForm(p=>({...p,email:e.target.value}))}/><ErrMsg msg={errs.email}/></div>
          <div><Label>Password *</Label><Inp type="password" value={form.password} err={errs.password} onChange={e=>setForm(p=>({...p,password:e.target.value}))} placeholder="Min 6 characters"/><ErrMsg msg={errs.password}/></div>
          <div><Label>Phone</Label><Inp value={form.phone} onChange={e=>setForm(p=>({...p,phone:e.target.value}))} placeholder="9876543210"/></div>
          <div style={{gridColumn:"1/-1"}}><Label>Designation *</Label><Inp value={form.designation} err={errs.designation} onChange={e=>setForm(p=>({...p,designation:e.target.value}))} placeholder="Operations Manager"/><ErrMsg msg={errs.designation}/></div>
        </div>

        {/* Permissions matrix */}
        <div style={{borderTop:"1px solid var(--b1)",paddingTop:16,marginBottom:16}}>
          <div style={{fontFamily:FFD,fontSize:12,fontWeight:700,color:"var(--brand)",letterSpacing:.8,marginBottom:14,textTransform:"uppercase"}}>Access Permissions</div>
          <div style={{overflowX:"auto"}}>
            <table style={{width:"100%",borderCollapse:"collapse",fontSize:13}}>
              <thead>
                <tr style={{borderBottom:"1px solid var(--b1)"}}>
                  <th style={{textAlign:"left",padding:"0 0 10px",fontSize:11,fontWeight:700,color:"var(--muted)",width:"40%"}}>MODULE</th>
                  {PERMS.map(p=><th key={p} style={{textAlign:"center",padding:"0 0 10px",fontSize:11,fontWeight:700,color:"var(--muted)",width:"20%"}}>{p.toUpperCase()}</th>)}
                </tr>
              </thead>
              <tbody>
                {MODULES.map(mod=>{
                  const perms=form.permissions[mod]||(Object.keys(form.permissions).length===0?{View:true,Create:false,Edit:false}:{View:false,Create:false,Edit:false});
                  return(
                    <tr key={mod} style={{borderBottom:"1px solid var(--b1)22"}}>
                      <td style={{padding:"10px 0",fontWeight:600,color:"var(--text)"}}>{mod}</td>
                      {PERMS.map(perm=>(
                        <td key={perm} style={{padding:"10px 0",textAlign:"center"}}>
                          <div onClick={()=>togglePerm(mod,perm)} style={{width:20,height:20,borderRadius:4,border:`2px solid ${perms[perm]?"var(--brand)":"var(--b2)"}`,background:perms[perm]?"var(--brand)":"transparent",cursor:"pointer",display:"inline-flex",alignItems:"center",justifyContent:"center",color:"#fff",fontSize:12}}>
                            {perms[perm]&&"✓"}
                          </div>
                        </td>
                      ))}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div style={{display:"flex",gap:8,marginTop:10}}>
            <button onClick={()=>{const p=defaultPerms();Object.keys(p).forEach(m=>{PERMS.forEach(pr=>{p[m][pr]=true;});});setForm(f=>({...f,permissions:p}));}} style={{background:"var(--green)15",border:"1px solid var(--green)44",borderRadius:8,color:"var(--green)",padding:"6px 14px",cursor:"pointer",fontSize:12,fontWeight:700,fontFamily:FF}}>✓ Grant All</button>
            <button onClick={()=>setForm(f=>({...f,permissions:defaultPerms()}))} style={{background:"var(--s2)",border:"1px solid var(--b1)",borderRadius:8,color:"var(--muted)",padding:"6px 14px",cursor:"pointer",fontSize:12,fontWeight:700,fontFamily:FF}}>↩ Reset Default</button>
          </div>
        </div>

        <div style={{display:"flex",gap:12}}>
          <Btn onClick={doAdd}>{loading?<Spin/>:"Create Admin User →"}</Btn>
          <BtnGhost onClick={()=>{setModal(null);setForm(blankAdmin);setErrs({});}}>Cancel</BtnGhost>
        </div>
      </Modal>}

      {modal?.mode==="perms"&&<Modal title={`Permissions — ${modal.user.name}`} onClose={()=>setModal(null)} width={560}>
        <div style={{overflowX:"auto",marginBottom:16}}>
          <table style={{width:"100%",borderCollapse:"collapse",fontSize:13}}>
            <thead>
              <tr style={{borderBottom:"1px solid var(--b1)"}}>
                <th style={{textAlign:"left",padding:"0 0 10px",fontSize:11,fontWeight:700,color:"var(--muted)",width:"40%"}}>MODULE</th>
                {PERMS.map(p=><th key={p} style={{textAlign:"center",padding:"0 0 10px",fontSize:11,fontWeight:700,color:"var(--muted)"}}>{p.toUpperCase()}</th>)}
              </tr>
            </thead>
            <tbody>
              {MODULES.map(mod=>{
                const perms=modal.user.permissions?.[mod]||{View:false,Create:false,Edit:false};
                return(
                  <tr key={mod} style={{borderBottom:"1px solid var(--b1)22"}}>
                    <td style={{padding:"10px 0",fontWeight:600,color:"var(--text)"}}>{mod}</td>
                    {PERMS.map(perm=>(
                      <td key={perm} style={{padding:"10px 0",textAlign:"center"}}>
                        <div onClick={()=>{
                          setUsers(p=>p.map(u=>u.id===modal.user.id?{...u,permissions:{...u.permissions,[mod]:{...u.permissions?.[mod],[perm]:!perms[perm]}}}:u));
                          setModal(m=>({...m,user:{...m.user,permissions:{...m.user.permissions,[mod]:{...perms,[perm]:!perms[perm]}}}}));
                        }} style={{width:20,height:20,borderRadius:4,border:`2px solid ${perms[perm]?"var(--brand)":"var(--b2)"}`,background:perms[perm]?"var(--brand)":"transparent",cursor:"pointer",display:"inline-flex",alignItems:"center",justifyContent:"center",color:"#fff",fontSize:12}}>
                          {perms[perm]&&"✓"}
                        </div>
                      </td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <Btn onClick={()=>{addToast("Permissions updated!","success");setModal(null);}}>Save Permissions →</Btn>
      </Modal>}

      <div className="fu" style={{display:"flex",justifyContent:"space-between",alignItems:"flex-end",marginBottom:24,flexWrap:"wrap",gap:12}}>
        <div>
          <h2 style={{fontFamily:FFD,fontWeight:700,fontSize:26,color:"var(--text)",marginBottom:4}}>Admin Users</h2>
          <p style={{color:"var(--muted)",fontSize:14}}>Manage admin accounts and their access permissions</p>
        </div>
        <Btn onClick={()=>{setForm({...blankAdmin,permissions:defaultPerms()});setModal({mode:"add"});}}>+ Create Admin User</Btn>
      </div>

      <div style={{display:"flex",flexDirection:"column",gap:12}}>
        {adminUsers.map((u,i)=>(
          <Card key={i} style={{padding:20}}>
            <div style={{display:"flex",gap:14,alignItems:"center",flexWrap:"wrap"}}>
              <div style={{width:46,height:46,background:"linear-gradient(135deg,var(--purple)44,var(--purple)22)",border:"1px solid var(--purple)44",borderRadius:13,display:"flex",alignItems:"center",justifyContent:"center",fontSize:18,fontFamily:FFD,fontWeight:700,color:"var(--purple)",flexShrink:0}}>{(u.name||"A")[0]}</div>
              <div style={{flex:1,minWidth:160}}>
                <div style={{display:"flex",alignItems:"center",gap:8,flexWrap:"wrap",marginBottom:3}}>
                  <div style={{fontSize:15,fontWeight:700,color:"var(--text)",fontFamily:FFD}}>{u.name}</div>
                  <Badge color={u.role==="superadmin"?"var(--amber)":"var(--purple)"} style={{fontSize:10}}>{u.role==="superadmin"?"Super Admin":"Admin"}</Badge>
                  <Badge color={u.suspended?"var(--red)":"var(--green)"} style={{fontSize:10}}>{u.suspended?"Suspended":"Active"}</Badge>
                </div>
                <div style={{fontSize:12,color:"var(--muted)",marginBottom:3}}>{u.email} {u.phone&&`· ${u.phone}`}</div>
                {u.designation&&<div style={{fontSize:12,color:"var(--sub)"}}>{u.designation}</div>}
                {/* Permission summary */}
                {u.permissions&&(
                  <div style={{display:"flex",gap:6,marginTop:8,flexWrap:"wrap"}}>
                    {MODULES.filter(m=>u.permissions[m]?.View||u.permissions[m]?.Create||u.permissions[m]?.Edit).slice(0,5).map(m=>(
                      <span key={m} style={{fontSize:10,background:"var(--brand)15",color:"var(--brand)",border:"1px solid var(--acc)33",borderRadius:6,padding:"2px 7px"}}>
                        {m} {[u.permissions[m]?.View&&"V",u.permissions[m]?.Create&&"C",u.permissions[m]?.Edit&&"E"].filter(Boolean).join("/")}
                      </span>
                    ))}
                  </div>
                )}
              </div>
              <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                {u.role!=="superadmin"&&<>
                  <Btn variant="purple" style={{fontSize:11,padding:"7px 12px",background:"linear-gradient(135deg,#9b7aff,#7c5ae8)"}} onClick={()=>setModal({mode:"perms",user:u})}>⚙ Permissions</Btn>
                  <button onClick={()=>toggleSuspend(u.id)} style={{background:u.suspended?"var(--green)15":"var(--amber)15",border:`1px solid ${u.suspended?"var(--green)":"var(--amber)"}44`,borderRadius:8,color:u.suspended?"var(--green)":"var(--amber)",padding:"7px 12px",cursor:"pointer",fontSize:11,fontWeight:700,fontFamily:FF}}>{u.suspended?"Activate":"Suspend"}</button>
                </>}
                {u.role==="superadmin"&&<Badge color="var(--amber)">Full Access</Badge>}
              </div>
            </div>
          </Card>
        ))}
        {adminUsers.length===0&&<Card style={{textAlign:"center",padding:48}}>
          <div style={{fontSize:40,marginBottom:12}}>🔑</div>
          <div style={{fontFamily:FFD,fontSize:16,fontWeight:700,color:"var(--sub)"}}>No admin users yet</div>
          <div style={{fontSize:13,color:"var(--muted)",marginTop:6,marginBottom:20}}>Create admin users to delegate platform management</div>
          <Btn onClick={()=>{setForm({...blankAdmin,permissions:defaultPerms()});setModal({mode:"add"});}}>+ Create First Admin User</Btn>
        </Card>}
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════
   PLANS PAGE (Super Admin)
══════════════════════════════════════════════════════════════ */
/* ══════════════════════════════════════════════════════════════
   PLANS PAGE (Super Admin) — Subscription based
══════════════════════════════════════════════════════════════ */
/* ══════════════════════════════════════════════════════════════
   BULK CREDIT PRICING (configurable, used in PlansPage)
══════════════════════════════════════════════════════════════ */
const DEFAULT_BULK_PRICING = [
  {id:"single",label:"Single Search",credits:1,price:300,pricePerCredit:300,color:"#4f7cff",desc:"Pay per search, no commitment"},
  {id:"pack10",label:"10 Credits",credits:10,price:2500,pricePerCredit:250,color:"#9b7aff",desc:"Save ₹500 vs single"},
  {id:"pack50",label:"50 Credits",credits:50,price:10000,pricePerCredit:200,color:"#0fba81",desc:"Save ₹5,000 vs single"},
  {id:"pack100",label:"100 Credits",credits:100,price:18000,pricePerCredit:180,color:"#f5a623",desc:"Save ₹12,000 vs single",popular:true},
];

function BulkCreditPricing() {
  const [pricing,setPricing]=useState(()=>{
    try{return JSON.parse(localStorage.getItem("meitho_bulk_pricing"))||DEFAULT_BULK_PRICING;}
    catch{return DEFAULT_BULK_PRICING;}
  });
  const [editing,setEditing]=useState(null);
  const [form,setForm]=useState({});

  const save=()=>{
    const updated=pricing.map(p=>p.id===editing.id?{...p,...form,price:parseInt(form.price)||0,credits:parseInt(form.credits)||0,pricePerCredit:Math.round((parseInt(form.price)||0)/(parseInt(form.credits)||1))}:p);
    setPricing(updated);
    localStorage.setItem("meitho_bulk_pricing",JSON.stringify(updated));
    setEditing(null);
    addToastGlobal?.("Bulk pricing updated!","success");
  };

  return(
    <div style={{marginTop:28}}>
      {editing&&<Modal title={"Edit — "+editing.label} onClose={()=>setEditing(null)} width={420}>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:14}}>
          <div><Label>Label</Label><Inp value={form.label||""} onChange={e=>setForm(p=>({...p,label:e.target.value}))}/></div>
          <div><Label>Credits</Label><Inp type="number" value={form.credits||""} onChange={e=>setForm(p=>({...p,credits:e.target.value}))}/></div>
          <div><Label>Price (₹)</Label><Inp type="number" value={form.price||""} onChange={e=>setForm(p=>({...p,price:e.target.value}))}/></div>
          <div><Label>Color</Label><Inp value={form.color||""} onChange={e=>setForm(p=>({...p,color:e.target.value}))}/></div>
          <div style={{gridColumn:"span 2"}}><Label>Description</Label><Inp value={form.desc||""} onChange={e=>setForm(p=>({...p,desc:e.target.value}))}/></div>
        </div>
        {form.price&&form.credits&&<div style={{background:"var(--brand)10",borderRadius:8,padding:"8px 12px",fontSize:12,color:"var(--muted)",marginBottom:14}}>
          Price per credit: <strong style={{color:"var(--brand)"}}>₹{Math.round(parseInt(form.price)/parseInt(form.credits))}</strong>
        </div>}
        <div style={{display:"flex",gap:10}}><Btn onClick={save}>Save →</Btn><BtnGhost onClick={()=>setEditing(null)}>Cancel</BtnGhost></div>
      </Modal>}

      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14}}>
        <div>
          <div style={{fontFamily:FFD,fontSize:15,fontWeight:700,color:"var(--text)",marginBottom:2}}>Bulk Verification Credit Packages</div>
          <div style={{fontSize:12,color:"var(--muted)"}}>Used when companies buy credits for bulk HRMS verification</div>
        </div>
      </div>
      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(200px,1fr))",gap:12}}>
        {pricing.map(p=>(
          <div key={p.id} style={{background:"var(--s2)",border:"1px solid var(--b1)",borderRadius:12,padding:"16px",position:"relative",borderTop:"3px solid "+p.color}}>
            {p.popular&&<div style={{position:"absolute",top:-1,right:12,background:p.color,color:"#fff",fontSize:9,fontWeight:700,padding:"2px 8px",borderRadius:"0 0 6px 6px"}}>POPULAR</div>}
            <div style={{fontFamily:FFD,fontSize:14,fontWeight:700,color:"var(--text)",marginBottom:4}}>{p.label}</div>
            <div style={{fontSize:22,fontWeight:800,color:p.color,fontFamily:FFD,marginBottom:2}}>₹{p.price.toLocaleString("en-IN")}</div>
            <div style={{fontSize:11,color:"var(--muted)",marginBottom:8}}>{p.credits} credits · ₹{p.pricePerCredit}/credit</div>
            <div style={{fontSize:11,color:"var(--muted)",marginBottom:10}}>{p.desc}</div>
            <button onClick={()=>{setEditing(p);setForm({...p});}} style={{width:"100%",background:"transparent",border:"1px solid var(--b2)",borderRadius:7,color:"var(--muted)",padding:"5px 0",cursor:"pointer",fontSize:11,fontWeight:600,fontFamily:FF,transition:"all .15s"}} onMouseEnter={e=>{e.currentTarget.style.borderColor=p.color;e.currentTarget.style.color=p.color;}} onMouseLeave={e=>{e.currentTarget.style.borderColor="var(--b2)";e.currentTarget.style.color="var(--muted)";}}>Edit</button>
          </div>
        ))}
      </div>
    </div>
  );
}

const DEFAULT_PLANS = [
  {id:"starter",name:"Starter",duration:"monthly",durationLabel:"Monthly",searches:10,price:2999,walletCredit:2999,platformFee:2999,color:"#4f7cff",popular:false,active:true,desc:"Perfect for small teams",features:"10 searches/month,UAN + PAN + Aadhaar,Employment history,Search history,Email support,₹2999 wallet credit"},
  {id:"growth",name:"Growth",duration:"quarterly",durationLabel:"Quarterly",searches:30,price:7999,walletCredit:7999,platformFee:2999,color:"#9b7aff",popular:true,active:true,desc:"Best for growing teams",features:"30 searches/month,UAN + PAN + Aadhaar,Employment history,Reference check,Priority support,Analytics,₹7999 wallet credit"},
  {id:"pro",name:"Pro",duration:"halfyearly",durationLabel:"Half Yearly",searches:75,price:17999,walletCredit:17999,platformFee:2999,color:"#0fba81",popular:false,active:true,desc:"For established companies",features:"75 searches/month,UAN + PAN + Aadhaar,Full BGV reports,Bulk operations,HRMS integration,Priority support,₹17999 wallet credit"},
  {id:"enterprise",name:"Enterprise",duration:"yearly",durationLabel:"Yearly",searches:200,price:39999,walletCredit:39999,platformFee:2999,color:"#f5a623",popular:false,active:true,desc:"Unlimited scale",features:"200 searches/month,All verification types,Dedicated manager,Custom integrations,SLA guarantee,White-label options,₹39999 wallet credit"},
];

function PlansPage({companies,setCompanies,users,setUsers,addToast,setPage}) {
  const [plans,setPlans]=useState(()=>{
    try{return JSON.parse(localStorage.getItem("meitho_plans_v3"))||DEFAULT_PLANS;}catch{return DEFAULT_PLANS;}
  });
  const [modal,setModal]=useState(null);
  const [form,setForm]=useState({});
  const [loading,setLoading]=useState(false);

  const savePlans=(updated)=>{
    setPlans(updated);
    localStorage.setItem("meitho_plans_v3",JSON.stringify(updated));
  };

  const savePlan=()=>{
    if(!form.name||!form.price){addToast("Name and price required.","error");return;}
    setLoading(true);
    const updated=plans.map(p=>p.id===modal.plan.id?{...p,...form,price:parseInt(form.price)||0,platformFee:parseInt(form.platformFee)||5000,searches:parseInt(form.searches)||20}:p);
    savePlans(updated);
    addToast(`${form.name} plan updated!`,"success");
    setModal(null);setLoading(false);
  };

  const addPlan=()=>{
    if(!form.name||!form.price){addToast("Name and price required.","error");return;}
    setLoading(true);
    const newPlan={...form,id:"plan_"+Date.now(),active:true,price:parseInt(form.price)||0,platformFee:parseInt(form.platformFee)||5000,searches:parseInt(form.searches)||20,popular:false};
    savePlans([...plans,newPlan]);
    addToast(`${newPlan.name} created!`,"success");
    setModal(null);setForm({});setLoading(false);
  };

  const togglePlan=(id)=>{
    const updated=plans.map(p=>p.id===id?{...p,active:!p.active}:p);
    savePlans(updated);
    const pl=updated.find(p=>p.id===id);
    addToast(`${pl.name} ${pl.active?"activated":"deactivated"}.`,pl.active?"success":"amber");
  };

  const PlanForm=({onSave,onCancel,submitLabel})=>(
    <div>
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:14,marginBottom:14}}>
        <div><Label>Plan Name *</Label><Inp value={form.name||""} onChange={e=>setForm(p=>({...p,name:e.target.value}))} placeholder="Growth"/></div>
        <div><Label>Duration Label</Label><Inp value={form.durationLabel||""} onChange={e=>setForm(p=>({...p,durationLabel:e.target.value}))} placeholder="3 Months"/></div>
        <div><Label>Total Price (₹) *</Label><Inp type="number" value={form.price||""} onChange={e=>setForm(p=>({...p,price:e.target.value}))} placeholder="15000"/></div>
        <div><Label>Platform Fee (₹)</Label><Inp type="number" value={form.platformFee||5000} onChange={e=>setForm(p=>({...p,platformFee:e.target.value}))} placeholder="5000"/></div>
        <div><Label>Searches / Month</Label><Inp type="number" value={form.searches||""} onChange={e=>setForm(p=>({...p,searches:e.target.value}))} placeholder="30"/></div>
        <div><Label>Color (hex)</Label><Inp value={form.color||""} onChange={e=>setForm(p=>({...p,color:e.target.value}))} placeholder="#4f7cff"/></div>
        <div style={{gridColumn:"1/-1"}}><Label>Description</Label><Inp value={form.desc||""} onChange={e=>setForm(p=>({...p,desc:e.target.value}))} placeholder="Best for growing teams"/></div>
        <div style={{gridColumn:"1/-1"}}>
          <Label>Features (comma separated)</Label>
          <textarea value={form.features||""} onChange={e=>setForm(p=>({...p,features:e.target.value}))} placeholder="Feature 1, Feature 2" style={{width:"100%",background:"var(--ibg)",border:"1px solid var(--b1)",borderRadius:10,padding:"11px 14px",color:"var(--text)",fontSize:14,fontFamily:FF,outline:"none",minHeight:80,resize:"vertical"}}/>
        </div>
      </div>
      <div style={{background:"var(--amber)15",border:"1px solid var(--amber)44",borderRadius:10,padding:"10px 14px",fontSize:12,color:"var(--amber)",marginBottom:16}}>
        ⚠ Platform fee ₹{form.platformFee||5000} is a one-time onboarding fee charged on top of plan price.
      </div>
      <div style={{display:"flex",gap:12}}><Btn onClick={onSave}>{loading?<Spin/>:submitLabel}</Btn><BtnGhost onClick={onCancel}>Cancel</BtnGhost></div>
    </div>
  );

  return(
    <div>
      {modal?.mode==="edit"&&<Modal title={`Edit Plan — ${modal.plan.name}`} onClose={()=>setModal(null)} width={560}>
        <PlanForm onSave={savePlan} onCancel={()=>setModal(null)} submitLabel="Save Changes →"/>
      </Modal>}
      {modal?.mode==="add"&&<Modal title="Create New Plan" onClose={()=>{setModal(null);setForm({});}} width={560}>
        <PlanForm onSave={addPlan} onCancel={()=>{setModal(null);setForm({});}} submitLabel="Create Plan →"/>
      </Modal>}

      <div className="fu" style={{display:"flex",justifyContent:"space-between",alignItems:"flex-end",marginBottom:20,flexWrap:"wrap",gap:12}}>
        <div>
          <h2 style={{fontFamily:FFD,fontWeight:700,fontSize:26,color:"var(--text)",marginBottom:4}}>Subscription Plans</h2>
          <p style={{color:"var(--muted)",fontSize:14}}>₹2,999 onboarding fee · Wallet-based pay per search</p>
        </div>
        <div style={{display:"flex",gap:8}}>
          <BtnGhost onClick={()=>setPage&&setPage("walletadmin")} style={{fontSize:13}}>💰 Wallet Management →</BtnGhost>
          <Btn onClick={()=>{setForm({platformFee:2999,searches:10,color:"#4f7cff",walletCredit:2999});setModal({mode:"add"});}}>+ New Plan</Btn>
        </div>
      </div>

      {/* How it works */}
      <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:12,marginBottom:20}} className="mg1">
        {[
          {icon:"💳",title:"₹2,999 Onboarding Fee",desc:"One-time platform setup fee for every company. Covers KYC access & setup."},
          {icon:"💰",title:"Wallet Based System",desc:"Plan amount credited to company wallet. Pay per search — UAN ₹100, PAN ₹20, Aadhaar ₹20."},
          {icon:"🔄",title:"Top-Up Anytime",desc:"Companies can top-up wallet anytime. No expiry on wallet balance."},
        ].map((s,i)=>(
          <div key={i} style={{background:"var(--s2)",border:"1px solid var(--b1)",borderRadius:12,padding:"14px 16px"}}>
            <div style={{fontSize:24,marginBottom:8}}>{s.icon}</div>
            <div style={{fontSize:13,fontWeight:700,color:"var(--text)",marginBottom:4}}>{s.title}</div>
            <div style={{fontSize:11,color:"var(--muted)",lineHeight:1.6}}>{s.desc}</div>
          </div>
        ))}
      </div>

      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(260px,1fr))",gap:20}}>
        {plans.map((p,i)=>(
          <Card key={p.id} className={`fu${Math.min(i+1,4)}`} style={{borderTop:`3px solid ${p.color||"var(--brand)"}`,opacity:p.active?1:.55,position:"relative",overflow:"hidden"}}>
            {p.popular&&<div style={{position:"absolute",top:12,right:12,background:p.color||"var(--brand)",color:"#fff",fontSize:9,fontWeight:700,padding:"3px 8px",borderRadius:99,letterSpacing:.5}}>POPULAR</div>}
            <div style={{marginBottom:14}}>
              <div style={{fontFamily:FFD,fontSize:18,fontWeight:800,color:"var(--text)",marginBottom:2}}>{p.name}</div>
              <div style={{fontSize:12,color:"var(--muted)"}}>{p.durationLabel||"Monthly"}</div>
            </div>
            <div style={{marginBottom:12}}>
              <div style={{display:"flex",alignItems:"flex-end",gap:4,marginBottom:2}}>
                <span style={{fontFamily:FFD,fontSize:32,fontWeight:800,color:p.color||"var(--brand)"}}>{p.price?`₹${p.price.toLocaleString("en-IN")}`:"Custom"}</span>
              </div>
              <div style={{fontSize:11,color:"var(--green)",fontWeight:600}}>✓ ₹{(p.walletCredit||p.price||0).toLocaleString("en-IN")} wallet credit included</div>
              <div style={{fontSize:11,color:"var(--muted)",marginTop:2}}>+ ₹2,999 one-time onboarding fee</div>
            </div>
            <div style={{background:"var(--s2)",borderRadius:8,padding:"10px 14px",marginBottom:16,display:"flex",alignItems:"center",gap:8}}>
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><circle cx="8" cy="8" r="6" stroke={p.color||"var(--brand)"} strokeWidth="1.5"/><path d="M8 5v3.5l2 2" stroke={p.color||"var(--brand)"} strokeWidth="1.5" strokeLinecap="round"/></svg>
              <span style={{fontSize:13,fontWeight:700,color:"var(--text)"}}>{p.searches} searches</span>
              <span style={{fontSize:12,color:"var(--muted)"}}>/month</span>
            </div>
            {p.features&&p.features.split(",").map(f=>f.trim()).filter(Boolean).map(f=>(
              <div key={f} style={{display:"flex",gap:8,marginBottom:7,fontSize:12,color:"var(--sub)"}}>
                <span style={{color:p.color||"var(--brand)",fontWeight:700,flexShrink:0}}>✓</span>{f}
              </div>
            ))}
            <div style={{marginTop:16,paddingTop:14,borderTop:"1px solid var(--b1)",display:"flex",gap:8,justifyContent:"space-between",alignItems:"center"}}>
              <div style={{fontSize:11,color:"var(--muted)"}}>
                <strong style={{color:"var(--sub)"}}>{companies.filter(c=>c.plan===p.id).length}</strong> companies
              </div>
              <div style={{display:"flex",gap:6}}>
                <Btn style={{fontSize:11,padding:"6px 12px",background:`linear-gradient(135deg,${p.color||"var(--brand)"},${p.color||"var(--brand)"}cc)`}} onClick={()=>{setForm({...p});setModal({mode:"edit",plan:p});}}>Edit</Btn>
                <button onClick={()=>togglePlan(p.id)} style={{background:p.active?"var(--red)15":"var(--green)15",border:`1px solid ${p.active?"var(--red)":"var(--green)"}44`,borderRadius:8,color:p.active?"var(--red)":"var(--green)",padding:"6px 12px",cursor:"pointer",fontSize:11,fontWeight:700,fontFamily:FF}}>{p.active?"Off":"On"}</button>
              </div>
            </div>
          </Card>
        ))}
      </div>

      {/* Bulk Verification Credit Pricing */}
      <BulkCreditPricing/>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════
   DASHBOARD
══════════════════════════════════════════════════════════════ */
/* ══════════════════════════════════════════════════════════════
   HR DASHBOARD
══════════════════════════════════════════════════════════════ */
function HRDashboard({user,employees,history,setPage,company}) {
  const {dark}=useTheme();
  const myH=history.filter(h=>h.userId===user.id||h.companyId===user.companyId);
  const todayCount=myH.filter(h=>(h.timestamp||h.date||"").startsWith(todayStr())).length;
  const foundRate=myH.length?Math.round(myH.filter(h=>h.found).length/myH.length*100):0;

  // Quota
  const searchesUsed=user.searchesUsed||0;
  const searchesTotal=user.searchesAllocated||user.companySearches||0;
  const searchesLeft=Math.max(0,searchesTotal-searchesUsed);
  const quotaPct=searchesTotal>0?Math.min(100,Math.round(searchesUsed/searchesTotal*100)):0;
  const planName=user.planName||user.plan||"No Plan";

  // Days remaining
  const planExpiry=user.planExpiry?new Date(user.planExpiry):null;
  const daysLeft=planExpiry?Math.max(0,Math.ceil((planExpiry-new Date())/(1000*60*60*24))):null;

  const stats=[
    {label:"Searches Left",value:searchesLeft,total:searchesTotal,icon:<svg width="20" height="20" viewBox="0 0 20 20" fill="none"><circle cx="9" cy="9" r="6" stroke="currentColor" strokeWidth="1.5"/><path d="M14 14l4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>,color:"var(--brand)",grad:"card-gradient-blue"},
    {label:"Today's Searches",value:todayCount,icon:<svg width="20" height="20" viewBox="0 0 20 20" fill="none"><rect x="3" y="3" width="14" height="14" rx="3" stroke="currentColor" strokeWidth="1.5"/><path d="M7 10h6M10 7v6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>,color:"var(--green)",grad:"card-gradient-green"},
    {label:"Total Searches",value:myH.length,icon:<svg width="20" height="20" viewBox="0 0 20 20" fill="none"><path d="M3 4h14M3 8h14M3 12h10M3 16h6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>,color:"var(--purple)",grad:"card-gradient-purple"},
    {label:"Match Rate",value:foundRate+"%",icon:<svg width="20" height="20" viewBox="0 0 20 20" fill="none"><path d="M4 10l4 4 8-8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>,color:"var(--amber)",grad:"card-gradient-amber"},
  ];

  return(
    <div className={dark?"bg-mesh-dark":"bg-mesh-light"} style={{minHeight:"100%",padding:"4px 0"}}>
      {/* Header */}
      <div className="fu" style={{marginBottom:24}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",flexWrap:"wrap",gap:12}}>
          <div>
            <h2 style={{fontFamily:FFD,fontWeight:800,fontSize:28,color:"var(--text)",marginBottom:6,letterSpacing:-.5}}>
              Welcome back, <span className="grad-text">{(user.name||"").split(" ")[0]}</span> 👋
            </h2>
            <div style={{display:"flex",gap:8,flexWrap:"wrap",alignItems:"center"}}>
              <span style={{color:"var(--muted)",fontSize:14}}>{user.company}</span>
              {company?.verified&&<Badge color="var(--green)">✓ Verified</Badge>}
              <Badge color="var(--brand)" style={{fontSize:10}}>{planName}</Badge>
              <span style={{display:"flex",alignItems:"center",gap:4,fontSize:11,color:"var(--green)",fontWeight:600}}>
                <span style={{width:6,height:6,borderRadius:"50%",background:"#0cc0df",display:"inline-block",position:"relative"}}>
                  <span className="ping" style={{position:"absolute",inset:0,borderRadius:"50%",background:"#0cc0df"}}/>
                </span>
                Live
              </span>
            </div>
          </div>
          {daysLeft!==null&&(
            <div style={{background:"var(--s1)",border:"1px solid var(--b1)",borderRadius:12,padding:"10px 16px",textAlign:"center"}}>
              <div style={{fontSize:10,color:"var(--muted)",fontWeight:700,marginBottom:2}}>PLAN EXPIRES IN</div>
              <div style={{fontFamily:FFD,fontSize:24,fontWeight:800,color:daysLeft<10?"var(--red)":"var(--brand)"}}>{daysLeft}</div>
              <div style={{fontSize:11,color:"var(--muted)"}}>days</div>
            </div>
          )}
        </div>
      </div>

      {/* Stat cards */}
      <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:14,marginBottom:20}} className="mg1">
        {stats.map((s,i)=>(
          <div key={s.label} className={`fu${i} stat-card neon-hover glass`} style={{borderRadius:14,padding:20,border:"1px solid var(--b1)"}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:12}}>
              <div style={{width:40,height:40,borderRadius:10,background:s.color+"20",display:"flex",alignItems:"center",justifyContent:"center",color:s.color}}>{s.icon}</div>
              {s.total&&<div style={{fontSize:10,color:"var(--muted)",fontWeight:600}}>{s.total} total</div>}
            </div>
            <div className="tick" style={{fontFamily:FFD,fontSize:34,fontWeight:800,color:s.color,marginBottom:4,letterSpacing:-1}}>{s.value}</div>
            <div style={{fontSize:12,color:"var(--muted)",fontWeight:600}}>{s.label}</div>
          </div>
        ))}
      </div>

      {/* Quota bar */}
      {searchesTotal>0&&(
        <div className="fu1 glass neon-hover" style={{borderRadius:14,padding:18,marginBottom:20,border:"1px solid var(--b1)"}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12,flexWrap:"wrap",gap:8}}>
            <div>
              <div style={{fontFamily:FFD,fontSize:14,fontWeight:700,color:"var(--text)",marginBottom:2}}>Search Quota — {planName}</div>
              <div style={{fontSize:12,color:"var(--muted)"}}>{searchesUsed} used · {searchesLeft} remaining · {searchesTotal} total</div>
            </div>
            <div style={{display:"flex",gap:8}}>
              <Badge color={searchesLeft>10?"var(--green)":searchesLeft>0?"var(--amber)":"var(--red)"}>
                {searchesLeft>10?"✓ Good":"⚠ Low"} — {searchesLeft} left
              </Badge>
              <Btn onClick={()=>setPage("support")} style={{padding:"6px 12px",fontSize:11,background:"transparent",border:"1px solid var(--b2)",color:"var(--sub)"}}>Request Top-up</Btn>
            </div>
          </div>
          <div style={{height:8,background:"var(--s2)",borderRadius:99,overflow:"hidden"}}>
            <div className="quota-fill" style={{"--qw":`${quotaPct}%`,height:"100%",borderRadius:99,background:quotaPct>80?"linear-gradient(90deg,var(--amber),var(--red))":quotaPct>50?"linear-gradient(90deg,var(--brand),var(--green))":"linear-gradient(90deg,var(--brand),#ff914d)"}}/>
          </div>
          <div style={{display:"flex",justifyContent:"space-between",marginTop:6,fontSize:10,color:"var(--muted)"}}>
            <span>0</span><span style={{color:quotaPct>80?"var(--red)":"var(--brand)",fontWeight:600}}>{quotaPct}% used</span><span>{searchesTotal}</span>
          </div>
        </div>
      )}

      {/* Main content */}
      <div style={{display:"grid",gridTemplateColumns:"1.5fr 1fr",gap:16}} className="mg1">
        {/* Recent searches */}
        <div className="fu2 glass neon-hover" style={{borderRadius:14,padding:20,border:"1px solid var(--b1)"}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16}}>
            <div style={{fontFamily:FFD,fontSize:13,fontWeight:700,color:"var(--sub)",letterSpacing:.6}}>RECENT SEARCHES</div>
            <button onClick={()=>setPage("search")} style={{background:"none",border:"none",color:"var(--brand)",cursor:"pointer",fontSize:12,fontWeight:700,fontFamily:FF}}>Search →</button>
          </div>
          {myH.length===0&&(
            <div style={{textAlign:"center",padding:"24px 0"}}>
              <div style={{fontSize:32,marginBottom:8}}>🔍</div>
              <div style={{fontSize:13,color:"var(--muted)",marginBottom:12}}>No searches yet</div>
              <Btn onClick={()=>setPage("search")} style={{fontSize:12,padding:"8px 16px"}}>Start Searching →</Btn>
            </div>
          )}
          {myH.slice(0,6).map((h,i)=>(
            <div key={i} className="fu" style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"10px 0",borderBottom:i<5?"1px solid var(--b1)":"none"}}>
              <div style={{display:"flex",gap:10,alignItems:"center"}}>
                <div style={{width:32,height:32,borderRadius:8,background:h.found?"var(--green)15":"var(--red)15",display:"flex",alignItems:"center",justifyContent:"center",fontSize:13}}>
                  {h.searchType==="uan"?"🏦":h.searchType==="pan"?"📋":h.searchType==="dl"?"🚗":"🪪"}
                </div>
                <div>
                  <div style={{fontSize:13,fontWeight:600,color:"var(--text)"}}>{h.searchValue||h.name||"—"}</div>
                  <div style={{fontSize:10,color:"var(--muted)",fontFamily:FFM,textTransform:"uppercase"}}>{h.searchType||"aadhaar"} · {(h.timestamp||h.date||"").slice(0,10)}</div>
                </div>
              </div>
              <Badge color={h.found?"var(--green)":"var(--red)"} style={{fontSize:10}}>{h.found?"✓ Found":"✗ Not Found"}</Badge>
            </div>
          ))}
        </div>

        {/* Plan card */}
        <div className="fu3" style={{display:"flex",flexDirection:"column",gap:14}}>
          <div className="glass neon-hover" style={{borderRadius:14,padding:20,border:"1px solid var(--b1)",flex:1}}>
            <div style={{fontFamily:FFD,fontSize:13,fontWeight:700,color:"var(--sub)",letterSpacing:.6,marginBottom:14}}>YOUR PLAN</div>
            {searchesTotal>0?(
              <>
                <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:12}}>
                  <div style={{width:44,height:44,borderRadius:12,background:"var(--brand)15",display:"flex",alignItems:"center",justifyContent:"center",fontSize:22}}>⚡</div>
                  <div>
                    <div style={{fontFamily:FFD,fontSize:17,fontWeight:700,color:"var(--text)"}}>{planName}</div>
                    <div style={{fontSize:12,color:"var(--muted)"}}>{searchesTotal} searches/month</div>
                  </div>
                </div>
                <div style={{display:"flex",gap:10,marginBottom:14}}>
                  <div style={{flex:1,background:"var(--s2)",borderRadius:8,padding:"10px",textAlign:"center"}}>
                    <div style={{fontSize:20,fontWeight:700,color:"var(--green)",fontFamily:FFD}}>{searchesLeft}</div>
                    <div style={{fontSize:10,color:"var(--muted)",fontWeight:600}}>REMAINING</div>
                  </div>
                  <div style={{flex:1,background:"var(--s2)",borderRadius:8,padding:"10px",textAlign:"center"}}>
                    <div style={{fontSize:20,fontWeight:700,color:"var(--brand)",fontFamily:FFD}}>{searchesUsed}</div>
                    <div style={{fontSize:10,color:"var(--muted)",fontWeight:600}}>USED</div>
                  </div>
                </div>
                <Btn onClick={()=>setPage("search")} style={{width:"100%",justifyContent:"center",fontSize:13}}>🔍 Search Employee →</Btn>
              </>
            ):(
              <div style={{textAlign:"center",padding:"16px 0"}}>
                <div style={{fontSize:32,marginBottom:8}}>📋</div>
                <div style={{fontSize:13,color:"var(--muted)",marginBottom:12}}>No plan assigned yet</div>
                <button onClick={()=>setPage("support")} style={{background:"none",border:"1px solid var(--acc)",borderRadius:8,color:"var(--brand)",padding:"8px 16px",cursor:"pointer",fontSize:12,fontWeight:700,fontFamily:FF}}>Contact Admin</button>
              </div>
            )}
          </div>

          {/* Quick actions */}
          <div className="glass neon-hover" style={{borderRadius:14,padding:16,border:"1px solid var(--b1)"}}>
            <div style={{fontFamily:FFD,fontSize:11,fontWeight:700,color:"var(--sub)",letterSpacing:.6,marginBottom:10}}>QUICK ACTIONS</div>
            {[
              {icon:"⬆",label:"Upload Exit Data",page:"upload",color:"var(--brand)"},
              {icon:"📦",label:"Bulk Upload",page:"bulk",color:"var(--purple)"},
              {icon:"📜",label:"Search History",page:"history",color:"var(--green)"},
            ].map(a=>(
              <button key={a.page} onClick={()=>setPage(a.page)} style={{width:"100%",display:"flex",alignItems:"center",gap:10,padding:"9px 10px",background:"transparent",border:"none",borderRadius:8,cursor:"pointer",marginBottom:4,transition:"background .15s",fontFamily:FF}} onMouseEnter={e=>e.currentTarget.style.background="var(--s2)"} onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
                <span style={{fontSize:16}}>{a.icon}</span>
                <span style={{fontSize:13,fontWeight:500,color:"var(--sub)"}}>{a.label}</span>
                <svg width="12" height="12" viewBox="0 0 12 12" fill="none" style={{marginLeft:"auto",color:"var(--muted)"}}><path d="M3 6h6M6 3l3 3-3 3" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/></svg>
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════
   SEARCH PAGE — UAN / Aadhaar / PAN with 3rd party API + DB cache
══════════════════════════════════════════════════════════════ */
function SearchPage({user,employees,setEmployees,addToast,setHistory,company,wallets,setWallets}) {
  const {dark}=useTheme();
  const [searchType,setSearchType]=useState("uan");
  const [q,setQ]=useState("");
  const [dlDob,setDlDob]=useState("");
  const [ecourtForm,setEcourtForm]=useState({name:"",father_name:"",address:"",year:"",state:""});
  const [consent,setConsent]=useState(false);
  const [loading,setLoading]=useState(false);
  const [result,setResult]=useState(null);
  const [searched,setSearched]=useState(false);
  const [apiSource,setApiSource]=useState(""); // "db" or "api"
  // Address Verification — separate inline form (name/email/phone/addresses)
  // instead of the usual single search box, since this check needs more
  // fields than the other modules.
  const [addrForm,setAddrForm]=useState({candidateName:"",candidateEmail:"",candidatePhone:"",presentAddress:"",permanentAddress:"",sameAddress:false});
  const [addrLoading,setAddrLoading]=useState(false);
  const isSuperAdmin=user.role==="superadmin";

  // If the default/current module isn't accessible to this user (fresh HR
  // staff start with zero access), auto-switch to whichever module they do
  // have — or leave "uan" selected with everything locked, showing the
  // "no access" state below, if they have none at all.
  useEffect(()=>{
    if(!hasModuleAccess(user,isSuperAdmin,searchType)){
      const firstAllowed=SEARCH_MODULES.find(m=>hasModuleAccess(user,isSuperAdmin,m.key));
      if(firstAllowed)setSearchType(firstAllowed.key);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  },[]);

  // Check wallet balance before search
  const getSearchCost=()=>getCompanyPrice(company,searchType);
  const hasQuota=()=>{
    if(isSuperAdmin)return true;
    const companyId=user.companyId||company?.id||"";
    const w=wallets[companyId]||{balance:0};
    const cost=getSearchCost();
    // Check HR allocation first
    // For now check company wallet balance
    return (w.balance||0)>=cost;
  };

  const searchTypes=[
    {id:"uan",label:"UAN Number",placeholder:"12-digit UAN",icon:"🏦",hint:"Universal Account Number from EPFO"},
    {id:"aadhaar",label:"Aadhaar Number",placeholder:"XXXX-XXXX-XXXX",icon:"🪪",hint:"12-digit Aadhaar number"},
    {id:"pan",label:"PAN Number",placeholder:"ABCDE1234F",icon:"📋",hint:"10-character PAN card number"},
    {id:"dl",label:"Driving License",placeholder:"e.g. UP2020150000000",icon:"🚗",hint:"DL number + Date of Birth required"},
    {id:"ecourt",label:"Criminal Check",placeholder:"Full Name e.g. Rajesh Kumar",icon:"⚖️",hint:"eCourt India Database Search"},
    {id:"address",label:"Address Verify",placeholder:"",icon:"🏠",hint:"Candidate selfie + GPS location match"},
  ];

  const validateInput=()=>{
    if(searchType==="ecourt"){
      if(!ecourtForm.name?.trim()&&!q.trim())return "Please enter candidate name";
      if(!ecourtForm.father_name?.trim())return "Father's name is required for criminal check";
      if(!ecourtForm.address?.trim())return "Address is required for criminal check";
      return null;
    }
    if(!q.trim())return "Please enter a value to search";
    if(searchType==="aadhaar"&&!validAadhaar(q))return "Format: XXXX-XXXX-XXXX";
    if(searchType==="pan"&&!/^[A-Z]{5}[0-9]{4}[A-Z]$/.test(q.toUpperCase()))return "Format: ABCDE1234F";
    if(searchType==="uan"&&!/^\d{12}$/.test(q.replace(/\s/g,"")))return "UAN must be 12 digits";
    if(searchType==="aadhaar"&&!consent)return "Please confirm candidate consent";
    return null;
  };

  // Check our DB first, then call API
  const checkDatabase=()=>{
    const val=q.trim().replace(/\s/g,"");
    if(searchType==="uan")return employees.find(e=>e.uan===val||e.uan===q.trim());
    if(searchType==="aadhaar")return employees.find(e=>e.aadhaar===q.trim()||e.aadhaar===val);
    if(searchType==="pan")return employees.find(e=>e.pan?.toUpperCase()===val.toUpperCase());
    if(searchType==="dl")return employees.find(e=>e.dl_number?.toUpperCase()===val.toUpperCase());
    return null;
  };

  // ✅ LIVE Meitho Verification API Integration
  // APIs: UAN Employment History V2 | PAN Comprehensive | DigiLocker (Aadhaar)
  // Base URL: https://kyc-api.surepass.app
  // ✅ Firebase Function URLs (proxy to Surepass — no CORS issues)
  const FN_BASE = "https://us-central1-meitho.cloudfunctions.net";
  // Cloud Run direct URLs (faster + correct)
  const CR_UAN   = "https://uanverify-mtvra6khyq-uc.a.run.app";
  const CR_PAN   = "https://panverify-mtvra6khyq-uc.a.run.app";
  const CR_AADH  = "https://aadhaarverify-mtvra6khyq-uc.a.run.app";
  const CR_AADH_DL = "https://aadhaardownload-mtvra6khyq-uc.a.run.app";
  const CR_DL    = "https://dlverify-mtvra6khyq-uc.a.run.app";
  const CR_COURT = "https://ecourtsearch-mtvra6khyq-uc.a.run.app";

  const callSurepassAPI=async()=>{
    const val=q.trim();
    const headers=await getAuthedHeaders();

    // ── UAN Employment History V2 ──────────────────────────────
    if(searchType==="ecourt"){
      try{
        const payload={name:ecourtForm.name||q.trim()};
        if(ecourtForm.father_name?.trim())payload.father_name=ecourtForm.father_name.trim();
        if(ecourtForm.address?.trim())payload.address=ecourtForm.address.trim();
        if(ecourtForm.year?.trim())payload.year=ecourtForm.year.trim();
        if(ecourtForm.state?.trim())payload.state=ecourtForm.state.trim();
        const res=await fetch(CR_COURT,{
          method:"POST",headers,
          body:JSON.stringify(payload)
        });
        const json=await res.json();
        if(!json.success)return{found:false,error:json.error||"eCourt search failed"};
        const d=json.data||{};
        return{
          found:true,searchType:"ecourt",source:"api",
          data:{
            name:d.name,father_name:d.father_name,
            total_cases:d.total_cases,
            criminal_cases:d.criminal_cases,
            civil_cases:d.civil_cases,
            pending_cases:d.pending_cases,
            disposed_cases:d.disposed_cases,
            has_criminal:d.has_criminal,
            cases:d.cases||[],
          }
        };
      }catch(e){return{found:false,error:e.message};}
    }

    if(searchType==="dl"){
      if(!dlDob){addToast("Enter date of birth for DL verification","error");setLoading(false);return;}
      try{
        const res=await fetch("https://dlverify-mtvra6khyq-uc.a.run.app",{method:"POST",headers,body:JSON.stringify({dl_number:val,dob:dlDob})});
        const json=await res.json();
        if(!json.success) return {found:false,error:json.error||"DL not found or invalid"};
        const d=json.data||{};
        const doe=d.doe&&!d.doe.startsWith("1800")?d.doe:"";
        return {
          found:true,searchType:"dl",source:"api",
          data:{
            dl_number:d.dl_number,name:d.name,dob:d.dob,
            doe,doi:d.doi,state:d.state,gender:d.gender==="M"?"Male":"Female",
            father_name:d.father_name,blood_group:d.blood_group,
            permanent_address:d.permanent_address,
            vehicle_classes:d.vehicle_classes||[],
            ola_name:d.ola_name,has_image:d.has_image,
            profile_image:d.profile_image,
            isExpired:doe&&new Date(doe)<new Date(),
          }
        };
      }catch(e){return {found:false,error:e.message};}
    }

    if(searchType==="uan"){
      try{
        const res=await fetch(CR_UAN,{
          method:"POST",headers,
          body:JSON.stringify({uan:val.replace(/\D/g,"")})
        });
        const json=await res.json();
        if(json.status_code!==200){
          return {found:false,source:"EPFO",error:json.message||"No records found"};
        }
        const d=json.data||{};
        // Map employment history with all possible field names
        const empHistory=(d.employment_history||d.employments||[]).map(e=>({
          name:e.name||e.employee_name||"",
          company:e.establishment_name||e.company_name||e.employer_name||"",
          designation:e.designation||e.post||e.job_title||"",
          doj:e.date_of_joining||e.doj||e.joining_date||"",
          dor:(e.date_of_exit||e.dor||"").startsWith("1800")?"Currently Employed":(e.date_of_exit||e.dor||""),
          salary:e.wages||e.salary||e.basic_wages||"",
          pf:e.pf_status||e.epf_status||e.member_status||"",
          epfOffice:e.office_name||e.epf_office||"",
          memberId:e.member_id||e.pf_account_number||"",
          guardianName:e.guardian_name||"",
        }));
        
        // Sort by DOJ to get correct order (oldest first)
        empHistory.sort((a,b)=>{
          const da=new Date(a.doj||"1900-01-01");
          const db=new Date(b.doj||"1900-01-01");
          return da-db;
        });

        // Calculate total experience correctly
        // Filter out invalid dates (like 1800-xx-xx from EPFO)
        const isValidDate=(d)=>{
          if(!d||d==="Present"||d==="")return false;
          const yr=parseInt(d.split("-")[0]||"0");
          return yr>1950&&yr<=new Date().getFullYear()+1;
        };
        // Fix DOR - if invalid date set to empty
        empHistory.forEach(e=>{
          if(e.dor&&!isValidDate(e.dor)){e.dor="";}
        });
        const firstJob=empHistory[0];
        const lastJob=empHistory[empHistory.length-1];
        const startDate=firstJob?new Date(firstJob.doj||""):null;
        const lastDor=lastJob?.dor||"";
        const endDate=isValidDate(lastDor)?new Date(lastDor):new Date();
        const totalExpYears=startDate&&!isNaN(startDate)&&startDate.getFullYear()>1950?
          Math.round((endDate-startDate)/(1000*60*60*24*365)*10)/10:0;
        const lastCompanyRemark=(!lastDor||lastDor===""||!isValidDate(lastDor))?
          "Last company exit date not available — experience calculated till current date":null;

        return {
          found:true,source:"EPFO",
          data:{
            uan:val,
            name:d.name||d.employee_name||d.member_name||d.full_name||(empHistory.length>0?empHistory[0].name:"")||"",
            dob:"", // DOB not returned by Surepass UAN API
            fatherName:empHistory.length>0?empHistory[0].guardian_name||"":"",
            gender:d.gender||"",
            aadhaarLinked:d.aadhaar_linked||d.is_aadhaar_seeded||false,
            employmentHistory:empHistory,
            totalExperience:totalExpYears,
            lastCompanyRemark,
          }
        };
      }catch(e){
        console.error("UAN error:",e);
        return {found:false,source:"EPFO",error:e.message};
      }
    }

    // ── Aadhaar — DigiLocker Via Link ─────────────────────────
    if(searchType==="aadhaar"){
      try{
        const res=await fetch("https://aadhaarverify-mtvra6khyq-uc.a.run.app",{
          method:"POST",headers,
          body:JSON.stringify({aadhaar:val.replace(/\D/g,"")})
        });
        const json=await res.json();
        if(json.status_code!==200){
          return {found:false,source:"Meitho",error:"DigiLocker error: "+(json.message||json.error||"Verification failed. Please contact Meitho support.")};
        }
        const d=json.data||{};
        const clientId=d.client_id||d.reference_id||"";
        const link=d.url||d.redirect_url||d.digilocker_url||d.authorization_url||"";
        return {
          found:true,source:"Meitho",digilockerFlow:true,
          data:{
            aadhaar:val,verified:false,
            clientId,link,
            expiryMinutes:d.expiry_minutes||10,
            message:"DigiLocker verification initiated.",
          }
        };
      }catch(e){
        console.error("Aadhaar error:",e);
        return {found:false,source:"Meitho",error:"DigiLocker connection failed: "+e.message};
      }
    }

    // ── PAN Comprehensive ──────────────────────────────────────
    if(searchType==="pan"){
      try{
        const res=await fetch(CR_PAN,{
          method:"POST",headers,
          body:JSON.stringify({pan:val.toUpperCase()})
        });
        const json=await res.json();
        if(json.status_code!==200){
          return {found:false,source:"Income Tax",error:json.message||"PAN not found"};
        }
        const d=json.data||{};
        // Build full address string from address object
        const addrObj=d.address||d.res_address||d.address_details||{};
        const addrStr=typeof addrObj==="string"?addrObj:
          addrObj.full||
          [addrObj.line_1||addrObj.door_no||addrObj.premise_name,
           addrObj.line_2||addrObj.street_name||addrObj.road_name,
           addrObj.city||addrObj.loc_name||addrObj.district,
           addrObj.state||addrObj.state_name,
           addrObj.zip||addrObj.pincode?`PIN: ${addrObj.zip||addrObj.pincode}`:"",
           addrObj.country]
          .filter(x=>x&&String(x).trim()&&String(x).trim()!=="undefined").join(", ")||"";
        return {
          found:!!(d.full_name||d.name||d.pan_number),
          source:"Income Tax",
          data:{
            pan:d.pan_number||val.toUpperCase(),
            name:d.full_name||d.name||"",
            dob:d.dob||d.date_of_birth||"",
            status:d.status||d.pan_status||"Active",
            type:d.category||d.pan_type||"Individual",
            aadhaar_linked:d.aadhaar_linked||false,
            aadhaarLinked:d.aadhaar_linked||false,
            masked_aadhaar:d.masked_aadhaar||"",
            verified:true,
            gender:d.gender||"",
            email:d.email||"",
            mobile:d.phone_number||d.mobile||"",
            address:addrStr||"",
            lessInfo:d.less_info||false,
            fullNameSplit:d.full_name_split||[],
          }
        };
      }catch(e){
        console.error("PAN error:",e);
        return {found:false,source:"Income Tax",error:e.message};
      }
    }

    // ── Criminal Check — eCourt Search ────────────────────────
    if(searchType==="criminal"){
      try{
        const res=await fetch(CR_COURT,{
          method:"POST",headers,
          body:JSON.stringify({name:val.trim()})
        });
        const json=await res.json();
        if(json.status_code!==200){
          return {found:false,source:"eCourt",error:json.message||"eCourt search failed"};
        }
        const d=json.data||{};
        const cases=d.court_case_details||d.cases||d.results||[];
        const totalCases=d.total_count||d.total||cases.length||0;
        return {
          found:true,
          source:"eCourt",
          data:{
            name:val.trim(),
            totalCases,
            cases:cases.slice(0,10),
            criminalCheck:totalCases===0?"clear":"records_found",
            searchDate:new Date().toISOString().split("T")[0],
          }
        };
      }catch(e){
        console.error("Criminal check error:",e);
        return {found:false,source:"eCourt",error:e.message};
      }
    }

    // ── Education Verification ─────────────────────────────────
    if(searchType==="education"){
      // Education verification - show manual verification prompt
      // Full API integration coming soon via Surepass
      return {
        found:true,
        source:"Meitho",
        educationCheck:true,
        data:{
          name:val.trim(),
          status:"manual_required",
          message:"Education verification initiated. Please collect original documents and upload them for manual verification.",
          checkId:"EDU-"+Date.now().toString(36).toUpperCase(),
        }
      };
    }

    return {found:false};
  };

  const doSearch=async()=>{
    if(!hasModuleAccess(user,isSuperAdmin,searchType)){
      addToast("You don't have access to this module. Ask your Company Admin to enable it.","error");
      return;
    }
    const err=validateInput();
    if(err){addToast(err,"error");return;}
    if(!isSuperAdmin){
      const cost=getSearchCost();
      const companyId=user.companyId||company?.id||"";
      const bal=(wallets[companyId]||{balance:0}).balance||0;
      if(bal<cost){
        addToast("⚠ Insufficient wallet balance. Need ₹"+cost+", available ₹"+bal+". Contact your admin to top up.","error");
        return;
      }
    }
    setLoading(true);setResult(null);setSearched(false);

    try{
      // Step 1: Check Meitho DB first
      const dbHit=checkDatabase();
      let searchResult;

      if(dbHit){
        setApiSource("db");
        // For DL from DB, wrap properly so result card works
        if(searchType==="dl"){
          searchResult={
            found:true,fromDB:true,searchType:"dl",source:"db",
            data:{
              dl_number:dbHit.dl_number||q.trim().toUpperCase(),
              name:dbHit.name||"",
              dob:dbHit.dob||"",
              father_name:dbHit.father_name||"",
              blood_group:dbHit.blood_group||"",
              permanent_address:dbHit.permanent_address||"",
              vehicle_classes:dbHit.vehicle_classes||[],
              doi:dbHit.dl_issue||"",
              doe:dbHit.dl_expiry||"",
              state:dbHit.state||dbHit.dl_state||"",
              ola_name:dbHit.ola_name||"",
              has_image:dbHit.has_image||false,
              profile_image:dbHit.profile_image||"",
              isExpired:dbHit.dl_expiry&&new Date(dbHit.dl_expiry)<new Date(),
            },
            employee:dbHit,
          };
        } else if(searchType==="ecourt"){
          searchResult={found:true,fromDB:true,searchType:"ecourt",source:"db",data:dbHit,employee:dbHit};
        } else {
          searchResult={found:true,fromDB:true,searchType,source:"db",employee:dbHit};
        }
        addToast("Result from Meitho database ✓","success");
      } else {
        // Step 2: Call Meitho Verification API
        setApiSource("api");
        const apiResult=await callSurepassAPI();
        searchResult=apiResult;

        // Step 3: Save to our DB if found
        if(apiResult.found&&apiResult.data){
          const empData={
            uan:searchType==="uan"?q.trim():apiResult.data?.uan||"",
            aadhaar:searchType==="aadhaar"?q.trim():apiResult.data?.aadhaar||"",
            pan:searchType==="pan"?q.trim().toUpperCase():apiResult.data?.pan||"",
            dl_number:searchType==="dl"?q.trim().toUpperCase():apiResult.data?.dl_number||"",
            name:apiResult.data?.name||"",
            dob:apiResult.data?.dob||"",
            gender:apiResult.data?.gender||"",
            state:apiResult.data?.state||"",
            blood_group:apiResult.data?.blood_group||"",
            father_name:apiResult.data?.father_name||"",
            permanent_address:apiResult.data?.permanent_address||"",
            vehicle_classes:apiResult.data?.vehicle_classes||[],
            dl_expiry:apiResult.data?.doe||"",
            dl_issue:apiResult.data?.doi||"",
            ola_name:apiResult.data?.ola_name||"",
            profile_image:apiResult.data?.profile_image||"",
            verified:true,
            verifiedBy:searchType,
            source:"meitho",
            employmentHistory:apiResult.data?.employmentHistory||[],
            lastCompany:apiResult.data?.employmentHistory?.[apiResult.data.employmentHistory.length-1]?.company||"",
            fetchedAt:new Date().toISOString(),
            addedBy:"search",
          };
          try{
            const id=await addEmployee(empData);
            setEmployees(p=>[...p,{...empData,id}]);
          }catch(e){console.warn("Could not save to DB:",e);}
        }
      }

      setResult(searchResult);
      setSearched(true);

      // Step 4: Save search audit trail
      const searchVal = searchType==="ecourt"
        ? (ecourtForm.name||q.trim())
        : searchType==="dl"
          ? q.trim().toUpperCase()
          : q.trim();
      const audit={
        userId:user.id,
        userName:user.name||user.email,
        companyId:user.companyId||"superadmin",
        companyName:company?.name||"Meitho Admin",
        searchType,
        searchValue:searchVal,
        found:searchResult.found,
        source:dbHit?"db":"api",
        isSuperAdmin,
        timestamp:new Date().toISOString(),
        cost:getSearchCost(),
        resultName:searchResult.data?.name||searchResult.employee?.name||"",
        employeeData:searchResult.found?(searchResult.fromDB?searchResult.employee:searchResult.data)||{}:{},
        // DL specific
        dl_number:searchType==="dl"?searchResult.data?.dl_number||q.trim().toUpperCase():"",
        dl_state:searchType==="dl"?searchResult.data?.state||"":"",
        dl_expiry:searchType==="dl"?searchResult.data?.doe||"":"",
        // eCourt specific
        ecourt_cases:searchType==="ecourt"?searchResult.data?.total_cases||0:0,
        ecourt_criminal:searchType==="ecourt"?searchResult.data?.criminal_cases||0:0,
      };
      await addSearchHistory(audit);
      setHistory(p=>[audit,...p]);

      // Step 5: Deduct wallet balance (not for super admin, and never on
      // an outright API/connection error — only for completed searches,
      // whether that's a hit or a genuine "not found" result).
      const isGenuineError = !!searchResult.error && !searchResult.found && !searchResult.digilockerFlow;
      if(!isSuperAdmin && !isGenuineError){
        const cost=getSearchCost();
        const companyId=user.companyId||company?.id||"";
        try{
          const rc=await walletChargeSrv([{type:searchType,qty:1}],`${searchType.toUpperCase()} Search — ${q.trim()}`,searchType);
          if(rc.charged){
            setWallets(p=>({...p,[companyId]:{...p[companyId],balance:rc.after}}));
            checkLowWalletAndNotify(companyId,company?.name,rc.before,rc.after);
          }
        }catch(e){console.warn("Wallet deduction failed:",e);}
      } else if(isGenuineError){
        addToast("Search could not be completed — you have not been charged for this attempt.","amber");
      }

    }catch(e){addToast("Search failed: "+e.message,"error");}
    setLoading(false);
  };

  // Address Verification submit — separate flow since it emails the
  // candidate a link instead of returning an immediate result.
  const doAddressVerify=async()=>{
    if(!hasModuleAccess(user,isSuperAdmin,"address")){
      addToast("You don't have access to this module. Ask your Company Admin to enable it.","error");
      return;
    }
    if(!addrForm.candidateName||!addrForm.candidateEmail){addToast("Enter candidate name and email","error");return;}
    if(!addrForm.presentAddress){addToast("Enter present address","error");return;}
    if(!addrForm.sameAddress&&!addrForm.permanentAddress){addToast("Enter permanent address, or check 'same as present'","error");return;}
    const cost=getCompanyPrice(company,"address_verify");
    const companyIdLocal=user.companyId||company?.id||"";
    const wallet=(wallets||{})[companyIdLocal]||{balance:0};
    if(!isSuperAdmin&&(wallet.balance||0)<cost){
      addToast("⚠ Insufficient wallet balance. Need ₹"+cost+", available ₹"+(wallet.balance||0)+". Contact your admin to top up.","error");
      return;
    }
    setAddrLoading(true);
    try{
      const checkId="AV-"+Date.now().toString(36).toUpperCase();
      await setDoc(doc(db,"address_verifications",checkId),{
        id:checkId,companyId:companyIdLocal,companyName:company?.name||"",
        initiatedBy:user.id,initiatedByName:user.name||user.email,
        candidateName:addrForm.candidateName,candidateEmail:addrForm.candidateEmail,candidatePhone:addrForm.candidatePhone,
        presentAddress:addrForm.presentAddress,
        permanentAddress:addrForm.sameAddress?addrForm.presentAddress:addrForm.permanentAddress,
        sameAddress:addrForm.sameAddress,
        presentVerification:{status:"pending",attempts:0},
        permanentVerification:addrForm.sameAddress?null:{status:"pending",attempts:0},
        status:"pending",cost,
        initiatedAt:new Date().toISOString(),
      });

      if(!isSuperAdmin){
        const rc=await walletChargeSrv([{type:"address_verify",qty:1}],"Address Verification — "+addrForm.candidateName,"address_verify");
        if(rc.charged){
          setWallets(p=>({...p,[companyIdLocal]:{...p[companyIdLocal],balance:rc.after}}));
          checkLowWalletAndNotify(companyIdLocal,company?.name,rc.before,rc.after);
        }
      }

      const verifyLink=`${window.location.origin}/address-verify/${checkId}`;
      await fetch(CF_SEND_CANDIDATE,{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({
        candidateEmail:addrForm.candidateEmail,candidateName:addrForm.candidateName,
        customSubject:`Address Verification Required — ${company?.name||"Meitho"}`,
        customBody:`<p>Dear ${addrForm.candidateName},</p><p>${company?.name||"Your employer"} needs to verify your address as part of your background check.</p><p>Please click below, confirm your address, and take a quick selfie at that location — it only takes a minute.</p><p><a href="${verifyLink}" style="background:#ff3131;color:white;padding:12px 24px;border-radius:8px;text-decoration:none;display:inline-block;margin-top:8px;">Verify My Address →</a></p><p>Best regards,<br/>${company?.name||"Meitho"} Team</p>`,
      })});

      addToast("Address verification sent — candidate has been emailed.","success");
      setAddrForm({candidateName:"",candidateEmail:"",candidatePhone:"",presentAddress:"",permanentAddress:"",sameAddress:false});
    }catch(e){addToast("Failed: "+e.message,"error");}
    finally{setAddrLoading(false);}
  };

  const ResultCard=()=>{
    if(!result)return null;

    // ── eCourt Criminal Check Result ──────────────────────────
    if(result.searchType==="ecourt"&&result.data){
      const d=result.data;
      const hasCriminal=d.has_criminal;

      const generateEcourtReport=()=>{
        const dt=new Date().toLocaleString("en-IN");
        const caseRows=d.cases.map(c=>`
          <div style="background:${c.case_type==="Criminal"?"rgba(239,68,68,0.04)":"rgba(248,250,252,1)"};border-radius:10px;padding:14px 16px;margin-bottom:12px;border:1px solid ${c.case_type==="Criminal"?"rgba(239,68,68,0.15)":"#e2e8f0"};border-left:4px solid ${c.case_type==="Criminal"?"#ef4444":c.case_status==="Pending"?"#f59e0b":"#ff3131"};">
            <div style="display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:8px;flex-wrap:wrap;gap:6px;">
              <div>
                <span style="font-size:10px;font-weight:700;padding:2px 8px;border-radius:99px;background:${c.case_type==="Criminal"?"rgba(239,68,68,0.1)":"rgba(12,192,223,0.1)"};color:${c.case_type==="Criminal"?"#ef4444":"#0cc0df"};margin-right:6px;">${c.case_type||"Unknown"}</span>
                <span style="font-size:10px;font-weight:700;padding:2px 8px;border-radius:99px;background:${c.case_status==="Pending"?"rgba(245,158,11,0.1)":"rgba(255,49,49,0.1)"};color:${c.case_status==="Pending"?"#f59e0b":"#ff3131"};">${c.case_status||"Unknown"}</span>
              </div>
              <div style="font-size:10px;color:#94a3b8;font-family:monospace;">${c.cnr_number||c.case_no||""}</div>
            </div>
            <div style="font-size:13px;font-weight:700;color:#0a0a1a;margin-bottom:4px;">${c.case_name||c.case_type_name||"—"}</div>
            <div style="display:grid;grid-template-columns:1fr 1fr;gap:8px;margin-bottom:8px;">
              <div><span style="font-size:9px;color:#94a3b8;display:block;margin-bottom:2px;">COURT</span><span style="font-size:11px;color:#334155;">${c.court_name||"—"}</span></div>
              <div><span style="font-size:9px;color:#94a3b8;display:block;margin-bottom:2px;">STATE</span><span style="font-size:11px;color:#334155;">${c.state||"—"}</span></div>
              <div><span style="font-size:9px;color:#94a3b8;display:block;margin-bottom:2px;">FILING DATE</span><span style="font-size:11px;color:#334155;">${c.filing_date||"—"}</span></div>
              <div><span style="font-size:9px;color:#94a3b8;display:block;margin-bottom:2px;">UNDER ACT</span><span style="font-size:11px;color:#334155;">${c.under_act||"—"} ${c.under_section?"§"+c.under_section:""}</span></div>
            </div>
            ${c.petitioner?`<div style="font-size:11px;color:#475569;margin-bottom:2px;"><b>Petitioner:</b> ${c.petitioner}</div>`:""}
            ${c.respondent?`<div style="font-size:11px;color:#475569;"><b>Respondent:</b> ${c.respondent}</div>`:""}
          </div>`).join("");

        const html=`<!DOCTYPE html><html><head><meta charset="UTF-8"/>
<title>Criminal Check — ${d.name}</title>
<style>
*{margin:0;padding:0;box-sizing:border-box;}
body{font-family:'Segoe UI',Arial,sans-serif;color:#1a1a2e;background:#fff;}
@page{margin:12mm;size:A4;}
@media print{body{-webkit-print-color-adjust:exact;print-color-adjust:exact;}.no-print{display:none;}}
</style></head><body>
<div class="no-print" style="background:#0a0a1a;padding:10px 20px;text-align:right;">
  <button onclick="window.print()" style="background:linear-gradient(90deg,#ff3131,#ff914d);color:#fff;border:none;padding:8px 20px;border-radius:8px;cursor:pointer;font-size:13px;font-weight:700;">⬇ Download PDF</button>
</div>
<div style="background:linear-gradient(135deg,#0a0a1a,#1a1a2e);padding:20px 32px;display:flex;justify-content:space-between;align-items:center;">
  <div><img src="${LOGO1}" style="height:32px;margin-bottom:4px;" alt="Meitho BGV"/>
  <div style="font-size:9px;color:#0cc0df;letter-spacing:2px;font-weight:700;">CRIMINAL BACKGROUND CHECK REPORT</div></div>
  <div style="text-align:right;">
    <div style="font-size:9px;color:rgba(255,255,255,0.4);">${dt}</div>
    <div style="font-size:9px;padding:3px 8px;border-radius:99px;background:${hasCriminal?"rgba(239,68,68,0.2)":"rgba(255,49,49,0.2)"};color:${hasCriminal?"#ef4444":"#ff3131"};display:inline-block;margin-top:4px;font-weight:700;">${hasCriminal?"⚠ CRIMINAL CASES FOUND":"✅ NO CRIMINAL CASES"}</div>
  </div>
</div>
<div style="background:#0cc0df;padding:5px 32px;font-size:9px;color:#fff;font-weight:700;display:flex;justify-content:space-between;">
  <span>MEITHO BGV PLATFORM</span><span>meitho.com · help@meitho.com · 8300016265</span>
</div>
<div style="padding:24px 32px;">
  <div style="font-size:10px;font-weight:800;color:#0cc0df;border-bottom:2px solid #0cc0df;padding-bottom:5px;margin-bottom:14px;letter-spacing:1.5px;text-transform:uppercase;">Search Details</div>
  <div style="display:grid;grid-template-columns:repeat(3,1fr);gap:12px;margin-bottom:20px;">
    ${[["Candidate Name",d.name],["Father's Name",d.father_name||"—"],["Search Year",d.year||"All Years"]].map(([l,v])=>`<div style="background:#f8fafc;border-radius:8px;padding:10px 12px;border-left:3px solid #0cc0df;"><div style="font-size:8px;color:#94a3b8;font-weight:700;text-transform:uppercase;margin-bottom:3px;">${l}</div><div style="font-size:13px;font-weight:700;">${v}</div></div>`).join("")}
  </div>
  <div style="display:grid;grid-template-columns:repeat(4,1fr);gap:10px;margin-bottom:20px;">
    ${[["Total Cases",d.total_cases,"#0cc0df"],["Criminal",d.criminal_cases,"#ef4444"],["Civil",d.civil_cases,"#0cc0df"],["Pending",d.pending_cases,"#f59e0b"]].map(([l,v,c])=>`<div style="background:#f8fafc;border-radius:8px;padding:12px;text-align:center;border-top:3px solid ${c};"><div style="font-size:24px;font-weight:900;color:${c};">${v}</div><div style="font-size:9px;color:#94a3b8;font-weight:700;text-transform:uppercase;margin-top:3px;">${l}</div></div>`).join("")}
  </div>
  ${hasCriminal?`<div style="background:rgba(239,68,68,0.06);border:1px solid rgba(239,68,68,0.2);border-radius:10px;padding:14px;margin-bottom:16px;"><div style="font-size:13px;font-weight:700;color:#ef4444;">⚠️ Criminal Cases Found — Requires HR Review</div></div>`:`<div style="background:rgba(255,49,49,0.06);border:1px solid rgba(255,49,49,0.2);border-radius:10px;padding:14px;margin-bottom:16px;"><div style="font-size:13px;font-weight:700;color:#ff3131;">✅ No Criminal Cases Found in eCourt Database</div></div>`}
  <div style="font-size:10px;font-weight:800;color:#0cc0df;border-bottom:2px solid #0cc0df;padding-bottom:5px;margin-bottom:14px;letter-spacing:1.5px;text-transform:uppercase;">Case Details (${d.total_cases} cases)</div>
  ${caseRows}
</div>
<div style="background:#0a0a1a;padding:14px 32px;display:flex;justify-content:space-between;align-items:center;">
  <div style="font-size:10px;color:rgba(255,255,255,0.4);">Source: eCourt India · Powered by Surepass</div>
  <div style="font-size:10px;color:#0cc0df;">meitho.com · help@meitho.com</div>
</div>
</body></html>`;
        const win=window.open("","_blank","width=1000,height=800");
        if(!win){addToast("Allow popups to download report","error");return;}
        win.document.write(html);win.document.close();win.focus();
        setTimeout(()=>win.print(),800);
      };

      return(
        <Card className="fu" style={{marginTop:20,border:`1px solid ${hasCriminal?"rgba(239,68,68,0.3)":"rgba(255,49,49,0.3)"}`}}>
          {/* Header */}
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16,paddingBottom:14,borderBottom:"1px solid var(--b1)"}}>
            <div>
              <div style={{fontFamily:FFD,fontSize:20,fontWeight:800,color:"var(--text)",marginBottom:3}}>{d.name}</div>
              {d.father_name&&<div style={{fontSize:12,color:"var(--muted)"}}>Father: {d.father_name}</div>}
            </div>
            <div style={{display:"flex",gap:8,alignItems:"center",flexWrap:"wrap"}}>
              <Badge color={hasCriminal?"#ef4444":"var(--green)"}>{hasCriminal?"⚠️ Criminal Cases Found":"✅ Clear"}</Badge>
              <button onClick={generateEcourtReport} style={{background:"linear-gradient(90deg,#ff3131,#ff914d)",border:"none",borderRadius:8,color:"#fff",padding:"7px 14px",cursor:"pointer",fontSize:12,fontWeight:700,fontFamily:FF}}>↓ Report</button>
            </div>
          </div>

          {/* Summary stats */}
          <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:8,marginBottom:16}}>
            {[
              {label:"Total Cases",val:d.total_cases,color:"var(--brand)"},
              {label:"Criminal",val:d.criminal_cases,color:"#ef4444"},
              {label:"Civil",val:d.civil_cases,color:"#0cc0df"},
              {label:"Pending",val:d.pending_cases,color:"#f59e0b"},
            ].map(s=>(
              <div key={s.label} style={{background:"var(--s2)",borderRadius:8,padding:"10px",textAlign:"center",border:`1px solid var(--b1)`}}>
                <div style={{fontFamily:FFD,fontSize:22,fontWeight:900,color:s.color}}>{s.val}</div>
                <div style={{fontSize:9,color:"var(--muted)",fontWeight:700,textTransform:"uppercase",marginTop:2}}>{s.label}</div>
              </div>
            ))}
          </div>

          {/* Alert */}
          {hasCriminal&&(
            <div style={{background:"rgba(239,68,68,0.08)",border:"1px solid rgba(239,68,68,0.2)",borderRadius:10,padding:"12px 16px",marginBottom:14,fontSize:13,color:"#ef4444",fontWeight:600}}>
              ⚠️ {d.criminal_cases} criminal case{d.criminal_cases>1?"s":""} found. HR review required before hiring.
            </div>
          )}
          {!hasCriminal&&d.total_cases===0&&(
            <div style={{background:"rgba(255,49,49,0.08)",border:"1px solid rgba(255,49,49,0.2)",borderRadius:10,padding:"12px 16px",marginBottom:14,fontSize:13,color:"var(--green)",fontWeight:600}}>
              ✅ No cases found in eCourt database for this candidate.
            </div>
          )}

          {/* Cases list */}
          {d.cases.length>0&&(
            <div>
              <div style={{fontSize:11,color:"var(--muted)",fontWeight:700,textTransform:"uppercase",marginBottom:10}}>Case Details ({d.total_cases} cases)</div>
              {d.cases.map((c,i)=>(
                <div key={i} style={{background:"var(--s2)",borderRadius:10,padding:"12px 14px",marginBottom:8,border:`1px solid ${c.case_type==="Criminal"?"rgba(239,68,68,0.2)":"var(--b1)"}`,borderLeft:`3px solid ${c.case_type==="Criminal"?"#ef4444":c.case_status==="Pending"?"#f59e0b":"var(--green)"}`}}>
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:6,flexWrap:"wrap",gap:6}}>
                    <div style={{flex:1}}>
                      <div style={{display:"flex",gap:6,marginBottom:4,flexWrap:"wrap"}}>
                        <Badge color={c.case_type==="Criminal"?"#ef4444":"#0cc0df"} style={{fontSize:9}}>{c.case_type||"Unknown"}</Badge>
                        <Badge color={c.case_status==="Pending"?"#f59e0b":"var(--green)"} style={{fontSize:9}}>{c.case_status||"Unknown"}</Badge>
                        {c.under_act&&<Badge color="var(--muted)" style={{fontSize:9}}>{c.under_act}</Badge>}
                      </div>
                      <div style={{fontSize:13,fontWeight:700,color:"var(--text)"}}>{c.case_name||c.case_type_name||"—"}</div>
                    </div>
                    {c.cnr_number&&<div style={{fontSize:10,color:"var(--muted)",fontFamily:FFM}}>{c.cnr_number}</div>}
                  </div>
                  <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:6,fontSize:11,color:"var(--muted)"}}>
                    <div>🏛️ {c.court_name||"—"}</div>
                    <div>📍 {c.state||"—"}</div>
                    {c.filing_date&&<div>📅 Filed: {c.filing_date}</div>}
                    {c.under_section&&<div>§ {c.under_act} {c.under_section}</div>}
                  </div>
                  {c.petitioner&&<div style={{fontSize:11,color:"var(--muted)",marginTop:6}}>Petitioner: <span style={{color:"var(--sub)"}}>{c.petitioner.slice(0,80)}{c.petitioner.length>80?"...":""}</span></div>}
                  {c.respondent&&<div style={{fontSize:11,color:"var(--muted)",marginTop:2}}>Respondent: <span style={{color:"var(--sub)"}}>{c.respondent.slice(0,80)}{c.respondent.length>80?"...":""}</span></div>}
                  {c.case_details_link&&<a href={c.case_details_link} target="_blank" rel="noopener noreferrer" style={{fontSize:10,color:"var(--brand)",display:"block",marginTop:6,fontWeight:600}}>🔗 View Case Details →</a>}
                </div>
              ))}
            </div>
          )}

          <div style={{marginTop:10,fontSize:11,color:"var(--muted)",borderTop:"1px solid var(--b1)",paddingTop:10,display:"flex",justifyContent:"space-between"}}>
            <span>⚖️ eCourt Criminal Check · Powered by Meitho</span>
            <span style={{color:"var(--brand)",fontWeight:700}}>₹100</span>
          </div>
        </Card>
      );
    }

    // ── DL Result Card ────────────────────────────────────────
    if(result.searchType==="dl"&&result.data){
      const d=result.data;
      const isExpired=d.isExpired;
      const src=result.source==="db"?"DB Cache":"Live API";

      const generateDLReport=()=>{
        const dt=new Date().toLocaleString("en-IN");
        const photoHtml=d.profile_image?`<img src="data:image/jpeg;base64,${d.profile_image}" style="width:100px;height:120px;object-fit:cover;border-radius:8px;border:2px solid #e2e8f0;"/>`:
          `<div style="width:100px;height:120px;border-radius:8px;background:#f1f5f9;border:2px solid #e2e8f0;display:flex;align-items:center;justify-content:center;font-size:32px;">🪪</div>`;
        const html=`<!DOCTYPE html><html><head><meta charset="UTF-8"/>
<title>DL Verification — ${d.name}</title>
<style>
*{margin:0;padding:0;box-sizing:border-box;}
body{font-family:'Segoe UI',Arial,sans-serif;color:#1a1a2e;background:#fff;}
@page{margin:12mm;size:A4;}
@media print{body{-webkit-print-color-adjust:exact;print-color-adjust:exact;}.no-print{display:none;}}
</style></head><body>
<div class="no-print" style="background:#0a0a1a;padding:10px 20px;text-align:right;">
  <button onclick="window.print()" style="background:linear-gradient(90deg,#ff3131,#ff914d);color:#fff;border:none;padding:8px 20px;border-radius:8px;cursor:pointer;font-size:13px;font-weight:700;">⬇ Download PDF</button>
  <span style="color:#fff;font-size:11px;margin-left:10px;opacity:.6;">Print dialog → Save as PDF</span>
</div>
<div style="background:linear-gradient(135deg,#0a0a1a,#1a1a2e);padding:20px 32px;display:flex;justify-content:space-between;align-items:center;">
  <div>
    <img src="${LOGO1}" style="height:32px;margin-bottom:4px;" alt="Meitho BGV"/>
    <div style="font-size:9px;color:#0cc0df;letter-spacing:2px;font-weight:700;">DRIVING LICENSE VERIFICATION REPORT</div>
  </div>
  <div style="text-align:right;">
    <div style="font-size:16px;font-weight:900;color:#0cc0df;font-family:monospace;">${d.dl_number}</div>
    <div style="font-size:9px;color:rgba(255,255,255,0.4);">${dt}</div>
    <div style="font-size:9px;padding:3px 8px;border-radius:99px;background:${isExpired?"rgba(255,49,49,0.2)":"rgba(255,49,49,0.2)"};color:${isExpired?"#ff3131":"#ff3131"};display:inline-block;margin-top:4px;font-weight:700;">${isExpired?"⚠ EXPIRED":"✅ VALID"}</div>
  </div>
</div>
<div style="background:#0cc0df;padding:5px 32px;font-size:9px;color:#fff;font-weight:700;letter-spacing:.5px;display:flex;justify-content:space-between;">
  <span>MEITHO BGV PLATFORM</span><span>meitho.com · help@meitho.com · 8300016265</span>
</div>
<div style="padding:24px 32px;">
  <div style="display:flex;gap:24px;margin-bottom:24px;align-items:flex-start;">
    ${photoHtml}
    <div style="flex:1;">
      <div style="font-size:26px;font-weight:900;color:#0a0a1a;margin-bottom:4px;">${d.name||"—"}</div>
      <div style="font-size:14px;font-family:monospace;color:#64748b;margin-bottom:12px;">${d.dl_number}</div>
      <div style="display:flex;gap:8px;flex-wrap:wrap;">
        ${d.state?`<span style="padding:3px 10px;border-radius:99px;background:#0cc0df15;border:1px solid #0cc0df30;color:#0cc0df;font-size:11px;font-weight:700;">📍 ${d.state}</span>`:""}
        ${d.blood_group?`<span style="padding:3px 10px;border-radius:99px;background:#ef444415;border:1px solid #ef444430;color:#ef4444;font-size:11px;font-weight:700;">🩸 ${d.blood_group}</span>`:""}
        <span style="padding:3px 10px;border-radius:99px;background:${isExpired?"#ff313115":"#ff313115"};border:1px solid ${isExpired?"#ff313130":"#ff313130"};color:${isExpired?"#ff3131":"#ff3131"};font-size:11px;font-weight:700;">${isExpired?"⚠ EXPIRED":"✅ VALID LICENSE"}</span>
      </div>
    </div>
  </div>
  <div style="font-size:10px;font-weight:800;letter-spacing:1.5px;text-transform:uppercase;color:#0cc0df;border-bottom:2px solid #0cc0df;padding-bottom:5px;margin-bottom:14px;">Personal Details</div>
  <div style="display:grid;grid-template-columns:1fr 1fr 1fr;gap:12px;margin-bottom:20px;">
    ${[["Full Name",d.name],["Date of Birth",d.dob],["Father/Spouse",d.father_name],["Blood Group",d.blood_group],["Nationality","Indian"]].filter(([,v])=>v).map(([l,v])=>`
    <div style="background:#f8fafc;border-radius:8px;padding:10px 12px;border-left:3px solid #0cc0df;">
      <div style="font-size:8px;color:#94a3b8;font-weight:700;text-transform:uppercase;margin-bottom:3px;">${l}</div>
      <div style="font-size:13px;font-weight:700;color:#0a0a1a;">${v}</div>
    </div>`).join("")}
  </div>
  <div style="font-size:10px;font-weight:800;letter-spacing:1.5px;text-transform:uppercase;color:#0cc0df;border-bottom:2px solid #0cc0df;padding-bottom:5px;margin-bottom:14px;">License Details</div>
  <div style="display:grid;grid-template-columns:1fr 1fr 1fr;gap:12px;margin-bottom:20px;">
    ${[["License Number",d.dl_number],["Issue Date",d.doi],["Expiry Date",d.doe],["Issuing RTO",d.ola_name],["State",d.state],["Status",isExpired?"EXPIRED":"VALID"]].filter(([,v])=>v).map(([l,v])=>`
    <div style="background:#f8fafc;border-radius:8px;padding:10px 12px;border-left:3px solid ${isExpired&&l==="Status"?"#ff3131":"#ff3131"};">
      <div style="font-size:8px;color:#94a3b8;font-weight:700;text-transform:uppercase;margin-bottom:3px;">${l}</div>
      <div style="font-size:13px;font-weight:700;color:${isExpired&&l==="Status"?"#ff3131":"#0a0a1a"};font-family:${l==="License Number"?"monospace":"inherit"};">${v}</div>
    </div>`).join("")}
  </div>
  ${d.permanent_address?`
  <div style="font-size:10px;font-weight:800;letter-spacing:1.5px;text-transform:uppercase;color:#0cc0df;border-bottom:2px solid #0cc0df;padding-bottom:5px;margin-bottom:14px;">Registered Address</div>
  <div style="background:#f8fafc;border-radius:8px;padding:12px 16px;border-left:3px solid #0cc0df;margin-bottom:20px;">
    <div style="font-size:13px;font-weight:600;color:#0a0a1a;">${d.permanent_address}</div>
  </div>`:""}
  ${d.vehicle_classes?.length>0?`
  <div style="font-size:10px;font-weight:800;letter-spacing:1.5px;text-transform:uppercase;color:#0cc0df;border-bottom:2px solid #0cc0df;padding-bottom:5px;margin-bottom:14px;">Authorised Vehicle Classes</div>
  <div style="display:flex;gap:8px;flex-wrap:wrap;margin-bottom:20px;">
    ${d.vehicle_classes.map(vc=>`<span style="padding:6px 16px;border-radius:8px;background:#0cc0df15;border:1px solid #0cc0df30;color:#0cc0df;font-size:12px;font-weight:700;">🚗 ${vc}</span>`).join("")}
  </div>`:""}
  ${isExpired?`<div style="background:#ff313108;border:1px solid #ff313130;border-radius:10px;padding:14px 16px;margin-bottom:20px;">
    <div style="font-size:13px;font-weight:700;color:#ff3131;">⚠️ License Expired</div>
    <div style="font-size:12px;color:#64748b;margin-top:4px;">This driving license expired on ${d.doe}. Candidate must renew before driving.</div>
  </div>`:""}
</div>
<div style="background:#0a0a1a;padding:14px 32px;display:flex;justify-content:space-between;align-items:center;margin-top:8px;">
  <div style="font-size:10px;color:rgba(255,255,255,0.4);">Source: ${src} · Powered by Surepass</div>
  <div style="font-size:10px;color:#0cc0df;font-weight:600;">meitho.com · help@meitho.com</div>
  <div style="font-size:10px;color:rgba(255,255,255,0.4);">© ${new Date().getFullYear()} Meitho BGV · Confidential</div>
</div>
</body></html>`;
        const win=window.open("","_blank","width=1000,height=800");
        if(!win){addToast("Allow popups to download report","error");return;}
        win.document.write(html);win.document.close();win.focus();
        setTimeout(()=>win.print(),800);
      };

      return(
        <Card className="fu" style={{marginTop:20,border:`1px solid ${isExpired?"rgba(255,49,49,0.3)":"rgba(255,49,49,0.3)"}`}}>
          {/* Header */}
          <div style={{display:"flex",gap:16,alignItems:"flex-start",marginBottom:18,paddingBottom:16,borderBottom:"1px solid var(--b1)"}}>
            {d.has_image&&d.profile_image?(
              <img src={`data:image/jpeg;base64,${d.profile_image}`} alt="DL Photo"
                style={{width:72,height:90,objectFit:"cover",borderRadius:10,border:"2px solid var(--b1)",flexShrink:0}}/>
            ):(
              <div style={{width:72,height:90,borderRadius:10,background:"var(--s2)",border:"2px solid var(--b1)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:28,flexShrink:0}}>🪪</div>
            )}
            <div style={{flex:1}}>
              <div style={{fontFamily:FFD,fontSize:22,fontWeight:800,color:"var(--text)",marginBottom:3}}>{d.name||"—"}</div>
              <div style={{fontSize:13,color:"var(--muted)",marginBottom:8,fontFamily:FFM,letterSpacing:1}}>{d.dl_number}</div>
              <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                <Badge color={isExpired?"#ff3131":"var(--green)"}>{isExpired?"⚠️ Expired":"✅ Valid License"}</Badge>
                {d.state&&<Badge color="var(--brand)">📍 {d.state}</Badge>}
                {d.blood_group&&<Badge color="#ef4444">🩸 {d.blood_group}</Badge>}
                <Badge color={result.source==="db"?"#8b5cf6":"var(--brand)"} style={{fontSize:9}}>{result.source==="db"?"DB Cache":"Live API"}</Badge>
              </div>
            </div>
            <button onClick={generateDLReport} style={{background:"linear-gradient(90deg,#ff3131,#ff914d)",border:"none",borderRadius:8,color:"#fff",padding:"8px 16px",cursor:"pointer",fontSize:12,fontWeight:700,fontFamily:FF,flexShrink:0}}>↓ Report</button>
          </div>

          {/* Details Grid */}
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:14}} className="mg1">
            {[
              ["DL Number",d.dl_number,FFM],
              ["Full Name",d.name],
              ["Date of Birth",d.dob],
              ["Father / Spouse",d.father_name],
              ["Blood Group",d.blood_group],
              ["Issue Date",d.doi],
              ["Expiry Date",d.doe],
              ["Issuing RTO",d.ola_name],
              ["State",d.state],
            ].filter(([,v])=>v).map(([l,v,ff])=>(
              <div key={l} style={{background:"var(--s2)",borderRadius:8,padding:"10px 12px"}}>
                <div style={{fontSize:9,color:"var(--muted)",fontWeight:700,textTransform:"uppercase",marginBottom:3}}>{l}</div>
                <div style={{fontSize:13,fontWeight:600,color:"var(--text)",fontFamily:ff||FF}}>{v}</div>
              </div>
            ))}
          </div>

          {/* Address */}
          {d.permanent_address&&(
            <div style={{background:"var(--s2)",borderRadius:8,padding:"10px 12px",marginBottom:12}}>
              <div style={{fontSize:9,color:"var(--muted)",fontWeight:700,textTransform:"uppercase",marginBottom:3}}>Registered Address</div>
              <div style={{fontSize:13,fontWeight:600,color:"var(--text)"}}>{d.permanent_address}</div>
            </div>
          )}

          {/* Vehicle Classes */}
          {d.vehicle_classes?.length>0&&(
            <div style={{marginBottom:12}}>
              <div style={{fontSize:11,color:"var(--muted)",fontWeight:700,textTransform:"uppercase",marginBottom:8}}>Authorised Vehicle Classes</div>
              <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                {d.vehicle_classes.map((vc,i)=>(
                  <Badge key={i} color="var(--brand)" style={{fontSize:11,padding:"4px 12px"}}>🚗 {vc}</Badge>
                ))}
              </div>
            </div>
          )}

          {/* Expired Warning */}
          {isExpired&&(
            <div style={{background:"rgba(255,49,49,0.08)",border:"1px solid rgba(255,49,49,0.2)",borderRadius:10,padding:"12px 16px",fontSize:13,color:"#ff3131",fontWeight:600,marginBottom:10}}>
              ⚠️ License expired on {d.doe}. Candidate must renew before driving.
            </div>
          )}

          <div style={{marginTop:10,fontSize:11,color:"var(--muted)",borderTop:"1px solid var(--b1)",paddingTop:10,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
            <span>🚗 Driving License Verification · Powered by Meitho</span>
            <span style={{color:"var(--brand)",fontWeight:700}}>₹50</span>
          </div>
        </Card>
      );
    }

    // ── Criminal Check Result ──────────────────────────────────
    if(searchType==="criminal"&&result.data){
      const emp=result.data;
      const isClear=emp.criminalCheck==="clear";
      return(
        <Card className="fu" style={{marginTop:20,border:`1px solid ${isClear?"rgba(255,49,49,0.3)":"rgba(255,49,49,0.3)"}`}}>
          <div style={{display:"flex",gap:14,alignItems:"flex-start",marginBottom:16}}>
            <div style={{width:52,height:52,borderRadius:12,background:isClear?"rgba(255,49,49,0.12)":"rgba(255,49,49,0.12)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:28,flexShrink:0}}>{isClear?"✅":"⚠️"}</div>
            <div>
              <div style={{fontFamily:FFD,fontSize:18,fontWeight:800,color:isClear?"var(--green)":"#ff3131"}}>{isClear?"No Criminal Records Found":"Records Found"}</div>
              <div style={{fontSize:13,color:"var(--muted)",marginTop:2}}>Name searched: <strong>{emp.name}</strong> · eCourt Database</div>
              <div style={{fontSize:12,color:"var(--muted)",marginTop:2}}>Search date: {emp.searchDate}</div>
            </div>
          </div>
          {isClear?(
            <div style={{background:"rgba(255,49,49,0.06)",border:"1px solid rgba(255,49,49,0.2)",borderRadius:10,padding:"12px 16px",fontSize:13,color:"var(--green)",fontWeight:600}}>
              ✅ No cases found in eCourt database across all Indian courts for this name.
            </div>
          ):(
            <div>
              <div style={{background:"rgba(255,49,49,0.06)",border:"1px solid rgba(255,49,49,0.2)",borderRadius:10,padding:"12px 16px",marginBottom:12}}>
                <div style={{fontSize:14,fontWeight:700,color:"#ff3131",marginBottom:4}}>⚠ {emp.totalCases} case(s) found</div>
                <div style={{fontSize:12,color:"var(--muted)"}}>Please review the details below carefully.</div>
              </div>
              {emp.cases&&emp.cases.map((c,i)=>(
                <div key={i} style={{background:"var(--s2)",borderRadius:8,padding:"12px",marginBottom:8,border:"1px solid var(--b1)"}}>
                  <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
                    {[["Case Number",c.case_number||c.cnr_number||"—"],["Court",c.court_name||c.court||"—"],["Filing Date",c.filing_date||c.date||"—"],["Status",c.case_status||c.status||"—"],["Petitioner",c.petitioner_name||c.petitioner||"—"],["Acts",c.acts||c.section||"—"]].map(([l,v])=>(
                      <div key={l}>
                        <div style={{fontSize:9,color:"var(--muted)",fontWeight:700,textTransform:"uppercase"}}>{l}</div>
                        <div style={{fontSize:12,fontWeight:600,color:"var(--text)"}}>{v}</div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
          <div style={{marginTop:12,fontSize:11,color:"var(--muted)"}}>
            ⚠ Results from eCourt public database. Always verify with official court records for legal proceedings.
          </div>
        </Card>
      );
    }

    // ── Education Check Result ─────────────────────────────────
    if(searchType==="education"&&result.data){
      const emp=result.data;
      return(
        <Card className="fu" style={{marginTop:20,border:"1px solid rgba(12,192,223,0.3)"}}>
          <div style={{display:"flex",gap:14,alignItems:"flex-start",marginBottom:16}}>
            <div style={{width:52,height:52,borderRadius:12,background:"rgba(12,192,223,0.12)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:28,flexShrink:0}}>🎓</div>
            <div>
              <div style={{fontFamily:FFD,fontSize:18,fontWeight:800,color:"var(--brand)"}}>Education Verification Initiated</div>
              <div style={{fontSize:13,color:"var(--muted)",marginTop:2}}>Candidate: <strong>{emp.name}</strong></div>
              <div style={{fontSize:11,color:"var(--muted)",fontFamily:FFM,marginTop:2}}>ID: {emp.checkId}</div>
            </div>
          </div>
          <div style={{background:"rgba(12,192,223,0.06)",border:"1px solid rgba(12,192,223,0.2)",borderRadius:10,padding:"16px",marginBottom:12}}>
            <div style={{fontSize:13,fontWeight:600,color:"var(--text)",marginBottom:8}}>Next Steps:</div>
            {["Request candidate to provide original degree/diploma certificates","Verify with the issuing university/institution directly","Cross-check with DigiLocker if documents are available","Use Meitho Reference Check to verify employment history"].map((s,i)=>(
              <div key={i} style={{display:"flex",gap:8,fontSize:12,color:"var(--sub)",marginBottom:6}}>
                <span style={{color:"var(--brand)",fontWeight:700,flexShrink:0}}>{i+1}.</span>{s}
              </div>
            ))}
          </div>
          <div style={{background:"rgba(255,145,77,0.06)",border:"1px solid rgba(255,145,77,0.2)",borderRadius:8,padding:"10px 14px",fontSize:12,color:"#ff914d"}}>
            📋 Automated education API verification coming soon. Currently requires manual document verification.
          </div>
        </Card>
      );
    }

    // DigiLocker verify-account result
    if(result.data?.digilockerId!==undefined&&!result.digilockerFlow) return(
      <Card className="fu" style={{marginTop:20,border:"1px solid rgba(12,192,223,0.3)"}}>
        <div style={{display:"flex",gap:14,alignItems:"flex-start"}}>
          <div style={{width:48,height:48,borderRadius:12,background:result.data?.accountExists?"rgba(255,49,49,0.15)":"rgba(255,49,49,0.15)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:24,flexShrink:0}}>{result.data?.accountExists?"✅":"❌"}</div>
          <div style={{flex:1}}>
            <div style={{fontFamily:FFD,fontSize:16,fontWeight:700,color:"var(--text)",marginBottom:6}}>
              DigiLocker Account {result.data?.accountExists?"Verified":"Not Found"}
            </div>
            <div style={{fontSize:13,color:"var(--muted)",marginBottom:12,lineHeight:1.7}}>{result.data?.message}</div>
            {result.data?.accountExists&&(
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
                <div style={{background:"var(--s2)",borderRadius:8,padding:"10px 12px"}}>
                  <div style={{fontSize:10,color:"var(--muted)",fontWeight:700,marginBottom:3}}>AADHAAR (MASKED)</div>
                  <div style={{fontSize:13,fontWeight:600,color:"var(--text)",fontFamily:FFM}}>{result.data?.maskedAadhaar||maskAadhaar(q)}</div>
                </div>
                <div style={{background:"var(--s2)",borderRadius:8,padding:"10px 12px"}}>
                  <div style={{fontSize:10,color:"var(--muted)",fontWeight:700,marginBottom:3}}>DIGILOCKER ID</div>
                  <div style={{fontSize:11,fontWeight:600,color:"var(--text)",fontFamily:FFM,wordBreak:"break-all"}}>{result.data?.digilockerId||"—"}</div>
                </div>
              </div>
            )}
          </div>
        </div>
      </Card>
    );
    if(result.digilockerFlow) return(
      <Card className="fu" style={{marginTop:20,border:"1px solid rgba(12,192,223,0.3)"}}>
        <div style={{display:"flex",gap:14,alignItems:"flex-start"}}>
          <div style={{width:48,height:48,borderRadius:12,background:"rgba(12,192,223,0.15)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:24,flexShrink:0}}>🔐</div>
          <div style={{flex:1}}>
            <div style={{fontFamily:FFD,fontSize:16,fontWeight:700,color:"var(--text)",marginBottom:6}}>DigiLocker Verification Initiated</div>
            <div style={{fontSize:13,color:"var(--muted)",marginBottom:12,lineHeight:1.7}}>
              <strong style={{color:"var(--text)"}}>How it works:</strong><br/>
              1. Click <strong style={{color:"var(--brand)"}}>↗ Open DigiLocker</strong> — share this link with the candidate<br/>
              2. Candidate logs in with their Aadhaar-linked mobile number<br/>
              3. They approve sharing their Aadhaar details<br/>
              4. Come back here and click <strong style={{color:"var(--green)"}}>↓ Check & Download</strong><br/>
              <br/>
              <span style={{color:"#ff914d",fontSize:12}}>⚠️ Note: DigiLocker link opens on a Government/Surepass portal — this is completely normal, secure and legally compliant. It works like how UPI opens your bank app.</span><br/>
              Link expires in <strong style={{color:"#ff914d"}}>{Math.round((result.data?.expiryMinutes||1800)/60)} minutes</strong>.
            </div>
            {/* Link box */}
            {result.data?.link&&<div style={{background:"rgba(12,192,223,0.06)",border:"1px solid rgba(12,192,223,0.2)",borderRadius:8,padding:"10px 14px",marginBottom:12}}>
              <div style={{fontSize:10,color:"var(--muted)",fontWeight:700,marginBottom:4,textTransform:"uppercase"}}>DigiLocker Link</div>
              <div style={{fontSize:11,color:"var(--brand)",fontFamily:FFM,wordBreak:"break-all",marginBottom:8}}>{result.data.link}</div>
              <button onClick={()=>{navigator.clipboard?.writeText(result.data.link);addToast("Link copied!","success");}} style={{background:"var(--brand)",border:"none",borderRadius:6,color:"#fff",padding:"5px 12px",cursor:"pointer",fontSize:11,fontWeight:700,fontFamily:FF}}>📋 Copy Link</button>
            </div>}
            {/* Client ID */}
            <div style={{background:"var(--s2)",border:"1px solid var(--b1)",borderRadius:8,padding:"10px 14px",fontSize:12,color:"var(--muted)",marginBottom:12}}>
              <strong style={{color:"var(--text)"}}>Client ID:</strong> <span style={{fontFamily:FFM,color:"var(--brand)"}}>{result.data?.clientId||"—"}</span>
              <div style={{marginTop:4,fontSize:11}}>Save this ID — you will need it to download Aadhaar after candidate completes verification</div>
            </div>
            <div style={{display:"flex",gap:10,flexWrap:"wrap"}}>
              {result.data?.link&&<Btn onClick={()=>window.open(result.data?.link,"_blank")} style={{fontSize:12}}>↗ Open DigiLocker</Btn>}
              <Btn onClick={async()=>{
                if(!result.data?.clientId){addToast("No client ID available","error");return;}
                addToast("Checking verification status...","amber");
                try{
                  const dlHeaders=await getAuthedHeaders();
                  const r=await fetch("https://aadhaardownload-mtvra6khyq-uc.a.run.app",{
                    method:"POST",headers:dlHeaders,
                    body:JSON.stringify({client_id:result.data.clientId})
                  });
                  const json=await r.json();
                  if((json.status_code===200||json.success)&&json.data){
                    const d=json.data;
                    const aadhaarData={
                      name:d.name||"",
                      dob:d.dob||"",
                      gender:d.gender||"",
                      address:d.address||"",
                      fatherName:d.father_name||"",
                      mobile:d.mobile||"",
                      masked_aadhaar:d.masked_aadhaar||"",
                      photo:d.photo||"",
                      zip:d.zip||"",
                      aadhaar:q.trim(),
                      verified:true,
                    };
                    setResult({...result,digilockerFlow:false,found:true,source:"DigiLocker",data:aadhaarData});
                    addToast("✅ Aadhaar verified successfully!","success");
                  } else {
                    addToast(json.message||"Candidate has not completed DigiLocker yet. Ask them to open the link and verify.","amber");
                  }
                }catch(e){addToast("Download failed: "+e.message,"error");}
              }} style={{fontSize:12,background:"linear-gradient(90deg,var(--green),#059669)"}}>↓ Check & Download Aadhaar</Btn>
              <BtnGhost onClick={()=>{setResult(null);setSearched(false);}} style={{fontSize:12}}>New Search</BtnGhost>
            </div>
          </div>
        </div>
      </Card>
    );
    if(result.error&&!result.found) return(
      <Card className="fu" style={{textAlign:"center",padding:"40px 20px",marginTop:20,border:"1px solid rgba(255,49,49,0.2)"}}>
        <div style={{width:56,height:56,background:"rgba(255,49,49,0.1)",borderRadius:14,display:"flex",alignItems:"center",justifyContent:"center",margin:"0 auto 16px",fontSize:24}}>⚠️</div>
        <div style={{fontFamily:FFD,fontSize:18,fontWeight:700,color:"var(--text)",marginBottom:8}}>API Error</div>
        <div style={{fontSize:13,color:"var(--muted)",marginBottom:16}}>{result.error}</div>
        <div style={{fontSize:11,color:"var(--muted)"}}>Check your connection and try again. If issue persists contact Meitho support at help@meitho.com</div>
      </Card>
    );
    if(!result.found) return(
      <Card className="fu" style={{textAlign:"center",padding:"40px 20px",marginTop:20}}>
        <div style={{width:56,height:56,background:"var(--red)10",borderRadius:14,display:"flex",alignItems:"center",justifyContent:"center",margin:"0 auto 16px",fontSize:24}}>🔍</div>
        <div style={{fontFamily:FFD,fontSize:18,fontWeight:700,color:"var(--text)",marginBottom:8}}>No Record Found</div>
        <div style={{fontSize:13,color:"var(--muted)",marginBottom:20}}>No data found for this {searchType.toUpperCase()} in {apiSource==="db"?"Meitho database":"EPFO/Government records"}</div>
        {searchType==="uan"&&(
          <div style={{background:"var(--amber)10",border:"1px solid var(--amber)30",borderRadius:8,padding:"12px 16px",fontSize:13,color:"var(--amber)"}}>
            💡 Try searching by <strong>Aadhaar number</strong> instead
          </div>
        )}
      </Card>
    );

    const emp=result.fromDB?result.employee:result.data;
    const history=emp?.employmentHistory||[];

    return(
      <div style={{marginTop:20,display:"flex",flexDirection:"column",gap:16}}>
        {/* Source badge */}
        <div style={{display:"flex",alignItems:"center",gap:8}}>
          <div style={{background:apiSource==="db"?"var(--green)15":"var(--acc)15",border:`1px solid ${apiSource==="db"?"var(--green)30":"var(--acc)30"}`,borderRadius:99,padding:"4px 12px",fontSize:11,fontWeight:700,color:apiSource==="db"?"var(--green)":"var(--brand)"}}>
            {apiSource==="db"?"✓ From Meitho Database":"⚡ Live Verification"}
          </div>
          <div style={{fontSize:11,color:"var(--muted)"}}>{new Date().toLocaleString("en-IN")}</div>
        </div>

        {/* Identity Card */}
        <Card className="fu1">
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",flexWrap:"wrap",gap:12,marginBottom:16,paddingBottom:14,borderBottom:"1px solid var(--b1)"}}>
            <div style={{display:"flex",gap:14,alignItems:"center"}}>
              <div style={{width:52,height:52,borderRadius:14,background:"var(--brand)15",display:"flex",alignItems:"center",justifyContent:"center",fontSize:24}}>👤</div>
              <div>
                <div style={{fontFamily:FFD,fontSize:20,fontWeight:700,color:"var(--text)",marginBottom:3}}>{emp?.name||(emp?.employmentHistory&&emp.employmentHistory[0]?.name)||"—"}</div>
                <div style={{fontSize:13,color:"var(--muted)"}}>DOB: {emp?.dob||"—"}</div>
              </div>
            </div>
            <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
              {(emp?.verified||emp?.aadhaarLinked)&&<Badge color="var(--green)" style={{fontSize:11}}>✓ Verified</Badge>}
              <Badge color="var(--brand)" style={{fontSize:11}}>{result.source||"EPFO"}</Badge>
            </div>
          </div>

          {/* Less info notice */}
          {emp?.lessInfo&&(
            <div style={{background:"rgba(255,145,77,0.08)",border:"1px solid rgba(255,145,77,0.25)",borderRadius:8,padding:"10px 14px",marginBottom:12,fontSize:12,color:"#ff914d"}}>
              ℹ️ <strong>Limited data available</strong> — Income Tax database has partial records for this PAN. Address, email and phone may not be available. This is normal for some PANs.
            </div>
          )}
          <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(180px,1fr))",gap:12,marginBottom:16}}>
            {emp?.uan&&<div style={{background:"var(--s2)",borderRadius:8,padding:"10px 12px"}}>
              <div style={{fontSize:10,color:"var(--muted)",fontWeight:700,marginBottom:3,textTransform:"uppercase"}}>UAN</div>
              <div style={{fontSize:14,fontWeight:600,color:"var(--text)",fontFamily:FFM}}>{emp.uan}</div>
              {emp.aadhaarLinked&&<div style={{fontSize:10,color:"var(--green)",marginTop:2}}>✓ Aadhaar Linked</div>}
            </div>}
            {emp?.aadhaar&&<div style={{background:"var(--s2)",borderRadius:8,padding:"10px 12px"}}>
              <div style={{fontSize:10,color:"var(--muted)",fontWeight:700,marginBottom:3,textTransform:"uppercase"}}>Aadhaar</div>
              <div style={{fontSize:14,fontWeight:600,color:"var(--text)",fontFamily:FFM}}>{maskAadhaar(emp.aadhaar)}</div>
              {emp.verified&&<div style={{fontSize:10,color:"var(--green)",marginTop:2}}>✓ Verified</div>}
            </div>}
            {(emp?.pan||emp?.pan_number)&&<div style={{background:"var(--s2)",borderRadius:8,padding:"10px 12px"}}>
              <div style={{fontSize:10,color:"var(--muted)",fontWeight:700,marginBottom:3,textTransform:"uppercase"}}>PAN</div>
              <div style={{fontSize:14,fontWeight:600,color:"var(--text)",fontFamily:FFM}}>{emp.pan||emp.pan_number}</div>
              <div style={{fontSize:10,color:"var(--green)",marginTop:2}}>✓ {emp.status||"Active"}</div>
            </div>}
            {emp?.gender&&<div style={{background:"var(--s2)",borderRadius:8,padding:"10px 12px"}}>
              <div style={{fontSize:10,color:"var(--muted)",fontWeight:700,marginBottom:3,textTransform:"uppercase"}}>Gender</div>
              <div style={{fontSize:14,fontWeight:600,color:"var(--text)"}}>{emp.gender==="M"?"Male":emp.gender==="F"?"Female":emp.gender||"—"}</div>
            </div>}
            {emp?.mobile&&<div style={{background:"var(--s2)",borderRadius:8,padding:"10px 12px"}}>
              <div style={{fontSize:10,color:"var(--muted)",fontWeight:700,marginBottom:3,textTransform:"uppercase"}}>Phone</div>
              <div style={{fontSize:13,fontWeight:600,color:"var(--text)",fontFamily:FFM}}>{emp.mobile}</div>
            </div>}
            {emp?.email&&<div style={{background:"var(--s2)",borderRadius:8,padding:"10px 12px"}}>
              <div style={{fontSize:10,color:"var(--muted)",fontWeight:700,marginBottom:3,textTransform:"uppercase"}}>Email</div>
              <div style={{fontSize:12,color:"var(--sub)"}}>{emp.email}</div>
            </div>}
            {emp?.masked_aadhaar&&<div style={{background:"var(--s2)",borderRadius:8,padding:"10px 12px"}}>
              <div style={{fontSize:10,color:"var(--muted)",fontWeight:700,marginBottom:3,textTransform:"uppercase"}}>Masked Aadhaar</div>
              <div style={{fontSize:13,fontWeight:600,color:"var(--text)",fontFamily:FFM}}>{emp.masked_aadhaar}</div>
              {emp.aadhaar_linked&&<div style={{fontSize:10,color:"var(--green)",marginTop:2}}>✓ Linked to PAN</div>}
            </div>}
            {emp?.address&&emp.address.trim&&emp.address.trim()!==""&&<div style={{background:"var(--s2)",borderRadius:8,padding:"10px 12px",gridColumn:"1/-1"}}>
              <div style={{fontSize:10,color:"var(--muted)",fontWeight:700,marginBottom:3,textTransform:"uppercase"}}>Address</div>
              <div style={{fontSize:13,color:"var(--sub)"}}>{typeof emp.address==="object"?(emp.address.full||[emp.address.line_1,emp.address.line_2,emp.address.city,emp.address.state].filter(Boolean).join(", ")):emp.address}</div>
            </div>}
            {emp?.lessInfo&&<div style={{background:"rgba(255,145,77,0.06)",border:"1px solid rgba(255,145,77,0.2)",borderRadius:8,padding:"10px 12px",gridColumn:"1/-1"}}>
              <div style={{fontSize:11,color:"#ff914d"}}>⚠ Limited data returned by Income Tax API. Address and additional details not available for this PAN.</div>
            </div>}
            {emp?.email&&<div style={{background:"var(--s2)",borderRadius:8,padding:"10px 12px"}}>
              <div style={{fontSize:10,color:"var(--muted)",fontWeight:700,marginBottom:3,textTransform:"uppercase"}}>Email</div>
              <div style={{fontSize:12,color:"var(--sub)"}}>{emp.email}</div>
            </div>}
            {(emp?.mobile||emp?.phone_number)&&<div style={{background:"var(--s2)",borderRadius:8,padding:"10px 12px"}}>
              <div style={{fontSize:10,color:"var(--muted)",fontWeight:700,marginBottom:3,textTransform:"uppercase"}}>Phone</div>
              <div style={{fontSize:13,fontWeight:600,color:"var(--text)",fontFamily:FFM}}>{emp.mobile||emp.phone_number}</div>
            </div>}
          </div>

          {/* Salary from our DB */}
          {result.fromDB&&emp?.lastSalary&&(
            <div style={{background:"var(--green)08",border:"1px solid var(--green)20",borderRadius:8,padding:"10px 14px",marginBottom:12,display:"flex",alignItems:"center",gap:10}}>
              <span style={{fontSize:13,color:"var(--muted)"}}>Last Known Salary:</span>
              <span style={{fontSize:16,fontWeight:700,color:"var(--green)",fontFamily:FFD}}>{fmtSalary(emp.lastSalary)}</span>
              <span style={{fontSize:11,color:"var(--muted)"}}>per month</span>
            </div>
          )}
        </Card>

        {/* Employment History */}
        {history.length>0&&(
          <Card className="fu2">
            <div style={{fontFamily:FFD,fontSize:15,fontWeight:700,color:"var(--text)",marginBottom:16,display:"flex",alignItems:"center",gap:8}}>
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><circle cx="8" cy="8" r="6" stroke="var(--brand)" strokeWidth="1.5"/><path d="M8 5v3.5l2 2" stroke="var(--brand)" strokeWidth="1.5" strokeLinecap="round"/></svg>
              Employment History ({history.length} {history.length===1?"company":"companies"})
            </div>
            <div style={{display:"flex",flexDirection:"column",gap:12}}>
              {history.map((job,i)=>{
                const colors=["var(--brand)","var(--purple)","var(--green)","var(--amber)","var(--red)"];
                const c=colors[i%colors.length];
                return(
                  <div key={i} style={{display:"flex",gap:14,padding:"14px 16px",background:"var(--s2)",borderRadius:10,border:"1px solid var(--b1)",borderLeft:`3px solid ${c}`}}>
                    <div style={{width:28,height:28,borderRadius:"50%",background:c+"20",display:"flex",alignItems:"center",justifyContent:"center",fontSize:12,fontWeight:700,color:c,flexShrink:0}}>{i+1}</div>
                    <div style={{flex:1}}>
                      <div style={{display:"flex",justifyContent:"space-between",flexWrap:"wrap",gap:8,marginBottom:6}}>
                        <div>
                          <div style={{fontFamily:FFD,fontSize:14,fontWeight:700,color:"var(--text)",marginBottom:2}}>{job.company}</div>
                          <div style={{fontSize:12,color:"var(--sub)"}}>{job.designation}</div>
                        </div>
                        <div style={{textAlign:"right"}}>
                          <div style={{fontSize:12,fontWeight:600,color:"var(--text)",fontFamily:FFM}}>{job.doj} → {job.dor||"Present"}</div>
                          {job.pf&&<Badge color={job.pf==="Active"?"var(--green)":"var(--muted)"} style={{fontSize:9,marginTop:3}}>PF: {job.pf}</Badge>}
                        </div>
                      </div>
                      {job.salary&&<div style={{fontSize:12,color:"var(--green)",fontWeight:600}}>₹{parseInt(job.salary).toLocaleString("en-IN")}/mo</div>}
                    </div>
                  </div>
                );
              })}
            </div>
            {/* Summary */}
            {(()=>{
              // Sort by DOJ oldest first
              const sorted=[...history].sort((a,b)=>{
                const da=new Date(a.doj||"1900-01-01");
                const db=new Date(b.doj||"1900-01-01");
                return da-db;
              });
              const firstJob=sorted[0];
              const lastJob=sorted[sorted.length-1];
              const isValidDt=(d)=>{if(!d||d==="Present"||d==="")return false;const yr=parseInt((d+"").split("-")[0]||"0");return yr>1950&&yr<=new Date().getFullYear()+1;};
              const hasExitDate=isValidDt(lastJob?.dor);
              const startDate=firstJob?new Date(firstJob.doj||""):null;
              const endDate=hasExitDate?new Date(lastJob.dor):new Date();
              const totalYears=startDate&&!isNaN(startDate)&&startDate.getFullYear()>1950?
                Math.round((endDate-startDate)/(1000*60*60*24*365)*10)/10:
                (emp?.totalExperience||0);
              // Find current/latest company (no exit date = current)
              const currentJob=sorted.slice().reverse().find(j=>!j.dor||j.dor===""||j.dor==="Present")||lastJob;
              return(
                <div>
                  <div style={{marginTop:14,padding:"12px 16px",background:"var(--brand)06",border:"1px solid var(--brand)20",borderRadius:8,display:"flex",gap:20,flexWrap:"wrap"}}>
                    <div><div style={{fontSize:10,color:"var(--muted)",fontWeight:700,marginBottom:2}}>TOTAL COMPANIES</div><div style={{fontSize:16,fontWeight:700,color:"var(--brand)",fontFamily:FFD}}>{history.length}</div></div>
                    <div><div style={{fontSize:10,color:"var(--muted)",fontWeight:700,marginBottom:2}}>TOTAL EXPERIENCE</div><div style={{fontSize:16,fontWeight:700,color:"var(--brand)",fontFamily:FFD}}>{totalYears} yrs</div></div>
                    <div><div style={{fontSize:10,color:"var(--muted)",fontWeight:700,marginBottom:2}}>FIRST COMPANY</div><div style={{fontSize:13,fontWeight:700,color:"var(--text)"}}>{firstJob?.company||"—"}</div><div style={{fontSize:10,color:"var(--muted)"}}>{firstJob?.doj||""}</div></div>
                    <div><div style={{fontSize:10,color:"var(--muted)",fontWeight:700,marginBottom:2}}>CURRENT / LATEST</div><div style={{fontSize:13,fontWeight:700,color:"var(--text)"}}>{currentJob?.company||"—"}</div><div style={{fontSize:10,color:"var(--muted)"}}>{currentJob?.designation||""}</div></div>
                    {!hasExitDate&&<Badge color="var(--green)" style={{alignSelf:"center"}}>Currently Employed</Badge>}
                  </div>
                  {(!hasExitDate)&&(
                    <div style={{marginTop:8,padding:"8px 12px",background:"rgba(255,145,77,0.08)",border:"1px solid rgba(255,145,77,0.25)",borderRadius:8,fontSize:11,color:"#ff914d"}}>
                      ⚠ {emp?.lastCompanyRemark||"Last company exit date not available — experience calculated till current date"}
                    </div>
                  )}
                </div>
              );
            })()}
          </Card>
        )}

        {/* Not found fallback suggestion */}
        {result.found&&history.length===0&&searchType!=="uan"&&(
          <div style={{background:"var(--amber)10",border:"1px solid var(--amber)30",borderRadius:10,padding:"14px 18px",display:"flex",gap:12,alignItems:"center"}}>
            <span style={{fontSize:20}}>💡</span>
            <div>
              <div style={{fontSize:13,fontWeight:600,color:"var(--amber)",marginBottom:3}}>No employment history found</div>
              <div style={{fontSize:12,color:"var(--muted)"}}>Identity verified but no EPFO records. Try searching by <strong>UAN number</strong> for full employment history.</div>
            </div>
          </div>
        )}
      </div>
    );
  };

  return(
    <div>
      <div className="fu" style={{marginBottom:24}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-end",flexWrap:"wrap",gap:12}}>
          <div>
            <h2 style={{fontFamily:FFD,fontWeight:700,fontSize:26,color:"var(--text)",marginBottom:4}}>Employee Search</h2>
            <p style={{color:"var(--muted)",fontSize:14}}>Search by UAN, Aadhaar, or PAN — verified from EPFO & Government records</p>
          </div>
          {!isSuperAdmin&&(
            <div style={{background:"var(--s2)",border:"1px solid var(--b1)",borderRadius:10,padding:"10px 16px",textAlign:"right"}}>
              <div style={{fontSize:11,color:"var(--muted)",marginBottom:2}}>SEARCHES REMAINING</div>
              <div style={{fontFamily:FFD,fontSize:22,fontWeight:700,color:"var(--brand)"}}>{Math.max(0,(user.searchesAllocated||0)-(user.searchesUsed||0))}</div>
            </div>
          )}
          {isSuperAdmin&&(
            <Badge color="var(--brand)" style={{fontSize:12,padding:"8px 14px"}}>👑 Unlimited Searches</Badge>
          )}
        </div>
      </div>

      <Card className="fu1" style={{marginBottom:8}}>
        {/* Search type selector */}
        <div style={{display:"flex",gap:8,marginBottom:20}}>
          {searchTypes.map(t=>{
            const locked=!hasModuleAccess(user,isSuperAdmin,t.id);
            return(
              <button key={t.id} onClick={()=>{
                if(locked){addToast("You don't have access to this module. Ask your Company Admin to enable it.","amber");return;}
                setSearchType(t.id);setQ("");setResult(null);setSearched(false);setConsent(false);
              }} style={{flex:1,padding:"12px 8px",border:`1.5px solid ${searchType===t.id?"var(--brand)":"var(--b1)"}`,borderRadius:10,background:searchType===t.id?"var(--acc)10":"transparent",color:locked?"var(--muted)":searchType===t.id?"var(--brand)":"var(--muted)",cursor:locked?"not-allowed":"pointer",fontFamily:FF,fontWeight:600,fontSize:13,transition:"all .15s",display:"flex",flexDirection:"column",alignItems:"center",gap:4,opacity:locked?0.5:1,position:"relative"}}>
                {locked&&<span style={{position:"absolute",top:4,right:6,fontSize:11}}>🔒</span>}
                <span style={{fontSize:18}}>{t.icon}</span>
                <span>{t.label}</span>
              </button>
            );
          })}
        </div>

        {/* Search hint */}
        <div style={{fontSize:12,color:"var(--muted)",marginBottom:12,display:"flex",alignItems:"center",gap:6}}>
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><circle cx="6" cy="6" r="5" stroke="currentColor" strokeWidth="1.2"/><path d="M6 4v3M6 8.5v.5" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/></svg>
          {searchTypes.find(t=>t.id===searchType)?.hint}
        </div>

        {/* Input */}
        {searchType!=="address"&&(
        <div style={{display:"flex",gap:10,marginBottom:searchType==="aadhaar"?12:0,flexWrap:"wrap"}}>
          <Inp
            style={{flex:1,minWidth:200,fontFamily:searchType!=="pan"?FFM:FF}}
            value={q}
            placeholder={searchTypes.find(t=>t.id===searchType)?.placeholder}
            onChange={e=>{
              let v=e.target.value;
              if(searchType==="aadhaar")v=fmtAadhaar(v);
              if(searchType==="pan")v=v.toUpperCase().slice(0,10);
              if(searchType==="uan")v=v.replace(/\D/g,"").slice(0,12);
              if(searchType==="dl")v=v.toUpperCase().slice(0,20);
              setQ(v);
            }}
            onKeyDown={e=>e.key==="Enter"&&doSearch()}
          />
          <Btn onClick={doSearch} style={{padding:"11px 28px"}}>
            {loading?<Spin/>:<>
              <svg width="15" height="15" viewBox="0 0 15 15" fill="none"><circle cx="7" cy="7" r="5" stroke="white" strokeWidth="1.5"/><path d="M11 11l3 3" stroke="white" strokeWidth="1.5" strokeLinecap="round"/></svg>
              Search
            </>}
          </Btn>
        </div>
        )}

        {/* Address Verification — inline form (name/email/phone/addresses) */}
        {searchType==="address"&&(
          <div style={{display:"flex",flexDirection:"column",gap:10}}>
            <div><Label>Candidate Name *</Label><Inp value={addrForm.candidateName} onChange={e=>setAddrForm(p=>({...p,candidateName:e.target.value}))}/></div>
            <div><Label>Candidate Email *</Label><Inp value={addrForm.candidateEmail} onChange={e=>setAddrForm(p=>({...p,candidateEmail:e.target.value}))}/></div>
            <div><Label>Candidate Phone</Label><Inp value={addrForm.candidatePhone} onChange={e=>setAddrForm(p=>({...p,candidatePhone:e.target.value}))}/></div>
            <div><Label>Present Address *</Label><textarea value={addrForm.presentAddress} onChange={e=>setAddrForm(p=>({...p,presentAddress:e.target.value}))} rows={2} style={{width:"100%",padding:"9px 12px",borderRadius:8,border:"1px solid var(--b1)",background:"var(--s2)",color:"var(--text)",fontSize:13,fontFamily:FF,resize:"vertical",outline:"none"}} placeholder="Full address with city, state, pincode"/></div>
            <div onClick={()=>setAddrForm(p=>({...p,sameAddress:!p.sameAddress}))} style={{display:"flex",alignItems:"center",gap:10,cursor:"pointer",padding:"6px 0"}}>
              <div style={{width:18,height:18,borderRadius:5,border:`2px solid ${addrForm.sameAddress?"var(--brand)":"var(--b2)"}`,background:addrForm.sameAddress?"var(--brand)":"transparent",display:"flex",alignItems:"center",justifyContent:"center"}}>
                {addrForm.sameAddress&&<svg width="10" height="10" viewBox="0 0 10 10" fill="none"><path d="M1.5 5l2.5 2.5L8.5 3" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>}
              </div>
              <span style={{fontSize:13,color:"var(--text)"}}>Permanent address is the same as present</span>
            </div>
            {!addrForm.sameAddress&&(
              <div><Label>Permanent Address *</Label><textarea value={addrForm.permanentAddress} onChange={e=>setAddrForm(p=>({...p,permanentAddress:e.target.value}))} rows={2} style={{width:"100%",padding:"9px 12px",borderRadius:8,border:"1px solid var(--b1)",background:"var(--s2)",color:"var(--text)",fontSize:13,fontFamily:FF,resize:"vertical",outline:"none"}} placeholder="Full address with city, state, pincode"/></div>
            )}
            <div style={{background:"var(--s2)",borderRadius:8,padding:"10px 14px",display:"flex",justifyContent:"space-between",alignItems:"center",fontSize:13}}>
              <span style={{color:"var(--muted)"}}>Cost ({addrForm.sameAddress?"1 address":"2 addresses"})</span>
              {isSuperAdmin?<Badge color="var(--brand)" style={{fontSize:10}}>👑 Unlimited</Badge>:<span style={{fontWeight:700,color:"var(--brand)"}}>₹{getCompanyPrice(company,"address_verify")}</span>}
            </div>
            <Btn onClick={doAddressVerify} disabled={addrLoading} style={{justifyContent:"center"}}>{addrLoading?"Sending...":"📧 Send to Candidate"}</Btn>
          </div>
        )}

        {/* eCourt extra fields */}
        {searchType==="ecourt"&&(
          <div style={{marginTop:12,display:"flex",flexDirection:"column",gap:10}}>
            <div style={{fontSize:11,color:"#ff914d",fontWeight:700,letterSpacing:.5,textTransform:"uppercase",marginBottom:2}}>⚖️ Search Parameters</div>
            <div style={{fontSize:11,color:"var(--muted)",background:"rgba(255,145,77,0.08)",border:"1px solid rgba(255,145,77,0.2)",borderRadius:8,padding:"8px 12px",lineHeight:1.6}}>
              💡 Name + Father Name + Address are required for accurate results
            </div>
            {[
              {k:"name",l:"Candidate Full Name *",p:"Rajesh Kumar"},
              {k:"father_name",l:"Father's Name *",p:"Suresh Kumar"},
              {k:"address",l:"Address *",p:"MG Road, Chennai, Tamil Nadu"},
              {k:"year",l:"Year (Optional)",p:"2020"},
              {k:"state",l:"State (Optional)",p:"Tamil Nadu"},
            ].map(fi=>(
              <div key={fi.k}>
                <Label>{fi.l}</Label>
                <Inp value={ecourtForm[fi.k]} onChange={e=>setEcourtForm(p=>({...p,[fi.k]:e.target.value}))} placeholder={fi.p}/>
              </div>
            ))}
          </div>
        )}

        {/* DOB field for DL */}
        {searchType==="dl"&&(
          <div style={{marginTop:10}}>
            <Label>Date of Birth *</Label>
            <Inp type="date" value={dlDob} onChange={e=>setDlDob(e.target.value)}/>
            <div style={{fontSize:11,color:"var(--muted)",marginTop:4}}>Required for driving license verification</div>
          </div>
        )}

        {/* Consent for Aadhaar */}
        {searchType==="aadhaar"&&(
          <div onClick={()=>setConsent(c=>!c)} style={{display:"flex",alignItems:"flex-start",gap:10,cursor:"pointer",padding:"10px 14px",background:consent?"var(--green)08":"var(--s2)",border:`1px solid ${consent?"var(--green)30":"var(--b1)"}`,borderRadius:8,userSelect:"none"}}>
            <div style={{width:18,height:18,borderRadius:4,border:`2px solid ${consent?"var(--green)":"var(--b2)"}`,background:consent?"var(--green)":"transparent",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,marginTop:1}}>
              {consent&&<svg width="10" height="10" viewBox="0 0 10 10" fill="none"><path d="M2 5l2.5 2.5L8 3" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>}
            </div>
            <div style={{fontSize:12,color:"var(--sub)",lineHeight:1.6}}>
              <strong style={{color:"var(--text)"}}>Candidate Consent:</strong> I confirm that the candidate has given explicit consent for Aadhaar verification as required under the Aadhaar Act, 2016. ⚖️
            </div>
          </div>
        )}
      </Card>

      {/* Not found suggestion */}
      {searched&&result&&!result.found&&searchType!=="aadhaar"&&(
        <div style={{marginTop:12,background:"var(--amber)08",border:"1px solid var(--amber)25",borderRadius:10,padding:"12px 16px",display:"flex",justifyContent:"space-between",alignItems:"center",flexWrap:"wrap",gap:8}}>
          <span style={{fontSize:13,color:"var(--amber)",fontWeight:600}}>💡 Not found by {searchType.toUpperCase()}?</span>
          <button onClick={()=>{setSearchType("aadhaar");setQ("");setResult(null);setSearched(false);}} style={{background:"var(--amber)",border:"none",borderRadius:8,color:"#fff",padding:"7px 14px",cursor:"pointer",fontSize:12,fontWeight:700,fontFamily:FF}}>
            Try Aadhaar Search →
          </button>
        </div>
      )}

      <ResultCard/>
      {result&&result.found&&result.data&&<MeithoScoreCard data={{
        ...result.data,
        uan:result.data.uan||q,
        pan:result.data.pan||result.data.pan_number||(searchType==="pan"?q:""),
        // Map employment history correctly from UAN API response
        employmentHistory:result.data.employmentHistory||
          (result.data.employment_history||[]).map(e=>({
            company:e.establishment_name||e.company||"",
            designation:e.designation||"",
            doj:e.date_of_joining||e.doj||"",
            dor:e.date_of_exit||e.dor||"Currently Employed",
            salary:e.wages||e.salary||"",
          })),
        // Map Aadhaar linkage
        aadhaarLinked:result.data.aadhaar_linked||result.data.aadhaarLinked||false,
      }}/>}
      {/* WhatsApp Share Button */}
      {result&&result.found&&result.data&&(result.data.mobile||result.data.phone||result.data.mobile_number)&&(
        <div style={{marginTop:12,display:"flex",gap:10,flexWrap:"wrap"}}>
          <button onClick={()=>{
            const phone=result.data.mobile||result.data.phone||result.data.mobile_number;
            const name=result.data.name||result.data.full_name||q;
            const reportId="BGV-"+Date.now().toString(36).toUpperCase();
            sendWhatsAppMessage(phone, getBGVWhatsAppMsg(name, searchType, true, reportId, company?.name));
          }} style={{background:"rgba(37,211,102,0.1)",border:"1px solid rgba(37,211,102,0.25)",borderRadius:9,color:"#25D366",padding:"9px 16px",cursor:"pointer",fontSize:13,fontWeight:700,fontFamily:FF,display:"flex",alignItems:"center",gap:7}}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="#25D366"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
            Send BGV Result on WhatsApp
          </button>
        </div>
      )}
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════
   UPLOAD EXIT DATA
══════════════════════════════════════════════════════════════ */
function UploadPage({user,employees,setEmployees,addToast}) {
  const blank={
    uan:"",firstName:"",lastName:"",aadhaar:"",dob:"",
    lastCompany:"",lastSalary:"",doj:"",relievingDate:"",
    email:"",phone:"",designation:"",
    reason:"",rehire:"Yes",rehireComment:"",noticePeriodServed:"Yes",
  };
  const [form,setForm]=useState(blank);
  const [errs,setErrs]=useState({});
  const [loading,setLoading]=useState(false);
  const set=(k,v)=>{setForm(p=>({...p,[k]:v}));if(k==="section"&&v==="Other")setForm(p=>({...p,[k]:v}));setErrs(p=>({...p,[k]:""}));};

  const validate=()=>{
    const e={};
    if(!form.firstName.trim())e.firstName="Required";
    if(!form.lastName.trim())e.lastName="Required";
    if(form.aadhaar&&!validAadhaar(form.aadhaar))e.aadhaar="Format: 1234-5678-9012";
    if(!form.dob)e.dob="Required";
    if(!form.lastCompany.trim())e.lastCompany="Required";
    if(!form.lastSalary||isNaN(form.lastSalary))e.lastSalary="Numbers only";
    if(!form.doj)e.doj="Required";
    if(!form.relievingDate)e.relievingDate="Required";
    if(form.doj&&form.relievingDate&&form.doj>=form.relievingDate)e.relievingDate="Must be after joining date";
    if(!form.designation.trim())e.designation="Required";
    if(!form.reason)e.reason="Required";
    if(form.rehire==="No"&&!form.rehireComment.trim())e.rehireComment="Please provide reason";
    if(!form.uan&&!form.aadhaar)e.uan="Either UAN or Aadhaar is required";
    const duplicate=employees.find(e=>((form.uan&&e.uan===form.uan)||(form.aadhaar&&e.aadhaar===form.aadhaar))&&e.lastCompany===form.lastCompany&&e.doj===form.doj);
    if(duplicate)e.uan="Record already exists for this employee + Company + DOJ";
    return e;
  };

  const submit=async()=>{
    const e=validate();if(Object.keys(e).length){setErrs(e);return;}
    setLoading(true);
    try{
      const fullName=`${form.firstName.trim()} ${form.lastName.trim()}`;
      const data={...form,name:fullName,addedBy:user.companyId||"c1"};
      const id=await addEmployee(data);
      setEmployees(p=>[...p,{...data,id}]);
      addToast(`✓ ${fullName}'s record uploaded successfully!`,"success");
      setForm(blank);setErrs({});
    }catch(e){addToast("Upload failed: "+e.message,"error");}
    setLoading(false);
  };

  const {dark}=useTheme();

  return(
    <div className={dark?"bg-mesh-dark":"bg-mesh-light"} style={{minHeight:"100%",padding:"4px 0"}}>
      <div className="fu" style={{marginBottom:20}}>
        <h2 style={{fontFamily:FFD,fontWeight:800,fontSize:26,color:"var(--text)",marginBottom:4,letterSpacing:-.5}}>Upload Exit Data</h2>
        <p style={{color:"var(--muted)",fontSize:14}}>Add employee exit records to the Meitho verification network</p>
      </div>

      {/* Info banner */}
      <div className="fu1 glass" style={{borderRadius:12,padding:"12px 16px",marginBottom:18,border:"1px solid var(--brand)30",display:"flex",alignItems:"center",gap:12}}>
        <div style={{width:32,height:32,borderRadius:8,background:"var(--brand)20",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,color:"var(--brand)"}}>
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><circle cx="8" cy="8" r="6" stroke="currentColor" strokeWidth="1.5"/><path d="M8 5v3.5M8 10v.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>
        </div>
        <div style={{fontSize:13,color:"var(--sub)"}}>
          <strong style={{color:"var(--text)"}}>UAN or Aadhaar required.</strong> Other employee details will be enriched via EPFO verification. Company adds <strong style={{color:"var(--brand)"}}>salary information only</strong>.
        </div>
      </div>

      {/* Section 1: Identity */}
      <div className="fu2 glass neon-hover" style={{borderRadius:14,padding:20,marginBottom:14,border:"1px solid var(--b1)"}}>
        <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:16,paddingBottom:12,borderBottom:"1px solid var(--b1)"}}>
          <div style={{width:28,height:28,borderRadius:7,background:"var(--brand)15",display:"flex",alignItems:"center",justifyContent:"center",color:"var(--brand)"}}>
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><circle cx="7" cy="4.5" r="2.5" stroke="currentColor" strokeWidth="1.3"/><path d="M2 12c0-2.76 2.24-5 5-5s5 2.24 5 5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/></svg>
          </div>
          <span style={{fontFamily:FFD,fontSize:13,fontWeight:700,color:"var(--text)"}}>Identity</span>
          <span style={{fontSize:11,color:"var(--muted)",marginLeft:4}}>— UAN or Aadhaar required</span>
        </div>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
          <div><Label>First Name *</Label><Inp value={form.firstName} err={errs.firstName} onChange={e=>set("firstName",e.target.value)} placeholder="Rajesh"/><ErrMsg msg={errs.firstName}/></div>
          <div><Label>Last Name *</Label><Inp value={form.lastName} err={errs.lastName} onChange={e=>set("lastName",e.target.value)} placeholder="Kumar"/><ErrMsg msg={errs.lastName}/></div>
          <div>
            <Label>UAN Number {!form.aadhaar?"*":""}</Label>
            <Inp value={form.uan} err={errs.uan} placeholder="12-digit UAN" style={{fontFamily:FFM}} onChange={e=>set("uan",e.target.value.replace(/\D/g,"").slice(0,12))}/>
            <ErrMsg msg={errs.uan}/>
            {form.uan&&<div style={{fontSize:10,color:"var(--green)",marginTop:3}}>✓ Primary identifier</div>}
          </div>
          <div>
            <Label>Aadhaar Number {!form.uan?"*":""}</Label>
            <Inp value={form.aadhaar} err={errs.aadhaar} placeholder="XXXX-XXXX-XXXX" style={{fontFamily:FFM}} onChange={e=>set("aadhaar",fmtAadhaar(e.target.value))}/>
            <ErrMsg msg={errs.aadhaar}/>
          </div>
          <div><Label>Date of Birth *</Label><Inp type="date" value={form.dob} err={errs.dob} onChange={e=>set("dob",e.target.value)}/><ErrMsg msg={errs.dob}/></div>
          <div><Label>Email</Label><Inp type="email" value={form.email} onChange={e=>set("email",e.target.value)} placeholder="employee@email.com"/></div>
          <div><Label>Phone</Label><Inp value={form.phone} onChange={e=>set("phone",e.target.value)} placeholder="9876543210"/></div>
        </div>
      </div>

      {/* Section 2: Employment */}
      <div className="fu3 glass neon-hover" style={{borderRadius:14,padding:20,marginBottom:14,border:"1px solid var(--b1)"}}>
        <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:16,paddingBottom:12,borderBottom:"1px solid var(--b1)"}}>
          <div style={{width:28,height:28,borderRadius:7,background:"var(--purple)15",display:"flex",alignItems:"center",justifyContent:"center",color:"var(--purple)"}}>
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><rect x="2" y="5" width="10" height="7" rx="1.5" stroke="currentColor" strokeWidth="1.3"/><path d="M5 5V4a2 2 0 014 0v1" stroke="currentColor" strokeWidth="1.3"/></svg>
          </div>
          <span style={{fontFamily:FFD,fontSize:13,fontWeight:700,color:"var(--text)"}}>Employment Details</span>
        </div>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
          <div style={{gridColumn:"span 2"}}><Label>Company Name *</Label><Inp value={form.lastCompany} err={errs.lastCompany} onChange={e=>set("lastCompany",e.target.value)} placeholder="Acme Corp Pvt Ltd"/><ErrMsg msg={errs.lastCompany}/></div>
          <div><Label>Designation *</Label><Inp value={form.designation} err={errs.designation} onChange={e=>set("designation",e.target.value)} placeholder="Software Engineer"/><ErrMsg msg={errs.designation}/></div>
          <div>
            <Label>Last Salary (₹/mo) *</Label>
            <div style={{position:"relative"}}>
              <span style={{position:"absolute",left:12,top:"50%",transform:"translateY(-50%)",color:"var(--muted)",fontSize:14,fontWeight:600}}>₹</span>
              <Inp value={form.lastSalary} err={errs.lastSalary} style={{paddingLeft:24}} onChange={e=>set("lastSalary",e.target.value.replace(/\D/g,""))} placeholder="85000"/>
            </div>
            <ErrMsg msg={errs.lastSalary}/>
          </div>
          <div><Label>Date of Joining *</Label><Inp type="date" value={form.doj} err={errs.doj} onChange={e=>set("doj",e.target.value)}/><ErrMsg msg={errs.doj}/></div>
          <div><Label>Last Working Date *</Label><Inp type="date" value={form.relievingDate} err={errs.relievingDate} onChange={e=>set("relievingDate",e.target.value)}/><ErrMsg msg={errs.relievingDate}/></div>
        </div>
      </div>

      {/* Section 3: Exit */}
      <div className="fu4 glass neon-hover" style={{borderRadius:14,padding:20,marginBottom:18,border:"1px solid var(--b1)"}}>
        <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:16,paddingBottom:12,borderBottom:"1px solid var(--b1)"}}>
          <div style={{width:28,height:28,borderRadius:7,background:"var(--amber)15",display:"flex",alignItems:"center",justifyContent:"center",color:"var(--amber)"}}>
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M7 1v6M4 4l3-3 3 3M2 11h10" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/></svg>
          </div>
          <span style={{fontFamily:FFD,fontSize:13,fontWeight:700,color:"var(--text)"}}>Exit Details</span>
        </div>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
          <div style={{gridColumn:"span 2"}}>
            <Label>Reason for Leaving *</Label>
            <Sel value={form.reason} style={{borderColor:errs.reason?"var(--red)":"var(--b1)"}} onChange={e=>set("reason",e.target.value)}>
              <option value="">Select reason...</option>
              {["Better Opportunity","Personal Reasons","Termination","Relocation","Higher Studies","Retirement","Other"].map(r=><option key={r}>{r}</option>)}
            </Sel>
            <ErrMsg msg={errs.reason}/>
          </div>
          <div>
            <Label>Rehire Eligible?</Label>
            <div style={{display:"flex",gap:8,marginTop:4}}>
              {["Yes","No"].map(v=>(
                <button key={v} onClick={()=>set("rehire",v)} style={{flex:1,padding:"10px",border:`1.5px solid ${form.rehire===v?(v==="Yes"?"var(--green)":"var(--red)"):"var(--b1)"}`,borderRadius:8,background:form.rehire===v?(v==="Yes"?"var(--green)15":"var(--red)15"):"transparent",color:form.rehire===v?(v==="Yes"?"var(--green)":"var(--red)"):"var(--muted)",fontWeight:700,fontSize:13,cursor:"pointer",fontFamily:FF,transition:"all .15s"}}>
                  {v==="Yes"?"✓ Yes":"✗ No"}
                </button>
              ))}
            </div>
          </div>
          <div>
            <Label>Notice Period Served?</Label>
            <div style={{display:"flex",gap:8,marginTop:4}}>
              {["Yes","No"].map(v=>(
                <button key={v} onClick={()=>set("noticePeriodServed",v)} style={{flex:1,padding:"10px",border:`1.5px solid ${form.noticePeriodServed===v?"var(--brand)":"var(--b1)"}`,borderRadius:8,background:form.noticePeriodServed===v?"var(--acc)15":"transparent",color:form.noticePeriodServed===v?"var(--brand)":"var(--muted)",fontWeight:700,fontSize:13,cursor:"pointer",fontFamily:FF,transition:"all .15s"}}>
                  {v==="Yes"?"✓ Yes":"✗ No"}
                </button>
              ))}
            </div>
          </div>
          {form.rehire==="No"&&(
            <div style={{gridColumn:"span 2"}}>
              <Label>Reason for Not Rehiring *</Label>
              <textarea value={form.rehireComment} onChange={e=>set("rehireComment",e.target.value)} placeholder="Describe the reason..." style={{width:"100%",background:"var(--ibg)",border:`1.5px solid ${errs.rehireComment?"var(--red)":"var(--b1)"}`,borderRadius:8,padding:"10px 13px",color:"var(--text)",fontSize:14,fontFamily:FF,outline:"none",resize:"vertical",minHeight:80,transition:"border-color .15s"}} onFocus={e=>e.target.style.borderColor="var(--brand)"} onBlur={e=>e.target.style.borderColor=errs.rehireComment?"var(--red)":"var(--b1)"}/>
              <ErrMsg msg={errs.rehireComment}/>
            </div>
          )}
        </div>
      </div>

      <div style={{display:"flex",gap:10,alignItems:"center"}}>
        <Btn onClick={submit} style={{padding:"12px 28px",fontSize:14,background:"linear-gradient(90deg,#ff3131,#ff914d)"}}>
          {loading?<Spin/>:<>
            <svg width="15" height="15" viewBox="0 0 15 15" fill="none"><path d="M7.5 1v9M4 6l3.5 4L11 6" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
            Submit Record
          </>}
        </Btn>
        <BtnGhost onClick={()=>{setForm(blank);setErrs({});}}>Clear Form</BtnGhost>
        <span style={{fontSize:12,color:"var(--muted)"}}>* Required fields</span>
      </div>
    </div>
  );
}

function BulkUploadPage({user,employees,setEmployees,addToast}) {
  const [preview,setPreview]=useState([]); const [errors,setErrors]=useState([]); const [done,setDone]=useState(false); const [loading,setLoading]=useState(false);
  const fileRef=useRef();
  const parseCSV=text=>{const lines=text.trim().split("\n");const headers=lines[0].split(",").map(h=>h.trim().toLowerCase().replace(/ /g,"_"));return lines.slice(1).map(line=>{const vals=line.split(",").map(v=>v.trim().replace(/^"|"$/g,""));const obj={};headers.forEach((h,j)=>obj[h]=vals[j]||"");return obj;});};
  const handleFile=e=>{
    const file=e.target.files[0];if(!file)return;
    const reader=new FileReader();
    reader.onload=ev=>{
      const rows=parseCSV(ev.target.result);const errs=[];const valid=[];
      rows.forEach((r,i)=>{
        const rowErrs=[];
        if(!validAadhaar(r.aadhaar||""))rowErrs.push("Invalid Aadhaar");
        if(!r.name)rowErrs.push("Name missing");
        if(employees.find(e=>e.aadhaar===r.aadhaar&&e.lastCompany===r.lastCompany&&e.doj===r.doj))rowErrs.push("Exact duplicate (same Aadhaar+Company+DOJ)");
        if(rowErrs.length)errs.push({row:i+2,errs:rowErrs,data:r});
        else valid.push({...r,addedBy:user.companyId||"c1"});
      });
      setPreview(valid);setErrors(errs);setDone(false);
    };
    reader.readAsText(file);
  };
  const submitAll=async()=>{
    if(!preview.length)return;
    setLoading(true);
    try {
      await bulkAddEmployees(preview);
      setEmployees(p=>[...p,...preview.map((r,i)=>({...r,id:"bulk"+i}))]);
      await addUserCredits(user.id,preview.length*5);
      addToast(`${preview.length} records uploaded! +${preview.length*5} credits added.`,"success");
      setDone(true);setPreview([]);setErrors([]);
    }catch(e){addToast("Bulk upload failed: "+e.message,"error");}
    setLoading(false);
  };
  const sampleCSV=`aadhaar,name,lastCompany,lastSalary,doj,relievingDate,reason,rehire,designation\n1111-2222-3333,Raj Kumar,Sample Corp,70000,2020-01-15,2024-01-15,Better Opportunity,Yes,Engineer`;
  return (
    <div>
      <div className="fu" style={{marginBottom:24}}><h2 style={{fontFamily:FFD,fontWeight:700,fontSize:26,color:"var(--text)",marginBottom:4}}>Bulk Upload</h2><p style={{color:"var(--muted)",fontSize:14}}>Each row earns +5 credits</p></div>
      {done&&<div className="fu" style={{background:"var(--green)15",border:"1px solid var(--green)44",borderRadius:12,padding:16,marginBottom:20,display:"flex",gap:10,alignItems:"center"}}><span style={{fontSize:22}}>✅</span><div style={{fontWeight:700,color:"var(--green)",fontFamily:FFD}}>Bulk upload complete!</div></div>}
      <Card className="fu1" style={{marginBottom:16}}><h3 style={{fontFamily:FFD,fontSize:14,fontWeight:700,color:"var(--sub)",marginBottom:12}}>STEP 1 — Download Template</h3><Btn style={{fontSize:13}} onClick={()=>{const a=document.createElement("a");a.href="data:text/csv,"+encodeURIComponent(sampleCSV);a.download="bgv_template.csv";a.click();}}>⬇ Download CSV Template</Btn></Card>
      <Card className="fu2" style={{marginBottom:16}}>
        <h3 style={{fontFamily:FFD,fontSize:14,fontWeight:700,color:"var(--sub)",marginBottom:12}}>STEP 2 — Upload Filled CSV</h3>
        <div onClick={()=>fileRef.current.click()} style={{border:"2px dashed var(--b2)",borderRadius:12,padding:40,textAlign:"center",cursor:"pointer"}}>
          <div style={{fontSize:36,marginBottom:10}}>📂</div>
          <div style={{fontFamily:FFD,fontWeight:700,fontSize:15,color:"var(--sub)",marginBottom:6}}>Click to select CSV file</div>
          <div style={{fontSize:12,color:"var(--muted)"}}>CSV only · auto-validates Aadhaar format</div>
          <input ref={fileRef} type="file" accept=".csv" onChange={handleFile} style={{display:"none"}}/>
        </div>
        {errors.length>0&&<div style={{marginTop:14,background:"var(--red)11",border:"1px solid var(--red)33",borderRadius:10,padding:14}}><div style={{fontWeight:700,color:"var(--red)",marginBottom:8,fontSize:13}}>⚠ {errors.length} rows skipped</div>{errors.slice(0,4).map((e,i)=><div key={i} style={{fontSize:12,color:"var(--muted)",marginBottom:3}}>Row {e.row}: {e.errs.join(", ")} ({e.data.name||"unknown"})</div>)}</div>}
      </Card>
      {preview.length>0&&<Card className="fu3"><div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14}}><h3 style={{fontFamily:FFD,fontSize:14,fontWeight:700,color:"var(--sub)"}}>STEP 3 — Preview ({preview.length} valid records)</h3><Btn style={{fontSize:13}} onClick={submitAll}>{loading?<Spin/>:"Save →"}</Btn></div>
        <div style={{overflowX:"auto"}}><table style={{width:"100%",borderCollapse:"collapse",fontSize:13}}><thead><tr style={{borderBottom:"1px solid var(--b1)"}}>{["Name","Aadhaar","Company","Rehire"].map(h=><th key={h} style={{textAlign:"left",padding:"0 0 10px",fontSize:10,fontWeight:700,color:"var(--muted)",textTransform:"uppercase",letterSpacing:1}}>{h}</th>)}</tr></thead>
        <tbody>{preview.slice(0,8).map((r,i)=><tr key={i} style={{borderBottom:"1px solid var(--b1)33"}}><td style={{padding:"10px 0",color:"var(--text)",fontWeight:600}}>{r.name}</td><td style={{padding:"10px 0",color:"var(--muted)",fontFamily:FFM}}>{r.aadhaar}</td><td style={{padding:"10px 0",color:"var(--sub)"}}>{r.lastcompany||r.lastCompany}</td><td style={{padding:"10px 0"}}><Badge color={(r.rehire||"Yes")==="Yes"?"var(--green)":"var(--red)"}>{r.rehire||"Yes"}</Badge></td></tr>)}</tbody></table></div>
      </Card>}
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════
   HISTORY
══════════════════════════════════════════════════════════════ */
/* ══════════════════════════════════════════════════════════════
   SEARCH HISTORY PAGE
══════════════════════════════════════════════════════════════ */
function HistoryPage({user,history,companies,addToast}) {
  const {dark}=useTheme();
  const isSuperAdmin=user.role==="superadmin";
  const [filter,setFilter]=useState("all");
  const [typeFilter,setTypeFilter]=useState("all");
  const [q,setQ]=useState("");
  // Force re-render when history changes
  const [localHistory,setLocalHistory]=useState(history);
  useEffect(()=>setLocalHistory(history),[history]);
  const [detailModal,setDetailModal]=useState(null);

  const myHistory=isSuperAdmin
    ? (localHistory||history)
    : (localHistory||history).filter(h=>h.userId===user.id||h.companyId===user.companyId);

  const filtered=myHistory.filter(h=>{
    const matchQ=!q||(h.searchValue||h.name||"").toLowerCase().includes(q.toLowerCase())||(h.companyName||"").toLowerCase().includes(q.toLowerCase())||(h.userName||"").toLowerCase().includes(q.toLowerCase());
    const matchF=filter==="all"||(filter==="found"&&h.found)||(filter==="not_found"&&!h.found);
    const matchT=typeFilter==="all"||h.searchType===typeFilter;
    return matchQ&&matchF&&matchT;
  });

  // Stats
  const totalSpent=myHistory.reduce((s,h)=>{
    const cost=histCost(h,companies);
    return s+(h.found?cost:0);
  },0);
  const thisMonth=myHistory.filter(h=>{
    const d=new Date(h.timestamp||h.date||"");
    const now=new Date();
    return d.getMonth()===now.getMonth()&&d.getFullYear()===now.getFullYear();
  });
  const thisMonthSpent=thisMonth.reduce((s,h)=>{
    const cost=histCost(h,companies);
    return s+(h.found?cost:0);
  },0);


  const generateBGVReport=(record)=>{
    const reportDate=new Date().toLocaleString("en-IN",{dateStyle:"full",timeStyle:"short"});
    const emp=record.employeeData||{};
    const history=(emp.employmentHistory||[]).filter(j=>j.company);
    const empName=emp.name||emp.full_name||(emp.employmentHistory&&emp.employmentHistory[0]?.name)||"";
    const displayName=empName||(record.searchType==="pan"?"":"")||"—";
    const ms=emp.meithoScore||null;
    const reportId="BGV-"+Date.now().toString(36).toUpperCase();
    const verifiedAt=new Date().toLocaleString("en-IN");
    const verifiedBadgeHtml=`<div style="display:inline-flex;align-items:center;gap:6px;background:linear-gradient(135deg,#ff3131,#059669);padding:5px 14px;border-radius:99px;margin-bottom:12px;"><span style="color:#fff;font-size:10px;font-weight:800;letter-spacing:1px;">&#10003; MEITHO VERIFIED</span><span style="color:rgba(255,255,255,0.7);font-size:9px;">· ${new Date().toLocaleDateString("en-IN")}</span></div>`;
    const sortedHistory=[...history].sort((a,b)=>new Date(a.doj||"1900")-new Date(b.doj||"1900"));
    const firstJob=sortedHistory[0];
    const lastJob=sortedHistory[sortedHistory.length-1];
    const hasExit=lastJob?.dor&&lastJob.dor!=="Present"&&lastJob.dor!=="";
    const startDate=firstJob?new Date(firstJob.doj||""):null;
    const endDate=hasExit?new Date(lastJob.dor):new Date();
    const totalExp=startDate&&!isNaN(startDate)&&startDate.getFullYear()>1950?Math.round((endDate-startDate)/(1000*60*60*24*365)*10)/10:0;
    // Logo: color for light bg, white for dark bg
    const LOGO1="data:image/png;base64,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";
    const LOGO2="data:image/png;base64,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";
    const scoreColor=!ms?"#0cc0df":ms>=85?"#ff3131":ms>=65?"#0cc0df":ms>=45?"#f59e0b":"#ef4444";
    const scoreGrade=!ms?"—":ms>=85?"Highly Trusted":ms>=65?"Trusted":ms>=45?"Moderate Risk":"High Risk";
    const html=`<!DOCTYPE html><html><head><meta charset="UTF-8"/><title>Meitho BGV — ${displayName}</title>
<style>
*{margin:0;padding:0;box-sizing:border-box;}
body{font-family:'Segoe UI',Arial,sans-serif;color:#1a1a2e;background:#fff;font-size:13px;line-height:1.6;}
@media print{body{-webkit-print-color-adjust:exact;print-color-adjust:exact;}@page{margin:0;}}
.page{max-width:820px;margin:0 auto;}
.header{background:linear-gradient(135deg,#0a0a1a,#1a2040);color:#fff;padding:28px 40px;display:flex;justify-content:space-between;align-items:center;}
.logo{height:44px;width:auto;}
.report-id{font-size:18px;font-weight:900;color:#0cc0df;font-family:monospace;letter-spacing:1px;}
.report-date{font-size:10px;color:rgba(255,255,255,0.5);margin-top:3px;}
.report-by{font-size:10px;color:rgba(255,255,255,0.4);margin-top:2px;}
.statusbar{background:#0cc0df;color:#fff;padding:7px 40px;display:flex;justify-content:space-between;font-size:10px;font-weight:700;letter-spacing:.8px;}
.content{padding:28px 40px;}
/* Score */
.score-box{display:flex;gap:20px;align-items:center;background:linear-gradient(135deg,#f0fffe,#fff8ff);border:2px solid ${scoreColor};border-radius:14px;padding:20px 24px;margin-bottom:22px;}
.score-circle{width:88px;height:88px;border-radius:50%;background:${scoreColor};display:flex;flex-direction:column;align-items:center;justify-content:center;color:#fff;flex-shrink:0;box-shadow:0 6px 20px ${scoreColor}55;}
.score-num{font-size:32px;font-weight:900;line-height:1;}
.score-max{font-size:10px;opacity:.75;}
.score-grade{font-size:18px;font-weight:800;color:${scoreColor};margin-bottom:6px;}
.score-sub{font-size:11px;color:#64748b;margin-bottom:10px;}
.bars{display:grid;grid-template-columns:1fr 1fr;gap:8px;}
.bar{display:flex;flex-direction:column;gap:3px;}
.bar-top{display:flex;justify-content:space-between;font-size:10px;color:#64748b;font-weight:600;}
.bar-track{height:4px;background:#e2e8f0;border-radius:2px;}
.bar-fill{height:4px;border-radius:2px;background:${scoreColor};}
/* Section */
.sec{margin-bottom:22px;}
.sec-title{font-size:9px;font-weight:800;letter-spacing:1.8px;text-transform:uppercase;color:#0cc0df;border-bottom:1.5px solid #e2e8f0;padding-bottom:5px;margin-bottom:12px;}
/* Info grid */
.grid{display:grid;grid-template-columns:repeat(3,1fr);gap:10px;}
.col2{grid-column:span 2;}
.col3{grid-column:1/-1;}
.cell{background:#f8fafc;border-radius:8px;padding:9px 12px;border-left:3px solid #0cc0df;}
.cell-l{font-size:9px;font-weight:700;color:#94a3b8;text-transform:uppercase;letter-spacing:.5px;margin-bottom:2px;}
.cell-v{font-size:13px;font-weight:700;color:#0f172a;}
.badge{display:inline-block;padding:1px 7px;border-radius:4px;font-size:9px;font-weight:700;margin-left:5px;}
.b-green{background:#dcfce7;color:#16a34a;}
.b-blue{background:#dbeafe;color:#1d4ed8;}
.b-gray{background:#f1f5f9;color:#64748b;}
/* Timeline */
.timeline{padding-left:18px;position:relative;}
.timeline::before{content:"";position:absolute;left:5px;top:6px;bottom:6px;width:2px;background:#e2e8f0;}
.job{position:relative;padding-left:14px;margin-bottom:14px;}
.job::before{content:"";position:absolute;left:-13px;top:5px;width:10px;height:10px;border-radius:50%;background:#0cc0df;border:2px solid #fff;box-shadow:0 0 0 2px #0cc0df;}
.job-co{font-size:14px;font-weight:700;color:#0f172a;}
.job-role{font-size:12px;color:#475569;margin-top:1px;}
.job-date{font-size:10px;color:#94a3b8;font-family:monospace;margin-top:3px;}
/* Verify */
.vgrid{display:grid;grid-template-columns:1fr 1fr;gap:8px;}
.vitem{display:flex;gap:10px;align-items:center;padding:9px 12px;border-radius:8px;border:1px solid #e2e8f0;}
.vcheck{width:26px;height:26px;border-radius:50%;display:flex;align-items:center;justify-content:center;font-size:12px;flex-shrink:0;font-weight:700;}
.ok{background:#dcfce7;color:#16a34a;}
.na{background:#f1f5f9;color:#94a3b8;}
.vl{font-size:12px;font-weight:700;color:#0f172a;}
.vs{font-size:10px;color:#94a3b8;}
/* Footer */
.footer{background:#f8fafc;border-top:2px solid #e2e8f0;padding:16px 40px;display:flex;justify-content:space-between;align-items:center;}
.f-note{font-size:10px;color:#94a3b8;text-align:center;max-width:280px;}
.f-contact{font-size:10px;color:#0cc0df;font-weight:600;text-align:right;}
.warn{margin-top:10px;padding:8px 12px;background:#fffbeb;border:1px solid #fcd34d;border-radius:6px;font-size:11px;color:#92400e;}
</style></head><body><div class="page">
<div class="header">
  <img src="${LOGO1}" class="logo" alt="Meitho"/>
  <div style="text-align:right;">
    <div class="report-id">${reportId}</div>
    <div class="report-date">${reportDate}</div>
    <div class="report-by">Verified by: ${record.userName||"Admin"} · ${record.companyName||"Meitho BGV"}</div>
  </div>
</div>
<div class="statusbar">
  <span>BACKGROUND VERIFICATION REPORT</span>
  <span>${(record.searchType||"").toUpperCase()} SEARCH · ${record.source==="db"?"MEITHO DATABASE":"LIVE API"}</span>
  <span>${record.found?"✓ RECORD FOUND":"✗ NOT FOUND"}</span>
</div>
<div class="content">
${verifiedBadgeHtml}
${ms!==null&&ms!==undefined?`<div class="score-box">
  <div class="score-circle"><div class="score-num">${ms}</div><div class="score-max">/100</div></div>
  <div style="flex:1">
    <div class="score-grade">${scoreGrade}</div>
    <div class="score-sub">Meitho Trust Score — Identity · Employment · References · Criminal</div>
    <div class="bars">
      ${[["Identity",25,25],["Stability",22,25],["References",0,25],["Criminal",20,25]].map(([l,v,m])=>`<div class="bar"><div class="bar-top"><span>${l}</span><span>${v}/${m}</span></div><div class="bar-track"><div class="bar-fill" style="width:${v/m*100}%"></div></div></div>`).join("")}
    </div>
  </div>
</div>`:""}
<div class="sec">
  <div class="sec-title">Candidate Identity</div>
  <div class="grid">
    <div class="cell col3"><div class="cell-l">Full Name</div><div class="cell-v" style="font-size:17px;">${displayName}</div></div>
    ${emp.dob?`<div class="cell"><div class="cell-l">Date of Birth</div><div class="cell-v">${emp.dob}</div></div>`:""}
    ${emp.gender?`<div class="cell"><div class="cell-l">Gender</div><div class="cell-v">${emp.gender==="M"?"Male":emp.gender==="F"?"Female":emp.gender}</div></div>`:""}
    ${emp.uan?`<div class="cell"><div class="cell-l">UAN Number</div><div class="cell-v" style="font-family:monospace;">${emp.uan}<span class="badge b-green">EPFO</span></div></div>`:""}
    ${(emp.pan||record.searchType==="pan")?`<div class="cell"><div class="cell-l">PAN Number</div><div class="cell-v" style="font-family:monospace;">${emp.pan||record.searchValue||""}<span class="badge b-blue">${emp.status||"Valid"}</span></div></div>`:""}
    ${emp.masked_aadhaar?`<div class="cell"><div class="cell-l">Aadhaar (Masked)</div><div class="cell-v" style="font-family:monospace;">${emp.masked_aadhaar}${emp.aadhaar_linked?'<span class="badge b-green">Linked</span>':""}</div></div>`:""}
    ${emp.mobile||emp.phone_number?`<div class="cell"><div class="cell-l">Phone</div><div class="cell-v">${emp.mobile||emp.phone_number}</div></div>`:""}
    ${emp.email?`<div class="cell"><div class="cell-l">Email</div><div class="cell-v">${emp.email}</div></div>`:""}
    ${emp.address&&(typeof emp.address==="string"?emp.address.trim():(emp.address?.full||"").trim())?`<div class="cell col3"><div class="cell-l">Address</div><div class="cell-v">${typeof emp.address==="string"?emp.address:emp.address.full}</div></div>`:""}
  </div>
</div>
${sortedHistory.length>0?`<div class="sec">
  <div class="sec-title">Employment History — ${sortedHistory.length} ${sortedHistory.length===1?"Company":"Companies"} · ${totalExp} Years Experience</div>
  <div class="timeline">
    ${sortedHistory.map(j=>{const isCurrent=!j.dor||j.dor===""||j.dor==="Present";return`<div class="job"><div class="job-co">${j.company||"—"}</div><div class="job-role">${j.designation||"—"}</div><div class="job-date">${j.doj||"—"} → ${isCurrent?"Present":j.dor}${j.salary?" · Rs."+(parseInt(j.salary)||0).toLocaleString("en-IN")+"/mo":""}</div><span class="badge ${isCurrent?"b-green":"b-gray"}">${isCurrent?"Currently Employed":"Relieved"}</span></div>`;}).join("")}
  </div>
  ${!hasExit?`<div class="warn">⚠ Last company exit date not available — experience calculated till current date</div>`:""}
</div>`:""}
<div class="sec">
  <div class="sec-title">Verification Summary</div>
  <div class="vgrid">
    <div class="vitem"><div class="vcheck ${emp.uan?"ok":"na"}">${emp.uan?"✓":"—"}</div><div><div class="vl">UAN / EPFO</div><div class="vs">${emp.uan?"Employment history verified":"Not provided"}</div></div></div>
    <div class="vitem"><div class="vcheck ${(emp.pan||record.searchType==="pan")?"ok":"na"}">${(emp.pan||record.searchType==="pan")?"✓":"—"}</div><div><div class="vl">PAN Card</div><div class="vs">${(emp.pan||record.searchType==="pan")?`${emp.status||"Valid"} · ${emp.aadhaar_linked?"Aadhaar Linked":""}  `:"Not provided"}</div></div></div>
    <div class="vitem"><div class="vcheck na">—</div><div><div class="vl">Aadhaar</div><div class="vs">DigiLocker — Coming Soon</div></div></div>
    <div class="vitem"><div class="vcheck na">—</div><div><div class="vl">Criminal Check</div><div class="vs">Run Full BGV for eCourt search</div></div></div>
  </div>
</div>
</div>
<div class="footer">
  <img src="${LOGO1}" style="height:26px;" alt="Meitho"/>
  <div class="f-note">This report is generated by Meitho BGV Platform. Data sourced from EPFO and Government APIs. Report ID: ${reportId}</div>
  <div class="f-contact">help@meitho.com<br/>meitho.com · 8300016265</div>
</div>
</div></body></html>`;
    const win=window.open("","_blank","width=900,height=700");
    if(!win){addToastGlobal?.("Please allow popups to download the BGV report","error");return;}
    win.document.write(html);
    win.document.close();
    win.focus();
    setTimeout(()=>{win.print();},600);
  };

  const exportCSV=()=>{
    const rows=[["Search Type","Value","Company","Searched By","Date","Result","Source","Cost"]];
    filtered.forEach(h=>{
      const cost=histCost(h,companies);
      rows.push([
        h.searchType||"",h.searchValue||"—",
        h.companyName||"—",h.userName||"—",
        (h.timestamp||"").slice(0,16),
        h.found?"Found":"Not Found",
        h.source||"api",
        h.found?"Rs."+cost:""
      ]);
    });
    const a=document.createElement("a");
    a.href="data:text/csv,"+encodeURIComponent(rows.map(r=>r.map(v=>'"'+v+'"').join(",")).join("\n"));
    a.download="meitho_history_"+todayStr()+".csv";a.click();
    addToastGlobal?.("History exported!","success");
  };

  return(
    <div>
      {/* Header */}
      <div className="fu" style={{display:"flex",justifyContent:"space-between",alignItems:"flex-end",marginBottom:20,flexWrap:"wrap",gap:12}}>
        <div>
          <h2 style={{fontFamily:FFD,fontWeight:700,fontSize:26,color:"var(--text)",marginBottom:4}}>Search History</h2>
          <p style={{color:"var(--muted)",fontSize:14}}>{myHistory.length} total searches</p>
        </div>
        <Btn style={{fontSize:13,padding:"9px 16px"}} onClick={exportCSV}>↓ Export CSV</Btn>
      </div>

      {/* Stats */}
      <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:12,marginBottom:18}} className="mg1">
        {[
          {label:"Total Searches",value:myHistory.length,color:"var(--brand)"},
          {label:"Found",value:myHistory.filter(h=>h.found).length,color:"var(--green)"},
          {label:"Not Found",value:myHistory.filter(h=>!h.found).length,color:"#ff3131"},
          {label:"This Month Spent",value:"₹"+thisMonthSpent.toLocaleString("en-IN"),color:"#ff914d"},
        ].map((s,i)=>(
          <div key={s.label} className={`fu${i} glass neon-hover`} style={{borderRadius:12,padding:"14px 16px",border:"1px solid var(--b1)"}}>
            <div style={{fontFamily:FFD,fontSize:24,fontWeight:800,color:s.color,marginBottom:2}}>{s.value}</div>
            <div style={{fontSize:11,color:"var(--muted)",fontWeight:600}}>{s.label}</div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="fu1 glass" style={{borderRadius:12,padding:14,border:"1px solid var(--b1)",marginBottom:16}}>
        <div style={{display:"flex",gap:10,marginBottom:12,flexWrap:"wrap"}}>
          <Inp style={{flex:1,minWidth:200}} value={q} onChange={e=>setQ(e.target.value)} placeholder={isSuperAdmin?"Search by name, company, user...":"Search by name or value..."}/>
        </div>
        <div style={{display:"flex",gap:6,flexWrap:"wrap",marginBottom:8}}>
          <span style={{fontSize:10,color:"var(--muted)",fontWeight:700,alignSelf:"center",marginRight:4}}>RESULT:</span>
          {[{id:"all",label:"All"},{id:"found",label:"✓ Found"},{id:"not_found",label:"✗ Not Found"}].map(t=>(
            <button key={t.id} onClick={()=>setFilter(t.id)} style={{padding:"5px 12px",border:"none",borderRadius:20,cursor:"pointer",fontSize:12,fontWeight:600,fontFamily:FF,background:filter===t.id?"var(--brand)":"var(--s2)",color:filter===t.id?"#fff":"var(--muted)"}}>
              {t.label}
            </button>
          ))}
        </div>
        <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
          <span style={{fontSize:10,color:"var(--muted)",fontWeight:700,alignSelf:"center",marginRight:4}}>TYPE:</span>
          {[{id:"all",label:"All"},{id:"uan",label:"🏦 UAN"},{id:"pan",label:"📋 PAN"},{id:"aadhaar",label:"🪪 Aadhaar"},{id:"full_bgv",label:"⚡ Full BGV"}].map(t=>(
            <button key={t.id} onClick={()=>setTypeFilter(t.id)} style={{padding:"5px 12px",border:"none",borderRadius:20,cursor:"pointer",fontSize:12,fontWeight:600,fontFamily:FF,background:typeFilter===t.id?"#ff914d":"var(--s2)",color:typeFilter===t.id?"#fff":"var(--muted)"}}>
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {/* Table */}
      <Card className="fu2" style={{overflow:"auto"}}>
        {filtered.length===0?(
          <div style={{textAlign:"center",padding:"32px 0",color:"var(--muted)",fontSize:13}}>No search history found</div>
        ):(
          <table style={{width:"100%",borderCollapse:"collapse",minWidth:700}}>
            <thead>
              <tr style={{borderBottom:"2px solid var(--b1)",background:"var(--s2)"}}>
                {(isSuperAdmin?["Type","Value/Name","Company","Searched By","Date","Result","Cost","Source","Actions"]:["Type","Value/Name","Date","Result","Cost","Source","Actions"]).map(h=>(
                  <th key={h} style={{textAlign:"left",padding:"12px 14px",fontSize:10,fontWeight:700,color:"var(--muted)",textTransform:"uppercase",letterSpacing:.8,whiteSpace:"nowrap"}}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map((r,i)=>{
                const ts=(r.timestamp||r.date||"");
                const dateStr=ts?new Date(ts).toLocaleString("en-IN",{day:"2-digit",month:"short",hour:"2-digit",minute:"2-digit"}):"—";
                const cost=histCost(r,companies);
                const empName=r.resultName||r.employeeData?.name||r.employeeData?.full_name||"";
                const displayValue=r.searchType==="ecourt"
                  ?(r.searchValue||r.employeeData?.name||"—")
                  :r.searchType==="dl"
                    ?(r.dl_number||r.searchValue||"—")
                    :(r.searchValue||"—");
                return(
                  <tr key={i} style={{borderBottom:"1px solid var(--b1)",transition:"background .1s"}} onMouseEnter={e=>e.currentTarget.style.background="var(--s2)"} onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
                    <td style={{padding:"12px 14px"}}>
                      <div style={{display:"flex",alignItems:"center",gap:6}}>
                        <span style={{fontSize:16}}>{{uan:"🏦",aadhaar:"🪪",pan:"📋",dl:"🚗",ecourt:"⚖️",full_bgv:"⚡",refcheck:"📧"}[r.searchType]||"🔍"}</span>
                        <span style={{fontSize:10,fontWeight:700,color:"var(--muted)",textTransform:"uppercase"}}>{r.searchType||"search"}</span>
                      </div>
                    </td>
                    <td style={{padding:"12px 14px"}}>
                      <div style={{fontSize:13,fontWeight:600,color:"var(--text)",fontFamily:FFM}}>{displayValue}</div>
                      {empName&&<div style={{fontSize:11,color:"var(--muted)"}}>{empName}</div>}
                      {r.searchType==="ecourt"&&r.ecourt_cases>0&&<div style={{fontSize:11,color:r.ecourt_criminal>0?"#ef4444":"var(--green)"}}>{r.ecourt_criminal>0?`⚠ ${r.ecourt_criminal} Criminal`:"✅ No Criminal"} · {r.ecourt_cases} total</div>}
                      {r.searchType==="dl"&&r.dl_state&&<div style={{fontSize:11,color:"var(--muted)"}}>📍 {r.dl_state} {r.dl_expiry?`· Exp: ${r.dl_expiry}`:""}</div>}
                    </td>
                    {isSuperAdmin&&<td style={{padding:"12px 14px",fontSize:12,color:"var(--sub)"}}>{r.companyName||"—"}</td>}
                    {isSuperAdmin&&<td style={{padding:"12px 14px",fontSize:12,color:"var(--muted)"}}>{r.userName||"—"}</td>}
                    <td style={{padding:"12px 14px",fontSize:11,color:"var(--muted)",whiteSpace:"nowrap"}}>{dateStr}</td>
                    <td style={{padding:"12px 14px"}}>
                      <Badge color={r.found?"var(--green)":"var(--red)"} style={{fontSize:10}}>{r.found?"✓ Found":"✗ Not Found"}</Badge>
                    </td>
                    <td style={{padding:"12px 14px",fontSize:13,fontWeight:700,color:cost>0?"var(--brand)":"var(--muted)",fontFamily:FFD}}>{cost>0?"₹"+cost:"—"}</td>
                    <td style={{padding:"12px 14px"}}>
                      <Badge color={r.source==="db"?"#8b5cf6":"var(--brand)"} style={{fontSize:9}}>{r.source==="db"?"DB Cache":"Live API"}</Badge>
                    </td>
                    <td style={{padding:"12px 14px"}}>
                      <div style={{display:"flex",gap:5}}>
                        {r.found&&r.searchType==="dl"&&<button onClick={()=>{
                          const d=r.employeeData||{};
                          const isExp=d.dl_expiry&&new Date(d.dl_expiry)<new Date();
                          const html=`<!DOCTYPE html><html><head><meta charset="UTF-8"/><title>DL Report</title>
<style>*{margin:0;padding:0;box-sizing:border-box;}body{font-family:'Segoe UI',sans-serif;color:#1a1a2e;}</style></head><body>
<div style="background:#0a0a1a;padding:16px 32px;display:flex;justify-content:space-between;align-items:center;">
<div><img src="${LOGO1}" style="height:30px;" alt="Meitho BGV"/><div style="font-size:9px;color:#0cc0df;letter-spacing:2px;margin-top:4px;">DRIVING LICENSE VERIFICATION</div></div>
<div style="text-align:right;color:#fff;font-size:10px;">${new Date().toLocaleString("en-IN")}</div></div>
<div style="background:#0cc0df;padding:5px 32px;font-size:9px;color:#fff;font-weight:700;">MEITHO BGV · meitho.com · help@meitho.com · 8300016265</div>
<div style="padding:24px 32px;">
<div style="display:inline-flex;align-items:center;gap:6px;background:linear-gradient(135deg,#ff3131,#059669);padding:5px 14px;border-radius:99px;margin-bottom:12px;">
  <span style="color:#fff;font-size:11px;font-weight:800;letter-spacing:.5px;">✅ MEITHO VERIFIED</span>
</div>
<div style="font-size:22px;font-weight:900;margin-bottom:4px;">${d.name||r.searchValue||"—"}</div>
<div style="font-family:monospace;color:#64748b;margin-bottom:16px;">${d.dl_number||r.dl_number||r.searchValue||"—"}</div>
<div style="display:grid;grid-template-columns:1fr 1fr 1fr;gap:12px;margin-bottom:16px;">
${[["DL Number",d.dl_number||r.dl_number||r.searchValue],["Date of Birth",d.dob],["Father/Spouse",d.father_name],["Blood Group",d.blood_group],["Issue Date",d.dl_issue||d.doi],["Expiry Date",d.dl_expiry||d.doe],["State",d.state||r.dl_state||d.dl_state],["Issuing RTO",d.ola_name],["Status",isExp?"EXPIRED":"VALID"]].filter(([,v])=>v).map(([l,v])=>`
<div style="background:#f8fafc;border-radius:8px;padding:10px 12px;border-left:3px solid ${isExp&&l==="Status"?"#ef4444":"#0cc0df"};">
<div style="font-size:8px;color:#94a3b8;font-weight:700;text-transform:uppercase;margin-bottom:3px;">${l}</div>
<div style="font-size:13px;font-weight:700;color:${isExp&&l==="Status"?"#ef4444":"#0a0a1a"};">${v}</div></div>`).join("")}
</div>
${d.permanent_address?`<div style="background:#f8fafc;border-radius:8px;padding:12px;border-left:3px solid #0cc0df;margin-bottom:16px;"><div style="font-size:8px;color:#94a3b8;font-weight:700;text-transform:uppercase;margin-bottom:3px;">ADDRESS</div><div style="font-size:13px;">${d.permanent_address}</div></div>`:""}
${(d.vehicle_classes||[]).length>0?`<div style="margin-bottom:16px;"><div style="font-size:10px;font-weight:700;color:#0cc0df;margin-bottom:8px;text-transform:uppercase;">Vehicle Classes</div><div style="display:flex;gap:8px;">${(d.vehicle_classes||[]).map(v=>`<span style="padding:5px 12px;background:#0cc0df15;border:1px solid #0cc0df30;border-radius:6px;color:#0cc0df;font-size:12px;font-weight:700;">🚗 ${v}</span>`).join("")}</div></div>`:""}
</div>
<div style="background:#0a0a1a;padding:12px 32px;display:flex;justify-content:space-between;">
<div style="font-size:10px;color:rgba(255,255,255,0.4);">Source: RTO Database · Powered by Surepass</div>
<div style="font-size:10px;color:#0cc0df;">meitho.com</div></div>
<div class="no-print" style="padding:16px 32px;background:#f8fafc;text-align:right;">
<button onclick="window.print()" style="background:linear-gradient(90deg,#ff3131,#ff914d);color:#fff;border:none;padding:8px 20px;border-radius:8px;cursor:pointer;font-size:13px;font-weight:700;">⬇ Download PDF</button></div>
</body></html>`;
                          const win=window.open("","_blank","width=1000,height=800");
                          if(!win){addToast("Allow popups","error");return;}
                          win.document.write(html);win.document.close();win.focus();
                          setTimeout(()=>win.print(),800);
                        }} style={{background:"var(--green)10",border:"1px solid var(--green)30",borderRadius:7,color:"var(--green)",padding:"5px 8px",cursor:"pointer",fontSize:11,fontWeight:700,fontFamily:FF}}>↓</button>}
                        {r.found&&r.searchType!=="dl"&&<button onClick={()=>generateBGVReport(r)} style={{background:"var(--green)10",border:"1px solid var(--green)30",borderRadius:7,color:"var(--green)",padding:"5px 8px",cursor:"pointer",fontSize:11,fontWeight:700,fontFamily:FF,whiteSpace:"nowrap"}}>↓</button>}
                        <button onClick={async()=>{
                          if(!window.confirm("Delete this record?"))return;
                          try{
                            if(r.id)await deleteDoc(doc(db,"searchHistory",r.id));
                            addToast("Deleted!","success");
                          }catch(e){addToast("Delete failed: "+e.message,"error");}
                        }} style={{background:"rgba(255,49,49,0.08)",border:"1px solid rgba(255,49,49,0.2)",borderRadius:7,color:"#ff3131",padding:"5px 8px",cursor:"pointer",fontSize:11,fontWeight:700,fontFamily:FF}}>🗑</button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </Card>
    </div>
  );
}


function CompaniesTab({history,wallets,companies,days,byCompany,tt,COLORS,KPI,SectionTitle,dateRange}) {
  const [selCompany,setSelCompany]=useState(null);
  const Card=({children,style={}})=><div className="fu" style={{borderRadius:14,padding:20,...style}}>{children}</div>;

  if(selCompany){
    const c=selCompany;
    const cHistory=history.filter(h=>h.companyId===c.id);
    const cWallet=(wallets||{})[c.id]?.balance||0;
    const cSearches=cHistory.length;
    const cCost=cHistory.reduce((s,h)=>s+(h.cost||0),0);
    const cFound=cHistory.filter(h=>h.found).length;
    const cFoundRate=cSearches>0?Math.round((cFound/cSearches)*100):0;
    const burnRate=days>0&&cCost>0?Math.round(cCost/days):0;
    const runway=burnRate>0?Math.round(cWallet/burnRate):999;
    const cByType=Object.entries(cHistory.reduce((acc,h)=>{
      const t=h.searchType||"other";
      if(!acc[t])acc[t]={count:0,cost:0,found:0};
      acc[t].count++;acc[t].cost+=h.cost||0;if(h.found)acc[t].found++;
      return acc;
    },{})).map(([type,d])=>({type,label:{uan:"UAN",pan:"PAN",aadhaar:"Aadhaar",dl:"DL",ecourt:"Criminal",full_bgv:"Full BGV",refcheck:"Ref Check"}[type]||type,...d}));
    const cByUser=Object.entries(cHistory.reduce((acc,h)=>{
      const n=h.userName||"Unknown";
      if(!acc[n])acc[n]={searches:0,cost:0,found:0};
      acc[n].searches++;acc[n].cost+=h.cost||0;if(h.found)acc[n].found++;
      return acc;
    },{})).map(([name,d])=>({name,...d,rate:d.searches>0?Math.round((d.found/d.searches)*100):0})).sort((a,b)=>b.searches-a.searches);

    return(
      <div>
        <button onClick={()=>setSelCompany(null)} style={{background:"var(--s2)",border:"1px solid var(--b1)",borderRadius:8,color:"var(--text)",padding:"7px 14px",cursor:"pointer",fontSize:12,fontWeight:600,fontFamily:FF,marginBottom:16}}>← Back to Companies</button>
        <div className="fu" style={{marginBottom:16,padding:"20px 24px"}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
            <div>
              <div style={{fontFamily:FFD,fontSize:22,fontWeight:800,color:"var(--text)",marginBottom:4}}>{c.name}</div>
              <div style={{fontSize:13,color:"var(--muted)"}}>{c.email} · {c.industry||""}</div>
            </div>
            <Badge color={c.status==="Active"?"var(--green)":c.status==="Rejected"?"var(--red)":"#f59e0b"}>{c.status||"Pending"}</Badge>
          </div>
        </div>
        <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:14,marginBottom:16}} className="mg2">
          <KPI icon="🔍" label="Total Searches" value={cSearches} color="var(--brand)"/>
          <KPI icon="💰" label="Total Spent" value={"₹"+cCost.toLocaleString("en-IN")} color="#ff914d"/>
          <KPI icon="✅" label="Found Rate" value={cFoundRate+"%"} color="#ff3131"/>
          <KPI icon="💳" label="Wallet Balance" value={"₹"+cWallet.toLocaleString("en-IN")} color={cWallet<1000?"#ff3131":"#8b5cf6"}/>
        </div>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:16,marginBottom:16}} className="mg1">
          <Card>
            <SectionTitle>Search Type Breakdown</SectionTitle>
            <table style={{width:"100%",borderCollapse:"collapse"}}>
              <thead><tr style={{background:"var(--s2)"}}>{["Type","Searches","Found%","Cost"].map(h=><th key={h} style={{padding:"8px 12px",textAlign:"left",fontSize:10,fontWeight:700,color:"var(--muted)"}}>{h}</th>)}</tr></thead>
              <tbody>
                {cByType.map((t,i)=>(
                  <tr key={i} style={{borderBottom:"1px solid var(--b1)"}}>
                    <td style={{padding:"8px 12px",fontSize:12,fontWeight:600,color:"var(--text)"}}>{t.label}</td>
                    <td style={{padding:"8px 12px",fontSize:12}}>{t.count}</td>
                    <td style={{padding:"8px 12px",fontSize:12,color:"var(--green)",fontWeight:700}}>{t.count>0?Math.round((t.found/t.count)*100):0}%</td>
                    <td style={{padding:"8px 12px",fontSize:12,color:"var(--brand)",fontWeight:700}}>₹{t.cost}</td>
                  </tr>
                ))}
                {cByType.length===0&&<tr><td colSpan={4} style={{textAlign:"center",padding:20,color:"var(--muted)",fontSize:12}}>No searches yet</td></tr>}
              </tbody>
            </table>
          </Card>
          <Card>
            <SectionTitle>HR User Performance</SectionTitle>
            <table style={{width:"100%",borderCollapse:"collapse"}}>
              <thead><tr style={{background:"var(--s2)"}}>{["HR User","Searches","Found%","Spent"].map(h=><th key={h} style={{padding:"8px 12px",textAlign:"left",fontSize:10,fontWeight:700,color:"var(--muted)"}}>{h}</th>)}</tr></thead>
              <tbody>
                {cByUser.map((u,i)=>(
                  <tr key={i} style={{borderBottom:"1px solid var(--b1)"}}>
                    <td style={{padding:"8px 12px",fontSize:12,fontWeight:600,color:"var(--text)"}}>{u.name}</td>
                    <td style={{padding:"8px 12px",fontSize:12}}>{u.searches}</td>
                    <td style={{padding:"8px 12px",fontSize:12,color:"var(--green)",fontWeight:700}}>{u.rate}%</td>
                    <td style={{padding:"8px 12px",fontSize:12,color:"var(--brand)",fontWeight:700}}>₹{u.cost}</td>
                  </tr>
                ))}
                {cByUser.length===0&&<tr><td colSpan={4} style={{textAlign:"center",padding:20,color:"var(--muted)",fontSize:12}}>No data</td></tr>}
              </tbody>
            </table>
          </Card>
        </div>
        <Card>
          <SectionTitle>Recent Searches</SectionTitle>
          <div style={{maxHeight:240,overflowY:"auto"}}>
            {cHistory.slice(0,20).map((h,i)=>(
              <div key={i} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"8px 0",borderBottom:"1px solid var(--b1)"}}>
                <div style={{display:"flex",gap:10,alignItems:"center"}}>
                  <span style={{fontSize:15}}>{{uan:"🏦",pan:"📋",aadhaar:"🪪",dl:"🚗",ecourt:"⚖️",full_bgv:"⚡",refcheck:"📧"}[h.searchType]||"🔍"}</span>
                  <div>
                    <div style={{fontSize:12,color:"var(--text)",fontWeight:500}}>{h.searchValue||"—"} · <span style={{color:"var(--muted)"}}>{h.searchType?.toUpperCase()}</span></div>
                    <div style={{fontSize:10,color:"var(--muted)"}}>{h.timestamp?.slice(0,10)} · {h.userName||""}</div>
                  </div>
                </div>
                <div style={{display:"flex",gap:8,alignItems:"center"}}>
                  <Badge color={h.found?"var(--green)":"var(--red)"} style={{fontSize:9}}>{h.found?"Found":"Not Found"}</Badge>
                  <span style={{fontSize:12,fontWeight:700,color:"var(--brand)"}}>₹{h.cost||0}</span>
                </div>
              </div>
            ))}
            {cHistory.length===0&&<div style={{textAlign:"center",padding:24,color:"var(--muted)",fontSize:13}}>No searches yet</div>}
          </div>
        </Card>
      </div>
    );
  }

  return(
    <div>
      <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:14,marginBottom:20}} className="mg2">
        <KPI icon="🏢" label="Total Companies" value={companies.length} color="var(--brand)"/>
        <KPI icon="✅" label="Active" value={companies.filter(c=>c.status==="Active").length} color="#ff3131"/>
        <KPI icon="💳" label="Total Wallet" value={"₹"+Object.values(wallets||{}).reduce((s,w)=>s+(w.balance||0),0).toLocaleString("en-IN")} color="#ff914d"/>
        <KPI icon="⏳" label="Pending" value={companies.filter(c=>!c.status||c.status==="Pending").length} color="#f59e0b"/>
      </div>
      {companies.filter(c=>(wallets||{})[c.id]?.balance<1000&&c.status==="Active").length>0&&(
        <div style={{background:"rgba(255,49,49,0.06)",border:"1px solid rgba(255,49,49,0.2)",borderRadius:10,padding:"12px 16px",marginBottom:16,display:"flex",gap:10,alignItems:"center"}}>
          <span>⚠️</span>
          <div style={{fontSize:13,color:"var(--text)"}}>
            <strong>{companies.filter(c=>(wallets||{})[c.id]?.balance<1000&&c.status==="Active").length} companies</strong> have low wallet balance (&lt;₹1,000)
          </div>
        </div>
      )}
      {byCompany.length>0&&(
        <Card style={{marginBottom:16}}>
          <SectionTitle>Top Companies by Spend</SectionTitle>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={byCompany} layout="vertical">
              <XAxis type="number" tick={{fontSize:10,fill:"var(--muted)"}} tickFormatter={v=>"₹"+v}/>
              <YAxis type="category" dataKey="name" tick={{fontSize:11,fill:"var(--muted)"}} width={130}/>
              <Tooltip {...tt} formatter={v=>"₹"+v}/>
              <Bar dataKey="cost" fill="#ff3131" radius={[0,4,4,0]} name="Spent"/>
            </BarChart>
          </ResponsiveContainer>
        </Card>
      )}
      <Card>
        <SectionTitle>All Companies</SectionTitle>
        <div style={{overflowX:"auto"}}>
          <table style={{width:"100%",borderCollapse:"collapse",minWidth:700}}>
            <thead><tr style={{background:"var(--s2)"}}>
              {["Company","Status","Wallet","Searches","Spent","Industry",""].map(h=>(
                <th key={h} style={{padding:"10px 12px",textAlign:"left",fontSize:10,fontWeight:700,color:"var(--muted)",textTransform:"uppercase"}}>{h}</th>
              ))}
            </tr></thead>
            <tbody>
              {companies.map((c,i)=>{
                const cWallet=(wallets||{})[c.id]?.balance||0;
                const cSearches=history.filter(h=>h.companyId===c.id).length;
                const cCost=history.filter(h=>h.companyId===c.id).reduce((s,h)=>s+(h.cost||0),0);
                const lowBal=cWallet<1000&&c.status==="Active";
                return(
                  <tr key={i} style={{borderBottom:"1px solid var(--b1)",background:lowBal?"rgba(255,49,49,0.03)":"transparent"}}>
                    <td style={{padding:"10px 12px"}}>
                      <div style={{fontSize:13,fontWeight:600,color:"var(--text)"}}>{c.name}</div>
                      <div style={{fontSize:10,color:"var(--muted)"}}>{c.email||""}</div>
                    </td>
                    <td style={{padding:"10px 12px"}}><Badge color={c.status==="Active"?"var(--green)":c.status==="Rejected"?"var(--red)":"#f59e0b"} style={{fontSize:9}}>{c.status||"Pending"}</Badge></td>
                    <td style={{padding:"10px 12px"}}>
                      <div style={{fontSize:13,fontWeight:700,color:lowBal?"#ff3131":"var(--green)"}}>₹{cWallet.toLocaleString("en-IN")}</div>
                      {lowBal&&<div style={{fontSize:9,color:"#ff3131",fontWeight:700}}>⚠ LOW</div>}
                    </td>
                    <td style={{padding:"10px 12px",fontSize:13,textAlign:"center"}}>{cSearches}</td>
                    <td style={{padding:"10px 12px",fontSize:13,fontWeight:700,color:"var(--brand)"}}>₹{cCost.toLocaleString("en-IN")}</td>
                    <td style={{padding:"10px 12px",fontSize:12,color:"var(--muted)"}}>{c.industry||"—"}</td>
                    <td style={{padding:"10px 12px"}}>
                      <button onClick={()=>setSelCompany(c)} style={{background:"rgba(12,192,223,0.1)",border:"1px solid rgba(12,192,223,0.2)",borderRadius:6,color:"var(--brand)",padding:"4px 10px",cursor:"pointer",fontSize:11,fontWeight:700,fontFamily:FF}}>View →</button>
                    </td>
                  </tr>
                );
              })}
              {companies.length===0&&<tr><td colSpan={7} style={{textAlign:"center",padding:32,color:"var(--muted)",fontSize:13}}>No companies found</td></tr>}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}

function AnalyticsPage({employees,history,companies,user,company,isSuperAdmin,wallets}) {
  const {dark}=useTheme();
  const [dateRange,setDateRange]=useState("30");
  const [tab,setTab]=useState("overview");
  const isCompanyAdmin=user?.role==="admin"||user?.companyAdmin===true;
  const isHRUser=!isSuperAdmin&&!isCompanyAdmin; // regular HR (non-admin) team member
  const reportsLocked=isHRUser&&user?.searchPermissions?.reports!==true;
  const [scope,setScope]=useState(isHRUser?"mine":"company"); // "mine" | "company" — HR-level report toggle

  // Gate: HR staff need explicit report access granted by their Company
  // Admin. Safe to early-return here — every hook in this component is
  // already declared above this line, so no hooks get skipped.
  if(reportsLocked){
    return(
      <div style={{textAlign:"center",padding:"80px 20px"}}>
        <div style={{fontSize:40,marginBottom:16}}>🔒</div>
        <div style={{fontFamily:FFD,fontSize:18,fontWeight:800,color:"var(--text)",marginBottom:8}}>Reports Access Not Granted</div>
        <div style={{fontSize:13,color:"var(--muted)",maxWidth:360,margin:"0 auto"}}>Your Company Admin hasn't given you access to Reports yet. Ask them to enable it from Companies → HR Users → Permissions.</div>
      </div>
    );
  }

  const tt={contentStyle:{background:"var(--s2)",border:"1px solid var(--b2)",borderRadius:8,fontSize:12,color:"var(--text)"},cursor:{fill:"rgba(255,255,255,0.04)"}};
  const COLORS=["#0cc0df","#ff3131","#ff914d","#ff3131","#8b5cf6","#f59e0b","#ef4444"];
  const PRICES={uan:100,pan:50,aadhaar:50,dl:50,ecourt:75,full_bgv:450,refcheck:125};

  // Filter history by date range
  const now=new Date();
  const days=parseInt(dateRange);
  const cutoff=new Date(now-days*24*60*60*1000);
  const companyId=user?.companyId||company?.id||"";

  const myHistory=isSuperAdmin
    ? history.filter(h=>new Date(h.timestamp||h.createdAt||0)>=cutoff)
    : history.filter(h=>{
        const inCompany=h.companyId===companyId||h.userId===user?.id;
        const inDate=new Date(h.timestamp||h.createdAt||0)>=cutoff;
        const inScope=scope==="mine"?(h.userId===user?.id||h.userName===(user?.name||user?.email)):true;
        return inCompany&&inDate&&inScope;
      });

  // KPI calculations
  const totalSearches=myHistory.length;
  const totalCost=myHistory.reduce((s,h)=>s+(h.cost||PRICES[h.searchType]||0),0);
  const foundCount=myHistory.filter(h=>h.found).length;
  const foundRate=totalSearches>0?Math.round((foundCount/totalSearches)*100):0;
  const dbHits=myHistory.filter(h=>h.source==="db").length;
  const dbRate=totalSearches>0?Math.round((dbHits/totalSearches)*100):0;

  // Search type breakdown
  const byType=Object.entries(myHistory.reduce((acc,h)=>{
    const t=h.searchType||"unknown";
    if(!acc[t])acc[t]={count:0,cost:0,found:0};
    acc[t].count++;
    acc[t].cost+=h.cost||PRICES[t]||0;
    if(h.found)acc[t].found++;
    return acc;
  },{})).map(([type,data])=>({
    type,label:{uan:"UAN",pan:"PAN",aadhaar:"Aadhaar",dl:"DL",ecourt:"Criminal",full_bgv:"Full BGV",refcheck:"Ref Check"}[type]||type,
    ...data,
    foundRate:data.count>0?Math.round((data.found/data.count)*100):0,
  })).sort((a,b)=>b.count-a.count);

  // Daily trend (last N days)
  const trendDays=Math.min(days,30);
  const trendData=Array.from({length:trendDays},(_,i)=>{
    const d=new Date(now);d.setDate(d.getDate()-(trendDays-1-i));
    const ds=d.toISOString().split("T")[0];
    const dayHistory=myHistory.filter(h=>(h.timestamp||"").startsWith(ds));
    return{
      day:d.toLocaleDateString("en-IN",{day:"numeric",month:"short"}),
      searches:dayHistory.length,
      cost:dayHistory.reduce((s,h)=>s+(h.cost||0),0),
    };
  });

  // Super Admin: Company breakdown
  const byCompany=isSuperAdmin?Object.entries(myHistory.reduce((acc,h)=>{
    const n=h.companyName||h.companyId||"Unknown";
    if(!acc[n])acc[n]={searches:0,cost:0};
    acc[n].searches++;acc[n].cost+=h.cost||0;
    return acc;
  },{})).map(([name,d])=>({name:name.slice(0,20),...d})).sort((a,b)=>b.cost-a.cost).slice(0,10):[];

  // Company: HR user breakdown
  const byUser=!isSuperAdmin?Object.entries(myHistory.reduce((acc,h)=>{
    const n=h.userName||h.userId||"Unknown";
    if(!acc[n])acc[n]={searches:0,cost:0,found:0};
    acc[n].searches++;acc[n].cost+=h.cost||0;
    if(h.found)acc[n].found++;
    return acc;
  },{})).map(([name,d])=>({name:name.slice(0,20),...d,rate:d.searches>0?Math.round((d.found/d.searches)*100):0})).sort((a,b)=>b.searches-a.searches):[];

  // Wallet info
  const walletBal=isSuperAdmin
    ? Object.values(wallets||{}).reduce((s,w)=>s+(w.balance||0),0)
    : (wallets||{})[companyId]?.balance||0;

  // Donut chart data
  const donutData=byType.map((t,i)=>({name:t.label,value:t.count,color:COLORS[i%COLORS.length]}));

  // Score distribution from history
  const scores=myHistory.filter(h=>h.meithoScore>0).map(h=>h.meithoScore);
  const scoreDistData=[
    {name:"Elite 85+",count:scores.filter(s=>s>=85).length,color:"#ff3131"},
    {name:"Good 65-84",count:scores.filter(s=>s>=65&&s<85).length,color:"#0cc0df"},
    {name:"Moderate 45-64",count:scores.filter(s=>s>=45&&s<65).length,color:"#ff914d"},
    {name:"Risk <45",count:scores.filter(s=>s<45).length,color:"#ef4444"},
  ].filter(d=>d.count>0);

  const KPI=({label,value,sub,color="#0cc0df",icon})=>(
    <div className="fu" style={{padding:"18px 20px",borderRadius:14}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:8}}>
        <div style={{fontSize:22}}>{icon}</div>
        {sub&&<div style={{fontSize:10,color:"var(--muted)",fontWeight:600,textAlign:"right"}}>{sub}</div>}
      </div>
      <div style={{fontFamily:FFD,fontSize:28,fontWeight:900,color,lineHeight:1,marginBottom:4}}>{value}</div>
      <div style={{fontSize:12,color:"var(--muted)",fontWeight:600}}>{label}</div>
    </div>
  );

  const SectionTitle=({children})=>(
    <div style={{fontFamily:FFD,fontSize:13,fontWeight:700,color:"var(--sub)",letterSpacing:.8,textTransform:"uppercase",marginBottom:14,marginTop:8,display:"flex",alignItems:"center",gap:8}}>
      <div style={{width:3,height:14,background:"var(--brand)",borderRadius:2}}/>
      {children}
    </div>
  );

  return(
    <div style={{paddingBottom:40}}>
      {/* Header */}
      <div className="fu" style={{marginBottom:20,display:"flex",justifyContent:"space-between",alignItems:"center",flexWrap:"wrap",gap:12}}>
        <div>
          <h2 style={{fontFamily:FFD,fontWeight:800,fontSize:26,color:"var(--text)",marginBottom:2}}>
            {isSuperAdmin?"Platform Analytics":isHRUser?"My Reports":"Company Analytics"}
          </h2>
          <p style={{color:"var(--muted)",fontSize:13}}>{isSuperAdmin?"Complete platform overview · All companies":isHRUser?(scope==="mine"?"Your activity · "+(user?.name||user?.email||""):"Company-wide · "+(company?.name||"")):"Your verification insights · "+company?.name}</p>
        </div>
        <div style={{display:"flex",gap:8,alignItems:"center",flexWrap:"wrap"}}>
          {isHRUser&&(
            <div style={{display:"flex",background:"var(--s2)",borderRadius:8,padding:3,border:"1px solid var(--b1)"}}>
              <button onClick={()=>setScope("mine")} style={{padding:"6px 12px",borderRadius:6,border:"none",background:scope==="mine"?"var(--bg)":"transparent",color:scope==="mine"?"var(--text)":"var(--muted)",cursor:"pointer",fontSize:12,fontWeight:scope==="mine"?700:500,fontFamily:FF}}>👤 My Activity</button>
              <button onClick={()=>setScope("company")} style={{padding:"6px 12px",borderRadius:6,border:"none",background:scope==="company"?"var(--bg)":"transparent",color:scope==="company"?"var(--text)":"var(--muted)",cursor:"pointer",fontSize:12,fontWeight:scope==="company"?700:500,fontFamily:FF}}>🏢 Company</button>
            </div>
          )}
          <select value={dateRange} onChange={e=>setDateRange(e.target.value)}
            style={{padding:"8px 14px",background:"var(--s2)",border:"1px solid var(--b1)",borderRadius:8,color:"var(--text)",fontSize:13,fontFamily:FF,cursor:"pointer",outline:"none"}}>
            <option value="7">Last 7 days</option>
            <option value="30">Last 30 days</option>
            <option value="90">Last 90 days</option>
            <option value="365">Last 1 year</option>
          </select>
          <button onClick={()=>{
            const rows=[["Date","Type","Search Value","Found","Cost","Source","Company/User"],...myHistory.map(h=>[h.timestamp?.slice(0,10)||"",h.searchType||"",h.searchValue||"",h.found?"Yes":"No",h.cost||0,h.source||"",h.companyName||h.userName||""])];
            const csv=rows.map(r=>r.join(",")).join("\n");
            const a=document.createElement("a");a.href="data:text/csv;charset=utf-8,"+encodeURIComponent(csv);a.download="meitho_analytics.csv";a.click();
          }} style={{background:"var(--s2)",border:"1px solid var(--b1)",borderRadius:8,color:"var(--text)",padding:"8px 14px",cursor:"pointer",fontSize:12,fontWeight:600,fontFamily:FF}}>
            ↓ CSV
          </button>
          {<button onClick={()=>{
            const dt=new Date().toLocaleString("en-IN");
            const reportTitle=isSuperAdmin?"PLATFORM REPORT":isHRUser&&scope==="mine"?"MY ACTIVITY REPORT":"ANALYTICS REPORT";
            const reportOwner=isSuperAdmin?"Meitho BGV — All Companies":isHRUser&&scope==="mine"?(user?.name||user?.email||""):(company?.name||"");
            const html=`<!DOCTYPE html><html><head><meta charset="UTF-8"/><title>${reportTitle}</title>
<style>*{margin:0;padding:0;box-sizing:border-box;}body{font-family:'Segoe UI',Arial,sans-serif;color:#1a1a2e;background:#fff;}
@media print{.no-print{display:none!important;}body{-webkit-print-color-adjust:exact;print-color-adjust:exact;}}
table{width:100%;border-collapse:collapse;}th{background:#0a0a1a;color:#fff;padding:10px 14px;font-size:11px;text-align:left;}
td{padding:9px 14px;border-bottom:1px solid #f1f5f9;font-size:12px;}tr:last-child td{border:none;}
</style></head><body>
<div style="background:linear-gradient(135deg,#0a0a1a,#1a1a2e);padding:28px 32px;display:flex;justify-content:space-between;align-items:center;">
  <div><div style="font-size:24px;font-weight:900;color:#fff;">Meitho</div><div style="font-size:11px;color:#0cc0df;margin-top:4px;letter-spacing:2px;">${reportTitle}</div></div>
  <div style="text-align:right;color:rgba(255,255,255,0.5);font-size:11px;">${dt}<br/>${reportOwner}<br/>Last ${dateRange} days</div>
</div>
<div style="background:#0cc0df;height:3px;"></div>
<div style="padding:24px 32px;">
  <div style="display:grid;grid-template-columns:repeat(4,1fr);gap:12px;margin-bottom:24px;">
    ${[["Total Searches",totalSearches,"#0cc0df"],["Total Spent","₹"+totalCost.toLocaleString("en-IN"),"#ff914d"],["Found Rate",foundRate+"%","#ff3131"],["Wallet Balance","₹"+walletBal.toLocaleString("en-IN"),"#8b5cf6"]].map(([l,v,c])=>`
    <div style="background:#f8fafc;border-radius:10px;padding:14px;border-left:3px solid ${c};">
      <div style="font-size:9px;color:#94a3b8;font-weight:700;text-transform:uppercase;margin-bottom:4px;">${l}</div>
      <div style="font-size:22px;font-weight:900;color:${c};">${v}</div>
    </div>`).join("")}
  </div>
  <div style="margin-bottom:20px;">
    <div style="font-size:11px;font-weight:700;color:#0cc0df;text-transform:uppercase;letter-spacing:1px;margin-bottom:10px;border-bottom:2px solid #0cc0df;padding-bottom:4px;">Search Type Breakdown</div>
    <table>
      <thead><tr><th>Type</th><th>Searches</th><th>Found</th><th>Found Rate</th><th>Total Cost</th></tr></thead>
      <tbody>${byType.map(t=>`<tr><td>${t.label}</td><td>${t.count}</td><td>${t.found}</td><td>${t.foundRate}%</td><td>₹${t.cost.toLocaleString("en-IN")}</td></tr>`).join("")}</tbody>
    </table>
  </div>
  <div>
    <div style="font-size:11px;font-weight:700;color:#0cc0df;text-transform:uppercase;letter-spacing:1px;margin-bottom:10px;border-bottom:2px solid #0cc0df;padding-bottom:4px;">Recent Searches</div>
    <table>
      <thead><tr><th>Date</th><th>Type</th><th>Search Value</th><th>Result</th><th>Cost</th></tr></thead>
      <tbody>${myHistory.slice(0,30).map(h=>`<tr><td>${(h.timestamp||"").slice(0,10)}</td><td>${h.searchType||""}</td><td>${h.searchValue||""}</td><td style="color:${h.found?"#ff3131":"#ef4444"}">${h.found?"Found":"Not Found"}</td><td>₹${h.cost||0}</td></tr>`).join("")}</tbody>
    </table>
  </div>
</div>
<div style="background:#0a0a1a;padding:14px 32px;text-align:center;font-size:10px;color:rgba(255,255,255,0.4);">
  Meitho BGV · meitho.com · Generated ${dt}
</div>
<div class="no-print" style="padding:16px 32px;background:#f8fafc;text-align:right;">
  <button onclick="window.print()" style="background:linear-gradient(90deg,#ff3131,#ff914d);color:#fff;border:none;padding:10px 24px;border-radius:8px;cursor:pointer;font-size:13px;font-weight:700;">⬇ Download PDF Report</button>
</div>
</body></html>`;
            const win=window.open("","_blank","width=1000,height=800");
            if(!win){return;}
            win.document.write(html);win.document.close();win.focus();
            setTimeout(()=>win.print(),800);
          }} style={{background:"linear-gradient(90deg,#ff3131,#ff914d)",border:"none",borderRadius:8,color:"#fff",padding:"8px 14px",cursor:"pointer",fontSize:12,fontWeight:700,fontFamily:FF}}>
            📄 PDF Report
          </button>}
        </div>
      </div>

      {/* Tabs */}
      <div style={{display:"flex",gap:6,marginBottom:20,borderBottom:"1px solid var(--b1)",paddingBottom:0}}>
        {(isSuperAdmin
          ?[{id:"overview",l:"📊 Overview"},{id:"searches",l:"🔍 Searches"},{id:"revenue",l:"💰 Revenue"},{id:"companies",l:"🏢 Companies"}]
          :[{id:"overview",l:"📊 Overview"},{id:"searches",l:"🔍 Searches"},{id:"cost",l:"💰 Cost"},{id:"users",l:"👥 HR Users"}]
        ).map(t=>(
          <button key={t.id} onClick={()=>setTab(t.id)} style={{padding:"8px 16px",background:"transparent",border:"none",borderBottom:`2px solid ${tab===t.id?"var(--brand)":"transparent"}`,color:tab===t.id?"var(--brand)":"var(--muted)",cursor:"pointer",fontSize:13,fontWeight:tab===t.id?700:500,fontFamily:FF,marginBottom:-1}}>
            {t.l}
          </button>
        ))}
      </div>

      {/* Overview Tab */}
      {tab==="overview"&&(
        <div>
          {/* KPI Cards */}
          <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:14,marginBottom:20}} className="mg2">
            <KPI icon="🔍" label="Total Searches" value={totalSearches.toLocaleString("en-IN")} sub={`Last ${dateRange} days`} color="var(--brand)"/>
            <KPI icon="💰" label="Total Spent" value={"₹"+totalCost.toLocaleString("en-IN")} sub="Platform fee excl." color="#ff914d"/>
            <KPI icon="✅" label="Found Rate" value={foundRate+"%"} sub={foundCount+" found"} color="#ff3131"/>
            <KPI icon="🗄️" label="DB Cache Rate" value={dbRate+"%"} sub={dbHits+" from DB"} color="#8b5cf6"/>
          </div>
          {isSuperAdmin&&(
            <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:14,marginBottom:20}} className="mg2">
              <KPI icon="🏢" label="Active Companies" value={companies.filter(c=>c.status==="Active").length} color="var(--brand)"/>
              <KPI icon="⏳" label="Pending Approval" value={companies.filter(c=>c.status==="Pending"||!c.status).length} color="#f59e0b"/>
              <KPI icon="💳" label="Total Wallet Balance" value={"₹"+walletBal.toLocaleString("en-IN")} color="#ff3131"/>
              <KPI icon="📈" label="Platform Revenue" value={"₹"+Math.round(totalCost*0.1).toLocaleString("en-IN")} sub="10% platform fee" color="#ff3131"/>
            </div>
          )}
          {!isSuperAdmin&&(
            <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:14,marginBottom:20}} className="mg2">
              <KPI icon="💳" label="Wallet Balance" value={"₹"+walletBal.toLocaleString("en-IN")} color="#ff3131"/>
              <KPI icon="⏱" label="Est. Runway" value={totalSearches>0?Math.round(walletBal/(totalCost/days))+"d":"∞"} sub="at current rate" color="#0cc0df"/>
              <KPI icon="📊" label="Avg Cost/Search" value={totalSearches>0?"₹"+Math.round(totalCost/totalSearches):"₹0"} color="#ff914d"/>
              <KPI icon="💡" label="Saved vs Agency" value={"₹"+(totalSearches*1500-totalCost>0?(totalSearches*1500-totalCost):0).toLocaleString("en-IN")} sub="vs ₹1500/search" color="#8b5cf6"/>
            </div>
          )}

          {/* Trend + Donut */}
          <div style={{display:"grid",gridTemplateColumns:"2fr 1fr",gap:16,marginBottom:20}} className="mg1">
            <Card>
              <SectionTitle>Search Trend</SectionTitle>
              <ResponsiveContainer width="100%" height={200}>
                <LineChart data={trendData}>
                  <XAxis dataKey="day" tick={{fontSize:10,fill:"var(--muted)"}} interval={Math.floor(trendData.length/6)}/>
                  <YAxis tick={{fontSize:10,fill:"var(--muted)"}}/>
                  <Tooltip {...tt}/>
                  <Line type="monotone" dataKey="searches" stroke="#0cc0df" strokeWidth={2.5} dot={false} name="Searches"/>
                </LineChart>
              </ResponsiveContainer>
            </Card>
            <Card>
              <SectionTitle>By Type</SectionTitle>
              {donutData.length>0?(
                <ResponsiveContainer width="100%" height={200}>
                  <PieChart>
                    <Pie data={donutData} cx="50%" cy="50%" innerRadius={50} outerRadius={80} dataKey="value" labelLine={false}>
                      {donutData.map((d,i)=><Cell key={i} fill={d.color}/>)}
                    </Pie>
                    <Tooltip {...tt}/>
                  </PieChart>
                </ResponsiveContainer>
              ):<div style={{height:200,display:"flex",alignItems:"center",justifyContent:"center",color:"var(--muted)",fontSize:13}}>No data</div>}
              <div style={{display:"flex",flexWrap:"wrap",gap:6,marginTop:8}}>
                {donutData.map((d,i)=>(
                  <div key={i} style={{display:"flex",alignItems:"center",gap:4,fontSize:10,color:"var(--muted)"}}>
                    <div style={{width:8,height:8,borderRadius:"50%",background:d.color,flexShrink:0}}/>
                    {d.name} ({d.value})
                  </div>
                ))}
              </div>
            </Card>
          </div>

          {/* Score distribution */}
          {scoreDistData.length>0&&(
            <Card style={{marginBottom:16}}>
              <SectionTitle>Meitho Score Distribution</SectionTitle>
              <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:12}}>
                {scoreDistData.map((s,i)=>(
                  <div key={i} style={{textAlign:"center",padding:"14px 10px",background:"var(--s2)",borderRadius:10,border:`1px solid ${s.color}25`}}>
                    <div style={{fontFamily:FFD,fontSize:24,fontWeight:900,color:s.color}}>{s.count}</div>
                    <div style={{fontSize:11,color:"var(--muted)",marginTop:3}}>{s.name}</div>
                  </div>
                ))}
              </div>
            </Card>
          )}

          {/* Recent Activity */}
          <Card>
            <SectionTitle>Recent Activity</SectionTitle>
            <div style={{maxHeight:260,overflowY:"auto"}}>
              {myHistory.slice(0,20).map((h,i)=>(
                <div key={i} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"8px 0",borderBottom:"1px solid var(--b1)"}}>
                  <div style={{display:"flex",gap:10,alignItems:"center"}}>
                    <span style={{fontSize:16}}>{{uan:"🏦",pan:"📋",aadhaar:"🪪",dl:"🚗",ecourt:"⚖️",full_bgv:"⚡",refcheck:"📧"}[h.searchType]||"🔍"}</span>
                    <div>
                      <div style={{fontSize:12,color:"var(--text)",fontWeight:500}}>{h.searchValue||"—"} <span style={{color:"var(--muted)",fontWeight:400}}>· {h.searchType?.toUpperCase()}</span></div>
                      <div style={{fontSize:10,color:"var(--muted)"}}>{h.timestamp?.slice(0,10)||"—"} {isSuperAdmin&&"· "+(h.companyName||"")}</div>
                    </div>
                  </div>
                  <div style={{display:"flex",gap:8,alignItems:"center"}}>
                    <Badge color={h.found?"var(--green)":"var(--red)"} style={{fontSize:9}}>{h.found?"Found":"Not Found"}</Badge>
                    <div style={{fontSize:12,fontWeight:700,color:"var(--brand)"}}>₹{h.cost||0}</div>
                  </div>
                </div>
              ))}
              {myHistory.length===0&&<div style={{textAlign:"center",padding:32,color:"var(--muted)",fontSize:13}}>No searches in this period</div>}
            </div>
          </Card>
        </div>
      )}

      {/* Searches Tab */}
      {tab==="searches"&&(
        <div>
          <Card style={{marginBottom:16}}>
            <SectionTitle>Search Type Performance</SectionTitle>
            <table style={{width:"100%",borderCollapse:"collapse"}}>
              <thead><tr style={{background:"var(--s2)"}}>
                {["Type","Searches","Found","Found Rate","Total Cost","Avg Cost"].map(h=>(
                  <th key={h} style={{padding:"10px 12px",textAlign:"left",fontSize:10,fontWeight:700,color:"var(--muted)",textTransform:"uppercase"}}>{h}</th>
                ))}
              </tr></thead>
              <tbody>
                {byType.map((t,i)=>(
                  <tr key={i} style={{borderBottom:"1px solid var(--b1)"}}>
                    <td style={{padding:"10px 12px",fontSize:13,fontWeight:600,color:"var(--text)"}}>{t.label}</td>
                    <td style={{padding:"10px 12px",fontSize:13,color:"var(--text)"}}>{t.count}</td>
                    <td style={{padding:"10px 12px",fontSize:13,color:"var(--text)"}}>{t.found}</td>
                    <td style={{padding:"10px 12px"}}>
                      <div style={{display:"flex",alignItems:"center",gap:8}}>
                        <div style={{height:4,width:60,background:"var(--b1)",borderRadius:2,overflow:"hidden"}}>
                          <div style={{height:"100%",width:t.foundRate+"%",background:t.foundRate>=80?"var(--green)":t.foundRate>=50?"#ff914d":"var(--red)",borderRadius:2}}/>
                        </div>
                        <span style={{fontSize:12,color:t.foundRate>=80?"var(--green)":t.foundRate>=50?"#ff914d":"var(--red)",fontWeight:700}}>{t.foundRate}%</span>
                      </div>
                    </td>
                    <td style={{padding:"10px 12px",fontSize:13,fontWeight:700,color:"var(--brand)"}}>₹{t.cost.toLocaleString("en-IN")}</td>
                    <td style={{padding:"10px 12px",fontSize:13,color:"var(--muted)"}}>₹{t.count>0?Math.round(t.cost/t.count):0}</td>
                  </tr>
                ))}
                {byType.length===0&&<tr><td colSpan={6} style={{textAlign:"center",padding:32,color:"var(--muted)",fontSize:13}}>No data in this period</td></tr>}
              </tbody>
            </table>
          </Card>

          <Card>
            <SectionTitle>Daily Search Volume</SectionTitle>
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={trendData}>
                <XAxis dataKey="day" tick={{fontSize:10,fill:"var(--muted)"}} interval={Math.floor(trendData.length/8)}/>
                <YAxis tick={{fontSize:10,fill:"var(--muted)"}}/>
                <Tooltip {...tt}/>
                <Bar dataKey="searches" fill="#0cc0df" radius={[4,4,0,0]} name="Searches"/>
              </BarChart>
            </ResponsiveContainer>
          </Card>
        </div>
      )}

      {/* Revenue/Cost Tab */}
      {(tab==="revenue"||tab==="cost")&&(
        <div>
          <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:14,marginBottom:20}} className="mg1">
            <KPI icon="💰" label={isSuperAdmin?"Total Revenue":"Total Spent"} value={"₹"+totalCost.toLocaleString("en-IN")} color="#ff914d"/>
            {isSuperAdmin&&<KPI icon="📈" label="Platform Fee (10%)" value={"₹"+Math.round(totalCost*0.1).toLocaleString("en-IN")} color="#ff3131"/>}
            {!isSuperAdmin&&<KPI icon="💳" label="Wallet Balance" value={"₹"+walletBal.toLocaleString("en-IN")} color="#ff3131"/>}
            <KPI icon="📊" label="Avg per Search" value={totalSearches>0?"₹"+Math.round(totalCost/totalSearches):"₹0"} color="#8b5cf6"/>
          </div>

          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:16,marginBottom:16}} className="mg1">
            <Card>
              <SectionTitle>Cost by Search Type</SectionTitle>
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={byType} layout="vertical">
                  <XAxis type="number" tick={{fontSize:10,fill:"var(--muted)"}} tickFormatter={v=>"₹"+v}/>
                  <YAxis type="category" dataKey="label" tick={{fontSize:11,fill:"var(--muted)"}} width={80}/>
                  <Tooltip {...tt} formatter={v=>"₹"+v}/>
                  <Bar dataKey="cost" radius={[0,4,4,0]} name="Cost">
                    {byType.map((t,i)=><Cell key={i} fill={COLORS[i%COLORS.length]}/>)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </Card>
            <Card>
              <SectionTitle>Daily Cost Trend</SectionTitle>
              <ResponsiveContainer width="100%" height={220}>
                <LineChart data={trendData}>
                  <XAxis dataKey="day" tick={{fontSize:10,fill:"var(--muted)"}} interval={Math.floor(trendData.length/6)}/>
                  <YAxis tick={{fontSize:10,fill:"var(--muted)"}} tickFormatter={v=>"₹"+v}/>
                  <Tooltip {...tt} formatter={v=>"₹"+v}/>
                  <Line type="monotone" dataKey="cost" stroke="#ff914d" strokeWidth={2.5} dot={false} name="Cost"/>
                </LineChart>
              </ResponsiveContainer>
            </Card>
          </div>
        </div>
      )}

      {/* Companies Tab (Super Admin) */}
            {tab==="companies"&&isSuperAdmin&&(
        <CompaniesTab history={history} wallets={wallets} companies={companies}
          days={days} byCompany={byCompany} tt={tt} COLORS={COLORS}
          KPI={KPI} SectionTitle={SectionTitle} dateRange={dateRange}/>
      )}

      {/* HR Users Tab (Company) */}
      {tab==="users"&&!isSuperAdmin&&(
        <div>
          <Card>
            <SectionTitle>HR User Performance</SectionTitle>
            {byUser.length>0?(
              <table style={{width:"100%",borderCollapse:"collapse"}}>
                <thead><tr style={{background:"var(--s2)"}}>
                  {["HR User","Searches","Found","Found Rate","Total Spent","Last Active"].map(h=>(
                    <th key={h} style={{padding:"10px 12px",textAlign:"left",fontSize:10,fontWeight:700,color:"var(--muted)",textTransform:"uppercase"}}>{h}</th>
                  ))}
                </tr></thead>
                <tbody>
                  {byUser.map((u,i)=>{
                    const lastActive=myHistory.filter(h=>h.userName===u.name||h.userId===u.name).sort((a,b)=>new Date(b.timestamp||0)-new Date(a.timestamp||0))[0]?.timestamp?.slice(0,10)||"—";
                    return(
                      <tr key={i} style={{borderBottom:"1px solid var(--b1)"}}>
                        <td style={{padding:"10px 12px",fontSize:13,fontWeight:600,color:"var(--text)"}}>{u.name}</td>
                        <td style={{padding:"10px 12px",fontSize:13}}>{u.searches}</td>
                        <td style={{padding:"10px 12px",fontSize:13}}>{u.found}</td>
                        <td style={{padding:"10px 12px"}}>
                          <div style={{display:"flex",alignItems:"center",gap:8}}>
                            <div style={{height:4,width:60,background:"var(--b1)",borderRadius:2,overflow:"hidden"}}>
                              <div style={{height:"100%",width:u.rate+"%",background:u.rate>=80?"var(--green)":"#ff914d",borderRadius:2}}/>
                            </div>
                            <span style={{fontSize:12,fontWeight:700,color:u.rate>=80?"var(--green)":"#ff914d"}}>{u.rate}%</span>
                          </div>
                        </td>
                        <td style={{padding:"10px 12px",fontSize:13,fontWeight:700,color:"var(--brand)"}}>₹{u.cost.toLocaleString("en-IN")}</td>
                        <td style={{padding:"10px 12px",fontSize:12,color:"var(--muted)"}}>{lastActive}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            ):<div style={{textAlign:"center",padding:32,color:"var(--muted)",fontSize:13}}>No HR user data in this period</div>}
          </Card>
        </div>
      )}
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════
   REPORTS — customizable, generate-on-demand report builder for all
   3 roles. Distinct from the fixed Analytics dashboard above: here the
   person picks which sections they want, then generates a single PDF/CSV
   combining just those sections. Sections shown depend on role:
   - Super Admin: Platform Revenue, Company Breakdown, Search Types, ATS Overview (platform-wide)
   - Company Admin: BGV Summary, Search Types, Staff Activity, ATS Summary (if ATS enabled)
   - HR Staff: My Activity, My Search Types (only if granted Reports access — gated by caller)
══════════════════════════════════════════════════════════════ */
function ReportsPage({user,company,companies,history,isSuperAdmin,addToast}) {
  const isCompanyAdmin=user?.role==="admin"||user?.companyAdmin===true;
  const isHRUser=!isSuperAdmin&&!isCompanyAdmin;
  const reportsLocked=isHRUser&&user?.searchPermissions?.reports!==true;

  const companyId=user?.companyId||company?.id||"";
  const atsEnabled=isSuperAdmin?true:!!company?.atsEnabled;

  const [dateRange,setDateRange]=useState("30");
  const [sections,setSections]=useState(()=>{
    const base={bgvSummary:true,searchTypes:true};
    if(!isHRUser)base.staffActivity=true;
    if(isSuperAdmin)base.companyBreakdown=true;
    if(atsEnabled)base.atsSummary=false;
    return base;
  });
  const [generated,setGenerated]=useState(null); // compiled report data, or null before generating
  const [loadingATS,setLoadingATS]=useState(false);
  const [atsData,setAtsData]=useState({jobs:[],applications:[]});

  // Gate: HR staff need explicit report access granted by their Company
  // Admin. Placed AFTER every hook above so this early return never skips
  // a hook call on subsequent renders (React's Rules of Hooks).
  if(reportsLocked){
    return(
      <div style={{textAlign:"center",padding:"80px 20px"}}>
        <div style={{fontSize:40,marginBottom:16}}>🔒</div>
        <div style={{fontFamily:FFD,fontSize:18,fontWeight:800,color:"var(--text)",marginBottom:8}}>Reports Access Not Granted</div>
        <div style={{fontSize:13,color:"var(--muted)",maxWidth:360,margin:"0 auto"}}>Your Company Admin hasn't given you access to Reports yet. Ask them to enable it from Companies → HR Users → Permissions.</div>
      </div>
    );
  }


  const now=new Date();
  const days=parseInt(dateRange);
  const cutoff=new Date(now-days*24*60*60*1000);
  const PRICES={uan:100,pan:50,aadhaar:50,dl:50,ecourt:75,full_bgv:450,refcheck:125};

  const scopedHistory=isSuperAdmin
    ? history.filter(h=>new Date(h.timestamp||h.createdAt||0)>=cutoff)
    : history.filter(h=>{
        const inCompany=h.companyId===companyId;
        const inDate=new Date(h.timestamp||h.createdAt||0)>=cutoff;
        const inScope=isHRUser?(h.userId===user?.id||h.userName===(user?.name||user?.email)):true;
        return inCompany&&inDate&&inScope;
      });

  const SECTION_LIST=[
    {key:"bgvSummary",label:isHRUser?"My BGV Search Summary":"BGV Search Summary",always:true},
    {key:"searchTypes",label:"Search Type Breakdown"},
    ...(!isHRUser?[{key:"staffActivity",label:"Staff Activity (per HR user)"}]:[]),
    ...(isSuperAdmin?[{key:"companyBreakdown",label:"Company Breakdown"}]:[]),
    ...(atsEnabled?[{key:"atsSummary",label:isSuperAdmin?"ATS Overview (platform-wide)":"ATS / Meitho Select Summary"}]:[]),
  ];

  const toggleSection=(key)=>setSections(p=>({...p,[key]:!p[key]}));

  const loadATSIfNeeded=async()=>{
    if(!sections.atsSummary)return;
    setLoadingATS(true);
    try{
      const jobsSnap=isSuperAdmin
        ? await getDocs(collection(db,"ats_jobs"))
        : await getDocs(query(collection(db,"ats_jobs"),where("companyId","==",companyId)));
      const appsSnap=isSuperAdmin
        ? await getDocs(collection(db,"ats_applications"))
        : await getDocs(query(collection(db,"ats_applications"),where("companyId","==",companyId)));
      setAtsData({
        jobs:jobsSnap.docs.map(d=>({id:d.id,...d.data()})),
        applications:appsSnap.docs.map(d=>({id:d.id,...d.data()})),
      });
    }catch(e){addToast("Failed to load ATS data for report","error");}
    finally{setLoadingATS(false);}
  };

  const generate=async()=>{
    await loadATSIfNeeded();
    const report={generatedAt:new Date().toISOString(),dateRangeLabel:`Last ${days} days`};

    if(sections.bgvSummary){
      const total=scopedHistory.length;
      const cost=scopedHistory.reduce((s,h)=>s+(h.cost||PRICES[h.searchType]||0),0);
      const found=scopedHistory.filter(h=>h.found).length;
      report.bgvSummary={total,cost,found,foundRate:total>0?Math.round((found/total)*100):0};
    }
    if(sections.searchTypes){
      const byType=Object.entries(scopedHistory.reduce((acc,h)=>{
        const t=h.searchType||"unknown";
        if(!acc[t])acc[t]={count:0,cost:0};
        acc[t].count++;acc[t].cost+=h.cost||PRICES[t]||0;
        return acc;
      },{})).map(([type,d])=>({type,...d})).sort((a,b)=>b.count-a.count);
      report.searchTypes=byType;
    }
    if(sections.staffActivity&&!isHRUser){
      const byUser=Object.entries(scopedHistory.reduce((acc,h)=>{
        const n=h.userName||h.userId||"Unknown";
        if(!acc[n])acc[n]={searches:0,cost:0,found:0};
        acc[n].searches++;acc[n].cost+=h.cost||0;
        if(h.found)acc[n].found++;
        return acc;
      },{})).map(([name,d])=>({name,...d})).sort((a,b)=>b.searches-a.searches);
      report.staffActivity=byUser;
    }
    if(sections.companyBreakdown&&isSuperAdmin){
      const byCompany=Object.entries(scopedHistory.reduce((acc,h)=>{
        const n=h.companyName||h.companyId||"Unknown";
        if(!acc[n])acc[n]={searches:0,cost:0};
        acc[n].searches++;acc[n].cost+=h.cost||0;
        return acc;
      },{})).map(([name,d])=>({name,...d})).sort((a,b)=>b.cost-a.cost);
      report.companyBreakdown=byCompany;
    }
    if(sections.atsSummary&&atsEnabled){
      const jobsInRange=atsData.jobs.filter(j=>new Date(j.createdAt||0)>=cutoff);
      const appsInRange=atsData.applications.filter(a=>new Date(a.appliedAt||0)>=cutoff);
      const hired=appsInRange.filter(a=>a.stage==="joined").length;

      // Stage-wise funnel — how many applications ever REACHED each stage
      // (via stageHistory), and the drop-off % compared to the stage before it.
      const stageReached=TRACK_STAGES.map(s=>({
        stage:s.id,label:s.label,
        count:appsInRange.filter(a=>(a.stageHistory||[]).some(h=>h.stage===s.id)||a.stage===s.id).length,
      }));
      const funnel=stageReached.map((s,i)=>{
        const prev=i>0?stageReached[i-1].count:s.count;
        const dropOff=(i>0&&prev>0)?Math.round((1-(s.count/prev))*100):0;
        return {...s,dropOff};
      });

      // Time to hire — days from application to the "joined" stage, averaged
      // across everyone who actually joined in this date range.
      const hireDurations=appsInRange.filter(a=>a.stage==="joined"&&a.appliedAt).map(a=>{
        const joinedEntry=(a.stageHistory||[]).slice().reverse().find(h=>h.stage==="joined");
        const joinedAt=joinedEntry?.timestamp||a.stageUpdatedAt;
        if(!joinedAt)return null;
        return (new Date(joinedAt)-new Date(a.appliedAt))/(1000*60*60*24);
      }).filter(d=>d!=null&&d>=0);
      const avgTimeToHire=hireDurations.length>0?Math.round((hireDurations.reduce((s,d)=>s+d,0)/hireDurations.length)*10)/10:null;

      // Offer acceptance rate — of everyone who actually responded (accepted
      // or declined), what % accepted. Excludes offers still pending a reply.
      const offersResponded=appsInRange.filter(a=>a.offerResponse==="accepted"||a.offerResponse==="declined");
      const offersAccepted=appsInRange.filter(a=>a.offerResponse==="accepted").length;
      const offerAcceptanceRate=offersResponded.length>0?Math.round((offersAccepted/offersResponded.length)*100):null;

      // Source of candidates — captured at application time from ?src=/?ref=/
      // ?utm_source= in the job link, defaulting to "Direct" if none present.
      const bySource=Object.entries(appsInRange.reduce((acc,a)=>{
        const src=a.source||"Direct";
        acc[src]=(acc[src]||0)+1;
        return acc;
      },{})).map(([source,count])=>({source,count})).sort((a,b)=>b.count-a.count);

      report.atsSummary={
        jobsPosted:jobsInRange.length,
        applications:appsInRange.length,
        hired,
        conversionRate:appsInRange.length>0?Math.round((hired/appsInRange.length)*100):0,
        funnel,
        avgTimeToHire,
        offerAcceptanceRate,
        offersResponded:offersResponded.length,
        bySource,
      };
    }
    setGenerated(report);
    addToast("Report generated","success");
  };

  const downloadPDF=()=>{
    if(!generated)return;
    const title=isSuperAdmin?"Meitho Platform Report":isHRUser?"My Activity Report":`${company?.name||"Company"} Report`;
    let html=`<div style="font-family:Arial,sans-serif;max-width:700px;margin:0 auto;padding:40px;color:#1e293b;">
      <div style="border-bottom:3px solid #ff3131;padding-bottom:16px;margin-bottom:24px;">
        <h1 style="color:#ff3131;margin:0;">Meitho</h1>
        <div style="font-size:14px;color:#64748b;margin-top:4px;">${title} · ${generated.dateRangeLabel}</div>
        <div style="font-size:11px;color:#94a3b8;">Generated ${new Date(generated.generatedAt).toLocaleString("en-IN")}</div>
      </div>`;
    if(generated.bgvSummary){
      const s=generated.bgvSummary;
      html+=`<h3>BGV Search Summary</h3><table border="1" cellpadding="8" style="border-collapse:collapse;width:100%;margin-bottom:20px;"><tr><td>Total Searches</td><td>${s.total}</td></tr><tr><td>Total Cost</td><td>₹${s.cost.toLocaleString("en-IN")}</td></tr><tr><td>Found</td><td>${s.found} (${s.foundRate}%)</td></tr></table>`;
    }
    if(generated.searchTypes){
      html+=`<h3>Search Type Breakdown</h3><table border="1" cellpadding="8" style="border-collapse:collapse;width:100%;margin-bottom:20px;"><tr><th>Type</th><th>Count</th><th>Cost</th></tr>${generated.searchTypes.map(t=>`<tr><td>${t.type}</td><td>${t.count}</td><td>₹${t.cost.toLocaleString("en-IN")}</td></tr>`).join("")}</table>`;
    }
    if(generated.staffActivity){
      html+=`<h3>Staff Activity</h3><table border="1" cellpadding="8" style="border-collapse:collapse;width:100%;margin-bottom:20px;"><tr><th>Name</th><th>Searches</th><th>Cost</th><th>Found</th></tr>${generated.staffActivity.map(u=>`<tr><td>${u.name}</td><td>${u.searches}</td><td>₹${u.cost.toLocaleString("en-IN")}</td><td>${u.found}</td></tr>`).join("")}</table>`;
    }
    if(generated.companyBreakdown){
      html+=`<h3>Company Breakdown</h3><table border="1" cellpadding="8" style="border-collapse:collapse;width:100%;margin-bottom:20px;"><tr><th>Company</th><th>Searches</th><th>Cost</th></tr>${generated.companyBreakdown.map(c=>`<tr><td>${c.name}</td><td>${c.searches}</td><td>₹${c.cost.toLocaleString("en-IN")}</td></tr>`).join("")}</table>`;
    }
    if(generated.atsSummary){
      const a=generated.atsSummary;
      html+=`<h3>ATS / Meitho Select Summary</h3><table border="1" cellpadding="8" style="border-collapse:collapse;width:100%;margin-bottom:20px;"><tr><td>Jobs Posted</td><td>${a.jobsPosted}</td></tr><tr><td>Applications</td><td>${a.applications}</td></tr><tr><td>Hired</td><td>${a.hired}</td></tr><tr><td>Conversion Rate</td><td>${a.conversionRate}%</td></tr><tr><td>Avg Time to Hire</td><td>${a.avgTimeToHire!=null?a.avgTimeToHire+" days":"—"}</td></tr><tr><td>Offer Acceptance Rate</td><td>${a.offerAcceptanceRate!=null?a.offerAcceptanceRate+"% ("+a.offersResponded+" responded)":"—"}</td></tr></table>`;
      if(a.funnel?.length){
        html+=`<h4>Pipeline Funnel &amp; Stage Drop-off</h4><table border="1" cellpadding="8" style="border-collapse:collapse;width:100%;margin-bottom:20px;"><tr><th>Stage</th><th>Reached</th><th>Drop-off</th></tr>${a.funnel.map(f=>`<tr><td>${f.label}</td><td>${f.count}</td><td>${f.dropOff}%</td></tr>`).join("")}</table>`;
      }
      if(a.bySource?.length){
        html+=`<h4>Source of Candidates</h4><table border="1" cellpadding="8" style="border-collapse:collapse;width:100%;margin-bottom:20px;"><tr><th>Source</th><th>Applications</th></tr>${a.bySource.map(s=>`<tr><td>${s.source}</td><td>${s.count}</td></tr>`).join("")}</table>`;
      }
    }
    html+=`</div><script>window.onload=()=>window.print();</script>`;
    const w=window.open("","_blank");
    w.document.write(html);
    w.document.close();
  };

  const downloadCSV=()=>{
    if(!generated)return;
    let rows=[];
    if(generated.searchTypes){rows.push(["Search Type Breakdown"]);rows.push(["Type","Count","Cost"]);generated.searchTypes.forEach(t=>rows.push([t.type,t.count,t.cost]));rows.push([]);}
    if(generated.staffActivity){rows.push(["Staff Activity"]);rows.push(["Name","Searches","Cost","Found"]);generated.staffActivity.forEach(u=>rows.push([u.name,u.searches,u.cost,u.found]));rows.push([]);}
    if(generated.companyBreakdown){rows.push(["Company Breakdown"]);rows.push(["Company","Searches","Cost"]);generated.companyBreakdown.forEach(c=>rows.push([c.name,c.searches,c.cost]));rows.push([]);}
    if(generated.atsSummary){
      const a=generated.atsSummary;
      rows.push(["ATS / Meitho Select Summary"]);
      rows.push(["Jobs Posted",a.jobsPosted]);rows.push(["Applications",a.applications]);rows.push(["Hired",a.hired]);
      rows.push(["Conversion Rate",a.conversionRate+"%"]);
      rows.push(["Avg Time to Hire (days)",a.avgTimeToHire??"—"]);
      rows.push(["Offer Acceptance Rate",a.offerAcceptanceRate!=null?a.offerAcceptanceRate+"%":"—"]);
      rows.push([]);
      if(a.funnel?.length){rows.push(["Pipeline Funnel"]);rows.push(["Stage","Reached","Drop-off"]);a.funnel.forEach(f=>rows.push([f.label,f.count,f.dropOff+"%"]));rows.push([]);}
      if(a.bySource?.length){rows.push(["Source of Candidates"]);rows.push(["Source","Applications"]);a.bySource.forEach(s=>rows.push([s.source,s.count]));rows.push([]);}
    }
    const csv=rows.map(r=>r.join(",")).join("\n");
    const a=document.createElement("a");a.href="data:text/csv;charset=utf-8,"+encodeURIComponent(csv);a.download="meitho_report.csv";a.click();
  };

  return(
    <div style={{paddingBottom:40}}>
      <div style={{marginBottom:20}}>
        <h2 style={{fontFamily:FFD,fontWeight:800,fontSize:26,color:"var(--text)",marginBottom:2}}>📑 Reports</h2>
        <p style={{color:"var(--muted)",fontSize:13}}>Pick the sections you need, then generate a combined report.</p>
      </div>

      <div className="fu" style={{borderRadius:14,padding:"20px 24px",marginBottom:20}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16,flexWrap:"wrap",gap:10}}>
          <div style={{fontFamily:FFD,fontSize:13,fontWeight:700,color:"var(--text)"}}>Report Sections</div>
          <select value={dateRange} onChange={e=>setDateRange(e.target.value)}
            style={{padding:"8px 14px",background:"var(--s2)",border:"1px solid var(--b1)",borderRadius:8,color:"var(--text)",fontSize:13,fontFamily:FF,cursor:"pointer",outline:"none"}}>
            <option value="7">Last 7 days</option>
            <option value="30">Last 30 days</option>
            <option value="90">Last 90 days</option>
            <option value="365">Last 1 year</option>
          </select>
        </div>
        <div style={{display:"flex",flexDirection:"column",gap:8,marginBottom:18}}>
          {SECTION_LIST.map(s=>(
            <div key={s.key} onClick={()=>!s.always&&toggleSection(s.key)} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"10px 14px",borderRadius:10,border:`1.5px solid ${sections[s.key]?"var(--brand)":"var(--b1)"}`,background:sections[s.key]?"var(--brand)08":"transparent",cursor:s.always?"default":"pointer",opacity:s.always?0.85:1}}>
              <span style={{fontSize:13,fontWeight:600,color:"var(--text)"}}>{s.label}{s.always&&<span style={{marginLeft:6,fontSize:9,color:"var(--muted)",fontWeight:400}}>(always included)</span>}</span>
              <div style={{width:40,height:22,borderRadius:99,background:sections[s.key]?"var(--brand)":"var(--b2)",position:"relative"}}>
                <div style={{width:16,height:16,borderRadius:"50%",background:"#fff",position:"absolute",top:3,left:sections[s.key]?21:3,transition:"left .15s"}}/>
              </div>
            </div>
          ))}
        </div>
        <Btn onClick={generate} disabled={loadingATS}>{loadingATS?"Loading...":"⚡ Generate Report"}</Btn>
      </div>

      {generated&&(
        <div className="fu" style={{borderRadius:14,padding:"20px 24px"}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:20,flexWrap:"wrap",gap:10}}>
            <div style={{fontFamily:FFD,fontSize:14,fontWeight:700,color:"var(--text)"}}>Report Preview</div>
            <div style={{display:"flex",gap:8}}>
              <button onClick={downloadCSV} style={{background:"var(--s2)",border:"1px solid var(--b1)",borderRadius:8,color:"var(--text)",padding:"8px 14px",cursor:"pointer",fontSize:12,fontWeight:600,fontFamily:FF}}>↓ CSV</button>
              <button onClick={downloadPDF} style={{background:"linear-gradient(90deg,#ff3131,#ff914d)",border:"none",borderRadius:8,color:"#fff",padding:"8px 14px",cursor:"pointer",fontSize:12,fontWeight:700,fontFamily:FF}}>📄 PDF</button>
            </div>
          </div>

          {generated.bgvSummary&&(
            <div style={{marginBottom:24}}>
              <div style={{fontSize:11,fontWeight:700,color:"var(--muted)",textTransform:"uppercase",marginBottom:10}}>BGV Search Summary</div>
              <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:10}} className="mg2">
                <div style={{background:"var(--s2)",borderRadius:10,padding:"12px 14px"}}><div style={{fontSize:20,fontWeight:900,color:"var(--brand)"}}>{generated.bgvSummary.total}</div><div style={{fontSize:11,color:"var(--muted)"}}>Total Searches</div></div>
                <div style={{background:"var(--s2)",borderRadius:10,padding:"12px 14px"}}><div style={{fontSize:20,fontWeight:900,color:"#ff914d"}}>₹{generated.bgvSummary.cost.toLocaleString("en-IN")}</div><div style={{fontSize:11,color:"var(--muted)"}}>Total Cost</div></div>
                <div style={{background:"var(--s2)",borderRadius:10,padding:"12px 14px"}}><div style={{fontSize:20,fontWeight:900,color:"var(--green)"}}>{generated.bgvSummary.foundRate}%</div><div style={{fontSize:11,color:"var(--muted)"}}>Found Rate</div></div>
              </div>
            </div>
          )}

          {generated.searchTypes&&(
            <div style={{marginBottom:24}}>
              <div style={{fontSize:11,fontWeight:700,color:"var(--muted)",textTransform:"uppercase",marginBottom:10}}>Search Type Breakdown</div>
              <table style={{width:"100%",borderCollapse:"collapse"}}>
                <thead><tr style={{background:"var(--s2)"}}>{["Type","Count","Cost"].map(h=><th key={h} style={{padding:"8px 12px",textAlign:"left",fontSize:10,fontWeight:700,color:"var(--muted)",textTransform:"uppercase"}}>{h}</th>)}</tr></thead>
                <tbody>{generated.searchTypes.map((t,i)=>(<tr key={i} style={{borderBottom:"1px solid var(--b1)"}}><td style={{padding:"8px 12px",fontSize:12,fontWeight:600,color:"var(--text)",textTransform:"uppercase"}}>{t.type}</td><td style={{padding:"8px 12px",fontSize:12}}>{t.count}</td><td style={{padding:"8px 12px",fontSize:12,color:"var(--brand)",fontWeight:700}}>₹{t.cost.toLocaleString("en-IN")}</td></tr>))}</tbody>
              </table>
            </div>
          )}

          {generated.staffActivity&&(
            <div style={{marginBottom:24}}>
              <div style={{fontSize:11,fontWeight:700,color:"var(--muted)",textTransform:"uppercase",marginBottom:10}}>Staff Activity</div>
              <table style={{width:"100%",borderCollapse:"collapse"}}>
                <thead><tr style={{background:"var(--s2)"}}>{["Name","Searches","Cost","Found"].map(h=><th key={h} style={{padding:"8px 12px",textAlign:"left",fontSize:10,fontWeight:700,color:"var(--muted)",textTransform:"uppercase"}}>{h}</th>)}</tr></thead>
                <tbody>{generated.staffActivity.map((u,i)=>(<tr key={i} style={{borderBottom:"1px solid var(--b1)"}}><td style={{padding:"8px 12px",fontSize:12,fontWeight:600,color:"var(--text)"}}>{u.name}</td><td style={{padding:"8px 12px",fontSize:12}}>{u.searches}</td><td style={{padding:"8px 12px",fontSize:12,color:"var(--brand)",fontWeight:700}}>₹{u.cost.toLocaleString("en-IN")}</td><td style={{padding:"8px 12px",fontSize:12}}>{u.found}</td></tr>))}</tbody>
              </table>
            </div>
          )}

          {generated.companyBreakdown&&(
            <div style={{marginBottom:24}}>
              <div style={{fontSize:11,fontWeight:700,color:"var(--muted)",textTransform:"uppercase",marginBottom:10}}>Company Breakdown</div>
              <table style={{width:"100%",borderCollapse:"collapse"}}>
                <thead><tr style={{background:"var(--s2)"}}>{["Company","Searches","Cost"].map(h=><th key={h} style={{padding:"8px 12px",textAlign:"left",fontSize:10,fontWeight:700,color:"var(--muted)",textTransform:"uppercase"}}>{h}</th>)}</tr></thead>
                <tbody>{generated.companyBreakdown.map((c,i)=>(<tr key={i} style={{borderBottom:"1px solid var(--b1)"}}><td style={{padding:"8px 12px",fontSize:12,fontWeight:600,color:"var(--text)"}}>{c.name}</td><td style={{padding:"8px 12px",fontSize:12}}>{c.searches}</td><td style={{padding:"8px 12px",fontSize:12,color:"var(--brand)",fontWeight:700}}>₹{c.cost.toLocaleString("en-IN")}</td></tr>))}</tbody>
              </table>
            </div>
          )}

          {generated.atsSummary&&(
            <div>
              <div style={{fontSize:11,fontWeight:700,color:"var(--muted)",textTransform:"uppercase",marginBottom:10}}>ATS / Meitho Select Summary</div>
              <div style={{display:"grid",gridTemplateColumns:"repeat(6,1fr)",gap:10,marginBottom:24}} className="mg2">
                <div style={{background:"var(--s2)",borderRadius:10,padding:"12px 14px"}}><div style={{fontSize:20,fontWeight:900,color:"var(--brand)"}}>{generated.atsSummary.jobsPosted}</div><div style={{fontSize:11,color:"var(--muted)"}}>Jobs Posted</div></div>
                <div style={{background:"var(--s2)",borderRadius:10,padding:"12px 14px"}}><div style={{fontSize:20,fontWeight:900,color:"#f59e0b"}}>{generated.atsSummary.applications}</div><div style={{fontSize:11,color:"var(--muted)"}}>Applications</div></div>
                <div style={{background:"var(--s2)",borderRadius:10,padding:"12px 14px"}}><div style={{fontSize:20,fontWeight:900,color:"var(--green)"}}>{generated.atsSummary.hired}</div><div style={{fontSize:11,color:"var(--muted)"}}>Hired</div></div>
                <div style={{background:"var(--s2)",borderRadius:10,padding:"12px 14px"}}><div style={{fontSize:20,fontWeight:900,color:"#8b5cf6"}}>{generated.atsSummary.conversionRate}%</div><div style={{fontSize:11,color:"var(--muted)"}}>Conversion</div></div>
                <div style={{background:"var(--s2)",borderRadius:10,padding:"12px 14px"}}><div style={{fontSize:20,fontWeight:900,color:"#06b6d4"}}>{generated.atsSummary.avgTimeToHire!=null?generated.atsSummary.avgTimeToHire+"d":"—"}</div><div style={{fontSize:11,color:"var(--muted)"}}>Avg Time to Hire</div></div>
                <div style={{background:"var(--s2)",borderRadius:10,padding:"12px 14px"}}><div style={{fontSize:20,fontWeight:900,color:"#ff914d"}}>{generated.atsSummary.offerAcceptanceRate!=null?generated.atsSummary.offerAcceptanceRate+"%":"—"}</div><div style={{fontSize:11,color:"var(--muted)"}}>Offer Acceptance</div></div>
              </div>

              {generated.atsSummary.funnel?.length>0&&(
                <div style={{marginBottom:24}}>
                  <div style={{fontSize:11,fontWeight:700,color:"var(--muted)",textTransform:"uppercase",marginBottom:10}}>Pipeline Funnel &amp; Stage Drop-off</div>
                  <table style={{width:"100%",borderCollapse:"collapse"}}>
                    <thead><tr style={{background:"var(--s2)"}}>{["Stage","Reached","Drop-off"].map(h=><th key={h} style={{padding:"8px 12px",textAlign:"left",fontSize:10,fontWeight:700,color:"var(--muted)",textTransform:"uppercase"}}>{h}</th>)}</tr></thead>
                    <tbody>{generated.atsSummary.funnel.map((f,i)=>(
                      <tr key={i} style={{borderBottom:"1px solid var(--b1)"}}>
                        <td style={{padding:"8px 12px",fontSize:12,fontWeight:600,color:"var(--text)"}}>{f.label}</td>
                        <td style={{padding:"8px 12px",fontSize:12}}>{f.count}</td>
                        <td style={{padding:"8px 12px",fontSize:12,fontWeight:700,color:f.dropOff>40?"#ef4444":f.dropOff>15?"#f59e0b":"var(--green)"}}>{i===0?"—":f.dropOff+"%"}</td>
                      </tr>
                    ))}</tbody>
                  </table>
                </div>
              )}

              {generated.atsSummary.bySource?.length>0&&(
                <div>
                  <div style={{fontSize:11,fontWeight:700,color:"var(--muted)",textTransform:"uppercase",marginBottom:10}}>Source of Candidates</div>
                  <table style={{width:"100%",borderCollapse:"collapse"}}>
                    <thead><tr style={{background:"var(--s2)"}}>{["Source","Applications"].map(h=><th key={h} style={{padding:"8px 12px",textAlign:"left",fontSize:10,fontWeight:700,color:"var(--muted)",textTransform:"uppercase"}}>{h}</th>)}</tr></thead>
                    <tbody>{generated.atsSummary.bySource.map((s,i)=>(
                      <tr key={i} style={{borderBottom:"1px solid var(--b1)"}}>
                        <td style={{padding:"8px 12px",fontSize:12,fontWeight:600,color:"var(--text)"}}>{s.source}</td>
                        <td style={{padding:"8px 12px",fontSize:12,color:"var(--brand)",fontWeight:700}}>{s.count}</td>
                      </tr>
                    ))}</tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════
   SUPPORT PAGE (HR/Company)
══════════════════════════════════════════════════════════════ */
/* ══════════════════════════════════════════════════════════════
   SUPPORT CONSTANTS
══════════════════════════════════════════════════════════════ */
const SUPPORT_SECTIONS=["Search Issue","Upload Issue","Company Profile","Credits & Billing","Login / Access","BGV Report","Integration","Other"];
const PRIORITY_LEVELS=["Low","Medium","High","Critical"];
const STATUS_COLOR={open:"var(--amber)",replied:"var(--brand)",closed:"var(--muted)",resolved:"var(--green)"};
const PRIORITY_COLOR={Low:"var(--muted)",Medium:"var(--amber)",High:"var(--red)",Critical:"var(--red)"};

/* ══════════════════════════════════════════════════════════════
   SUPPORT PAGE (HR / Company)
══════════════════════════════════════════════════════════════ */
function SupportPage({user,company}) {
  const [tickets,setTickets]=useState([]);
  const [activeTicket,setActiveTicket]=useState(null);
  const [showNew,setShowNew]=useState(false);
  const [reply,setReply]=useState("");
  const [replyLoading,setReplyLoading]=useState(false);
  const [rating,setRating]=useState(0);
  const [ratingDone,setRatingDone]=useState(false);
  const [newMsgIds,setNewMsgIds]=useState([]);
  const companyId=user.companyId||(company?.id)||user.id||"unknown";
  const msgEndRef=useRef();
  const prevMsgCount=useRef(0);

  const blank={section:"",priority:"Medium",details:"",email:user.email||"",phone:user.phone||"",companyName:company?.name||user.company||"",contactName:user.name||"",designation:user.designation||""};
  const [form,setForm]=useState(blank);
  const [errs,setErrs]=useState({});
  const [submitting,setSubmitting]=useState(false);

  // Real-time subscription
  useEffect(()=>{
    if(!companyId||companyId==="unknown")return;
    const unsub=subscribeToUserTickets(companyId,(data)=>{
      setTickets(data);
    });
    return unsub;
  },[companyId]);

  // Update active ticket when data changes + notify on new admin message
  useEffect(()=>{
    if(activeTicket){
      const updated=tickets.find(t=>t.id===activeTicket.id);
      if(updated){
        const msgs=updated.messages||[];
        const prev=prevMsgCount.current;
        if(msgs.length>prev && prev>0){
          const newMsgs=msgs.slice(prev);
          const hasAdminReply=newMsgs.some(m=>m.role==="admin");
          if(hasAdminReply){
            addToastGlobal?.("💬 Meitho Support replied to your ticket!","success");
            // Highlight new messages
            setNewMsgIds(newMsgs.map((_,i)=>prev+i));
            setTimeout(()=>setNewMsgIds([]),3000);
          }
        }
        prevMsgCount.current=msgs.length;
        setActiveTicket(updated);
      }
    }
  },[tickets]);

  useEffect(()=>{
    if(activeTicket){
      prevMsgCount.current=(activeTicket.messages||[]).length;
    }
  },[activeTicket?.id]);

  useEffect(()=>{
    setTimeout(()=>msgEndRef.current?.scrollIntoView({behavior:"smooth"}),100);
  },[activeTicket?.messages?.length]);

  const set=(k,v)=>{
    setForm(p=>({...p,[k]:v}));
    if(k==="section"&&v==="Other")setForm(p=>({...p,[k]:v,priority:"High"}));
    setErrs(p=>({...p,[k]:""}));
  };

  const validate=()=>{
    const e={};
    if(!form.section)e.section="Required";
    if(!form.details.trim()||form.details.trim().length<10)e.details="Min 10 characters";
    if(!form.email.trim())e.email="Required";
    if(!form.contactName.trim())e.contactName="Required";
    if(!form.companyName.trim())e.companyName="Required";
    return e;
  };

  const submit=async()=>{
    const e=validate();if(Object.keys(e).length){setErrs(e);return;}
    setSubmitting(true);
    try{
      await createSupportTicket({
        ...form,
        companyId,
        userId:user.id,
        userRole:user.role,
        messages:[{role:"user",text:form.details,sender:form.contactName,timestamp:new Date().toISOString()}],
      });
      addToastGlobal?.("✓ Ticket raised! Our team will respond shortly.","success");
      setShowNew(false);setForm(blank);setErrs({});
    }catch(err){addToastGlobal?.("Failed: "+err.message,"error");}
    setSubmitting(false);
  };

  const sendReply=async()=>{
    if(!reply.trim()||!activeTicket)return;
    setReplyLoading(true);
    try{
      await addTicketMessage(activeTicket.id,{role:"user",text:reply.trim(),sender:user.name||user.email,timestamp:new Date().toISOString()});
      setReply("");
    }catch(err){addToastGlobal?.("Failed: "+err.message,"error");}
    setReplyLoading(false);
  };

  const submitRating=async()=>{
    if(!rating||!activeTicket)return;
    try{
      await updateTicketStatus(activeTicket.id,"resolved");
      await addTicketMessage(activeTicket.id,{role:"rating",text:`Rated ${rating} star${rating!==1?"s":""}`,sender:user.name,rating,timestamp:new Date().toISOString()});
      setRatingDone(true);
      addToastGlobal?.(`Thank you for rating us ${rating}⭐`,"success");
    }catch(err){addToastGlobal?.("Failed: "+err.message,"error");}
  };

  // Unread count per ticket
  const unreadCount=(t)=>(t.messages||[]).filter(m=>m.role==="admin"&&!m.read).length;
  const totalUnread=tickets.reduce((sum,t)=>sum+unreadCount(t),0);

  // Marks every admin reply in this ticket as read — this was previously
  // never actually happening anywhere, so the unread badge could never
  // clear even after opening and reading the ticket.
  const openTicket=async(t)=>{
    setActiveTicket({...t});setRating(0);setRatingDone(false);
    const hasUnread=(t.messages||[]).some(m=>m.role==="admin"&&!m.read);
    if(!hasUnread)return;
    try{
      const updatedMessages=(t.messages||[]).map(m=>m.role==="admin"?{...m,read:true}:m);
      await updateDoc(doc(db,"supportTickets",t.id),{messages:updatedMessages});
      setActiveTicket(p=>p&&p.id===t.id?{...p,messages:updatedMessages}:p);
    }catch(e){/* non-critical — unread badge will just stay until next successful open */}
  };

  return(
    <div>
      <div className="fu" style={{marginBottom:20}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-end",flexWrap:"wrap",gap:12}}>
          <div>
            <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:4}}>
              <h2 style={{fontFamily:FFD,fontWeight:700,fontSize:26,color:"var(--text)"}}>Support</h2>
              {totalUnread>0&&<span style={{background:"var(--red)",color:"#fff",borderRadius:99,padding:"2px 8px",fontSize:11,fontWeight:700}}>{totalUnread} new</span>}
            </div>
            <p style={{color:"var(--muted)",fontSize:14}}>Raise a ticket — our team responds within 2 hours</p>
          </div>
          {!showNew&&!activeTicket&&<Btn onClick={()=>setShowNew(true)}>+ New Ticket</Btn>}
          {(showNew||activeTicket)&&<BtnGhost onClick={()=>{setShowNew(false);setActiveTicket(null);setRating(0);setRatingDone(false);}}>← All Tickets</BtnGhost>}
        </div>
      </div>

      {/* New Ticket Form */}
      {showNew&&(
        <Card className="fu1" style={{marginBottom:16}}>
          <div style={{fontFamily:FFD,fontSize:15,fontWeight:700,color:"var(--text)",marginBottom:18,paddingBottom:12,borderBottom:"1px solid var(--b1)"}}>Raise a Support Ticket</div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:14}}>
            <div>
              <Label>Section / Area *</Label>
              <Sel value={form.section} style={{borderColor:errs.section?"var(--red)":"var(--b1)"}} onChange={e=>set("section",e.target.value)}>
                <option value="">Select section...</option>
                {SUPPORT_SECTIONS.map(s=><option key={s}>{s}</option>)}
              </Sel><ErrMsg msg={errs.section}/>
            </div>
            <div>
              <Label>Priority Level *</Label>
              <Sel value={form.priority} onChange={e=>set("priority",e.target.value)}>
                {PRIORITY_LEVELS.map(p=><option key={p}>{p}</option>)}
              </Sel>
              {form.section==="Other"&&<div style={{fontSize:11,color:"var(--amber)",marginTop:4}}>⚠ "Other" defaults to High priority</div>}
            </div>
            <div><Label>Your Name *</Label><Inp value={form.contactName} err={errs.contactName} onChange={e=>set("contactName",e.target.value)} placeholder="Full Name"/><ErrMsg msg={errs.contactName}/></div>
            <div><Label>Designation</Label><Inp value={form.designation} onChange={e=>set("designation",e.target.value)} placeholder="HR Manager"/></div>
            <div><Label>Company Name *</Label><Inp value={form.companyName} err={errs.companyName} onChange={e=>set("companyName",e.target.value)}/><ErrMsg msg={errs.companyName}/></div>
            <div><Label>Email ID *</Label><Inp type="email" value={form.email} err={errs.email} onChange={e=>set("email",e.target.value)}/><ErrMsg msg={errs.email}/></div>
            <div><Label>Contact Number</Label><Inp value={form.phone} onChange={e=>set("phone",e.target.value)} placeholder="9876543210"/></div>
            <div style={{gridColumn:"span 2"}}>
              <Label>Issue Details *</Label>
              <textarea value={form.details} onChange={e=>set("details",e.target.value)} placeholder="Describe your issue in detail..."
                style={{width:"100%",background:"var(--ibg)",border:`1.5px solid ${errs.details?"var(--red)":"var(--b1)"}`,borderRadius:8,padding:"10px 13px",color:"var(--text)",fontSize:14,fontFamily:FF,outline:"none",resize:"vertical",minHeight:100,transition:"border-color .15s"}}
                onFocus={e=>e.target.style.borderColor="var(--brand)"} onBlur={e=>e.target.style.borderColor=errs.details?"var(--red)":"var(--b1)"}/>
              <ErrMsg msg={errs.details}/>
            </div>
          </div>
          <div style={{marginTop:16,display:"flex",gap:10}}>
            <Btn onClick={submit}>{submitting?<Spin/>:"Submit Ticket →"}</Btn>
            <BtnGhost onClick={()=>{setShowNew(false);setForm(blank);setErrs({});}}>Cancel</BtnGhost>
          </div>
        </Card>
      )}

      {/* Active Chat View */}
      {activeTicket&&!showNew&&(
        <Card className="fu1" style={{padding:0,overflow:"hidden"}}>
          {/* Ticket Header */}
          <div style={{padding:"14px 18px",borderBottom:"1px solid var(--b1)",background:"var(--s2)"}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",flexWrap:"wrap",gap:8}}>
              <div>
                <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:4}}>
                  <span style={{fontFamily:FFM,fontSize:11,color:"var(--muted)"}}>#{activeTicket.id.slice(-6).toUpperCase()}</span>
                  <span style={{fontFamily:FFD,fontSize:15,fontWeight:700,color:"var(--text)"}}>{activeTicket.section}</span>
                  <Badge color={STATUS_COLOR[activeTicket.status]||"var(--muted)"} style={{textTransform:"capitalize"}}>{activeTicket.status}</Badge>
                  <Badge color={PRIORITY_COLOR[activeTicket.priority]||"var(--muted)"}>{activeTicket.priority}</Badge>
                </div>
                <div style={{fontSize:12,color:"var(--muted)"}}>Raised on {activeTicket.createdAt?.toDate?.()?.toLocaleDateString("en-IN")||"—"} · {activeTicket.messages?.length||0} messages</div>
              </div>
            </div>
          </div>

          {/* Messages */}
          <div style={{maxHeight:400,minHeight:200,overflowY:"auto",padding:"16px 18px",display:"flex",flexDirection:"column",gap:12}}>
            {(activeTicket.messages||[]).map((msg,i)=>{
              const isAdmin=msg.role==="admin";
              const isRating=msg.role==="rating";
              const isNew=newMsgIds.includes(i);
              if(isRating) return(
                <div key={i} style={{textAlign:"center",padding:"8px",background:"var(--green)10",borderRadius:8,border:"1px solid var(--green)25"}}>
                  <div style={{fontSize:20,marginBottom:4}}>{"⭐".repeat(msg.rating)}</div>
                  <div style={{fontSize:12,color:"var(--green)",fontWeight:600}}>Rated {msg.rating} star{msg.rating!==1?"s":""} — Thank you!</div>
                </div>
              );
              return(
                <div key={i} style={{display:"flex",justifyContent:isAdmin?"flex-start":"flex-end",animation:isNew?"bounceIn .4s ease":undefined}}>
                  <div style={{maxWidth:"78%"}}>
                    <div style={{fontSize:11,color:"var(--muted)",marginBottom:3,textAlign:isAdmin?"left":"right"}}>
                      {isAdmin?<span style={{color:"var(--brand)",fontWeight:600}}>🛡 Meitho Support</span>:msg.sender} · {new Date(msg.timestamp).toLocaleString("en-IN")}
                    </div>
                    <div style={{background:isAdmin?"var(--brand)":"var(--s2)",color:isAdmin?"#fff":"var(--text)",borderRadius:isAdmin?"4px 12px 12px 12px":"12px 4px 12px 12px",padding:"10px 14px",fontSize:13,lineHeight:1.6,border:isAdmin?"none":"1px solid var(--b1)",boxShadow:isNew?"0 0 12px var(--acc)44":"none",transition:"box-shadow .5s"}}>
                      {msg.text}
                    </div>
                  </div>
                </div>
              );
            })}
            <div ref={msgEndRef}/>
          </div>

          {/* Rating section for resolved/closed tickets */}
          {(activeTicket.status==="resolved"||activeTicket.status==="closed")&&(
            <div style={{padding:"14px 18px",borderTop:"1px solid var(--b1)",background:"var(--s2)"}}>
              {!(activeTicket.messages||[]).some(m=>m.role==="rating")?(
                <div>
                  <div style={{fontSize:13,fontWeight:600,color:"var(--green)",marginBottom:10}}>
                    ✓ Ticket {activeTicket.status}! Rate your support experience:
                  </div>
                  <div style={{display:"flex",gap:8,alignItems:"center",flexWrap:"wrap"}}>
                    {[1,2,3,4,5].map(s=>(
                      <button key={s} onClick={()=>setRating(s)}
                        style={{fontSize:26,background:"none",border:"none",cursor:"pointer",transition:"transform .1s",transform:rating>=s?"scale(1.25)":"scale(1)",filter:rating>=s?"none":"grayscale(1) opacity(.35)"}}>⭐</button>
                    ))}
                    {rating>0&&(
                      <Btn onClick={submitRating} style={{marginLeft:4,padding:"7px 16px",fontSize:13,background:"var(--amber)"}}>
                        Submit {rating}★ Rating
                      </Btn>
                    )}
                    <button onClick={async()=>{
                      try{
                        await updateTicketStatus(activeTicket.id,"open");
                        await addTicketMessage(activeTicket.id,{role:"user",text:"Reopened this ticket — issue still persists.",sender:user.name||user.email,timestamp:new Date().toISOString()});
                        addToastGlobal?.("Ticket reopened!","success");
                      }catch(err){addToastGlobal?.("Failed: "+err.message,"error");}
                    }} style={{marginLeft:"auto",background:"var(--amber)10",border:"1px solid var(--amber)30",borderRadius:8,color:"var(--amber)",padding:"7px 14px",cursor:"pointer",fontSize:12,fontWeight:600,fontFamily:FF}}>
                      ↩ Reopen Ticket
                    </button>
                  </div>
                </div>
              ):(
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                  <div style={{fontSize:13,color:"var(--green)",fontWeight:600}}>
                    ✓ You rated this ticket {"⭐".repeat((activeTicket.messages||[]).find(m=>m.role==="rating")?.rating||0)}
                  </div>
                  <button onClick={async()=>{
                    try{
                      await updateTicketStatus(activeTicket.id,"open");
                      await addTicketMessage(activeTicket.id,{role:"user",text:"Reopened this ticket — issue still persists.",sender:user.name||user.email,timestamp:new Date().toISOString()});
                      addToastGlobal?.("Ticket reopened!","success");
                    }catch(err){addToastGlobal?.("Failed: "+err.message,"error");}
                  }} style={{background:"var(--amber)10",border:"1px solid var(--amber)30",borderRadius:8,color:"var(--amber)",padding:"7px 14px",cursor:"pointer",fontSize:12,fontWeight:600,fontFamily:FF}}>
                    ↩ Reopen Ticket
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Reply box - show when ticket is open or replied */}
          {(activeTicket.status==="open"||activeTicket.status==="replied")?(
            <div style={{padding:"12px 14px",borderTop:"1px solid var(--b1)",display:"flex",gap:10}}>
              <input value={reply} onChange={e=>setReply(e.target.value)}
                onKeyDown={e=>e.key==="Enter"&&!e.shiftKey&&sendReply()}
                placeholder="Type your reply..." style={{flex:1,background:"var(--ibg)",border:"1.5px solid var(--b1)",borderRadius:8,padding:"10px 13px",color:"var(--text)",fontSize:14,fontFamily:FF,outline:"none",transition:"border-color .15s"}}
                onFocus={e=>e.target.style.borderColor="var(--brand)"} onBlur={e=>e.target.style.borderColor="var(--b1)"}/>
              <Btn onClick={sendReply} style={{padding:"10px 18px",flexShrink:0}}>
                {replyLoading?<Spin/>:<svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M14 2L2 8l5 2 2 5 5-13z" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>}
              </Btn>
            </div>
          ):null}
        </Card>
      )}

      {/* Tickets List */}
      {!showNew&&!activeTicket&&(
        <div>
          {tickets.length===0?(
            <Card style={{textAlign:"center",padding:"48px 20px"}}>
              <div style={{width:56,height:56,background:"var(--s2)",borderRadius:14,display:"flex",alignItems:"center",justifyContent:"center",margin:"0 auto 16px"}}>
                <svg width="26" height="26" viewBox="0 0 26 26" fill="none"><path d="M21 16c0 1.1-.9 2-2 2H8l-4 4V7c0-1.1.9-2 2-2h13c1.1 0 2 .9 2 2v9z" stroke="var(--muted)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
              </div>
              <div style={{fontFamily:FFD,fontSize:16,fontWeight:700,color:"var(--sub)",marginBottom:6}}>No support tickets yet</div>
              <div style={{fontSize:13,color:"var(--muted)",marginBottom:20}}>Click "+ New Ticket" to raise your first support request</div>
              <Btn onClick={()=>setShowNew(true)}>+ New Ticket</Btn>
            </Card>
          ):(
            <div style={{display:"flex",flexDirection:"column",gap:10}}>
              {tickets.map((t,i)=>{
                const unread=unreadCount(t);
                const lastMsg=t.messages?.[t.messages.length-1];
                return(
                  <div key={i} onClick={()=>openTicket(t)}
                    style={{padding:16,cursor:"pointer",borderRadius:10,background:"var(--s1)",border:`1px solid var(--b1)`,borderLeft:`3px solid ${STATUS_COLOR[t.status]||"var(--b1)"}`,marginBottom:0,transition:"box-shadow .15s,border-color .15s"}}
                    onMouseEnter={e=>{e.currentTarget.style.boxShadow="0 4px 16px #00000022";e.currentTarget.style.borderColor=STATUS_COLOR[t.status]||"var(--brand)";}}
                    onMouseLeave={e=>{e.currentTarget.style.boxShadow="none";e.currentTarget.style.borderColor=STATUS_COLOR[t.status]||"var(--b1)";}}>
                    <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",flexWrap:"wrap",gap:8}}>
                      <div style={{flex:1,minWidth:180}}>
                        <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:5,flexWrap:"wrap"}}>
                          <span style={{fontFamily:FFM,fontSize:11,color:"var(--muted)"}}>#{t.id.slice(-6).toUpperCase()}</span>
                          <span style={{fontFamily:FFD,fontSize:14,fontWeight:700,color:"var(--text)"}}>{t.section}</span>
                          <Badge color={STATUS_COLOR[t.status]||"var(--muted)"} style={{fontSize:10,textTransform:"capitalize"}}>{t.status}</Badge>
                          <Badge color={PRIORITY_COLOR[t.priority]||"var(--muted)"} style={{fontSize:10}}>{t.priority}</Badge>
                          {unread>0&&<span style={{background:"var(--red)",color:"#fff",borderRadius:99,padding:"1px 7px",fontSize:10,fontWeight:700}}>{unread} new</span>}
                        </div>
                        <div style={{fontSize:12,color:"var(--muted)",marginBottom:4}}>
                          {(lastMsg?.text||"").slice(0,80)}{(lastMsg?.text||"").length>80?"...":""}
                        </div>
                        {lastMsg?.role==="admin"&&<div style={{fontSize:11,color:"var(--brand)",fontWeight:600}}>💬 Meitho Support replied</div>}
                      </div>
                      <div style={{textAlign:"right",flexShrink:0}}>
                        <div style={{fontSize:11,color:"var(--muted)"}}>{t.createdAt?.toDate?.()?.toLocaleDateString("en-IN")||"—"}</div>
                        <div style={{fontSize:11,color:"var(--sub)",marginTop:2}}>{t.messages?.length||0} msg{(t.messages?.length||0)!==1?"s":""}</div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Contact info */}
          <div style={{marginTop:24,display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
            {[{label:"Support Email",email:"help@meitho.com",color:"var(--brand)"},{label:"Sales Email",email:"sales@meitho.com",color:"var(--green)"}].map(c=>(
              <div key={c.label} style={{background:"var(--s2)",border:"1px solid var(--b1)",borderRadius:10,padding:"14px 16px",display:"flex",gap:12,alignItems:"center"}}>
                <div style={{width:36,height:36,borderRadius:9,background:c.color+"15",display:"flex",alignItems:"center",justifyContent:"center",color:c.color,flexShrink:0}}>
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M2 3h12l-6 8-6-8zM2 3v10h12V3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                </div>
                <div>
                  <div style={{fontSize:10,color:"var(--muted)",fontWeight:700,marginBottom:2,textTransform:"uppercase",letterSpacing:.5}}>{c.label}</div>
                  <a href={`mailto:${c.email}`} style={{fontSize:13,fontWeight:600,color:"var(--text)",textDecoration:"none"}}>{c.email}</a>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════
   SUPPORT INBOX (Super Admin)
══════════════════════════════════════════════════════════════ */
function SupportInboxPage({user}) {
  const [tickets,setTickets]=useState([]);
  const [activeTicket,setActiveTicket]=useState(null);
  const [reply,setReply]=useState("");
  const [replyLoading,setReplyLoading]=useState(false);
  const [filter,setFilter]=useState("all");
  const msgEndRef=useRef();
  const prevCounts=useRef({});

  useEffect(()=>{
    const unsub=subscribeToAllTickets((data)=>{
      // Notify on new user messages
      data.forEach(t=>{
        const prev=prevCounts.current[t.id]||0;
        const curr=t.messages?.length||0;
        if(curr>prev&&prev>0){
          const newMsgs=(t.messages||[]).slice(prev);
          if(newMsgs.some(m=>m.role==="user")){
            addToastGlobal?.(`💬 New reply from ${t.companyName}!`,"success");
          }
        }
        prevCounts.current[t.id]=curr;
      });
      setTickets(data);
    });
    return unsub;
  },[]);

  useEffect(()=>{
    if(activeTicket){
      const updated=tickets.find(t=>t.id===activeTicket.id);
      if(updated)setActiveTicket(updated);
    }
  },[tickets]);

  useEffect(()=>{
    setTimeout(()=>msgEndRef.current?.scrollIntoView({behavior:"smooth"}),100);
  },[activeTicket?.messages?.length]);

  const sendReply=async()=>{
    if(!reply.trim()||!activeTicket)return;
    setReplyLoading(true);
    try{
      await addTicketMessage(activeTicket.id,{role:"admin",text:reply.trim(),sender:"Meitho Support",timestamp:new Date().toISOString()});
      setReply("");
    }catch(err){addToastGlobal?.("Failed: "+err.message,"error");}
    setReplyLoading(false);
  };

  const changeStatus=async(status)=>{
    if(!activeTicket)return;
    try{
      await updateTicketStatus(activeTicket.id,status);
      addToastGlobal?.(`Ticket marked as ${status}.`,"success");
    }catch(err){addToastGlobal?.("Failed: "+err.message,"error");}
  };

  const filtered=tickets.filter(t=>filter==="all"||t.status===filter);
  const openCount=tickets.filter(t=>t.status==="open"||t.status==="replied").length;
  const unreadUser=(t)=>(t.messages||[]).filter(m=>m.role==="user"&&!m.readByAdmin).length;

  // Marks every company/user message in this ticket as read by admin — same
  // missing piece as the company-side unread badge; opening the ticket never
  // actually cleared it before.
  const openAdminTicket=async(t)=>{
    setActiveTicket(t);
    const hasUnread=(t.messages||[]).some(m=>m.role==="user"&&!m.readByAdmin);
    if(!hasUnread)return;
    try{
      const updatedMessages=(t.messages||[]).map(m=>m.role==="user"?{...m,readByAdmin:true}:m);
      await updateDoc(doc(db,"supportTickets",t.id),{messages:updatedMessages});
      setActiveTicket(p=>p&&p.id===t.id?{...p,messages:updatedMessages}:p);
    }catch(e){/* non-critical */}
  };

  return(
    <div>
      <div className="fu" style={{marginBottom:16}}>
        <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:4}}>
          <h2 style={{fontFamily:FFD,fontWeight:700,fontSize:26,color:"var(--text)"}}>Support Inbox</h2>
          {openCount>0&&<span style={{background:"var(--amber)",color:"#fff",borderRadius:99,padding:"2px 10px",fontSize:12,fontWeight:700}}>{openCount} open</span>}
        </div>
        <p style={{color:"var(--muted)",fontSize:14}}>{tickets.length} total tickets</p>
      </div>

      <div style={{display:"flex",gap:14,height:"calc(100vh - 180px)",minHeight:500}}>
        {/* Left panel */}
        <div style={{width:340,flexShrink:0,display:"flex",flexDirection:"column",background:"var(--s1)",border:"1px solid var(--b1)",borderRadius:12,overflow:"hidden"}}>
          {/* Filter tabs */}
          <div style={{padding:"10px",borderBottom:"1px solid var(--b1)",flexShrink:0}}>
            <div style={{display:"flex",gap:3,flexWrap:"wrap"}}>
              {["all","open","replied","resolved","closed"].map(s=>(
                <button key={s} onClick={()=>setFilter(s)} style={{flex:1,minWidth:40,padding:"5px 2px",border:"none",borderRadius:5,cursor:"pointer",fontSize:10,fontWeight:600,fontFamily:FF,transition:"all .15s",background:filter===s?"var(--brand)":"var(--s2)",color:filter===s?"#fff":"var(--muted)",textTransform:"capitalize"}}>{s}</button>
              ))}
            </div>
          </div>
          {/* Ticket list */}
          <div style={{flex:1,overflowY:"auto"}}>
            {filtered.length===0?(
              <div style={{textAlign:"center",padding:"40px 16px",color:"var(--muted)",fontSize:13}}>No {filter!=="all"?filter+" ":""}tickets</div>
            ):filtered.map((t,i)=>{
              const unread=unreadUser(t);
              const isActive=activeTicket?.id===t.id;
              const lastMsg=t.messages?.[t.messages.length-1];
              return(
                <div key={i} onClick={()=>openAdminTicket(t)}
                  style={{padding:"12px 14px",borderBottom:"1px solid var(--b1)",cursor:"pointer",background:isActive?"var(--acc)10":"transparent",borderLeft:`3px solid ${isActive?"var(--brand)":STATUS_COLOR[t.status]||"transparent"}`,transition:"background .1s"}}
                  onMouseEnter={e=>!isActive&&(e.currentTarget.style.background="var(--s2)")}
                  onMouseLeave={e=>!isActive&&(e.currentTarget.style.background="transparent")}>
                  <div style={{display:"flex",justifyContent:"space-between",marginBottom:3}}>
                    <div style={{fontFamily:FFD,fontSize:13,fontWeight:700,color:"var(--text)",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",maxWidth:180}}>{t.companyName}</div>
                    <div style={{display:"flex",alignItems:"center",gap:4,flexShrink:0}}>
                      {unread>0&&<span style={{background:"var(--red)",color:"#fff",borderRadius:99,padding:"1px 6px",fontSize:9,fontWeight:700}}>{unread}</span>}
                      <div style={{fontSize:10,color:"var(--muted)"}}>{t.createdAt?.toDate?.()?.toLocaleDateString("en-IN")||"—"}</div>
                    </div>
                  </div>
                  <div style={{display:"flex",gap:4,marginBottom:3}}>
                    <Badge color={STATUS_COLOR[t.status]||"var(--muted)"} style={{fontSize:9,textTransform:"capitalize"}}>{t.status}</Badge>
                    <Badge color={PRIORITY_COLOR[t.priority]||"var(--muted)"} style={{fontSize:9}}>{t.priority}</Badge>
                    <span style={{fontSize:11,color:"var(--sub)",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{t.section}</span>
                  </div>
                  <div style={{fontSize:11,color:"var(--muted)",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>
                    {lastMsg?.role==="user"?<span style={{color:"var(--brand)"}}>↩ {lastMsg.sender}: </span>:lastMsg?.role==="admin"?<span style={{color:"var(--green)"}}>✓ You: </span>:""}
                    {(lastMsg?.text||"").slice(0,50)}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Right panel */}
        <div style={{flex:1,display:"flex",flexDirection:"column",overflow:"hidden",minWidth:0}}>
          {!activeTicket?(
            <div style={{flex:1,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",background:"var(--s1)",border:"1px solid var(--b1)",borderRadius:12}}>
              <svg width="48" height="48" viewBox="0 0 48 48" fill="none" style={{marginBottom:12,opacity:.3}}><path d="M42 32c0 3.31-2.69 6-6 6H14l-8 8V10c0-3.31 2.69-6 6-6h24c3.31 0 6 2.69 6 6v22z" stroke="var(--muted)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
              <div style={{fontSize:14,color:"var(--muted)",fontWeight:600}}>Select a ticket to view conversation</div>
            </div>
          ):(
            <div style={{flex:1,display:"flex",flexDirection:"column",background:"var(--s1)",border:"1px solid var(--b1)",borderRadius:12,overflow:"hidden"}}>
              {/* Header */}
              <div style={{padding:"12px 16px",borderBottom:"1px solid var(--b1)",background:"var(--s2)",flexShrink:0}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",flexWrap:"wrap",gap:8}}>
                  <div>
                    <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:3,flexWrap:"wrap"}}>
                      <span style={{fontFamily:FFM,fontSize:11,color:"var(--muted)"}}>#{activeTicket.id.slice(-6).toUpperCase()}</span>
                      <span style={{fontFamily:FFD,fontSize:14,fontWeight:700,color:"var(--text)"}}>{activeTicket.section}</span>
                      <Badge color={STATUS_COLOR[activeTicket.status]||"var(--muted)"} style={{fontSize:10,textTransform:"capitalize"}}>{activeTicket.status}</Badge>
                      <Badge color={PRIORITY_COLOR[activeTicket.priority]||"var(--muted)"} style={{fontSize:10}}>{activeTicket.priority}</Badge>
                    </div>
                    <div style={{fontSize:11,color:"var(--muted)"}}>
                      {activeTicket.companyName} · {activeTicket.contactName} · {activeTicket.email}
                      {activeTicket.phone&&` · ${activeTicket.phone}`}
                    </div>
                    {/* Show rating if given */}
                    {(()=>{const r=(activeTicket.messages||[]).find(m=>m.role==="rating");return r?<div style={{fontSize:11,color:"var(--amber)",marginTop:2}}>{"⭐".repeat(r.rating)} Rated {r.rating}/5</div>:null;})()}
                  </div>
                  <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                    <button onClick={()=>changeStatus("replied")} style={{background:"var(--brand)15",border:"1px solid var(--brand)30",borderRadius:7,color:"var(--brand)",padding:"5px 10px",cursor:"pointer",fontSize:11,fontWeight:600,fontFamily:FF}}>Mark Replied</button>
                    <button onClick={()=>changeStatus("resolved")} style={{background:"var(--green)15",border:"1px solid var(--green)30",borderRadius:7,color:"var(--green)",padding:"5px 10px",cursor:"pointer",fontSize:11,fontWeight:600,fontFamily:FF}}>✓ Resolve</button>
                    <button onClick={()=>changeStatus("closed")} style={{background:"var(--red)10",border:"1px solid var(--red)25",borderRadius:7,color:"var(--red)",padding:"5px 10px",cursor:"pointer",fontSize:11,fontWeight:600,fontFamily:FF}}>✕ Close</button>
                  </div>
                </div>
              </div>

              {/* Messages */}
              <div style={{flex:1,overflowY:"auto",padding:"14px 16px",display:"flex",flexDirection:"column",gap:10}}>
                {(activeTicket.messages||[]).map((msg,i)=>{
                  const isAdmin=msg.role==="admin";
                  const isRating=msg.role==="rating";
                  if(isRating) return(
                    <div key={i} style={{textAlign:"center",padding:"8px",background:"var(--amber)10",borderRadius:8,border:"1px solid var(--amber)25"}}>
                      <div style={{fontSize:18}}>{"⭐".repeat(msg.rating)}</div>
                      <div style={{fontSize:12,color:"var(--amber)",fontWeight:600}}>Customer rated {msg.rating}/5</div>
                    </div>
                  );
                  return(
                    <div key={i} style={{display:"flex",justifyContent:isAdmin?"flex-end":"flex-start"}}>
                      <div style={{maxWidth:"75%"}}>
                        <div style={{fontSize:11,color:"var(--muted)",marginBottom:3,textAlign:isAdmin?"right":"left"}}>
                          {isAdmin?<span style={{color:"var(--brand)",fontWeight:600}}>You (Meitho Support)</span>:<span style={{color:"var(--sub)",fontWeight:600}}>{msg.sender} · {activeTicket.companyName}</span>}
                          <span style={{marginLeft:4}}>{new Date(msg.timestamp).toLocaleString("en-IN")}</span>
                        </div>
                        <div style={{background:isAdmin?"var(--brand)":"var(--s2)",color:isAdmin?"#fff":"var(--text)",borderRadius:isAdmin?"12px 4px 12px 12px":"4px 12px 12px 12px",padding:"10px 14px",fontSize:13,lineHeight:1.6,border:isAdmin?"none":"1px solid var(--b1)"}}>
                          {msg.text}
                        </div>
                      </div>
                    </div>
                  );
                })}
                <div ref={msgEndRef}/>
              </div>

              {/* Reply */}
              {activeTicket.status!=="closed"?(
                <div style={{padding:"10px 12px",borderTop:"1px solid var(--b1)",display:"flex",gap:8,flexShrink:0}}>
                  <input value={reply} onChange={e=>setReply(e.target.value)}
                    onKeyDown={e=>e.key==="Enter"&&!e.shiftKey&&sendReply()}
                    placeholder="Reply to this ticket..." style={{flex:1,background:"var(--ibg)",border:"1.5px solid var(--b1)",borderRadius:8,padding:"9px 12px",color:"var(--text)",fontSize:13,fontFamily:FF,outline:"none",transition:"border-color .15s"}}
                    onFocus={e=>e.target.style.borderColor="var(--brand)"} onBlur={e=>e.target.style.borderColor="var(--b1)"}/>
                  <Btn onClick={sendReply} style={{padding:"9px 14px",flexShrink:0}}>
                    {replyLoading?<Spin/>:<>
                      <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M12.5 1.5L1.5 7l4.5 1.5L7.5 13l5-11.5z" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                      Send
                    </>}
                  </Btn>
                </div>
              ):(
                <div style={{padding:"10px 12px",borderTop:"1px solid var(--b1)",textAlign:"center",fontSize:13,color:"var(--muted)",flexShrink:0}}>Ticket closed</div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Global toast ref for support pages
/* ══════════════════════════════════════════════════════════════
   INVOICES TAB
══════════════════════════════════════════════════════════════ */
/* ══════════════════════════════════════════════════════════════
   COMPANY WALLET PAGE
══════════════════════════════════════════════════════════════ */
// ── Shared invoice template (wallet top-up invoices + monthly usage invoices) ──
const INV_LOGO="data:image/png;base64,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";
const INV_SELLER={
  legal:"Goport Delivery Private Limited",brand:"Meitho",
  address:"Ward 7, Flat No. 5, Madhupur, Diglipur, North and Middle Andaman, 744202, India",
  pan:"AALCG8795L",gstin:"35AALCG8795L1ZA",
  email:"help@meitho.com",phone:"+91 83000 16265",web:"meitho.com",
};
const invEsc=(v)=>String(v==null?"":v).replace(/&/g,"&amp;").replace(/</g,"&lt;").replace(/>/g,"&gt;");
const invMoney=(n)=>"₹"+(Number(n)||0).toLocaleString("en-IN");
const buildInvoiceHtml=({invNo,dateStr,status,statusKind,title,billTo,meta,items,totals,notes})=>{
  const S=INV_SELLER;
  const rows=(items||[]).map((it,i)=>`<tr><td class="c">${i+1}</td><td>${invEsc(it.desc)}</td><td class="c">${invEsc(it.qty)}</td><td class="r">${invEsc(it.rate)}</td><td class="r b">${invEsc(it.amount)}</td></tr>`).join("")
    ||`<tr><td colspan="5" class="empty">No items for this period</td></tr>`;
  const tot=(totals||[]).map(([l,v,strong])=>`<div class="trow${strong?" strong":""}"><span>${invEsc(l)}</span><span>${invEsc(v)}</span></div>`).join("");
  const metaHtml=(meta||[]).filter(m=>m&&m[1]!==""&&m[1]!=null).map(([l,v])=>`<div class="mcell"><div class="ml">${invEsc(l)}</div><div class="mv">${invEsc(v)}</div></div>`).join("");
  const noteHtml=(notes||[]).map(n=>`<li>${invEsc(n)}</li>`).join("");
  return `<!DOCTYPE html><html><head><meta charset="UTF-8"/><title>Invoice ${invEsc(invNo)} — ${invEsc(billTo?.name||"")}</title>
<style>
*{margin:0;padding:0;box-sizing:border-box;}
html,body{background:#fff;}
body{font-family:'Segoe UI',Arial,sans-serif;color:#0f172a;font-size:12.5px;line-height:1.5;padding:14mm 14mm 40mm;}
@page{size:A4;margin:0;}
@media print{body{-webkit-print-color-adjust:exact;print-color-adjust:exact;}.noprint{display:none!important;}}
.noprint{position:sticky;top:0;background:#0f172a;margin:-14mm -14mm 10mm;padding:10px 14mm;text-align:right;}
.noprint button{background:linear-gradient(90deg,#ff3131,#ff914d);color:#fff;border:none;padding:8px 20px;border-radius:8px;cursor:pointer;font-size:13px;font-weight:700;}
.top{display:flex;justify-content:space-between;align-items:flex-start;gap:20px;}
.logo{width:196px;height:auto;display:block;image-rendering:-webkit-optimize-contrast;}
.ttl{text-align:right;}
.doc{font-size:24px;font-weight:800;letter-spacing:2px;color:#0f172a;}
.no{font-family:Consolas,monospace;font-size:14px;font-weight:700;color:#ff3131;margin-top:2px;}
.dt{font-size:11.5px;color:#64748b;margin-top:2px;}
.badge.fail{background:#fef2f2!important;color:#b91c1c!important;border-color:#fecaca!important;}
.badge{display:inline-block;margin-top:6px;padding:3px 12px;border-radius:99px;font-size:10.5px;font-weight:800;letter-spacing:.5px;background:#ecfdf5;color:#047857;border:1px solid #a7f3d0;}
.bar{height:4px;border-radius:4px;background:linear-gradient(90deg,#ff3131,#ff914d);margin:14px 0 18px;}
.parties{display:grid;grid-template-columns:1fr 1fr;gap:16px;margin-bottom:16px;}
.box{border:1px solid #e2e8f0;border-radius:10px;padding:12px 14px;}
.bl{font-size:9.5px;font-weight:800;letter-spacing:1.2px;color:#94a3b8;text-transform:uppercase;margin-bottom:6px;}
.bn{font-size:14.5px;font-weight:800;margin-bottom:3px;}
.bt{color:#475569;font-size:11.5px;}
.bt b{color:#0f172a;}
.meta{display:grid;grid-template-columns:repeat(4,1fr);gap:0;border:1px solid #e2e8f0;border-radius:10px;margin-bottom:18px;overflow:hidden;}
.mcell{padding:9px 12px;border-right:1px solid #e2e8f0;}
.mcell:last-child{border-right:none;}
.ml{font-size:9px;font-weight:800;letter-spacing:1px;color:#94a3b8;text-transform:uppercase;}
.mv{font-size:12.5px;font-weight:700;margin-top:2px;word-break:break-word;}
table.items{width:100%;border-collapse:collapse;margin-bottom:14px;}
.items th{background:#0f172a;color:#fff;padding:9px 12px;font-size:10.5px;letter-spacing:.8px;text-transform:uppercase;text-align:left;}
.items th.c,.items td.c{text-align:center;width:44px;}
.items th.r,.items td.r{text-align:right;}
.items td{padding:10px 12px;border-bottom:1px solid #eef2f7;}
.items td.b{font-weight:700;}
.items td.empty{text-align:center;color:#94a3b8;padding:22px;}
.tot{margin-left:auto;width:290px;border:1px solid #e2e8f0;border-radius:10px;padding:6px 14px;margin-bottom:18px;}
.trow{display:flex;justify-content:space-between;padding:6px 0;color:#475569;}
.trow.strong{border-top:2px solid #0f172a;margin-top:4px;padding-top:9px;font-size:16px;font-weight:800;color:#0f172a;}
.notes{background:#f8fafc;border-radius:10px;padding:12px 16px;color:#475569;font-size:11.5px;}
.notes b{color:#0f172a;}
.notes ul{margin:4px 0 0 16px;}
.footer{position:fixed;left:0;right:0;bottom:0;padding:9mm 14mm 8mm;background:#fff;border-top:3px solid transparent;border-image:linear-gradient(90deg,#ff3131,#ff914d) 1;text-align:center;color:#64748b;font-size:10.5px;line-height:1.6;}
.footer .co{font-weight:800;color:#0f172a;font-size:11.5px;}
.footer .thx{margin-top:3px;color:#94a3b8;}
</style></head><body>
<div class="noprint"><button onclick="window.print()">⬇ Download Invoice PDF</button></div>
<div class="top">
  <img class="logo" src="${INV_LOGO}" alt="Meitho"/>
  <div class="ttl"><div class="doc">${invEsc(title||"INVOICE")}</div><div class="no">${invEsc(invNo)}</div><div class="dt">Date: ${invEsc(dateStr)}</div>${status?`<span class="badge ${statusKind==="fail"?"fail":""}">${invEsc(status)}</span>`:""}</div>
</div>
<div class="bar"></div>
<div class="parties">
  <div class="box"><div class="bl">From</div><div class="bn">${invEsc(S.legal)}</div>
    <div class="bt">Operating the platform “${invEsc(S.brand)}”<br/>${invEsc(S.address)}<br/><b>GSTIN:</b> ${invEsc(S.gstin)} &nbsp;·&nbsp; <b>PAN:</b> ${invEsc(S.pan)}</div></div>
  <div class="box"><div class="bl">Billed to</div><div class="bn">${invEsc(billTo?.name||"—")}</div>
    <div class="bt">${billTo?.address?invEsc(billTo.address)+"<br/>":""}${billTo?.email?invEsc(billTo.email)+"<br/>":""}${billTo?.phone?invEsc(billTo.phone)+"<br/>":""}<b>GSTIN:</b> ${invEsc(billTo?.gst||"Not provided")}</div></div>
</div>
${metaHtml?`<div class="meta">${metaHtml}</div>`:""}
<table class="items"><thead><tr><th class="c">#</th><th>Description</th><th class="c" style="width:70px">Qty</th><th class="r" style="width:90px">Rate</th><th class="r" style="width:110px">Amount</th></tr></thead><tbody>${rows}</tbody></table>
<div class="tot">${tot}</div>
${noteHtml?`<div class="notes"><b>Notes</b><ul>${noteHtml}</ul></div>`:""}
<div class="footer">
  <div class="co">${invEsc(S.legal)}</div>
  <div>Registered office: ${invEsc(S.address)}</div>
  <div>GSTIN ${invEsc(S.gstin)} · PAN ${invEsc(S.pan)} · ${invEsc(S.email)} · ${invEsc(S.phone)} · ${invEsc(S.web)}</div>
  <div class="thx">This is a computer-generated invoice and does not require a signature. Thank you for choosing Meitho.</div>
</div>
<script>window.onload=()=>setTimeout(()=>window.print(),500);</script>
</body></html>`;
};
// ── Shared report shell (header + repeating footer) for verification reports ──
const buildReportHtml=({title,reportId,dateStr,status,statusTone,candidate,bodyHtml,consentAt,preparedFor})=>{
  const S=INV_SELLER;
  const tone={ok:["#ecfdf5","#047857","#a7f3d0"],warn:["#fffbeb","#b45309","#fde68a"],bad:["#fef2f2","#b91c1c","#fecaca"],info:["#eff6ff","#1d4ed8","#bfdbfe"]}[statusTone||"info"];
  const cand=(candidate||[]).filter(c=>c&&c[1]!==""&&c[1]!=null).map(([l,v])=>`<div class="cc"><div class="cl">${invEsc(l)}</div><div class="cv">${invEsc(v)}</div></div>`).join("");
  return `<!DOCTYPE html><html><head><meta charset="UTF-8"/><title>${invEsc(title)} — ${invEsc(reportId)}</title>
<style>
*{box-sizing:border-box;margin:0;padding:0}
html,body{background:#fff}
body{font-family:'Segoe UI',Arial,sans-serif;color:#0f172a;font-size:12.5px;line-height:1.5}
@page{size:A4;margin:12mm 12mm 12mm}
@media print{body{-webkit-print-color-adjust:exact;print-color-adjust:exact}.noprint{display:none!important}}
.noprint{background:#0f172a;padding:10px 14px;text-align:right}
.noprint button{background:linear-gradient(90deg,#ff3131,#ff914d);color:#fff;border:none;padding:8px 20px;border-radius:8px;cursor:pointer;font-size:13px;font-weight:700}
.shell{width:100%;border-collapse:collapse;max-width:820px;margin:0 auto}
.shell td{padding:0;border:none}
tfoot{display:table-footer-group}
.top{display:flex;justify-content:space-between;align-items:flex-start;gap:20px;padding-top:6px}
.logo{width:196px;height:auto;display:block}
.ttl{text-align:right}
.doc{font-size:19px;font-weight:800;letter-spacing:1.4px;color:#0f172a;line-height:1.2}
.rid{font-family:Consolas,monospace;font-size:13.5px;font-weight:700;color:#ff3131;margin-top:3px}
.dt{font-size:11px;color:#64748b;margin-top:2px}
.badge{display:inline-block;margin-top:6px;padding:3px 12px;border-radius:99px;font-size:10.5px;font-weight:800;letter-spacing:.5px;background:${tone[0]};color:${tone[1]};border:1px solid ${tone[2]}}
.bar{height:4px;border-radius:4px;background:linear-gradient(90deg,#ff3131,#ff914d);margin:14px 0 16px}
.cand{display:grid;grid-template-columns:repeat(3,1fr);border:1px solid #e2e8f0;border-radius:10px;margin-bottom:16px;overflow:hidden}
.cc{padding:10px 14px;border-right:1px solid #e2e8f0;border-bottom:1px solid #e2e8f0}
.cc:nth-child(3n){border-right:none}
.cl{font-size:9px;font-weight:800;letter-spacing:1px;color:#94a3b8;text-transform:uppercase}
.cv{font-size:12.5px;font-weight:700;margin-top:2px;word-break:break-word}
.sec{margin-bottom:14px;border:1px solid #e2e8f0;border-radius:10px;overflow:hidden;page-break-inside:avoid}
.sec-h{background:#f8fafc;border-bottom:1px solid #e2e8f0;padding:9px 14px;font-weight:800;display:flex;justify-content:space-between;align-items:center;border-left:4px solid #ff3131}
.chip{font-size:10.5px;font-weight:800;padding:2px 10px;border-radius:99px;border:1px solid #e2e8f0;color:#475569;background:#fff}
.chip.ok{background:#ecfdf5;color:#047857;border-color:#a7f3d0}
.chip.bad{background:#fef2f2;color:#b91c1c;border-color:#fecaca}
.chip.wait{background:#fffbeb;color:#b45309;border-color:#fde68a}
table.kv{width:100%;border-collapse:collapse}
table.kv td{padding:7px 14px;border-top:1px solid #f1f5f9;vertical-align:top}
table.kv td.k{width:32%;font-size:11px;font-weight:700;color:#64748b}
.foot{border-top:3px solid #ff914d;margin-top:12px;padding:8px 0 0;text-align:center;color:#64748b;font-size:10px;line-height:1.55}
.foot .co{font-weight:800;color:#0f172a;font-size:11px}
.foot .dis{color:#94a3b8;margin-top:2px}
</style></head><body>
<div class="noprint"><button onclick="window.print()">⬇ Download Report PDF</button></div>
<table class="shell"><tbody><tr><td>
<div class="top">
  <img class="logo" src="${INV_LOGO}" alt="Meitho"/>
  <div class="ttl"><div class="doc">${invEsc(title)}</div><div class="rid">${invEsc(reportId)}</div><div class="dt">Generated: ${invEsc(dateStr)}</div>${status?`<span class="badge">${invEsc(status)}</span>`:""}</div>
</div>
<div class="bar"></div>
${cand?`<div class="cand">${cand}</div>`:""}
${bodyHtml}
</td></tr></tbody>
<tfoot><tr><td>
<div class="foot">
  <div class="co">${invEsc(S.legal)} · ${invEsc(S.brand)}</div>
  <div>Registered office: ${invEsc(S.address)}</div>
  <div>${invEsc(S.email)} · ${invEsc(S.phone)} · ${invEsc(S.web)}</div>
  <div class="dis">Confidential${preparedFor?" — prepared for "+invEsc(preparedFor):""}. Based on details submitted by the candidate${consentAt?" (consent recorded "+invEsc(consentAt)+")":""} and on third-party sources as available on the date of generation.</div>
</div>
</td></tr></tfoot></table>
<script>window.onload=()=>setTimeout(()=>window.print(),500)</script>
</body></html>`;
};
const openInvoice=(html)=>{
  const win=window.open("","_blank","width=900,height=900");
  if(!win){alert("Please allow popups to download the invoice");return;}
  win.document.write(html);win.document.close();win.focus();
};
function openRechargeInvoice(t,company,email){
  const P=rechargeParts(t);
  const created=t.createdAt?new Date(t.createdAt):new Date();
  const invNo=(P.failed?"RCPT-":"INV-")+created.getFullYear()+"-"+String(created.getMonth()+1).padStart(2,"0")+"-"+String(t.id||t.razorpayId||"").slice(-6).toUpperCase();
  const dateStr=created.toLocaleString("en-IN",{day:"2-digit",month:"short",year:"numeric",hour:"2-digit",minute:"2-digit"});
  const txnRef=t.razorpayId||t.payRef||t.id||"—";
  const billTo={name:company?.name||"",gst:company?.gst||"",address:company?.address||"",email:email||company?.email||"",phone:company?.phone||""};
  if(P.failed){
    openInvoice(buildInvoiceHtml({
      invNo,dateStr,status:"PAYMENT FAILED",statusKind:"fail",title:"PAYMENT RECEIPT",billTo,
      meta:[["Transaction ID",txnRef],["Payment status","Failed"],["Wallet amount added","₹0.00"],["Wallet balance",money2(t.balanceAfter??t.balanceBefore??0)]],
      items:[{desc:"Wallet recharge attempt — payment not completed",qty:1,rate:money2(P.amt),amount:"₹0.00"}],
      totals:[["Recharge attempted",money2(P.amt)],["Amount added to wallet","₹0.00",true]],
      notes:[t.failReason?("Reason: "+t.failReason):"The payment could not be completed.","No amount has been added to your wallet. If any amount was debited from your account, it is usually refunded by your bank / payment provider within 5–7 working days.","This is a payment receipt, not a tax invoice."].filter(Boolean),
    }));
    return;
  }
  const items=[{desc:"Wallet recharge (prepaid verification credit)",qty:1,rate:money2(P.amt),amount:money2(P.amt)}];
  const totals=[["Wallet recharge amount",money2(P.amt)]];
  const notes=["The wallet is credited with the recharge amount only. GST is not added to the wallet balance.","Verification charges are deducted from the wallet as checks are run. Wallet balance never expires."];
  if(P.hasGst){
    items.push({desc:"IGST @ "+(t.gstPct||GST_PCT)+"% on wallet recharge of "+money2(P.amt),qty:"",rate:"",amount:money2(P.gst)});
    totals.push(["IGST @ "+(t.gstPct||GST_PCT)+"%",money2(P.gst)]);
  } else if(P.fee>0){
    items.push({desc:"Platform fee (10%)",qty:1,rate:money2(P.fee),amount:money2(P.fee)});
    totals.push(["Platform fee (10%)",money2(P.fee)]);
    notes.push("Issued before GST billing was introduced.");
  } else {
    notes.push("Issued before GST billing was introduced.");
  }
  totals.push(["Total paid",money2(P.total),true]);
  openInvoice(buildInvoiceHtml({
    invNo,dateStr,status:"PAYMENT SUCCESSFUL",title:"INVOICE",billTo,
    meta:[["Transaction ID",txnRef],["Payment status","Successful"],["Wallet amount added",money2(P.amt)],["New wallet balance",money2(t.balanceAfter||0)]],
    items,totals,notes,
  }));
}

// Recharge-only history (no deductions) with an invoice / receipt per recharge
function RechargeHistory({txns,company,email}){
  const rows=(txns||[]).filter(isRechargeTxn);
  const th={textAlign:"left",padding:"8px 12px",fontSize:10,fontWeight:700,color:"var(--muted)",textTransform:"uppercase"};
  return(
    <div className="fu3 glass" style={{borderRadius:14,border:"1px solid var(--b1)",overflow:"hidden"}}>
      <div style={{padding:"12px 16px",borderBottom:"1px solid var(--b1)",fontFamily:FFD,fontSize:13,fontWeight:700,color:"var(--text)"}}>Wallet Recharge History</div>
      {rows.length===0?<div style={{padding:"20px",textAlign:"center",color:"var(--muted)",fontSize:13}}>No recharges yet</div>:(
        <div style={{maxHeight:380,overflow:"auto"}}>
          <table style={{width:"100%",borderCollapse:"collapse",minWidth:620}}>
            <thead style={{background:"var(--s2)"}}><tr>{["Date","Recharge","GST ("+GST_PCT+"%)","Total paid","Status",""].map(h=><th key={h} style={th}>{h}</th>)}</tr></thead>
            <tbody>
              {rows.slice(0,100).map((t,i)=>{
                const P=rechargeParts(t);
                return(
                  <tr key={t.id||i} style={{borderBottom:"1px solid var(--b1)"}}>
                    <td style={{padding:"8px 12px",fontSize:11,color:"var(--muted)",whiteSpace:"nowrap"}}>{(t.createdAt||"").slice(0,10)}</td>
                    <td style={{padding:"8px 12px",fontSize:13,fontWeight:700,color:P.failed?"var(--muted)":"var(--green)",fontFamily:FFD}}>{money2(P.amt)}</td>
                    <td style={{padding:"8px 12px",fontSize:12,color:"var(--text)"}}>{P.failed||!P.hasGst?"—":money2(P.gst)}</td>
                    <td style={{padding:"8px 12px",fontSize:12,fontFamily:FFD,color:"var(--text)"}}>{P.failed?"—":money2(P.total)}</td>
                    <td style={{padding:"8px 12px"}}><Badge color={P.failed?"#ef4444":"#10b981"} style={{fontSize:9}}>{P.failed?"Failed":"Successful"}</Badge></td>
                    <td style={{padding:"8px 12px"}}>
                      <button onClick={()=>openRechargeInvoice(t,company,email)} style={{background:"rgba(255,49,49,0.1)",border:"1px solid rgba(255,49,49,0.2)",borderRadius:6,color:"var(--text)",padding:"4px 10px",cursor:"pointer",fontSize:10.5,fontWeight:700,fontFamily:FF,whiteSpace:"nowrap"}}>{P.failed?"🧾 Receipt":"📄 Invoice"}</button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function WalletPage({user,company,wallets,setWallets,transactions,setTransactions,addToast}) {
  const {dark}=useTheme();
  const companyId=user.companyId||company?.id||"";
  const wallet=wallets[companyId]||{balance:0,totalLoaded:0,totalSpent:0};
  const [txns,setTxns]=useState([]);
  const [hrAllocs,setHrAllocs]=useState([]);
  const [loading,setLoading]=useState(true);
  const [topupAmt,setTopupAmt]=useState("");
  const [allocModal,setAllocModal]=useState(null);
  const [allocAmt,setAllocAmt]=useState("");
  const [showTopup,setShowTopup]=useState(false);

  useEffect(()=>{
    const load=async()=>{
      try{
        const [w,t,h]=await Promise.all([
          getWallet(companyId),
          getCompanyTransactions(companyId),
          getCompanyHRAllocations(companyId),
        ]);
        setWallets(p=>({...p,[companyId]:w}));
        setTxns(t);
        setHrAllocs(h);
      }catch(e){console.warn("Wallet load:",e);}
      setLoading(false);
    };
    if(companyId)load();
  },[companyId]);

  const topupGst=gstOf(parseInt(topupAmt)||0);
  const totalPay=Math.round(((parseInt(topupAmt)||0)+topupGst)*100)/100;
  const isAdmin=user.role==="admin"||user.companyAdmin||user.role==="superadmin";
  const lowBalance=(wallet.balance||0)<500;

  const [topupBusy,setTopupBusy]=useState(false);
  const doTopup=async()=>{
    const amt=parseInt(topupAmt)||0;
    if(amt<MIN_WALLET_LOAD){addToast("Minimum top-up is Rs."+MIN_WALLET_LOAD,"error");return;}
    if(topupBusy)return;
    setTopupBusy(true);
    // 1) The server creates the order and works out the GST, so the amount cannot be altered here
    let order;
    try{
      const headers=await getAuthedHeaders();
      const r=await fetch(CF_RZP_ORDER,{method:"POST",headers,body:JSON.stringify({amount:amt})});
      order=await r.json().catch(()=>({}));
      if(!r.ok||!order.orderId)throw new Error(order.error||"Could not start the payment");
    }catch(e){addToast("Could not start payment: "+e.message,"error");setTopupBusy(false);return;}

    const options={
      key:order.keyId,order_id:order.orderId,amount:order.amountPaise,currency:"INR",
      name:"Meitho",description:"Wallet recharge (incl. 18% GST)",
      handler:async(resp)=>{
        // 2) The server verifies Razorpay's signature and credits the wallet
        try{
          const headers=await getAuthedHeaders();
          const r=await fetch(CF_RZP_VERIFY,{method:"POST",headers,body:JSON.stringify({
            razorpay_order_id:resp.razorpay_order_id,razorpay_payment_id:resp.razorpay_payment_id,razorpay_signature:resp.razorpay_signature})});
          const j=await r.json().catch(()=>({}));
          if(!r.ok||!j.success)throw new Error(j.error||"Could not confirm the payment");
          const w=await getWallet(companyId);
          setWallets(p=>({...p,[companyId]:w}));
          addToast("Rs."+order.amount+" added to wallet!","success");
          setShowTopup(false);setTopupAmt("");
          const t=await getCompanyTransactions(companyId);setTxns(t);
          const latest=t.find(x=>x.type==="topup"&&x.razorpayId===resp.razorpay_payment_id)||t.find(x=>x.type==="topup");
          if(latest)openRechargeInvoice(latest,company,user.email);
        }catch(e){
          addToast("Payment received, but confirmation is pending ("+e.message+"). Your wallet will be credited automatically within a few minutes. Contact support if it is not.","amber");
        }
        setTopupBusy(false);
      },
      modal:{ondismiss:()=>setTopupBusy(false)},
      prefill:{email:user.email||""},
      theme:{color:"#ff3131"},
    };
    const launch=()=>{
      const rzp=new window.Razorpay(options);
      rzp.on("payment.failed",async(resp)=>{
        try{
          await recordFailedTopup(companyId,order.amount,order.gst,resp?.error?.description||resp?.error?.reason||"",resp?.error?.metadata?.payment_id||"",user.name||user.email);
          const t=await getCompanyTransactions(companyId);setTxns(t);
        }catch(e){console.warn("Could not record failed payment:",e);}
        addToast("Payment failed. No amount was added to your wallet.","error");
        setTopupBusy(false);
      });
      rzp.open();
    };
    if(window.Razorpay)launch();
    else{const sc=document.createElement("script");sc.src="https://checkout.razorpay.com/v1/checkout.js";sc.onload=launch;sc.onerror=()=>{addToast("Could not load the payment window. Check your connection.","error");setTopupBusy(false);};document.head.appendChild(sc);}
  };

  const doAllocate=async()=>{
    const amt=parseInt(allocAmt)||0;
    if(!allocModal||amt<=0)return;
    try{
      await setHRAllocation(companyId,allocModal.id,amt,wallet.balance);
      setHrAllocs(p=>p.map(h=>h.id===allocModal.id?{...h,allocated:amt,balance:amt}:h));
      addToast("Rs."+amt+" allocated to "+allocModal.name,"success");
      setAllocModal(null);setAllocAmt("");
    }catch(e){addToast("Failed: "+e.message,"error");}
  };

  const doReverse=async(hr)=>{
    try{
      await reverseHRAllocation(companyId,hr.id);
      const w=await getWallet(companyId);
      setWallets(p=>({...p,[companyId]:w}));
      setHrAllocs(p=>p.map(h=>h.id===hr.id?{...h,balance:0,allocated:0}:h));
      addToast("Rs."+(hr.balance||0)+" returned to company pool","success");
    }catch(e){addToast("Failed: "+e.message,"error");}
  };

  return(
    <div className={dark?"bg-mesh-dark":"bg-mesh-light"} style={{minHeight:"100%",padding:"4px 0"}}>
      {allocModal&&<Modal title={"Allocate to "+allocModal.name} onClose={()=>{setAllocModal(null);setAllocAmt("");}}>
        <div style={{marginBottom:16}}>
          <div style={{display:"flex",gap:10,marginBottom:14}}>
            <div style={{flex:1,background:"var(--s2)",borderRadius:8,padding:"12px",textAlign:"center"}}>
              <div style={{fontSize:11,color:"var(--muted)",fontWeight:700,marginBottom:2}}>WALLET POOL</div>
              <div style={{fontSize:22,fontWeight:700,color:"var(--green)",fontFamily:FFD}}>Rs.{(wallet.balance||0).toLocaleString("en-IN")}</div>
            </div>
            <div style={{flex:1,background:"var(--s2)",borderRadius:8,padding:"12px",textAlign:"center"}}>
              <div style={{fontSize:11,color:"var(--muted)",fontWeight:700,marginBottom:2}}>CURRENT ALLOC</div>
              <div style={{fontSize:22,fontWeight:700,color:"var(--brand)",fontFamily:FFD}}>Rs.{(allocModal.allocated||0).toLocaleString("en-IN")}</div>
            </div>
          </div>
          <Label>Amount (Rs.)</Label>
          <Inp type="number" value={allocAmt} onChange={e=>setAllocAmt(e.target.value)} placeholder="e.g. 1000" style={{marginBottom:10}}/>
          <div style={{display:"flex",gap:6,marginBottom:12,flexWrap:"wrap"}}>
            {[500,1000,2000,5000].map(n=>(
              <button key={n} onClick={()=>setAllocAmt(String(n))} style={{flex:1,minWidth:60,background:allocAmt===String(n)?"var(--brand)":"var(--s2)",border:"1px solid var(--b2)",borderRadius:8,color:allocAmt===String(n)?"#fff":"var(--muted)",padding:"6px 0",cursor:"pointer",fontWeight:700,fontSize:12,fontFamily:FF}}>Rs.{n.toLocaleString("en-IN")}</button>
            ))}
          </div>
        </div>
        <div style={{display:"flex",gap:10}}><Btn onClick={doAllocate}>Allocate Rs.{allocAmt||0} ></Btn><BtnGhost onClick={()=>{setAllocModal(null);setAllocAmt("");}}>Cancel</BtnGhost></div>
      </Modal>}

      <div className="fu" style={{marginBottom:20}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",flexWrap:"wrap",gap:12}}>
          <div>
            <h2 style={{fontFamily:FFD,fontWeight:800,fontSize:26,color:"var(--text)",marginBottom:4}}>Wallet</h2>
            <p style={{color:"var(--muted)",fontSize:14}}>{company?.name||user.company}</p>
          </div>
          {isAdmin&&<Btn onClick={()=>setShowTopup(s=>!s)}>+ Top Up Wallet</Btn>}
        </div>
      </div>

      {lowBalance&&<div style={{background:"rgba(255,49,49,0.08)",border:"1px solid rgba(255,49,49,0.25)",borderRadius:12,padding:"12px 16px",marginBottom:16,display:"flex",justifyContent:"space-between",alignItems:"center",flexWrap:"wrap",gap:8}}>
        <div style={{fontSize:13,color:"#ff3131",fontWeight:600}}>Warning: Low wallet balance — Rs.{(wallet.balance||0).toLocaleString("en-IN")} remaining</div>
        <Btn onClick={()=>setShowTopup(true)} style={{fontSize:12,padding:"7px 14px"}}>Top Up Now</Btn>
      </div>}

      {showTopup&&isAdmin&&<div className="fu1 glass" style={{borderRadius:14,padding:20,border:"1px solid var(--b1)",marginBottom:16}}>
        <div style={{fontFamily:FFD,fontSize:14,fontWeight:700,color:"var(--text)",marginBottom:14}}>Top Up Wallet</div>
        <div style={{display:"flex",gap:10,flexWrap:"wrap",marginBottom:14}}>
          {[500,2999,5000,10000,20000,50000].map(n=>(
            <button key={n} onClick={()=>setTopupAmt(String(n))} style={{background:topupAmt===String(n)?"var(--brand)":"var(--s2)",border:"1px solid "+(topupAmt===String(n)?"var(--brand)":"var(--b2)"),borderRadius:8,color:topupAmt===String(n)?"#fff":"var(--muted)",padding:"8px 14px",cursor:"pointer",fontWeight:700,fontSize:13,fontFamily:FF}}>Rs.{n.toLocaleString("en-IN")}</button>
          ))}
        </div>
        <div style={{marginBottom:14}}>
          <Label>Custom Amount (min Rs.{MIN_WALLET_LOAD.toLocaleString("en-IN")})</Label>
          <Inp type="number" value={topupAmt} onChange={e=>setTopupAmt(e.target.value)} placeholder="Enter amount"/>
        </div>
        {topupAmt&&parseInt(topupAmt)>=MIN_WALLET_LOAD&&(
          <div style={{background:"var(--s2)",borderRadius:10,padding:"12px 16px",marginBottom:14}}>
            <div style={{display:"flex",justifyContent:"space-between",marginBottom:6,fontSize:13}}><span style={{color:"var(--muted)"}}>Wallet credit</span><span style={{fontWeight:700,color:"var(--green)"}}>Rs.{parseInt(topupAmt).toLocaleString("en-IN")}</span></div>
            <div style={{display:"flex",justifyContent:"space-between",marginBottom:6,fontSize:13}}><span style={{color:"var(--muted)"}}>IGST ({GST_PCT}%)</span><span style={{fontWeight:700,color:"#ff914d"}}>Rs.{topupGst.toLocaleString("en-IN",{minimumFractionDigits:2,maximumFractionDigits:2})}</span></div>
            <div style={{display:"flex",justifyContent:"space-between",paddingTop:8,borderTop:"1px solid var(--b1)",fontSize:14,fontWeight:700}}><span>Total payable</span><span style={{color:"var(--brand)"}}>Rs.{totalPay.toLocaleString("en-IN",{minimumFractionDigits:2,maximumFractionDigits:2})}</span></div>
            <div style={{fontSize:11,color:"var(--muted)",marginTop:6}}>GST is charged on the recharge amount and is not added to the wallet. Non-refundable. Invoice generated automatically.</div>
          </div>
        )}
        <div style={{display:"flex",gap:10}}>
          <Btn onClick={doTopup} style={{background:"linear-gradient(90deg,#ff3131,#ff914d)"}}>Pay Rs.{totalPay.toLocaleString("en-IN",{minimumFractionDigits:2,maximumFractionDigits:2})} ></Btn>
          <BtnGhost onClick={()=>{setShowTopup(false);setTopupAmt("");}}>Cancel</BtnGhost>
        </div>
      </div>}

      <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:14,marginBottom:20}} className="mg1">
        {[
          {label:"Available Balance",value:"Rs."+(wallet.balance||0).toLocaleString("en-IN"),color:lowBalance?"#ff3131":"var(--green)",icon:"Rs"},
          {label:"Total Loaded",value:"Rs."+(wallet.totalLoaded||0).toLocaleString("en-IN"),color:"var(--brand)",icon:"UP"},
          {label:"Total Spent",value:"Rs."+(wallet.totalSpent||0).toLocaleString("en-IN"),color:"#ff914d",icon:"SC"},
        ].map((s,i)=>(
          <div key={s.label} className={"fu"+i+" glass neon-hover"} style={{borderRadius:14,padding:18,border:"1px solid var(--b1)",textAlign:"center"}}>
            <div style={{fontFamily:FFD,fontSize:24,fontWeight:800,color:s.color,marginBottom:4}}>{s.value}</div>
            <div style={{fontSize:12,color:"var(--muted)",fontWeight:600}}>{s.label}</div>
          </div>
        ))}
      </div>

      {isAdmin&&hrAllocs.length>0&&(
        <div className="fu1 glass" style={{borderRadius:14,padding:20,border:"1px solid var(--b1)",marginBottom:16}}>
          <div style={{fontFamily:FFD,fontSize:14,fontWeight:700,color:"var(--text)",marginBottom:14}}>HR Wallet Allocations</div>
          {hrAllocs.map((hr,i)=>(
            <div key={i} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"10px",background:"var(--s2)",borderRadius:10,marginBottom:8,border:"1px solid var(--b1)",flexWrap:"wrap",gap:8}}>
              <div>
                <div style={{fontSize:13,fontWeight:600,color:"var(--text)"}}>{hr.name||hr.id}</div>
                <div style={{fontSize:11,color:"var(--muted)"}}>Allocated: Rs.{(hr.allocated||0).toLocaleString("en-IN")} | Spent: Rs.{(hr.spent||0).toLocaleString("en-IN")}</div>
              </div>
              <div style={{display:"flex",alignItems:"center",gap:10}}>
                <div style={{fontFamily:FFD,fontSize:18,fontWeight:800,color:(hr.balance||0)>200?"var(--green)":"#ff3131"}}>Rs.{(hr.balance||0).toLocaleString("en-IN")}</div>
                <button onClick={()=>{setAllocModal(hr);setAllocAmt(String(hr.allocated||0));}} style={{background:"var(--brand)10",border:"1px solid var(--brand)30",borderRadius:7,color:"var(--brand)",padding:"5px 10px",cursor:"pointer",fontSize:11,fontWeight:700,fontFamily:FF}}>Allocate</button>
                {(hr.balance||0)>0&&<button onClick={()=>doReverse(hr)} style={{background:"rgba(255,145,77,0.1)",border:"1px solid rgba(255,145,77,0.3)",borderRadius:7,color:"#ff914d",padding:"5px 10px",cursor:"pointer",fontSize:11,fontWeight:700,fontFamily:FF}}>Reverse</button>}
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="fu2 glass" style={{borderRadius:14,padding:14,border:"1px solid var(--b1)",marginBottom:14}}>
        <div style={{fontFamily:FFD,fontSize:12,fontWeight:700,color:"var(--text)",marginBottom:8}}>Search Pricing{company?.pricing&&<span style={{marginLeft:8,fontSize:10,fontWeight:700,color:"#0cc0df",background:"rgba(12,192,223,0.1)",padding:"2px 8px",borderRadius:99}}>Custom rates</span>}</div>
        <div style={{display:"flex",gap:16,flexWrap:"wrap"}}>
          {[["UAN/EPFO","uan"],["PAN","pan"],["Aadhaar","aadhaar"],["DL","dl"],["Criminal","ecourt"],["Reference","refcheck"],["Education","education"],["Address","address_verify"]].map(([lb,key])=>(
            <div key={key} style={{fontSize:12}}><span style={{color:"var(--muted)"}}>{lb}: </span><span style={{fontWeight:700,color:"var(--brand)"}}>Rs.{getCompanyPrice(company,key)}</span></div>
          ))}
        </div>
      </div>

      <RechargeHistory txns={txns} company={company} email={user.email}/>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════
   SUPER ADMIN WALLET MANAGEMENT
══════════════════════════════════════════════════════════════ */
function WalletAdminPage({companies,wallets,setWallets,transactions,setTransactions,addToast}) {
  const {dark}=useTheme();
  const [allWallets,setAllWallets]=useState([]);
  const [allTxns,setAllTxns]=useState([]);
  const [adjustModal,setAdjustModal]=useState(null);
  const [adjustAmt,setAdjustAmt]=useState("");
  const [adjustNote,setAdjustNote]=useState("");
  const [payMode,setPayMode]=useState("UPI");
  const [payRef,setPayRef]=useState("");
  const [payDate,setPayDate]=useState(new Date().toISOString().slice(0,10));
  const [loading,setLoading]=useState(true);
  const [search,setSearch]=useState("");

  useEffect(()=>{
    const load=async()=>{
      try{
        const [w,t]=await Promise.all([getAllWallets(),getAllTransactions()]);
        setAllWallets(w);setAllTxns(t);
        const wMap={};w.forEach(x=>wMap[x.id]=x);setWallets(wMap);
      }catch(e){console.warn(e);}
      setLoading(false);
    };
    load();
  },[]);

  const doAdjust=async()=>{
    const amt=parseInt(adjustAmt)||0;
    if(!adjustModal||amt===0){addToast("Enter valid amount","error");return;}
    if(!adjustNote.trim()){addToast("Enter reason/note","error");return;}
    if(amt>0&&!payRef.trim()){addToast("Enter payment reference (UTR/TXN ID)","error");return;}
    try{
      const note=amt>0
        ?`${adjustNote} | Mode: ${payMode} | Ref: ${payRef} | Date: ${payDate}`
        :adjustNote;
      const gstAmt=amt>0?gstOf(amt):0;
      const newBal=await manualWalletAdjust(adjustModal.id,amt,note,"Super Admin",amt>0?{gstPct:GST_PCT,gstAmount:gstAmt,totalPaid:Math.round((amt+gstAmt)*100)/100,payMode,payRef:payRef.trim(),payDate,status:"success"}:{});
      setAllWallets(p=>p.map(w=>w.id===adjustModal.id?{...w,balance:newBal,totalLoaded:(w.totalLoaded||0)+(amt>0?amt:0)}:w));
      addToast(`✅ Rs.${Math.abs(amt).toLocaleString("en-IN")} ${amt>0?"added to":"deducted from"} ${adjustModal.companyName}. New balance: Rs.${newBal.toLocaleString("en-IN")}`,"success");
      // Send email notification to company
      if(amt>0){
        try{
          await fetch(CF_SEND_CANDIDATE,{
            method:"POST",headers:{"Content-Type":"application/json"},
            body:JSON.stringify({
              to:adjustModal.email||"",
              subject:"Meitho Wallet Recharged — ₹"+amt.toLocaleString("en-IN"),
              html:`<div style="font-family:Arial;max-width:480px;margin:0 auto;background:#0a0a1a;border-radius:12px;overflow:hidden;">
                <div style="background:linear-gradient(90deg,#ff3131,#ff914d);padding:20px 28px;">
                  <div style="font-size:22px;font-weight:900;color:#fff;">Meitho BGV</div>
                  <div style="font-size:12px;color:rgba(255,255,255,0.8);">Wallet Recharge Confirmation</div>
                </div>
                <div style="padding:28px;">
                  <p style="color:rgba(255,255,255,0.7);margin-bottom:16px;">Hi ${adjustModal.companyName},</p>
                  <p style="color:rgba(255,255,255,0.7);margin-bottom:20px;">Your Meitho BGV wallet has been recharged successfully.</p>
                  <div style="background:rgba(255,255,255,0.05);border-radius:10px;padding:16px;margin-bottom:20px;">
                    <div style="color:rgba(255,255,255,0.4);font-size:11px;margin-bottom:4px;">Amount Added</div>
                    <div style="font-size:32px;font-weight:900;color:#10b981;">₹${amt.toLocaleString("en-IN")}</div>
                    <div style="color:rgba(255,255,255,0.4);font-size:11px;margin-top:8px;">New Balance: ₹${newBal.toLocaleString("en-IN")}</div>
                    <div style="color:rgba(255,255,255,0.4);font-size:11px;">Payment Mode: ${payMode} | Ref: ${payRef}</div>
                  </div>
                  <a href="https://meitho.com" style="background:linear-gradient(90deg,#ff3131,#ff914d);color:#fff;text-decoration:none;padding:12px 24px;border-radius:8px;font-weight:700;font-size:13px;display:inline-block;">Login to Meitho BGV →</a>
                </div>
              </div>`,
              name:adjustModal.companyName,
            })
          });
        }catch(e){console.warn("Email failed:",e);}
      }
      setAdjustModal(null);setAdjustAmt("");setAdjustNote("");setPayRef("");setPayMode("UPI");
    }catch(e){addToast("Failed: "+e.message,"error");}
  };

  const totalBalance=allWallets.reduce((s,w)=>s+(w.balance||0),0);
  const totalLoaded=allWallets.reduce((s,w)=>s+(w.totalLoaded||0),0);
  const totalRevenue=allTxns.filter(t=>t.type==="topup").reduce((s,t)=>s+(t.platformFee||0),0);
  const lowBalCompanies=companies.filter(c=>{const w=allWallets.find(x=>x.id===c.id);return (w?.balance||0)<500&&c.status==="Active";});

  const filteredCompanies=companies.filter(c=>!search||(c.name||"").toLowerCase().includes(search.toLowerCase())||(c.email||"").toLowerCase().includes(search.toLowerCase()));

  return(
    <div className={dark?"bg-mesh-dark":"bg-mesh-light"} style={{minHeight:"100%",padding:"4px 0"}}>

      {/* Add Balance Modal */}
      {adjustModal&&<Modal title={"💰 Add/Adjust Wallet — "+adjustModal.companyName} onClose={()=>{setAdjustModal(null);setAdjustAmt("");setAdjustNote("");setPayRef("");setPayMode("UPI");}}>
        <div style={{marginBottom:14}}>
          {/* Amount */}
          <Label>Wallet credit amount, excluding GST (+add / -deduct) *</Label>
          <Inp type="number" value={adjustAmt} onChange={e=>setAdjustAmt(e.target.value)} placeholder="e.g. 2999 or -500" style={{marginBottom:6}}/>
          {parseInt(adjustAmt)>0&&<div style={{fontSize:11.5,color:"var(--muted)",marginBottom:12}}>IGST {GST_PCT}%: ₹{gstOf(parseInt(adjustAmt)).toLocaleString("en-IN",{minimumFractionDigits:2,maximumFractionDigits:2})} · Client pays ₹{(Math.round((parseInt(adjustAmt)+gstOf(parseInt(adjustAmt)))*100)/100).toLocaleString("en-IN",{minimumFractionDigits:2,maximumFractionDigits:2})}. Only ₹{parseInt(adjustAmt).toLocaleString("en-IN")} goes into the wallet.</div>}

          {/* Payment Mode - only for positive amounts */}
          {parseInt(adjustAmt)>0&&<>
            <Label>Payment Mode *</Label>
            <div style={{display:"flex",gap:8,marginBottom:12,flexWrap:"wrap"}}>
              {["UPI","NEFT","IMPS","Cheque","Cash","Bank Transfer"].map(m=>(
                <button key={m} onClick={()=>setPayMode(m)} style={{padding:"6px 14px",borderRadius:8,border:`1.5px solid ${payMode===m?"#ff3131":"var(--b1)"}`,background:payMode===m?"rgba(255,49,49,0.1)":"transparent",color:payMode===m?"#ff3131":"var(--muted)",cursor:"pointer",fontSize:12,fontWeight:payMode===m?700:500,fontFamily:FF}}>{m}</button>
              ))}
            </div>

            <Label>Payment Reference (UTR/TXN ID) *</Label>
            <Inp value={payRef} onChange={e=>setPayRef(e.target.value)} placeholder="e.g. UTR123456789 or TXN987654" style={{marginBottom:12}}/>

            <Label>Payment Date *</Label>
            <Inp type="date" value={payDate} onChange={e=>setPayDate(e.target.value)} style={{marginBottom:12}}/>
          </>}

          <Label>Note / Reason *</Label>
          <Inp value={adjustNote} onChange={e=>setAdjustNote(e.target.value)} placeholder="e.g. Wallet recharge for Q3, Correction..." style={{marginBottom:12}}/>

          {/* Preview */}
          <div style={{background:"var(--s2)",border:"1px solid var(--b1)",borderRadius:10,padding:"12px 16px"}}>
            <div style={{fontSize:11,color:"var(--muted)",marginBottom:6,fontWeight:700,textTransform:"uppercase"}}>Preview</div>
            <div style={{display:"flex",justifyContent:"space-between",marginBottom:4}}>
              <span style={{fontSize:12,color:"var(--muted)"}}>Current Balance:</span>
              <span style={{fontSize:13,fontWeight:700,color:"var(--text)"}}>₹{(adjustModal.balance||0).toLocaleString("en-IN")}</span>
            </div>
            <div style={{display:"flex",justifyContent:"space-between",marginBottom:4}}>
              <span style={{fontSize:12,color:"var(--muted)"}}>Amount:</span>
              <span style={{fontSize:13,fontWeight:700,color:parseInt(adjustAmt)>0?"var(--green)":"#ff3131"}}>{parseInt(adjustAmt)>0?"+":""}₹{Math.abs(parseInt(adjustAmt)||0).toLocaleString("en-IN")}</span>
            </div>
            <div style={{height:1,background:"var(--b1)",margin:"8px 0"}}/>
            <div style={{display:"flex",justifyContent:"space-between"}}>
              <span style={{fontSize:13,fontWeight:700,color:"var(--text)"}}>New Balance:</span>
              <span style={{fontSize:16,fontWeight:900,color:"var(--brand)",fontFamily:FFD}}>₹{Math.max(0,(adjustModal.balance||0)+(parseInt(adjustAmt)||0)).toLocaleString("en-IN")}</span>
            </div>
          </div>
        </div>
        <div style={{display:"flex",gap:10}}>
          <Btn onClick={doAdjust}>✅ Confirm & Apply</Btn>
          <BtnGhost onClick={()=>{setAdjustModal(null);setAdjustAmt("");setAdjustNote("");setPayRef("");setPayMode("UPI");}}>Cancel</BtnGhost>
        </div>
      </Modal>}

      <div className="fu" style={{marginBottom:20,display:"flex",justifyContent:"space-between",alignItems:"center",flexWrap:"wrap",gap:12}}>
        <div>
          <h2 style={{fontFamily:FFD,fontWeight:800,fontSize:26,color:"var(--text)",marginBottom:4}}>💰 Wallet Management</h2>
          <p style={{color:"var(--muted)",fontSize:13}}>Manage company wallet balances · Add funds after payment received</p>
        </div>
      </div>

      {/* Low Balance Alert */}
      {lowBalCompanies.length>0&&(
        <div style={{background:"rgba(255,49,49,0.06)",border:"1px solid rgba(255,49,49,0.2)",borderRadius:10,padding:"12px 16px",marginBottom:16,display:"flex",gap:10,alignItems:"center"}}>
          <span style={{fontSize:18}}>⚠️</span>
          <div style={{fontSize:13,color:"var(--text)"}}>
            <strong>{lowBalCompanies.length} companies</strong> have low balance (&lt;₹500): 
            <span style={{color:"var(--muted)",fontSize:12}}> {lowBalCompanies.map(c=>c.name).join(", ")}</span>
          </div>
        </div>
      )}

      {/* KPI Cards */}
      <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:14,marginBottom:20}} className="mg2">
        {[
          {label:"Total Wallet Balance",value:"₹"+totalBalance.toLocaleString("en-IN"),color:"var(--green)",icon:"💳"},
          {label:"Total Loaded",value:"₹"+totalLoaded.toLocaleString("en-IN"),color:"var(--brand)",icon:"📈"},
          {label:"Total Transactions",value:allTxns.length,color:"#ff914d",icon:"📊"},
          {label:"Low Balance",value:lowBalCompanies.length+" companies",color:lowBalCompanies.length>0?"#ff3131":"var(--green)",icon:"⚠️"},
        ].map((s,i)=>(
          <div key={i} className="fu" style={{borderRadius:14,padding:"16px 18px"}}>
            <div style={{fontSize:22,marginBottom:6}}>{s.icon}</div>
            <div style={{fontFamily:FFD,fontSize:22,fontWeight:900,color:s.color,marginBottom:3}}>{s.value}</div>
            <div style={{fontSize:11,color:"var(--muted)",fontWeight:600}}>{s.label}</div>
          </div>
        ))}
      </div>

      {/* How it works */}
      <div style={{background:"rgba(16,185,129,0.06)",border:"1px solid rgba(16,185,129,0.2)",borderRadius:12,padding:"14px 18px",marginBottom:20,display:"flex",gap:12,alignItems:"flex-start"}}>
        <span style={{fontSize:20}}>💡</span>
        <div style={{fontSize:13,color:"var(--muted)",lineHeight:1.7}}>
          <strong style={{color:"var(--text)"}}>How wallet top-up works:</strong><br/>
          Company contacts Meitho → Pays via UPI/NEFT/Bank Transfer → 
          You verify payment → Click "Add Balance" → Enter payment details → 
          Balance added instantly → Company gets email confirmation ✅
        </div>
      </div>

      {/* Company Wallets Table */}
      <div className="fu" style={{borderRadius:14,padding:0,overflow:"hidden",marginBottom:16}}>
        <div style={{padding:"12px 16px",borderBottom:"1px solid var(--b1)",display:"flex",justifyContent:"space-between",alignItems:"center",gap:12,flexWrap:"wrap"}}>
          <div style={{fontFamily:FFD,fontSize:13,fontWeight:700,color:"var(--text)"}}>Company Wallets</div>
          <Inp value={search} onChange={e=>setSearch(e.target.value)} placeholder="Search company..." style={{width:200,padding:"6px 12px",fontSize:12}}/>
        </div>
        {loading?<div style={{padding:40,textAlign:"center"}}><Spin/></div>:(
          <table style={{width:"100%",borderCollapse:"collapse"}}>
            <thead style={{background:"var(--s2)"}}><tr>
              {["Company","Balance","Total Loaded","Total Spent","Status",""].map(h=><th key={h} style={{textAlign:"left",padding:"10px 14px",fontSize:10,fontWeight:700,color:"var(--muted)",textTransform:"uppercase"}}>{h}</th>)}
            </tr></thead>
            <tbody>
              {filteredCompanies.map((c,i)=>{
                const w=allWallets.find(x=>x.id===c.id)||{balance:0,totalLoaded:0,totalSpent:0};
                const low=(w.balance||0)<500;
                return(
                  <tr key={i} style={{borderBottom:"1px solid var(--b1)",background:low&&c.status==="Active"?"rgba(255,49,49,0.02)":"transparent"}}>
                    <td style={{padding:"12px 14px"}}>
                      <div style={{fontSize:13,fontWeight:600,color:"var(--text)"}}>{c.name}</div>
                      <div style={{fontSize:11,color:"var(--muted)"}}>{c.email}</div>
                      <div style={{fontSize:10,color:"var(--muted)"}}>{c.phone||""}</div>
                    </td>
                    <td style={{padding:"12px 14px"}}>
                      <div style={{fontSize:16,fontWeight:900,color:low?"#ff3131":"var(--green)",fontFamily:FFD}}>₹{(w.balance||0).toLocaleString("en-IN")}</div>
                      {low&&c.status==="Active"&&<div style={{fontSize:10,color:"#ff3131",fontWeight:700}}>⚠ LOW BALANCE</div>}
                    </td>
                    <td style={{padding:"12px 14px",fontSize:13,color:"var(--text)",fontFamily:FFD}}>₹{(w.totalLoaded||0).toLocaleString("en-IN")}</td>
                    <td style={{padding:"12px 14px",fontSize:13,color:"#ff914d",fontFamily:FFD}}>₹{(w.totalSpent||0).toLocaleString("en-IN")}</td>
                    <td style={{padding:"12px 14px"}}><Badge color={c.status==="Active"?"var(--green)":"#f59e0b"} style={{fontSize:9}}>{c.status||"Pending"}</Badge></td>
                    <td style={{padding:"12px 14px"}}>
                      <button onClick={()=>setAdjustModal({...c,...w,companyName:c.name})}
                        style={{background:"linear-gradient(90deg,#ff3131,#ff914d)",border:"none",borderRadius:8,color:"#fff",padding:"7px 14px",cursor:"pointer",fontSize:12,fontWeight:700,fontFamily:FF,whiteSpace:"nowrap"}}>
                        + Add Balance
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Recent Transactions */}
      <div className="fu" style={{borderRadius:14,padding:0,overflow:"hidden"}}>
        <div style={{padding:"12px 16px",borderBottom:"1px solid var(--b1)",fontFamily:FFD,fontSize:13,fontWeight:700,color:"var(--text)"}}>Recent Transactions</div>
        <div style={{maxHeight:320,overflowY:"auto"}}>
          <table style={{width:"100%",borderCollapse:"collapse"}}>
            <thead style={{background:"var(--s2)"}}><tr>
              {["Date","Company","Type","Amount","Reference/Note"].map(h=><th key={h} style={{textAlign:"left",padding:"8px 12px",fontSize:10,fontWeight:700,color:"var(--muted)",textTransform:"uppercase"}}>{h}</th>)}
            </tr></thead>
            <tbody>
              {allTxns.slice(0,100).map((t,i)=>{
                const co=companies.find(c=>c.id===t.companyId);
                const isDebit=t.amount<0;
                return(
                  <tr key={i} style={{borderBottom:"1px solid var(--b1)"}}>
                    <td style={{padding:"8px 12px",fontSize:11,color:"var(--muted)"}}>{(t.createdAt||"").slice(0,10)}</td>
                    <td style={{padding:"8px 12px",fontSize:12,color:"var(--text)",fontWeight:500}}>{co?.name||t.companyId}</td>
                    <td style={{padding:"8px 12px"}}><Badge color={isDebit?"#ff914d":"var(--green)"} style={{fontSize:9}}>{t.type}</Badge></td>
                    <td style={{padding:"8px 12px",fontSize:13,fontWeight:700,color:isDebit?"#ff3131":"var(--green)",fontFamily:FFD}}>{isDebit?"":"+"}₹{Math.abs(t.amount||0).toLocaleString("en-IN")}</td>
                    <td style={{padding:"8px 12px",fontSize:11,color:"var(--muted)",maxWidth:200,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{t.note||t.searchType||""}</td>
                  </tr>
                );
              })}
              {allTxns.length===0&&<tr><td colSpan={5} style={{textAlign:"center",padding:32,color:"var(--muted)",fontSize:13}}>No transactions yet</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}


function InvoicesTab({companyId,user}) {
  const {dark}=useTheme();
  const [invoices,setInvoices]=useState([]);
  const [loading,setLoading]=useState(true);

  useEffect(()=>{
    const load=async()=>{
      try{
        const t=await getCompanyTransactions(companyId||"");
        setInvoices(t.filter(x=>x.type==="topup"));
      }catch(e){console.warn(e);}
      setLoading(false);
    };
    if(companyId)load();
  },[companyId]);

  const downloadInvoice=(inv,idx)=>{
    const lines=[
      "╔══════════════════════════════════════════╗",
      "║           MEITHO BGV — INVOICE           ║",
      "╚══════════════════════════════════════════╝",
      "",
      "Invoice No    : INV-"+(String(idx+1).padStart(4,"0")),
      "Date          : "+(inv.createdAt||"").slice(0,10),
      "Company ID    : "+(inv.companyId||""),
      "",
      "──────────────────────────────────────────",
      "  PAYMENT DETAILS",
      "──────────────────────────────────────────",
      "  Wallet Credit    : Rs."+(inv.amount||0).toLocaleString("en-IN"),
      "  Platform Fee 10% : Rs."+(inv.platformFee||0).toLocaleString("en-IN"),
      "  Total Paid       : Rs."+(inv.totalPaid||0).toLocaleString("en-IN"),
      "",
      "  Payment via      : Razorpay",
      "  Transaction ID   : "+(inv.razorpayId||"—"),
      "  Status           : PAID",
      "",
      "──────────────────────────────────────────",
      "  Non-refundable. Thank you for choosing Meitho BGV.",
      "  support: help@meitho.com | meitho.com",
      "╚══════════════════════════════════════════╝",
    ];
    const blob=new Blob([lines.join("\n")],{type:"text/plain"});
    const a=document.createElement("a");
    a.href=URL.createObjectURL(blob);
    a.download="Meitho_Invoice_INV-"+(String(idx+1).padStart(4,"0"))+".txt";
    a.click();
  };

  return(
    <div>
      {loading?<div style={{textAlign:"center",padding:32,color:"var(--muted)"}}>Loading...</div>:
      invoices.length===0?<div style={{textAlign:"center",padding:32,color:"var(--muted)"}}>No invoices yet. Top up your wallet to generate invoices.</div>:(
        <div className="glass" style={{borderRadius:14,border:"1px solid var(--b1)",overflow:"hidden"}}>
          <table style={{width:"100%",borderCollapse:"collapse"}}>
            <thead style={{background:"var(--s2)"}}><tr>
              {["Invoice #","Date","Amount","Platform Fee","Total Paid","Tx ID",""].map(h=><th key={h} style={{textAlign:"left",padding:"10px 14px",fontSize:10,fontWeight:700,color:"var(--muted)",textTransform:"uppercase"}}>{h}</th>)}
            </tr></thead>
            <tbody>
              {invoices.map((inv,i)=>(
                <tr key={i} style={{borderBottom:"1px solid var(--b1)"}}>
                  <td style={{padding:"10px 14px",fontSize:13,fontWeight:700,color:"var(--brand)",fontFamily:FFM}}>INV-{String(i+1).padStart(4,"0")}</td>
                  <td style={{padding:"10px 14px",fontSize:12,color:"var(--muted)"}}>{(inv.createdAt||"").slice(0,10)}</td>
                  <td style={{padding:"10px 14px",fontSize:13,fontWeight:700,color:"var(--green)",fontFamily:FFD}}>₹{(inv.amount||0).toLocaleString("en-IN")}</td>
                  <td style={{padding:"10px 14px",fontSize:12,color:"#ff914d"}}>₹{(inv.platformFee||0).toLocaleString("en-IN")}</td>
                  <td style={{padding:"10px 14px",fontSize:13,fontWeight:700,color:"var(--text)",fontFamily:FFD}}>₹{(inv.totalPaid||0).toLocaleString("en-IN")}</td>
                  <td style={{padding:"10px 14px",fontSize:11,color:"var(--muted)",fontFamily:FFM}}>{(inv.razorpayId||"—").slice(0,15)}</td>
                  <td style={{padding:"10px 14px"}}>
                    <button onClick={()=>downloadInvoice(inv,i)} style={{background:"var(--brand)10",border:"1px solid var(--brand)30",borderRadius:7,color:"var(--brand)",padding:"5px 10px",cursor:"pointer",fontSize:11,fontWeight:700,fontFamily:FF}}>↓ Invoice</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════
   MEITHO SCORE CALCULATOR
══════════════════════════════════════════════════════════════ */
function calcMeithoScore(data) {
  // Handle both field name formats from different API responses
  // Handle ALL possible field name formats from different APIs
  const rawHistory = data.employmentHistory || data.employment_history || 
                     data.employmenthistory || data.emp_history || [];
  const history = rawHistory.map(e=>({
    company: e.establishment_name||e.company||e.employer||e.companyName||"",
    designation: e.designation||e.title||e.position||"",
    doj: e.date_of_joining||e.doj||e.joining_date||e.from||"",
    dor: e.date_of_exit||e.dor||e.exit_date||e.to||"Currently Employed",
    salary: e.wages||e.salary||e.ctc||"",
    epf_wages: e.epf_wages||e.pf_wages||"",
  }));
  const breakdown={identity:0,stability:0,experience:0,continuity:0};
  const badges=[];
  const isFresher=!data.uan&&!data.pan&&!data.pan_number&&history.length===0;
  if(isFresher) return{score:null,breakdown,grade:"Fresher",color:"#8b5cf6",emoji:"🌟",badges:["🌟 Welcome to Corporate World"],isFresher:true};
  if(data.uan)breakdown.identity+=10;
  if(data.pan||data.pan_number)breakdown.identity+=8;
  if(data.aadhaar||data.aadhaarLinked||data.masked_aadhaar||data.aadhaar_linked)breakdown.identity+=7;
  breakdown.identity=Math.min(25,breakdown.identity);
  if(history.length>0){
    const tenures=history.map(j=>{
      const s2=new Date(j.doj||"");const e2=(j.dor&&j.dor!=="Currently Employed"&&!j.dor.startsWith("1800"))?new Date(j.dor):new Date();
      const y=(e2-s2)/(1000*60*60*24*365);return isNaN(y)||y<0?0:y;
    });
    const avgT=tenures.reduce((s,t)=>s+t,0)/(tenures.length||1);
    const totalY=tenures.reduce((s,t)=>s+t,0);
    breakdown.stability=avgT>=2?25:avgT>=1?18:avgT>=0.5?10:3;
    breakdown.experience=totalY>=5?25:totalY>=3?18:totalY>=1?12:5;
    const sorted=[...history].sort((a,b)=>new Date(a.doj||"")-new Date(b.doj||""));
    let maxGap=0;
    for(let i=1;i<sorted.length;i++){
      const pEnd=sorted[i-1].dor&&!sorted[i-1].dor.startsWith("1800")?new Date(sorted[i-1].dor):null;
      const tStart=new Date(sorted[i].doj||"");
      if(pEnd&&!isNaN(pEnd)&&!isNaN(tStart)){const g=(tStart-pEnd)/(1000*60*60*24);if(g>maxGap)maxGap=g;}
    }
    breakdown.continuity=maxGap===0?25:maxGap<=90?18:maxGap<=180?10:5;
    if(avgT>=3)badges.push("🏅 Loyal Employee");
    if(totalY>=5)badges.push("✅ Senior Professional");
    if(totalY<1)badges.push("💼 Early Career");
    if(avgT<0.5&&history.length>2)badges.push("⚠️ Job Hopper");
    const r2y=history.filter(j=>(new Date()-new Date(j.doj||""))/(1000*60*60*24*365)<=2).length;
    if(r2y>=3)badges.push("🔴 Frequent Switcher");
    if(maxGap===0&&history.length>1)badges.push("🔗 No Career Gaps");
  } else {breakdown.stability=10;breakdown.experience=5;breakdown.continuity=10;badges.push("💼 Early Career");}
  if(data.referenceRating){breakdown.continuity=Math.min(25,breakdown.continuity+Math.round(data.referenceRating/5*5));badges.push("📧 Reference Verified");}
  const score=breakdown.identity+breakdown.stability+breakdown.experience+breakdown.continuity;
  const grade=score>=85?"Highly Trusted":score>=65?"Trusted":score>=45?"Moderate Risk":"High Risk";
  const color=score>=85?"#ff3131":score>=65?"#0cc0df":score>=45?"#ff914d":"#ef4444";
  const emoji=score>=85?"🟢":score>=65?"🟡":score>=45?"🟠":"🔴";
  return{score,breakdown,grade,color,emoji,badges,isFresher:false};
}

function MeithoScoreCard({data,compact=false}) {
  const ms=calcMeithoScore(data||{});
  if(ms.isFresher) return(
    <div className="glass" style={{borderRadius:14,padding:20,border:"1px solid rgba(139,92,246,0.3)",background:"rgba(139,92,246,0.06)",marginTop:16}}>
      <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:10}}>
        <div style={{fontSize:40}}>🌟</div>
        <div><div style={{fontFamily:FFD,fontSize:18,fontWeight:800,color:"#8b5cf6"}}>Welcome to Corporate World!</div>
        <div style={{fontSize:12,color:"var(--muted)",marginTop:2}}>No UAN found — considered a Fresher</div></div>
      </div>
      <div style={{background:"rgba(139,92,246,0.1)",borderRadius:8,padding:"10px 14px",fontSize:12,color:"var(--sub)",lineHeight:1.7}}>
        ✨ First job! Meitho Score will be calculated once EPFO records are available.
      </div>
    </div>
  );
  if(compact) return(
    <div style={{display:"flex",alignItems:"center",gap:10,padding:"10px 14px",background:ms.color+"18",border:`1px solid ${ms.color}30`,borderRadius:10}}>
      <div style={{fontFamily:FFD,fontSize:28,fontWeight:900,color:ms.color}}>{ms.score}</div>
      <div><div style={{fontSize:11,fontWeight:700,color:ms.color}}>{ms.emoji} {ms.grade}</div>
      <div style={{fontSize:10,color:"var(--muted)"}}>Meitho Score / 100</div></div>
    </div>
  );
  return(
    <Card className="fu" style={{border:`1px solid ${ms.color}25`,marginTop:16}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16,paddingBottom:14,borderBottom:"1px solid var(--b1)"}}>
        <div>
          <div style={{fontFamily:FFD,fontSize:11,fontWeight:700,color:"var(--muted)",textTransform:"uppercase",letterSpacing:1,marginBottom:4}}>Meitho Score</div>
          <div style={{display:"flex",alignItems:"baseline",gap:6}}>
            <div style={{fontFamily:FFD,fontSize:48,fontWeight:900,color:ms.color,lineHeight:1}}>{ms.score}</div>
            <div style={{fontSize:14,color:"var(--muted)"}}>/100</div>
          </div>
          <div style={{fontSize:13,fontWeight:700,color:ms.color,marginTop:4}}>{ms.emoji} {ms.grade}</div>
        </div>
        <div style={{position:"relative",width:80,height:80,flexShrink:0}}>
          <svg width="80" height="80" viewBox="0 0 80 80">
            <circle cx="40" cy="40" r="34" fill="none" stroke="var(--b1)" strokeWidth="8"/>
            <circle cx="40" cy="40" r="34" fill="none" stroke={ms.color} strokeWidth="8"
              strokeDasharray={`${(ms.score/100)*213.6} 213.6`} strokeLinecap="round" transform="rotate(-90 40 40)"/>
          </svg>
          <div style={{position:"absolute",inset:0,display:"flex",alignItems:"center",justifyContent:"center",fontFamily:FFD,fontSize:18,fontWeight:900,color:ms.color}}>{ms.score}</div>
        </div>
      </div>
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,marginBottom:ms.badges?.length>0?12:0}}>
        {[{label:"Identity",key:"identity",icon:"🪪",max:25},{label:"Stability",key:"stability",icon:"⏱",max:25},{label:"Experience",key:"experience",icon:"📈",max:25},{label:"Continuity",key:"continuity",icon:"🔗",max:25}].map(s=>(
          <div key={s.key} style={{background:"var(--s2)",borderRadius:8,padding:"10px 12px"}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:5}}>
              <div style={{fontSize:11,color:"var(--muted)",fontWeight:600}}>{s.icon} {s.label}</div>
              <div style={{fontFamily:FFD,fontSize:13,fontWeight:800,color:"var(--text)"}}>{ms.breakdown[s.key]}<span style={{fontSize:9,color:"var(--muted)"}}>/{s.max}</span></div>
            </div>
            <div style={{height:4,background:"var(--b1)",borderRadius:2,overflow:"hidden"}}>
              <div style={{height:"100%",width:`${(ms.breakdown[s.key]/s.max)*100}%`,background:ms.color,borderRadius:2}}/>
            </div>
          </div>
        ))}
      </div>
      {ms.badges?.length>0&&<div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
        {ms.badges.map((b,i)=><div key={i} style={{fontSize:11,fontWeight:600,padding:"3px 10px",borderRadius:99,background:"var(--s2)",border:"1px solid var(--b1)",color:"var(--sub)"}}>{b}</div>)}
      </div>}
    </Card>
  );
}


function FullBGVPage({user,company,wallets,setWallets,employees,setEmployees,addToast,setHistory}) {
  const {dark}=useTheme();
  const [form,setForm]=useState({uan:"",pan:"",aadhaar:"",name:"",fatherName:"",address:"",dl:"",dlDob:""});
  const [checks,setChecks]=useState({uan:false,pan:false,aadhaar:false,dl:false,criminal:false});
  const [loading,setLoading]=useState(false);
  const [results,setResults]=useState(null);
  const [consent,setConsent]=useState(false);
  const isSuperAdmin=user.role==="superadmin";
  const FN_BASE="https://us-central1-meitho.cloudfunctions.net";
  const CR_UAN="https://uanverify-mtvra6khyq-uc.a.run.app";
  const CR_PAN="https://panverify-mtvra6khyq-uc.a.run.app";
  const CR_AADH="https://aadhaarverify-mtvra6khyq-uc.a.run.app";
  const CR_AADH_DL="https://aadhaardownload-mtvra6khyq-uc.a.run.app";
  const CR_DL="https://dlverify-mtvra6khyq-uc.a.run.app";
  const CR_COURT="https://ecourtsearch-mtvra6khyq-uc.a.run.app";
  const companyId=user?.companyId||user?.company?.id||company?.id||"";
  const wallet=wallets[companyId]||{balance:0};

  useEffect(()=>{
    if(companyId&&!wallets[companyId]){
      getWallet(companyId).then(w=>setWallets(p=>({...p,[companyId]:w}))).catch(()=>{});
    }
  },[companyId]);

  // Calculate cost based on selected checks
  const selectedChecks=Object.entries(checks).filter(([,v])=>v).map(([k])=>k);
  // Price map for full BGV checks
  const searchCost=selectedChecks.reduce((s,k)=>s+getCompanyPrice(company,k),0);

  const CHECK_CONFIG=[
    {id:"uan",label:"UAN / EPFO",icon:"🏦",desc:"Employment history from EPFO",price:100,placeholder:"12-digit UAN",field:"uan"},
    {id:"pan",label:"PAN Card",icon:"📋",desc:"Identity & Aadhaar linkage",price:50,placeholder:"ABCDE1234F",field:"pan"},
    {id:"aadhaar",label:"Aadhaar Verify",icon:"🪪",desc:"DigiLocker Aadhaar authentication",price:50,placeholder:"12-digit Aadhaar",field:"aadhaar"},
    {id:"dl",label:"Driving License",icon:"🚗",desc:"DL validity & vehicle classes",price:50,placeholder:"DL number e.g. UP2020150000000",field:"dl"},
    {id:"criminal",label:"Criminal Check",icon:"⚖️",desc:"eCourt India database search",price:75,placeholder:"Candidate full name",field:"name"},
    {id:"education",label:"Education",icon:"🎓",desc:"Degree verification",price:100,placeholder:"Candidate full name",field:"name",comingSoon:true},
  ];

  const doFullBGV=async()=>{
    if(selectedChecks.length===0){addToast("Select at least one check","error");return;}
    const unauthorizedCheck=selectedChecks.find(k=>!hasModuleAccess(user,isSuperAdmin,k==="criminal"?"ecourt":k));
    if(unauthorizedCheck){addToast("You don't have access to one of the selected checks. Ask your Company Admin to enable it.","error");return;}
    if(checks.uan&&!form.uan){addToast("Enter UAN number","error");return;}
    if(checks.pan&&!form.pan){addToast("Enter PAN number","error");return;}
    if(checks.dl&&!form.dl){addToast("Enter DL number","error");return;}
    if(checks.dl&&!form.dlDob){addToast("Enter date of birth for DL verification","error");return;}
    if(checks.aadhaar&&!form.aadhaar){addToast("Enter Aadhaar number","error");return;}
    if(checks.criminal&&!form.name){addToast("Enter candidate name for criminal check","error");return;}

    if(!consent){addToast("Please confirm candidate consent","error");return;}
    if(!isSuperAdmin&&wallet.balance<searchCost){
      addToast("Insufficient wallet balance. Need ₹"+searchCost+" Available: ₹"+(wallet.balance||0),"error");return;
    }
    setLoading(true);setResults(null);
    const res={uan:null,pan:null,aadhaar:null,dl:null,criminal:null,education:null};
    const headers=await getAuthedHeaders();
    const calls=[];

    if(checks.uan&&form.uan)
      calls.push(fetch("https://uanverify-mtvra6khyq-uc.a.run.app",{method:"POST",headers,body:JSON.stringify({uan:form.uan.replace(/\D/g,"")})}).then(r=>r.json()).then(j=>{res.uan=j;}).catch(e=>{res.uan={error:e.message};}));
    if(checks.pan&&form.pan)
      calls.push(fetch("https://panverify-mtvra6khyq-uc.a.run.app",{method:"POST",headers,body:JSON.stringify({pan:form.pan.toUpperCase()})}).then(r=>r.json()).then(j=>{res.pan=j;}).catch(e=>{res.pan={error:e.message};}));
    if(checks.dl&&form.dl&&form.dlDob)
      calls.push(fetch("https://dlverify-mtvra6khyq-uc.a.run.app",{method:"POST",headers,body:JSON.stringify({dl_number:form.dl.toUpperCase(),dob:form.dlDob})}).then(r=>r.json()).then(j=>{res.dl=j;}).catch(e=>{res.dl={error:e.message};}));

    if(checks.aadhaar&&form.aadhaar)
      calls.push(fetch("https://aadhaarverify-mtvra6khyq-uc.a.run.app",{method:"POST",headers,body:JSON.stringify({aadhaar:form.aadhaar.replace(/\D/g,"")})}).then(r=>r.json()).then(j=>{res.aadhaar=j;}).catch(e=>{res.aadhaar={error:e.message};}));

    if(checks.criminal&&(form.name||form.uan)){
      const crimPayload={name:form.name||combined?.name||""};
      if(form.fatherName?.trim())crimPayload.father_name=form.fatherName.trim();
      if(form.address?.trim())crimPayload.address=form.address.trim();
      if(crimPayload.name)
        calls.push(fetch("https://ecourtsearch-mtvra6khyq-uc.a.run.app",{method:"POST",headers,body:JSON.stringify(crimPayload)}).then(r=>r.json()).then(j=>{res.criminal=j;}).catch(e=>{res.criminal={error:e.message};}));
    }

    await Promise.all(calls);

    const uanData=res.uan?.data||{};
    const panData=res.pan?.data||{};
    const dlData=res.dl?.data||{};
    const combined={
      uan:form.uan||uanData.uan||"",
      pan:form.pan||panData.pan_number||"",
      dl_number:form.dl||dlData.dl_number||"",
      name:uanData.name||panData.full_name||dlData.name||form.name||"",
      dob:uanData.dob||panData.dob||dlData.dob||"",
      gender:uanData.gender||panData.gender||dlData.gender||"",
      employmentHistory:(uanData.employment_history||[]).map(e=>({company:e.establishment_name||"",designation:e.designation||"",doj:e.date_of_joining||"",dor:e.date_of_exit||"",salary:e.wages||""})),
      aadhaarLinked:panData.aadhaar_linked||false,
      masked_aadhaar:panData.masked_aadhaar||"",
      panStatus:panData.status||"",
      dl_valid:dlData.dl_number?!dlData.isExpired:null,
      dl_expiry:dlData.doe||"",
      dl_state:dlData.state||"",
      dl_vehicle_classes:dlData.vehicle_classes||[],
      dl_blood_group:dlData.blood_group||"",
      dl_profile_image:dlData.profile_image||"",
    };
    const ms=calcMeithoScore(combined);
    try{
      await addEmployee({...combined,source:"full_bgv",addedBy:companyId,verifiedAt:new Date().toISOString(),meithoScore:ms.score,meithoGrade:ms.grade});
    }catch(e){console.warn("Save error:",e);}
    const audit={userId:user.id,userName:user.name||user.email,companyId,companyName:company?.name||"",searchType:"full_bgv",searchValue:form.uan||form.pan||form.name,found:!!(combined.name),source:"api",timestamp:new Date().toISOString(),employeeData:combined,meithoScore:ms.score};
    try{await addSearchHistory(audit);setHistory(p=>[audit,...p]);}catch(e){}

    // Deduct wallet — this was previously missing entirely, so every Full
    // BGV search ran for free regardless of outcome. Only skip charging if
    // every single selected check came back as an outright API error (no
    // genuine attempt succeeded) — matching the same policy used in the
    // main Search page.
    const anyCheckSucceeded=selectedChecks.some(k=>res[k]&&!res[k]?.error);
    if(!isSuperAdmin&&anyCheckSucceeded){
      try{
        const rc=await walletChargeSrv([{type:"full_bgv",qty:1}],"Full BGV Bundle — "+(combined.name||form.uan||form.pan||form.name||""),"full_bgv");
        if(rc.charged){
          setWallets(p=>({...p,[companyId]:{...p[companyId],balance:rc.after}}));
          checkLowWalletAndNotify(companyId,company?.name,rc.before,rc.after);
        }
      }catch(e){console.warn("Wallet deduction failed:",e);}
    }else if(!isSuperAdmin&&!anyCheckSucceeded){
      addToast("None of the selected checks could be completed — you have not been charged for this attempt.","amber");
    }

    setResults({combined,ms,res,cost:searchCost,selectedChecks});
    setLoading(false);
    addToast("✅ BGV complete! Meitho Score: "+ms.score+"/100","success");
  };

  return(
    <div className={dark?"bg-mesh-dark":"bg-mesh-light"} style={{minHeight:"100%",padding:"4px 0"}}>
      <div className="fu" style={{marginBottom:20}}>
        <h2 style={{fontFamily:FFD,fontWeight:800,fontSize:26,color:"var(--text)",marginBottom:4}}>Full BGV Check</h2>
        <p style={{color:"var(--muted)",fontSize:14}}>Select checks to run and get a combined report with Meitho Score</p>
      </div>

      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:16}} className="mg1">
        {/* Left - Config */}
        <div style={{display:"flex",flexDirection:"column",gap:14}}>
          {/* Check selector */}
          <div className="fu1 glass neon-hover" style={{borderRadius:14,padding:20,border:"1px solid var(--b1)"}}>
            <div style={{fontFamily:FFD,fontSize:13,fontWeight:700,color:"var(--text)",marginBottom:14}}>Select Verifications</div>
            {CHECK_CONFIG.map(c=>{
              const moduleKey=c.id==="criminal"?"ecourt":c.id; // FullBGV uses "criminal", canonical module key is "ecourt"
              const locked=!c.comingSoon&&!hasModuleAccess(user,isSuperAdmin,moduleKey);
              return(
              <div key={c.id} onClick={()=>{
                if(c.comingSoon)return;
                if(locked){addToast("You don't have access to this module. Ask your Company Admin to enable it.","amber");return;}
                setChecks(p=>({...p,[c.id]:!p[c.id]}));
              }} style={{display:"flex",alignItems:"center",gap:12,padding:"10px 12px",marginBottom:8,borderRadius:10,border:`1.5px solid ${c.comingSoon||locked?"var(--b1)":checks[c.id]?"var(--brand)":"var(--b1)"}`,background:c.comingSoon||locked?"transparent":checks[c.id]?"var(--brand)06":"transparent",cursor:c.comingSoon?"default":locked?"not-allowed":"pointer",transition:"all .15s",userSelect:"none",opacity:c.comingSoon||locked?0.5:1}}>
                <div style={{width:20,height:20,borderRadius:5,border:`2px solid ${c.comingSoon||locked?"var(--b2)":checks[c.id]?"var(--brand)":"var(--b2)"}`,background:c.comingSoon||locked?"transparent":checks[c.id]?"var(--brand)":"transparent",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
                  {!c.comingSoon&&!locked&&checks[c.id]&&<svg width="11" height="11" viewBox="0 0 11 11" fill="none"><path d="M2 5.5l2.5 2.5L9 3" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>}
                  {locked&&<span style={{fontSize:10}}>🔒</span>}
                </div>
                <span style={{fontSize:20,flexShrink:0}}>{c.icon}</span>
                <div style={{flex:1}}>
                  <div style={{fontSize:13,fontWeight:600,color:"var(--text)"}}>{c.label}{c.comingSoon&&<span style={{marginLeft:6,fontSize:9,background:"#ff914d",color:"#fff",padding:"1px 5px",borderRadius:99,fontWeight:700}}>SOON</span>}{locked&&<span style={{marginLeft:6,fontSize:9,background:"var(--muted)",color:"#fff",padding:"1px 5px",borderRadius:99,fontWeight:700}}>NO ACCESS</span>}</div>
                  <div style={{fontSize:11,color:"var(--muted)"}}>{c.comingSoon?"Coming soon — API activation pending":locked?"Ask your Company Admin for access":c.desc}</div>
                </div>
                <div style={{fontFamily:FFD,fontSize:14,fontWeight:800,color:c.comingSoon||locked?"var(--muted)":checks[c.id]?"var(--brand)":"var(--muted)"}}>₹{c.price}</div>
              </div>
              );
            })}
            {/* Total cost */}
            <div style={{marginTop:10,padding:"10px 14px",background:"var(--s2)",borderRadius:8,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
              <span style={{fontSize:13,color:"var(--muted)"}}>Total Cost</span>
              <div style={{display:"flex",gap:12,alignItems:"center"}}>
                <span style={{fontFamily:FFD,fontSize:20,fontWeight:900,color:"#ff3131"}}>₹{searchCost}</span>
                {!isSuperAdmin&&<Badge color={wallet.balance>=searchCost?"var(--green)":"#ff3131"} style={{fontSize:10}}>Wallet: ₹{(wallet.balance||0).toLocaleString("en-IN")}</Badge>}
                {isSuperAdmin&&<Badge color="var(--brand)" style={{fontSize:10}}>👑 Unlimited</Badge>}
              </div>
            </div>
          </div>

          {/* Input fields */}
          <div className="fu2 glass neon-hover" style={{borderRadius:14,padding:20,border:"1px solid var(--b1)"}}>
            <div style={{fontFamily:FFD,fontSize:13,fontWeight:700,color:"var(--text)",marginBottom:14}}>Candidate Details</div>
            <div style={{display:"flex",flexDirection:"column",gap:12}}>
              {checks.uan&&<div><Label>UAN Number *</Label><Inp value={form.uan} onChange={e=>setForm(p=>({...p,uan:e.target.value.replace(/\D/g,"").slice(0,12)}))} placeholder="12-digit UAN" style={{fontFamily:FFM}}/></div>}
              {checks.pan&&<div><Label>PAN Number *</Label><Inp value={form.pan} onChange={e=>setForm(p=>({...p,pan:e.target.value.toUpperCase().slice(0,10)}))} placeholder="ABCDE1234F" style={{fontFamily:FFM}}/></div>}
              {checks.aadhaar&&<div><Label>Aadhaar Number *</Label><Inp value={form.aadhaar} onChange={e=>setForm(p=>({...p,aadhaar:e.target.value.replace(/\D/g,"").slice(0,12)}))} placeholder="12-digit Aadhaar" style={{fontFamily:FFM}}/></div>}
              {checks.dl&&<div><Label>DL Number *</Label><Inp value={form.dl} onChange={e=>setForm(p=>({...p,dl:e.target.value.toUpperCase().slice(0,20)}))} placeholder="e.g. UP2020150000000" style={{fontFamily:FFM}}/></div>}
              {checks.dl&&<div><Label>Date of Birth * (for DL)</Label><Inp type="date" value={form.dlDob} onChange={e=>setForm(p=>({...p,dlDob:e.target.value}))}/></div>}
              <div><Label>Candidate Full Name {checks.criminal?"*":""}</Label><Inp value={form.name} onChange={e=>setForm(p=>({...p,name:e.target.value}))} placeholder="e.g. Rajesh Kumar"/></div>
              {checks.criminal&&<div><Label>Father's Name * (for Criminal Check)</Label><Inp value={form.fatherName||""} onChange={e=>setForm(p=>({...p,fatherName:e.target.value}))} placeholder="e.g. Suresh Kumar"/></div>}
              {checks.criminal&&<div><Label>Address * (for Criminal Check)</Label><Inp value={form.address||""} onChange={e=>setForm(p=>({...p,address:e.target.value}))} placeholder="e.g. Chennai Tamil Nadu"/></div>}
              <div onClick={()=>setConsent(c=>!c)} style={{display:"flex",alignItems:"flex-start",gap:10,cursor:"pointer",padding:"10px 12px",background:consent?"var(--green)08":"var(--s2)",border:`1px solid ${consent?"var(--green)30":"var(--b1)"}`,borderRadius:8,userSelect:"none"}}>
                <div style={{width:18,height:18,borderRadius:4,border:`2px solid ${consent?"var(--green)":"var(--b2)"}`,background:consent?"var(--green)":"transparent",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,marginTop:1}}>
                  {consent&&<svg width="10" height="10" viewBox="0 0 10 10" fill="none"><path d="M2 5l2.5 2.5L8 3" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>}
                </div>
                <div style={{fontSize:12,color:"var(--sub)",lineHeight:1.6}}>Candidate has given consent for all selected verification checks.</div>
              </div>
              <Btn onClick={doFullBGV} style={{justifyContent:"center",background:"linear-gradient(90deg,#ff3131,#ff914d)"}}>
                {loading?<Spin/>:`🔍 Run ${selectedChecks.length} Check${selectedChecks.length!==1?"s":""} — ₹${searchCost}`}
              </Btn>
            </div>
          </div>
        </div>

        {/* Right - Results */}
        <div>
          {loading&&<div className="glass" style={{borderRadius:14,padding:40,border:"1px solid var(--b1)",textAlign:"center"}}>
            <div style={{fontSize:40,marginBottom:12}} className="float">🔍</div>
            <div style={{fontFamily:FFD,fontSize:16,fontWeight:700,color:"var(--text)",marginBottom:8}}>Running {selectedChecks.length} Check{selectedChecks.length!==1?"s":""}...</div>
            <div style={{fontSize:13,color:"var(--muted)"}}>Verifying from government databases simultaneously</div>
          </div>}

          {results&&<div style={{display:"flex",flexDirection:"column",gap:12}}>
            <MeithoScoreCard data={results.combined}/>

            {/* DL Result in Full BGV */}
            {results.res?.dl?.success&&results.res.dl.data&&(()=>{
              const d=results.res.dl.data;
              const isExpired=d.doe&&!d.doe.startsWith("1800")&&new Date(d.doe)<new Date();
              return(
                <Card className="fu1" style={{border:`1px solid ${isExpired?"rgba(255,49,49,0.3)":"rgba(255,49,49,0.3)"}`}}>
                  <div style={{fontFamily:FFD,fontSize:13,fontWeight:700,color:"var(--text)",marginBottom:12}}>🚗 Driving License Result</div>
                  <div style={{display:"flex",gap:14,alignItems:"flex-start"}}>
                    {d.has_image&&d.profile_image&&(
                      <img src={`data:image/jpeg;base64,${d.profile_image}`} alt="DL"
                        style={{width:60,height:75,objectFit:"cover",borderRadius:8,border:"2px solid var(--b1)",flexShrink:0}}/>
                    )}
                    <div style={{flex:1}}>
                      <div style={{fontFamily:FFD,fontSize:17,fontWeight:800,color:"var(--text)",marginBottom:3}}>{d.name}</div>
                      <div style={{fontSize:12,fontFamily:FFM,color:"var(--muted)",marginBottom:8}}>{d.dl_number}</div>
                      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
                        {[["State",d.state],["Expiry",d.doe],["Blood Group",d.blood_group],["Status",isExpired?"⚠️ Expired":"✅ Valid"]].filter(([,v])=>v).map(([l,v])=>(
                          <div key={l} style={{background:"var(--s2)",borderRadius:6,padding:"7px 10px"}}>
                            <div style={{fontSize:9,color:"var(--muted)",fontWeight:700,textTransform:"uppercase"}}>{l}</div>
                            <div style={{fontSize:12,fontWeight:600,color:l==="Status"&&isExpired?"#ff3131":"var(--text)"}}>{v}</div>
                          </div>
                        ))}
                      </div>
                      {d.vehicle_classes?.length>0&&(
                        <div style={{marginTop:8,display:"flex",gap:6,flexWrap:"wrap"}}>
                          {d.vehicle_classes.map((vc,i)=><Badge key={i} color="var(--brand)" style={{fontSize:10}}>🚗 {vc}</Badge>)}
                        </div>
                      )}
                    </div>
                  </div>
                </Card>
              );
            })()}

            <Card className="fu1">
              <div style={{fontFamily:FFD,fontSize:13,fontWeight:700,color:"var(--text)",marginBottom:12}}>Verification Summary</div>
              {CHECK_CONFIG.filter(c=>results.selectedChecks.includes(c.id)).map((c,i)=>{
                const r=results.res[c.id];
                const ok=c.id==="uan"?r?.status_code===200:c.id==="pan"?r?.status_code===200:c.id==="aadhaar"?r?.status_code===200:c.id==="dl"?r?.success===true:c.id==="criminal"?r?.success===true:false;
                const detail=c.id==="dl"?r?.data?.dl_number?"✓ DL Verified · "+(r.data.state||""):"Not found":c.id==="criminal"?r?.data?("⚖️ "+r.data.total_cases+" cases · "+(r.data.criminal_cases>0?"⚠ "+r.data.criminal_cases+" Criminal":"✅ Clear")):"Not found":c.id==="education"?"Coming Soon":"✓ Verified";
                return(
                  <div key={i} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"10px 0",borderBottom:"1px solid var(--b1)"}}>
                    <div style={{display:"flex",gap:8,alignItems:"center"}}>
                      <span style={{fontSize:18}}>{c.icon}</span>
                      <div>
                        <div style={{fontSize:13,color:"var(--sub)",fontWeight:500}}>{c.label}</div>
                        {ok&&<div style={{fontSize:11,color:"var(--muted)"}}>{detail}</div>}
                      </div>
                    </div>
                    <Badge color={ok?"var(--green)":r?.error?"#ff3131":"var(--muted)"} style={{fontSize:10}}>
                      {ok?"✓ Done":r?.error?"Error":"Not Run"}
                    </Badge>
                  </div>
                );
              })}
            </Card>
            <Btn onClick={()=>addToast("Downloading BGV Report...","success")} style={{justifyContent:"center",background:"linear-gradient(90deg,var(--green),#059669)"}}>↓ Download BGV Report</Btn>
          </div>}

          {!loading&&!results&&(
            <div className="glass" style={{borderRadius:14,padding:32,border:"1px solid var(--b1)",textAlign:"center",color:"var(--muted)"}}>
              <div style={{fontSize:40,marginBottom:12}}>🛡️</div>
              <div style={{fontSize:14,color:"var(--sub)",marginBottom:8}}>Select checks and run Full BGV</div>
              <div style={{fontSize:12,color:"var(--muted)"}}>Results appear here after verification</div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}


/* ══════════════════════════════════════════════════════════════
   2FA SETTINGS (in user settings modal)
══════════════════════════════════════════════════════════════ */
function TwoFASettings({user,addToast}) {
  const [enabled,setEnabled]=useState(()=>localStorage.getItem("meitho_2fa_"+user.id)==="true");
  const [loading,setLoading]=useState(false);

  const toggle=async()=>{
    setLoading(true);
    await new Promise(r=>setTimeout(r,800));
    const newVal=!enabled;
    setEnabled(newVal);
    localStorage.setItem("meitho_2fa_"+user.id,String(newVal));
    addToast(newVal?"✅ 2FA enabled! OTP will be sent on next login.":"2FA disabled.","success");
    setLoading(false);
  };

  return(
    <div style={{background:"var(--s2)",borderRadius:12,padding:16,border:"1px solid var(--b1)"}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",flexWrap:"wrap",gap:10}}>
        <div>
          <div style={{fontFamily:FFD,fontSize:14,fontWeight:700,color:"var(--text)",marginBottom:4}}>🔐 Two-Factor Authentication</div>
          <div style={{fontSize:12,color:"var(--muted)"}}>
            {enabled?"✅ Enabled — OTP sent to email on every login":"Disabled — Single password login"}
          </div>
        </div>
        <button onClick={toggle} disabled={loading} style={{background:enabled?"rgba(255,49,49,0.1)":"var(--brand)10",border:"1px solid "+(enabled?"rgba(255,49,49,0.3)":"var(--brand)30"),borderRadius:8,color:enabled?"#ff3131":"var(--brand)",padding:"8px 16px",cursor:"pointer",fontWeight:700,fontSize:13,fontFamily:FF}}>
          {loading?<Spin/>:enabled?"Disable 2FA":"Enable 2FA"}
        </button>
      </div>
      {enabled&&<div style={{marginTop:10,fontSize:12,color:"var(--muted)",background:"var(--brand)06",borderRadius:8,padding:"8px 12px"}}>
        OTP will be sent to <strong style={{color:"var(--text)"}}>{user.email}</strong> on every login
      </div>}
    </div>
  );
}


/* ══════════════════════════════════════════════════════════════
   REFERENCE CHECK MODULE
══════════════════════════════════════════════════════════════ */
function ReferenceCheckPage({user,company,wallets,addToast}) {
  const {dark}=useTheme();
  const isSuperAdmin=user.role==="superadmin";
  const companyId=user?.companyId||user?.company?.id||company?.id||"";
  const wallet=wallets[companyId]||{balance:0};
  const [checks,setChecks]=useState([]);
  const [loading,setLoading]=useState(false);
  const [loadingChecks,setLoadingChecks]=useState(true);
  const [showNew,setShowNew]=useState(false);
  const [detailModal,setDetailModal]=useState(null);
  const [form,setForm]=useState({
    candidateName:"",candidateEmail:"",candidatePhone:"",designation:"",
    ref1Name:"",ref1Email:"",ref1Company:"",
    ref2Name:"",ref2Email:"",ref2Company:"",
  });
  const COST=getCompanyPrice(company,"refcheck");
  const FN_BASE="https://us-central1-meitho.cloudfunctions.net";
  const CR_UAN="https://uanverify-mtvra6khyq-uc.a.run.app";
  const CR_PAN="https://panverify-mtvra6khyq-uc.a.run.app";
  const CR_AADH="https://aadhaarverify-mtvra6khyq-uc.a.run.app";
  const CR_AADH_DL="https://aadhaardownload-mtvra6khyq-uc.a.run.app";
  const CR_DL="https://dlverify-mtvra6khyq-uc.a.run.app";
  const CR_COURT="https://ecourtsearch-mtvra6khyq-uc.a.run.app";
  const CANDIDATE_EMAIL_URL=CF_SEND_CANDIDATE;
  const REF_EMAIL_URL=CF_SEND_REFERENCE;
  const REMINDER_URL=CF_SEND_REMINDER;

  // Load checks from Firestore with real-time updates
  useEffect(()=>{
    let unsub=null;
    if(!db){setLoadingChecks(false);return;}
    try{
      let q;
      if(isSuperAdmin){
        q=query(collection(db,"refchecks"),orderBy("initiatedAt","desc"));
      }else if(companyId){
        q=query(collection(db,"refchecks"),where("companyId","==",companyId),orderBy("initiatedAt","desc"));
      }else{
        q=query(collection(db,"refchecks"),where("initiatedBy","==",user.id||""),orderBy("initiatedAt","desc"));
      }
      unsub=onSnapshot(q,(snap)=>{
        const data=snap.docs.map(d=>({id:d.id,...d.data()}));
        data.sort((a,b)=>new Date(b.initiatedAt||0)-new Date(a.initiatedAt||0));
        setChecks(data);
        setLoadingChecks(false);
      },(e)=>{
        console.warn("Load refchecks error:",e.message);
        // Fallback: load without orderBy
        getDocs(collection(db,"refchecks")).then(snap=>{
          let data=snap.docs.map(d=>({id:d.id,...d.data()}));
          if(!isSuperAdmin)data=data.filter(c=>c.companyId===companyId||c.initiatedBy===user.id);
          data.sort((a,b)=>new Date(b.initiatedAt||0)-new Date(a.initiatedAt||0));
          setChecks(data);
        }).catch(()=>{}).finally(()=>setLoadingChecks(false));
      });
    }catch(e){console.warn("Load refchecks:",e);setLoadingChecks(false);}
    return()=>{if(unsub)unsub();};
  },[companyId,isSuperAdmin]);

  const doInitiate=async()=>{
    if(!form.candidateName||!form.candidateEmail){addToast("Enter candidate name and email","error");return;}
    if(!form.ref1Email&&!form.ref2Email){addToast("Enter at least one reference email","error");return;}
    if(!isSuperAdmin&&wallet.balance<COST){addToast("Insufficient wallet balance. Need ₹"+COST,"error");return;}
    setLoading(true);
    try{
      const checkId="RC-"+Date.now().toString(36).toUpperCase();
      const newCheck={
        id:checkId,companyId,
        initiatedBy:user.id,initiatedByName:user.name||user.email,
        candidateName:form.candidateName,
        candidateEmail:form.candidateEmail,
        candidatePhone:form.candidatePhone,
        designation:form.designation,
        ref1:{name:form.ref1Name,email:form.ref1Email,company:form.ref1Company,status:"pending"},
        ref2:{name:form.ref2Name,email:form.ref2Email,company:form.ref2Company,status:"pending"},
        candidateStatus:"pending",
        overallStatus:"pending",
        cost:COST,
        initiatedAt:new Date().toISOString(),
      };

      // Save to Firestore
      try{
        await setDoc(doc(collection(db,"refchecks"),checkId),newCheck);
      }catch(e){console.warn("Firestore save failed:",e);}

      // Send candidate email only
      try{
        const r=await fetch(CANDIDATE_EMAIL_URL,{
          method:"POST",headers:{"Content-Type":"application/json"},
          body:JSON.stringify({
            candidateName:form.candidateName,
            candidateEmail:form.candidateEmail,
            companyName:company?.name||user.company||"Meitho BGV",
            designation:form.designation||"the applied role",
            formLink:"https://meitho.com/refcheck/"+checkId+"/candidate",
            hrName:user.name||user.email,
          })
        });
        const j=await r.json();
        if(j.success)addToast("✅ Email sent to "+form.candidateEmail,"success");
        else addToast("⚠ Check initiated but email failed: "+j.error,"amber");
      }catch(e){
        addToast("⚠ Check initiated but email failed: "+e.message,"amber");
      }

      setChecks(p=>[newCheck,...p]);
      setShowNew(false);
      setForm({candidateName:"",candidateEmail:"",candidatePhone:"",designation:"",ref1Name:"",ref1Email:"",ref1Company:"",ref2Name:"",ref2Email:"",ref2Company:""});
    }catch(e){addToast("Failed: "+e.message,"error");}
    setLoading(false);
  };

  const downloadRefReport=(check)=>{
    const dt=new Date().toLocaleString("en-IN");
    const cd=check.candidate_data||{};
    const r1=check.ref1_data||{};
    const r2=check.ref2_data||{};
    const rv1=r1.verifications||{};
    const rv2=r2.verifications||{};
    const avg=(vals)=>{const f=vals.filter(v=>v!=null&&v!=="");return f.length?Math.round(f.reduce((s,v)=>s+Number(v),0)/f.length*10)/10:0;};
    const avgB=avg([r1.behaviour,r2.behaviour]);
    const avgE=avg([r1.ethical,r2.ethical]);
    const avgP=avg([r1.professional,r2.professional]);
    const overall=avg([avgB,avgE,avgP]);
    const star=(n)=>n?"★".repeat(Math.round(n))+"☆".repeat(5-Math.round(n))+" "+n+"/5":"Not rated";
    const vBadge=(v)=>v==="true"?'<span style="color:#ff3131;font-weight:700;">✓ True</span>':v==="false"?'<span style="color:#ef4444;font-weight:700;">✗ False</span>':v==="partial"?'<span style="color:#f59e0b;font-weight:700;">~ Partially True</span>':"<span style='color:#94a3b8'>Not verified</span>";
    const field=(label,value,vKey,rv)=>value?`
      <tr>
        <td style="padding:8px 12px;color:#64748b;font-size:12px;border-bottom:1px solid #f1f5f9;width:160px;">${label}</td>
        <td style="padding:8px 12px;font-weight:600;font-size:12px;border-bottom:1px solid #f1f5f9;">${value}</td>
        <td style="padding:8px 12px;font-size:12px;border-bottom:1px solid #f1f5f9;">${vBadge(rv[vKey]?.status)}</td>
        <td style="padding:8px 12px;font-size:11px;color:#64748b;border-bottom:1px solid #f1f5f9;font-style:italic;">${rv[vKey]?.correction||""}</td>
      </tr>`:"";

    const html=`<!DOCTYPE html><html><head><meta charset="UTF-8"/>
<title>Reference Check Report — ${check.candidateName}</title>
<style>
*{margin:0;padding:0;box-sizing:border-box;}
body{font-family:'Segoe UI',Arial,sans-serif;color:#1a1a2e;background:#fff;font-size:13px;}
@page{margin:10mm;size:A4;}
@media print{
  body{-webkit-print-color-adjust:exact;print-color-adjust:exact;}
  .no-print{display:none;}
  .page-break{page-break-before:always;}
}
.watermark{
  position:fixed;top:50%;left:50%;transform:translate(-50%,-50%) rotate(-30deg);
  opacity:0.04;z-index:0;pointer-events:none;
}
.content{position:relative;z-index:1;}
</style>
</head><body>
<!-- Watermark Logo -->
<div class="watermark"><img src="${LOGO1}" style="width:400px;" alt=""/></div>

<div class="content">
<!-- Download button (hidden on print) -->
<div class="no-print" style="background:#0a0a1a;padding:12px 24px;text-align:right;">
  <button onclick="window.print()" style="background:linear-gradient(90deg,#ff3131,#ff914d);color:#fff;border:none;padding:10px 24px;border-radius:8px;cursor:pointer;font-size:13px;font-weight:700;">⬇ Download as PDF</button>
  <span style="color:#fff;font-size:11px;margin-left:12px;opacity:0.6;">Tip: In print dialog → Save as PDF</span>
</div>

<!-- Header -->
<div style="background:linear-gradient(135deg,#0a0a1a,#1a2040);color:#fff;padding:24px 36px;display:flex;justify-content:space-between;align-items:center;">
  <div>
    <img src="${LOGO1}" style="height:36px;margin-bottom:6px;" alt="Meitho BGV"/>
    <div style="font-size:9px;color:#0cc0df;letter-spacing:2px;font-weight:700;">REFERENCE VERIFICATION REPORT</div>
  </div>
  <div style="text-align:right;">
    <div style="font-size:18px;font-weight:900;color:#0cc0df;font-family:monospace;">RC-${check.id}</div>
    <div style="font-size:10px;color:rgba(255,255,255,0.5);margin-top:2px;">${dt}</div>
    <div style="font-size:10px;color:rgba(255,255,255,0.4);">Status: ${(check.overallStatus||"pending").toUpperCase()}</div>
  </div>
</div>
<div style="background:#0cc0df;color:#fff;padding:6px 36px;display:flex;justify-content:space-between;font-size:10px;font-weight:700;letter-spacing:.5px;">
  <span>MEITHO BGV PLATFORM</span><span>meitho.com · help@meitho.com · 8300016265</span>
</div>

<!-- Candidate Details -->
<div style="padding:20px 36px;">
  <div style="font-size:9px;font-weight:800;letter-spacing:1.5px;text-transform:uppercase;color:#0cc0df;border-bottom:2px solid #0cc0df;padding-bottom:5px;margin-bottom:14px;">1. Candidate Information</div>
  <div style="display:grid;grid-template-columns:repeat(3,1fr);gap:10px;margin-bottom:16px;">
    ${[["Full Name",check.candidateName],["Designation",check.designation||cd.designation],["Initiated By",check.initiatedByName],["Check ID",check.id],["Initiated On",new Date(check.initiatedAt).toLocaleDateString("en-IN")],["Overall Status",(check.overallStatus||"pending").toUpperCase()]].map(([l,v])=>v?`<div style="background:#f8fafc;border-radius:8px;padding:9px 12px;border-left:3px solid #0cc0df;"><div style="font-size:9px;color:#94a3b8;font-weight:700;text-transform:uppercase;margin-bottom:2px;">${l}</div><div style="font-size:13px;font-weight:700;">${v}</div></div>`:"").join("")}
  </div>

<!-- Candidate Employment Claims & Verification -->
<div style="font-size:9px;font-weight:800;letter-spacing:1.5px;text-transform:uppercase;color:#0cc0df;border-bottom:2px solid #0cc0df;padding-bottom:5px;margin:16px 0 12px;">2. Employment Claims & Reference Verification</div>
<table style="width:100%;border-collapse:collapse;margin-bottom:16px;">
  <thead>
    <tr style="background:#f1f5f9;">
      <th style="padding:8px 12px;text-align:left;font-size:10px;color:#64748b;font-weight:700;text-transform:uppercase;">Field</th>
      <th style="padding:8px 12px;text-align:left;font-size:10px;color:#64748b;font-weight:700;text-transform:uppercase;">Candidate Claims</th>
      <th style="padding:8px 12px;text-align:left;font-size:10px;color:#64748b;font-weight:700;text-transform:uppercase;">Ref 1 Verdict</th>
      <th style="padding:8px 12px;text-align:left;font-size:10px;color:#64748b;font-weight:700;text-transform:uppercase;">Ref 1 Correction</th>
    </tr>
  </thead>
  <tbody>
    ${field("Company",cd.currentCompany,"company",rv1)}
    ${field("Designation",cd.designation||check.designation,"designation",rv1)}
    ${field("Date of Joining",cd.doj,"doj",rv1)}
    ${field("Date of Relieving",cd.dor||"Currently Employed","dor",rv1)}
    ${field("Last Salary",cd.lastSalary?"₹"+parseInt(cd.lastSalary).toLocaleString("en-IN")+"/mo":"","lastSalary",rv1)}
    ${field("CTC",cd.ctc?"₹"+parseInt(cd.ctc).toLocaleString("en-IN")+"/yr":"","ctc",rv1)}
    ${field("Notice Period",cd.noticePeriod,"noticePeriod",rv1)}
    ${field("Reason for Leaving",cd.reasonForLeaving,"reasonForLeaving",rv1)}
  </tbody>
</table>

${check.ref2?.email?`
<table style="width:100%;border-collapse:collapse;margin-bottom:16px;">
  <thead>
    <tr style="background:#f1f5f9;">
      <th style="padding:8px 12px;text-align:left;font-size:10px;color:#64748b;font-weight:700;text-transform:uppercase;">Field</th>
      <th style="padding:8px 12px;text-align:left;font-size:10px;color:#64748b;font-weight:700;text-transform:uppercase;">Candidate Claims</th>
      <th style="padding:8px 12px;text-align:left;font-size:10px;color:#64748b;font-weight:700;text-transform:uppercase;">Ref 2 Verdict</th>
      <th style="padding:8px 12px;text-align:left;font-size:10px;color:#64748b;font-weight:700;text-transform:uppercase;">Ref 2 Correction</th>
    </tr>
  </thead>
  <tbody>
    ${field("Company",cd.currentCompany,"company",rv2)}
    ${field("Designation",cd.designation||check.designation,"designation",rv2)}
    ${field("Date of Joining",cd.doj,"doj",rv2)}
    ${field("Date of Relieving",cd.dor||"Currently Employed","dor",rv2)}
    ${field("Last Salary",cd.lastSalary?"₹"+parseInt(cd.lastSalary).toLocaleString("en-IN")+"/mo":"","lastSalary",rv2)}
    ${field("CTC",cd.ctc?"₹"+parseInt(cd.ctc).toLocaleString("en-IN")+"/yr":"","ctc",rv2)}
    ${field("Notice Period",cd.noticePeriod,"noticePeriod",rv2)}
    ${field("Reason for Leaving",cd.reasonForLeaving,"reasonForLeaving",rv2)}
  </tbody>
</table>`:""}

<!-- Reference Ratings -->
<div style="font-size:9px;font-weight:800;letter-spacing:1.5px;text-transform:uppercase;color:#0cc0df;border-bottom:2px solid #0cc0df;padding-bottom:5px;margin:16px 0 12px;">3. Performance Ratings</div>
<div style="display:grid;grid-template-columns:${check.ref2?.email?"1fr 1fr":"1fr"};gap:16px;margin-bottom:16px;">
  ${[{ref:check.ref1,data:r1,num:1},{ref:check.ref2,data:r2,num:2}].filter(x=>x.ref?.email).map(({ref,data,num})=>`
  <div style="background:#f8fafc;border-radius:10px;padding:16px;border:1px solid #e2e8f0;">
    <div style="font-size:13px;font-weight:700;margin-bottom:4px;">Reference ${num}</div>
    <div style="font-size:11px;color:#64748b;margin-bottom:12px;">${ref.name||""}${ref.email?" · "+ref.email:""}${ref.company?" · "+ref.company:""}</div>
    <div style="margin-bottom:8px;"><span style="color:#64748b;font-size:11px;">Behaviour & Attitude:</span><br/><span style="color:#ff914d;font-size:13px;font-weight:700;">${star(data.behaviour)}</span></div>
    <div style="margin-bottom:8px;"><span style="color:#64748b;font-size:11px;">Ethical Conduct:</span><br/><span style="color:#ff914d;font-size:13px;font-weight:700;">${star(data.ethical)}</span></div>
    <div style="margin-bottom:8px;"><span style="color:#64748b;font-size:11px;">Professional Skills:</span><br/><span style="color:#ff914d;font-size:13px;font-weight:700;">${star(data.professional)}</span></div>
    <div style="margin-bottom:8px;"><span style="color:#64748b;font-size:11px;">Would Rehire:</span><br/><span style="font-weight:700;font-size:13px;color:${data.rehire==="Yes"?"#ff3131":data.rehire==="No"?"#ef4444":"#f59e0b"}">${data.rehire||"Not answered"}</span></div>
    ${data.comments?`<div style="margin-top:10px;padding:8px;background:#fff;border-radius:6px;border:1px solid #e2e8f0;font-size:11px;color:#64748b;font-style:italic;">"${data.comments}"</div>`:""}
  </div>`).join("")}
</div>

<!-- Overall Score -->
<div style="font-size:9px;font-weight:800;letter-spacing:1.5px;text-transform:uppercase;color:#0cc0df;border-bottom:2px solid #0cc0df;padding-bottom:5px;margin:16px 0 12px;">4. Overall Score</div>
<div style="display:grid;grid-template-columns:repeat(4,1fr);gap:10px;margin-bottom:20px;">
  ${[["Behaviour Avg",avgB],["Ethical Avg",avgE],["Professional Avg",avgP],["Overall Score",overall]].map(([l,v],i)=>`
  <div style="background:${i===3?"linear-gradient(135deg,#0a0a1a,#1a2040)":"#f8fafc"};border-radius:10px;padding:14px;text-align:center;border:1px solid ${i===3?"transparent":"#e2e8f0"};">
    <div style="font-size:9px;color:${i===3?"rgba(255,255,255,0.6)":"#94a3b8"};font-weight:700;text-transform:uppercase;margin-bottom:4px;">${l}</div>
    <div style="font-size:${i===3?"26px":"22px"};font-weight:900;color:${i===3?"#0cc0df":v>=4?"#ff3131":v>=3?"#0cc0df":"#ef4444"};">${v||"—"}<span style="font-size:13px;font-weight:400;color:${i===3?"rgba(255,255,255,0.4)":"#94a3b8"}">/5</span></div>
  </div>`).join("")}
</div>
</div>

<!-- Footer -->
<div style="background:#0a0a1a;color:#fff;padding:14px 36px;display:flex;justify-content:space-between;align-items:center;margin-top:8px;">
  <img src="${LOGO1}" style="height:24px;" alt="Meitho BGV"/>
  <div style="font-size:10px;color:rgba(255,255,255,0.5);">Report ID: RC-${check.id} · Generated: ${dt} · This report is confidential</div>
  <div style="font-size:10px;color:#0cc0df;font-weight:600;">help@meitho.com</div>
</div>
</div>
</body></html>`;
    const win=window.open("","_blank","width=1000,height=800");
    if(!win){addToast("Please allow popups to download report","error");return;}
    win.document.write(html);
    win.document.close();
    win.focus();
    // Auto trigger print dialog for PDF save
    setTimeout(()=>{
      win.print();
    },800);
  };

  const sendReminder=async(check,type)=>{
    const headers=await getAuthedHeaders();
    const baseUrl="https://meitho.com/refcheck/"+check.id;
    try{
      const sends=[];
      if(type==="candidate"||type==="all"){
        sends.push(fetch(CF_SEND_REMINDER,{method:"POST",headers,body:JSON.stringify({
          toEmail:check.candidateEmail,toName:check.candidateName,
          type:"candidate",candidateName:check.candidateName,
          formLink:baseUrl+"/candidate",
        })}));
      }
      if((type==="ref1"||type==="all")&&check.ref1?.email){
        sends.push(fetch(CF_SEND_REMINDER,{method:"POST",headers,body:JSON.stringify({
          toEmail:check.ref1.email,toName:check.ref1.name,
          type:"reference",candidateName:check.candidateName,
          formLink:baseUrl+"/ref1",
        })}));
      }
      await Promise.all(sends);
      addToast("✅ Reminder sent!","success");
    }catch(e){addToast("Reminder failed: "+e.message,"error");}
  };

  const statusColor={pending:"#ff914d",submitted:"var(--green)",complete:"var(--brand)",failed:"#ff3131"};
  const statusLabel={pending:"⏳ Pending",submitted:"✅ Submitted",complete:"✅ Complete",failed:"❌ Failed"};

  return(
    <div className={dark?"bg-mesh-dark":"bg-mesh-light"} style={{minHeight:"100%",padding:"4px 0"}}>

      {/* New Check Modal */}
      {showNew&&<Modal title="New Reference Check" onClose={()=>setShowNew(false)} width={560}>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:16}}>
          <div style={{gridColumn:"1/-1",fontFamily:FFD,fontSize:13,fontWeight:700,color:"var(--brand)",marginBottom:4}}>📋 Candidate Details</div>
          <div><Label>Full Name *</Label><Inp value={form.candidateName} onChange={e=>setForm(p=>({...p,candidateName:e.target.value}))} placeholder="Rajesh Kumar"/></div>
          <div><Label>Email *</Label><Inp value={form.candidateEmail} onChange={e=>setForm(p=>({...p,candidateEmail:e.target.value}))} placeholder="rajesh@email.com"/></div>
          <div><Label>Phone (for WhatsApp notification)</Label><Inp value={form.candidatePhone} onChange={e=>setForm(p=>({...p,candidatePhone:e.target.value}))} placeholder="+91 XXXXXXXXXX"/></div>
          <div><Label>Designation</Label><Inp value={form.designation} onChange={e=>setForm(p=>({...p,designation:e.target.value}))} placeholder="Software Engineer"/></div>
          <div style={{gridColumn:"1/-1",fontFamily:FFD,fontSize:13,fontWeight:700,color:"var(--brand)",marginTop:8,marginBottom:4}}>👤 Reference 1</div>
          <div><Label>Name</Label><Inp value={form.ref1Name} onChange={e=>setForm(p=>({...p,ref1Name:e.target.value}))} placeholder="Reference name"/></div>
          <div><Label>Email *</Label><Inp value={form.ref1Email} onChange={e=>setForm(p=>({...p,ref1Email:e.target.value}))} placeholder="ref@company.com"/></div>
          <div style={{gridColumn:"1/-1"}}><Label>Company</Label><Inp value={form.ref1Company} onChange={e=>setForm(p=>({...p,ref1Company:e.target.value}))} placeholder="Previous company"/></div>
          <div style={{gridColumn:"1/-1",fontFamily:FFD,fontSize:13,fontWeight:700,color:"var(--brand)",marginTop:8,marginBottom:4}}>👤 Reference 2 (Optional)</div>
          <div><Label>Name</Label><Inp value={form.ref2Name} onChange={e=>setForm(p=>({...p,ref2Name:e.target.value}))} placeholder="Reference name"/></div>
          <div><Label>Email</Label><Inp value={form.ref2Email} onChange={e=>setForm(p=>({...p,ref2Email:e.target.value}))} placeholder="ref@company.com"/></div>
          <div style={{gridColumn:"1/-1"}}><Label>Company</Label><Inp value={form.ref2Company} onChange={e=>setForm(p=>({...p,ref2Company:e.target.value}))} placeholder="Previous company"/></div>
        </div>
        <div style={{background:"var(--brand)08",border:"1px solid var(--brand)20",borderRadius:8,padding:"10px 14px",fontSize:12,color:"var(--muted)",marginBottom:14}}>
          📧 Candidate will receive form link first. References contacted after candidate submits.
          {!isSuperAdmin&&<> Cost: <strong style={{color:"var(--brand)"}}>₹{COST}</strong> · Wallet: <strong style={{color:"var(--green)"}}>₹{(wallet.balance||0).toLocaleString("en-IN")}</strong></>}
        </div>
        <div style={{display:"flex",gap:10}}>
          <Btn onClick={doInitiate}>{loading?<Spin/>:"📧 Send & Initiate →"}</Btn>
          <BtnGhost onClick={()=>setShowNew(false)}>Cancel</BtnGhost>
        </div>
      </Modal>}

      {/* Detail Modal */}
      {detailModal&&<Modal title={"Reference Check — "+detailModal.candidateName} onClose={()=>setDetailModal(null)} width={560}>
        <div style={{display:"flex",flexDirection:"column",gap:12}}>
          {/* Status */}
          <div style={{display:"flex",gap:10}}>
            <Badge color={statusColor[detailModal.overallStatus]||"#ff914d"} style={{fontSize:12}}>{statusLabel[detailModal.overallStatus]||"⏳ Pending"}</Badge>
            <span style={{fontSize:11,color:"var(--muted)"}}>ID: {detailModal.id}</span>
          </div>
          {/* Candidate */}
          <div style={{background:"var(--s2)",borderRadius:10,padding:14}}>
            <div style={{fontSize:10,color:"var(--muted)",fontWeight:700,marginBottom:8,textTransform:"uppercase"}}>Candidate</div>
            <div style={{fontSize:14,fontWeight:700,color:"var(--text)",marginBottom:4}}>{detailModal.candidateName}</div>
            <div style={{fontSize:12,color:"var(--muted)",marginBottom:4}}>{detailModal.designation}</div>
            <div style={{fontSize:12,color:"var(--sub)"}}>{detailModal.candidateEmail}</div>
            <div style={{marginTop:8}}>
              <Badge color={detailModal.candidateStatus==="submitted"?"var(--green)":"#ff914d"}>
                {detailModal.candidateStatus==="submitted"?"✅ Form Submitted":"⏳ Awaiting Response"}
              </Badge>
            </div>
            {detailModal.candidate_data&&<div style={{marginTop:10,padding:"12px",background:"var(--bg)",borderRadius:8}}>
              <div style={{fontSize:11,color:"var(--muted)",fontWeight:700,marginBottom:8}}>SUBMITTED DETAILS</div>
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
                {[
                  ["Full Name",detailModal.candidate_data.fullName],
                  ["DOB",detailModal.candidate_data.dob],
                  ["UAN",detailModal.candidate_data.uan],
                  ["PAN",detailModal.candidate_data.pan],
                  ["Company",detailModal.candidate_data.currentCompany],
                  ["Designation",detailModal.candidate_data.designation],
                  ["Date of Joining",detailModal.candidate_data.doj],
                  ["Date of Relieving",detailModal.candidate_data.dor],
                  ["Last Salary",detailModal.candidate_data.lastSalary?"₹"+parseInt(detailModal.candidate_data.lastSalary).toLocaleString("en-IN")+"/mo":""],
                  ["CTC",detailModal.candidate_data.ctc?"₹"+parseInt(detailModal.candidate_data.ctc).toLocaleString("en-IN")+"/yr":""],
                  ["Notice Period",detailModal.candidate_data.noticePeriod],
                  ["Reason for Leaving",detailModal.candidate_data.reasonForLeaving],
                ].filter(([,v])=>v).map(([l,v])=>(
                  <div key={l} style={{background:"var(--s2)",borderRadius:6,padding:"6px 10px"}}>
                    <div style={{fontSize:9,color:"var(--muted)",fontWeight:700,textTransform:"uppercase"}}>{l}</div>
                    <div style={{fontSize:12,fontWeight:600,color:"var(--text)"}}>{v}</div>
                  </div>
                ))}
              </div>
            </div>}
          </div>
          {/* References */}
          {[{ref:detailModal.ref1,data:detailModal.ref1_data,num:1},{ref:detailModal.ref2,data:detailModal.ref2_data,num:2}].filter(r=>r.ref?.email).map(({ref,data,num})=>(
            <div key={num} style={{background:"var(--s2)",borderRadius:10,padding:14}}>
              <div style={{fontSize:10,color:"var(--muted)",fontWeight:700,marginBottom:8,textTransform:"uppercase"}}>Reference {num}</div>
              <div style={{fontSize:13,fontWeight:600,color:"var(--text)",marginBottom:2}}>{ref.name||"—"}</div>
              <div style={{fontSize:12,color:"var(--sub)",marginBottom:6}}>{ref.email} · {ref.company}</div>
              <Badge color={ref.status==="submitted"?"var(--green)":"#ff914d"}>
                {ref.status==="submitted"?"✅ Submitted":"⏳ Pending"}
              </Badge>
              {data&&<div style={{marginTop:10,padding:"10px",background:"var(--bg)",borderRadius:8}}>
                <div style={{fontSize:11,color:"var(--muted)",fontWeight:700,marginBottom:6}}>VERIFICATION</div>
                {[["Company",data.verifyCompany],["Designation",data.verifyDesignation],["Date of Joining",data.verifyDoj],["Date of Relieving",data.verifyDor],["Salary/CTC",data.verifySalary],["Notice Period",data.verifyNotice],["Reason for Leaving",data.verifyReason]].filter(([,v])=>v).map(([l,v])=>(
                  <div key={l} style={{display:"flex",justifyContent:"space-between",fontSize:12,marginBottom:4}}>
                    <span style={{color:"var(--muted)"}}>{l}</span>
                    <span style={{fontWeight:700,color:v==="True"?"var(--green)":v==="False"?"#ff3131":"#ff914d"}}>{v}</span>
                  </div>
                ))}
                <div style={{height:1,background:"var(--b1)",margin:"8px 0"}}/>
                <div style={{fontSize:11,color:"var(--muted)",fontWeight:700,marginBottom:6}}>RATINGS</div>
                {[["Behaviour",data.behaviour],["Ethical",data.ethical],["Professional",data.professional]].map(([l,v])=>(
                  <div key={l} style={{display:"flex",justifyContent:"space-between",fontSize:12,marginBottom:4}}>
                    <span style={{color:"var(--muted)"}}>{l}</span>
                    <span style={{fontWeight:700,color:"var(--brand)"}}>{"⭐".repeat(v||0)} {v||0}/5</span>
                  </div>
                ))}
                <div style={{fontSize:12,marginTop:4}}><span style={{color:"var(--muted)"}}>Rehire: </span><span style={{fontWeight:700,color:data.rehire==="Yes"?"var(--green)":"#ff3131"}}>{data.rehire||"—"}</span></div>
                {data.comments&&<div style={{fontSize:11,color:"var(--muted)",marginTop:6,fontStyle:"italic"}}>"{data.comments}"</div>}
              </div>}
            </div>
          ))}
          <div style={{display:"flex",gap:8}}>
            <Btn onClick={()=>sendReminder(detailModal,"all")} style={{fontSize:12}}>Send Reminder</Btn>
            <BtnGhost onClick={()=>setDetailModal(null)} style={{fontSize:12}}>Close</BtnGhost>
          </div>
        </div>
      </Modal>}

      {/* Header */}
      <div className="fu" style={{marginBottom:20}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",flexWrap:"wrap",gap:12}}>
          <div>
            <h2 style={{fontFamily:FFD,fontWeight:800,fontSize:26,color:"var(--text)",marginBottom:4}}>Reference Checks</h2>
            <p style={{color:"var(--muted)",fontSize:14}}>{checks.length} checks initiated</p>
          </div>
          <Btn onClick={()=>setShowNew(true)}>+ New Reference Check</Btn>
        </div>
      </div>

      {/* Flow info */}
      <div className="fu1 glass" style={{borderRadius:12,padding:"12px 18px",marginBottom:16,border:"1px solid var(--b1)"}}>
        <div style={{display:"flex",gap:6,flexWrap:"wrap",alignItems:"center"}}>
          {["📧 Candidate Email Sent","→","✅ Candidate Submits Form","→","📧 References Contacted","→","⭐ References Rate","→","📊 HR Gets Report"].map((s,i)=>(
            <span key={i} style={{fontSize:11,color:s==="→"?"var(--muted)":"var(--sub)",fontWeight:s==="→"?400:600}}>{s}</span>
          ))}
        </div>
      </div>

      {/* Checks list */}
      {loadingChecks?<div style={{textAlign:"center",padding:40,color:"var(--muted)"}}>Loading...</div>:
      checks.length===0?(
        <div className="fu2 glass" style={{borderRadius:14,padding:"48px 20px",border:"1px solid var(--b1)",textAlign:"center"}}>
          <div style={{fontSize:40,marginBottom:12}}>📧</div>
          <div style={{fontFamily:FFD,fontSize:16,fontWeight:700,color:"var(--sub)",marginBottom:8}}>No reference checks yet</div>
          <Btn onClick={()=>setShowNew(true)}>+ Start First Reference Check</Btn>
        </div>
      ):(
        <div style={{display:"flex",flexDirection:"column",gap:12}}>
          {checks.map((c,i)=>(
            <div key={i} className="fu glass neon-hover" style={{borderRadius:14,padding:18,border:"1px solid var(--b1)"}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",flexWrap:"wrap",gap:8,marginBottom:12}}>
                <div>
                  <div style={{fontFamily:FFD,fontSize:16,fontWeight:700,color:"var(--text)"}}>{c.candidateName}</div>
                  <div style={{fontSize:12,color:"var(--muted)"}}>{c.designation} · {c.candidateEmail}</div>
                  <div style={{fontSize:11,color:"var(--muted)",marginTop:2}}>{c.id} · {new Date(c.initiatedAt).toLocaleDateString("en-IN")}</div>
                </div>
                <Badge color={statusColor[c.overallStatus]||"#ff914d"}>{statusLabel[c.overallStatus]||"⏳ Pending"}</Badge>
              </div>
              {/* Progress */}
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:8,marginBottom:12}}>
                {[
                  {label:"Candidate",status:c.candidateStatus,email:c.candidateEmail},
                  {label:"Ref 1",status:c.ref1?.status,email:c.ref1?.email},
                  {label:"Ref 2",status:c.ref2?.email?c.ref2?.status:"n/a",email:c.ref2?.email||"Not added"},
                ].map((s,j)=>(
                  <div key={j} style={{background:"var(--s2)",borderRadius:8,padding:"8px 10px",textAlign:"center"}}>
                    <div style={{fontSize:10,color:"var(--muted)",fontWeight:700,marginBottom:4}}>{s.label}</div>
                    <div style={{fontSize:16,marginBottom:2}}>{s.status==="submitted"?"✅":s.status==="n/a"?"➖":"⏳"}</div>
                    <div style={{fontSize:9,color:"var(--muted)",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{s.email}</div>
                  </div>
                ))}
              </div>
              <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                <button onClick={()=>setDetailModal(c)} style={{background:"var(--brand)10",border:"1px solid var(--brand)30",borderRadius:7,color:"var(--brand)",padding:"5px 12px",cursor:"pointer",fontSize:11,fontWeight:700,fontFamily:FF}}>👁 View Details</button>
                <button onClick={()=>sendReminder(c,"all")} style={{background:"var(--green)10",border:"1px solid var(--green)30",borderRadius:7,color:"var(--green)",padding:"5px 12px",cursor:"pointer",fontSize:11,fontWeight:700,fontFamily:FF}}>🔔 Send Reminder</button>
                {/* WhatsApp Remind Candidate */}
                {c.candidatePhone&&c.candidateStatus!=="submitted"&&(
                  <button onClick={()=>sendWhatsAppMessage(c.candidatePhone, getRefCheckCandidateMsg(c.candidateName,"https://meitho.com/refcheck/"+c.id+"/candidate",company?.name))}
                    style={{background:"rgba(37,211,102,0.08)",border:"1px solid rgba(37,211,102,0.25)",borderRadius:7,color:"#25D366",padding:"5px 12px",cursor:"pointer",fontSize:11,fontWeight:700,fontFamily:FF,display:"flex",alignItems:"center",gap:4}}>
                    <svg width="11" height="11" viewBox="0 0 24 24" fill="#25D366"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                    WA Remind Candidate
                  </button>
                )}
                <button onClick={()=>{const url="https://meitho.com/refcheck/"+c.id+"/candidate";navigator.clipboard?.writeText(url);addToast("Candidate link copied!","success");}} style={{background:"rgba(255,145,77,0.1)",border:"1px solid rgba(255,145,77,0.3)",borderRadius:7,color:"#ff914d",padding:"5px 12px",cursor:"pointer",fontSize:11,fontWeight:700,fontFamily:FF}}>📋 Copy Link</button>
                {(c.overallStatus==="complete"||c.overallStatus==="in_progress"||c.candidateStatus==="submitted")&&<button onClick={()=>downloadRefReport(c)} style={{background:"linear-gradient(90deg,var(--green),#059669)",border:"none",borderRadius:7,color:"#fff",padding:"5px 12px",cursor:"pointer",fontSize:11,fontWeight:700,fontFamily:FF}}>↓ Download Report</button>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}



let addToastGlobal = null;

/* ══════════════════════════════════════════════════════════════
   ADDRESS VERIFICATION — HR initiates present/permanent address checks.
   Candidate gets an emailed link to a public page where they take a
   selfie with GPS location captured, which we compare against the
   geocoded address. Report shows the selfie + match/mismatch to HR.
══════════════════════════════════════════════════════════════ */
function AddressVerificationPage({user,company,wallets,addToast}) {
  const isSuperAdmin=user.role==="superadmin";
  const companyId=user?.companyId||company?.id||"";
  const wallet=wallets[companyId]||{balance:0};
  const [checks,setChecks]=useState([]);
  const [loading,setLoading]=useState(false);
  const [loadingChecks,setLoadingChecks]=useState(true);
  const [showNew,setShowNew]=useState(false);
  const [detailModal,setDetailModal]=useState(null);
  const [form,setForm]=useState({
    candidateName:"",candidateEmail:"",candidatePhone:"",
    presentAddress:"",permanentAddress:"",sameAddress:false,
  });
  const COST=getCompanyPrice(company,"address_verify");

  useEffect(()=>{
    let unsub=null;
    if(!db){setLoadingChecks(false);return;}
    try{
      const q=isSuperAdmin
        ?query(collection(db,"address_verifications"))
        :query(collection(db,"address_verifications"),where("companyId","==",companyId));
      unsub=onSnapshot(q,(snap)=>{
        const data=snap.docs.map(d=>({id:d.id,...d.data()}));
        data.sort((a,b)=>new Date(b.initiatedAt||0)-new Date(a.initiatedAt||0));
        setChecks(data);
        setLoadingChecks(false);
      },(e)=>{console.warn("Load address_verifications error:",e.message);setLoadingChecks(false);});
    }catch(e){setLoadingChecks(false);}
    return()=>{if(unsub)unsub();};
  },[companyId,isSuperAdmin]);

  const doInitiate=async()=>{
    if(!form.candidateName||!form.candidateEmail){addToast("Enter candidate name and email","error");return;}
    if(!form.presentAddress){addToast("Enter present address","error");return;}
    if(!form.sameAddress&&!form.permanentAddress){addToast("Enter permanent address, or check 'same as present'","error");return;}
    if(!isSuperAdmin&&wallet.balance<COST){addToast("Insufficient wallet balance. Need ₹"+COST,"error");return;}
    setLoading(true);
    try{
      const checkId="AV-"+Date.now().toString(36).toUpperCase();
      const newCheck={
        id:checkId,companyId,companyName:company?.name||"",
        initiatedBy:user.id,initiatedByName:user.name||user.email,
        candidateName:form.candidateName,candidateEmail:form.candidateEmail,candidatePhone:form.candidatePhone,
        presentAddress:form.presentAddress,
        permanentAddress:form.sameAddress?form.presentAddress:form.permanentAddress,
        sameAddress:form.sameAddress,
        presentVerification:{status:"pending",attempts:0},
        permanentVerification:form.sameAddress?null:{status:"pending",attempts:0},
        status:"pending",
        cost:COST,
        initiatedAt:new Date().toISOString(),
      };
      await setDoc(doc(db,"address_verifications",checkId),newCheck);

      // Deduct wallet
      if(!isSuperAdmin){
        const rc=await walletChargeSrv([{type:"address_verify",qty:1}],"Address Verification — "+form.candidateName,"address_verify");
        if(rc.charged)checkLowWalletAndNotify(companyId,company?.name,rc.before,rc.after);
      }

      // Email candidate
      const verifyLink=`${window.location.origin}/address-verify/${checkId}`;
      await fetch(CF_SEND_CANDIDATE,{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({
        candidateEmail:form.candidateEmail,candidateName:form.candidateName,
        customSubject:`Address Verification Required — ${company?.name||"Meitho"}`,
        customBody:`<p>Dear ${form.candidateName},</p><p>${company?.name||"Your employer"} needs to verify your address as part of your background check.</p><p>Please click below, confirm your address, and take a quick selfie at that location — it only takes a minute.</p><p><a href="${verifyLink}" style="background:#ff3131;color:white;padding:12px 24px;border-radius:8px;text-decoration:none;display:inline-block;margin-top:8px;">Verify My Address →</a></p><p>Best regards,<br/>${company?.name||"Meitho"} Team</p>`,
      })});

      addToast("Address verification initiated — candidate has been emailed.","success");
      setShowNew(false);
      setForm({candidateName:"",candidateEmail:"",candidatePhone:"",presentAddress:"",permanentAddress:"",sameAddress:false});
    }catch(e){addToast("Failed: "+e.message,"error");}
    finally{setLoading(false);}
  };

  const overallStatus=(c)=>{
    const p=c.presentVerification?.status;
    const perm=c.sameAddress?p:c.permanentVerification?.status;
    if(p==="submitted"||perm==="submitted")return "completed";
    return "pending";
  };

  return(
    <div>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:20,flexWrap:"wrap",gap:12}}>
        <div>
          <h2 style={{fontFamily:FFD,fontWeight:800,fontSize:24,color:"var(--text)",marginBottom:4}}>🏠 Address Verification Records</h2>
          <p style={{color:"var(--muted)",fontSize:13}}>Past address verifications and their reports. To start a new one, go to Search → Address Verify.</p>
        </div>
      </div>

      {loadingChecks?(
        <div style={{textAlign:"center",padding:60,color:"var(--muted)"}}>Loading...</div>
      ):checks.length===0?(
        <div className="fu" style={{borderRadius:14,padding:48,textAlign:"center",color:"var(--muted)"}}>
          <div style={{fontSize:32,marginBottom:10}}>📭</div>
          <div style={{fontSize:13}}>No address verifications yet.</div>
        </div>
      ):(
        <div className="fu" style={{borderRadius:14,padding:0,overflow:"hidden"}}>
          <table style={{width:"100%",borderCollapse:"collapse"}}>
            <thead><tr style={{background:"var(--s2)"}}>{["Candidate","Email","Type","Status","Initiated","Action"].map(h=><th key={h} style={{padding:"8px 12px",textAlign:"left",fontSize:10,fontWeight:700,color:"var(--muted)",textTransform:"uppercase"}}>{h}</th>)}</tr></thead>
            <tbody>
              {checks.map((c,i)=>{
                const st=overallStatus(c);
                return(
                  <tr key={i} style={{borderBottom:"1px solid var(--b1)"}}>
                    <td style={{padding:"10px 12px",fontSize:13,fontWeight:600,color:"var(--text)"}}>{c.candidateName}</td>
                    <td style={{padding:"10px 12px",fontSize:12,color:"var(--muted)"}}>{c.candidateEmail}</td>
                    <td style={{padding:"10px 12px",fontSize:11,color:"var(--muted)"}}>{c.sameAddress?"Same address":"Present + Permanent"}</td>
                    <td style={{padding:"10px 12px"}}><Badge color={st==="completed"?"var(--green)":st==="partial"?"#f59e0b":"var(--muted)"} style={{fontSize:9}}>{st==="completed"?"✅ Completed":st==="partial"?"⏳ Partial":"⏳ Pending"}</Badge></td>
                    <td style={{padding:"10px 12px",fontSize:11,color:"var(--muted)"}}>{(c.initiatedAt||"").slice(0,10)}</td>
                    <td style={{padding:"10px 12px"}}>
                      <button onClick={()=>setDetailModal(c)} style={{background:"var(--s2)",border:"1px solid var(--b1)",borderRadius:6,color:"var(--text)",padding:"5px 12px",cursor:"pointer",fontSize:11,fontWeight:600,fontFamily:FF}}>View</button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* New Verification Modal */}
      {showNew&&(
        <Modal title="🏠 New Address Verification" onClose={()=>setShowNew(false)} width={520}>
          <div style={{display:"flex",flexDirection:"column",gap:12}}>
            <div><Label>Candidate Name *</Label><Inp value={form.candidateName} onChange={e=>setForm(p=>({...p,candidateName:e.target.value}))}/></div>
            <div><Label>Candidate Email *</Label><Inp value={form.candidateEmail} onChange={e=>setForm(p=>({...p,candidateEmail:e.target.value}))}/></div>
            <div><Label>Candidate Phone</Label><Inp value={form.candidatePhone} onChange={e=>setForm(p=>({...p,candidatePhone:e.target.value}))}/></div>
            <div><Label>Present Address *</Label><textarea value={form.presentAddress} onChange={e=>setForm(p=>({...p,presentAddress:e.target.value}))} rows={2} style={{width:"100%",padding:"9px 12px",borderRadius:8,border:"1px solid var(--b1)",background:"var(--s2)",color:"var(--text)",fontSize:13,fontFamily:FF,resize:"vertical",outline:"none"}} placeholder="Full address with city, state, pincode"/></div>
            <div onClick={()=>setForm(p=>({...p,sameAddress:!p.sameAddress}))} style={{display:"flex",alignItems:"center",gap:10,cursor:"pointer",padding:"8px 0"}}>
              <div style={{width:18,height:18,borderRadius:5,border:`2px solid ${form.sameAddress?"var(--brand)":"var(--b2)"}`,background:form.sameAddress?"var(--brand)":"transparent",display:"flex",alignItems:"center",justifyContent:"center"}}>
                {form.sameAddress&&<svg width="10" height="10" viewBox="0 0 10 10" fill="none"><path d="M1.5 5l2.5 2.5L8.5 3" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>}
              </div>
              <span style={{fontSize:13,color:"var(--text)"}}>Permanent address is the same as present</span>
            </div>
            {!form.sameAddress&&(
              <div><Label>Permanent Address *</Label><textarea value={form.permanentAddress} onChange={e=>setForm(p=>({...p,permanentAddress:e.target.value}))} rows={2} style={{width:"100%",padding:"9px 12px",borderRadius:8,border:"1px solid var(--b1)",background:"var(--s2)",color:"var(--text)",fontSize:13,fontFamily:FF,resize:"vertical",outline:"none"}} placeholder="Full address with city, state, pincode"/></div>
            )}
            <div style={{background:"var(--s2)",borderRadius:8,padding:"10px 14px",display:"flex",justifyContent:"space-between",alignItems:"center",fontSize:13}}>
              <span style={{color:"var(--muted)"}}>Cost ({form.sameAddress?"1 address":"2 addresses"})</span>
              {isSuperAdmin?<Badge color="var(--brand)" style={{fontSize:10}}>👑 Unlimited</Badge>:<span style={{fontWeight:700,color:"var(--brand)"}}>₹{COST}</span>}
            </div>
            <div style={{display:"flex",gap:10,marginTop:6}}>
              <Btn onClick={doInitiate} disabled={loading}>{loading?"Sending...":"📧 Send to Candidate"}</Btn>
              <BtnGhost onClick={()=>setShowNew(false)}>Cancel</BtnGhost>
            </div>
          </div>
        </Modal>
      )}

      {/* Detail Modal */}
      {detailModal&&(
        <Modal title={"📍 "+detailModal.candidateName} onClose={()=>setDetailModal(null)} width={600}>
          {[
            {label:"Present Address",addr:detailModal.presentAddress,v:detailModal.presentVerification,field:"presentVerification"},
            ...(detailModal.sameAddress?[]:[{label:"Permanent Address",addr:detailModal.permanentAddress,v:detailModal.permanentVerification,field:"permanentVerification"}]),
          ].map((section,i)=>(
            <div key={i} style={{marginBottom:20,paddingBottom:20,borderBottom:i===0&&!detailModal.sameAddress?"1px solid var(--b1)":"none"}}>
              <div style={{fontSize:12,fontWeight:700,color:"var(--muted)",textTransform:"uppercase",marginBottom:6}}>{section.label}</div>
              <div style={{fontSize:13,color:"var(--text)",marginBottom:12}}>{section.addr}</div>
              {section.v?.status==="submitted"?(
                <div style={{display:"flex",gap:14,alignItems:"flex-start",flexWrap:"wrap"}}>
                  {section.v.selfieUrl&&<img src={section.v.selfieUrl} alt="Selfie" style={{width:100,height:100,borderRadius:10,objectFit:"cover",border:"1px solid var(--b1)"}}/>}
                  <div style={{flex:1,minWidth:220}}>
                    <Badge color={section.v.matched?"var(--green)":"#ef4444"} style={{fontSize:10,marginBottom:6}}>{section.v.matched?((section.v.pincodeMatched&&!section.v.manualOverride)?"✅ Matched (by PIN code)":"✅ Location Matched"):"⚠ Location Mismatch"}</Badge>
                    {section.v.addressPincode&&(isSuperAdmin||!section.v.manualOverride)&&(
                      <div style={{fontSize:11,color:"var(--muted)",marginBottom:2}}>PIN: {section.v.addressPincode} (address) vs {section.v.capturedPincode||"—"} (candidate's location)</div>
                    )}
                    {section.v.geocodeFailed&&!section.v.matched&&(
                      <div style={{fontSize:11,color:"#f59e0b",background:"rgba(245,158,11,0.08)",border:"1px solid rgba(245,158,11,0.2)",borderRadius:6,padding:"5px 8px",marginTop:4,marginBottom:6}}>
                        ⚠ Could not look up this address automatically — mismatch may be inaccurate. Please verify manually below.
                      </div>
                    )}
                    {(isSuperAdmin||!section.v.manualOverride)&&<div style={{fontSize:12,color:"var(--muted)",marginTop:4}}>Distance: {section.v.distanceMeters!=null?Math.round(section.v.distanceMeters)+"m":"—"}</div>}
                    <div style={{fontSize:12,color:"var(--muted)"}}>
                      Candidate's location: {section.v.capturedLat?.toFixed(5)}, {section.v.capturedLng?.toFixed(5)}
                      {section.v.capturedLat&&<a href={`https://www.google.com/maps?q=${section.v.capturedLat},${section.v.capturedLng}`} target="_blank" rel="noopener noreferrer" style={{marginLeft:6,color:"var(--brand)",textDecoration:"none"}}>View →</a>}
                    </div>
                    <div style={{fontSize:12,color:"var(--muted)"}}>
                      Address location: {section.v.targetLat?section.v.targetLat.toFixed(5)+", "+section.v.targetLng.toFixed(5):"Not found"}
                      {section.v.targetLat&&<a href={`https://www.google.com/maps?q=${section.v.targetLat},${section.v.targetLng}`} target="_blank" rel="noopener noreferrer" style={{marginLeft:6,color:"var(--brand)",textDecoration:"none"}}>View →</a>}
                    </div>
                    <div style={{fontSize:12,color:"var(--muted)"}}>Attempts: {section.v.attempts||1}</div>
                    <div style={{fontSize:11,color:"var(--muted)",marginTop:4}}>{(section.v.submittedAt||"").slice(0,16).replace("T"," ")}</div>
                    {!section.v.matched&&isSuperAdmin&&(
                      <button onClick={async()=>{
                        if(!window.confirm("Mark this as manually verified/matched? Use this if you've confirmed the location is correct despite the automatic mismatch."))return;
                        try{
                          await updateDoc(doc(db,"address_verifications",detailModal.id),{
                            [`${section.field}.matched`]:true,
                            [`${section.field}.manualOverride`]:true,
                            [`${section.field}.overrideBy`]:user?.name||user?.email||"",
                            [`${section.field}.overrideAt`]:new Date().toISOString(),
                          });
                          addToast("Marked as manually verified","success");
                          setDetailModal(p=>({...p,[section.field]:{...p[section.field],matched:true,manualOverride:true}}));
                        }catch(e){addToast("Failed: "+e.message,"error");}
                      }} style={{marginTop:10,background:"rgba(16,185,129,0.1)",border:"1px solid rgba(16,185,129,0.25)",borderRadius:7,color:"var(--green)",padding:"5px 12px",cursor:"pointer",fontSize:11,fontWeight:700,fontFamily:FF}}>
                        ✓ Mark as Manually Verified
                      </button>
                    )}
                    {section.v.manualOverride&&isSuperAdmin&&<div style={{fontSize:11,color:"var(--green)",marginTop:6}}>✓ Manually verified by {section.v.overrideBy}</div>}
                  </div>
                </div>
              ):(
                <Badge color="var(--muted)" style={{fontSize:10}}>⏳ Awaiting candidate submission</Badge>
              )}
            </div>
          ))}
        </Modal>
      )}
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════
   ROOT APP — Firebase Auth + Firestore
══════════════════════════════════════════════════════════════ */
/* ══════════════════════════════════════════════════════════════
   ADMIN OVERVIEW
══════════════════════════════════════════════════════════════ */
function AdminOverview({employees,users,companies,history,setPage}) {
  const {dark}=useTheme();
  const activeCompanies=companies.filter(c=>c.status==="Active").length;
  const pendingCompanies=companies.filter(c=>c.status==="Pending").length;
  const todaySearches=history.filter(h=>(h.timestamp||"").startsWith(todayStr())).length;
  const avgMatch=history.length?Math.round(history.filter(h=>h.found).length/history.length*100):0;
  const recentActivity=[...history].slice(0,8);
  return(
    <div className={dark?"bg-mesh-dark":"bg-mesh-light"} style={{minHeight:"100%",padding:"4px 0"}}>
      <div className="fu" style={{marginBottom:24}}>
        <h2 style={{fontFamily:FFD,fontWeight:800,fontSize:28,color:"var(--text)",marginBottom:4}}>Platform <span style={{background:"linear-gradient(90deg,#ff3131,#ff914d)",WebkitBackgroundClip:"text",WebkitTextFillColor:"transparent"}}>Overview</span></h2>
        <p style={{color:"var(--muted)",fontSize:14}}>{new Date().toLocaleDateString("en-IN",{weekday:"long",year:"numeric",month:"long",day:"numeric"})}</p>
      </div>
      <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:14,marginBottom:20}} className="mg1">
        {[
          {label:"Total Companies",value:activeCompanies,sub:pendingCompanies>0?pendingCompanies+" pending":"All active",color:"var(--brand)"},
          {label:"Employee Records",value:employees.length,sub:"In Meitho DB",color:"var(--green)"},
          {label:"Today's Searches",value:todaySearches,sub:history.length+" total",color:"#8b5cf6"},
          {label:"Match Rate",value:avgMatch+"%",sub:"Platform average",color:"#ff914d"},
        ].map((s,i)=>(
          <div key={s.label} className={"fu"+i+" glass neon-hover"} style={{borderRadius:14,padding:20,border:"1px solid var(--b1)"}}>
            <div style={{fontFamily:FFD,fontSize:32,fontWeight:800,color:s.color,marginBottom:4}}>{s.value}</div>
            <div style={{fontSize:12,color:"var(--text)",fontWeight:600,marginBottom:2}}>{s.label}</div>
            <div style={{fontSize:11,color:"var(--muted)"}}>{s.sub}</div>
          </div>
        ))}
      </div>
      {pendingCompanies>0&&(
        <div style={{background:"rgba(245,158,11,0.08)",border:"1px solid rgba(245,158,11,0.3)",borderRadius:12,padding:"14px 18px",marginBottom:18,display:"flex",justifyContent:"space-between",alignItems:"center",flexWrap:"wrap",gap:10}}>
          <div style={{fontSize:13,fontWeight:700,color:"#f59e0b"}}>⏳ {pendingCompanies} company registration{pendingCompanies!==1?"s":""} pending approval</div>
          <Btn onClick={()=>setPage("approvals")} style={{padding:"8px 16px",fontSize:13,background:"#f59e0b"}}>Review Now →</Btn>
        </div>
      )}
      <div style={{display:"grid",gridTemplateColumns:"1.5fr 1fr",gap:16}} className="mg1">
        <div className="fu1 glass neon-hover" style={{borderRadius:14,padding:20,border:"1px solid var(--b1)"}}>
          <div style={{fontFamily:FFD,fontSize:13,fontWeight:700,color:"var(--sub)",marginBottom:16}}>RECENT ACTIVITY</div>
          {recentActivity.length===0&&<div style={{textAlign:"center",padding:"24px 0",color:"var(--muted)",fontSize:13}}>No activity yet</div>}
          {recentActivity.map((a,i)=>(
            <div key={i} style={{display:"flex",gap:10,alignItems:"center",padding:"9px 0",borderBottom:i<recentActivity.length-1?"1px solid var(--b1)":"none"}}>
              <span style={{fontSize:18}}>{a.searchType==="uan"?"🏦":a.searchType==="pan"?"📋":"🪪"}</span>
              <div style={{flex:1}}>
                <div style={{fontSize:12,fontWeight:600,color:"var(--text)"}}>{a.companyName||"Unknown"}</div>
                <div style={{fontSize:11,color:"var(--muted)",fontFamily:FFM}}>{(a.searchType||"").toUpperCase()} · {a.searchValue||"—"}</div>
              </div>
              <Badge color={a.found?"var(--green)":"var(--red)"} style={{fontSize:9}}>{a.found?"Found":"Not Found"}</Badge>
            </div>
          ))}
        </div>
        <div style={{display:"flex",flexDirection:"column",gap:12}}>
          <div className="fu2 glass neon-hover" style={{borderRadius:14,padding:18,border:"1px solid var(--b1)"}}>
            <div style={{fontFamily:FFD,fontSize:12,fontWeight:700,color:"var(--sub)",marginBottom:14}}>QUICK ACTIONS</div>
            {[
              {icon:"🔍",label:"Search Employee",page:"search"},
              {icon:"🏢",label:"Manage Companies",page:"companies"},
              {icon:"✅",label:"Approvals",page:"approvals"},
              {icon:"💰",label:"Wallet Management",page:"walletadmin"},
              {icon:"📊",label:"Analytics",page:"analytics"},
            ].map(a=>(
              <button key={a.page} onClick={()=>setPage(a.page)} style={{width:"100%",display:"flex",alignItems:"center",gap:10,padding:"9px 10px",background:"transparent",border:"none",borderRadius:8,cursor:"pointer",marginBottom:3,fontFamily:FF,textAlign:"left"}} onMouseEnter={e=>e.currentTarget.style.background="var(--s2)"} onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
                <span style={{fontSize:16}}>{a.icon}</span>
                <span style={{fontSize:13,fontWeight:500,color:"var(--sub)"}}>{a.label}</span>
                <span style={{marginLeft:"auto",color:"var(--muted)",fontSize:12}}>→</span>
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════
   COMPANIES ADMIN PAGE
══════════════════════════════════════════════════════════════ */
function CompanyDetailView({company:c,history:searchHistory,users,wallets,transactions,companies,addToast,onBack,setCompanies}) {
  const {dark}=useTheme();
  const history=searchHistory||[];
  const [tab,setTab]=useState("details");
  const [editMode,setEditMode]=useState(false);
  const [editData,setEditData]=useState({
    name:c.name||"",
    email:c.email||"",
    phone:c.phone||c.contactPhone||"",
    contactPerson:c.contactPerson||c.pocName||"",
    industry:c.industry||c.profile?.industry||"",
    address:c.address||c.profile?.address||"",
    gst:c.gst||c.profile?.gst||"",
    cin:c.cin||c.profile?.cin||"",
    pan:c.pan||c.profile?.pan||"",
    website:c.website||c.profile?.website||"",
    registeredOn:c.registeredOn||c.createdAt?.slice(0,10)||"",
    slug:c.slug||"",
  });
  const [pricingEditMode,setPricingEditMode]=useState(false);
  const [pricingData,setPricingData]=useState({
    uan:c.pricing?.uan??"",pan:c.pricing?.pan??"",aadhaar:c.pricing?.aadhaar??"",
    dl:c.pricing?.dl??"",ecourt:c.pricing?.ecourt??"",refcheck:c.pricing?.refcheck??"",
    education:c.pricing?.education??"",full_bgv:c.pricing?.full_bgv??"",address_verify:c.pricing?.address_verify??"",
  });
  const [walletData,setWalletData]=useState((wallets||{})[c.id]||{balance:0,totalLoaded:0});
  const [roleOverrides,setRoleOverrides]=useState({}); // {uid: {role, companyAdmin}} — local reflection until page refetches
  const [roleChangeLoading,setRoleChangeLoading]=useState(null);

  const toggleCompanyAdminRole=async(u)=>{
    const isCurrentlyAdmin=(roleOverrides[u.id]?.role??u.role)==="admin";
    const newRole=isCurrentlyAdmin?"hr":"admin";
    if(!window.confirm(isCurrentlyAdmin
      ? `Remove Company Admin access from ${u.name||u.email}? They'll become a regular HR staff member.`
      : `Make ${u.name||u.email} a Company Admin? They'll get full admin access to this company's account.`
    ))return;
    setRoleChangeLoading(u.id);
    try{
      await saveUserProfile(u.id,{role:newRole,companyAdmin:newRole==="admin"});
      setRoleOverrides(p=>({...p,[u.id]:{role:newRole,companyAdmin:newRole==="admin"}}));
      addToast(`${u.name||u.email} is now ${newRole==="admin"?"a Company Admin":"an HR Staff member"}`,"success");
    }catch(e){addToast("Failed to update role: "+e.message,"error");}
    finally{setRoleChangeLoading(null);}
  };

  // Per-module search + report access grant popup
  const [permModal,setPermModal]=useState(null); // the user object being edited, or null
  const [permDraft,setPermDraft]=useState({});
  const [permSaving,setPermSaving]=useState(false);

  const openPermModal=(u)=>{
    setPermModal(u);
    setPermDraft({
      uan:u.searchPermissions?.uan===true,
      pan:u.searchPermissions?.pan===true,
      aadhaar:u.searchPermissions?.aadhaar===true,
      dl:u.searchPermissions?.dl===true,
      ecourt:u.searchPermissions?.ecourt===true,
      reports:u.searchPermissions?.reports===true,
    });
  };

  const savePermissions=async()=>{
    if(!permModal)return;
    setPermSaving(true);
    try{
      await saveUserProfile(permModal.id,{searchPermissions:permDraft});
      setRoleOverrides(p=>({...p,[permModal.id]:{...p[permModal.id],searchPermissions:permDraft}}));
      addToast(`Permissions updated for ${permModal.name||permModal.email}`,"success");
      setPermModal(null);
    }catch(e){addToast("Failed to save permissions: "+e.message,"error");}
    finally{setPermSaving(false);}
  };

  const [compTxns,setCompTxns]=useState((transactions||[]).filter(t=>t.companyId===c.id));
  const [addBalModal,setAddBalModal]=useState(false);
  const [addAmt,setAddAmt]=useState("");
  const [addNote,setAddNote]=useState("");
  const [addMode,setAddMode]=useState("UPI");
  const [addRef,setAddRef]=useState("");
  const [addDate,setAddDate]=useState(new Date().toISOString().slice(0,10));
  const [companyData,setCompanyData]=useState(c);

  const cHistory=history.filter(h=>h.companyId===c.id);
  const cUsers=users.filter(u=>u.companyId===c.id||u.company===c.name);
  const totalSearches=cHistory.length;
  const totalSpent=cHistory.reduce((s,h)=>s+(h.cost||0),0);
  const foundRate=totalSearches>0?Math.round((cHistory.filter(h=>h.found).length/totalSearches)*100):0;

  useEffect(()=>{
    // Load wallet if not in props
    if(!walletData.balance){
      getAllWallets().then(all=>{
        const w=all.find(x=>x.id===c.id);
        if(w)setWalletData(w);
      }).catch(()=>{});
    }
    if(compTxns.length===0){
      getAllTransactions().then(all=>{
        setCompTxns(all.filter(t=>t.companyId===c.id));
      }).catch(()=>{});
    }
  },[c.id]);

  const saveEdit=async()=>{
    try{
      const newSlug=(editData.slug||"").trim();
      if(newSlug&&newSlug!==(c.slug||"")){
        const dupe=await getDocs(query(collection(db,"companies"),where("slug","==",newSlug)));
        const takenByOther=dupe.docs.some(d=>d.id!==c.id);
        if(takenByOther){addToast("That career page URL is already taken by another company — choose a different one.","error");return;}
      }
      await updateCompany(c.id,editData);
      setCompanies(p=>p.map(x=>x.id===c.id?{...x,...editData}:x));
      setCompanyData(p=>({...p,...editData}));
      setEditMode(false);
      addToast("Company updated!","success");
    }catch(e){addToast("Failed","error");}
  };

  const savePricing=async()=>{
    try{
      // Only store keys that have a real override; blank = fall back to platform default
      const cleanPricing={};
      Object.entries(pricingData).forEach(([k,v])=>{
        if(v!==""&&v!==null&&v!==undefined&&!isNaN(Number(v)))cleanPricing[k]=Number(v);
      });
      await updateCompany(c.id,{pricing:cleanPricing});
      setCompanies(p=>p.map(x=>x.id===c.id?{...x,pricing:cleanPricing}:x));
      setCompanyData(p=>({...p,pricing:cleanPricing}));
      setPricingEditMode(false);
      addToast("Custom pricing saved for "+companyData.name,"success");
    }catch(e){addToast("Failed to save pricing","error");}
  };

  const resetPricing=async()=>{
    if(!window.confirm("Reset "+companyData.name+" to default platform pricing?"))return;
    try{
      await updateCompany(c.id,{pricing:{}});
      setCompanies(p=>p.map(x=>x.id===c.id?{...x,pricing:{}}:x));
      setCompanyData(p=>({...p,pricing:{}}));
      setPricingData({uan:"",pan:"",aadhaar:"",dl:"",ecourt:"",refcheck:"",education:"",full_bgv:"",address_verify:""});
      addToast("Pricing reset to default","success");
    }catch(e){addToast("Failed","error");}
  };

  const toggle=async()=>{
    const ns=companyData.status==="Active"?"Inactive":"Active";
    try{
      await updateCompany(c.id,{status:ns});
      setCompanies(p=>p.map(x=>x.id===c.id?{...x,status:ns}:x));
      setCompanyData(p=>({...p,status:ns}));
      addToast(companyData.name+" → "+ns,"success");
    }catch(e){addToast("Failed","error");}
  };

  const grantBadge=async(type)=>{
    try{
      await updateCompany(c.id,{verified:true,verifiedType:type,verifiedAt:new Date().toISOString()});
      setCompanies(p=>p.map(x=>x.id===c.id?{...x,verified:true,verifiedType:type}:x));
      setCompanyData(p=>({...p,verified:true,verifiedType:type}));
      addToast("Badge granted!","success");
    }catch(e){addToast("Failed","error");}
  };

  const removeBadge=async()=>{
    try{
      await updateCompany(c.id,{verified:false,verifiedType:null});
      setCompanies(p=>p.map(x=>x.id===c.id?{...x,verified:false,verifiedType:null}:x));
      setCompanyData(p=>({...p,verified:false,verifiedType:null}));
      addToast("Badge removed","success");
    }catch(e){addToast("Failed","error");}
  };

  const doAddBalance=async()=>{
    const amt=parseInt(addAmt)||0;
    if(amt<=0){addToast("Enter valid amount","error");return;}
    if(!addRef.trim()){addToast("Enter payment reference","error");return;}
    if(!addNote.trim()){addToast("Enter note","error");return;}
    try{
      const note=`${addNote} | Mode: ${addMode} | Ref: ${addRef} | Date: ${addDate}`;
      const gstAmt=gstOf(amt);
      const newBal=await manualWalletAdjust(c.id,amt,note,"Super Admin",{gstPct:GST_PCT,gstAmount:gstAmt,totalPaid:Math.round((amt+gstAmt)*100)/100,payMode:addMode,payRef:addRef.trim(),payDate:addDate,status:"success"});
      setWalletData(p=>({...p,balance:newBal,totalLoaded:(p?.totalLoaded||0)+amt}));
      getCompanyTransactions(c.id).then(setCompTxns).catch(()=>{});
      addToast("₹"+amt.toLocaleString("en-IN")+" added!","success");
      // Email company
      try{await fetch(CF_SEND_CANDIDATE,{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({to:c.email,subject:"Meitho Wallet Recharged — ₹"+amt.toLocaleString("en-IN"),html:`<p>Hi ${c.name},</p><p>Your wallet has been recharged with <strong>₹${amt.toLocaleString("en-IN")}</strong>. New balance: ₹${newBal.toLocaleString("en-IN")}.</p><p>Mode: ${addMode} | Ref: ${addRef}</p>`,name:c.name})});}catch(e){}
      setAddBalModal(false);setAddAmt("");setAddNote("");setAddRef("");setAddMode("UPI");
    }catch(e){addToast("Failed: "+e.message,"error");}
  };

  const trendData=Object.entries(cHistory.reduce((acc,h)=>{
    const d=new Date(h.timestamp||"");
    const key=d.toLocaleString("en-IN",{month:"short",year:"2-digit"});
    if(!acc[key])acc[key]={searches:0,cost:0};
    acc[key].searches++;acc[key].cost+=h.cost||0;
    return acc;
  },{})).slice(-6).map(([m,d])=>({month:m,...d}));

  const byType=Object.entries(cHistory.reduce((acc,h)=>{
    const t=h.searchType||"other";
    if(!acc[t])acc[t]={count:0,cost:0};
    acc[t].count++;acc[t].cost+=h.cost||0;
    return acc;
  },{})).map(([type,d])=>({type,label:{uan:"UAN",pan:"PAN",aadhaar:"Aadhaar",dl:"DL",ecourt:"Criminal",full_bgv:"Full BGV",refcheck:"Ref Check"}[type]||type,...d}));

  const TABS=[{id:"details",label:"📋 Details"},{id:"pricing",label:"💵 Pricing"},{id:"wallet",label:"💰 Wallet"},{id:"history",label:"🔍 Searches"},{id:"users",label:"👥 HR Users"},{id:"badge",label:"✅ Badge"},{id:"trends",label:"📈 Trends"}];

  return(
    <div>
      {/* Back + Actions */}
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16,flexWrap:"wrap",gap:10}}>
        <button onClick={onBack} style={{background:"var(--s2)",border:"1px solid var(--b1)",borderRadius:8,color:"var(--text)",padding:"7px 14px",cursor:"pointer",fontSize:12,fontWeight:600,fontFamily:FF}}>← All Companies</button>
        <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
          <button onClick={toggle} style={{background:companyData.status==="Active"?"rgba(255,49,49,0.08)":"rgba(16,185,129,0.08)",border:"1px solid "+(companyData.status==="Active"?"rgba(255,49,49,0.2)":"rgba(16,185,129,0.2)"),borderRadius:8,color:companyData.status==="Active"?"#ff3131":"var(--green)",padding:"7px 14px",cursor:"pointer",fontSize:12,fontWeight:700,fontFamily:FF}}>{companyData.status==="Active"?"⏸ Deactivate":"▶ Activate"}</button>
          {c.phone&&<a href={"https://wa.me/91"+c.phone.replace(/\D/g,"")} target="_blank" rel="noopener noreferrer" style={{background:"rgba(37,211,102,0.1)",border:"1px solid rgba(37,211,102,0.25)",borderRadius:8,color:"#25D366",padding:"7px 14px",fontSize:12,fontWeight:700,textDecoration:"none"}}>💬 WhatsApp</a>}
          <a href={"mailto:"+c.email} style={{background:"rgba(255,49,49,0.08)",border:"1px solid rgba(255,49,49,0.2)",borderRadius:8,color:"#ff3131",padding:"7px 14px",fontSize:12,fontWeight:700,textDecoration:"none"}}>📧 Email</a>
        </div>
      </div>

      {/* Header */}
      <div className="fu" style={{marginBottom:16,padding:"20px 24px",borderRadius:14}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",flexWrap:"wrap",gap:12,marginBottom:14}}>
          <div style={{display:"flex",gap:14,alignItems:"center"}}>
            <div style={{width:48,height:48,borderRadius:12,background:"linear-gradient(135deg,#ff3131,#ff914d)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:20,color:"#fff",fontWeight:900,flexShrink:0}}>{(c.name||"?")[0].toUpperCase()}</div>
            <div>
              <div style={{fontFamily:FFD,fontSize:20,fontWeight:800,color:"var(--text)"}}>{companyData.name||c.name}
                {companyData.verified&&<span style={{marginLeft:8,fontSize:10,background:"rgba(16,185,129,0.15)",color:"#10b981",padding:"3px 8px",borderRadius:99,fontWeight:700}}>{companyData.verifiedType||"✅ Verified"}</span>}
              </div>
              <div style={{fontSize:12,color:"var(--muted)",marginTop:2}}>{c.email} {c.phone&&"· "+c.phone} {c.industry&&"· "+c.industry}</div>
            </div>
          </div>
          <Badge color={companyData.status==="Active"?"var(--green)":companyData.status==="Pending"?"#f59e0b":"#ff3131"}>{companyData.status||"Pending"}</Badge>
        </div>
        <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:10}} className="mg2">
          {[["🔍","Searches",totalSearches,"var(--brand)"],["💰","Spent","₹"+totalSpent.toLocaleString("en-IN"),"#ff914d"],["✅","Found Rate",foundRate+"%","var(--green)"],["💳","Wallet","₹"+(walletData?.balance||0).toLocaleString("en-IN"),(walletData?.balance||0)<500?"#ff3131":"var(--green)"]].map(([icon,label,val,color],i)=>(
            <div key={i} style={{background:"var(--s2)",borderRadius:10,padding:"10px 12px",border:"1px solid var(--b1)"}}>
              <div style={{fontSize:16,marginBottom:3}}>{icon}</div>
              <div style={{fontFamily:FFD,fontSize:16,fontWeight:800,color,marginBottom:1}}>{val}</div>
              <div style={{fontSize:10,color:"var(--muted)",fontWeight:600}}>{label}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Tabs */}
      <div style={{display:"flex",gap:4,marginBottom:14,background:"var(--s2)",borderRadius:10,padding:4,flexWrap:"wrap"}}>
        {TABS.map(t=>(
          <button key={t.id} onClick={()=>setTab(t.id)} style={{padding:"7px 14px",borderRadius:8,border:"none",background:tab===t.id?"var(--bg)":"transparent",color:tab===t.id?"var(--text)":"var(--muted)",cursor:"pointer",fontSize:12,fontWeight:tab===t.id?700:500,fontFamily:FF,transition:"all .15s"}}>{t.label}</button>
        ))}
      </div>

      {/* TAB: DETAILS */}
      {tab==="details"&&(
        <div className="fu" style={{borderRadius:14,padding:"20px 24px"}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16}}>
            <div style={{fontFamily:FFD,fontSize:14,fontWeight:700,color:"var(--text)"}}>Company Details</div>
            <button onClick={()=>editMode?saveEdit():setEditMode(true)} style={{background:editMode?"linear-gradient(90deg,#ff3131,#ff914d)":"var(--s2)",border:"1px solid var(--b1)",borderRadius:8,color:editMode?"#fff":"var(--text)",padding:"6px 14px",cursor:"pointer",fontSize:12,fontWeight:700,fontFamily:FF}}>{editMode?"💾 Save":"✏️ Edit"}</button>
          </div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:14}} className="mg1">
            {[["Company Name","name"],["Email","email"],["Phone","phone"],["Contact Person","contactPerson"],["Industry","industry"],["Address","address"],["GST Number","gst"],["CIN","cin"],["PAN","pan"],["Website","website"],["Registered On","registeredOn"]].map(([label,key])=>{
              // Check both top-level and profile nested fields
              const val=editMode?editData[key]:(c[key]||c.profile?.[key]||"");
              return(
                <div key={key}>
                  <Label>{label}</Label>
                  {editMode
                    ?<Inp value={editData[key]||""} onChange={e=>setEditData(p=>({...p,[key]:e.target.value}))}/>
                    :<div style={{fontSize:13,color:val?"var(--text)":"var(--muted)",padding:"8px 12px",background:"var(--s2)",borderRadius:8,border:"1px solid var(--b1)",minHeight:36}}>{val||"—"}</div>
                  }
                </div>
              );
            })}
          </div>

          {/* Career page slug — separate row since it needs a live URL preview */}
          <div style={{marginTop:14,paddingTop:14,borderTop:"1px solid var(--b1)"}}>
            <Label>Career Page URL <span style={{color:"var(--muted)",fontWeight:400}}>(candidates apply here)</span></Label>
            {editMode?(
              <div style={{display:"flex",alignItems:"center",gap:6}}>
                <span style={{fontSize:13,color:"var(--muted)",whiteSpace:"nowrap"}}>meitho.com/ats/</span>
                <Inp value={editData.slug||""} onChange={e=>setEditData(p=>({...p,slug:e.target.value.toLowerCase().replace(/[^a-z0-9-]/g,"-").replace(/-+/g,"-")}))} placeholder="your-company-name"/>
              </div>
            ):(
              c.slug
                ?<a href={`/ats/${c.slug}`} target="_blank" rel="noopener noreferrer" style={{fontSize:13,color:"#0cc0df",padding:"8px 12px",background:"var(--s2)",borderRadius:8,border:"1px solid var(--b1)",minHeight:36,display:"block",textDecoration:"none"}}>meitho.com/ats/{c.slug} ↗</a>
                :<div style={{fontSize:12,color:"#f59e0b",padding:"8px 12px",background:"rgba(245,158,11,0.08)",borderRadius:8,border:"1px solid rgba(245,158,11,0.25)"}}>⚠ Not set — this company's careers page won't work until a URL slug is set here.</div>
            )}
          </div>
          {editMode&&<div style={{marginTop:14,display:"flex",gap:10}}><Btn onClick={saveEdit}>💾 Save Changes</Btn><BtnGhost onClick={()=>setEditMode(false)}>Cancel</BtnGhost></div>}
        </div>
      )}

      {/* TAB: PRICING */}
      {tab==="pricing"&&(
        <div className="fu" style={{borderRadius:14,padding:"20px 24px"}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:6,flexWrap:"wrap",gap:8}}>
            <div>
              <div style={{fontFamily:FFD,fontSize:14,fontWeight:700,color:"var(--text)"}}>Custom Pricing</div>
              <div style={{fontSize:12,color:"var(--muted)",marginTop:2}}>Leave a field blank to use the platform default price for that check.</div>
            </div>
            <div style={{display:"flex",gap:8}}>
              {!pricingEditMode
                ?<Btn onClick={()=>setPricingEditMode(true)}>✏️ Edit Pricing</Btn>
                :<><Btn onClick={savePricing}>💾 Save Pricing</Btn><BtnGhost onClick={()=>setPricingEditMode(false)}>Cancel</BtnGhost></>
              }
              <BtnGhost onClick={resetPricing}>↺ Reset to Default</BtnGhost>
            </div>
          </div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:14,marginTop:16}} className="mg1">
            {[["UAN / Employment","uan"],["PAN Verification","pan"],["Aadhaar Check","aadhaar"],["Driving License","dl"],["Criminal / eCourt","ecourt"],["Reference Check","refcheck"],["Education Verification","education"],["Address Verification","address_verify"]].map(([label,key])=>(
              <div key={key}>
                <Label>{label} <span style={{color:"var(--muted)",fontWeight:400}}>(default ₹{SEARCH_PRICES[key]||0})</span></Label>
                {pricingEditMode
                  ?<Inp type="number" placeholder={"₹"+(SEARCH_PRICES[key]||0)} value={pricingData[key]} onChange={e=>setPricingData(p=>({...p,[key]:e.target.value}))}/>
                  :<div style={{fontSize:13,color:"var(--text)",padding:"8px 12px",background:"var(--s2)",borderRadius:8,border:"1px solid var(--b1)",minHeight:36,fontWeight:700}}>
                      ₹{getCompanyPrice(companyData,key)}
                      {companyData.pricing?.[key]!=null&&companyData.pricing[key]!==""&&<span style={{marginLeft:8,fontSize:10,fontWeight:600,color:"#0cc0df"}}>custom</span>}
                    </div>
                }
              </div>
            ))}
          </div>
        </div>
      )}

      {/* TAB: WALLET */}
      {tab==="wallet"&&(
        <div>
          {addBalModal&&(
            <Modal title="💰 Add Balance" onClose={()=>{setAddBalModal(false);setAddAmt("");setAddNote("");setAddRef("");}}>
              <div style={{marginBottom:14}}>
                <Label>Wallet credit amount, excluding GST (₹) *</Label>
                <Inp type="number" value={addAmt} onChange={e=>setAddAmt(e.target.value)} placeholder="e.g. 2999" style={{marginBottom:12}}/>
                <Label>Payment Mode *</Label>
                <div style={{display:"flex",gap:8,marginBottom:12,flexWrap:"wrap"}}>
                  {["UPI","NEFT","IMPS","Cheque","Cash","Bank Transfer"].map(m=>(
                    <button key={m} onClick={()=>setAddMode(m)} style={{padding:"5px 12px",borderRadius:7,border:`1.5px solid ${addMode===m?"#ff3131":"var(--b1)"}`,background:addMode===m?"rgba(255,49,49,0.1)":"transparent",color:addMode===m?"#ff3131":"var(--muted)",cursor:"pointer",fontSize:11,fontWeight:addMode===m?700:500,fontFamily:FF}}>{m}</button>
                  ))}
                </div>
                <Label>Payment Reference (UTR/TXN ID) *</Label>
                <Inp value={addRef} onChange={e=>setAddRef(e.target.value)} placeholder="e.g. UTR123456789" style={{marginBottom:12}}/>
                <Label>Payment Date</Label>
                <Inp type="date" value={addDate} onChange={e=>setAddDate(e.target.value)} style={{marginBottom:12}}/>
                <Label>Note *</Label>
                <Inp value={addNote} onChange={e=>setAddNote(e.target.value)} placeholder="e.g. Q3 wallet recharge" style={{marginBottom:12}}/>
                <div style={{background:"var(--s2)",borderRadius:10,padding:"12px 16px",fontSize:13}}>
                  <div style={{display:"flex",justifyContent:"space-between",marginBottom:4}}><span style={{color:"var(--muted)"}}>Current:</span><span style={{fontWeight:700}}>₹{(walletData?.balance||0).toLocaleString("en-IN")}</span></div>
                  <div style={{display:"flex",justifyContent:"space-between",marginBottom:4}}><span style={{color:"var(--muted)"}}>Adding:</span><span style={{fontWeight:700,color:"var(--green)"}}>+₹{(parseInt(addAmt)||0).toLocaleString("en-IN")}</span></div>
                  <div style={{display:"flex",justifyContent:"space-between",marginBottom:4}}><span style={{color:"var(--muted)"}}>IGST ({GST_PCT}%):</span><span style={{fontWeight:700}}>₹{gstOf(parseInt(addAmt)||0).toLocaleString("en-IN",{minimumFractionDigits:2,maximumFractionDigits:2})}</span></div>
                  <div style={{display:"flex",justifyContent:"space-between",marginBottom:4}}><span style={{color:"var(--muted)"}}>Client pays (incl. GST):</span><span style={{fontWeight:700}}>₹{(Math.round(((parseInt(addAmt)||0)+gstOf(parseInt(addAmt)||0))*100)/100).toLocaleString("en-IN",{minimumFractionDigits:2,maximumFractionDigits:2})}</span></div>
                  <div style={{borderTop:"1px solid var(--b1)",paddingTop:8,display:"flex",justifyContent:"space-between"}}><span style={{fontWeight:700}}>New Balance:</span><span style={{fontWeight:900,color:"var(--green)",fontFamily:FFD,fontSize:16}}>₹{((walletData?.balance||0)+(parseInt(addAmt)||0)).toLocaleString("en-IN")}</span></div>
                </div>
              </div>
              <div style={{display:"flex",gap:10}}><Btn onClick={doAddBalance}>✅ Add Balance</Btn><BtnGhost onClick={()=>setAddBalModal(false)}>Cancel</BtnGhost></div>
            </Modal>
          )}
          <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:14,marginBottom:14}} className="mg1">
            {[["💳","Balance","₹"+(walletData?.balance||0).toLocaleString("en-IN"),(walletData?.balance||0)<500?"#ff3131":"var(--green)"],["📈","Total Loaded","₹"+(walletData?.totalLoaded||0).toLocaleString("en-IN"),"#ff914d"],["📉","Total Spent","₹"+totalSpent.toLocaleString("en-IN"),"var(--brand)"]].map(([icon,label,val,color],i)=>(
              <div key={i} className="fu" style={{padding:"16px 18px",borderRadius:12}}>
                <div style={{fontSize:22,marginBottom:6}}>{icon}</div>
                <div style={{fontFamily:FFD,fontSize:22,fontWeight:900,color,marginBottom:3}}>{val}</div>
                <div style={{fontSize:11,color:"var(--muted)",fontWeight:600}}>{label}</div>
              </div>
            ))}
          </div>
          <div style={{display:"flex",gap:10,marginBottom:16}}>
            <Btn onClick={()=>setAddBalModal(true)}>+ Add Balance</Btn>
          </div>
          <RechargeHistory txns={compTxns} company={companyData||c} email={c.email}/>
        </div>
      )}

      {/* TAB: HISTORY */}
      {tab==="history"&&(
        <div className="fu" style={{borderRadius:14,padding:0,overflow:"hidden"}}>
          <div style={{padding:"12px 16px",borderBottom:"1px solid var(--b1)",display:"flex",justifyContent:"space-between",alignItems:"center"}}>
            <div style={{fontFamily:FFD,fontSize:13,fontWeight:700,color:"var(--text)"}}>{cHistory.length} Searches</div>
            <button onClick={()=>{const rows=[["Date","Type","Value","Found","Cost","HR User"],...cHistory.map(h=>[(h.timestamp||"").slice(0,10),h.searchType,h.searchValue,h.found?"Yes":"No",h.cost||0,h.userName||""])];const csv=rows.map(r=>r.join(",")).join("\n");const a=document.createElement("a");a.href="data:text/csv;charset=utf-8,"+encodeURIComponent(csv);a.download=c.name+"_searches.csv";a.click();}} style={{background:"var(--s2)",border:"1px solid var(--b1)",borderRadius:7,color:"var(--text)",padding:"5px 12px",cursor:"pointer",fontSize:11,fontWeight:600,fontFamily:FF}}>↓ Export CSV</button>
          </div>
          <div style={{maxHeight:400,overflowY:"auto"}}>
            <table style={{width:"100%",borderCollapse:"collapse"}}>
              <thead><tr style={{background:"var(--s2)"}}>{["Date","Type","Value","Found","Cost","HR User"].map(h=><th key={h} style={{padding:"8px 12px",textAlign:"left",fontSize:10,fontWeight:700,color:"var(--muted)",textTransform:"uppercase"}}>{h}</th>)}</tr></thead>
              <tbody>
                {cHistory.slice(0,100).map((h,i)=>(
                  <tr key={i} style={{borderBottom:"1px solid var(--b1)"}}>
                    <td style={{padding:"8px 12px",fontSize:11,color:"var(--muted)"}}>{(h.timestamp||"").slice(0,10)}</td>
                    <td style={{padding:"8px 12px"}}><Badge color="var(--brand)" style={{fontSize:9}}>{(h.searchType||"").toUpperCase()}</Badge></td>
                    <td style={{padding:"8px 12px",fontSize:12,fontFamily:"monospace",color:"var(--text)"}}>{h.searchValue||"—"}</td>
                    <td style={{padding:"8px 12px"}}><Badge color={h.found?"var(--green)":"#ff3131"} style={{fontSize:9}}>{h.found?"✓":"✗"}</Badge></td>
                    <td style={{padding:"8px 12px",fontSize:12,fontWeight:700,color:"var(--brand)"}}>₹{h.cost||0}</td>
                    <td style={{padding:"8px 12px",fontSize:11,color:"var(--muted)"}}>{h.userName||"—"}</td>
                  </tr>
                ))}
                {cHistory.length===0&&<tr><td colSpan={6} style={{padding:32,textAlign:"center",color:"var(--muted)",fontSize:12}}>No searches yet</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* TAB: HR USERS */}
      {tab==="users"&&(
        <div className="fu" style={{borderRadius:14,padding:0,overflow:"hidden"}}>
          <div style={{padding:"12px 16px",borderBottom:"1px solid var(--b1)",fontFamily:FFD,fontSize:13,fontWeight:700,color:"var(--text)"}}>{cUsers.length} HR Users</div>
          <table style={{width:"100%",borderCollapse:"collapse"}}>
            <thead><tr style={{background:"var(--s2)"}}>{["Name","Email","Role","Searches","Status","Action"].map(h=><th key={h} style={{padding:"8px 12px",textAlign:"left",fontSize:10,fontWeight:700,color:"var(--muted)",textTransform:"uppercase"}}>{h}</th>)}</tr></thead>
            <tbody>
              {cUsers.map((u,i)=>{
                const effectiveRole=roleOverrides[u.id]?.role??u.role??"hr";
                const isAdminRole=effectiveRole==="admin";
                return(
                  <tr key={i} style={{borderBottom:"1px solid var(--b1)"}}>
                    <td style={{padding:"10px 12px",fontSize:13,fontWeight:600,color:"var(--text)"}}>{u.name||"—"}</td>
                    <td style={{padding:"10px 12px",fontSize:12,color:"var(--muted)"}}>{u.email}</td>
                    <td style={{padding:"10px 12px"}}><Badge color={isAdminRole?"#f59e0b":"var(--brand)"} style={{fontSize:9}}>{isAdminRole?"Company Admin":"HR Staff"}</Badge></td>
                    <td style={{padding:"10px 12px",fontSize:13,fontWeight:700,color:"var(--brand)"}}>{cHistory.filter(h=>h.userId===u.id||h.userName===u.name).length}</td>
                    <td style={{padding:"10px 12px"}}><Badge color={u.suspended?"#ff3131":"var(--green)"} style={{fontSize:9}}>{u.suspended?"Suspended":"Active"}</Badge></td>
                    <td style={{padding:"10px 12px"}}>
                      <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                        <button onClick={()=>toggleCompanyAdminRole(u)} disabled={roleChangeLoading===u.id} style={{background:isAdminRole?"rgba(255,49,49,0.08)":"rgba(245,158,11,0.08)",border:`1px solid ${isAdminRole?"rgba(255,49,49,0.2)":"rgba(245,158,11,0.2)"}`,borderRadius:7,color:isAdminRole?"#ff3131":"#f59e0b",padding:"4px 10px",cursor:"pointer",fontSize:10,fontWeight:700,fontFamily:FF,whiteSpace:"nowrap"}}>
                          {roleChangeLoading===u.id?"...":isAdminRole?"↓ Make HR Staff":"↑ Make Admin"}
                        </button>
                        {!isAdminRole&&(
                          <button onClick={()=>openPermModal({...u,searchPermissions:roleOverrides[u.id]?.searchPermissions??u.searchPermissions})} style={{background:"rgba(12,192,223,0.08)",border:"1px solid rgba(12,192,223,0.2)",borderRadius:7,color:"#0cc0df",padding:"4px 10px",cursor:"pointer",fontSize:10,fontWeight:700,fontFamily:FF,whiteSpace:"nowrap"}}>
                            🔒 Permissions
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
              {cUsers.length===0&&<tr><td colSpan={6} style={{padding:32,textAlign:"center",color:"var(--muted)",fontSize:12}}>No HR users found</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {/* TAB: BADGE */}
      {tab==="badge"&&(
        <div className="fu" style={{borderRadius:14,padding:"22px 24px"}}>
          <div style={{fontFamily:FFD,fontSize:14,fontWeight:700,color:"var(--text)",marginBottom:16}}>Verification Badge</div>
          <div style={{background:"var(--s2)",border:"1px solid var(--b1)",borderRadius:12,padding:"14px 16px",marginBottom:20}}>
            <div style={{fontSize:11,color:"var(--muted)",fontWeight:600,marginBottom:8,textTransform:"uppercase"}}>Current Status</div>
            {companyData.verified
              ?<div style={{display:"flex",alignItems:"center",gap:10}}>
                <div style={{fontSize:24}}>{companyData.verifiedType?.split(" ")[0]||"✅"}</div>
                <div><div style={{fontSize:14,fontWeight:700,color:"var(--green)"}}>{companyData.verifiedType||"BGV Verified"}</div><div style={{fontSize:11,color:"var(--muted)"}}>Active badge</div></div>
              </div>
              :<div style={{fontSize:13,color:"var(--muted)"}}>No badge assigned</div>
            }
          </div>
          <div style={{fontSize:11,color:"var(--muted)",fontWeight:600,marginBottom:12,textTransform:"uppercase"}}>Assign Badge</div>
          <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:12,marginBottom:20}} className="mg1">
            {[{type:"✅ BGV Verified",color:"#10b981",desc:"Standard verified company"},{type:"🏆 Premium Partner",color:"#f59e0b",desc:"Top-tier trusted partner"},{type:"⭐ Trusted Employer",color:"#0cc0df",desc:"Verified trusted employer"}].map((b,i)=>(
              <div key={i} onClick={()=>grantBadge(b.type)} style={{padding:"14px 16px",borderRadius:12,border:`2px solid ${companyData.verifiedType===b.type?"#ff3131":"var(--b1)"}`,background:companyData.verifiedType===b.type?"rgba(255,49,49,0.05)":"var(--s2)",cursor:"pointer"}}>
                <div style={{fontSize:20,marginBottom:6}}>{b.type.split(" ")[0]}</div>
                <div style={{fontSize:12,fontWeight:700,color:b.color,marginBottom:4}}>{b.type}</div>
                <div style={{fontSize:10,color:"var(--muted)"}}>{b.desc}</div>
              </div>
            ))}
          </div>
          {companyData.verified&&<button onClick={removeBadge} style={{background:"rgba(255,49,49,0.08)",border:"1px solid rgba(255,49,49,0.2)",borderRadius:8,color:"#ff3131",padding:"8px 16px",cursor:"pointer",fontSize:12,fontWeight:700,fontFamily:FF}}>✕ Remove Badge</button>}
        </div>
      )}

      {/* TAB: TRENDS */}
      {tab==="trends"&&(
        <div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:14,marginBottom:14}} className="mg1">
            <div className="fu" style={{borderRadius:14,padding:"18px 20px"}}>
              <div style={{fontFamily:FFD,fontSize:13,fontWeight:700,color:"var(--text)",marginBottom:14}}>Monthly Searches</div>
              {trendData.length>0
                ?<ResponsiveContainer width="100%" height={180}><BarChart data={trendData}><XAxis dataKey="month" tick={{fontSize:10,fill:"var(--muted)"}}/><YAxis tick={{fontSize:10,fill:"var(--muted)"}}/><Tooltip/><Bar dataKey="searches" fill="#ff3131" radius={[4,4,0,0]} name="Searches"/></BarChart></ResponsiveContainer>
                :<div style={{textAlign:"center",padding:40,color:"var(--muted)",fontSize:12}}>No data yet</div>}
            </div>
            <div className="fu" style={{borderRadius:14,padding:"18px 20px"}}>
              <div style={{fontFamily:FFD,fontSize:13,fontWeight:700,color:"var(--text)",marginBottom:14}}>Cost Trend</div>
              {trendData.length>0
                ?<ResponsiveContainer width="100%" height={180}><LineChart data={trendData}><XAxis dataKey="month" tick={{fontSize:10,fill:"var(--muted)"}}/><YAxis tick={{fontSize:10,fill:"var(--muted)"}}/><Tooltip formatter={v=>"₹"+v}/><Line dataKey="cost" stroke="#ff914d" strokeWidth={2} dot={{fill:"#ff914d",r:4}} name="Cost"/></LineChart></ResponsiveContainer>
                :<div style={{textAlign:"center",padding:40,color:"var(--muted)",fontSize:12}}>No data yet</div>}
            </div>
          </div>
          <div className="fu" style={{borderRadius:14,padding:"18px 20px"}}>
            <div style={{fontFamily:FFD,fontSize:13,fontWeight:700,color:"var(--text)",marginBottom:14}}>By Search Type</div>
            <table style={{width:"100%",borderCollapse:"collapse"}}>
              <thead><tr style={{background:"var(--s2)"}}>{["Type","Count","Found%","Cost"].map(h=><th key={h} style={{padding:"8px 12px",textAlign:"left",fontSize:10,fontWeight:700,color:"var(--muted)",textTransform:"uppercase"}}>{h}</th>)}</tr></thead>
              <tbody>
                {byType.map((t,i)=>(
                  <tr key={i} style={{borderBottom:"1px solid var(--b1)"}}>
                    <td style={{padding:"8px 12px",fontSize:12,fontWeight:600,color:"var(--text)"}}>{t.label}</td>
                    <td style={{padding:"8px 12px",fontSize:12}}>{t.count}</td>
                    <td style={{padding:"8px 12px",fontSize:12,color:"var(--green)",fontWeight:700}}>{t.count>0?Math.round((cHistory.filter(h=>h.found&&h.searchType===t.type).length/t.count)*100):0}%</td>
                    <td style={{padding:"8px 12px",fontSize:12,fontWeight:700,color:"var(--brand)"}}>₹{t.cost.toLocaleString("en-IN")}</td>
                  </tr>
                ))}
                {byType.length===0&&<tr><td colSpan={4} style={{padding:24,textAlign:"center",color:"var(--muted)",fontSize:12}}>No searches yet</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Per-module search + report permissions popup */}
      {permModal&&(
        <Modal title={"🔒 Permissions — "+(permModal.name||permModal.email)} onClose={()=>setPermModal(null)} width={440}>
          <div style={{fontSize:12,color:"var(--muted)",marginBottom:16}}>
            New HR staff start with no search access. Toggle on whatever this person needs — nothing else changes for them.
          </div>
          <div style={{display:"flex",flexDirection:"column",gap:10,marginBottom:20}}>
            {[...SEARCH_MODULES,{key:"reports",label:"Reports Access"}].map(m=>(
              <div key={m.key} onClick={()=>setPermDraft(p=>({...p,[m.key]:!p[m.key]}))} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"10px 14px",borderRadius:10,border:`1.5px solid ${permDraft[m.key]?"var(--brand)":"var(--b1)"}`,background:permDraft[m.key]?"var(--brand)08":"transparent",cursor:"pointer"}}>
                <span style={{fontSize:13,fontWeight:600,color:"var(--text)"}}>{m.label}</span>
                <div style={{width:40,height:22,borderRadius:99,background:permDraft[m.key]?"var(--brand)":"var(--b2)",position:"relative",transition:"background .15s"}}>
                  <div style={{width:16,height:16,borderRadius:"50%",background:"#fff",position:"absolute",top:3,left:permDraft[m.key]?21:3,transition:"left .15s"}}/>
                </div>
              </div>
            ))}
          </div>
          <div style={{display:"flex",gap:10}}>
            <Btn onClick={savePermissions} disabled={permSaving}>{permSaving?"Saving...":"💾 Save Permissions"}</Btn>
            <BtnGhost onClick={()=>setPermModal(null)}>Cancel</BtnGhost>
          </div>
        </Modal>
      )}
    </div>
  );
}

function CompaniesAdminPage({companies,setCompanies,users,setUsers,addToast,history:searchHistory,wallets,transactions}) {
  const {dark}=useTheme();
  const [search,setSearch]=useState("");
  const [selCompany,setSelCompany]=useState(null);
  const [tab,setTab]=useState("details");
  const [editMode,setEditMode]=useState(false);
  const [editData,setEditData]=useState({});
  const [walletData,setWalletData]=useState(null);
  const [compTxns,setCompTxns]=useState([]);
  const [addBalModal,setAddBalModal]=useState(false);
  const [addAmt,setAddAmt]=useState("");
  const [addNote,setAddNote]=useState("");
  const [addMode,setAddMode]=useState("UPI");
  const [addRef,setAddRef]=useState("");
  const [addDate,setAddDate]=useState(new Date().toISOString().slice(0,10));
  const [loading,setLoading]=useState(false);
  const [tempPwModal,setTempPwModal]=useState(null); // {email, tempPassword} — shown once, never stored
  const [pwResetLoading,setPwResetLoading]=useState(null); // company id currently resetting

  const adminResetCompanyPassword=async(c)=>{
    if(!c.email){addToast("This company has no email on file","error");return;}
    if(!window.confirm(`Generate a new temporary password for ${c.name} (${c.email})? Their current password will stop working immediately.`))return;
    setPwResetLoading(c.id);
    try{
      const idToken=await auth.currentUser?.getIdToken();
      const res=await fetch("https://adminresetpassword-mtvra6khyq-uc.a.run.app",{
        method:"POST",
        headers:{"Content-Type":"application/json","Authorization":"Bearer "+idToken},
        body:JSON.stringify({email:c.email}),
      });
      const json=await res.json();
      if(!res.ok||json.error)throw new Error(json.error||"Reset failed");
      setTempPwModal({email:c.email,companyName:c.name,tempPassword:json.tempPassword});
    }catch(err){addToast("Failed to reset password: "+err.message,"error");}
    finally{setPwResetLoading(null);}
  };
  // Use alias to avoid conflict with window.history
  const history=searchHistory||[];

  const filtered=companies.filter(c=>!search||(c.name||"").toLowerCase().includes(search.toLowerCase())||(c.email||"").toLowerCase().includes(search.toLowerCase()));

  const openCompany=async(c)=>{
    setSelCompany(c);
    setTab("details");
    setEditData({name:c.name||"",email:c.email||"",phone:c.phone||"",industry:c.industry||"",address:c.address||"",contactPerson:c.contactPerson||""});
    // Load wallet from wallets prop (already loaded in App)
    const w=(wallets||{})[c.id]||{balance:0,totalLoaded:0,totalSpent:0};
    setWalletData(w);
    // Filter transactions for this company
    const txns=(transactions||[]).filter(t=>t.companyId===c.id);
    setCompTxns(txns);
    // If no wallet data, try loading from Firestore
    if(!w.balance&&!w.totalLoaded){
      try{
        const allW=await getAllWallets();
        const cW=allW.find(x=>x.id===c.id)||{balance:0,totalLoaded:0,totalSpent:0};
        setWalletData(cW);
      }catch(e){}
    }
    // If no transactions, try loading from Firestore
    if(txns.length===0){
      try{
        const allT=await getAllTransactions();
        setCompTxns(allT.filter(t=>t.companyId===c.id));
      }catch(e){}
    }
  };

  const toggle=async id=>{
    const co=companies.find(c=>c.id===id);
    const ns=co.status==="Active"?"Inactive":"Active";
    try{await updateCompany(id,{status:ns});setCompanies(p=>p.map(c=>c.id===id?{...c,status:ns}:c));setSelCompany(p=>({...p,status:ns}));addToast(co.name+" → "+ns,"success");}
    catch(e){addToast("Failed","error");}
  };

  const grantVerify=async(id,type)=>{
    try{
      await updateCompany(id,{verified:true,verifiedType:type,verifiedAt:new Date().toISOString()});
      setCompanies(p=>p.map(c=>c.id===id?{...c,verified:true,verifiedType:type}:c));
      setSelCompany(p=>({...p,verified:true,verifiedType:type}));
      addToast("Badge granted: "+type,"success");
    }catch(e){addToast("Failed","error");}
  };

  const removeVerify=async(id)=>{
    try{
      await updateCompany(id,{verified:false,verifiedType:null});
      setCompanies(p=>p.map(c=>c.id===id?{...c,verified:false,verifiedType:null}:c));
      setSelCompany(p=>({...p,verified:false,verifiedType:null}));
      addToast("Badge removed","success");
    }catch(e){addToast("Failed","error");}
  };

  const saveEdit=async()=>{
    try{
      await updateCompany(selCompany.id,editData);
      setCompanies(p=>p.map(c=>c.id===selCompany.id?{...c,...editData}:c));
      setSelCompany(p=>({...p,...editData}));
      setEditMode(false);
      addToast("Company updated!","success");
    }catch(e){addToast("Failed","error");}
  };

  const doAddBalance=async()=>{
    const amt=parseInt(addAmt)||0;
    if(amt<=0){addToast("Enter valid amount","error");return;}
    if(!addRef.trim()){addToast("Enter payment reference","error");return;}
    if(!addNote.trim()){addToast("Enter note","error");return;}
    try{
      const note=`${addNote} | Mode: ${addMode} | Ref: ${addRef} | Date: ${addDate}`;
      const newBal=await manualWalletAdjust(selCompany.id,amt,note,"Super Admin");
      setWalletData(p=>({...p,balance:newBal,totalLoaded:(p?.totalLoaded||0)+amt}));
      addToast("₹"+amt.toLocaleString("en-IN")+" added! New balance: ₹"+newBal.toLocaleString("en-IN"),"success");
      setAddBalModal(false);setAddAmt("");setAddNote("");setAddRef("");setAddMode("UPI");
    }catch(e){addToast("Failed: "+e.message,"error");}
  };

  const generateCompanyInvoice=()=>{
    const c=selCompany;
    const now=new Date();
    const month=now.toLocaleString("en-IN",{month:"long",year:"numeric"});
    const invoiceNo="INV-"+c.id?.slice(0,4).toUpperCase()+"-"+now.getFullYear()+(now.getMonth()+1).toString().padStart(2,"0");
    const cHistory=(history||[]).filter(h=>h.companyId===c.id);
    const monthHistory=cHistory.filter(h=>{
      const d=new Date(h.timestamp);
      return d.getMonth()===now.getMonth()&&d.getFullYear()===now.getFullYear();
    });
    const total=monthHistory.reduce((s,h)=>s+(h.cost||0),0);
    const byType=Object.entries(monthHistory.reduce((acc,h)=>{
      const t=h.searchType||"other";
      if(!acc[t])acc[t]={count:0,cost:0};
      acc[t].count++;acc[t].cost+=h.cost||0;
      return acc;
    },{}));

    const win=window.open("","_blank","width=800,height:900");
    if(!win){alert("Please allow popups");return;}
    win.document.write(`<!DOCTYPE html><html><head><meta charset="UTF-8"/><title>Invoice — ${c.name}</title>
    <style>*{margin:0;padding:0;box-sizing:border-box;}body{font-family:Arial,sans-serif;background:#fff;color:#1e293b;}
    .header{background:linear-gradient(135deg,#ff3131,#ff914d);padding:28px 40px;display:flex;justify-content:space-between;align-items:center;}
    .logo{color:#fff;font-size:24px;font-weight:900;letter-spacing:-0.5px;}
    .inv-title{color:rgba(255,255,255,0.9);font-size:13px;margin-top:4px;}
    .body{padding:40px;}
    .row{display:flex;justify-content:space-between;margin-bottom:32px;}
    .label{font-size:10px;color:#94a3b8;text-transform:uppercase;font-weight:700;margin-bottom:4px;}
    .value{font-size:14px;color:#1e293b;font-weight:600;}
    table{width:100%;border-collapse:collapse;margin-bottom:24px;}
    th{background:#f8fafc;padding:10px 14px;text-align:left;font-size:10px;color:#64748b;text-transform:uppercase;font-weight:700;}
    td{padding:10px 14px;border-bottom:1px solid #f1f5f9;font-size:13px;}
    .total{background:#fff7ed;border-radius:10px;padding:16px 20px;display:flex;justify-content:space-between;margin-bottom:24px;}
    .footer{text-align:center;font-size:11px;color:#94a3b8;margin-top:32px;}
    .badge{display:inline-block;background:rgba(16,185,129,0.1);color:#10b981;padding:3px 10px;border-radius:99px;font-size:10px;font-weight:700;}
    @media print{body{-webkit-print-color-adjust:exact;}}
    </style></head><body>
    <div class="header">
      <div><div class="logo">Meitho BGV</div><div class="inv-title">Background Verification Platform · meitho.com</div></div>
      <div style="text-align:right;color:#fff;">
        <div style="font-size:11px;opacity:.7;">Invoice Number</div>
        <div style="font-size:18px;font-weight:900;">${invoiceNo}</div>
        <div style="font-size:11px;opacity:.7;margin-top:4px;">${now.toLocaleDateString("en-IN")}</div>
      </div>
    </div>
    <div class="body">
      <div class="row">
        <div>
          <div class="label">Billed To</div>
          <div style="font-size:18px;font-weight:800;color:#1e293b;margin-bottom:4px;">${c.name}</div>
          <div class="value" style="font-weight:400;color:#64748b;">${c.email||""}</div>
          <div class="value" style="font-weight:400;color:#64748b;">${c.phone||""}</div>
          <div class="value" style="font-weight:400;color:#64748b;">${c.address||""}</div>
        </div>
        <div style="text-align:right;">
          <div class="label">Period</div>
          <div class="value">${month}</div>
          <div style="margin-top:12px;"><div class="label">Wallet Balance</div>
          <div style="font-size:20px;font-weight:900;color:#ff3131;">₹${(walletData?.balance||0).toLocaleString("en-IN")}</div></div>
        </div>
      </div>
      <table>
        <thead><tr><th>Verification Type</th><th>Searches</th><th>Rate</th><th style="text-align:right;">Amount</th></tr></thead>
        <tbody>
          ${byType.map(([type,d])=>`<tr>
            <td>${{uan:"UAN / Employment History",pan:"PAN Verification",aadhaar:"Aadhaar Verification",dl:"Driving License",ecourt:"Criminal / eCourt Check",full_bgv:"Full BGV Package",refcheck:"Reference Check"}[type]||type}</td>
            <td>${d.count}</td>
            <td>₹${d.count>0?Math.round(d.cost/d.count):0}</td>
            <td style="text-align:right;font-weight:700;">₹${d.cost.toLocaleString("en-IN")}</td>
          </tr>`).join("")}
          ${byType.length===0?`<tr><td colspan="4" style="text-align:center;color:#94a3b8;padding:20px;">No searches this month</td></tr>`:""}
        </tbody>
      </table>
      <div class="total">
        <div style="font-size:14px;font-weight:700;color:#1e293b;">Total for ${month}</div>
        <div style="font-size:24px;font-weight:900;color:#ff3131;">₹${total.toLocaleString("en-IN")}</div>
      </div>
      <div style="background:#f8fafc;border-radius:10px;padding:16px 20px;margin-bottom:24px;font-size:12px;color:#64748b;line-height:1.7;">
        <strong style="color:#1e293b;">Note:</strong> Amounts are deducted directly from your Meitho BGV wallet. 
        This invoice is auto-generated for your records. Contact help@meitho.com for queries.
      </div>
      <div class="footer">
        Meitho BGV · meitho.com · help@meitho.com · +91 8300016265<br/>
        Invoice ID: ${invoiceNo} · Generated: ${now.toLocaleString("en-IN")}
      </div>
    </div>
    <script>window.onload=()=>window.print();</script>
    </body></html>`);
    win.document.close();
  };

  // ── Company Detail Page ──
  if(selCompany){
    return <CompanyDetailView
      company={selCompany}
      history={history}
      users={users||[]}
      wallets={wallets||{}}
      transactions={transactions||[]}
      companies={companies||[]}
      addToast={addToast}
      onBack={()=>{setSelCompany(null);setTab("details");setEditMode(false);}}
      setCompanies={setCompanies}
    />;
  }

  // ── Company Tiles Grid ──
  return(
    <div>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:20,flexWrap:"wrap",gap:12}}>
        <div>
          <h2 style={{fontFamily:FFD,fontWeight:800,fontSize:26,color:"var(--text)",marginBottom:4}}>🏢 Companies</h2>
          <p style={{color:"var(--muted)",fontSize:13}}>{companies.length} registered companies · Click any company to manage</p>
        </div>
        <Inp value={search} onChange={e=>setSearch(e.target.value)} placeholder="Search companies..." style={{width:240}}/>
      </div>

      {/* Status summary */}
      <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:12,marginBottom:20}} className="mg2">
        {[
          ["🏢","Total",companies.length,"var(--brand)"],
          ["✅","Active",companies.filter(c=>c.status==="Active").length,"var(--green)"],
          ["⏳","Pending",companies.filter(c=>!c.status||c.status==="Pending").length,"#f59e0b"],
          ["🔵","Verified",companies.filter(c=>c.verified).length,"#0cc0df"],
        ].map(([icon,label,val,color],i)=>(
          <div key={i} className="fu" style={{padding:"14px 16px",borderRadius:12}}>
            <div style={{fontSize:18,marginBottom:4}}>{icon}</div>
            <div style={{fontFamily:FFD,fontSize:22,fontWeight:900,color,marginBottom:2}}>{val}</div>
            <div style={{fontSize:11,color:"var(--muted)",fontWeight:600}}>{label}</div>
          </div>
        ))}
      </div>

      {/* Company Tiles */}
      <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:16}} className="mg2">
        {filtered.map((c,i)=>{
          const cSearches=(history||[]).filter(h=>h.companyId===c.id).length;
          const cBalance=(wallets||{})[c.id]?.balance||0;
          const cSpentFromHistory=(history||[]).filter(h=>h.companyId===c.id).reduce((s,h)=>s+(h.cost||0),0);
          const cSpentFromWallet=(wallets||{})[c.id]?.totalSpent||0;
          const cSpent=cSpentFromHistory>0?cSpentFromHistory:cSpentFromWallet;
          const cHRUsers=(users||[]).filter(u=>u.companyId===c.id||u.company===c.name).length;
          const lowBal=cBalance<500&&c.status==="Active";
          return(
            <div key={i} onClick={()=>openCompany(c)} className="fu" style={{borderRadius:14,padding:"18px 20px",cursor:"pointer",border:`1px solid ${lowBal?"rgba(255,49,49,0.3)":"var(--b1)"}`,transition:"all .2s",position:"relative",overflow:"hidden"}}
              onMouseEnter={e=>{e.currentTarget.style.transform="translateY(-2px)";e.currentTarget.style.boxShadow="0 8px 24px rgba(0,0,0,0.12)";}}
              onMouseLeave={e=>{e.currentTarget.style.transform="translateY(0)";e.currentTarget.style.boxShadow="";}}>
              {lowBal&&<div style={{position:"absolute",top:0,left:0,right:0,height:3,background:"linear-gradient(90deg,#ff3131,#ff914d)"}}/>}
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:14}}>
                <div style={{display:"flex",gap:10,alignItems:"center"}}>
                  <div style={{width:40,height:40,borderRadius:10,background:"linear-gradient(135deg,#ff3131,#ff914d)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:16,color:"#fff",fontWeight:900,flexShrink:0}}>
                    {(c.name||"?")[0].toUpperCase()}
                  </div>
                  <div>
                    <div style={{fontSize:13,fontWeight:700,color:"var(--text)",marginBottom:2}}>{c.name}</div>
                    <div style={{fontSize:10,color:"var(--muted)"}}>{c.industry||c.email?.split("@")[1]||""}</div>
                  </div>
                </div>
                <Badge color={c.status==="Active"?"var(--green)":c.status==="Pending"?"#f59e0b":"#ff3131"} style={{fontSize:9}}>{c.status||"Pending"}</Badge>
              </div>

              {/* Registration Details */}
              <div style={{marginBottom:12,padding:"10px 12px",background:"var(--s2)",borderRadius:10,border:"1px solid var(--b1)"}}>
                {[
                  ["📧",c.email],
                  ["📱",c.phone||c.contactPhone],
                  ["👤",c.contactPerson||c.pocName],
                  ["🏭",c.industry],
                  ["📍",c.address||c.profile?.address],
                  ["🔖",c.gst||c.profile?.gst],
                  ["🏛️",c.cin||c.profile?.cin],
                  ["📅","Joined: "+(c.registeredOn||c.createdAt?.slice(0,10)||"—")],
                ].filter(([,v])=>v).map(([icon,val],j)=>(
                  <div key={j} style={{display:"flex",gap:6,alignItems:"flex-start",marginBottom:4}}>
                    <span style={{fontSize:10,flexShrink:0,marginTop:1}}>{icon}</span>
                    <span style={{fontSize:10,color:"var(--muted)",lineHeight:1.4,wordBreak:"break-all"}}>{val}</span>
                  </div>
                ))}
              </div>
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,marginBottom:12}}>
                {[
                  ["💳 Wallet","₹"+cBalance.toLocaleString("en-IN"),lowBal?"#ff3131":"var(--green)"],
                  ["🔍 Searches",cSearches,"var(--brand)"],
                  ["💰 Spent","₹"+cSpent.toLocaleString("en-IN"),"#ff914d"],
                  ["👥 HR Users",cHRUsers,"var(--muted)"],
                  ["🎯 ATS",c.atsEnabled?"Enabled":"Disabled",c.atsEnabled?"var(--green)":"var(--muted)"],
                ].map(([label,val,color],j)=>(
                  <div key={j} style={{background:"var(--s2)",borderRadius:8,padding:"8px 10px"}}>
                    <div style={{fontSize:9,color:"var(--muted)",fontWeight:600,marginBottom:2}}>{label}</div>
                    <div style={{fontSize:13,fontWeight:700,color}}>{val}</div>
                  </div>
                ))}
              </div>
              {c.verified&&<div style={{display:"inline-flex",alignItems:"center",gap:5,background:"rgba(16,185,129,0.1)",border:"1px solid rgba(16,185,129,0.2)",borderRadius:99,padding:"3px 10px",marginBottom:8}}>
                <span style={{fontSize:10,fontWeight:700,color:"#10b981"}}>{c.verifiedType||"✅ BGV Verified"}</span>
              </div>}
              {lowBal&&<div style={{fontSize:10,color:"#ff3131",fontWeight:700}}>⚠️ Low wallet balance</div>}
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginTop:10,paddingTop:8,borderTop:"1px solid var(--b1)",gap:6,flexWrap:"wrap"}}>
                <button onClick={async(e)=>{
                  e.stopPropagation();
                  try{
                    await updateCompany(c.id,{atsEnabled:!c.atsEnabled});
                    setCompanies(p=>p.map(x=>x.id===c.id?{...x,atsEnabled:!x.atsEnabled}:x));
                    addToast("ATS "+(c.atsEnabled?"disabled":"enabled")+" for "+c.name,"success");
                  }catch(err){addToast("Failed","error");}
                }} style={{background:c.atsEnabled?"rgba(255,49,49,0.08)":"rgba(16,185,129,0.08)",border:`1px solid ${c.atsEnabled?"rgba(255,49,49,0.2)":"rgba(16,185,129,0.2)"}`,borderRadius:7,color:c.atsEnabled?"#ff3131":"var(--green)",padding:"4px 10px",cursor:"pointer",fontSize:10,fontWeight:700,fontFamily:FF}}>
                  🎯 {c.atsEnabled?"Disable ATS":"Enable ATS"}
                </button>
                <button onClick={async(e)=>{
                  e.stopPropagation();
                  adminResetCompanyPassword(c);
                }} disabled={pwResetLoading===c.id} style={{background:"rgba(12,192,223,0.08)",border:"1px solid rgba(12,192,223,0.2)",borderRadius:7,color:"#0cc0df",padding:"4px 10px",cursor:"pointer",fontSize:10,fontWeight:700,fontFamily:FF}}>
                  🔑 {pwResetLoading===c.id?"Resetting...":"Reset Password"}
                </button>
                <div style={{fontSize:10,color:"var(--muted)"}}>Click to manage →</div>
              </div>
            </div>
          );
        })}
        {filtered.length===0&&(
          <div style={{gridColumn:"1/-1",textAlign:"center",padding:60,color:"var(--muted)",fontSize:14}}>No companies found</div>
        )}
      </div>

      {/* Temp password reveal — shown exactly once, never stored or logged anywhere else */}
      {tempPwModal&&(
        <Modal title="🔑 Temporary Password Generated" onClose={()=>setTempPwModal(null)} width={460}>
          <div style={{fontSize:13,color:"var(--muted)",marginBottom:16}}>
            For <strong style={{color:"var(--text)"}}>{tempPwModal.companyName}</strong> ({tempPwModal.email}). Their old password no longer works. Share this securely — it won't be shown again, and Meitho does not store it anywhere.
          </div>
          <div style={{display:"flex",gap:8,alignItems:"center",background:"var(--s2)",border:"1px solid var(--b1)",borderRadius:10,padding:"12px 14px",marginBottom:16}}>
            <div style={{flex:1,fontFamily:"monospace",fontSize:16,fontWeight:700,color:"var(--text)",letterSpacing:1}}>{tempPwModal.tempPassword}</div>
            <button onClick={()=>{navigator.clipboard?.writeText(tempPwModal.tempPassword);addToast("Copied to clipboard","success");}} style={{background:"rgba(12,192,223,0.12)",border:"none",borderRadius:7,color:"#0cc0df",padding:"6px 12px",cursor:"pointer",fontSize:11,fontWeight:700,fontFamily:FF}}>Copy</button>
          </div>
          <div style={{fontSize:11,color:"#f59e0b",background:"rgba(245,158,11,0.08)",border:"1px solid rgba(245,158,11,0.2)",borderRadius:8,padding:"8px 12px",marginBottom:16}}>
            ⚠ Recommend the company change this password after logging in.
          </div>
          <Btn onClick={()=>setTempPwModal(null)} style={{width:"100%",justifyContent:"center"}}>Done</Btn>
        </Modal>
      )}
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════
   USERS PAGE (Admin Users)
══════════════════════════════════════════════════════════════ */
function UsersPage({users,setUsers,addToast}) {
  const {dark}=useTheme();
  const [search,setSearch]=useState("");
  const filtered=users.filter(u=>!u.isSuperAdmin&&(!search||(u.name||"").toLowerCase().includes(search.toLowerCase())||(u.email||"").toLowerCase().includes(search.toLowerCase())));
  return(
    <div className={dark?"bg-mesh-dark":"bg-mesh-light"} style={{minHeight:"100%",padding:"4px 0"}}>
      <div className="fu" style={{marginBottom:20}}>
        <h2 style={{fontFamily:FFD,fontWeight:800,fontSize:26,color:"var(--text)",marginBottom:4}}>Admin Users</h2>
        <p style={{color:"var(--muted)",fontSize:14}}>{filtered.length} users</p>
      </div>
      <div style={{marginBottom:14}}><Inp value={search} onChange={e=>setSearch(e.target.value)} placeholder="Search users..."/></div>
      <div className="fu1 glass" style={{borderRadius:14,border:"1px solid var(--b1)",overflow:"hidden"}}>
        <table style={{width:"100%",borderCollapse:"collapse"}}>
          <thead style={{background:"var(--s2)"}}><tr>
            {["Name","Email","Role","Company","Status"].map(h=><th key={h} style={{textAlign:"left",padding:"10px 14px",fontSize:10,fontWeight:700,color:"var(--muted)",textTransform:"uppercase"}}>{h}</th>)}
          </tr></thead>
          <tbody>
            {filtered.map((u,i)=>(
              <tr key={i} style={{borderBottom:"1px solid var(--b1)"}} onMouseEnter={e=>e.currentTarget.style.background="var(--s2)"} onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
                <td style={{padding:"12px 14px",fontSize:13,fontWeight:600,color:"var(--text)"}}>{u.name||"—"}</td>
                <td style={{padding:"12px 14px",fontSize:12,color:"var(--sub)"}}>{u.email}</td>
                <td style={{padding:"12px 14px"}}><Badge color="var(--brand)" style={{fontSize:10}}>{u.role||"hr"}</Badge></td>
                <td style={{padding:"12px 14px",fontSize:12,color:"var(--sub)"}}>{u.company||"—"}</td>
                <td style={{padding:"12px 14px"}}><Badge color={u.suspended?"var(--red)":"var(--green)"} style={{fontSize:10}}>{u.suspended?"Suspended":"Active"}</Badge></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════
   EMPLOYEE DB
══════════════════════════════════════════════════════════════ */
function EmployeeDB({employees,setEmployees,addToast}) {
  const {dark}=useTheme();
  const [search,setSearch]=useState("");
  const filtered=employees.filter(e=>!search||
    (e.name||"").toLowerCase().includes(search.toLowerCase())||
    (e.uan||"").includes(search)||
    (e.pan||"").toLowerCase().includes(search.toLowerCase())
  );
  return(
    <div className={dark?"bg-mesh-dark":"bg-mesh-light"} style={{minHeight:"100%",padding:"4px 0"}}>
      <div className="fu" style={{marginBottom:20}}>
        <h2 style={{fontFamily:FFD,fontWeight:800,fontSize:26,color:"var(--text)",marginBottom:4}}>Employee Database</h2>
        <p style={{color:"var(--muted)",fontSize:14}}>{employees.length} records in Meitho DB</p>
      </div>
      <div style={{marginBottom:14}}><Inp value={search} onChange={e=>setSearch(e.target.value)} placeholder="Search by name, UAN, PAN..."/></div>
      {filtered.length===0?
        <div className="fu1 glass" style={{borderRadius:14,padding:"48px 20px",border:"1px solid var(--b1)",textAlign:"center"}}>
          <div style={{fontSize:40,marginBottom:12}}>📂</div>
          <div style={{fontSize:14,color:"var(--sub)"}}>No employee records found</div>
          <div style={{fontSize:12,color:"var(--muted)",marginTop:6}}>Records are saved automatically when you search employees</div>
        </div>:
        <div className="fu1 glass" style={{borderRadius:14,border:"1px solid var(--b1)",overflow:"hidden"}}>
          <table style={{width:"100%",borderCollapse:"collapse"}}>
            <thead style={{background:"var(--s2)"}}><tr>
              {["Name","UAN","PAN","DOB","Added On"].map(h=><th key={h} style={{textAlign:"left",padding:"10px 14px",fontSize:10,fontWeight:700,color:"var(--muted)",textTransform:"uppercase"}}>{h}</th>)}
            </tr></thead>
            <tbody>
              {filtered.map((e,i)=>(
                <tr key={i} style={{borderBottom:"1px solid var(--b1)"}} onMouseEnter={ev=>ev.currentTarget.style.background="var(--s2)"} onMouseLeave={ev=>ev.currentTarget.style.background="transparent"}>
                  <td style={{padding:"12px 14px",fontSize:13,fontWeight:600,color:"var(--text)"}}>{e.name||"—"}</td>
                  <td style={{padding:"12px 14px",fontSize:12,color:"var(--sub)",fontFamily:FFM}}>{e.uan||"—"}</td>
                  <td style={{padding:"12px 14px",fontSize:12,color:"var(--sub)",fontFamily:FFM}}>{e.pan||"—"}</td>
                  <td style={{padding:"12px 14px",fontSize:12,color:"var(--sub)"}}>{e.dob||"—"}</td>
                  <td style={{padding:"12px 14px",fontSize:11,color:"var(--muted)"}}>{(e.verifiedAt||e.addedAt||"").slice(0,10)||"—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      }
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════
   INTEGRATIONS PAGE
══════════════════════════════════════════════════════════════ */
function IntegrationsPage({addToast,user,isSuperAdmin}) {
  const {dark}=useTheme();

  // Super Admin integrations (platform-level)
  const superAdminIntegrations=[
    {name:"Surepass API",icon:"🔐",desc:"Government data verification APIs — UAN, PAN, Aadhaar, DL, eCourt. Core verification engine.",status:"Active",color:"#0cc0df",tag:"Core"},
    {name:"Brevo Email",icon:"📧",desc:"Email delivery for OTPs, notifications and reference check emails.",status:"Active",color:"#ff3131",tag:"Core"},
    {name:"Razorpay",icon:"💳",desc:"Payment gateway for company wallet top-ups. Integrate after Razorpay approval.",status:"Pending",color:"#528FF0",tag:"Payment"},
    {name:"Cashfree",icon:"💰",desc:"Alternative payment gateway. Faster activation than Razorpay.",status:"Pending",color:"#ff914d",tag:"Payment"},
    {name:"WhatsApp Business",icon:"💬",desc:"Send BGV reports and notifications via WhatsApp to HR teams and candidates.",status:"Coming Soon",color:"#25D366",tag:"Notify"},
    {name:"Firebase",icon:"🔥",desc:"Google Cloud Firebase — Auth, Firestore, Hosting, Functions. Platform infrastructure.",status:"Active",color:"#ff914d",tag:"Core"},
  ];

  // Company/HR integrations (HRMS)
  const companyIntegrations=[
    {name:"Zoho People",icon:"🟢",desc:"Sync employee data from Zoho People HRMS. Auto-trigger BGV on new joiners.",status:"Configure",color:"#E42527",apiField:"zoho_key"},
    {name:"Keka HR",icon:"🔵",desc:"Connect Keka HRMS to auto-import employee records for batch verification.",status:"Configure",color:"#0052CC",apiField:"keka_key"},
    {name:"Darwinbox",icon:"🟣",desc:"Integrate with Darwinbox to sync headcount and trigger BGV workflows.",status:"Configure",color:"#6B4FBB",apiField:"darwinbox_key"},
    {name:"GreytHR",icon:"🟡",desc:"Connect GreytHR for seamless employee onboarding verification.",status:"Configure",color:"#F59E0B",apiField:"greythr_key"},
    {name:"SAP SuccessFactors",icon:"⚪",desc:"Enterprise HRMS integration for large organizations.",status:"Coming Soon",color:"#0070F3",apiField:"sap_key"},
    {name:"Custom API",icon:"🔧",desc:"Connect any HRMS using our REST API. Contact us for custom integration.",status:"Contact Us",color:"#64748b",apiField:"custom_key"},
  ];

  const [hrmsKeys,setHrmsKeys]=useState({});
  const [saving,setSaving]=useState("");

  const saveKey=async(field)=>{
    setSaving(field);
    addToast("HRMS key saved!","success");
    setTimeout(()=>setSaving(""),1000);
  };

  if(isSuperAdmin) return(
    <div className={dark?"bg-mesh-dark":"bg-mesh-light"} style={{minHeight:"100%",padding:"4px 0"}}>
      <div className="fu" style={{marginBottom:20}}>
        <h2 style={{fontFamily:FFD,fontWeight:800,fontSize:26,color:"var(--text)",marginBottom:4}}>Platform Integrations</h2>
        <p style={{color:"var(--muted)",fontSize:14}}>Super Admin · Manage all platform-level service integrations</p>
      </div>
      <div style={{display:"grid",gridTemplateColumns:"repeat(2,1fr)",gap:16}} className="mg1">
        {superAdminIntegrations.map((it,i)=>(
          <div key={i} className="fu glass" style={{borderRadius:14,padding:20,border:"1px solid var(--b1)"}}>
            <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:12}}>
              <div style={{display:"flex",alignItems:"center",gap:12}}>
                <div style={{width:44,height:44,borderRadius:12,background:it.color+"18",display:"flex",alignItems:"center",justifyContent:"center",fontSize:22}}>{it.icon}</div>
                <div>
                  <div style={{fontFamily:FFD,fontSize:15,fontWeight:700,color:"var(--text)"}}>{it.name}</div>
                  <div style={{fontSize:10,color:"var(--muted)",fontWeight:600}}>{it.tag}</div>
                </div>
              </div>
              <Badge color={it.status==="Active"?"var(--green)":it.status==="Pending"?"#f59e0b":"var(--muted)"} style={{fontSize:9}}>
                {it.status==="Active"?"✅ Active":it.status==="Pending"?"⏳ Pending":it.status}
              </Badge>
            </div>
            <div style={{fontSize:12,color:"var(--muted)",lineHeight:1.7}}>{it.desc}</div>
          </div>
        ))}
      </div>
    </div>
  );

  // Company/HR view — HRMS only
  return(
    <div className={dark?"bg-mesh-dark":"bg-mesh-light"} style={{minHeight:"100%",padding:"4px 0"}}>
      <div className="fu" style={{marginBottom:20}}>
        <h2 style={{fontFamily:FFD,fontWeight:800,fontSize:26,color:"var(--text)",marginBottom:4}}>HRMS Integrations</h2>
        <p style={{color:"var(--muted)",fontSize:14}}>Connect your HRMS to sync employee data and automate BGV workflows</p>
      </div>
      <div style={{background:"rgba(12,192,223,0.06)",border:"1px solid rgba(12,192,223,0.2)",borderRadius:12,padding:"14px 18px",marginBottom:20,fontSize:13,color:"var(--muted)",lineHeight:1.7}}>
        💡 <strong style={{color:"var(--text)"}}>How it works:</strong> Connect your HRMS by entering your API key below. Meitho will automatically sync new employee records and trigger BGV verification on joiners.
      </div>
      <div style={{display:"grid",gridTemplateColumns:"repeat(2,1fr)",gap:16}} className="mg1">
        {companyIntegrations.map((it,i)=>(
          <div key={i} className="fu glass" style={{borderRadius:14,padding:20,border:"1px solid var(--b1)"}}>
            <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:12}}>
              <div style={{display:"flex",alignItems:"center",gap:12}}>
                <div style={{width:44,height:44,borderRadius:12,background:it.color+"18",display:"flex",alignItems:"center",justifyContent:"center",fontSize:22}}>{it.icon}</div>
                <div style={{fontFamily:FFD,fontSize:15,fontWeight:700,color:"var(--text)"}}>{it.name}</div>
              </div>
              <Badge color={it.status==="Configure"?"var(--brand)":it.status==="Contact Us"?"#f59e0b":"var(--muted)"} style={{fontSize:9}}>{it.status}</Badge>
            </div>
            <div style={{fontSize:12,color:"var(--muted)",lineHeight:1.7,marginBottom:12}}>{it.desc}</div>
            {it.status==="Configure"&&(
              <div>
                <Label>API Key</Label>
                <div style={{display:"flex",gap:8}}>
                  <Inp type="password" value={hrmsKeys[it.apiField]||""} onChange={e=>setHrmsKeys(p=>({...p,[it.apiField]:e.target.value}))} placeholder="Enter your API key" style={{fontSize:12}}/>
                  <Btn onClick={()=>saveKey(it.apiField)} style={{padding:"8px 14px",fontSize:12,whiteSpace:"nowrap"}}>{saving===it.apiField?"Saved ✓":"Save"}</Btn>
                </div>
              </div>
            )}
            {it.status==="Contact Us"&&(
              <a href="mailto:help@meitho.com?subject=Custom HRMS Integration" style={{fontSize:12,color:"var(--brand)",fontWeight:600,textDecoration:"none"}}>📧 Contact us for custom integration →</a>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════
   COMPANY PROFILE PAGE
══════════════════════════════════════════════════════════════ */
function CompanyProfilePage({user,companies,addToast,wallets,setWallets,transactions,setTransactions,users,setUsers}) {
  const {dark}=useTheme();
  const company=companies.find(c=>c.id===user.companyId)||{};
  const [activeTab,setActiveTab]=useState("basic");
  const [editing,setEditing]=useState(false);
  const companyFormFrom=(c)=>({
    name:c.name||"",
    address:c.profile?.address||c.address||"",
    gst:c.profile?.gst||c.gst||"",
    industry:c.profile?.industry||c.industry||"",
    website:c.profile?.website||c.website||"",
    pan:c.profile?.pan||c.pan||"",
    cin:c.profile?.cin||c.cin||"",
    contactPerson:c.contactPerson||c.contact_person||"",
    phone:c.phone||c.contactPhone||"",
    email:c.contactEmail||c.email||"",
  });
  const [form,setForm]=useState(companyFormFrom(company));
  // Company data may arrive after first render, or be edited by Super Admin
  useEffect(()=>{if(!editing)setForm(companyFormFrom(company));},[company.id,JSON.stringify(company.profile||{}),company.name,company.address,company.gst,company.phone,company.contactEmail,company.contactPerson,editing]);

  const [showAddHR,setShowAddHR]=useState(false);
  const [hrForm,setHRForm]=useState({name:"",email:"",password:"",quota:100});
  const [hrLoading,setHRLoading]=useState(false);
  const [permUser,setPermUser]=useState(null);
  const [permDraft,setPermDraft]=useState({});
  const [permBusy,setPermBusy]=useState(false);
  const [budgetUser,setBudgetUser]=useState(null);
  const [budgetAmt,setBudgetAmt]=useState("");
  const [hrBudgets,setHrBudgets]=useState({});
  const PERM_MODULES=[...SEARCH_MODULES,{key:"reports",label:"Reports"}];
  const openPerms=(u)=>{setPermUser(u);setPermDraft(Object.fromEntries(PERM_MODULES.map(m=>[m.key,u.searchPermissions?.[m.key]===true])));};
  const savePerms=async()=>{
    if(!permUser)return;
    setPermBusy(true);
    try{
      await saveUserProfile(permUser.id,{searchPermissions:permDraft});
      if(setUsers)setUsers(p=>p.map(x=>x.id===permUser.id?{...x,searchPermissions:permDraft}:x));
      addToast("Permissions updated for "+(permUser.name||permUser.email),"success");
      setPermUser(null);
    }catch(e){addToast("Could not save permissions: "+e.message,"error");}
    setPermBusy(false);
  };
  const loadBudgets=async()=>{
    try{
      const list=await getCompanyHRAllocations(user.companyId);
      setHrBudgets(Object.fromEntries(list.map(a=>[a.id,a])));
    }catch(e){console.warn("Budgets load:",e);}
  };
  useEffect(()=>{if(activeTab==="hr")loadBudgets();},[activeTab]);
  const saveBudget=async()=>{
    if(!budgetUser)return;
    const amt=Math.max(0,parseInt(budgetAmt)||0);
    try{
      const prev=hrBudgets[budgetUser.id]||{};
      await setDoc(doc(db,"wallets",user.companyId,"hrAllocations",budgetUser.id),{
        userId:budgetUser.id,name:budgetUser.name||budgetUser.email||"",allocated:amt,spent:prev.spent||0,
        balance:Math.max(0,amt-(prev.spent||0)),updatedAt:new Date().toISOString(),
      },{merge:true});
      addToast(amt>0?`Budget of ₹${amt} set for ${budgetUser.name||budgetUser.email}`:"Budget cap removed — shares the full company wallet","success");
      setBudgetUser(null);setBudgetAmt("");
      loadBudgets();
    }catch(e){addToast("Could not save budget: "+e.message,"error");}
  };
  const companyHRUsers=(users||[]).filter(u=>u.companyId===user.companyId&&u.role!=="superadmin"&&u.id!==user.id);
  const wallet=wallets[user.companyId]||{balance:0};
  const tabs=[{id:"basic",label:"📋 Basic Info"},{id:"legal",label:"⚖️ Legal"},{id:"hr",label:"👥 HR Users"},{id:"wallet",label:"💰 Wallet"}];

  const saveProfile=async()=>{
    try{
      const {updateCompany}=await import("./firebase/services");
      await updateCompany(company.id,{
        name:form.name,phone:form.phone,contactPerson:form.contactPerson,contactEmail:form.email,
        profile:{address:form.address,gst:form.gst,industry:form.industry,website:form.website,pan:form.pan,cin:form.cin}
      });
      addToast("✅ Company profile updated!","success");
      setEditing(false);
    }catch(e){addToast("Update failed: "+e.message,"error");}
  };

  const createHRUser=async()=>{
    if(!hrForm.name||!hrForm.email||!hrForm.password){addToast("Fill all fields","error");return;}
    setHRLoading(true);
    try{
      const {initializeApp,getApps}=await import("firebase/app");
      const {getAuth,createUserWithEmailAndPassword,signOut}=await import("firebase/auth");
      // Use an isolated secondary Firebase app instance to create this HR
      // user's login. createUserWithEmailAndPassword auto-signs-in as the
      // new user on whatever auth instance it runs on — using the primary
      // one here would silently swap the admin's own active session over
      // to the brand-new HR account. This keeps the admin's session intact.
      const firebaseConfig={
        apiKey: "AIzaSyBAwGsTIxPxh-s4UwqJiIXge8Dc-zt6ivI",
        authDomain: "meitho.firebaseapp.com",
        projectId: "meitho",
        storageBucket: "meitho.firebasestorage.app",
      };
      const secondaryApp=getApps().find(a=>a.name==="hrCreate")||initializeApp(firebaseConfig,"hrCreate");
      const secondaryAuth=getAuth(secondaryApp);
      const cred=await createUserWithEmailAndPassword(secondaryAuth,hrForm.email,hrForm.password);
      await signOut(secondaryAuth);
      const newUser={
        id:cred.user.uid,name:hrForm.name,email:hrForm.email,
        role:"hr",companyId:user.companyId,company:company.name||"",
        status:"Active",searchQuota:hrForm.quota,usedQuota:0,
        // New HR staff start with zero search/report access — Company
        // Admin must explicitly grant each module via the Permissions popup.
        searchPermissions:{uan:false,pan:false,aadhaar:false,dl:false,ecourt:false,reports:false},
        createdAt:new Date().toISOString(),createdBy:user.id,
      };
      const {saveUserProfile}=await import("./firebase/services");
      await saveUserProfile(cred.user.uid,newUser);
      if(setUsers)setUsers(p=>[...p,newUser]);
      createNotification({
        companyId:user.companyId,targetRole:null,targetUserId:user.id,type:"hr_created",
        title:"New HR user needs permissions",
        message:`${hrForm.name} was created with no module access yet. Grant permissions from HR Users → Permissions.`,
        link:"profile",
      });
      addToast("✅ HR user "+hrForm.name+" created!","success");
      setShowAddHR(false);
      setHRForm({name:"",email:"",password:"",quota:100});
      openPerms(newUser);
    }catch(e){
      if(e.code==="auth/email-already-in-use")addToast("Email already registered","error");
      else addToast("Failed: "+e.message,"error");
    }
    setHRLoading(false);
  };

  return(
    <div className={dark?"bg-mesh-dark":"bg-mesh-light"} style={{minHeight:"100%",padding:"4px 0"}}>
      <div className="fu" style={{marginBottom:20,display:"flex",justifyContent:"space-between",alignItems:"flex-end",flexWrap:"wrap",gap:12}}>
        <div>
          <h2 style={{fontFamily:FFD,fontWeight:800,fontSize:26,color:"var(--text)",marginBottom:4}}>Company Profile</h2>
          <p style={{color:"var(--muted)",fontSize:14}}>{company.name||"Your Company"} · {company.status||"Active"}</p>
        </div>
        {activeTab==="basic"&&<Btn onClick={()=>editing?saveProfile():setEditing(true)}>{editing?"💾 Save Changes":"✏️ Edit Profile"}</Btn>}
      </div>

      <div style={{display:"flex",gap:6,marginBottom:16,flexWrap:"wrap"}}>
        {tabs.map(t=>(
          <button key={t.id} onClick={()=>{setActiveTab(t.id);setEditing(false);}} style={{padding:"7px 16px",border:"none",borderRadius:8,cursor:"pointer",fontSize:12,fontWeight:600,fontFamily:FF,background:activeTab===t.id?"var(--brand)":"var(--s2)",color:activeTab===t.id?"#fff":"var(--muted)"}}>
            {t.label}
          </button>
        ))}
      </div>

      {/* Basic Info */}
      {activeTab==="basic"&&(
        <div className="fu1 glass" style={{borderRadius:14,padding:24,border:"1px solid var(--b1)"}}>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:16}} className="mg1">
            {[
              {l:"Company Name",k:"name"},
              {l:"Industry",k:"industry"},
              {l:"Website",k:"website"},
              {l:"Address",k:"address"},
              {l:"Contact Person",k:"contactPerson"},
              {l:"Phone",k:"phone"},
              {l:"Email",k:"email"},
            ].map(({l,k})=>(
              <div key={k}>
                <div style={{fontSize:11,color:"var(--muted)",fontWeight:700,marginBottom:4,textTransform:"uppercase"}}>{l}</div>
                {editing
                  ?<Inp value={form[k]} onChange={e=>setForm(p=>({...p,[k]:e.target.value}))} style={{fontSize:13}}/>
                  :<div style={{fontSize:14,fontWeight:600,color:"var(--text)"}}>{form[k]||"—"}</div>
                }
              </div>
            ))}
          </div>
          {editing&&<div style={{display:"flex",gap:8,marginTop:16}}>
            <Btn onClick={saveProfile}>💾 Save</Btn>
            <BtnGhost onClick={()=>setEditing(false)}>Cancel</BtnGhost>
          </div>}
        </div>
      )}

      {/* Legal */}
      {activeTab==="legal"&&(
        <div className="fu1 glass" style={{borderRadius:14,padding:24,border:"1px solid var(--b1)"}}>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:16}} className="mg1">
            {[{l:"GST Number",k:"gst"},{l:"PAN",k:"pan"},{l:"CIN",k:"cin"}].map(({l,k})=>(
              <div key={k}>
                <div style={{fontSize:11,color:"var(--muted)",fontWeight:700,marginBottom:4,textTransform:"uppercase"}}>{l}</div>
                <div style={{fontSize:14,fontWeight:600,color:"var(--text)",fontFamily:FFM}}>{form[k]||"—"}</div>
              </div>
            ))}
          </div>
          <div style={{marginTop:16,fontSize:12,color:"var(--muted)"}}>To update legal details, contact <a href="mailto:help@meitho.com" style={{color:"#0cc0df"}}>help@meitho.com</a></div>
        </div>
      )}

      {permUser&&<Modal title={"Permissions — "+(permUser.name||permUser.email)} onClose={()=>setPermUser(null)}>
        <div style={{fontSize:12,color:"var(--muted)",marginBottom:12}}>Choose what this HR user can run. They only see the modules you tick.</div>
        {PERM_MODULES.map(m=>(
          <label key={m.key} style={{display:"flex",alignItems:"center",gap:10,padding:"9px 12px",background:"var(--s2)",border:"1px solid var(--b1)",borderRadius:9,marginBottom:6,cursor:"pointer",fontSize:13,color:"var(--text)"}}>
            <input type="checkbox" checked={permDraft[m.key]===true} onChange={e=>setPermDraft(p=>({...p,[m.key]:e.target.checked}))}/>{m.label}
          </label>
        ))}
        <div style={{display:"flex",gap:8,marginTop:12}}>
          <Btn onClick={savePerms}>{permBusy?<Spin/>:"Save permissions"}</Btn>
          <BtnGhost onClick={()=>setPermUser(null)}>Cancel</BtnGhost>
        </div>
      </Modal>}
      {budgetUser&&<Modal title={"Wallet share — "+(budgetUser.name||budgetUser.email)} onClose={()=>setBudgetUser(null)}>
        <div style={{fontSize:12,color:"var(--muted)",lineHeight:1.6,marginBottom:12}}>Set the most this HR user can spend from the company wallet. Their checks are charged to the company wallet, and they are blocked once they reach this amount. Leave empty or 0 for no cap.</div>
        <div style={{fontSize:12,color:"var(--muted)",marginBottom:6}}>Current: {(hrBudgets[budgetUser.id]?.allocated||0)>0?`₹${hrBudgets[budgetUser.id].spent||0} of ₹${hrBudgets[budgetUser.id].allocated} used`:"No cap"}</div>
        <Inp type="number" value={budgetAmt} onChange={e=>setBudgetAmt(e.target.value)} placeholder="e.g. 5000" style={{marginBottom:10}}/>
        <div style={{display:"flex",gap:6,marginBottom:12,flexWrap:"wrap"}}>
          {[1000,2500,5000,10000].map(n=><button key={n} onClick={()=>setBudgetAmt(String(n))} style={{background:budgetAmt===String(n)?"var(--brand)":"var(--s2)",border:"1px solid var(--b2)",borderRadius:8,color:budgetAmt===String(n)?"#fff":"var(--muted)",padding:"6px 12px",cursor:"pointer",fontWeight:700,fontSize:12,fontFamily:FF}}>₹{n.toLocaleString("en-IN")}</button>)}
        </div>
        <div style={{display:"flex",gap:8}}>
          <Btn onClick={saveBudget}>Save</Btn>
          <BtnGhost onClick={()=>setBudgetUser(null)}>Cancel</BtnGhost>
        </div>
      </Modal>}

      {/* HR Users */}
      {activeTab==="hr"&&(
        <div>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16}}>
            <div style={{fontSize:13,color:"var(--muted)"}}>{companyHRUsers.length} HR user{companyHRUsers.length!==1?"s":""}</div>
            <Btn onClick={()=>setShowAddHR(true)}>+ Add HR User</Btn>
          </div>

          {showAddHR&&<div className="fu1 glass" style={{borderRadius:14,padding:20,border:"1px solid var(--brand)30",marginBottom:16}}>
            <div style={{fontFamily:FFD,fontSize:14,fontWeight:700,color:"var(--text)",marginBottom:14}}>New HR User</div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:12}} className="mg1">
              <div><Label>Full Name *</Label><Inp value={hrForm.name} onChange={e=>setHRForm(p=>({...p,name:e.target.value}))} placeholder="Priya Sharma"/></div>
              <div><Label>Email *</Label><Inp value={hrForm.email} onChange={e=>setHRForm(p=>({...p,email:e.target.value}))} placeholder="priya@company.com"/></div>
              <div><Label>Password *</Label><Inp type="password" value={hrForm.password} onChange={e=>setHRForm(p=>({...p,password:e.target.value}))} placeholder="Min 8 chars"/></div>
              <div><Label>Search Quota</Label><Inp type="number" value={hrForm.quota} onChange={e=>setHRForm(p=>({...p,quota:parseInt(e.target.value)||100}))} placeholder="100"/></div>
            </div>
            <div style={{display:"flex",gap:8}}>
              <Btn onClick={createHRUser}>{hrLoading?<Spin/>:"Create HR User →"}</Btn>
              <BtnGhost onClick={()=>setShowAddHR(false)}>Cancel</BtnGhost>
            </div>
          </div>}

          {companyHRUsers.length===0?(
            <div className="fu2 glass" style={{borderRadius:14,padding:40,border:"1px solid var(--b1)",textAlign:"center",color:"var(--muted)"}}>
              <div style={{fontSize:32,marginBottom:12}}>👥</div>
              <div style={{fontSize:14,fontWeight:600,marginBottom:8}}>No HR users yet</div>
              <div style={{fontSize:12}}>Add HR users to let your team run BGV checks</div>
            </div>
          ):(
            <div style={{display:"flex",flexDirection:"column",gap:10}}>
              {companyHRUsers.map((u,i)=>(
                <div key={i} className="fu glass" style={{borderRadius:12,padding:"14px 18px",border:"1px solid var(--b1)",display:"flex",justifyContent:"space-between",alignItems:"center",flexWrap:"wrap",gap:10}}>
                  <div style={{display:"flex",gap:12,alignItems:"center"}}>
                    <div style={{width:36,height:36,borderRadius:9,background:"var(--brand)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:14,fontWeight:700,color:"#fff"}}>{(u.name||"H")[0]}</div>
                    <div>
                      <div style={{fontSize:14,fontWeight:700,color:"var(--text)"}}>{u.name}</div>
                      <div style={{fontSize:11,color:"var(--muted)"}}>{u.email}</div>
                    </div>
                  </div>
                  <div style={{display:"flex",gap:8,alignItems:"center"}}>
                    <Badge color="var(--green)" style={{fontSize:10}}>{u.status||"Active"}</Badge>
                    <div style={{fontSize:11,color:"var(--muted)"}}>{(()=>{const b=hrBudgets[u.id];return b&&(b.allocated||0)>0?`Budget: ₹${b.spent||0} of ₹${b.allocated} used`:"Budget: shares company wallet";})()}</div>
                    <button onClick={()=>openPerms(u)} style={{background:"rgba(12,192,223,0.08)",border:"1px solid rgba(12,192,223,0.25)",borderRadius:7,color:"#0cc0df",padding:"5px 10px",cursor:"pointer",fontSize:11,fontWeight:700,fontFamily:FF}}>🔒 Permissions</button>
                    <button onClick={()=>{setBudgetUser(u);setBudgetAmt(String(hrBudgets[u.id]?.allocated||""));}} style={{background:"var(--brand)10",border:"1px solid var(--brand)30",borderRadius:7,color:"var(--brand)",padding:"5px 10px",cursor:"pointer",fontSize:11,fontWeight:700,fontFamily:FF}}>💰 Wallet share</button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Wallet */}
      {activeTab==="wallet"&&<WalletPage user={user} company={company} wallets={wallets||{}} setWallets={setWallets||((v)=>{})} transactions={transactions||[]} setTransactions={setTransactions||((v)=>{})} addToast={addToast}/>}
    </div>
  );
}


/* ══════════════════════════════════════════════════════════════
   HRMS INTEGRATION PAGE
══════════════════════════════════════════════════════════════ */
function HRMSIntegrationPage({user,addToast}) {
  const {dark}=useTheme();
  const [config,setConfig]=useState(()=>{
    try{return JSON.parse(localStorage.getItem("meitho_hrms_config")||"{}");}catch{return {};}
  });

  const hrmsOptions=[
    {id:"greythr",name:"greytHR",icon:"🟢",desc:"Popular HRMS for Indian SMEs",fields:["subdomain","apiKey"]},
    {id:"darwinbox",name:"Darwinbox",icon:"🔵",desc:"Enterprise HRMS platform",fields:["tenantUrl","apiKey","apiSecret"]},
    {id:"keka",name:"Keka HR",icon:"🟣",desc:"Modern HR platform for India",fields:["domain","apiKey"]},
    {id:"bamboohr",name:"BambooHR",icon:"🟤",desc:"HR software for SMBs",fields:["subdomain","apiKey"]},
    {id:"sap",name:"SAP SuccessFactors",icon:"⚪",desc:"Enterprise HR suite",fields:["companyId","apiKey"]},
    {id:"csv",name:"CSV/Excel Upload",icon:"📊",desc:"Manual upload via CSV file",fields:[]},
  ];

  const save=(id,val)=>{
    const newCfg={...config,[id]:val};
    setConfig(newCfg);
    localStorage.setItem("meitho_hrms_config",JSON.stringify(newCfg));
    addToast("HRMS config saved!","success");
  };

  return(
    <div className={dark?"bg-mesh-dark":"bg-mesh-light"} style={{minHeight:"100%",padding:"4px 0"}}>
      <div className="fu" style={{marginBottom:20}}>
        <h2 style={{fontFamily:FFD,fontWeight:800,fontSize:26,color:"var(--text)",marginBottom:4}}>HRMS Integration</h2>
        <p style={{color:"var(--muted)",fontSize:14}}>Connect your HR system to sync employee data automatically</p>
      </div>

      <div style={{background:"rgba(12,192,223,0.06)",border:"1px solid rgba(12,192,223,0.2)",borderRadius:12,padding:"14px 18px",marginBottom:20,fontSize:13,color:"var(--sub)"}}>
        💡 Connect your HRMS to automatically import employee records for bulk verification. Data syncs every 24 hours.
      </div>

      <div style={{display:"grid",gridTemplateColumns:"repeat(2,1fr)",gap:16}} className="mg1">
        {hrmsOptions.map((h,i)=>{
          const isConnected=config[h.id]?.connected;
          return(
            <div key={h.id} className={"fu"+i+" glass neon-hover"} style={{borderRadius:14,padding:20,border:"1px solid "+(isConnected?"rgba(255,49,49,0.3)":"var(--b1)")}}>
              <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:12}}>
                <span style={{fontSize:28}}>{h.icon}</span>
                <div style={{flex:1}}>
                  <div style={{fontFamily:FFD,fontSize:15,fontWeight:700,color:"var(--text)"}}>{h.name}</div>
                  <div style={{fontSize:11,color:"var(--muted)"}}>{h.desc}</div>
                </div>
                <Badge color={isConnected?"var(--green)":"var(--muted)"} style={{fontSize:9}}>{isConnected?"Connected":"Not Connected"}</Badge>
              </div>

              {h.fields.length===0?(
                <div>
                  <div style={{fontSize:12,color:"var(--muted)",marginBottom:10}}>Upload CSV with employee data</div>
                  <Btn onClick={()=>addToast("Use Bulk Upload page to upload CSV","success")} style={{fontSize:12,padding:"7px 14px"}}>Go to Bulk Upload →</Btn>
                </div>
              ):(
                <div>
                  {h.fields.map(f=>(
                    <div key={f} style={{marginBottom:8}}>
                      <div style={{fontSize:10,color:"var(--muted)",fontWeight:700,marginBottom:4,textTransform:"uppercase"}}>{f}</div>
                      <Inp placeholder={"Enter "+f} defaultValue={config[h.id]?.[f]||""} onChange={e=>{setConfig(p=>({...p,[h.id]:{...p[h.id],[f]:e.target.value}}));}} style={{fontSize:12}}/>
                    </div>
                  ))}
                  <div style={{display:"flex",gap:8,marginTop:10}}>
                    <Btn onClick={()=>save(h.id,{...config[h.id],connected:true})} style={{fontSize:12,padding:"7px 14px"}}>Connect →</Btn>
                    {isConnected&&<BtnGhost onClick={()=>{save(h.id,{connected:false});addToast(h.name+" disconnected","amber");}} style={{fontSize:12,padding:"7px 14px"}}>Disconnect</BtnGhost>}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Sync status */}
      <div className="fu1 glass" style={{borderRadius:14,padding:20,border:"1px solid var(--b1)",marginTop:16}}>
        <div style={{fontFamily:FFD,fontSize:13,fontWeight:700,color:"var(--text)",marginBottom:12}}>Sync Configuration</div>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:16}} className="mg1">
          {[
            {label:"Sync Frequency",value:"Every 24 hours"},
            {label:"Last Sync",value:"Never"},
            {label:"Records Synced",value:"0"},
            {label:"Next Sync",value:"After connection"},
          ].map(s=>(
            <div key={s.label} style={{background:"var(--s2)",borderRadius:8,padding:"10px 14px"}}>
              <div style={{fontSize:10,color:"var(--muted)",fontWeight:700,textTransform:"uppercase",marginBottom:3}}>{s.label}</div>
              <div style={{fontSize:13,fontWeight:600,color:"var(--text)"}}>{s.value}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}


/* ══════════════════════════════════════════════════════════════
   FLOATING SUPPORT CHAT — Talk with Sushi
══════════════════════════════════════════════════════════════ */
const IS_PUBLIC_FORM = /^\/refcheck\/[^/]+\/(candidate|ref1|ref2)$/.test(window.location.pathname);
const IS_EMPLOYEE_PORTAL = window.location.pathname.startsWith("/employee");
const IS_ATS_CAREER = window.location.pathname.startsWith("/ats/");
const IS_FEEDBACK = window.location.pathname.startsWith("/feedback/");
const IS_ATS_JOB = /^\/ats\/[^/]+\/[^/]+/.test(window.location.pathname);
const IS_PUBLIC_PROFILE = window.location.pathname.startsWith("/p/");
const IS_TRACK = /^\/track\/[^/]+$/.test(window.location.pathname);
const IS_ADDRESS_VERIFY = window.location.pathname.startsWith("/address-verify/");
const IS_CAND_VERIFY = window.location.pathname.startsWith("/candidate-verify/");
const IS_DEMO = window.location.pathname === "/demo" || window.location.pathname.startsWith("/demo");
const IS_TERMS = window.location.pathname === "/terms";
const TERMS_VERSION = "2026-10-22";
const CAND_CONSENT_VERSION = "2026-10-22";
const IS_DL_COMPLETE = window.location.pathname === "/digilocker-complete";
const IS_PRIVACY = window.location.pathname === "/privacy";

class PublicFormErrorBoundary extends React.Component {
  constructor(props){super(props);this.state={hasError:false,error:""};}
  static getDerivedStateFromError(e){return {hasError:true,error:e.message};}
  render(){
    if(this.state.hasError){
      return(
        <div style={{minHeight:"100vh",display:"flex",alignItems:"center",justifyContent:"center",fontFamily:"sans-serif",background:"#f8fafc",padding:20}}>
          <div style={{textAlign:"center",maxWidth:440}}>
            <div style={{fontSize:48,marginBottom:16}}>⚠️</div>
            <div style={{fontSize:20,fontWeight:700,color:"#0a0a0a",marginBottom:8}}>Something went wrong</div>
            <div style={{fontSize:14,color:"#64748b",marginBottom:20}}>{this.state.error}</div>
            <button onClick={()=>window.location.reload()} style={{background:"linear-gradient(90deg,#ff3131,#ff914d)",color:"#fff",border:"none",borderRadius:8,padding:"10px 24px",cursor:"pointer",fontSize:14,fontWeight:700}}>Try Again</button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

function FloatingSupportChat({setPage}) {
  const [open,setOpen]=useState(false);
  return(
    <div style={{position:"fixed",bottom:24,right:24,zIndex:9999,display:"flex",flexDirection:"column",alignItems:"flex-end",gap:10}}>
      {open&&<div style={{width:260,background:"var(--s1)",border:"1px solid var(--b1)",borderRadius:16,boxShadow:"0 8px 32px rgba(0,0,0,0.25)",overflow:"hidden",animation:"fadeUp .2s ease"}}>
        <div style={{background:"linear-gradient(135deg,#ff3131,#ff914d)",padding:"14px 16px",display:"flex",alignItems:"center",gap:10}}>
          <div style={{width:36,height:36,borderRadius:"50%",background:"rgba(255,255,255,0.2)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:20}}>👩</div>
          <div style={{flex:1}}>
            <div style={{fontSize:13,fontWeight:700,color:"#fff"}}>Talk with Sushi</div>
            <div style={{fontSize:10,color:"rgba(255,255,255,0.8)",display:"flex",alignItems:"center",gap:4}}>
              <div style={{width:6,height:6,borderRadius:"50%",background:"#4ade80"}}/>Meitho Support
            </div>
          </div>
          <button onClick={()=>setOpen(false)} style={{background:"transparent",border:"none",color:"rgba(255,255,255,0.8)",cursor:"pointer",fontSize:18,padding:0}}>✕</button>
        </div>
        <div style={{padding:14}}>
          <div style={{background:"var(--s2)",borderRadius:10,padding:"10px 12px",marginBottom:12,fontSize:12,color:"var(--sub)",lineHeight:1.6}}>
            👋 Hi! I'm Sushi from Meitho support. How can I help you today?
          </div>
          <button onClick={()=>{setPage("support");setOpen(false);}} style={{width:"100%",background:"linear-gradient(90deg,#ff3131,#ff914d)",border:"none",borderRadius:8,color:"#fff",padding:"10px",cursor:"pointer",fontSize:12,fontWeight:700,fontFamily:FF}}>
            💬 Open Support Ticket →
          </button>
        </div>
      </div>}
      <button onClick={()=>setOpen(o=>!o)} style={{width:56,height:56,borderRadius:"50%",background:"linear-gradient(135deg,#ff3131,#ff914d)",border:"none",cursor:"pointer",boxShadow:"0 4px 20px rgba(255,49,49,0.4)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:24}} title="Talk with Sushi">
        {open?"✕":"💬"}
      </button>
    </div>
  );
}


function AppInner() {
  const {dark} = useTheme();
  const [authUser, setAuthUser] = useState(null);
  // Pre-configure Meitho Verification API token
  useEffect(()=>{
    try{
      const existing=JSON.parse(localStorage.getItem("meitho_api_config")||"{}");
      if(!existing.surepass?.token){
        localStorage.setItem("meitho_api_config",JSON.stringify({
          ...existing,
          surepass:{token:"[SECURED]",savedAt:new Date().toISOString()}
        }));
      }
    }catch(e){console.warn("Config init error:",e);}
  },[]);
  const [userProfile, setUserProfile] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [history, setHistory] = useState([]);
  const [wallets, setWallets] = useState({});

  // Load all wallets for super admin on mount
  useEffect(()=>{
    if(!userProfile)return;
    if(userProfile.role==="superadmin"){
      getAllWallets().then(all=>{
        const wMap={};
        all.forEach(w=>wMap[w.id]=w);
        setWallets(wMap);
      }).catch(()=>{});
      getAllTransactions().then(setTransactions).catch(()=>{});
    }
  },[userProfile?.role]);
  const [transactions, setTransactions] = useState([]);
  const [page, setPage] = useState("dashboard");
  const [loading, setLoading] = useState(true);
  const [toasts, setToasts] = useState([]);

  const addToast = useCallback((msg, type="info") => {
    const color=type==="success"?"#0fba81":type==="error"?"#f04f6a":type==="amber"?"#f5a623":"#4f7cff";
    const icon=type==="success"?"✓":type==="error"?"✕":type==="amber"?"⚠":"ℹ";
    const id=Date.now()+Math.random();
    setToasts(p=>[...p,{id,msg,color,icon}]);
    setTimeout(()=>setToasts(p=>p.filter(t=>t.id!==id)),3500);
  }, []);
  // Set global ref so Support pages can use addToast
  useEffect(()=>{ addToastGlobal=addToast; },[addToast]);

  // Auto logout after 5 min idle
  useEffect(()=>{
    if(!userProfile)return;
    let warnTimer,logoutTimer;
    let warned=false;
    const reset=()=>{
      clearTimeout(warnTimer);clearTimeout(logoutTimer);warned=false;
      // 29 min → show warning
      warnTimer=setTimeout(()=>{
        warned=true;
        addToast("⚠ You will be logged out in 60 seconds due to inactivity. Click anywhere to stay.","amber");
      },29*60*1000);
      // 30 min → logout
      logoutTimer=setTimeout(()=>{
        addToast("Session expired due to inactivity.","error");
        signOutUser();
      },30*60*1000);
    };
    const events=["mousedown","mousemove","keydown","scroll","touchstart","click"];
    events.forEach(e=>window.addEventListener(e,reset,true));
    reset();
    return()=>{
      clearTimeout(warnTimer);clearTimeout(logoutTimer);
      events.forEach(e=>window.removeEventListener(e,reset,true));
    };
  },[userProfile]);

  // Auth state listener
  const [authGateMessage, setAuthGateMessage] = useState("");
  useEffect(() => {
    const unsub = onAuthChange(async (fbUser) => {
      setLoading(true);
      if (fbUser) {
        try {
          const profile = await getUserProfile(fbUser.uid);
          if (profile) {
            // Gate: company (HR/admin) accounts must belong to an Active company
            // before they get real access — this is what actually enforces the
            // "super admin must approve first" rule now that accounts are
            // created at registration time instead of at approval time.
            if (profile.role !== "superadmin" && profile.companyId) {
              const companySnap = await getDoc(doc(db, "companies", profile.companyId));
              const companyStatus = companySnap.exists() ? companySnap.data().status : null;
              if (companyStatus !== "Active") {
                await signOutUser();
                setAuthGateMessage(
                  companyStatus === "Rejected"
                    ? "Your company's registration was not approved. Contact help@meitho.com for details."
                    : "Your account is pending approval. We'll notify you by email once it's active — usually within 24 hours."
                );
                setAuthUser(null); setUserProfile(null);
                setLoading(false);
                return;
              }
            }
            setAuthUser(fbUser);
            setUserProfile({...profile, id: fbUser.uid, email: fbUser.email});
          } else {
            // New user — create basic profile
            await saveUserProfile(fbUser.uid, {name: fbUser.displayName||fbUser.email, email: fbUser.email, role: "hr", credits: 0, plan: "credits"});
            const p = await getUserProfile(fbUser.uid);
            setAuthUser(fbUser);
            setUserProfile({...p, id: fbUser.uid});
          }
        } catch(e) { console.error("Profile load error:", e); }
      } else {
        setAuthUser(null); setUserProfile(null);
      }
      setLoading(false);
    });
    return unsub;
  }, []);

  // Real-time data listeners after login
  useEffect(() => {
    if (!userProfile) return;
    const unsubs = [];

    // Subscribe to employees (real-time)
    unsubs.push(subscribeToEmployees(setEmployees));

    // Subscribe to companies
    unsubs.push(subscribeToCompanies(setCompanies));

    // Subscribe to search history
    if(userProfile.role==="superadmin"){
      unsubs.push(subscribeToAllHistory(setHistory));
    } else {
      unsubs.push(subscribeToHistory(userProfile.id, setHistory));
    }

    // If admin, load all users
    if (userProfile.role === "superadmin") {
      getAllUsers().then(setAllUsers).catch(console.error);
    } else if ((userProfile.role==="admin"||userProfile.companyAdmin)&&userProfile.companyId) {
      // Company admins need their own company's users (HR staff list)
      getDocs(query(collection(db,"users"),where("companyId","==",userProfile.companyId)))
        .then(snap=>setAllUsers(snap.docs.map(d=>({id:d.id,...d.data()}))))
        .catch(console.error);
    }

    return () => unsubs.forEach(u => u && u());
  }, [userProfile?.id]);

  // Subscribe to live user profile (credits update in real-time)
  useEffect(() => {
    if (!userProfile?.id) return;
    const unsub = subscribeToUser(userProfile.id, (updated) => {
      setUserProfile(prev => ({...prev, ...updated}));
    });
    return unsub;
  }, [userProfile?.id]);

  const handleRegister = async (reg) => {
    // Create the Firebase Auth account immediately — Firebase securely hashes
    // and stores the credential itself. We must NOT keep a plaintext copy of
    // the password anywhere in Firestore or in emails.
    const fbUser = await createUser(reg.email, reg.password, reg.pocName || reg.companyName);
    const uid = fbUser.uid;

    // Auto-generate a unique career-page slug (meitho.com/ats/{slug}) so
    // every company's careers page works out of the box without needing
    // a super admin to manually configure it after approval.
    const baseSlug=(reg.companyName||"company").toLowerCase().trim().replace(/[^a-z0-9]+/g,"-").replace(/^-+|-+$/g,"")||"company";
    let slug=baseSlug;
    try{
      let attempt=0;
      while(attempt<5){
        const dupe=await getDocs(query(collection(db,"companies"),where("slug","==",slug)));
        if(dupe.empty)break;
        attempt++;
        slug=`${baseSlug}-${Math.random().toString(36).slice(2,6)}`;
      }
    }catch(e){slug=`${baseSlug}-${Date.now().toString(36).slice(-4)}`;}

    // Save registration as pending — super admin must approve before the
    // account gets real dashboard access (enforced by a status gate on login).
    const companyData = {
      name: reg.companyName,
      email: reg.email,
      status: "Pending",
      credits: 0,
      walletBalance: 0,
      verified: false,
      phone: reg.phone || "",
      ownerUid: uid,
      slug,
      registeredOn: todayStr(),
      contactPerson: reg.pocName || reg.companyName,
      contactEmail: reg.email,
      contactPhone: reg.phone || "",
      termsVersion: TERMS_VERSION,
      termsAcceptedAt: new Date().toISOString(),
      termsAcceptedBy: reg.email,
      profile: {
        address: reg.address || "",
        gst: reg.gst || "",
        industry: reg.industry || "",
        cin: "", pan: "", website: ""
      }
    };
    const companyId = await createCompany(companyData);

    await saveUserProfile(uid, {
      name: reg.pocName || reg.companyName,
      email: reg.email,
      role: "admin",
      companyAdmin: true,
      company: reg.companyName,
      companyId,
      plan: "credits",
      credits: 0,
      phone: reg.phone || "",
      designation: "Company Admin",
      suspended: false,
      registeredOn: todayStr(),
      termsAccepted: {version: TERMS_VERSION, at: new Date().toISOString(), via: "registration"},
    });

    createNotification({
      companyId:null,targetRole:"superadmin",type:"company_pending",
      title:"New company awaiting approval",
      message:`${reg.companyName} has registered and is waiting for approval.`,
      link:"approvals",
    });

    // The account exists now, but must not be usable until approved —
    // sign out immediately so the pending-approval gate (in the auth
    // listener) has nothing to let through yet.
    await signOutUser();

    // Send welcome email via Brevo — no password included, ever
    try {
      await fetch(CF_SEND_CANDIDATE, {
        method: "POST",
        headers: {"Content-Type":"application/json"},
        body: JSON.stringify({
          candidateName: reg.pocName || reg.companyName,
          candidateEmail: reg.email,
          companyName: "Meitho BGV",
          designation: "Valued Client",
          formLink: "https://meitho.com",
          hrName: "Meitho Team",
          customSubject: `Welcome to Meitho BGV — ${reg.companyName}`,
          customBody: `<div style="font-family:Arial,sans-serif;padding:24px;max-width:600px;">
            <h2 style="color:#0a0a1a;">Welcome to Meitho BGV! 🎉</h2>
            <p style="color:#475569;">Hi ${reg.pocName||reg.companyName},</p>
            <p style="color:#475569;">Thank you for registering <strong>${reg.companyName}</strong> on Meitho BGV Platform.</p>
            <p style="color:#475569;">Your account is currently <strong>pending approval</strong>. Our team will review and activate your account within 24 hours. Once approved, log in at meitho.com with the email and password you just set.</p>
            <p style="color:#475569;">Once approved you can:</p>
            <ul style="color:#475569;">
              <li>Search employee UAN, PAN, Aadhaar</li>
              <li>Run Full BGV checks</li>
              <li>Initiate Reference Checks</li>
              <li>Create HR users and allocate budgets</li>
            </ul>
            <p style="color:#475569;">Questions? Contact us at <a href="mailto:help@meitho.com">help@meitho.com</a> or WhatsApp <a href="https://wa.me/918300016265">8300016265</a></p>
            <a href="https://meitho.com" style="display:inline-block;background:linear-gradient(90deg,#ff3131,#ff914d);color:#fff;padding:12px 28px;border-radius:8px;text-decoration:none;font-weight:700;margin-top:16px;">Visit Meitho BGV →</a>
          </div>`
        })
      });
    } catch(e) { console.warn("Welcome email failed:", e); }
    addToast("Registration submitted! You'll be able to log in once approved.", "success");
  };

  if (loading) return (
    <div className={dark?"dark":"light"} style={{minHeight:"100vh",background:"var(--bg)",display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",gap:14}}>
      <div style={{width:56,height:56,borderRadius:14,background:"linear-gradient(135deg,#ff3131,#ff914d)",display:"flex",alignItems:"center",justifyContent:"center",boxShadow:"0 8px 24px rgba(255,49,49,0.35)"}}>
        <svg width="28" height="28" viewBox="0 0 20 20" fill="none"><path d="M10 2L4 5v5c0 3.55 2.57 6.87 6 7.74C13.43 16.87 16 13.55 16 10V5l-6-3z" fill="white"/><path d="M7.5 10l2 2 3-3" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
      </div>
      <div style={{fontFamily:FFD,fontWeight:900,fontSize:24,color:"var(--text)",letterSpacing:-.5}}>Meitho BGV</div>
      <Spin/>
      <p style={{color:"var(--muted)",fontSize:13,fontFamily:FF,fontWeight:500}}>Loading Meitho BGV...</p>
    </div>
  );

  if (!authUser || !userProfile) return <LandingPage onRegister={handleRegister} gateMessage={authGateMessage}/>;

  const company = companies.find(c => c.id === userProfile.companyId) || companies.find(c => c.email?.toLowerCase() === userProfile.email?.toLowerCase());
  // Auto-link companyId if found by email
  if(company && !userProfile.companyId && company.id) {
    saveUserProfile(userProfile.id, {companyId: company.id, company: company.name});
  }
  if (userProfile.role !== "superadmin" && userProfile.termsAccepted?.version !== TERMS_VERSION) {
    return <TermsGate user={userProfile} company={company} onAccepted={(t)=>setUserProfile(p=>({...p, termsAccepted:t}))}/>;
  }
  const props = {user: userProfile, employees, setEmployees, users: allUsers, setUsers: setAllUsers, companies, setCompanies, history, setHistory, wallets, setWallets, transactions, setTransactions, addToast, company};

  const renderPage = () => {
    const isSuperAdmin = userProfile.role === "superadmin";
    const isAdmin = userProfile.role === "admin" || userProfile.role === "superadmin" || userProfile.companyAdmin === true;
    const isHR = userProfile.role === "hr";
    const allProps = {...props, isSuperAdmin, isAdmin, isHR};

    if (isSuperAdmin) {
      if (page==="dashboard") return <AdminOverview {...allProps} setPage={setPage}/>;
      if (page==="approvals") return <ApprovalsPage {...allProps}/>;
      if (page==="companies") return <CompaniesAdminPage {...allProps}/>;
      if (page==="users") return <UsersPage {...allProps}/>;
      if (page==="admins") return <AdminUsersPage {...allProps}/>;
      if (page==="search") return <SearchPage {...allProps}/>;
      if (page==="refcheck") return <ReferenceCheckPage {...allProps}/>;
      if (page==="candverify") return <CandidateVerificationPage {...allProps}/>;
      if (page==="addressverify") return <AddressVerificationPage {...allProps}/>;
      if (page==="history") return <HistoryPage {...allProps}/>;
      if (page==="data") return <EmployeeDB {...allProps}/>;
      if (page==="bulkverify") return <BulkVerificationPage {...allProps} isSuperAdmin={true}/>;
      if (page==="empladmin") return <EmployeeAdminPage {...allProps}/>;
      if (page==="analytics") return <AnalyticsPage {...allProps}/>;
      if (page==="reports") return <ReportsPage {...allProps} isSuperAdmin={true}/>;
      if (page==="plans") return <PlansPage {...allProps} setPage={setPage}/>;
      if (page==="apiconfig") return <APIConfigPage addToast={addToast}/>;
      if (page==="integrations") return <IntegrationsPage {...allProps}/>;
      if (page==="support") return <SupportInboxPage user={userProfile}/>;
      if (page==="setup") return <FirebaseSetupPage/>;
      if (page==="landing") return <LandingPageEditor addToast={addToast}/>;
      if (page==="walletadmin") return <WalletAdminPage {...allProps}/>;
      if (page==="ats_overview") return <ATSOverview companies={props.companies||[]} addToast={addToast}/>;
      if (page==="ats_employee_files") return <EmployeeFilesPage {...allProps} isSuperAdmin={true}/>;
      if (page==="ats_offer_template") return <ATSOfferLetterTemplate {...allProps} setPage={setPage}/>;
    } else {
      if (page==="dashboard") return <HRDashboard {...allProps} setPage={setPage}/>;
      if (page==="search") return <SearchPage {...allProps}/>;
      if (page==="wallet") return isAdmin?<WalletPage {...allProps}/>:<AccessDenied/>;
      if (page==="hrms") return isAdmin?<HRMSIntegrationPage {...allProps}/>:<AccessDenied/>;
      if (page==="refcheck") return <ReferenceCheckPage {...allProps}/>;
      if (page==="candverify") return <CandidateVerificationPage {...allProps}/>;
      if (page==="addressverify") return <AddressVerificationPage {...allProps}/>;
      if (page==="walletadmin") return <WalletAdminPage {...allProps}/>;
      if (page==="upload") return <UploadPage {...allProps}/>;
      if (page==="bulk") return <BulkUploadPage {...allProps}/>;
      if (page==="bulkverify") return <BulkVerificationPage {...allProps} isSuperAdmin={isSuperAdmin}/>;
      if (page==="ats_dashboard") return <ATSDashboard {...allProps} isSuperAdmin={isSuperAdmin} setPage={setPage}/>;
      if (page==="ats_jobs") return <ATSJobsPage {...allProps} isSuperAdmin={isSuperAdmin} setPage={setPage}/>;
      if (page==="ats_job_create") return <ATSJobCreate {...allProps} setPage={setPage}/>;
      if (page==="ats_applications") return <ATSApplications {...allProps} isSuperAdmin={isSuperAdmin} setPage={setPage}/>;
      if (page==="ats_pipeline") return <ATSPipeline {...allProps} isSuperAdmin={isSuperAdmin} setPage={setPage}/>;
      if (page==="ats_overview") return <ATSOverview {...allProps} companies={allProps.companies||[]} addToast={addToast}/>;
      if (page==="ats_employee_files") return isAdmin?<EmployeeFilesPage {...allProps} isSuperAdmin={false}/>:<AccessDenied/>;
      if (page==="ats_approvals") return isAdmin?<CompanyOfferApprovalsPage {...allProps}/>:<AccessDenied/>;
      if (page==="ats_offer_template") return isAdmin?<ATSOfferLetterTemplate {...allProps} setPage={setPage}/>:<AccessDenied/>;
      if (page?.startsWith("ats_job_edit_")) return <ATSJobCreate {...allProps} setPage={setPage} editJobId={page.replace("ats_job_edit_","")}/>;
      if (page?.startsWith("ats_applications_")) return <ATSApplications {...allProps} isSuperAdmin={isSuperAdmin} setPage={setPage} jobId={page.replace("ats_applications_","")}/>;
      if (page?.startsWith("ats_candidate_")) return <ATSCandidateDetail {...allProps} isSuperAdmin={isSuperAdmin} setPage={setPage} candidateId={page.replace("ats_candidate_","")}/>;
      if (page==="history") return <HistoryPage {...allProps}/>;
      if (page==="analytics") return <AnalyticsPage {...allProps}/>;
      if (page==="reports") return <ReportsPage {...allProps} isSuperAdmin={isSuperAdmin}/>;
      if (page==="profile") return isAdmin?<CompanyProfilePage {...allProps}/>:<AccessDenied/>;
      if (page==="integrations") return isAdmin?<IntegrationsPage {...allProps}/>:<AccessDenied/>;
      if (page==="support") return <SupportPage user={userProfile} company={company}/>;
    }
  };

  return (
    <div className={dark?"dark":"light"} style={{display:"flex",background:"var(--bg)",minHeight:"100vh",fontFamily:FF}}>
      <Toast toasts={toasts} remove={id=>setToasts(p=>p.filter(t=>t.id!==id))}/>
      <Sidebar user={userProfile} page={page} setPage={setPage} onLogout={signOutUser} company={company}/>
      <main key={page} style={{flex:1,padding:"28px 28px 28px",overflowY:"auto",maxHeight:"100vh",minWidth:0,animation:"fadeUp .3s cubic-bezier(.22,.68,0,1.2)"}} className="main-content">
        {renderPage()}
      </main>
      {/* Floating Support Chat */}
      <FloatingSupportChat setPage={setPage}/>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════
   PUBLIC REFERENCE CHECK FORMS
══════════════════════════════════════════════════════════════ */
function PublicCandidateForm({checkId}) {
  const [dark]=useState(()=>{try{return localStorage.getItem("bgv_theme")!=="light";}catch{return false;}});
  const [form,setForm]=useState({
    fullName:"",dob:"",uan:"",pan:"",
    currentCompany:"",designation:"",doj:"",dor:"",
    lastSalary:"",ctc:"",reasonForLeaving:"",
    noticePeriod:"",consent:false
  });
  const [submitted,setSubmitted]=useState(false);
  const [loading,setLoading]=useState(false);
  const [error,setError]=useState("");
  const [checkData,setCheckData]=useState(null);

  // Load check details
  useEffect(()=>{
    fetch(CF_SAVE_REFCHECK,{
      method:"POST",headers:{"Content-Type":"application/json"},
      body:JSON.stringify({checkId,type:"getinfo",data:{}}),
    }).then(r=>r.json()).then(j=>{
      if(j.checkData)setCheckData(j.checkData);
    }).catch(()=>{});
  },[checkId]);

  const submit=async()=>{
    if(!form.fullName){setError("Please enter your full name");return;}
    if(!form.consent){setError("Please confirm consent");return;}
    setLoading(true);
    try{
      const r=await fetch(CF_SAVE_REFCHECK,{
        method:"POST",headers:{"Content-Type":"application/json"},
        body:JSON.stringify({checkId,type:"candidate",data:form}),
      });
      const j=await r.json();
      if(j.success)setSubmitted(true);
      else setError(j.error||"Submission failed");
    }catch(e){setError("Submission failed: "+e.message);}
    setLoading(false);
  };

  const bg=dark?"#06080a":"#f8fafc";
  const card=dark?"#111":"#fff";
  const text=dark?"#fff":"#0a0a0a";
  const sub=dark?"rgba(255,255,255,0.5)":"rgba(0,0,0,0.5)";
  const border=dark?"rgba(255,255,255,0.08)":"rgba(0,0,0,0.08)";

  if(submitted) return(
    <div style={{minHeight:"100vh",background:bg,display:"flex",alignItems:"center",justifyContent:"center",padding:20,fontFamily:FF}}>
      <div style={{textAlign:"center",maxWidth:440}}>
        <div style={{fontSize:64,marginBottom:16}}>✅</div>
        <div style={{fontFamily:FFD,fontSize:28,fontWeight:900,color:text,marginBottom:8}}>Thank You!</div>
        <div style={{fontSize:15,color:sub,lineHeight:1.7}}>Your details have been submitted. Your references will be contacted shortly for verification.</div>
      </div>
    </div>
  );

  return(
    <div style={{minHeight:"100vh",background:bg,padding:"40px 20px",fontFamily:FF}}>
      <div style={{maxWidth:560,margin:"0 auto"}}>
        <div style={{textAlign:"center",marginBottom:32}}>
          <div style={{display:"inline-flex",alignItems:"center",gap:10,marginBottom:12}}>
            <div style={{width:40,height:40,borderRadius:10,background:"linear-gradient(135deg,#ff3131,#ff914d)",display:"flex",alignItems:"center",justifyContent:"center"}}>
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none"><path d="M10 2L4 5v5c0 3.55 2.57 6.87 6 7.74C13.43 16.87 16 13.55 16 10V5l-6-3z" fill="white"/></svg>
            </div>
            <div style={{fontFamily:FFD,fontWeight:900,fontSize:22,color:text}}>Meitho BGV</div>
          </div>
          <div style={{fontFamily:FFD,fontSize:22,fontWeight:800,color:text,marginBottom:6}}>Employment Verification Form</div>
          <div style={{fontSize:13,color:sub}}>Please fill in your details accurately. This information will be verified.</div>
        </div>

        <div style={{background:card,borderRadius:16,padding:28,border:"1px solid "+border,boxShadow:"0 4px 24px rgba(0,0,0,0.08)"}}>
          {/* Personal Details */}
          <div style={{fontSize:11,fontWeight:700,color:"#0cc0df",letterSpacing:1.5,textTransform:"uppercase",marginBottom:14}}>Personal Details</div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:20}}>
            <div style={{gridColumn:"1/-1"}}><div style={{fontSize:11,fontWeight:700,color:sub,marginBottom:5}}>Full Name *</div><input value={form.fullName} onChange={e=>setForm(p=>({...p,fullName:e.target.value}))} placeholder="As per government ID" style={{width:"100%",padding:"9px 12px",background:dark?"rgba(255,255,255,0.05)":"#f8fafc",border:"1px solid "+border,borderRadius:8,color:text,fontSize:13,fontFamily:FF,outline:"none",boxSizing:"border-box"}}/></div>
            <div><div style={{fontSize:11,fontWeight:700,color:sub,marginBottom:5}}>Date of Birth</div><input type="date" value={form.dob} onChange={e=>setForm(p=>({...p,dob:e.target.value}))} style={{width:"100%",padding:"9px 12px",background:dark?"rgba(255,255,255,0.05)":"#f8fafc",border:"1px solid "+border,borderRadius:8,color:text,fontSize:13,fontFamily:FF,outline:"none",boxSizing:"border-box"}}/></div>
            <div><div style={{fontSize:11,fontWeight:700,color:sub,marginBottom:5}}>UAN Number</div><input value={form.uan} onChange={e=>setForm(p=>({...p,uan:e.target.value}))} placeholder="12-digit UAN" style={{width:"100%",padding:"9px 12px",background:dark?"rgba(255,255,255,0.05)":"#f8fafc",border:"1px solid "+border,borderRadius:8,color:text,fontSize:13,fontFamily:FFM,outline:"none",boxSizing:"border-box"}}/></div>
            <div><div style={{fontSize:11,fontWeight:700,color:sub,marginBottom:5}}>PAN Number</div><input value={form.pan} onChange={e=>setForm(p=>({...p,pan:e.target.value.toUpperCase()}))} placeholder="ABCDE1234F" style={{width:"100%",padding:"9px 12px",background:dark?"rgba(255,255,255,0.05)":"#f8fafc",border:"1px solid "+border,borderRadius:8,color:text,fontSize:13,fontFamily:FFM,outline:"none",boxSizing:"border-box"}}/></div>
          </div>

          {/* Employment Details */}
          <div style={{fontSize:11,fontWeight:700,color:"#0cc0df",letterSpacing:1.5,textTransform:"uppercase",marginBottom:14}}>Employment Details</div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:20}}>
            <div style={{gridColumn:"1/-1"}}><div style={{fontSize:11,fontWeight:700,color:sub,marginBottom:5}}>Current / Last Company *</div><input value={form.currentCompany} onChange={e=>setForm(p=>({...p,currentCompany:e.target.value}))} placeholder="Company name" style={{width:"100%",padding:"9px 12px",background:dark?"rgba(255,255,255,0.05)":"#f8fafc",border:"1px solid "+border,borderRadius:8,color:text,fontSize:13,fontFamily:FF,outline:"none",boxSizing:"border-box"}}/></div>
            <div style={{gridColumn:"1/-1"}}><div style={{fontSize:11,fontWeight:700,color:sub,marginBottom:5}}>Designation *</div><input value={form.designation} onChange={e=>setForm(p=>({...p,designation:e.target.value}))} placeholder="Your job title" style={{width:"100%",padding:"9px 12px",background:dark?"rgba(255,255,255,0.05)":"#f8fafc",border:"1px solid "+border,borderRadius:8,color:text,fontSize:13,fontFamily:FF,outline:"none",boxSizing:"border-box"}}/></div>
            <div><div style={{fontSize:11,fontWeight:700,color:sub,marginBottom:5}}>Date of Joining *</div><input type="date" value={form.doj} onChange={e=>setForm(p=>({...p,doj:e.target.value}))} style={{width:"100%",padding:"9px 12px",background:dark?"rgba(255,255,255,0.05)":"#f8fafc",border:"1px solid "+border,borderRadius:8,color:text,fontSize:13,fontFamily:FF,outline:"none",boxSizing:"border-box"}}/></div>
            <div><div style={{fontSize:11,fontWeight:700,color:sub,marginBottom:5}}>Date of Relieving</div><input type="date" value={form.dor} onChange={e=>setForm(p=>({...p,dor:e.target.value}))} style={{width:"100%",padding:"9px 12px",background:dark?"rgba(255,255,255,0.05)":"#f8fafc",border:"1px solid "+border,borderRadius:8,color:text,fontSize:13,fontFamily:FF,outline:"none",boxSizing:"border-box"}}/></div>
            <div><div style={{fontSize:11,fontWeight:700,color:sub,marginBottom:5}}>Last Salary (₹/month)</div><input type="number" value={form.lastSalary} onChange={e=>setForm(p=>({...p,lastSalary:e.target.value}))} placeholder="e.g. 50000" style={{width:"100%",padding:"9px 12px",background:dark?"rgba(255,255,255,0.05)":"#f8fafc",border:"1px solid "+border,borderRadius:8,color:text,fontSize:13,fontFamily:FF,outline:"none",boxSizing:"border-box"}}/></div>
            <div><div style={{fontSize:11,fontWeight:700,color:sub,marginBottom:5}}>CTC (₹/annum)</div><input type="number" value={form.ctc} onChange={e=>setForm(p=>({...p,ctc:e.target.value}))} placeholder="e.g. 600000" style={{width:"100%",padding:"9px 12px",background:dark?"rgba(255,255,255,0.05)":"#f8fafc",border:"1px solid "+border,borderRadius:8,color:text,fontSize:13,fontFamily:FF,outline:"none",boxSizing:"border-box"}}/></div>
            <div><div style={{fontSize:11,fontWeight:700,color:sub,marginBottom:5}}>Notice Period Served</div>
              <select value={form.noticePeriod} onChange={e=>setForm(p=>({...p,noticePeriod:e.target.value}))} style={{width:"100%",padding:"9px 12px",background:dark?"rgba(255,255,255,0.05)":"#f8fafc",border:"1px solid "+border,borderRadius:8,color:text,fontSize:13,fontFamily:FF,outline:"none",boxSizing:"border-box"}}>
                <option value="">Select</option><option>Yes - Full</option><option>Yes - Partial</option><option>No</option><option>Currently Employed</option>
              </select>
            </div>
            <div><div style={{fontSize:11,fontWeight:700,color:sub,marginBottom:5}}>Reason for Leaving</div>
              <select value={form.reasonForLeaving} onChange={e=>setForm(p=>({...p,reasonForLeaving:e.target.value}))} style={{width:"100%",padding:"9px 12px",background:dark?"rgba(255,255,255,0.05)":"#f8fafc",border:"1px solid "+border,borderRadius:8,color:text,fontSize:13,fontFamily:FF,outline:"none",boxSizing:"border-box"}}>
                <option value="">Select</option><option>Better Opportunity</option><option>Higher Salary</option><option>Career Growth</option><option>Relocation</option><option>Personal Reasons</option><option>Company Closure</option><option>Termination</option><option>Contract End</option>
              </select>
            </div>
          </div>

          {/* Consent */}
          <div onClick={()=>setForm(p=>({...p,consent:!p.consent}))} style={{display:"flex",gap:10,alignItems:"flex-start",cursor:"pointer",padding:"12px",background:form.consent?"rgba(255,49,49,0.06)":"rgba(0,0,0,0.02)",border:"1px solid "+(form.consent?"rgba(255,49,49,0.3)":border),borderRadius:8,marginBottom:16,userSelect:"none"}}>
            <div style={{width:18,height:18,borderRadius:4,border:"2px solid "+(form.consent?"#ff3131":border),background:form.consent?"#ff3131":"transparent",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,marginTop:1}}>
              {form.consent&&<svg width="10" height="10" viewBox="0 0 10 10"><path d="M2 5l2.5 2.5L8 3" stroke="white" strokeWidth="1.5" strokeLinecap="round"/></svg>}
            </div>
            <div style={{fontSize:12,color:sub,lineHeight:1.6}}>I confirm that all information provided is accurate and true. I consent to background verification and sharing these details with my references for verification purposes.</div>
          </div>

          {error&&<div style={{color:"#ff3131",fontSize:12,marginBottom:12,padding:"8px 12px",background:"rgba(255,49,49,0.08)",borderRadius:6}}>{error}</div>}

          <button onClick={submit} disabled={loading} style={{width:"100%",background:"linear-gradient(90deg,#ff3131,#ff914d)",border:"none",borderRadius:10,color:"#fff",padding:"13px",cursor:"pointer",fontSize:14,fontWeight:700,fontFamily:FF,boxShadow:"0 4px 14px rgba(255,49,49,0.35)"}}>
            {loading?"Submitting...":"Submit My Details →"}
          </button>
        </div>
        <div style={{textAlign:"center",marginTop:16,fontSize:11,color:sub}}>Secured by Meitho BGV · help@meitho.com</div>
      </div>
    </div>
  );
}


/* ══════════════════════════════════════════════════════════════
   PUBLIC ADDRESS VERIFICATION — meitho.com/address-verify/{id}
   Candidate takes a selfie (camera capture) and we grab their GPS location
   at that moment, compare it against the geocoded address HR entered.
   Allows up to 2 retries if too far off, then submits regardless either way.
══════════════════════════════════════════════════════════════ */
function AddressVerifyPage(){
  const checkId=window.location.pathname.split("/address-verify/")[1]?.split("/")[0]||"";
  const [data,setData]=useState(null);
  const [loading,setLoading]=useState(true);
  const [error,setError]=useState("");
  const [step,setStep]=useState(null); // "present" | "permanent" | "done"
  const [busy,setBusy]=useState(false);
  const [statusMsg,setStatusMsg]=useState("");
  const [retryWarning,setRetryWarning]=useState("");
  const fileRef=React.useRef(null);

  useEffect(()=>{
    if(!checkId)return;
    getDoc(doc(db,"address_verifications",checkId)).then(snap=>{
      if(!snap.exists()){setError("Invalid verification link.");setLoading(false);return;}
      const d={id:snap.id,...snap.data()};
      setData(d);
      const pDone=d.presentVerification?.status==="submitted",mDone=d.permanentVerification?.status==="submitted";
      if(pDone||mDone)setStep("done");
      else if(d.sameAddress)setStep("present");
      else setStep("choose");
    }).catch(()=>setError("Something went wrong loading your verification."))
    .finally(()=>setLoading(false));
  },[checkId]);

  // Ask for location as soon as the candidate opens an address step, so the
  // permission prompt appears before the camera and the fix is cached.
  useEffect(()=>{if(step==="present"||step==="permanent")getPositionSafe().catch(()=>{});},[step]);
  const pickPhoto=()=>fileRef.current?.click();

  const handlePhoto=async(e)=>{
    const file=e.target.files?.[0];
    if(!file||!data||!step||step==="done"||step==="choose")return;
    setBusy(true);setRetryWarning("");setStatusMsg("Getting your location...");
    try{
      let position=null;
      try{position=await getPositionSafe();}
      catch(pe){
        if(pe&&pe.code===1)throw pe; // explicitly denied: still required
        position=null; // GPS unavailable/timed out: submit selfie, flag for HR
      }
      const noLoc=!position;
      const capturedLat=noLoc?null:position.coords.latitude, capturedLng=noLoc?null:position.coords.longitude;

      setStatusMsg("Checking your address...");
      const targetAddress=step==="present"?data.presentAddress:data.permanentAddress;
      const geo=noLoc?null:await geocodeAddress(targetAddress).catch(()=>null);

      let distance=null, matched=false, pincodeMatched=false, stateMatched=null;
      if(geo&&!noLoc){
        distance=distanceMeters(capturedLat,capturedLng,geo.lat,geo.lng);
        matched=distance<=100;
      }

      // Location rule: the pincode (and state, when known) of where the selfie was taken
      // must match the address the candidate gave. A close GPS distance also counts.
      const targetParts=(step==="present"?data.presentParts:data.permanentParts)||null;
      const addressPincode=targetParts?.pin||extractPincode(targetAddress);
      let capturedPincode=null,capturedState="",capturedCity="";
      if(!noLoc&&(!matched||addressPincode)){
        const rg=await reverseGeocodeFull(capturedLat,capturedLng);
        capturedPincode=rg.pincode;capturedState=rg.state;capturedCity=rg.city;
        if(addressPincode&&capturedPincode&&capturedPincode===addressPincode){
          pincodeMatched=true;
          if(targetParts?.state&&capturedState){
            const a1=cvNormPlace(targetParts.state),a2=cvNormPlace(capturedState);
            stateMatched=a1===a2||a1.includes(a2)||a2.includes(a1);
          }
          if(stateMatched!==false)matched=true;
        }
      }

      const currentAttempts=(step==="present"?data.presentVerification?.attempts:data.permanentVerification?.attempts)||0;
      const newAttempts=currentAttempts+1;

      // Allow up to 2 retries (3 attempts total) if too far — after that,
      // submit whatever we have and let HR see the mismatch in the report.
      if(!matched&&!noLoc&&(geo||capturedPincode)&&newAttempts<3){
        setStatusMsg("");
        setRetryWarning((distance!=null&&capturedPincode!==addressPincode?`You seem to be about ${Math.round(distance)}m from the address`:`Your current location (PIN ${capturedPincode||"unknown"}${capturedState?", "+capturedState:""}) does not match the address you gave (PIN ${addressPincode||"unknown"})`)+`. Please try again from the correct location. (Attempt ${newAttempts}/3)`);
        await updateDoc(doc(db,"address_verifications",checkId),{
          [`${step}Verification.attempts`]:newAttempts,
        });
        setData(p=>({...p,[`${step}Verification`]:{...p[`${step}Verification`],attempts:newAttempts}}));
        setBusy(false);
        return;
      }

      setStatusMsg("Uploading...");
      const upload=await uploadAddressSelfie(checkId,step,file);
      const verification={
        status:"submitted",
        selfieUrl:upload.url,
        capturedLat,capturedLng,
        targetLat:geo?.lat??null,targetLng:geo?.lng??null, // what the address geocoded to — shown to HR so they can judge a mismatch caused by bad geocoding vs a genuine mismatch
        geocodeFailed:!geo,locationNotCaptured:noLoc,
        distanceMeters:distance,
        matched,
        pincodeMatched,stateMatched,addressPincode,capturedPincode,capturedState,capturedCity,
        attempts:newAttempts,
        submittedAt:new Date().toISOString(),
      };
      const updatePayload={[`${step}Verification`]:verification};

      // If same-address, mirror the same submission to permanentVerification too
      if(data.sameAddress&&step==="present"){
        updatePayload.permanentVerification=verification;
      }
      // Verifying either address is enough; the other one is optional
      updatePayload.status="completed";

      await updateDoc(doc(db,"address_verifications",checkId),updatePayload);
      setData(p=>({...p,...updatePayload}));

      setStep("done");
      setStatusMsg("");
    }catch(e){
      // Log the real error so it's visible in the browser console for
      // debugging — the generic message below was hiding storage/network/
      // geolocation issues from each other, making this impossible to
      // diagnose from user reports alone.
      console.error("Address verification submission failed:",e);
      let msg="Something went wrong. Please try again.";
      if(e.code===1||e.message==="User denied Geolocation"){
        msg="Location access is required to verify your address. Please allow location access in your browser/phone settings and try again.";
      }else if(e.code===2){
        msg="Couldn't determine your location (GPS unavailable). Please make sure location services are turned on and try again.";
      }else if(e.code===3){
        msg="Getting your location took too long. Please check your GPS signal and try again.";
      }else if(e.code==="storage/unauthorized"||/permission/i.test(e.message||"")){
        msg="Upload permission error — please contact support, this is a configuration issue on our end.";
      }else if(e.message){
        msg="Something went wrong: "+e.message;
      }
      setRetryWarning(msg);
      setStatusMsg("");
    }finally{
      setBusy(false);
      if(fileRef.current)fileRef.current.value="";
    }
  };

  const bg="#050a14",card="rgba(255,255,255,0.03)",border="rgba(255,255,255,0.08)",text="#f0f4ff",sub="rgba(255,255,255,0.5)";
  const G="linear-gradient(135deg,#ff3131,#ff914d)";

  if(loading)return(
    <div style={{minHeight:"100vh",background:bg,display:"flex",alignItems:"center",justifyContent:"center",color:text,fontFamily:"'Inter',sans-serif"}}>
      <div style={{textAlign:"center"}}><div style={{fontSize:32,marginBottom:12}}>⚡</div><div>Loading...</div></div>
    </div>
  );

  if(error||!data)return(
    <div style={{minHeight:"100vh",background:bg,display:"flex",alignItems:"center",justifyContent:"center",color:text,fontFamily:"'Inter',sans-serif",padding:20}}>
      <div style={{textAlign:"center"}}><div style={{fontSize:48,marginBottom:16}}>🔍</div><div style={{fontSize:18,fontWeight:700}}>{error||"Invalid link"}</div></div>
    </div>
  );

  const targetAddress=step==="present"?data.presentAddress:data.permanentAddress;
  const otherStep=!data.sameAddress&&(data.presentVerification?.status!=="submitted"?"present":(data.permanentVerification?.status!=="submitted"?"permanent":null));
  const otherLabel=otherStep==="present"?"present":"permanent";

  return(
    <div style={{minHeight:"100vh",background:bg,color:text,fontFamily:"'Inter',sans-serif",padding:"0 0 60px"}}>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&family=Sora:wght@700;800;900&display=swap');*{box-sizing:border-box;margin:0;padding:0;}`}</style>

      <div style={{background:G,padding:"20px 5%",marginBottom:32}}>
        <div style={{maxWidth:480,margin:"0 auto"}}>
          <div style={{fontFamily:"'Sora',sans-serif",fontSize:16,fontWeight:900,color:"#fff"}}>Meitho — Address Verification</div>
          <div style={{fontSize:12,color:"rgba(255,255,255,0.85)",marginTop:2}}>{data.candidateName}</div>
        </div>
      </div>

      <div style={{maxWidth:480,margin:"0 auto",padding:"0 5%"}}>
        {step==="done"?(
          <div style={{textAlign:"center",padding:"40px 0"}}>
            <div style={{width:80,height:80,borderRadius:"50%",background:"rgba(16,185,129,0.1)",border:"2px solid rgba(16,185,129,0.3)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:36,margin:"0 auto 24px"}}>✅</div>
            <h1 style={{fontFamily:"'Sora',sans-serif",fontSize:24,fontWeight:900,marginBottom:12}}>Verification Complete!</h1>
            <p style={{color:sub,lineHeight:1.7}}>Thank you. Your address verification has been submitted successfully.</p>
            {otherStep&&<div style={{marginTop:24,background:card,border:`1px solid ${border}`,borderRadius:14,padding:18}}>
              <div style={{fontSize:12.5,color:sub,marginBottom:12,lineHeight:1.6}}>Optional: if you are also at your <strong style={{color:text}}>{otherLabel} address</strong>, you can verify it too.</div>
              <button onClick={()=>{setRetryWarning("");setStep(otherStep);}} style={{background:"transparent",border:`1px solid ${border}`,borderRadius:10,color:text,padding:"10px 18px",cursor:"pointer",fontSize:13,fontWeight:600,fontFamily:"inherit"}}>Verify my {otherLabel} address</button>
            </div>}
          </div>
        ):step==="choose"?(
          <>
            <div style={{fontSize:14,color:text,lineHeight:1.7,marginBottom:16}}>You can verify <strong>either</strong> your present or your permanent address. Choose the address where you are right now — you only need to verify one.</div>
            {[["present","📍 Present Address",data.presentAddress],["permanent","🏠 Permanent Address",data.permanentAddress]].map(([k,t,a])=>(
              <button key={k} onClick={()=>{setRetryWarning("");setStep(k);}} style={{display:"block",width:"100%",textAlign:"left",background:card,border:`1px solid ${border}`,borderRadius:16,padding:18,marginBottom:14,cursor:"pointer",color:text,fontFamily:"inherit"}}>
                <div style={{fontSize:11,fontWeight:700,color:sub,textTransform:"uppercase",letterSpacing:1,marginBottom:6}}>{t}</div>
                <div style={{fontSize:14,lineHeight:1.6,marginBottom:10}}>{a}</div>
                <div style={{fontSize:13,fontWeight:700,color:"#ff914d"}}>I'm here — verify this address →</div>
              </button>
            ))}
          </>
        ):(
          <>
            <div style={{background:card,border:`1px solid ${border}`,borderRadius:16,padding:20,marginBottom:20}}>
              <div style={{fontSize:11,fontWeight:700,color:sub,textTransform:"uppercase",letterSpacing:1,marginBottom:8}}>
                {step==="present"?"📍 Present Address":"🏠 Permanent Address"}
              </div>
              <div style={{fontSize:14,color:text,lineHeight:1.6}}>{targetAddress}</div>
            </div>

            <div style={{background:card,border:`1px solid ${border}`,borderRadius:16,padding:24,textAlign:"center"}}>
              <div style={{fontSize:13,color:sub,marginBottom:16,lineHeight:1.6}}>
                Please take a selfie <strong style={{color:text}}>while standing at this address</strong>. We'll capture your location at the same time to verify it matches.
              </div>
              <div style={{fontSize:11,color:sub,marginBottom:16,background:"rgba(255,255,255,0.04)",borderRadius:8,padding:"8px 10px"}}>
                💡 If the rear camera opens instead of the front one, tap the flip-camera icon on your camera screen before taking the photo.
              </div>
              <input ref={fileRef} type="file" accept="image/*" capture onChange={handlePhoto} style={{display:"none"}}/>
              <button onClick={pickPhoto} disabled={busy} style={{background:G,border:"none",borderRadius:12,color:"#fff",padding:"16px 32px",cursor:"pointer",fontSize:14,fontWeight:700,fontFamily:"inherit",width:"100%"}}>
                {busy?(statusMsg||"Processing..."):"📸 Take Selfie & Verify Location"}
              </button>
              {retryWarning&&(
                <div style={{marginTop:14,background:"rgba(245,158,11,0.1)",border:"1px solid rgba(245,158,11,0.3)",borderRadius:10,padding:"10px 14px",fontSize:12,color:"#f59e0b",textAlign:"left"}}>
                  ⚠ {retryWarning}
                </div>
              )}
            </div>
            {(data.presentVerification?.status==="submitted"||data.permanentVerification?.status==="submitted")&&<div style={{textAlign:"center",marginTop:14}}>
              <button onClick={()=>setStep("done")} style={{background:"none",border:"none",color:sub,textDecoration:"underline",cursor:"pointer",fontSize:12,fontFamily:"inherit"}}>Skip — I'm done</button>
            </div>}
            <div style={{textAlign:"center",marginTop:16,fontSize:11,color:"rgba(255,255,255,0.3)"}}>
              Your location is only used to verify this address and is not stored for any other purpose.
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function PublicReferenceForm({checkId,refNum}) {
  const [dark]=useState(()=>{try{return localStorage.getItem("bgv_theme")!=="light";}catch{return false;}});
  const [candidateInfo,setCandidateInfo]=useState(null);
  const [submitted,setSubmitted]=useState(false);
  const [loading,setLoading]=useState(true);
  const [submitting,setSubmitting]=useState(false);
  const [error,setError]=useState("");

  // Per-field verification state: {field: {status: "true"|"false"|"partial", correction: ""}}
  const [verifications,setVerifications]=useState({});
  const [ratings,setRatings]=useState({behaviour:0,ethical:0,professional:0,rehire:"",comments:""});

  const setVerify=(field,key,val)=>setVerifications(p=>({...p,[field]:{...p[field],[key]:val}}));

  const loadData=()=>{
    setLoading(true);
    fetch(CF_SAVE_REFCHECK,{
      method:"POST",headers:{"Content-Type":"application/json"},
      body:JSON.stringify({checkId,type:"getinfo",data:{}}),
    })
    .then(r=>r.json())
    .then(j=>{
      if(j.checkData){
        const d=j.checkData;
        const cdAll=d.candidate_data||{};
        const cd=(Array.isArray(cdAll.companies)&&cdAll.companies[refNum-1])||cdAll;
        setCandidateInfo({
          name:d.candidateName||"",
          designation:cd.designation||d.designation||"",
          company:cd.currentCompany||cd.company||"",
          doj:cd.doj||"",
          dor:cd.dor||"",
          lastSalary:cd.lastSalary||"",
          ctc:cd.ctc||"",
          noticePeriod:cd.noticePeriod||"",
          reasonForLeaving:cd.reasonForLeaving||"",
          uan:cd.uan||"",
          pan:cd.pan||"",
          employeeId:cd.employeeId||"",
          docs:cd.docs||{},
        });
      }
      setLoading(false);
    })
    .catch(e=>{console.warn("Load check:",e);setLoading(false);});
  };

  useEffect(()=>{loadData();},[checkId]);

  const submit=async()=>{
    if(!ratings.behaviour||!ratings.ethical||!ratings.professional){setError("Please provide all star ratings");return;}
    if(!ratings.rehire){setError("Please answer the rehire question");return;}
    setSubmitting(true);
    try{
      const r=await fetch(CF_SAVE_REFCHECK,{
        method:"POST",headers:{"Content-Type":"application/json"},
        body:JSON.stringify({checkId,type:"ref"+refNum,data:{...ratings,verifications}}),
      });
      const j=await r.json();
      if(j.success)setSubmitted(true);
      else setError(j.error||"Submission failed. Please try again.");
    }catch(e){setError("Submission failed: "+e.message);}
    setSubmitting(false);
  };

  const bg=dark?"#06080a":"#f8fafc";
  const card=dark?"#111":"#fff";
  const text=dark?"#fff":"#0a0a0a";
  const sub=dark?"rgba(255,255,255,0.5)":"rgba(0,0,0,0.5)";
  const border=dark?"rgba(255,255,255,0.08)":"rgba(0,0,0,0.1)";
  const inputStyle={width:"100%",padding:"8px 11px",background:dark?"rgba(255,255,255,0.06)":"#f8fafc",border:"1px solid "+border,borderRadius:7,color:text,fontSize:12,fontFamily:FF,outline:"none",boxSizing:"border-box"};

  if(submitted) return(
    <div style={{minHeight:"100vh",background:bg,display:"flex",alignItems:"center",justifyContent:"center",padding:20,fontFamily:FF}}>
      <div style={{textAlign:"center",maxWidth:440}}>
        <div style={{fontSize:64,marginBottom:16}}>✅</div>
        <div style={{fontFamily:FFD,fontSize:28,fontWeight:900,color:text,marginBottom:8}}>Thank You!</div>
        <div style={{fontSize:15,color:sub,lineHeight:1.7}}>Your reference has been submitted. Your honest feedback helps build a trustworthy workplace.</div>
      </div>
    </div>
  );

  if(loading) return(
    <div style={{minHeight:"100vh",background:bg,display:"flex",alignItems:"center",justifyContent:"center",fontFamily:FF}}>
      <div style={{textAlign:"center",color:sub}}>
        <div style={{fontSize:32,marginBottom:12}}>⏳</div>
        <div>Loading...</div>
      </div>
    </div>
  );

  const StarRating=({field,label})=>(
    <div style={{marginBottom:18}}>
      <div style={{fontSize:12,fontWeight:700,color:sub,marginBottom:8,textTransform:"uppercase",letterSpacing:.5}}>{label}</div>
      <div style={{display:"flex",gap:8,alignItems:"center"}}>
        {[1,2,3,4,5].map(n=>(
          <button key={n} onClick={()=>setRatings(p=>({...p,[field]:n}))} style={{width:40,height:40,borderRadius:8,border:"2px solid "+(ratings[field]>=n?"#ff914d":border),background:ratings[field]>=n?"rgba(255,145,77,0.15)":"transparent",cursor:"pointer",fontSize:20,transition:"all .15s"}}>
            {ratings[field]>=n?"⭐":"☆"}
          </button>
        ))}
        {ratings[field]>0&&<span style={{fontSize:13,color:"#ff914d",fontWeight:700,marginLeft:4}}>{ratings[field]}/5</span>}
      </div>
    </div>
  );

  // Field verification with correction box - using stable keys to prevent re-render focus loss
  const VerifyField=({label,value,field})=>{
    const v=verifications[field]||{};
    const statusColor=v.status==="true"?"#ff3131":v.status==="false"?"#ef4444":v.status==="partial"?"#f59e0b":border;
    const statusBg=v.status==="true"?"rgba(255,49,49,0.06)":v.status==="false"?"rgba(239,68,68,0.06)":v.status==="partial"?"rgba(245,158,11,0.06)":"transparent";
    return(
      <div style={{background:statusBg,border:"1.5px solid "+statusColor,borderRadius:10,padding:"12px 14px",marginBottom:10,transition:"background .2s,border-color .2s"}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",flexWrap:"wrap",gap:8,marginBottom:8}}>
          <div style={{flex:1,minWidth:0}}>
            <div style={{fontSize:10,color:sub,fontWeight:700,textTransform:"uppercase",letterSpacing:.5,marginBottom:3}}>{label}</div>
            <div style={{fontSize:13,fontWeight:700,color:text,wordBreak:"break-word"}}>{value||"Not provided"}</div>
          </div>
          <div style={{display:"flex",gap:5,flexShrink:0,flexWrap:"wrap"}}>
            {[{val:"true",label:"✓ True",c:"#ff3131"},{val:"false",label:"✗ False",c:"#ef4444"},{val:"partial",label:"~ Partial",c:"#f59e0b"}].map(opt=>(
              <button key={opt.val} onClick={()=>setVerify(field,"status",v.status===opt.val?"":opt.val)} style={{padding:"5px 9px",borderRadius:6,border:"1.5px solid "+(v.status===opt.val?opt.c:border),background:v.status===opt.val?opt.c+"22":"transparent",color:v.status===opt.val?opt.c:sub,cursor:"pointer",fontSize:10,fontWeight:600,fontFamily:FF}}>
                {opt.label}
              </button>
            ))}
          </div>
        </div>
        {(v.status==="false"||v.status==="partial")&&(
          <div style={{marginTop:8}}>
            <div style={{fontSize:10,color:sub,fontWeight:700,marginBottom:4,textTransform:"uppercase"}}>Enter Correct Value</div>
            <textarea 
              key={field+"_correction"}
              defaultValue={v.correction||""}
              onBlur={e=>setVerify(field,"correction",e.target.value)}
              placeholder={"Enter correct "+label.toLowerCase()+"..."}
              rows={2}
              style={{...inputStyle,resize:"none"}}
            />
          </div>
        )}
      </div>
    );
  };

  const fields=[
    {label:"Company",value:candidateInfo?.company,field:"company"},
    {label:"Designation",value:candidateInfo?.designation,field:"designation"},
    {label:"Date of Joining",value:candidateInfo?.doj,field:"doj"},
    {label:"Date of Relieving",value:candidateInfo?.dor||"Currently Employed",field:"dor"},
    {label:"Last Salary (Monthly)",value:candidateInfo?.lastSalary?`₹${parseInt(candidateInfo.lastSalary).toLocaleString("en-IN")}/month`:"",field:"lastSalary"},
    {label:"CTC (Annual)",value:candidateInfo?.ctc?`₹${parseInt(candidateInfo.ctc).toLocaleString("en-IN")}/year`:"",field:"ctc"},
    {label:"Notice Period",value:candidateInfo?.noticePeriod,field:"noticePeriod"},
    {label:"Reason for Leaving",value:candidateInfo?.reasonForLeaving,field:"reasonForLeaving"},
  ].filter(f=>f.value&&f.value!=="₹0/month"&&f.value!=="₹0/year");

  return(
    <div style={{minHeight:"100vh",background:bg,padding:"32px 16px",fontFamily:FF}}>
      <div style={{maxWidth:600,margin:"0 auto"}}>
        {/* Header */}
        <div style={{textAlign:"center",marginBottom:24}}>
          <div style={{display:"inline-flex",alignItems:"center",gap:10,marginBottom:12}}>
            <div style={{width:38,height:38,borderRadius:9,background:"linear-gradient(135deg,#ff3131,#ff914d)",display:"flex",alignItems:"center",justifyContent:"center"}}>
              <svg width="18" height="18" viewBox="0 0 20 20" fill="none"><path d="M10 2L4 5v5c0 3.55 2.57 6.87 6 7.74C13.43 16.87 16 13.55 16 10V5l-6-3z" fill="white"/></svg>
            </div>
            <div style={{fontFamily:FFD,fontWeight:900,fontSize:20,color:text}}>Meitho BGV</div>
          </div>
          <div style={{fontFamily:FFD,fontSize:20,fontWeight:800,color:text,marginBottom:4}}>Reference Check — Form {refNum}</div>
          {candidateInfo?.name&&<div style={{fontSize:13,color:sub}}>Verifying <strong style={{color:text}}>{candidateInfo.name}</strong>'s employment claims</div>}
        </div>

        {/* No candidate data yet */}
        {!candidateInfo?.company&&(
          <div style={{background:card,borderRadius:14,padding:20,border:"1px solid rgba(255,145,77,0.3)",marginBottom:16,textAlign:"center"}}>
            <div style={{fontSize:32,marginBottom:8}}>⏳</div>
            <div style={{fontSize:14,fontWeight:700,color:"#ff914d",marginBottom:6}}>Candidate Details Pending</div>
            <div style={{fontSize:12,color:sub,marginBottom:14}}>The candidate hasn't submitted their employment details yet. You can still proceed with ratings below, or refresh to check again.</div>
            <button onClick={loadData} style={{background:"#ff914d",border:"none",borderRadius:7,color:"#fff",padding:"8px 20px",cursor:"pointer",fontSize:12,fontWeight:700,fontFamily:FF}}>🔄 Refresh</button>
          </div>
        )}

        {/* Candidate fields to verify */}
        {fields.length>0&&(
          <div style={{background:card,borderRadius:14,padding:20,border:"1px solid "+border,marginBottom:16,boxShadow:"0 2px 12px rgba(0,0,0,0.06)"}}>
            <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:16}}>
              <div style={{width:28,height:28,borderRadius:7,background:"rgba(12,192,223,0.12)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:14}}>📋</div>
              <div>
                <div style={{fontSize:13,fontWeight:700,color:text}}>Verify Candidate Claims</div>
                <div style={{fontSize:11,color:sub}}>Mark each field as True, False, or Partially True. If false, enter correct value.</div>
              </div>
            </div>
            {fields.map(f=><VerifyField key={f.field} {...f}/>)}
          </div>
        )}

        {/* Documents the candidate uploaded */}
        {candidateInfo&&CV_REF_DOCS.some(d=>candidateInfo.docs?.[d.k]?.url)&&(
          <div style={{background:card,borderRadius:14,padding:20,border:"1px solid "+border,marginBottom:16}}>
            <div style={{fontSize:13,fontWeight:700,color:text,marginBottom:4}}>📎 Documents provided by the candidate</div>
            <div style={{fontSize:11,color:sub,marginBottom:12}}>Open these to confirm the details above.</div>
            <div style={{display:"flex",flexWrap:"wrap",gap:8}}>
              {CV_REF_DOCS.filter(d=>candidateInfo.docs?.[d.k]?.url).map(d=>(
                <a key={d.k} href={candidateInfo.docs[d.k].url} target="_blank" rel="noopener noreferrer" style={{fontSize:12,fontWeight:600,color:"#0cc0df",border:"1px solid "+border,borderRadius:8,padding:"7px 11px",textDecoration:"none"}}>📄 {d.label}</a>
              ))}
            </div>
          </div>
        )}

        {/* Ratings */}
        <div style={{background:card,borderRadius:14,padding:20,border:"1px solid "+border,marginBottom:16,boxShadow:"0 2px 12px rgba(0,0,0,0.06)"}}>
          <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:16}}>
            <div style={{width:28,height:28,borderRadius:7,background:"rgba(255,145,77,0.12)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:14}}>⭐</div>
            <div>
              <div style={{fontSize:13,fontWeight:700,color:text}}>Performance Ratings</div>
              <div style={{fontSize:11,color:sub}}>Rate the candidate from 1-5 stars</div>
            </div>
          </div>
          <StarRating field="behaviour" label="Behaviour & Attitude"/>
          <StarRating field="ethical" label="Ethical Conduct"/>
          <StarRating field="professional" label="Professional Skills"/>

          {/* Rehire */}
          <div style={{marginBottom:18}}>
            <div style={{fontSize:12,fontWeight:700,color:sub,marginBottom:8,textTransform:"uppercase",letterSpacing:.5}}>Would You Rehire This Person?</div>
            <div style={{display:"flex",gap:10}}>
              {[{v:"Yes",c:"#ff3131"},{v:"No",c:"#ef4444"},{v:"Maybe",c:"#f59e0b"}].map(opt=>(
                <button key={opt.v} onClick={()=>setRatings(p=>({...p,rehire:opt.v}))} style={{flex:1,padding:"10px",border:"2px solid "+(ratings.rehire===opt.v?opt.c:border),borderRadius:8,background:ratings.rehire===opt.v?opt.c+"18":"transparent",color:ratings.rehire===opt.v?opt.c:sub,cursor:"pointer",fontSize:13,fontWeight:700,fontFamily:FF,transition:"all .15s"}}>
                  {opt.v==="Yes"?"✓ Yes":opt.v==="No"?"✗ No":"~ Maybe"}
                </button>
              ))}
            </div>
          </div>

          {/* Comments */}
          <div>
            <div style={{fontSize:12,fontWeight:700,color:sub,marginBottom:8,textTransform:"uppercase",letterSpacing:.5}}>Additional Comments (Optional)</div>
            <textarea value={ratings.comments} onChange={e=>setRatings(p=>({...p,comments:e.target.value}))} placeholder="Any additional feedback about the candidate's performance, work ethic, or character..." rows={4} style={{...inputStyle,resize:"vertical"}}/>
          </div>
        </div>

        {error&&<div style={{color:"#ef4444",fontSize:13,marginBottom:12,padding:"10px 14px",background:"rgba(239,68,68,0.08)",borderRadius:8,border:"1px solid rgba(239,68,68,0.2)"}}>{error}</div>}

        <button onClick={submit} disabled={submitting} style={{width:"100%",background:"linear-gradient(90deg,#ff3131,#ff914d)",border:"none",borderRadius:12,color:"#fff",padding:"14px",cursor:"pointer",fontSize:15,fontWeight:700,fontFamily:FF,boxShadow:"0 4px 16px rgba(255,49,49,0.35)",opacity:submitting?0.7:1}}>
          {submitting?"Submitting...":"Submit Reference →"}
        </button>

        <div style={{textAlign:"center",marginTop:16,fontSize:11,color:sub}}>Secured by Meitho BGV · help@meitho.com · meitho.com</div>
      </div>
    </div>
  );
}


function TermsGate({user,company,onAccepted}) {
  const [agree,setAgree]=useState(false);
  const [busy,setBusy]=useState(false);
  const [err,setErr]=useState("");
  const isAdminUser=user.role==="admin"||user.companyAdmin===true;
  const accept=async()=>{
    if(!agree){setErr("Please tick the box to continue.");return;}
    setBusy(true);setErr("");
    const t={version:TERMS_VERSION,at:new Date().toISOString(),via:"login"};
    try{
      await saveUserProfile(user.id,{termsAccepted:t});
      if(isAdminUser&&company?.id){
        try{await updateDoc(doc(db,"companies",company.id),{termsVersion:TERMS_VERSION,termsAcceptedAt:t.at,termsAcceptedBy:user.email||""});}catch(e){console.warn("Company terms record failed:",e);}
      }
      onAccepted(t);
    }catch(e){setErr("Could not record your acceptance: "+e.message);}
    setBusy(false);
  };
  return(
    <div style={{minHeight:"100vh",background:"#06080f",display:"flex",alignItems:"center",justifyContent:"center",padding:20,fontFamily:FF,color:"#fff"}}>
      <div style={{maxWidth:520,width:"100%",background:"rgba(255,255,255,0.04)",border:"1px solid rgba(255,255,255,0.1)",borderRadius:16,padding:"28px 26px"}}>
        <div style={{fontFamily:FFD,fontSize:22,fontWeight:800,marginBottom:6}}>Terms & Conditions</div>
        <div style={{fontSize:13,color:"rgba(255,255,255,0.6)",lineHeight:1.7,marginBottom:14}}>Before you continue, please review and accept the Meitho Terms & Conditions (version {TERMS_VERSION}). In short:</div>
        <ul style={{fontSize:13,color:"rgba(255,255,255,0.75)",lineHeight:1.8,paddingLeft:18,margin:"0 0 16px"}}>
          <li>You will run verifications only with the candidate's consent and only for lawful hiring purposes.</li>
          <li>Your company is the data fiduciary; Meitho processes data on your instructions under the DPDP Act, 2023.</li>
          <li>Wallet recharges attract 18% GST, which is not added to your wallet balance.</li>
          <li>Reports are based on third-party sources and must not be the sole basis of a hiring decision.</li>
        </ul>
        <a href="/terms" target="_blank" rel="noreferrer" style={{color:"#0cc0df",fontSize:13,fontWeight:600,textDecoration:"none"}}>Read the full Terms & Conditions →</a>
        <label style={{display:"flex",gap:10,alignItems:"flex-start",marginTop:18,cursor:"pointer",fontSize:13,color:"rgba(255,255,255,0.8)",lineHeight:1.6}}>
          <input type="checkbox" checked={agree} onChange={e=>setAgree(e.target.checked)} style={{marginTop:3}}/>
          <span>I have read and agree to the Terms & Conditions and Privacy Policy{isAdminUser?` on behalf of ${company?.name||"my company"}`:""}. I understand this is a legally binding electronic acceptance.</span>
        </label>
        {err&&<div style={{color:"#ff6b6b",fontSize:12,marginTop:10}}>{err}</div>}
        <div style={{display:"flex",gap:10,marginTop:18}}>
          <button onClick={accept} disabled={busy} style={{flex:1,padding:"12px",border:"none",borderRadius:10,background:"linear-gradient(90deg,#ff3131,#ff914d)",color:"#fff",fontWeight:700,fontSize:14,cursor:"pointer",fontFamily:FF,opacity:busy?.7:1}}>{busy?"Saving…":"I Agree & Continue"}</button>
          <button onClick={()=>signOutUser()} style={{padding:"12px 16px",border:"1px solid rgba(255,255,255,0.2)",borderRadius:10,background:"transparent",color:"rgba(255,255,255,0.7)",fontSize:13,cursor:"pointer",fontFamily:FF}}>Sign out</button>
        </div>
      </div>
    </div>
  );
}

function TermsPage() {
  const sections=[
    {title:"1. Parties and Acceptance",body:"These Terms & Conditions (\"Terms\") form a binding agreement between Goport Delivery Private Limited, which operates the Meitho platform at meitho.com (\"Meitho\", \"we\"), and the company or organisation registering for or using the platform (the \"Client\"). By registering, ticking the acceptance box or using the platform, an authorised person accepts these Terms on the Client's behalf. This electronic acceptance, recorded with date and time, is valid and binding under the Information Technology Act, 2000. If you do not agree, please do not use the platform."},
    {title:"2. Services",body:"Meitho provides background verification and recruitment tools, including UAN / employment history, PAN, Aadhaar (through DigiLocker), driving licence, court and criminal record search, education and reference checks, address verification by selfie and location, and an applicant tracking system (ATS). Checks are run against government and third-party sources through our service providers. Availability of a source is outside our control."},
    {title:"3. Accounts and Users",body:"The Client is responsible for all activity under its accounts. The Company Admin creates HR users, decides what each user may access and sets any spending limits. Login credentials must be kept confidential and shared with no one. Tell us immediately at help@meitho.com if you suspect unauthorised use."},
    {title:"4. Client Responsibilities and Candidate Consent",body:"The Client must (a) use the platform only for lawful employment-related verification; (b) obtain the candidate's free, specific and informed consent before any check, and give the candidate the notice required by law; (c) not verify anyone who has not consented; (d) not discriminate on any ground prohibited by law; and (e) give the candidate a fair chance to explain any adverse result before acting on it. Where the Client uses the Meitho candidate link, the consent is captured and time-stamped by Meitho on the Client's behalf, but responsibility for lawful use of the result remains with the Client."},
    {title:"5. Data Protection (DPDP Act, 2023)",body:"For candidate data, the Client acts as the Data Fiduciary and Meitho acts as its Data Processor. Meitho processes personal data only to deliver the requested verification and on the Client's documented instructions, applies reasonable security safeguards (including encryption in transit and at rest), limits access to authorised personnel, and will notify the Client without undue delay on becoming aware of a personal data breach affecting its data. We use trusted sub-processors, such as cloud hosting, identity-verification, payment and email providers, who are bound to protect the data. Candidate data is retained only as long as needed for the verification, the Client's lawful requirements and legal obligations, and is deleted or anonymised on written request, subject to law. See our Privacy Policy for details."},
    {title:"6. Fees, Wallet and Taxes",body:"Services are prepaid through a wallet. The price per check is as shown in the Client's dashboard or as separately agreed in writing; custom prices agreed with a Client apply to that Client. Wallet recharges are charged with GST at the applicable rate (currently 18% IGST); only the recharge amount, excluding GST, is credited to the wallet. A tax invoice is issued for each successful recharge. A check that returns a result, including a negative or \"no record found\" result, is charged. A check that fails because of a technical or source error is not charged or is refunded to the wallet. Wallet credits are non-transferable and are not refundable in cash except as required by law or agreed in writing."},
    {title:"7. Accuracy and Reliance",body:"Reports reflect information available from the sources on the date of the check and the details provided by the candidate. Sources may be incomplete, delayed or contain errors, and name-based searches can return partial matches. Meitho does not guarantee that any report is complete or error-free, and reports are an aid to, and must not be the sole basis for, a hiring decision. The Client is responsible for its own decisions."},
    {title:"8. Prohibited Uses",body:"The Client must not use the platform for any unlawful purpose; verify any person without consent; resell or white-label the services without our written permission; attempt to access another Client's data; probe, scrape, reverse engineer or overload the platform; or upload malicious code."},
    {title:"9. Confidentiality and Intellectual Property",body:"Each party will keep the other's non-public information confidential and use it only for these Terms. Meitho and its licensors own the platform, software, design and brand. The Client receives a limited, non-exclusive, non-transferable right to use the platform during the term. Reports generated for the Client may be used by the Client for its internal hiring purposes."},
    {title:"10. Suspension and Termination",body:"Either party may end the arrangement by written notice. We may suspend or terminate access immediately if the Client breaches these Terms, misuses candidate data or fails to pay. On termination the Client's access ends, and unused wallet balance will be handled as set out in section 6 or in any written agreement."},
    {title:"11. Limitation of Liability",body:"To the maximum extent permitted by law, Meitho is not liable for indirect, incidental, special or consequential loss, or for loss of profit, business or reputation. Meitho's total liability for any claim relating to a particular check or service is limited to the fees paid by the Client for that check or service. Nothing in these Terms excludes liability that cannot be excluded by law."},
    {title:"12. Indemnity",body:"The Client will indemnify Meitho against claims, losses and costs arising from the Client's failure to obtain valid candidate consent, its unlawful use of reports or its breach of these Terms."},
    {title:"13. Managed-Service Clients",body:"Where Meitho runs verifications for a Client as a managed service under a separate written agreement, that agreement governs scope, pricing and turnaround, and these Terms apply to the extent not inconsistent with it."},
    {title:"14. Governing Law and Disputes",body:"These Terms are governed by the laws of India. Courts at Chennai, Tamil Nadu have exclusive jurisdiction, after the parties have first tried in good faith to resolve the dispute by discussion."},
    {title:"15. Changes to These Terms",body:"We may update these Terms. Where the change is material, users will be asked to accept the new version the next time they sign in, and continued use of the platform after that is subject to the updated Terms. The version number and date of acceptance are recorded."},
    {title:"16. Contact",body:"Goport Delivery Private Limited (Meitho), Ward 7, Flat 5, Madhupur, Diglipur, North and Middle Andaman, 744202, India. Email: verification@meitho.com · help@meitho.com · WhatsApp +91 8300016265."},
  ];

  return(
    <div style={{minHeight:"100vh",background:"#06080f",fontFamily:FF,color:"#fff",padding:"40px 5%"}}>
      <div style={{maxWidth:800,margin:"0 auto"}}>
        <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:32}}>
          <a href="/" style={{color:"#0cc0df",textDecoration:"none",fontSize:13,fontWeight:600}}>← Back to Meitho</a>
        </div>
        <div style={{textAlign:"center",marginBottom:40}}>
          <div style={{width:48,height:48,borderRadius:12,background:"linear-gradient(135deg,#ff3131,#ff914d)",display:"flex",alignItems:"center",justifyContent:"center",margin:"0 auto 16px"}}>
            <svg width="22" height="22" viewBox="0 0 20 20" fill="none"><path d="M10 2L4 5v5c0 3.55 2.57 6.87 6 7.74C13.43 16.87 16 13.55 16 10V5l-6-3z" fill="white"/></svg>
          </div>
          <h1 style={{fontFamily:FFD,fontSize:36,fontWeight:900,color:"#fff",marginBottom:8}}>Terms & Conditions</h1>
          <p style={{color:"rgba(255,255,255,0.5)",fontSize:13}}>Version {TERMS_VERSION} · Last updated: 22 October 2026</p>
        </div>

        <div style={{background:"rgba(12,192,223,0.06)",border:"1px solid rgba(12,192,223,0.2)",borderRadius:12,padding:"16px 20px",marginBottom:32,fontSize:13,color:"rgba(255,255,255,0.7)",lineHeight:1.7}}>
          📋 Please read these Terms and Conditions carefully before using Meitho BGV platform. These terms govern your use of our background verification services.
        </div>

        {sections.map((s,i)=>(
          <div key={i} style={{marginBottom:24,background:"rgba(255,255,255,0.03)",borderRadius:12,padding:"20px 24px",border:"1px solid rgba(255,255,255,0.06)"}}>
            <h2 style={{fontFamily:FFD,fontSize:16,fontWeight:700,color:"#fff",marginBottom:10}}>{s.title}</h2>
            <p style={{fontSize:13,color:"rgba(255,255,255,0.6)",lineHeight:1.8}}>{s.body}</p>
          </div>
        ))}

        <div style={{textAlign:"center",padding:"32px 0",borderTop:"1px solid rgba(255,255,255,0.06)",marginTop:16}}>
          <p style={{fontSize:12,color:"rgba(255,255,255,0.3)",marginBottom:16}}>© 2026 Meitho BGV · All rights reserved · DPDP Act 2023 Compliant</p>
          <a href="/" style={{background:"linear-gradient(90deg,#ff3131,#ff914d)",border:"none",borderRadius:10,color:"#fff",padding:"10px 24px",cursor:"pointer",fontSize:13,fontWeight:700,textDecoration:"none"}}>Back to Meitho →</a>
        </div>
      </div>
    </div>
  );
}

function PrivacyPage() {
  const sections=[
    {title:"1. Information We Collect",body:"We collect company registration details (name, email, phone, GST), HR user information, candidate verification data (UAN, PAN, Aadhaar, DL numbers), search history and transaction records, and usage analytics for platform improvement."},
    {title:"2. How We Use Your Data",body:"Company and HR data is used for account management and service delivery. Candidate data is used solely for verification purposes. Transaction data is used for billing, invoicing and compliance. We never use candidate data for marketing or profiling."},
    {title:"3. Data Sharing",body:"We do not sell your data to any third party. Data is shared only with government databases (EPFO, Income Tax, RTO, DigiLocker, eCourt) for verification purposes, and with Surepass API for processing verifications. All third parties are bound by strict data processing agreements."},
    {title:"4. DPDP Act 2023 Compliance",body:"Meitho BGV is fully compliant with India's Digital Personal Data Protection Act 2023. We process personal data only with explicit consent, maintain data minimization principles, provide data erasure on request, and maintain detailed processing records."},
    {title:"5. Data Security",body:"All data is encrypted using AES-256 at rest. All data in transit is protected by TLS 1.3. We use Google Cloud Firebase with SOC 2 Type II certification. Regular security audits are conducted. Access is restricted using role-based controls."},
    {title:"6. Data Retention",body:"Company and HR user data is retained for the duration of the business relationship plus 3 years for compliance. Candidate verification data is retained for 5 years as required by law. Transaction records are retained for 7 years for GST compliance. You may request deletion of non-mandatory data."},
    {title:"7. Your Rights",body:"You have the right to access your personal data, correct inaccurate data, request deletion of data, withdraw consent for data processing, receive a copy of your data in portable format, and lodge complaints with the Data Protection Board of India."},
    {title:"8. Cookies",body:"We use essential cookies for session management and authentication. We do not use third-party tracking cookies. You can disable cookies in your browser settings, though this may affect platform functionality."},
    {title:"9. Contact Us",body:"For privacy-related queries, data requests, or complaints, contact our Data Protection Officer at: privacy@meitho.com | help@meitho.com | +91 8300016265 | Meitho BGV, Chennai, Tamil Nadu - 600001, India."},
  ];

  return(
    <div style={{minHeight:"100vh",background:"#06080f",fontFamily:FF,color:"#fff",padding:"40px 5%"}}>
      <div style={{maxWidth:800,margin:"0 auto"}}>
        <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:32}}>
          <a href="/" style={{color:"#0cc0df",textDecoration:"none",fontSize:13,fontWeight:600}}>← Back to Meitho</a>
        </div>
        <div style={{textAlign:"center",marginBottom:40}}>
          <div style={{width:48,height:48,borderRadius:12,background:"linear-gradient(135deg,#8b5cf6,#0cc0df)",display:"flex",alignItems:"center",justifyContent:"center",margin:"0 auto 16px"}}>
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none"><path d="M12 2L4 7v5c0 5.25 3.4 10.16 8 11.36C16.6 22.16 20 17.25 20 12V7l-8-5z" fill="white"/><path d="M9 12l2 2 4-4" stroke="rgba(139,92,246,1)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
          </div>
          <h1 style={{fontFamily:FFD,fontSize:36,fontWeight:900,color:"#fff",marginBottom:8}}>Privacy Policy</h1>
          <p style={{color:"rgba(255,255,255,0.5)",fontSize:13}}>Last updated: July 2026 · DPDP Act 2023 Compliant</p>
        </div>

        <div style={{background:"rgba(139,92,246,0.06)",border:"1px solid rgba(139,92,246,0.2)",borderRadius:12,padding:"16px 20px",marginBottom:32,fontSize:13,color:"rgba(255,255,255,0.7)",lineHeight:1.7}}>
          🔒 Your privacy is important to us. This policy explains how Meitho BGV collects, uses, and protects your personal data in compliance with the DPDP Act 2023.
        </div>

        {sections.map((s,i)=>(
          <div key={i} style={{marginBottom:24,background:"rgba(255,255,255,0.03)",borderRadius:12,padding:"20px 24px",border:"1px solid rgba(255,255,255,0.06)"}}>
            <h2 style={{fontFamily:FFD,fontSize:16,fontWeight:700,color:"#fff",marginBottom:10}}>{s.title}</h2>
            <p style={{fontSize:13,color:"rgba(255,255,255,0.6)",lineHeight:1.8}}>{s.body}</p>
          </div>
        ))}

        <div style={{textAlign:"center",padding:"32px 0",borderTop:"1px solid rgba(255,255,255,0.06)",marginTop:16}}>
          <p style={{fontSize:12,color:"rgba(255,255,255,0.3)",marginBottom:16}}>© 2026 Meitho BGV · All rights reserved · DPDP Act 2023 Compliant</p>
          <div style={{display:"flex",gap:12,justifyContent:"center",flexWrap:"wrap"}}>
            <a href="/terms" style={{color:"#0cc0df",textDecoration:"none",fontSize:13,fontWeight:600}}>Terms & Conditions</a>
            <a href="/" style={{background:"linear-gradient(90deg,#ff3131,#ff914d)",border:"none",borderRadius:10,color:"#fff",padding:"10px 24px",cursor:"pointer",fontSize:13,fontWeight:700,textDecoration:"none"}}>Back to Meitho →</a>
          </div>
        </div>
      </div>
    </div>
  );
}

function AccessDenied() {
  return(
    <div style={{display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",minHeight:"60vh",gap:16}}>
      <div style={{fontSize:48}}>🚫</div>
      <div style={{fontFamily:FFD,fontSize:22,fontWeight:700,color:"var(--text)"}}>Access Restricted</div>
      <div style={{fontSize:14,color:"var(--muted)",textAlign:"center",maxWidth:300,lineHeight:1.7}}>
        You don't have permission to view this page. Contact your company admin for access.
      </div>
    </div>
  );
}

function EmployeeAdminPage({user,company,companies,addToast,history}) {
  const {dark}=useTheme();
  const [empList,setEmpList]=useState([]);
  const [loading,setLoading]=useState(true);
  const [search,setSearch]=useState("");
  const [filterCompany,setFilterCompany]=useState("all");
  const [filterScore,setFilterScore]=useState("all");
  const [selectedEmp,setSelectedEmp]=useState(null);
  const [tab,setTab]=useState("all");

  useEffect(()=>{
    const loadAll=async()=>{
      setLoading(true);
      try{
        const snap=await getDocs(collection(db,"employees"));
        const emps=snap.docs.map(d=>({id:d.id,...d.data()}));
        setEmpList(emps);
      }catch(e){console.error("Load employees:",e);}
      setLoading(false);
    };
    loadAll();
  },[]);

  const filtered=empList.filter(e=>{
    const matchSearch=!search||(e.name||"").toLowerCase().includes(search.toLowerCase())||(e.uan||"").includes(search)||(e.pan||"").includes(search)||(e.meithoId||"").toLowerCase().includes(search.toLowerCase());
    const matchCompany=filterCompany==="all"||(e.companyId===filterCompany||e.addedBy===filterCompany);
    const matchScore=filterScore==="all"||(filterScore==="elite"&&(e.meithoScore||0)>=85)||(filterScore==="good"&&(e.meithoScore||0)>=65&&(e.meithoScore||0)<85)||(filterScore==="moderate"&&(e.meithoScore||0)>=45&&(e.meithoScore||0)<65)||(filterScore==="risk"&&(e.meithoScore||0)<45);
    return matchSearch&&matchCompany&&matchScore;
  });

  const stats={
    total:empList.length,
    elite:empList.filter(e=>(e.meithoScore||0)>=85).length,
    good:empList.filter(e=>(e.meithoScore||0)>=65&&(e.meithoScore||0)<85).length,
    moderate:empList.filter(e=>(e.meithoScore||0)>=45&&(e.meithoScore||0)<65).length,
    risk:empList.filter(e=>e.meithoScore>0&&(e.meithoScore||0)<45).length,
    withScore:empList.filter(e=>e.meithoScore>0).length,
    cardOrders:empList.filter(e=>e.cardOrdered).length,
    verified:empList.filter(e=>e.aadhaarVerified||e.panVerified).length,
  };

  if(selectedEmp){
    const emp=selectedEmp;
    const empHistory=history?.filter(h=>h.searchValue===emp.uan||h.searchValue===emp.pan)||[];
    return(
      <div>
        <button onClick={()=>setSelectedEmp(null)} style={{background:"var(--s2)",border:"1px solid var(--b1)",borderRadius:8,color:"var(--text)",padding:"7px 14px",cursor:"pointer",fontSize:12,fontWeight:600,fontFamily:FF,marginBottom:16}}>← Back to Employee Admin</button>
        <div className="fu" style={{marginBottom:16,padding:"22px 26px"}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",flexWrap:"wrap",gap:12}}>
            <div style={{display:"flex",gap:16,alignItems:"center"}}>
              <div style={{width:56,height:56,borderRadius:14,background:"linear-gradient(135deg,#8b5cf6,#0cc0df)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:22,color:"#fff",fontWeight:900}}>
                {(emp.name||"?")[0].toUpperCase()}
              </div>
              <div>
                <div style={{fontFamily:FFD,fontSize:22,fontWeight:800,color:"var(--text)"}}>{emp.name||"Unknown"}</div>
                <div style={{fontSize:12,color:"var(--muted)",marginTop:2}}>
                  {emp.meithoId&&<span style={{color:"var(--brand)",fontWeight:700,marginRight:8}}>{emp.meithoId}</span>}
                  {emp.uan&&<span style={{marginRight:8}}>UAN: {emp.uan}</span>}
                  {emp.pan&&<span>PAN: {emp.pan}</span>}
                </div>
              </div>
            </div>
            {emp.meithoScore>0&&(
              <div style={{textAlign:"center",padding:"12px 20px",background:emp.meithoScore>=85?"rgba(255,49,49,0.1)":emp.meithoScore>=65?"rgba(12,192,223,0.1)":emp.meithoScore>=45?"rgba(245,158,11,0.1)":"rgba(239,68,68,0.1)",borderRadius:12,border:`1px solid ${emp.meithoScore>=85?"rgba(255,49,49,0.3)":emp.meithoScore>=65?"rgba(12,192,223,0.3)":emp.meithoScore>=45?"rgba(245,158,11,0.3)":"rgba(239,68,68,0.3)"}`}}>
                <div style={{fontFamily:FFD,fontSize:32,fontWeight:900,color:emp.meithoScore>=85?"#ff3131":emp.meithoScore>=65?"#0cc0df":emp.meithoScore>=45?"#f59e0b":"#ef4444"}}>{emp.meithoScore}</div>
                <div style={{fontSize:10,color:"var(--muted)",fontWeight:700}}>MEITHO SCORE</div>
              </div>
            )}
          </div>
        </div>

        <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:12,marginBottom:16}} className="mg2">
          {[
            ["🪪","Aadhaar",emp.aadhaarVerified?"Verified":"Not Verified",emp.aadhaarVerified?"#ff3131":"var(--muted)"],
            ["📋","PAN",emp.panVerified?"Verified":"Not Verified",emp.panVerified?"#ff3131":"var(--muted)"],
            ["💳","Card",emp.cardOrdered?"Ordered":"Not Ordered",emp.cardOrdered?"#8b5cf6":"var(--muted)"],
            ["💼","Employment",(emp.employmentHistory||emp.employment_history||[]).length+" companies","var(--brand)"],
          ].map(([icon,label,val,color],i)=>(
            <div key={i} className="fu" style={{padding:"14px 16px",borderRadius:12}}>
              <div style={{fontSize:20,marginBottom:6}}>{icon}</div>
              <div style={{fontSize:11,color:"var(--muted)",fontWeight:600,marginBottom:3}}>{label}</div>
              <div style={{fontSize:13,fontWeight:700,color}}>{val}</div>
            </div>
          ))}
        </div>

        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:14,marginBottom:14}} className="mg1">
          <div className="fu" style={{borderRadius:12,padding:"18px 20px"}}>
            <div style={{fontFamily:FFD,fontSize:13,fontWeight:700,color:"var(--sub)",textTransform:"uppercase",letterSpacing:.8,marginBottom:12}}>Personal Details</div>
            {[["Name",emp.name],["Mobile",emp.mobile||emp.phone],["Email",emp.email],["DOB",emp.dob],["Gender",emp.gender],["Address",emp.address]].filter(([,v])=>v).map(([l,v],i)=>(
              <div key={i} style={{display:"flex",gap:8,marginBottom:8,fontSize:12}}>
                <span style={{color:"var(--muted)",width:60,flexShrink:0}}>{l}:</span>
                <span style={{color:"var(--text)",fontWeight:500}}>{v}</span>
              </div>
            ))}
          </div>
          <div className="fu" style={{borderRadius:12,padding:"18px 20px"}}>
            <div style={{fontFamily:FFD,fontSize:13,fontWeight:700,color:"var(--sub)",textTransform:"uppercase",letterSpacing:.8,marginBottom:12}}>Employment History</div>
            {(emp.employmentHistory||emp.employment_history||[]).map((j,i)=>(
              <div key={i} style={{marginBottom:10,paddingBottom:10,borderBottom:i<(emp.employmentHistory||emp.employment_history||[]).length-1?"1px solid var(--b1)":"none"}}>
                <div style={{fontSize:12,fontWeight:700,color:"var(--text)"}}>{j.establishment_name||j.company||"Unknown"}</div>
                <div style={{fontSize:11,color:"var(--muted)"}}>{j.date_of_joining||j.doj||""} → {j.date_of_exit||j.dor||"Present"}</div>
              </div>
            ))}
            {(emp.employmentHistory||emp.employment_history||[]).length===0&&<div style={{fontSize:12,color:"var(--muted)"}}>No employment history</div>}
          </div>
        </div>

        <div className="fu" style={{borderRadius:12,padding:"18px 20px"}}>
          <div style={{fontFamily:FFD,fontSize:13,fontWeight:700,color:"var(--sub)",textTransform:"uppercase",letterSpacing:.8,marginBottom:12}}>Search History</div>
          {empHistory.length>0?(
            <table style={{width:"100%",borderCollapse:"collapse"}}>
              <thead><tr style={{background:"var(--s2)"}}>{["Date","Type","Result","Cost"].map(h=><th key={h} style={{padding:"8px 12px",textAlign:"left",fontSize:10,fontWeight:700,color:"var(--muted)"}}>{h}</th>)}</tr></thead>
              <tbody>
                {empHistory.slice(0,10).map((h,i)=>(
                  <tr key={i} style={{borderBottom:"1px solid var(--b1)"}}>
                    <td style={{padding:"8px 12px",fontSize:11,color:"var(--muted)"}}>{(h.timestamp||"").slice(0,10)}</td>
                    <td style={{padding:"8px 12px",fontSize:12,color:"var(--text)"}}>{h.searchType?.toUpperCase()}</td>
                    <td style={{padding:"8px 12px"}}><Badge color={h.found?"var(--green)":"var(--red)"} style={{fontSize:9}}>{h.found?"Found":"Not Found"}</Badge></td>
                    <td style={{padding:"8px 12px",fontSize:12,fontWeight:700,color:"var(--brand)"}}>₹{h.cost||0}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ):<div style={{fontSize:12,color:"var(--muted)"}}>No search history found</div>}
        </div>
      </div>
    );
  }

  return(
    <div>
      <div className="fu" style={{marginBottom:20}}>
        <h2 style={{fontFamily:FFD,fontWeight:800,fontSize:26,color:"var(--text)",marginBottom:4}}>Employee Admin 👑</h2>
        <p style={{color:"var(--muted)",fontSize:13}}>Super Admin · Manage all verified employee profiles</p>
      </div>

      {/* Stats */}
      <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:12,marginBottom:20}} className="mg2">
        {[
          ["👥","Total Profiles",stats.total,"var(--brand)"],
          ["✅","Verified",stats.verified,"#ff3131"],
          ["🏆","Elite Score (85+)",stats.elite,"#ff3131"],
          ["💳","Card Orders",stats.cardOrders,"#8b5cf6"],
        ].map(([icon,label,val,color],i)=>(
          <div key={i} className="fu" style={{padding:"16px 18px",borderRadius:12}}>
            <div style={{fontSize:22,marginBottom:6}}>{icon}</div>
            <div style={{fontFamily:FFD,fontSize:26,fontWeight:900,color,marginBottom:3}}>{val}</div>
            <div style={{fontSize:11,color:"var(--muted)",fontWeight:600}}>{label}</div>
          </div>
        ))}
      </div>

      {/* Score Distribution */}
      <div className="fu" style={{marginBottom:16,padding:"18px 20px",borderRadius:14}}>
        <div style={{fontFamily:FFD,fontSize:12,fontWeight:700,color:"var(--sub)",textTransform:"uppercase",letterSpacing:.8,marginBottom:12}}>Score Distribution</div>
        <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:10}}>
          {[["🏆 Elite","85-100",stats.elite,"#ff3131"],["✅ Good","65-84",stats.good,"#0cc0df"],["⚠️ Moderate","45-64",stats.moderate,"#f59e0b"],["🔴 Risk","<45",stats.risk,"#ef4444"]].map(([l,r,v,c],i)=>(
            <div key={i} style={{textAlign:"center",padding:"12px",background:"var(--s2)",borderRadius:10,border:`1px solid ${c}25`}}>
              <div style={{fontFamily:FFD,fontSize:22,fontWeight:900,color:c}}>{v}</div>
              <div style={{fontSize:11,color:"var(--text)",fontWeight:600}}>{l}</div>
              <div style={{fontSize:10,color:"var(--muted)"}}>{r}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Filters */}
      <div style={{display:"flex",gap:10,marginBottom:16,flexWrap:"wrap"}}>
        <Inp value={search} onChange={e=>setSearch(e.target.value)} placeholder="Search by name, UAN, PAN, MeithoID..." style={{flex:1,minWidth:200}}/>
        <select value={filterCompany} onChange={e=>setFilterCompany(e.target.value)} style={{padding:"8px 12px",background:"var(--s2)",border:"1px solid var(--b1)",borderRadius:8,color:"var(--text)",fontSize:13,fontFamily:FF,cursor:"pointer",outline:"none"}}>
          <option value="all">All Companies</option>
          {companies?.map((c,i)=><option key={i} value={c.id}>{c.name}</option>)}
        </select>
        <select value={filterScore} onChange={e=>setFilterScore(e.target.value)} style={{padding:"8px 12px",background:"var(--s2)",border:"1px solid var(--b1)",borderRadius:8,color:"var(--text)",fontSize:13,fontFamily:FF,cursor:"pointer",outline:"none"}}>
          <option value="all">All Scores</option>
          <option value="elite">Elite (85+)</option>
          <option value="good">Good (65-84)</option>
          <option value="moderate">Moderate (45-64)</option>
          <option value="risk">Risk (&lt;45)</option>
        </select>
      </div>

      {/* Employee Table */}
      <div className="fu" style={{borderRadius:14,padding:0,overflow:"hidden"}}>
        <div style={{padding:"14px 20px",borderBottom:"1px solid var(--b1)",display:"flex",justifyContent:"space-between",alignItems:"center"}}>
          <div style={{fontFamily:FFD,fontSize:13,fontWeight:700,color:"var(--text)"}}>
            {loading?"Loading...":filtered.length+" employees"}
          </div>
          <button onClick={()=>{
            const rows=[["MeithoID","Name","UAN","PAN","Score","Aadhaar","PAN Verified","Card","Company"],...filtered.map(e=>[e.meithoId||"",e.name||"",e.uan||"",e.pan||"",e.meithoScore||"",e.aadhaarVerified?"Yes":"No",e.panVerified?"Yes":"No",e.cardOrdered?"Yes":"No",e.companyName||""])];
            const csv=rows.map(r=>r.join(",")).join("\n");
            const a=document.createElement("a");a.href="data:text/csv;charset=utf-8,"+encodeURIComponent(csv);a.download="meitho_employees.csv";a.click();
          }} style={{background:"var(--s2)",border:"1px solid var(--b1)",borderRadius:7,color:"var(--text)",padding:"6px 14px",cursor:"pointer",fontSize:11,fontWeight:600,fontFamily:FF}}>↓ Export CSV</button>
        </div>
        {loading?(
          <div style={{padding:40,textAlign:"center"}}><Spin/></div>
        ):(
          <div style={{overflowX:"auto"}}>
            <table style={{width:"100%",borderCollapse:"collapse",minWidth:700}}>
              <thead><tr style={{background:"var(--s2)"}}>
                {["Employee","MeithoID","UAN","Score","Verified","Card","Company",""].map(h=>(
                  <th key={h} style={{padding:"10px 14px",textAlign:"left",fontSize:10,fontWeight:700,color:"var(--muted)",textTransform:"uppercase"}}>{h}</th>
                ))}
              </tr></thead>
              <tbody>
                {filtered.slice(0,100).map((e,i)=>{
                  const scoreColor=e.meithoScore>=85?"#ff3131":e.meithoScore>=65?"#0cc0df":e.meithoScore>=45?"#f59e0b":"#ef4444";
                  return(
                    <tr key={i} style={{borderBottom:"1px solid var(--b1)"}}>
                      <td style={{padding:"10px 14px"}}>
                        <div style={{display:"flex",gap:10,alignItems:"center"}}>
                          <div style={{width:32,height:32,borderRadius:8,background:"linear-gradient(135deg,#8b5cf6,#0cc0df)",display:"flex",alignItems:"center",justifyContent:"center",color:"#fff",fontSize:13,fontWeight:700,flexShrink:0}}>
                            {(e.name||"?")[0].toUpperCase()}
                          </div>
                          <div>
                            <div style={{fontSize:13,fontWeight:600,color:"var(--text)"}}>{e.name||"Unknown"}</div>
                            <div style={{fontSize:10,color:"var(--muted)"}}>{e.email||e.mobile||""}</div>
                          </div>
                        </div>
                      </td>
                      <td style={{padding:"10px 14px",fontSize:11,fontFamily:"monospace",color:"var(--brand)",fontWeight:700}}>{e.meithoId||"—"}</td>
                      <td style={{padding:"10px 14px",fontSize:12,fontFamily:"monospace",color:"var(--muted)"}}>{e.uan||"—"}</td>
                      <td style={{padding:"10px 14px"}}>
                        {e.meithoScore>0?(
                          <div style={{display:"flex",alignItems:"center",gap:6}}>
                            <div style={{fontFamily:FFD,fontSize:16,fontWeight:900,color:scoreColor}}>{e.meithoScore}</div>
                            <div style={{fontSize:9,color:"var(--muted)"}}>/100</div>
                          </div>
                        ):<span style={{fontSize:12,color:"var(--muted)"}}>—</span>}
                      </td>
                      <td style={{padding:"10px 14px"}}>
                        <div style={{display:"flex",gap:4}}>
                          {e.aadhaarVerified&&<Badge color="#8b5cf6" style={{fontSize:8}}>Aadhaar</Badge>}
                          {e.panVerified&&<Badge color="#0cc0df" style={{fontSize:8}}>PAN</Badge>}
                          {!e.aadhaarVerified&&!e.panVerified&&<span style={{fontSize:11,color:"var(--muted)"}}>—</span>}
                        </div>
                      </td>
                      <td style={{padding:"10px 14px"}}>
                        <Badge color={e.cardOrdered?"#8b5cf6":"var(--muted)"} style={{fontSize:9}}>{e.cardOrdered?"Ordered":"No"}</Badge>
                      </td>
                      <td style={{padding:"10px 14px",fontSize:12,color:"var(--muted)"}}>{e.companyName||e.addedBy||"—"}</td>
                      <td style={{padding:"10px 14px"}}>
                        <button onClick={()=>setSelectedEmp(e)} style={{background:"rgba(12,192,223,0.1)",border:"1px solid rgba(12,192,223,0.2)",borderRadius:6,color:"var(--brand)",padding:"4px 10px",cursor:"pointer",fontSize:11,fontWeight:700,fontFamily:FF}}>View →</button>
                      </td>
                    </tr>
                  );
                })}
                {filtered.length===0&&<tr><td colSpan={8} style={{textAlign:"center",padding:40,color:"var(--muted)",fontSize:13}}>No employees found</td></tr>}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}


/* ══════════════════════════════════════════════════════════════
   CANDIDATE VERIFICATION (Option 2 — "Send to candidate")
   HR / Super Admin enters name+email+phone and ticks the checks needed.
   Candidate gets a secure link, submits only the required details (no
   checks run, nothing charged). HR/Super Admin is notified, reviews the
   data, then runs each check one by one (wallet charged per check, Super
   Admin free). Requests live in Firestore `candidate_verifications`.
══════════════════════════════════════════════════════════════ */
const CV_EXPIRY_DAYS=7;
const CV_URLS={
  uan:"https://uanverify-mtvra6khyq-uc.a.run.app",
  pan:"https://panverify-mtvra6khyq-uc.a.run.app",
  aadhaar:"https://aadhaarverify-mtvra6khyq-uc.a.run.app",
  aadhaarDl:"https://aadhaardownload-mtvra6khyq-uc.a.run.app",
  dl:"https://dlverify-mtvra6khyq-uc.a.run.app",
  ecourt:"https://ecourtsearch-mtvra6khyq-uc.a.run.app",
};
const CV_CHECKS=[
  {key:"uan",label:"UAN / Employment History",icon:"🏦",priceKey:"uan",
    fields:[{k:"uan",label:"UAN Number",ph:"12-digit UAN",type:"text",re:/^\d{12}$/,clean:v=>v.replace(/\D/g,""),err:"UAN must be 12 digits"}]},
  {key:"pan",label:"PAN Verification",icon:"📋",priceKey:"pan",
    fields:[{k:"pan",label:"PAN Number",ph:"ABCDE1234F",type:"text",re:/^[A-Z]{5}[0-9]{4}[A-Z]$/,clean:v=>v.toUpperCase().replace(/\s/g,""),err:"PAN format: ABCDE1234F"}]},
  {key:"aadhaar",label:"Aadhaar (DigiLocker)",icon:"🪪",priceKey:"aadhaar",
    fields:[{k:"aadhaar",label:"Aadhaar Number",ph:"12-digit Aadhaar",type:"text",re:/^\d{12}$/,clean:v=>v.replace(/\D/g,""),err:"Aadhaar must be 12 digits"}]},
  {key:"dl",label:"Driving License",icon:"🚗",priceKey:"dl",
    fields:[
      {k:"dl_number",label:"Driving License Number",ph:"e.g. TN0720150000000",type:"text",re:/^.{8,20}$/,clean:v=>v.toUpperCase().replace(/\s/g,""),err:"Enter a valid DL number"},
      {k:"dl_dob",label:"Date of Birth (as on DL)",ph:"",type:"date",re:/^\d{4}-\d{2}-\d{2}$/,clean:v=>v,err:"Enter date of birth"},
    ]},
  {key:"ecourt",label:"Criminal Check",icon:"⚖️",priceKey:"ecourt",
    fields:[
      {k:"father_name",label:"Father's Name",ph:"Full name",type:"text",re:/^.{2,}$/,clean:v=>v.trim(),err:"Father's name is required"},
      {k:"court_address",label:"Current Address",type:"addr",err:"Fill address line 1, city, state and a 6-digit pincode"},
    ]},
  {key:"refcheck",label:"Reference Check",icon:"💬",priceKey:"refcheck",custom:true,fields:[]},
  {key:"education",label:"Education Verification",icon:"🎓",priceKey:"education",custom:true,fields:[]},
  {key:"address",label:"Address Verification",icon:"🏠",priceKey:"address_verify",
    fields:[
      {k:"presentAddress",label:"Present Address",type:"addr",err:"Fill address line 1, city, state and a 6-digit pincode"},
      {k:"permanentAddress",label:"Permanent Address",type:"addr",optional:true,sameAs:"presentAddress",err:"Fill address line 1, city, state and a 6-digit pincode"},
    ]},
];
const CV_STATUS={
  draft:{label:"Draft",color:"#64748b"},
  pending_approval:{label:"Awaiting approval",color:"#ec4899"},
  sent:{label:"Sent",color:"#f59e0b"},
  opened:{label:"Opened",color:"#0cc0df"},
  submitted:{label:"Submitted — review",color:"#8b5cf6"},
  partial:{label:"Partially verified",color:"#ff914d"},
  completed:{label:"Completed",color:"#10b981"},
  expired:{label:"Expired",color:"#ef4444"},
};
const CV_MAX_BYTES=5*1024*1024;
const CV_REF_DOCS=[
  {k:"payslip1",label:"Payslip — latest month",req:true},
  {k:"payslip2",label:"Payslip — 2nd last month",req:true},
  {k:"payslip3",label:"Payslip — 3rd last month",req:true},
  {k:"offer",label:"Offer letter",req:true},
  {k:"appointment",label:"Appointment letter"},
  {k:"experience",label:"Experience letter"},
  {k:"relieving",label:"Relieving letter"},
];
const CV_ID_DOCS=[
  {k:"aadhaar_card",label:"Aadhaar card (front & back)",req:true},
  {k:"pan_card",label:"PAN card",req:true},
];
const CV_EDU_DOCS=[
  {k:"edu_10th",label:"10th marksheet / certificate",short:"10th",req:true},
  {k:"edu_12th",label:"12th marksheet / certificate",short:"12th"},
  {k:"edu_ug",label:"UG degree certificate (if applicable)",short:"UG degree"},
  {k:"edu_pg",label:"PG degree certificate (if applicable)",short:"PG degree"},
];
const CV_EDU_FIELDS=[
  {k:"board",label:"Board / University",ph:"e.g. CBSE / Anna University",req:true},
  {k:"institution",label:"School / College",ph:"Name of institution",req:true},
  {k:"year",label:"Year of passing",ph:"e.g. 2018",req:true,re:/^(19|20)\d{2}$/},
  {k:"rollNo",label:"Roll / Registration number",ph:"Optional"},
  {k:"percentage",label:"Percentage / CGPA",ph:"Optional"},
];
const CV_EDU_VERDICT={verified:["✓ Verified","#10b981"],not_verified:["✗ Not verified","#ef4444"],unable:["? Unable to verify","#f59e0b"]};
const cvCandDocList=(sub)=>[...CV_ID_DOCS,...CV_EDU_DOCS].filter(d=>sub?.candDocs?.[d.k]?.url).map(d=>({...d,...sub.candDocs[d.k]}));
const cvFileCandDocs=async(r)=>{
  const list=cvCandDocList(r.submission);
  if(!list.length)return false;
  const phone=String(r.candidatePhone||"").replace(/\D/g,"").slice(-10);
  const slug=String(r.candidateName||"candidate").toLowerCase().replace(/[^a-z0-9]+/g,"-").replace(/^-|-$/g,"");
  const employeeKey=slug+"_"+(phone||"nophone");
  const cid=r.companyId||"";
  const documents={};
  list.forEach(d=>{documents["candidate_"+d.k]={url:d.url,name:d.name||"",label:d.label.replace(" (if applicable)",""),source:"candidate_verification",uploadedAt:r.submittedAt||new Date().toISOString()};});
  await setDoc(doc(db,"employeeFiles",(cid||"unassigned")+"_"+employeeKey),cvClean({
    companyId:cid,employeeKey,candidateName:r.candidateName,phone:r.candidatePhone||"",email:r.candidateEmail||"",requestedBy:r.companyName||"",documents,updatedAt:new Date().toISOString(),
  }),{merge:true});
  return true;
};
const CV_REF_FIELDS=[
  {k:"company",label:"Company name",ph:"e.g. ABC Technologies Pvt Ltd",req:true},
  {k:"designation",label:"Designation",ph:"e.g. Senior Executive",req:true},
  {k:"employeeId",label:"Employee ID",ph:"Optional"},
  {k:"doj",label:"Date of joining",type:"date",req:true},
  {k:"dor",label:"Last working day (leave blank if currently working)",type:"date"},
  {k:"lastSalary",label:"Last monthly salary (₹)",type:"number",ph:"Optional"},
  {k:"reasonForLeaving",label:"Reason for leaving",ph:"Optional"},
  {k:"refName",label:"Reference person's name",ph:"Manager / HR who can confirm",req:true},
  {k:"refEmail",label:"Reference person's email",ph:"name@company.com",type:"email",req:true,re:/^\S+@\S+\.\S+$/},
  {k:"refRelation",label:"Their role (e.g. Reporting Manager, HR)",ph:"Optional"},
];
// Works for old requests (ref1Name…) and new ones (sub.refs)
const cvRefsOf=(sub)=>{
  sub=sub||{};
  if(Array.isArray(sub.refs))return sub.refs;
  return [1,2].filter(n=>sub["ref"+n+"Email"]).map(n=>({refName:sub["ref"+n+"Name"]||"",refEmail:sub["ref"+n+"Email"],company:sub["ref"+n+"Company"]||"",docs:{}}));
};
const cvRefSubRows=(sub)=>{
  const rows=[];
  cvRefsOf(sub).forEach((x,i)=>{
    rows.push([`Employer ${i+1}`,`${x.company||"—"} · ${x.designation||"—"}${x.doj?" · "+x.doj+" to "+(x.dor||"present"):""}`]);
    rows.push([`  Reference ${i+1}`,`${x.refName||""} <${x.refEmail||""}>${x.refRelation?" · "+x.refRelation:""}`]);
    const up=CV_REF_DOCS.filter(d=>x.docs?.[d.k]?.url).map(d=>d.label);
    rows.push([`  Documents`,up.length?up.join(", "):"None"]);
  });
  return rows;
};
const CV_REF_VERIFY=[
  {k:"company",label:"Company"},{k:"designation",label:"Designation"},{k:"doj",label:"Date of joining"},
  {k:"dor",label:"Last working day"},{k:"lastSalary",label:"Last monthly salary"},{k:"reasonForLeaving",label:"Reason for leaving"},
];
const CV_VERDICT={
  true:{t:"✓ True",c:"#059669"},false:{t:"✗ False",c:"#dc2626"},partial:{t:"~ Partially true",c:"#d97706"},
};
// One structured block per reference: what the candidate said, what the reference said, documents
const cvRefReportData=(sub,refDoc)=>{
  const refs=cvRefsOf(sub);
  return refs.map((x,i)=>{
    const n=i+1;
    const data=refDoc?refDoc["ref"+n+"_data"]:null;
    const person=refDoc?refDoc["ref"+n]:null;
    const ver=data?.verifications||{};
    return {
      n,employer:x.company||"—",employeeId:x.employeeId||"",
      refName:x.refName||person?.name||"",refEmail:x.refEmail||person?.email||"",refRole:x.refRelation||"",
      responded:!!data?.submittedAt,
      rows:CV_REF_VERIFY.map(f=>{
        let claimed=x[f.k];
        if(f.k==="dor"&&!claimed)claimed="Currently working";
        if(f.k==="lastSalary"&&claimed)claimed="₹"+claimed+"/month";
        const v=ver[f.k]||{};
        return {label:f.label,claimed:claimed||"—",status:v.status||"",correction:v.correction||""};
      }).filter(r=>r.claimed!=="—"||r.status),
      ratings:data?{behaviour:data.behaviour,ethical:data.ethical,professional:data.professional,rehire:data.rehire,comments:data.comments}:null,
      docs:CV_REF_DOCS.map(d=>({label:d.label,req:d.req,url:x.docs?.[d.k]?.url||""})),
    };
  });
};
// Full-page Reference Check report (same look as the Reference Check page report) with employer-wise claims, verdicts, ratings and attachments
const cvRefReportPdf=(r,refDoc)=>{
  const dt=new Date().toLocaleString("en-IN");
  const blocks=cvRefReportData(r.submission,refDoc);
  const star=(n)=>n?"★".repeat(Math.round(n))+"☆".repeat(5-Math.round(n))+" "+n+"/5":"Not rated";
  const avg=(vals)=>{const f=vals.filter(v=>v!=null&&v!=="").map(Number);return f.length?Math.round(f.reduce((a,b)=>a+b,0)/f.length*10)/10:0;};
  const rt=blocks.map(b=>b.ratings).filter(Boolean);
  const avgB=avg(rt.map(x=>x.behaviour)),avgE=avg(rt.map(x=>x.ethical)),avgP=avg(rt.map(x=>x.professional));
  const overall=avg([avgB,avgE,avgP]);
  const vBadge=(v)=>v==="true"?'<span style="color:#059669;font-weight:700;">✓ True</span>':v==="false"?'<span style="color:#ef4444;font-weight:700;">✗ False</span>':v==="partial"?'<span style="color:#f59e0b;font-weight:700;">~ Partially True</span>':"<span style='color:#94a3b8'>Not verified</span>";
  const th=(t)=>`<th style="padding:8px 12px;text-align:left;font-size:10px;color:#64748b;font-weight:700;text-transform:uppercase;">${t}</th>`;
  const td="padding:8px 12px;font-size:12px;border-bottom:1px solid #f1f5f9;";
  const sec=(t)=>`<div style="font-size:9px;font-weight:800;letter-spacing:1.5px;text-transform:uppercase;color:#0cc0df;border-bottom:2px solid #0cc0df;padding-bottom:5px;margin:18px 0 12px;">${t}</div>`;
  const status=(refDoc?.overallStatus||"pending").toUpperCase();
  const claims=blocks.map(b=>`
    <div style="margin-bottom:6px;font-size:12px;font-weight:700;">Employer ${b.n}: ${cvEsc(b.employer)} <span style="font-weight:400;color:#64748b;">— Reference ${b.n}: ${cvEsc(b.refName)} · ${cvEsc(b.refEmail)}${b.refRole?" · "+cvEsc(b.refRole):""}</span></div>
    <table style="width:100%;border-collapse:collapse;margin-bottom:16px;page-break-inside:avoid;">
      <thead><tr style="background:#f1f5f9;">${th("Field")}${th("Candidate Claims")}${th("Ref "+b.n+" Verdict")}${th("Ref "+b.n+" Correction")}</tr></thead>
      <tbody>${b.rows.map(x=>`<tr><td style="${td}color:#64748b;width:150px;">${cvEsc(x.label)}</td><td style="${td}font-weight:600;">${cvEsc(x.claimed)}</td><td style="${td}">${b.responded?vBadge(x.status):"<span style='color:#f59e0b'>Awaiting response</span>"}</td><td style="${td}font-size:11px;color:#dc2626;font-style:italic;">${cvEsc(x.correction)}</td></tr>`).join("")}</tbody>
    </table>`).join("");
  const rate=blocks.map(b=>{const d=b.ratings||{};return `
    <div style="background:#f8fafc;border-radius:10px;padding:16px;border:1px solid #e2e8f0;">
      <div style="font-size:13px;font-weight:700;margin-bottom:4px;">Reference ${b.n} — ${cvEsc(b.employer)}</div>
      <div style="font-size:11px;color:#64748b;margin-bottom:12px;">${cvEsc(b.refName)} · ${cvEsc(b.refEmail)}${b.refRole?" · "+cvEsc(b.refRole):""}</div>
      ${b.responded?`
      <div style="margin-bottom:8px;"><span style="color:#64748b;font-size:11px;">Behaviour &amp; Attitude:</span><br/><span style="color:#ff914d;font-size:13px;font-weight:700;">${star(d.behaviour)}</span></div>
      <div style="margin-bottom:8px;"><span style="color:#64748b;font-size:11px;">Ethical Conduct:</span><br/><span style="color:#ff914d;font-size:13px;font-weight:700;">${star(d.ethical)}</span></div>
      <div style="margin-bottom:8px;"><span style="color:#64748b;font-size:11px;">Professional Skills:</span><br/><span style="color:#ff914d;font-size:13px;font-weight:700;">${star(d.professional)}</span></div>
      <div style="margin-bottom:8px;"><span style="color:#64748b;font-size:11px;">Would Rehire:</span><br/><span style="font-weight:700;font-size:13px;color:${d.rehire==="Yes"?"#059669":d.rehire==="No"?"#ef4444":"#f59e0b"}">${cvEsc(d.rehire||"Not answered")}</span></div>
      ${d.comments?`<div style="margin-top:10px;padding:8px;background:#fff;border-radius:6px;border:1px solid #e2e8f0;font-size:11px;color:#64748b;font-style:italic;">"${cvEsc(d.comments)}"</div>`:""}`
      :`<div style="font-size:12px;color:#f59e0b;font-weight:700;">Awaiting response</div>`}
    </div>`;}).join("");
  const docsHtml=blocks.map(b=>{
    const x=cvRefsOf(r.submission)[b.n-1]||{};
    const items=CV_REF_DOCS.filter(d=>x.docs?.[d.k]?.url).map(d=>{
      const f=x.docs[d.k];const isImg=/\.(jpe?g|png)$/i.test(f.name||"");
      return `<div style="margin:10px 0;page-break-inside:avoid;"><div style="font-weight:700;font-size:12px;margin-bottom:4px;">${cvEsc(d.label)} <span style="font-weight:400;color:#64748b;">(${cvEsc(f.name||"")})</span></div>${isImg?`<img src="${cvEsc(f.url)}" style="max-width:100%;max-height:900px;border:1px solid #e2e8f0;"/>`:`<div style="font-size:12px;">PDF document — <a href="${cvEsc(f.url)}">open / download</a></div>`}</div>`;
    }).join("");
    return `<div class="page-break">${sec("Attachments — Employer "+b.n+": "+cvEsc(b.employer))}${items||"<div style='font-size:12px;color:#94a3b8'>No documents uploaded.</div>"}</div>`;
  }).join("");
  const html=`<!DOCTYPE html><html><head><meta charset="UTF-8"/><title>Reference Check Report — ${cvEsc(r.candidateName)}</title>
<style>*{margin:0;padding:0;box-sizing:border-box;}body{font-family:'Segoe UI',Arial,sans-serif;color:#1a1a2e;background:#fff;font-size:13px;}
@page{margin:10mm;size:A4;}@media print{body{-webkit-print-color-adjust:exact;print-color-adjust:exact;}.no-print{display:none;}.page-break{page-break-before:always;}}
.watermark{position:fixed;top:50%;left:50%;transform:translate(-50%,-50%) rotate(-30deg);opacity:0.04;z-index:0;pointer-events:none;}.content{position:relative;z-index:1;}</style></head><body>
<div class="watermark"><img src="${LOGO1}" style="width:400px;" alt=""/></div><div class="content">
<div class="no-print" style="background:#0a0a1a;padding:12px 24px;text-align:right;"><button onclick="window.print()" style="background:linear-gradient(90deg,#ff3131,#ff914d);color:#fff;border:none;padding:10px 24px;border-radius:8px;cursor:pointer;font-size:13px;font-weight:700;">⬇ Download as PDF</button><span style="color:#fff;font-size:11px;margin-left:12px;opacity:0.6;">Tip: In print dialog → Save as PDF</span></div>
<div style="background:linear-gradient(135deg,#0a0a1a,#1a2040);color:#fff;padding:24px 36px;display:flex;justify-content:space-between;align-items:center;">
  <div><img src="${LOGO1}" style="height:36px;margin-bottom:6px;" alt="Meitho BGV"/><div style="font-size:9px;color:#0cc0df;letter-spacing:2px;font-weight:700;">REFERENCE VERIFICATION REPORT</div></div>
  <div style="text-align:right;"><div style="font-size:18px;font-weight:900;color:#0cc0df;font-family:monospace;">${cvEsc(refDoc?.id||r.id)}</div><div style="font-size:10px;color:rgba(255,255,255,0.5);margin-top:2px;">${dt}</div><div style="font-size:10px;color:rgba(255,255,255,0.4);">Status: ${status}</div></div>
</div>
<div style="background:#0cc0df;color:#fff;padding:6px 36px;display:flex;justify-content:space-between;font-size:10px;font-weight:700;letter-spacing:.5px;"><span>MEITHO BGV PLATFORM</span><span>meitho.com · help@meitho.com · 8300016265</span></div>
<div style="padding:20px 36px;">
  ${sec("1. Candidate Information")}
  <div style="display:grid;grid-template-columns:repeat(3,1fr);gap:10px;margin-bottom:8px;">
    ${[["Full Name",r.candidateName],["Mobile",r.candidatePhone],["Email",r.candidateEmail],["Requested By",(r.initiatedByName||"")+(r.companyName?" · "+r.companyName:"")],["Request ID",r.id],["References",blocks.length+" employer"+(blocks.length>1?"s":"")]].map(([l,v])=>v?`<div style="background:#f8fafc;border-radius:8px;padding:9px 12px;border-left:3px solid #0cc0df;"><div style="font-size:9px;color:#94a3b8;font-weight:700;text-transform:uppercase;margin-bottom:2px;">${l}</div><div style="font-size:12px;font-weight:700;word-break:break-word;">${cvEsc(v)}</div></div>`:"").join("")}
  </div>
  ${sec("2. Employment Claims &amp; Reference Verification")}${claims}
  ${sec("3. Performance Ratings")}
  <div style="display:grid;grid-template-columns:${blocks.length>1?"1fr 1fr":"1fr"};gap:16px;margin-bottom:16px;">${rate}</div>
  ${rt.length?`${sec("4. Overall Score")}
  <div style="display:grid;grid-template-columns:repeat(4,1fr);gap:10px;margin-bottom:20px;">
  ${[["Behaviour Avg",avgB],["Ethical Avg",avgE],["Professional Avg",avgP],["Overall Score",overall]].map(([l,v],i)=>`<div style="background:${i===3?"linear-gradient(135deg,#0a0a1a,#1a2040)":"#f8fafc"};border-radius:10px;padding:14px;text-align:center;border:1px solid ${i===3?"transparent":"#e2e8f0"};"><div style="font-size:9px;color:${i===3?"rgba(255,255,255,0.6)":"#94a3b8"};font-weight:700;text-transform:uppercase;margin-bottom:4px;">${l}</div><div style="font-size:${i===3?"26px":"22px"};font-weight:900;color:${i===3?"#0cc0df":v>=4?"#059669":v>=3?"#0cc0df":"#ef4444"};">${v||"—"}<span style="font-size:13px;font-weight:400;color:${i===3?"rgba(255,255,255,0.4)":"#94a3b8"}">/5</span></div></div>`).join("")}
  </div>`:""}
  ${docsHtml}
</div>
<div style="background:#0a0a1a;color:#fff;padding:14px 36px;display:flex;justify-content:space-between;align-items:center;margin-top:8px;"><img src="${LOGO1}" style="height:24px;" alt="Meitho BGV"/><div style="font-size:10px;color:rgba(255,255,255,0.5);">Report ID: ${cvEsc(refDoc?.id||r.id)} · Generated: ${dt} · This report is confidential</div><div style="font-size:10px;color:#0cc0df;font-weight:600;">help@meitho.com</div></div>
</div></body></html>`;
  const w=window.open("","_blank","width=1000,height=800");
  if(!w){alert("Please allow popups to download the report");return;}
  w.document.write(html);w.document.close();w.focus();
  setTimeout(()=>w.print(),1200);
};

const CvRefReportView=({sub,refDoc})=>{
  const blocks=cvRefReportData(sub,refDoc);
  return <div style={{display:"grid",gap:12}}>{blocks.map(b=>(
    <div key={b.n} style={{border:"1.5px solid var(--b1)",borderRadius:10,overflow:"hidden"}}>
      <div style={{padding:"9px 12px",background:"rgba(255,49,49,0.06)",display:"flex",justifyContent:"space-between",gap:8,flexWrap:"wrap"}}>
        <div style={{fontWeight:800,color:"var(--text)"}}>Reference {b.n} — {b.employer}</div>
        <div style={{fontSize:11.5,fontWeight:700,color:b.responded?"#059669":"#d97706"}}>{b.responded?"✓ Responded":"⏳ Awaiting response"}</div>
      </div>
      <div style={{padding:"8px 12px",fontSize:12,color:"var(--muted)"}}>{b.refName} &lt;{b.refEmail}&gt;{b.refRole?" · "+b.refRole:""}{b.employeeId?" · Employee ID "+b.employeeId:""}</div>
      <div style={{padding:"0 12px 8px",overflowX:"auto"}}>
        <table style={{width:"100%",borderCollapse:"collapse",fontSize:12.5}}>
          <thead><tr style={{textAlign:"left",color:"var(--muted)",fontSize:11}}><th style={{padding:"4px 6px"}}>Detail</th><th style={{padding:"4px 6px"}}>Candidate said</th><th style={{padding:"4px 6px"}}>Reference says</th></tr></thead>
          <tbody>{b.rows.map(r=>{const v=CV_VERDICT[r.status];return(
            <tr key={r.label} style={{borderTop:"1px solid var(--b1)"}}>
              <td style={{padding:"6px",color:"var(--muted)",verticalAlign:"top"}}>{r.label}</td>
              <td style={{padding:"6px",color:"var(--text)",verticalAlign:"top",wordBreak:"break-word"}}>{r.claimed}</td>
              <td style={{padding:"6px",verticalAlign:"top"}}>{v?<span style={{fontWeight:700,color:v.c}}>{v.t}</span>:<span style={{color:"var(--muted)"}}>{b.responded?"Not answered":"—"}</span>}
                {(r.status==="false"||r.status==="partial")&&r.correction&&<div style={{color:"var(--text)",marginTop:2}}>Correct value: <b>{r.correction}</b></div>}</td>
            </tr>);})}</tbody>
        </table>
      </div>
      {b.ratings&&<div style={{padding:"8px 12px",borderTop:"1px solid var(--b1)",fontSize:12.5,color:"var(--text)",lineHeight:1.7}}>
        <b>Ratings (out of 5):</b> Behaviour {b.ratings.behaviour||"—"} · Ethics {b.ratings.ethical||"—"} · Professional {b.ratings.professional||"—"}<br/>
        <b>Would rehire:</b> {b.ratings.rehire||"—"}<br/>
        <b>Comments:</b> {b.ratings.comments||"—"}
      </div>}
      <div style={{padding:"8px 12px",borderTop:"1px solid var(--b1)",display:"flex",flexWrap:"wrap",gap:6}}>
        {b.docs.map(d=>d.url
          ?<a key={d.label} href={d.url} target="_blank" rel="noreferrer" style={{fontSize:11,color:"var(--brand)",border:"1px solid var(--b1)",borderRadius:6,padding:"3px 8px",textDecoration:"none"}}>📄 {d.label}</a>
          :<span key={d.label} style={{fontSize:11,color:"var(--muted)",border:"1px dashed var(--b1)",borderRadius:6,padding:"3px 8px"}}>{d.label}{d.req?" — missing":" — not provided"}</span>)}
      </div>
    </div>))}</div>;
};
const cvRefReportHtml=(sub,refDoc)=>cvRefReportData(sub,refDoc).map(b=>{
  const head=`<tr><td colspan="2" style="background:#fff1ec;font-weight:700;padding:8px">Reference ${b.n} — ${cvEsc(b.employer)} &nbsp;·&nbsp; ${b.responded?"Responded":"Awaiting response"}</td></tr>
  <tr><td class="k">Reference person</td><td>${cvEsc(b.refName)} &lt;${cvEsc(b.refEmail)}&gt;${b.refRole?" · "+cvEsc(b.refRole):""}${b.employeeId?" · Employee ID "+cvEsc(b.employeeId):""}</td></tr>`;
  const rows=b.rows.map(r=>{const v=CV_VERDICT[r.status];
    const says=v?`<b style="color:${v.c}">${v.t}</b>${(r.status==="false"||r.status==="partial")&&r.correction?` — correct value: <b>${cvEsc(r.correction)}</b>`:""}`:(b.responded?"Not answered":"Pending");
    return `<tr><td class="k">${cvEsc(r.label)}</td><td>Candidate said: ${cvEsc(r.claimed)}<br/>Reference says: ${says}</td></tr>`;}).join("");
  const rt=b.ratings?`<tr><td class="k">Ratings (out of 5)</td><td>Behaviour ${b.ratings.behaviour||"—"} · Ethics ${b.ratings.ethical||"—"} · Professional ${b.ratings.professional||"—"}</td></tr><tr><td class="k">Would rehire</td><td>${cvEsc(b.ratings.rehire||"—")}</td></tr><tr><td class="k">Comments</td><td>${cvEsc(b.ratings.comments||"—")}</td></tr>`:"";
  const docs=`<tr><td class="k">Documents</td><td>${b.docs.map(d=>d.url?`<a href="${cvEsc(d.url)}">${cvEsc(d.label)}</a>`:`${cvEsc(d.label)} (${d.req?"missing":"not provided"})`).join("<br/>")}</td></tr>`;
  return head+rows+rt+docs;
}).join("");
// Files the verified documents into Employee Files (same key the server uses: name + mobile)
const cvFileRefDocs=async(r,refDoc,isSA)=>{
  const phone=String(r.candidatePhone||"").replace(/\D/g,"").slice(-10);
  const slug=String(r.candidateName||"candidate").toLowerCase().replace(/[^a-z0-9]+/g,"-").replace(/^-|-$/g,"");
  const employeeKey=slug+"_"+(phone||"nophone");
  const cid=isSA?"":(r.companyId||"");
  const labels={payslip1:"Payslip (latest)",payslip2:"Payslip (2nd last)",payslip3:"Payslip (3rd last)",offer:"Offer letter",appointment:"Appointment letter",experience:"Experience letter",relieving:"Relieving letter"};
  const documents={};
  cvRefReportData(r.submission,refDoc).forEach(b=>{
    if(!b.responded)return;
    const sts=b.rows.map(x=>x.status).filter(Boolean);
    const verdict=sts.includes("false")?"disputed":sts.includes("partial")?"partially verified":"verified";
    const mark=verdict==="verified"?"✓ Verified":verdict==="partially verified"?"~ Partially verified":"✗ Disputed";
    const x=cvRefsOf(r.submission)[b.n-1]||{};
    Object.entries(x.docs||{}).forEach(([k,d])=>{
      if(!d?.url)return;
      documents["refverify_"+b.n+"_"+k]={url:d.url,name:d.name||"",label:`${labels[k]||k} — ${b.employer} (${mark})`,verificationCompany:b.employer,verificationStatus:verdict,source:"reference_check",verifiedBy:b.refName||"",verifiedAt:new Date().toISOString()};
    });
  });
  if(!Object.keys(documents).length)return false;
  await setDoc(doc(db,"employeeFiles",(cid||"unassigned")+"_"+employeeKey),cvClean({
    companyId:cid,employeeKey,candidateName:r.candidateName,phone:r.candidatePhone||"",email:r.candidateEmail||"",requestedBy:r.companyName||"",documents,updatedAt:new Date().toISOString(),
  }),{merge:true});
  return true;
};
const cvEsc=v=>String(v==null?"":v).replace(/&/g,"&amp;").replace(/</g,"&lt;").replace(/>/g,"&gt;").replace(/"/g,"&quot;");
const cvMask=(v)=>{const s=String(v||"");return s.length>4?"•".repeat(s.length-4)+s.slice(-4):s;};
const cvClean=(o)=>JSON.parse(JSON.stringify(o,(k,v)=>v===undefined?null:v));
// Derived live status — never stored as the source of truth for "expired"
const cvStatusOf=(r)=>{
  if(r.status==="draft"||r.status==="pending_approval")return r.status;
  const checks=r.checks||[];
  const res=r.results||{};
  if(r.submittedAt){
    const done=checks.filter(k=>res[k]?.status==="verified"||res[k]?.status==="failed"||res[k]?.status==="reviewed").length;
    if(checks.length&&done===checks.length)return "completed";
    if(done>0||checks.some(k=>res[k]))return "partial";
    return "submitted";
  }
  if(r.expiresAt&&new Date(r.expiresAt)<new Date())return "expired";
  return r.openedAt?"opened":"sent";
};
const cvCheckDef=(key)=>CV_CHECKS.find(c=>c.key===key);
// Reference check isn't a permission-gated search module (same as the existing Reference Check page)
const cvCan=(user,isSuperAdmin,key)=>key==="refcheck"||key==="education"||hasModuleAccess(user,isSuperAdmin,key);
const cvRefRows=(d)=>{
  d=d||{};const rows=[];
  [1,2].forEach(n=>{
    const ref=d["ref"+n],data=d["ref"+n+"_data"];
    if(!ref?.email)return;
    rows.push([`Reference ${n}`,`${ref.name||""} <${ref.email}>${ref.company?" · "+ref.company:""} — ${data?.submittedAt?"Responded":"Awaiting response"}`]);
    if(data?.submittedAt){
      rows.push([`  Behaviour / Ethics / Professional`,`${data.behaviour||"—"} / ${data.ethical||"—"} / ${data.professional||"—"} (out of 5)`]);
      rows.push([`  Would rehire`,data.rehire||"—"]);
      if(data.comments)rows.push([`  Comments`,data.comments]);
    }
  });
  return rows;
};

const cvCandidateEmailHtml=(r,link)=>`<p>Dear ${cvEsc(r.candidateName)},</p><p>${cvEsc(r.companyName||"Your employer")} has requested a background verification. Please open the secure link below and submit the few details asked — it takes about two minutes.</p><p><a href="${link}" style="background:#ff3131;color:white;padding:12px 24px;border-radius:8px;text-decoration:none;display:inline-block;margin-top:8px;">Submit My Details →</a></p><p style="font-size:12px;color:#666;">This link is valid for ${CV_EXPIRY_DAYS} days.</p><p>Best regards,<br/>${cvEsc(r.companyName||"Meitho")} Team</p>`;

const cvSendEmail=async(payload)=>{
  const res=await fetch(CF_SEND_CANDIDATE,{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify(payload)});
  let j={};try{j=await res.json();}catch(e){}
  if(!res.ok||j.error)throw new Error("Email could not be sent"+(j.error?" — "+j.error:" (server error "+res.status+")"));
  return j;
};
// Wallet: charge inside a Firestore transaction so two checks can't both
// pass on the same balance. Refund helper is used if the check errors out.
const cvCharge=async({companyId,cost,userId,userName,companyName,type,desc})=>{
  // Price, balance check, HR budget cap, wallet debit and transaction record all happen on the server.
  const rc=await walletChargeSrv([{type,qty:1}],desc,type);
  if(rc.charged)checkLowWalletAndNotify(companyId,companyName,rc.before,rc.after);
  return {before:rc.before,after:rc.after,txnId:rc.txnId};
};
const cvRefund=async({txnId})=>{
  if(!txnId)return null;
  const r=await walletRefundSrv(txnId);
  return r.after;
};

// Runs ONE verification against the Surepass-backed Cloud Functions.
// Returns {ok, data, error, awaiting?, clientId?, link?}
// Criminal check: keep a court record only when BOTH the candidate's name and the
// father's name appear in it. Records that match the name alone are excluded.
const cvNorm=(v)=>String(v||"").toLowerCase().replace(/\b(s\/o|d\/o|w\/o|c\/o|son of|daughter of|wife of|shri|smt|sri|mr|mrs|ms|late)\b/g," ").replace(/[^a-z0-9]+/g," ").trim();
const cvDlMessage=(name,company,link)=>`Hi ${name||""}, ${company||"your employer"} has requested your Aadhaar verification through Meitho. Please open this secure DigiLocker link and complete the steps (about 2 minutes): ${link}`;
const cvEmailDlLink=async(r,link)=>{
  if(!r.candidateEmail||!link)return false;
  const html=`<div style="font-family:Arial,sans-serif;max-width:560px;margin:auto;color:#0f172a"><h2 style="margin:0 0 12px">Aadhaar verification</h2><p>Hi ${cvEsc(r.candidateName||"")},</p><p>${cvEsc(r.companyName||"Your employer")} has requested your Aadhaar verification through Meitho. It takes about 2 minutes via DigiLocker.</p><p style="margin:22px 0"><a href="${link}" style="background:#ff914d;color:#fff;padding:12px 22px;border-radius:8px;text-decoration:none;font-weight:700">Verify with DigiLocker</a></p><p style="font-size:12px;color:#64748b">If the button does not work, copy this link: ${link}</p><p style="font-size:12px;color:#64748b">Questions? Write to verification@meitho.com</p></div>`;
  try{
    const res=await fetch("https://sendcandidateemail-mtvra6khyq-uc.a.run.app",{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({to:r.candidateEmail,subject:"Action needed: complete your Aadhaar verification",html,name:r.candidateName})});
    return res.ok;
  }catch(e){return false;}
};
const cvAddrEmail=async(r,checkId)=>{
  if(!r.candidateEmail||!checkId)return false;
  const link=`${window.location.origin}/address-verify/${checkId}`;
  try{
    const res=await fetch(CF_SEND_CANDIDATE,{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({
      candidateEmail:r.candidateEmail,candidateName:r.candidateName,
      customSubject:`Reminder: Address Verification Required — ${r.companyName||"Meitho"}`,
      customBody:`<p>Dear ${cvEsc(r.candidateName)},</p><p>${cvEsc(r.companyName||"Your employer")} needs to verify your address as part of your background check.</p><p>Please click below, confirm your address, and take a quick selfie at that location. Open the link in Chrome or Safari and allow location access.</p><p><a href="${link}" style="background:#ff3131;color:white;padding:12px 24px;border-radius:8px;text-decoration:none;display:inline-block;margin-top:8px;">Verify My Address →</a></p><p>Best regards,<br/>${cvEsc(r.companyName||"Meitho")} Team</p>`,
    })});
    return res.ok;
  }catch(e){return false;}
};
const cvMatchCases=(cases,candidateName,fatherName)=>{
  const nameToks=cvNorm(candidateName).split(" ").filter(t=>t.length>1);
  const fatherToks=cvNorm(fatherName).split(" ").filter(t=>t.length>1);
  const fatherPhrase=fatherToks.join(" ");
  const matched=[],excluded=[];
  (cases||[]).forEach(c=>{
    const text=" "+cvNorm([c.petitioner,c.respondent,c.case_name,c.father_name].filter(Boolean).join(" "))+" ";
    const nameOk=!nameToks.length||nameToks.every(t=>text.includes(" "+t+" "));
    const fatherOk=!fatherPhrase||text.includes(" "+fatherPhrase+" ");
    (nameOk&&fatherOk?matched:excluded).push(c);
  });
  return {matched,excluded};
};
const cvRunCheck=async(key,sub,candidateName)=>{
  const headers=await getAuthedHeaders();
  const post=async(url,body)=>{const res=await fetch(url,{method:"POST",headers,body:JSON.stringify(body)});return res.json();};
  try{
    if(key==="uan"){
      const json=await post(CV_URLS.uan,{uan:String(sub.uan||"").replace(/\D/g,"")});
      if(json.status_code!==200)return {ok:false,error:json.message||"No records found"};
      const d=json.data||{};
      const emp=(d.employment_history||d.employments||[]).map(e=>({
        company:e.establishment_name||e.company_name||e.employer_name||"",
        designation:e.designation||e.post||"",
        doj:e.date_of_joining||e.doj||"",
        dor:(e.date_of_exit||e.dor||"").startsWith("1800")?"":(e.date_of_exit||e.dor||""),
      })).sort((a,b)=>new Date(a.doj||"1900")-new Date(b.doj||"1900"));
      return {ok:true,data:{uan:sub.uan,name:d.name||d.employee_name||d.full_name||"",gender:d.gender||"",employmentHistory:emp}};
    }
    if(key==="pan"){
      const json=await post(CV_URLS.pan,{pan:String(sub.pan||"").toUpperCase()});
      if(json.status_code!==200)return {ok:false,error:json.message||"PAN not found"};
      const d=json.data||{};
      return {ok:true,data:{pan:d.pan_number||sub.pan,name:d.full_name||d.name||"",dob:d.dob||d.date_of_birth||"",status:d.status||d.pan_status||"Active",type:d.category||d.pan_type||"Individual",aadhaar_linked:!!d.aadhaar_linked,gender:d.gender||""}};
    }
    if(key==="dl"){
      const json=await post(CV_URLS.dl,{dl_number:sub.dl_number,dob:sub.dl_dob});
      if(!json.success)return {ok:false,error:json.error||"DL not found or invalid"};
      const d=json.data||{};
      const doe=d.doe&&!String(d.doe).startsWith("1800")?d.doe:"";
      return {ok:true,data:{dl_number:d.dl_number,name:d.name,dob:d.dob,doe,doi:d.doi,state:d.state,father_name:d.father_name,blood_group:d.blood_group,permanent_address:d.permanent_address,vehicle_classes:d.vehicle_classes||[],isExpired:!!(doe&&new Date(doe)<new Date())}};
    }
    if(key==="ecourt"){
      const json=await post(CV_URLS.ecourt,{name:candidateName,father_name:sub.father_name,address:sub.court_address});
      if(!json.success)return {ok:false,error:json.error||"eCourt search failed"};
      const d=json.data||{};
      const {matched,excluded}=cvMatchCases(d.cases||[],candidateName,sub.father_name);
      const crim=matched.filter(c=>c.case_type==="Criminal");
      return {ok:true,data:{name:d.name||candidateName,father_name:sub.father_name||d.father_name,
        total_cases:matched.length,criminal_cases:crim.length,civil_cases:matched.filter(c=>c.case_type==="Civil").length,
        pending_cases:matched.filter(c=>c.case_status==="Pending").length,disposed_cases:matched.filter(c=>c.case_status==="Disposed").length,
        has_criminal:crim.length>0,excluded_count:excluded.length,match_rule:"Candidate name and father's name both matched",
        cases:matched.slice(0,25)}};
    }
    if(key==="aadhaar"){
      const json=await post(CV_URLS.aadhaar,{aadhaar:String(sub.aadhaar||"").replace(/\D/g,"")});
      if(json.status_code!==200)return {ok:false,error:"DigiLocker error: "+(json.message||json.error||"Verification failed")};
      const d=json.data||{};
      return {ok:true,awaiting:true,clientId:d.client_id||d.reference_id||"",link:d.url||d.redirect_url||d.digilocker_url||d.authorization_url||"",data:{}};
    }
  }catch(e){return {ok:false,error:e.message};}
  return {ok:false,error:"Unsupported check"};
};

// One-line summary rows for a result — used by the detail view and the PDF
const cvSummaryRows=(key,d)=>{
  d=d||{};
  if(key==="uan"){
    const rows=[["Name",d.name],["UAN",d.uan],["Gender",d.gender]];
    (d.employmentHistory||[]).forEach((j,i)=>rows.push([`Employer ${i+1}`,`${j.company||"—"}${j.designation?" · "+j.designation:""} (${j.doj||"—"} → ${j.dor||"Present"})`]));
    return rows;
  }
  if(key==="pan")return [["Name",d.name],["PAN",d.pan],["DOB",d.dob],["Status",d.status],["Category",d.type],["Aadhaar linked",d.aadhaar_linked?"Yes":"No"]];
  if(key==="dl")return [["Name",d.name],["DL number",d.dl_number],["DOB",d.dob],["Issued",d.doi],["Expiry",d.doe||"—"],["Validity",d.isExpired?"EXPIRED":"Valid"],["Classes",(d.vehicle_classes||[]).join(", ")],["State",d.state]];
  if(key==="ecourt"){
    const rows=[["Name",d.name],["Father's name",d.father_name],["Matching rule",d.match_rule||"Name search"],["Cases matched",d.total_cases],["Criminal cases",d.criminal_cases],["Pending",d.pending_cases],["Result",d.has_criminal?"CRIMINAL RECORD FOUND":(d.total_cases>0?"No criminal record found ("+d.total_cases+" other case(s) listed below)":"Verified — no case found")]];
    if(d.excluded_count>0)rows.push(["Records excluded",d.excluded_count+" other record(s) with a similar name were excluded because the father's name did not match"]);
    (d.cases||[]).forEach((c,i)=>rows.push([`Case ${i+1}`,`${c.case_type||""} · ${c.case_status||""} · ${c.court_name||""} ${c.cnr_number?("· "+c.cnr_number):""}`]));
    return rows;
  }
  if(key==="aadhaar")return [["Name",d.name],["DOB",d.dob],["Gender",d.gender],["Aadhaar",d.masked_aadhaar],["Father / Care of",d.father_name],["Address",d.address]];
  return Object.entries(d).map(([k,v])=>[k,typeof v==="object"?JSON.stringify(v):v]);
};

const cvDownloadPdf=(r,addrDoc,refDoc)=>{
  const res=r.results||{};
  const sub=r.submission||{};
  const status=CV_STATUS[cvStatusOf(r)]?.label||"";
  const sections=(r.checks||[]).map(key=>{
    const def=cvCheckDef(key);const x=res[key];
    let body="";
    if(key==="address"){
      const a=addrDoc;
      body=`<tr><td class="k">Present address</td><td>${cvEsc(sub.presentAddress)}</td></tr><tr><td class="k">Permanent address</td><td>${cvEsc(sub.permanentAddress||sub.presentAddress)}</td></tr>`;
      if(a){
        const one=(lb,v)=>`<tr><td class="k">${lb} — selfie / GPS check</td><td>${v?.status==="submitted"?(v.locationNotCaptured?"Selfie received; location NOT captured, manual review required":v.matched?"Location matched":"Location did NOT match")+(v.distanceMeters!=null&&!v.manualOverride?` (${Math.round(v.distanceMeters)} m away)`:""):"Not verified (candidate verified the other address)"}</td></tr>`;
        const pvv=a.presentVerification||{},mv=a.permanentVerification||{};
        if(pvv.status!=="submitted"&&mv.status!=="submitted")body+=`<tr><td class="k">Selfie / GPS check</td><td>Awaiting candidate</td></tr>`;
        else body+=one("Present address",pvv)+(a.sameAddress?"":one("Permanent address",mv));
      }
    } else if(key==="education"){
      body=(sub.education||[]).map(it=>{
        const v=x?.items?.[it.k];const d=sub.candDocs?.[it.k];const vd=x?.status==="reviewed"&&v?CV_EDU_VERDICT[v.status]:null;
        const det=CV_EDU_FIELDS.filter(f=>it[f.k]).map(f=>cvEsc(f.label)+": "+cvEsc(it[f.k])).join(" · ");
        return `<tr><td class="k">${cvEsc(it.level)}</td><td>${det}<br/><b style="color:${vd?vd[1]:"#64748b"}">${vd?cvEsc(vd[0]):"Verification pending"}</b>${v?.comment?" — "+cvEsc(v.comment):""}${d?.url?` · <a href="${cvEsc(d.url)}">certificate</a>`:""}</td></tr>`;
      }).join("")||`<tr><td class="k">Details</td><td>Not submitted</td></tr>`;
    } else if(key==="refcheck"&&x){
      body=cvRefReportHtml(sub,refDoc);
    } else if(x?.status==="verified"||x?.status==="failed"){
      body=x.status==="failed"?`<tr><td class="k">Result</td><td style="color:#dc2626">Failed — ${cvEsc(x.error||"")}</td></tr>`:cvSummaryRows(key,x.data).filter(([,v])=>v!==undefined&&v!==""&&v!==null).map(([k,v])=>`<tr><td class="k">${cvEsc(k)}</td><td>${cvEsc(v)}</td></tr>`).join("");
    } else if(x?.status==="awaiting_candidate"){
      body=`<tr><td class="k">Status</td><td>Awaiting candidate DigiLocker consent</td></tr>`;
    } else {
      const given=key==="refcheck"?[]:(def.fields||[]).map(f=>[f.label,key==="aadhaar"?cvMask(sub[f.k]):sub[f.k]]).filter(([,v])=>v);
      body=(key==="refcheck"?cvRefReportHtml(sub,null):given.map(([k,v])=>`<tr><td class="k">${cvEsc(k)}</td><td>${cvEsc(v)}</td></tr>`).join(""))+`<tr><td class="k">Verification</td><td>Not run yet</td></tr>`;
    }
    const st=x?.status==="reviewed"?(x.overall==="verified"?"✓ All verified":"⚠ Issues found"):x?.status==="in_review"?"Under review":x?.status==="verified"?"✓ Verified":x?.status==="failed"?"✗ Failed":x?.status==="awaiting_candidate"||x?.status==="link_sent"?"Pending candidate":"Not run";
    const chip=(x?.status==="verified"||(x?.status==="reviewed"&&x.overall==="verified"))?"ok":(x?.status==="failed"||(x?.status==="reviewed"&&x.overall!=="verified"))?"bad":(x?.status==="awaiting_candidate"||x?.status==="link_sent"||x?.status==="in_review")?"wait":"";
    return `<div class="sec"><div class="sec-h"><span>${def.icon} ${cvEsc(def.label)}</span><span class="chip ${chip}">${st}</span></div><table class="kv">${body}</table></div>`;
  }).join("");
  const attachHtml=(()=>{
    const candItems=cvCandDocList(r.submission).map(d=>{
      const isImg=/\.(jpe?g|png)$/i.test(d.name||"");
      return `<div style="margin:10px 0;page-break-inside:avoid"><div style="font-weight:700;font-size:12px;margin-bottom:4px">${cvEsc(d.label)}</div>${isImg?`<img src="${cvEsc(d.url)}" style="max-width:100%;max-height:900px;border:1px solid #e2e8f0"/>`:`<div style="font-size:12px">PDF document — <a href="${cvEsc(d.url)}">open file</a></div>`}</div>`;
    }).join("");
    const candBlock=candItems?`<div style="page-break-before:always"><div class="sec-h" style="background:#fff3ec;padding:8px 14px;font-weight:700;border-radius:8px">Candidate documents — Aadhaar, PAN &amp; education</div>${candItems}</div>`:"";
    if(!(r.checks||[]).includes("refcheck"))return candBlock;
    const blocks=candBlock+cvRefsOf(r.submission).map((x,i)=>{
      const items=CV_REF_DOCS.filter(d=>x.docs?.[d.k]?.url).map(d=>{
        const f=x.docs[d.k];const isImg=/\.(jpe?g|png)$/i.test(f.name||"");
        return `<div style="margin:10px 0;page-break-inside:avoid"><div style="font-weight:700;font-size:12px;margin-bottom:4px">${cvEsc(d.label)} <span style="font-weight:400;color:#64748b">(${cvEsc(f.name||"")})</span></div>${isImg?`<img src="${cvEsc(f.url)}" style="max-width:100%;max-height:900px;border:1px solid #e2e8f0"/>`:`<div style="font-size:12px">PDF document — <a href="${cvEsc(f.url)}">open / download</a></div>`}</div>`;
      }).join("");
      return items?`<div style="page-break-before:always"><div class="sec-h" style="background:#fff3ec;padding:8px 14px;font-weight:700;border-radius:8px">Attachments — Employer ${i+1}: ${cvEsc(x.company||"")}</div>${items}</div>`:"";
    }).join("");
    return blocks;
  })();
  const stTone={completed:"ok",partial:"warn",submitted:"info",opened:"info",sent:"info",expired:"bad"}[cvStatusOf(r)]||"info";
  const html=buildReportHtml({
    title:"BACKGROUND VERIFICATION REPORT",reportId:r.id,dateStr:new Date().toLocaleString("en-IN",{day:"2-digit",month:"short",year:"numeric",hour:"2-digit",minute:"2-digit"}),
    status,statusTone:stTone,preparedFor:r.companyName||"",consentAt:(r.consent?.at||"").slice(0,16).replace("T"," "),
    candidate:[["Candidate",r.candidateName],["Email",r.candidateEmail],["Phone",r.candidatePhone||"—"],["Requested by",(r.initiatedByName||"")+(r.companyName?" · "+r.companyName:"")],["Sent",(r.createdAt||"").slice(0,10)],["Submitted",(r.submittedAt||"—").slice(0,10)]],
    bodyHtml:sections+attachHtml,
  });
  const w=window.open("","_blank","width=900,height=800");
  if(!w){alert("Please allow popups to download the report");return;}
  w.document.write(html);w.document.close();
};

function CandidateVerificationPage({user,company,wallets,setWallets,addToast,setHistory}){
  const isSuperAdmin=user.role==="superadmin";
  const companyId=user.companyId||company?.id||"";
  const [reqs,setReqs]=useState([]);
  const [loading,setLoading]=useState(true);
  const [showNew,setShowNew]=useState(false);
  const [sel,setSel]=useState(null);       // id of request open in detail modal
  const [q,setQ]=useState("");
  const [fStatus,setFStatus]=useState("all");
  const [busyKey,setBusyKey]=useState("");
  const [addrDoc,setAddrDoc]=useState(null);
  const [refDoc,setRefDoc]=useState(null);
  const [form,setForm]=useState({candidateName:"",candidateEmail:"",candidatePhone:"",checks:[],refCount:1});
  const [sending,setSending]=useState(false);
  const [eduDraft,setEduDraft]=useState({});
  const [,tick]=useState(0);

  const price=(def)=>isSuperAdmin?0:getCompanyPrice(company,def.priceKey);
  // Reference check is billed per reference email
  const costOf=(r,def)=>{
    if(def.key!=="refcheck")return price(def);
    const n=Math.max(1,r?.refCount||cvRefsOf(r?.submission).length||1);
    return price(def)*n;
  };
  const allowed=CV_CHECKS.filter(c=>cvCan(user,isSuperAdmin,c.key));

  // Live list — status changes (opened / submitted / verified) appear without refresh
  useEffect(()=>{
    const base=isSuperAdmin
      ?query(collection(db,"candidate_verifications"))
      :query(collection(db,"candidate_verifications"),where("companyId","==",companyId));
    const unsub=onSnapshot(base,(snap)=>{
      const d=snap.docs.map(x=>({id:x.id,...x.data()}));
      d.sort((a,b)=>new Date(b.createdAt||0)-new Date(a.createdAt||0));
      setReqs(d);setLoading(false);
    },(e)=>{console.warn("candidate_verifications load error:",e.message);setLoading(false);});
    const t=setInterval(()=>tick(n=>n+1),60000); // re-evaluate "expired"
    return()=>{unsub();clearInterval(t);};
  },[isSuperAdmin,companyId]);

  const cur=reqs.find(r=>r.id===sel)||null;

  // Address check lives in the existing address_verifications collection
  useEffect(()=>{
    setAddrDoc(null);
    const aid=cur?.results?.address?.addressCheckId;
    if(!aid)return;
    return onSnapshot(doc(db,"address_verifications",aid),(s)=>{
      const d=s.exists()?{id:s.id,...s.data()}:null;
      setAddrDoc(d);
      if(d&&d.status==="completed"&&cur.results.address.status!=="verified"){
        updateDoc(doc(db,"candidate_verifications",cur.id),{"results.address.status":"verified","results.address.completedAt":new Date().toISOString()}).catch(()=>{});
      }
    },()=>{});
  },[cur?.id,cur?.results?.address?.addressCheckId]);

  // Reference check lives in the existing refchecks collection — follow it live
  useEffect(()=>{
    setRefDoc(null);
    const rid=cur?.results?.refcheck?.refCheckId;
    if(!rid)return;
    return onSnapshot(doc(db,"refchecks",rid),(s)=>{
      const d=s.exists()?{id:s.id,...s.data()}:null;
      setRefDoc(d);
      // When every reference has responded, mark this check verified on the request
      if(d&&d.overallStatus==="complete"&&cur.results.refcheck.status!=="verified"){
        updateDoc(doc(db,"candidate_verifications",cur.id),{"results.refcheck.status":"verified","results.refcheck.completedAt":new Date().toISOString()}).catch(()=>{});
      }
    },()=>{});
  },[cur?.id,cur?.results?.refcheck?.refCheckId]);

  const toggleCheck=(k)=>setForm(f=>({...f,checks:f.checks.includes(k)?f.checks.filter(x=>x!==k):[...f.checks,k]}));

  // Keep the company wallet live on this page (it is not loaded globally for company users)
  useEffect(()=>{
    if(isSuperAdmin||!companyId)return;
    return onSnapshot(doc(db,"wallets",companyId),(snap)=>{
      if(snap.exists())setWallets&&setWallets(p=>({...p,[companyId]:{...(p[companyId]||{}),id:snap.id,...snap.data()}}));
    },()=>{});
  },[companyId,isSuperAdmin]);

  const isCompanyAdmin=user.role==="admin"||user.companyAdmin===true;
  const [confirmFor,setConfirmFor]=useState(null);
  const estOf=(rec)=>isSuperAdmin?0:(rec.checks||[]).reduce((t,k)=>t+costOf(rec,cvCheckDef(k)),0);
  const blankForm={candidateName:"",candidateEmail:"",candidatePhone:"",checks:[],refCount:1};

  const validateForm=()=>{
    const name=form.candidateName.trim(),email=form.candidateEmail.trim();
    if(!name||!email){addToast("Enter candidate name and email","error");return false;}
    if(!/^\S+@\S+\.\S+$/.test(email)){addToast("Enter a valid email address","error");return false;}
    if(!form.candidatePhone.trim()){addToast("Enter candidate phone number","error");return false;}
    if(form.checks.length===0){addToast("Select at least one verification","error");return false;}
    return true;
  };

  // Save the form as a Draft (nothing is sent to the candidate). Returns the saved record.
  const saveDraft=async()=>{
    if(!validateForm())return null;
    setSending(true);
    try{
      const id=form.id||("CV-"+Date.now().toString(36).toUpperCase()+Math.random().toString(36).slice(2,5).toUpperCase());
      const rec={
        id,companyId:isSuperAdmin?"superadmin":companyId,companyName:isSuperAdmin?"Meitho Admin":(company?.name||""),
        initiatedBy:user.id,initiatedByName:user.name||user.email,
        candidateName:form.candidateName.trim(),candidateEmail:form.candidateEmail.trim(),candidatePhone:form.candidatePhone.trim(),
        checks:form.checks,refCount:form.checks.includes("refcheck")?(form.refCount===2?2:1):0,
        status:"draft",results:{},submission:null,
        createdAt:form.createdAt||new Date().toISOString(),draftedAt:new Date().toISOString(),remindersSent:0,
      };
      await setDoc(doc(db,"candidate_verifications",id),rec,{merge:true});
      setShowNew(false);setForm(blankForm);
      return rec;
    }catch(e){addToast("Could not save draft: "+e.message,"error");return null;}
    finally{setSending(false);}
  };

  const onlySaveDraft=async()=>{const rec=await saveDraft();if(rec)addToast("Saved as draft. Send it from the list when you are ready.","success");};
  const saveAndReview=async()=>{const rec=await saveDraft();if(rec)openConfirm(rec);};

  // Work out whether sending is within this HR user's wallet share
  const openConfirm=async(rec)=>{
    const est=estOf(rec);
    const bal=(wallets?.[companyId]?.balance)||0;
    let cap=null;
    if(!isSuperAdmin&&!isCompanyAdmin&&user.id){
      try{
        const a=await getDoc(doc(db,"wallets",companyId,"hrAllocations",user.id));
        if(a.exists()&&(a.data().allocated||0)>0){const d=a.data();cap={allocated:d.allocated,spent:d.spent||0,left:Math.max(0,d.allocated-(d.spent||0))};}
      }catch(e){cap=null;}
    }
    const over=!!cap&&est>cap.left;
    setConfirmFor({rec,est,bal,cap,over,shortfall:over?est-cap.left:0});
  };

  const deliver=async(rec)=>{
    const now=new Date();
    const patch={status:"sent",createdAt:now.toISOString(),sentAt:now.toISOString(),expiresAt:new Date(now.getTime()+CV_EXPIRY_DAYS*86400000).toISOString(),remindersSent:0};
    await updateDoc(doc(db,"candidate_verifications",rec.id),patch);
    const r2={...rec,...patch};
    const link=`${window.location.origin}/candidate-verify/${rec.id}`;
    await cvSendEmail({
      candidateEmail:rec.candidateEmail,candidateName:rec.candidateName,
      customSubject:`Background Verification — Details Required (${rec.companyName||"Meitho"})`,
      customBody:cvCandidateEmailHtml(r2,link),
    });
  };

  const confirmSend=async()=>{
    if(!confirmFor)return;
    const {rec,est,over,shortfall}=confirmFor;
    setSending(true);
    try{
      if(over){
        await updateDoc(doc(db,"candidate_verifications",rec.id),{status:"pending_approval",approvalRequestedAt:new Date().toISOString(),approvalShortfall:shortfall,approvalEstimate:est});
        createNotification({companyId:isSuperAdmin?null:companyId,targetRole:"admin",type:"approval_request",title:"Approval needed: candidate verification",message:`${user.name||user.email} wants to send ${rec.candidateName} (about ₹${est}), which is ₹${shortfall} beyond their wallet share.`,link:"candverify"});
        addToast("This is beyond your wallet share, so it was sent to your Company Admin for approval.","success");
      }else{
        await deliver(rec);
        addToast("Request sent — candidate has been emailed.","success");
      }
      setConfirmFor(null);setSel(null);
    }catch(e){addToast("Failed: "+e.message,"error");}
    setSending(false);
  };

  const approveRequest=async(r)=>{
    setSending(true);
    try{
      const shortfall=r.approvalShortfall||0;
      if(shortfall>0&&r.initiatedBy){
        const ref=doc(db,"wallets",companyId,"hrAllocations",r.initiatedBy);
        const snap=await getDoc(ref);
        const d=snap.exists()?snap.data():{allocated:0,spent:0};
        await setDoc(ref,{userId:r.initiatedBy,name:r.initiatedByName||"",allocated:(d.allocated||0)+shortfall,spent:d.spent||0,balance:Math.max(0,(d.allocated||0)+shortfall-(d.spent||0)),updatedAt:new Date().toISOString()},{merge:true});
      }
      await deliver(r);
      createNotification({companyId:isSuperAdmin?null:companyId,targetRole:"admin",targetUserId:r.initiatedBy,type:"approval_result",title:"Request approved and sent",message:`${r.candidateName}'s verification was approved and sent to the candidate.`,link:"candverify"});
      addToast("Approved and sent to the candidate. The user's wallet share was increased by ₹"+shortfall+".","success");
      setSel(null);
    }catch(e){addToast("Could not approve: "+e.message,"error");}
    setSending(false);
  };
  const rejectRequest=async(r)=>{
    try{
      await updateDoc(doc(db,"candidate_verifications",r.id),{status:"draft",approvalRejectedAt:new Date().toISOString(),approvalShortfall:null});
      createNotification({companyId:isSuperAdmin?null:companyId,targetRole:"admin",targetUserId:r.initiatedBy,type:"approval_result",title:"Request not approved",message:`${r.candidateName}'s verification was not approved. It is back in Drafts.`,link:"candverify"});
      addToast("Sent back to drafts.","success");setSel(null);
    }catch(e){addToast("Failed: "+e.message,"error");}
  };
  // Edit details after the candidate has submitted (HR / Company Admin / Super Admin)
  const [editFor,setEditFor]=useState(null);
  const [editSaving,setEditSaving]=useState(false);
  const openEdit=(r)=>{
    const sub=r.submission||{};
    const fields={};
    (r.checks||[]).forEach(k=>{const d=cvCheckDef(k);(d?.fields||[]).forEach(f=>{fields[f.k]=sub[f.k]||"";});});
    setEditFor({id:r.id,name:r.candidateName||"",email:r.candidateEmail||"",phone:r.candidatePhone||"",fields,edu:JSON.parse(JSON.stringify(sub.education||[])),hadResults:Object.keys(r.results||{}).length>0,submitted:!!r.submittedAt,checks:r.checks||[]});
  };
  const saveEdit=async()=>{
    if(!editFor)return;
    if(!editFor.name.trim()||!/^\S+@\S+\.\S+$/.test(editFor.email.trim())||!editFor.phone.trim()){addToast("Name, a valid email and phone are required","error");return;}
    setEditSaving(true);
    try{
      const patch={candidateName:editFor.name.trim(),candidateEmail:editFor.email.trim(),candidatePhone:editFor.phone.trim(),updatedAt:new Date().toISOString()};
      if(editFor.submitted){
        (editFor.checks||[]).forEach(k=>{const d=cvCheckDef(k);(d?.fields||[]).forEach(f=>{
          const raw=String(editFor.fields[f.k]??"");
          const v=f.clean?f.clean(raw):raw.trim();
          patch["submission."+f.k]=v;
          if(f.type==="addr")patch["submission."+f.k+"_parts"]=null; // typed manually now
        });});
        if((editFor.edu||[]).length)patch["submission.education"]=editFor.edu;
        patch["submission.editedAt"]=new Date().toISOString();
        patch["submission.editedBy"]=user.name||user.email;
      }
      await updateDoc(doc(db,"candidate_verifications",editFor.id),patch);
      addToast("Details updated"+(editFor.hadResults?". Checks that already ran used the old details; re-run them if needed.":"."),"success");
      setEditFor(null);
    }catch(e){addToast("Could not save: "+e.message,"error");}
    setEditSaving(false);
  };
  const editDraft=(r)=>{setForm({id:r.id,createdAt:r.createdAt,candidateName:r.candidateName,candidateEmail:r.candidateEmail,candidatePhone:r.candidatePhone,checks:r.checks||[],refCount:r.refCount||1});setSel(null);setShowNew(true);};
  const deleteDraft=async(r)=>{
    if(!window.confirm("Delete this draft for "+r.candidateName+"?"))return;
    try{await deleteDoc(doc(db,"candidate_verifications",r.id));addToast("Draft deleted","success");setSel(null);}
    catch(e){addToast("Could not delete: "+e.message,"error");}
  };

  const resend=async(r)=>{
    try{
      const exp=new Date(Date.now()+CV_EXPIRY_DAYS*86400000).toISOString();
      await updateDoc(doc(db,"candidate_verifications",r.id),{expiresAt:exp,remindersSent:0,status:r.openedAt?"opened":"sent"});
      const link=`${window.location.origin}/candidate-verify/${r.id}`;
      await cvSendEmail({
        candidateEmail:r.candidateEmail,candidateName:r.candidateName,
        customSubject:`Reminder — Background Verification details required (${r.companyName||"Meitho"})`,
        customBody:cvCandidateEmailHtml(r,link),
      });
      addToast("Link re-sent and validity extended by "+CV_EXPIRY_DAYS+" days.","success");
    }catch(e){addToast("Could not resend: "+e.message,"error");}
  };

  const logHistory=async(r,key,ok,data,cost)=>{
    const def=cvCheckDef(key);
    const audit={
      userId:user.id,userName:user.name||user.email,
      companyId:isSuperAdmin?"superadmin":companyId,companyName:isSuperAdmin?"Meitho Admin":(company?.name||""),
      searchType:key==="address"?"address_verify":key,
      searchValue:`${r.candidateName} (${r.id})`,found:ok,source:"candidate_link",isSuperAdmin,
      timestamp:new Date().toISOString(),cost,resultName:data?.name||r.candidateName,
      employeeData:cvClean(data||{}),
    };
    try{await addSearchHistory(audit);setHistory&&setHistory(p=>[audit,...p]);}catch(e){console.warn("history log failed",e);}
    return def;
  };

  const saveResult=(r,key,obj)=>updateDoc(doc(db,"candidate_verifications",r.id),{[`results.${key}`]:cvClean(obj),updatedAt:new Date().toISOString()});

  const runCheck=async(r,key)=>{
    if(!cvCan(user,isSuperAdmin,key)){addToast("You don't have access to this module. Ask your Company Admin.","error");return;}
    const def=cvCheckDef(key);
    const sub=r.submission||{};
    if(key==="education"&&!(sub.education||[]).length){addToast("The candidate has not submitted education details.","error");return;}
    const cost=costOf(r,def);
    const cid=companyId;
    const desc=`${def.label} — ${r.candidateName} (${r.id})`;
    setBusyKey(r.id+key);
    let charged=false,chargeTxn=null;
    try{
      if(!isSuperAdmin){
        // Read the live balance from the server (the local copy may not be loaded yet)
        let bal=(wallets?.[cid]?.balance)||0;
        try{const ws=await getDoc(doc(db,"wallets",cid));if(ws.exists())bal=ws.data().balance||0;}catch(e){/* fall back to local copy */}
        if(bal<cost){addToast("⚠ Insufficient wallet balance. Need ₹"+cost+", available ₹"+bal+".","error");setBusyKey("");return;}
        const {after,txnId:tid}=await cvCharge({companyId:cid,cost,userId:user.id,userName:user.name||user.email,companyName:company?.name,type:def.priceKey,desc});
        charged=true;chargeTxn=tid;
        setWallets&&setWallets(p=>({...p,[cid]:{...(p[cid]||{}),balance:after}}));
      }

      if(key==="refcheck"){
        const rlist=cvRefsOf(sub).slice(0,Math.max(1,r.refCount||2));
        if(!rlist.length)throw new Error("Candidate has not provided any reference details");
        const refs=[0,1].map(i=>rlist[i]?{name:rlist[i].refName||"",email:rlist[i].refEmail||"",company:rlist[i].company||"",status:"pending"}:{name:"",email:"",company:"",status:"pending"});
        const companies=rlist.map(x=>({company:x.company||"",designation:x.designation||"",employeeId:x.employeeId||"",doj:x.doj||"",dor:x.dor||"",lastSalary:x.lastSalary||"",reasonForLeaving:x.reasonForLeaving||"",docs:x.docs||{}}));
        const refId="RC-"+Date.now().toString(36).toUpperCase();
        await setDoc(doc(db,"refchecks",refId),cvClean({
          id:refId,companyId:isSuperAdmin?"":cid,companyName:isSuperAdmin?"Meitho":(company?.name||r.companyName||""),
          initiatedBy:user.id,initiatedByName:user.name||user.email,
          candidateName:r.candidateName,candidateEmail:r.candidateEmail,candidatePhone:r.candidatePhone,designation:"",
          ref1:refs[0],ref2:refs[1],candidateStatus:"pending",overallStatus:"pending",cost,
          initiatedAt:new Date().toISOString(),fromCandidateVerification:r.id,refCount:rlist.length,
        }));
        // "candidate" step marks the candidate side done and makes the server email each reference
        const rr=await fetch(CF_SAVE_REFCHECK,{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({checkId:refId,type:"candidate",data:{fullName:r.candidateName,companies}})});
        const rj=await rr.json().catch(()=>({}));
        if(!rr.ok||rj.error)throw new Error("Could not send reference emails"+(rj.error?" — "+rj.error:""));
        await saveResult(r,key,{status:"link_sent",refCheckId:refId,cost,ranAt:new Date().toISOString(),ranBy:user.name||user.email});
        await logHistory(r,key,true,{name:r.candidateName},cost);
        addToast("Reference emails sent. You'll see responses here as they come in.","success");
      } else if(key==="education"){
        await saveResult(r,key,{status:"in_review",cost,ranAt:new Date().toISOString(),ranBy:user.name||user.email});
        await logHistory(r,key,true,{name:r.candidateName},cost);
        createNotification({companyId:null,targetRole:"superadmin",type:"candidate_submitted",title:"Education verification to review",message:`${r.candidateName} (${r.companyName||"Meitho"}) — certificates are ready for manual verification.`,link:"candverify"});
        addToast("Sent for verification. The Meitho team will mark each certificate and you'll be notified.","success");
      } else if(key==="address"){
        const checkId="AV-"+Date.now().toString(36).toUpperCase();
        const same=!sub.permanentAddress||sub.permanentAddress.trim()===sub.presentAddress.trim();
        await setDoc(doc(db,"address_verifications",checkId),{
          id:checkId,companyId:isSuperAdmin?"superadmin":cid,companyName:isSuperAdmin?"Meitho Admin":(company?.name||""),
          initiatedBy:user.id,initiatedByName:user.name||user.email,
          candidateName:r.candidateName,candidateEmail:r.candidateEmail,candidatePhone:r.candidatePhone,
          presentAddress:sub.presentAddress,permanentAddress:same?sub.presentAddress:sub.permanentAddress,sameAddress:same,
          presentParts:sub.presentAddress_parts||null,permanentParts:same?(sub.presentAddress_parts||null):(sub.permanentAddress_parts||null),
          presentVerification:{status:"pending",attempts:0},permanentVerification:same?null:{status:"pending",attempts:0},
          status:"pending",cost,initiatedAt:new Date().toISOString(),fromCandidateVerification:r.id,
        });
        const link=`${window.location.origin}/address-verify/${checkId}`;
        await fetch(CF_SEND_CANDIDATE,{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({
          candidateEmail:r.candidateEmail,candidateName:r.candidateName,
          customSubject:`Address Verification Required — ${r.companyName||"Meitho"}`,
          customBody:`<p>Dear ${cvEsc(r.candidateName)},</p><p>${cvEsc(r.companyName||"Your employer")} needs to verify your address as part of your background check.</p><p>Please click below, confirm your address, and take a quick selfie at that location.</p><p><a href="${link}" style="background:#ff3131;color:white;padding:12px 24px;border-radius:8px;text-decoration:none;display:inline-block;margin-top:8px;">Verify My Address →</a></p><p>Best regards,<br/>${cvEsc(r.companyName||"Meitho")} Team</p>`,
        })});
        await saveResult(r,key,{status:"link_sent",addressCheckId:checkId,cost,ranAt:new Date().toISOString(),ranBy:user.name||user.email});
        await logHistory(r,key,true,{name:r.candidateName},cost);
        addToast("Address verification link sent to the candidate.","success");
      } else {
        const out=await cvRunCheck(key,sub,r.candidateName);
        if(!out.ok){
          if(charged){
            const after=await cvRefund({txnId:chargeTxn});
            setWallets&&setWallets(p=>({...p,[cid]:{...(p[cid]||{}),balance:after}}));
          }
          await saveResult(r,key,{status:"failed",error:out.error||"Check failed",cost:0,ranAt:new Date().toISOString(),ranBy:user.name||user.email});
          addToast(`${def.label} could not be completed${charged?" — you have not been charged.":"."} ${out.error||""}`,"amber");
        } else if(out.awaiting){
          await saveResult(r,key,{status:"awaiting_candidate",clientId:out.clientId,link:out.link,cost,ranAt:new Date().toISOString(),ranBy:user.name||user.email});
          await logHistory(r,key,true,{name:r.candidateName},cost);
          const sent=await cvEmailDlLink(r,out.link);
          addToast(sent?"DigiLocker link emailed to "+r.candidateEmail+". You can also send it by WhatsApp from the request.":"DigiLocker link generated — use Email or WhatsApp on the request to send it to the candidate.","success");
        } else {
          await saveResult(r,key,{status:"verified",data:out.data,cost,ranAt:new Date().toISOString(),ranBy:user.name||user.email});
          await logHistory(r,key,true,out.data,cost);
          addToast(`${def.label} verified ✓`,"success");
        }
      }
    }catch(e){
      if(charged){
        try{
          const after=await cvRefund({txnId:chargeTxn});
          setWallets&&setWallets(p=>({...p,[cid]:{...(p[cid]||{}),balance:after}}));
        }catch(_){}
      }
      addToast("Failed: "+e.message,"error");
    }
    setBusyKey("");
  };

  const saveEdu=async(r)=>{
    const items=r.submission?.education||[];
    const old=r.results?.education?.items||{};
    const draft=eduDraft[r.id]||{};
    const merged={};
    for(const it of items){
      const v={...(old[it.k]||{}),...(draft[it.k]||{})};
      if(!v.status){addToast("Select a result for "+it.level,"error");return;}
      if(v.status!=="verified"&&!(v.comment||"").trim()){addToast("Add a comment for "+it.level,"error");return;}
      merged[it.k]={status:v.status,comment:(v.comment||"").trim()};
    }
    const overall=items.every(it=>merged[it.k].status==="verified")?"verified":"issues";
    setBusyKey(r.id+"eduSave");
    try{
      await saveResult(r,"education",{...(r.results?.education||{}),status:"reviewed",overall,items:merged,reviewedBy:user.name||user.email,reviewedAt:new Date().toISOString()});
      createNotification({companyId:r.companyId==="superadmin"?null:r.companyId,targetRole:"admin",targetUserId:r.initiatedBy,type:"candidate_submitted",title:"Education verification completed",message:`${r.candidateName}: education verification is complete — ${overall==="verified"?"all certificates verified":"issues found, please review"}.`,link:"candverify"});
      addToast("Education result saved","success");
    }catch(e){addToast("Could not save: "+e.message,"error");}
    setBusyKey("");
  };

  const fetchAadhaar=async(r)=>{
    const x=r.results?.aadhaar;
    if(!x?.clientId){addToast("No DigiLocker session found","error");return;}
    setBusyKey(r.id+"aadhaar-dl");
    try{
      const headers=await getAuthedHeaders();
      const res=await fetch(CV_URLS.aadhaarDl,{method:"POST",headers,body:JSON.stringify({client_id:x.clientId})});
      const json=await res.json();
      if((json.status_code===200||json.success)&&json.data){
        const d=json.data;
        if(!d.name&&!d.dob&&!d.address){
          console.warn("DigiLocker reply had no Aadhaar fields. Reply shape:",json.data?.debug_shape);
          addToast("DigiLocker responded but returned no Aadhaar details. Ask the candidate to complete DigiLocker and tick the Aadhaar document, then try again. (Details logged for support.)","amber");
          setBusyKey("");return;
        }
        await saveResult(r,"aadhaar",{...x,status:"verified",data:{name:d.name||"",dob:d.dob||"",gender:d.gender||"",address:d.address||"",father_name:d.father_name||"",masked_aadhaar:d.masked_aadhaar||cvMask(r.submission?.aadhaar),zip:d.zip||""},verifiedAt:new Date().toISOString()});
        addToast("✅ Aadhaar verified","success");
      } else addToast(json.message||"Candidate has not completed DigiLocker yet.","amber");
    }catch(e){addToast("Download failed: "+e.message,"error");}
    setBusyKey("");
  };

  const autoDlRef=useRef({});
  useEffect(()=>{
    if(!cur)return;
    const x=cur.results?.aadhaar;
    if(x?.status!=="awaiting_candidate"||!x?.clientId||autoDlRef.current[cur.id])return;
    autoDlRef.current[cur.id]=true;
    (async()=>{
      try{
        const headers=await getAuthedHeaders();
        const res=await fetch(CV_URLS.aadhaarDl,{method:"POST",headers,body:JSON.stringify({client_id:x.clientId})});
        const json=await res.json();
        const d=json.data;
        if((json.status_code===200||json.success)&&d&&(d.name||d.dob||d.address)){
          await saveResult(cur,"aadhaar",{...x,status:"verified",data:{name:d.name||"",dob:d.dob||"",gender:d.gender||"",address:d.address||"",father_name:d.father_name||"",masked_aadhaar:d.masked_aadhaar||cvMask(cur.submission?.aadhaar),zip:d.zip||""},verifiedAt:new Date().toISOString()});
          addToast("✅ Aadhaar fetched from DigiLocker","success");
        }
      }catch(e){/* silent */}
    })();
  // eslint-disable-next-line
  },[sel]);

  const filtered=reqs.filter(r=>{
    const st=cvStatusOf(r);
    const mq=!q||[r.candidateName,r.candidateEmail,r.id,r.companyName].some(v=>(v||"").toLowerCase().includes(q.toLowerCase()));
    return mq&&(fStatus==="all"||st===fStatus);
  });
  const counts=Object.keys(CV_STATUS).reduce((a,k)=>({...a,[k]:reqs.filter(r=>cvStatusOf(r)===k).length}),{});

  const stBadge=(k)=><Badge color={CV_STATUS[k].color}>{CV_STATUS[k].label}</Badge>;
  const chipFor=(r,key)=>{
    const def=cvCheckDef(key);const x=r.results?.[key];
    const c=x?.status==="verified"?"#10b981":x?.status==="failed"?"#ef4444":(x?.status==="awaiting_candidate"||x?.status==="link_sent")?"#f59e0b":"var(--muted)";
    return <span key={key} title={x?.status||"not run"} style={{fontSize:11,padding:"2px 7px",borderRadius:6,border:"1px solid var(--b1)",color:c,fontWeight:600,whiteSpace:"nowrap"}}>{def.icon} {def.key.toUpperCase()}</span>;
  };

  return(
    <div>
      <div className="fu" style={{display:"flex",justifyContent:"space-between",alignItems:"flex-end",marginBottom:20,flexWrap:"wrap",gap:12}}>
        <div>
          <h2 style={{fontFamily:FFD,fontWeight:700,fontSize:26,color:"var(--text)",marginBottom:4}}>Candidate Verification</h2>
          <p style={{color:"var(--muted)",fontSize:14}}>Create a request, review it, then send. The candidate submits details through a secure link and you run each check. Status updates live.</p>
        </div>
        <Btn style={{fontSize:13,padding:"9px 16px"}} onClick={()=>{setForm(blankForm);setShowNew(true);}}>＋ New Request</Btn>
      </div>

      <div style={{display:"flex",gap:8,flexWrap:"wrap",marginBottom:14}}>
        {[["all","All",reqs.length],...Object.entries(CV_STATUS).map(([k,v])=>[k,v.label,counts[k]||0])].map(([k,l,n])=>(
          <button key={k} onClick={()=>setFStatus(k)} style={{background:fStatus===k?"var(--brand)":"var(--s1)",color:fStatus===k?"#fff":"var(--text)",border:"1px solid var(--b1)",borderRadius:99,padding:"5px 12px",fontSize:12,fontWeight:600,cursor:"pointer",fontFamily:FF}}>{l} · {n}</button>
        ))}
        <Inp value={q} onChange={e=>setQ(e.target.value)} placeholder="Search name, email or ID…" style={{maxWidth:260,marginLeft:"auto"}}/>
      </div>

      <Card style={{padding:0,overflow:"hidden"}}>
        {loading?<div style={{padding:40,textAlign:"center",color:"var(--muted)"}}>Loading…</div>
        :filtered.length===0?<div style={{padding:"44px 20px",textAlign:"center",color:"var(--muted)",fontSize:13}}>No requests yet. Click “New Request” to start.</div>
        :<div style={{overflowX:"auto"}}><table style={{width:"100%",borderCollapse:"collapse",fontSize:13}}>
          <thead><tr style={{textAlign:"left",color:"var(--muted)",fontSize:11,textTransform:"uppercase"}}>
            {["Candidate","Type","Checks","Status","Sent","Expires",isSuperAdmin?"Company":"Sent by",""].map(h=><th key={h} style={{padding:"11px 14px",fontWeight:600,borderBottom:"1px solid var(--b1)"}}>{h}</th>)}
          </tr></thead>
          <tbody>{filtered.map(r=>{const st=cvStatusOf(r);return(
            <tr key={r.id} onClick={()=>setSel(r.id)} style={{cursor:"pointer",borderBottom:"1px solid var(--b1)"}}>
              <td style={{padding:"11px 14px"}}><div style={{fontWeight:600,color:"var(--text)"}}>{r.candidateName}</div><div style={{fontSize:11,color:"var(--muted)"}}>{r.candidateEmail}</div></td>
              <td style={{padding:"11px 14px"}}><Badge color="#0cc0df">🔗 Candidate link</Badge></td>
              <td style={{padding:"11px 14px"}}><div style={{display:"flex",gap:4,flexWrap:"wrap",maxWidth:260}}>{(r.checks||[]).map(k=>chipFor(r,k))}</div></td>
              <td style={{padding:"11px 14px"}}>{stBadge(st)}</td>
              <td style={{padding:"11px 14px",color:"var(--muted)",whiteSpace:"nowrap"}}>{timeAgo(r.createdAt)}</td>
              <td style={{padding:"11px 14px",color:st==="expired"?"#ef4444":"var(--muted)",whiteSpace:"nowrap"}}>{r.submittedAt?"—":(r.expiresAt||"").slice(0,10)}</td>
              <td style={{padding:"11px 14px",color:"var(--muted)"}}>{isSuperAdmin?(r.companyName||"—"):(r.initiatedByName||"—")}</td>
              <td style={{padding:"11px 14px",color:"var(--brand)",fontWeight:600}}>View →</td>
            </tr>);})}</tbody>
        </table></div>}
      </Card>

      {showNew&&(
        <Modal title={form.id?"Edit Draft":"New Verification Request"} onClose={()=>{setShowNew(false);setForm(blankForm);}} width={540}>
          <div style={{display:"grid",gap:12}}>
            <div><Label>Candidate name *</Label><Inp value={form.candidateName} onChange={e=>setForm(f=>({...f,candidateName:e.target.value}))} placeholder="Full name as per records"/></div>
            <div><Label>Email *</Label><Inp type="email" value={form.candidateEmail} onChange={e=>setForm(f=>({...f,candidateEmail:e.target.value}))} placeholder="candidate@email.com"/></div>
            <div><Label>Phone *</Label><Inp value={form.candidatePhone} onChange={e=>setForm(f=>({...f,candidatePhone:e.target.value}))} placeholder="10-digit mobile number"/></div>
            <div>
              <Label>Select verifications *</Label>
              {allowed.length===0?<div style={{fontSize:12,color:"#ef4444"}}>You don't have access to any verification module. Ask your Company Admin.</div>
              :<div style={{display:"grid",gap:6}}>{allowed.map(c=>{const on=form.checks.includes(c.key);return(
                <label key={c.key} style={{display:"flex",alignItems:"center",gap:10,padding:"9px 12px",border:"1px solid "+(on?"var(--brand)":"var(--b1)"),borderRadius:8,cursor:"pointer",background:on?"rgba(12,192,223,0.07)":"transparent"}}>
                  <input type="checkbox" checked={on} onChange={()=>toggleCheck(c.key)}/>
                  <span style={{flex:1,fontSize:13,color:"var(--text)"}}>{c.icon} {c.label}</span>
                  {!isSuperAdmin&&<span style={{fontSize:12,color:"var(--muted)"}}>₹{costOf(form,c)}{c.key==="refcheck"&&form.refCount===2?" (2 refs)":""}</span>}
                </label>);})}</div>}
              {form.checks.includes("refcheck")&&<div style={{marginTop:10,padding:"10px 12px",border:"1px dashed var(--b1)",borderRadius:8}}>
                <div style={{fontSize:12,fontWeight:700,color:"var(--text)",marginBottom:6}}>💬 How many references?</div>
                <div style={{display:"flex",gap:8}}>
                  {[1,2].map(n=><button key={n} type="button" onClick={()=>setForm(f=>({...f,refCount:n}))} style={{flex:1,padding:"8px",borderRadius:8,cursor:"pointer",fontFamily:FF,fontSize:12.5,fontWeight:700,border:"1.5px solid "+(form.refCount===n?"var(--brand)":"var(--b1)"),background:form.refCount===n?"rgba(12,192,223,0.10)":"transparent",color:"var(--text)"}}>{n} reference{n>1?"s":""}{!isSuperAdmin?" · ₹"+(price(cvCheckDef("refcheck"))*n):""}</button>)}
                </div>
                <div style={{fontSize:11,color:"var(--muted)",marginTop:6}}>The candidate fills each previous employer's details and uploads documents (3 payslips and offer letter required). Each reference verifies their own employer.</div>
              </div>}
              {!isSuperAdmin&&form.checks.length>0&&<div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginTop:10,padding:"10px 12px",background:"var(--s2)",border:"1px solid var(--b1)",borderRadius:8}}>
                <span style={{fontSize:12.5,color:"var(--muted)"}}>Estimated total ({form.checks.length} check{form.checks.length>1?"s":""})</span>
                <span style={{fontSize:15,fontWeight:800,color:"var(--brand)"}}>₹{form.checks.reduce((t,k)=>t+costOf(form,cvCheckDef(k)),0).toLocaleString("en-IN")}</span>
              </div>}
              <div style={{fontSize:11,color:"var(--muted)",marginTop:8}}>Nothing is sent or charged now. You will review and confirm before the candidate receives the link, and your wallet is charged per check only when you run it. Link valid for {CV_EXPIRY_DAYS} days once sent.</div>
            </div>
            <div style={{display:"flex",gap:10,justifyContent:"flex-end",marginTop:4}}>
              <BtnGhost onClick={()=>{setShowNew(false);setForm(blankForm);}}>Cancel</BtnGhost>
              <BtnGhost onClick={onlySaveDraft} disabled={sending}>💾 Save as draft</BtnGhost>
              <Btn onClick={saveAndReview} disabled={sending}>{sending?"Saving…":"Review & send →"}</Btn>
            </div>
          </div>
        </Modal>
      )}

      {confirmFor&&(()=>{
        const {rec,est,bal,cap,over,shortfall}=confirmFor;
        return(
        <Modal title="Confirm before sending" onClose={()=>setConfirmFor(null)} width={520}>
          <div style={{fontSize:13,color:"var(--muted)",marginBottom:12}}>Please check these details. The candidate will receive the verification link only after you confirm.</div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,fontSize:12.5,marginBottom:12}}>
            {[["Candidate",rec.candidateName],["Email",rec.candidateEmail],["Phone",rec.candidatePhone],["Requested by",rec.initiatedByName]].map(([k,v])=>(
              <div key={k}><div style={{fontSize:10,color:"var(--muted)",textTransform:"uppercase",fontWeight:600}}>{k}</div><div style={{color:"var(--text)",wordBreak:"break-word"}}>{v}</div></div>
            ))}
          </div>
          <div style={{border:"1px solid var(--b1)",borderRadius:10,padding:"10px 12px",marginBottom:12}}>
            <div style={{fontSize:11,color:"var(--muted)",fontWeight:700,textTransform:"uppercase",marginBottom:6}}>Verifications</div>
            {(rec.checks||[]).map(k=>{const d=cvCheckDef(k);return <div key={k} style={{display:"flex",justifyContent:"space-between",fontSize:13,color:"var(--text)",padding:"3px 0"}}><span>{d.icon} {d.label}{k==="refcheck"&&rec.refCount===2?" (2 references)":""}</span>{!isSuperAdmin&&<span>₹{costOf(rec,d)}</span>}</div>;})}
            {!isSuperAdmin&&<div style={{display:"flex",justifyContent:"space-between",borderTop:"1px solid var(--b1)",marginTop:6,paddingTop:6,fontWeight:800,color:"var(--brand)"}}><span>Estimated total</span><span>₹{est.toLocaleString("en-IN")}</span></div>}
          </div>
          {!isSuperAdmin&&<div style={{fontSize:12.5,color:"var(--muted)",marginBottom:10,lineHeight:1.7}}>
            Company wallet balance: <strong style={{color:"var(--text)"}}>₹{bal.toLocaleString("en-IN")}</strong>
            {cap&&<><br/>Your wallet share: <strong style={{color:"var(--text)"}}>₹{cap.left.toLocaleString("en-IN")}</strong> left of ₹{cap.allocated.toLocaleString("en-IN")}</>}
          </div>}
          {over&&<div style={{padding:"10px 12px",background:"rgba(236,72,153,0.1)",border:"1px solid rgba(236,72,153,0.35)",borderRadius:8,fontSize:12.5,color:"var(--text)",marginBottom:10}}>This is about <strong>₹{shortfall}</strong> beyond your wallet share, so it will be sent to your Company Admin for approval first. The candidate is not contacted until it is approved.</div>}
          {!over&&!isSuperAdmin&&est>bal&&<div style={{padding:"10px 12px",background:"rgba(245,158,11,0.1)",border:"1px solid rgba(245,158,11,0.3)",borderRadius:8,fontSize:12.5,color:"var(--text)",marginBottom:10}}>The company wallet balance is below the estimated total. You can still send, but some checks may not run until the wallet is recharged.</div>}
          <div style={{display:"flex",gap:10,justifyContent:"flex-end"}}>
            <BtnGhost onClick={()=>setConfirmFor(null)}>Back</BtnGhost>
            <Btn onClick={confirmSend} disabled={sending}>{sending?"Please wait…":over?"Send for approval":"✔ Confirm & send to candidate"}</Btn>
          </div>
        </Modal>);
      })()}

      {cur&&(()=>{
        const st=cvStatusOf(cur);const sub=cur.submission||{};const res=cur.results||{};
        const submitted=!!cur.submittedAt;
        return(
        <Modal title={cur.candidateName} onClose={()=>setSel(null)} width={720}>
          <div style={{display:"flex",gap:8,flexWrap:"wrap",alignItems:"center",marginBottom:14}}>
            {stBadge(st)}<span style={{fontSize:12,color:"var(--muted)"}}>{cur.id}</span>
            <div style={{marginLeft:"auto",display:"flex",gap:8,flexWrap:"wrap"}}>
              {st==="draft"&&<>
                <BtnGhost style={{fontSize:12}} onClick={()=>editDraft(cur)}>✏ Edit</BtnGhost>
                <BtnGhost style={{fontSize:12}} onClick={()=>deleteDraft(cur)}>🗑 Delete</BtnGhost>
                <Btn style={{fontSize:12}} onClick={()=>{const rec=cur;setSel(null);openConfirm(rec);}}>Review & send →</Btn>
              </>}
              {st==="pending_approval"&&(isCompanyAdmin||isSuperAdmin)&&<>
                <BtnGhost style={{fontSize:12}} onClick={()=>rejectRequest(cur)}>Reject</BtnGhost>
                <Btn style={{fontSize:12}} disabled={sending} onClick={()=>approveRequest(cur)}>{sending?"Please wait…":"Approve & send"}</Btn>
              </>}
              {st!=="draft"&&st!=="pending_approval"&&<>
                <BtnGhost style={{fontSize:12}} onClick={()=>openEdit(cur)}>✏ Edit details</BtnGhost>
                {!submitted&&<BtnGhost style={{fontSize:12}} onClick={()=>resend(cur)}>↻ Resend link</BtnGhost>}
                <Btn style={{fontSize:12}} onClick={()=>cvDownloadPdf(cur,addrDoc,refDoc)}>⬇ Download PDF</Btn>
              </>}
            </div>
          </div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:16,fontSize:12.5}}>
            {[["Email",cur.candidateEmail],["Phone",cur.candidatePhone],["Requested by",cur.initiatedByName+(isSuperAdmin?" · "+(cur.companyName||""):"")],["Sent",(cur.createdAt||"").slice(0,16).replace("T"," ")],["Opened",cur.openedAt?cur.openedAt.slice(0,16).replace("T"," "):"Not yet"],["Submitted",submitted?cur.submittedAt.slice(0,16).replace("T"," "):"Not yet"]].map(([k,v])=>(
              <div key={k}><div style={{fontSize:10,color:"var(--muted)",textTransform:"uppercase",fontWeight:600}}>{k}</div><div style={{color:"var(--text)"}}>{v}</div></div>
            ))}
          </div>
          {st!=="draft"&&st!=="pending_approval"&&<div style={{padding:"10px 12px",background:"rgba(12,192,223,0.06)",border:"1px solid rgba(12,192,223,0.2)",borderRadius:8,marginBottom:12}}>
            <div style={{fontSize:10,color:"var(--muted)",fontWeight:700,textTransform:"uppercase",marginBottom:4}}>Candidate link</div>
            <div style={{fontSize:12,color:"var(--brand)",fontFamily:FFM,wordBreak:"break-all",marginBottom:8,userSelect:"all"}}>{window.location.origin}/candidate-verify/{cur.id}</div>
            <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
              <button onClick={async()=>{const ok=await cvCopy(`${window.location.origin}/candidate-verify/${cur.id}`);addToast(ok?"Link copied":"Could not copy — select the link and copy it manually",ok?"success":"error");}} style={{background:"var(--brand)",border:"none",borderRadius:6,color:"#fff",padding:"5px 12px",cursor:"pointer",fontSize:11.5,fontWeight:700,fontFamily:FF}}>📋 Copy link</button>
              {cur.candidatePhone&&<button onClick={()=>sendWhatsAppMessage(cur.candidatePhone,`Hi ${cur.candidateName||""}, ${cur.companyName||"your employer"} needs your details for a background verification through Meitho. Please open this secure link and submit your details: ${window.location.origin}/candidate-verify/${cur.id}`)} style={{background:"#25D366",border:"none",borderRadius:6,color:"#fff",padding:"5px 12px",cursor:"pointer",fontSize:11.5,fontWeight:700,fontFamily:FF}}>💬 WhatsApp</button>}
              {!submitted&&<button onClick={()=>resend(cur)} style={{background:"transparent",border:"1px solid var(--brand)",borderRadius:6,color:"var(--brand)",padding:"5px 12px",cursor:"pointer",fontSize:11.5,fontWeight:700,fontFamily:FF}}>✉ Email again</button>}
            </div>
          </div>}
          {!submitted&&<div style={{padding:"10px 12px",background:"rgba(245,158,11,0.1)",border:"1px solid rgba(245,158,11,0.3)",borderRadius:8,fontSize:12.5,color:"var(--text)",marginBottom:14}}>
            {st==="draft"?"This is a draft. Nothing has been sent to the candidate. Click “Review & send” when you are ready."
              :st==="pending_approval"?((isCompanyAdmin||isSuperAdmin)?`${cur.initiatedByName||"An HR user"} needs approval: about ₹${cur.approvalEstimate||0} is ₹${cur.approvalShortfall||0} beyond their wallet share. Approving sends the link to the candidate and raises their wallet share by ₹${cur.approvalShortfall||0}.`:"Waiting for your Company Admin to approve. The candidate has not been contacted.")
              :st==="expired"?"This link has expired. Click “Resend link” to send a fresh one.":"Waiting for the candidate to submit their details. You'll get a notification when they do."}
          </div>}

          {submitted&&<div style={{border:"1px solid var(--b1)",borderRadius:10,padding:"10px 14px",marginBottom:12}}>
            <div style={{fontWeight:700,fontSize:13.5,color:"var(--text)",marginBottom:8}}>📎 Candidate documents (Aadhaar, PAN, education)</div>
            {cvCandDocList(sub).length?<>
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:6,fontSize:12.5}}>
                {cvCandDocList(sub).map(d=>(<div key={d.k}><a href={d.url} target="_blank" rel="noreferrer" style={{color:"var(--brand)"}}>📄 {d.label.replace(" (if applicable)","")}</a></div>))}
              </div>
              {!isSuperAdmin&&<div style={{marginTop:10}}>
                <Btn style={{fontSize:12,padding:"6px 12px"}} disabled={busyKey===cur.id+"savecand"} onClick={async()=>{
                  setBusyKey(cur.id+"savecand");
                  try{const ok=await cvFileCandDocs(cur);addToast(ok?"Documents saved to Employee Files under "+cur.candidateName:"No documents to save",ok?"success":"error");}
                  catch(e){addToast("Could not save: "+e.message,"error");}
                  setBusyKey("");
                }}>🗂 Save to Employee Files</Btn>
              </div>}
            </>:<div style={{fontSize:12.5,color:"var(--muted)"}}>No documents uploaded (submitted before this requirement).</div>}
          </div>}
          <div style={{display:"grid",gap:12}}>
            {(cur.checks||[]).map(key=>{
              const def=cvCheckDef(key);const x=res[key];
              const busy=busyKey===cur.id+key;
              const done=x?.status==="verified"||x?.status==="failed";
              return(
              <div key={key} style={{border:"1px solid var(--b1)",borderRadius:10,overflow:"hidden"}}>
                <div style={{display:"flex",alignItems:"center",gap:10,padding:"10px 14px",background:"var(--s2)"}}>
                  <div style={{flex:1,fontWeight:700,fontSize:13.5,color:"var(--text)"}}>{def.icon} {def.label}</div>
                  {x?.status==="verified"&&<Badge color="#10b981">✓ Verified</Badge>}
                  {x?.status==="failed"&&<Badge color="#ef4444">✗ Failed</Badge>}
                  {x?.status==="awaiting_candidate"&&<Badge color="#f59e0b">Awaiting DigiLocker</Badge>}
                  {x?.status==="in_review"&&<Badge color="#f59e0b">Under review by Meitho</Badge>}
                  {x?.status==="reviewed"&&(x.overall==="verified"?<Badge color="#10b981">✓ All verified</Badge>:<Badge color="#ef4444">⚠ Issues found</Badge>)}
                  {x?.status==="link_sent"&&<Badge color="#f59e0b">{key==="refcheck"?"Awaiting references":"Link sent to candidate"}</Badge>}
                  {!x&&submitted&&<Btn disabled={busy} style={{fontSize:12,padding:"6px 12px"}} onClick={()=>runCheck(cur,key)}>{busy?"Running…":`▶ Run${isSuperAdmin?"":" · ₹"+costOf(cur,def)}`}</Btn>}
                  {x?.status==="failed"&&<Btn disabled={busy} style={{fontSize:12,padding:"6px 12px"}} onClick={()=>runCheck(cur,key)}>{busy?"Running…":`↻ Retry${isSuperAdmin?"":" · ₹"+costOf(cur,def)}`}</Btn>}
                  {x?.status==="awaiting_candidate"&&<Btn disabled={busyKey===cur.id+"aadhaar-dl"} style={{fontSize:12,padding:"6px 12px"}} onClick={()=>fetchAadhaar(cur)}>↓ Check & Download</Btn>}
                </div>
                <div style={{padding:"10px 14px",fontSize:12.5}}>
                  {/* what the candidate submitted */}
                  {submitted?(
                    <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,marginBottom:x?10:0}}>
                      {def.fields.filter(f=>sub[f.k]).map(f=>(
                        <div key={f.k}><div style={{fontSize:10,color:"var(--muted)",textTransform:"uppercase",fontWeight:600}}>{f.label}</div><div style={{color:"var(--text)",fontFamily:f.type==="text"?FFM:FF,wordBreak:"break-word"}}>{key==="aadhaar"?cvMask(sub[f.k]):sub[f.k]}</div></div>
                      ))}
                      {key==="address"&&!sub.permanentAddress&&<div style={{color:"var(--muted)"}}>Permanent address: same as present</div>}
                    </div>
                  ):<div style={{color:"var(--muted)"}}>Not submitted yet.</div>}
                  {key==="education"&&submitted&&(()=>{
                    const items=sub.education||[];
                    const draft=eduDraft[cur.id]||{};
                    const val=(k)=>({...(x?.items?.[k]||{}),...(draft[k]||{})});
                    const setD=(k,patch)=>setEduDraft(p=>({...p,[cur.id]:{...(p[cur.id]||{}),[k]:{...val(k),...patch}}}));
                    return(<div style={{display:"grid",gap:8}}>
                      {!items.length&&<div style={{color:"var(--muted)"}}>No education details submitted.</div>}
                      {items.map(it=>{const d=sub.candDocs?.[it.k];const v=val(it.k);const vd=CV_EDU_VERDICT[v.status];return(
                        <div key={it.k} style={{border:"1px solid var(--b1)",borderRadius:8,padding:"8px 10px"}}>
                          <div style={{fontWeight:700,color:"var(--text)",marginBottom:4}}>🎓 {it.level}</div>
                          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:4,fontSize:12,color:"var(--text)"}}>
                            {CV_EDU_FIELDS.filter(f=>it[f.k]).map(f=><div key={f.k}><span style={{color:"var(--muted)"}}>{f.label}: </span>{it[f.k]}</div>)}
                          </div>
                          <div style={{marginTop:4,fontSize:12}}>{d?.url?<a href={d.url} target="_blank" rel="noreferrer" style={{color:"var(--brand)"}}>📄 View certificate</a>:<span style={{color:"var(--muted)"}}>No document</span>}</div>
                          {x&&isSuperAdmin&&<div style={{display:"flex",gap:8,marginTop:8,flexWrap:"wrap"}}>
                            <select value={v.status||""} onChange={e=>setD(it.k,{status:e.target.value})} style={{padding:"6px 8px",borderRadius:6,border:"1px solid var(--b1)",background:"var(--s2)",color:"var(--text)",fontSize:12}}>
                              <option value="">Select result…</option><option value="verified">✓ Verified</option><option value="not_verified">✗ Not verified</option><option value="unable">? Unable to verify</option>
                            </select>
                            <input value={v.comment||""} onChange={e=>setD(it.k,{comment:e.target.value})} placeholder="Comment (required if not verified)" style={{flex:1,minWidth:160,padding:"6px 8px",borderRadius:6,border:"1px solid var(--b1)",background:"var(--s2)",color:"var(--text)",fontSize:12}}/>
                          </div>}
                          {x&&!isSuperAdmin&&x.status==="reviewed"&&vd&&<div style={{marginTop:6,fontSize:12}}><Badge color={vd[1]}>{vd[0]}</Badge>{v.comment&&<span style={{marginLeft:8,color:"var(--text)"}}>{v.comment}</span>}</div>}
                        </div>);})}
                      {x&&isSuperAdmin&&items.length>0&&<div><Btn style={{fontSize:12,padding:"6px 12px"}} disabled={busyKey===cur.id+"eduSave"} onClick={()=>saveEdu(cur)}>{x.status==="reviewed"?"Update result":"Save result & notify"}</Btn></div>}
                      {x&&!isSuperAdmin&&x.status==="in_review"&&<div style={{fontSize:12,color:"var(--muted)"}}>The Meitho team is verifying these certificates. You'll be notified when the result is ready.</div>}
                    </div>);
                  })()}
                  {key==="refcheck"&&submitted&&<CvRefReportView sub={sub} refDoc={refDoc}/>}
                  {key==="refcheck"&&submitted&&x&&<div style={{marginTop:10}}><Btn style={{fontSize:12,padding:"6px 12px"}} onClick={()=>cvRefReportPdf(cur,refDoc)}>⬇ Download Reference Report (with documents)</Btn></div>}
                  {key==="refcheck"&&submitted&&!isSuperAdmin&&refDoc&&[1,2].some(n=>refDoc["ref"+n+"_data"]?.submittedAt)&&<div style={{marginTop:10}}>
                    <Btn style={{fontSize:12,padding:"6px 12px"}} disabled={busyKey===cur.id+"savefiles"} onClick={async()=>{
                      setBusyKey(cur.id+"savefiles");
                      try{const ok=await cvFileRefDocs(cur,refDoc,false);addToast(ok?"Documents saved to Employee Files under "+cur.candidateName+" ("+(cur.candidatePhone||"")+")":"No documents to save yet",ok?"success":"error");}
                      catch(e){addToast("Could not save: "+e.message,"error");}
                      setBusyKey("");
                    }}>🗂 Save documents to Employee Files</Btn>
                    <div style={{fontSize:11,color:"var(--muted)",marginTop:4}}>Saves the uploaded documents under the candidate's name and mobile number, labelled with each employer and its verification result.</div>
                  </div>}
                  {x?.status==="failed"&&<div style={{color:"#ef4444"}}>Failed: {x.error}</div>}
                  {x?.status==="verified"&&<div style={{display:"grid",gridTemplateColumns:"1fr",gap:4,borderTop:"1px dashed var(--b1)",paddingTop:8}}>
                    {cvSummaryRows(key,x.data).filter(([,v])=>v!==undefined&&v!==""&&v!==null).map(([k,v])=>(
                      <div key={k} style={{display:"flex",gap:10}}><div style={{width:130,flexShrink:0,color:"var(--muted)"}}>{k}</div><div style={{color:"var(--text)",wordBreak:"break-word"}}>{String(v)}</div></div>
                    ))}
                  </div>}
                  {x?.status==="awaiting_candidate"&&x.link&&<div style={{marginTop:8,padding:"8px 10px",background:"rgba(12,192,223,0.06)",border:"1px solid rgba(12,192,223,0.2)",borderRadius:8}}>
                    <div style={{fontSize:10,color:"var(--muted)",fontWeight:700,textTransform:"uppercase",marginBottom:3}}>DigiLocker link — share with candidate</div>
                    <div style={{fontSize:11,color:"var(--brand)",fontFamily:FFM,wordBreak:"break-all",marginBottom:6}}>{x.link}</div>
                    <button onClick={async()=>{const ok=await cvCopy(x.link);addToast(ok?"Link copied":"Could not copy — select the link and copy it manually",ok?"success":"error");}} style={{background:"var(--brand)",border:"none",borderRadius:6,color:"#fff",padding:"4px 10px",cursor:"pointer",fontSize:11,fontWeight:700,fontFamily:FF,marginRight:6}}>📋 Copy link</button>
                    <button onClick={async()=>{const ok=await cvEmailDlLink(cur,x.link);addToast(ok?"Link emailed to "+cur.candidateEmail:"Could not email — no candidate email or send failed",ok?"success":"error");}} style={{background:"transparent",border:"1px solid var(--brand)",borderRadius:6,color:"var(--brand)",padding:"4px 10px",cursor:"pointer",fontSize:11,fontWeight:700,fontFamily:FF,marginRight:6}}>✉ Email candidate</button>
                    {cur.candidatePhone&&<button onClick={()=>sendWhatsAppMessage(cur.candidatePhone,cvDlMessage(cur.candidateName,cur.companyName,x.link))} style={{background:"#25D366",border:"none",borderRadius:6,color:"#fff",padding:"4px 10px",cursor:"pointer",fontSize:11,fontWeight:700,fontFamily:FF}}>💬 WhatsApp</button>}
                  </div>}
                  {key==="address"&&(x?.status==="link_sent"||x?.status==="verified")&&addrDoc&&<div style={{marginTop:8,color:"var(--text)"}}>
                    {[["Present address","presentVerification"],["Permanent address","permanentVerification"]].map(([lb,f])=>{
                      const v=addrDoc[f];
                      if(f==="permanentVerification"&&addrDoc.sameAddress)return null;
                      return <div key={f} style={{marginBottom:3}}>{lb}: {v?.status==="submitted"?(v.locationNotCaptured?"⚠ Selfie received, location NOT captured (review manually)":v.matched?"✓ Location matched":"✗ Location did not match")+(v.distanceMeters!=null&&!(v.manualOverride&&!isSuperAdmin)?` (${Math.round(v.distanceMeters)} m)`:""):"Not verified (candidate verified the other address)"}
                        {v?.selfieUrl&&<> · <a href={v.selfieUrl} target="_blank" rel="noreferrer" style={{color:"var(--brand)"}}>View selfie</a></>}
                        {v?.capturedLat!=null&&<> · <a href={`https://www.google.com/maps?q=${v.capturedLat},${v.capturedLng}`} target="_blank" rel="noreferrer" style={{color:"var(--brand)"}}>📍 View location</a></>}
                        {isSuperAdmin&&v?.status==="submitted"&&!v.matched&&<> · <a href="#" onClick={async(ev)=>{ev.preventDefault();
                          if(!window.confirm("Mark this location as verified? Use this only if you have confirmed the candidate's location is correct."))return;
                          try{await updateDoc(doc(db,"address_verifications",addrDoc.id),{[f+".matched"]:true,[f+".manualOverride"]:true,[f+".overrideBy"]:user?.name||user?.email||"",[f+".overrideAt"]:new Date().toISOString()});addToast("Location marked as verified","success");}
                          catch(er){addToast("Failed: "+er.message,"error");}
                        }} style={{color:"var(--green)",fontWeight:700}}>✓ Mark location verified</a></>}
                        {isSuperAdmin&&v?.manualOverride&&<span style={{color:"var(--muted)"}}> · (manually verified by {v.overrideBy})</span>}</div>;
                    })}
                    {addrDoc.presentVerification?.status!=="submitted"&&addrDoc.permanentVerification?.status!=="submitted"&&<div style={{color:"var(--muted)"}}>Awaiting candidate</div>}
                  </div>}
                  {key==="address"&&x?.addressCheckId&&<div style={{marginTop:8,display:"flex",gap:6,flexWrap:"wrap"}}>
                    <button onClick={async()=>{const ok=await cvAddrEmail(cur,x.addressCheckId);addToast(ok?"Address link re-sent to "+cur.candidateEmail:"Could not email — no candidate email or send failed",ok?"success":"error");}} style={{background:"transparent",border:"1px solid var(--brand)",borderRadius:6,color:"var(--brand)",padding:"4px 10px",cursor:"pointer",fontSize:11,fontWeight:700,fontFamily:FF}}>✉ Resend link (email)</button>
                    {cur.candidatePhone&&<button onClick={()=>sendWhatsAppMessage(cur.candidatePhone,`Hi ${cur.candidateName||""}, ${cur.companyName||"your employer"} needs to verify your address through Meitho. Please open this link in Chrome or Safari, allow location and take a selfie at your address: ${window.location.origin}/address-verify/${x.addressCheckId}`)} style={{background:"#25D366",border:"none",borderRadius:6,color:"#fff",padding:"4px 10px",cursor:"pointer",fontSize:11,fontWeight:700,fontFamily:FF}}>💬 Resend via WhatsApp</button>}
                    <button onClick={async()=>{const ok=await cvCopy(`${window.location.origin}/address-verify/${x.addressCheckId}`);addToast(ok?"Link copied":"Could not copy the link",ok?"success":"error");}} style={{background:"transparent",border:"1px solid var(--b1)",borderRadius:6,color:"var(--muted)",padding:"4px 10px",cursor:"pointer",fontSize:11,fontWeight:700,fontFamily:FF}}>📋 Copy link</button>
                  </div>}
                  {x&&<div style={{fontSize:10.5,color:"var(--muted)",marginTop:8}}>Run by {x.ranBy||"—"} · {(x.ranAt||"").slice(0,16).replace("T"," ")}{!isSuperAdmin&&x.cost?` · ₹${x.cost}`:""}</div>}
                </div>
              </div>);
            })}
          </div>
        </Modal>);
      })()}

      {editFor&&(()=>{
        const setF=(k,v)=>setEditFor(p=>({...p,fields:{...p.fields,[k]:v}}));
        const inpS={width:"100%",padding:"8px 10px",border:"1px solid var(--b1)",borderRadius:8,background:"var(--s2)",color:"var(--text)",fontSize:13,fontFamily:FF,outline:"none"};
        const lab=(t)=><div style={{fontSize:10.5,color:"var(--muted)",textTransform:"uppercase",fontWeight:600,marginBottom:3}}>{t}</div>;
        return(
        <Modal title="Edit candidate details" onClose={()=>setEditFor(null)} width={600}>
          {editFor.hadResults&&<div style={{padding:"9px 12px",background:"rgba(245,158,11,0.1)",border:"1px solid rgba(245,158,11,0.3)",borderRadius:8,fontSize:12.5,color:"var(--text)",marginBottom:12}}>Some checks have already been run with the old details. Changing a value here does not re-run them; re-run the affected check if needed.</div>}
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:12}}>
            <div>{lab("Candidate name")}<input value={editFor.name} onChange={e=>setEditFor(p=>({...p,name:e.target.value}))} style={inpS}/></div>
            <div>{lab("Phone")}<input value={editFor.phone} onChange={e=>setEditFor(p=>({...p,phone:e.target.value}))} style={inpS}/></div>
            <div style={{gridColumn:"1 / -1"}}>{lab("Email")}<input value={editFor.email} onChange={e=>setEditFor(p=>({...p,email:e.target.value}))} style={inpS}/></div>
          </div>
          {editFor.submitted&&<>
            {(editFor.checks||[]).map(k=>{const d=cvCheckDef(k);if(!d||!(d.fields||[]).length)return null;return(
              <div key={k} style={{border:"1px solid var(--b1)",borderRadius:10,padding:"10px 12px",marginBottom:10}}>
                <div style={{fontWeight:700,fontSize:13,color:"var(--text)",marginBottom:8}}>{d.icon} {d.label}</div>
                <div style={{display:"grid",gap:8}}>
                  {d.fields.map(f=><div key={f.k}>{lab(f.label)}{(f.type==="textarea"||f.type==="addr")
                    ?<textarea rows={2} value={editFor.fields[f.k]||""} onChange={e=>setF(f.k,e.target.value)} style={{...inpS,resize:"vertical"}}/>
                    :<input type={f.type==="date"?"date":"text"} value={editFor.fields[f.k]||""} onChange={e=>setF(f.k,e.target.value)} style={inpS}/>}</div>)}
                </div>
              </div>);})}
            {(editFor.edu||[]).length>0&&<div style={{border:"1px solid var(--b1)",borderRadius:10,padding:"10px 12px",marginBottom:10}}>
              <div style={{fontWeight:700,fontSize:13,color:"var(--text)",marginBottom:8}}>🎓 Education details</div>
              {editFor.edu.map((row,i)=>(
                <div key={i} style={{marginBottom:10}}>
                  <div style={{fontSize:12,fontWeight:700,color:"var(--brand)",marginBottom:4}}>{row.level||row.k}</div>
                  <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
                    {CV_EDU_FIELDS.map(f=><div key={f.k}>{lab(f.label)}<input value={row[f.k]||""} onChange={e=>setEditFor(p=>({...p,edu:p.edu.map((x,j)=>j===i?{...x,[f.k]:e.target.value}:x)}))} style={inpS}/></div>)}
                  </div>
                </div>
              ))}
            </div>}
            <div style={{fontSize:11.5,color:"var(--muted)",marginBottom:8}}>Reference details and uploaded documents are not edited here. Ask the candidate to re-upload a document if needed.</div>
          </>}
          <div style={{display:"flex",gap:10,justifyContent:"flex-end",marginTop:6}}>
            <BtnGhost onClick={()=>setEditFor(null)}>Cancel</BtnGhost>
            <Btn onClick={saveEdit} disabled={editSaving}>{editSaving?"Saving…":"Save changes"}</Btn>
          </div>
        </Modal>);
      })()}
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════
   PUBLIC PAGE — meitho.com/candidate-verify/{id}
   Candidate-facing: shows only the fields for the selected checks.
══════════════════════════════════════════════════════════════ */
function CandidateVerifyPublicPage(){
  const id=window.location.pathname.split("/candidate-verify/")[1]?.split("/")[0]||"";
  const [data,setData]=useState(null);
  const [loading,setLoading]=useState(true);
  const [error,setError]=useState("");
  const [vals,setVals]=useState({});
  const [addr,setAddr]=useState({permanentAddress:{same:true}});
  const setAddrPart=(k,part,v)=>setAddr(p=>({...p,[k]:{...(p[k]||{}),[part]:v}}));
  const [errs,setErrs]=useState({});
  const [consent,setConsent]=useState(false);
  const [busy,setBusy]=useState(false);
  const [done,setDone]=useState(false);
  const [refs,setRefs]=useState([{docs:{}},{docs:{}}]);
  const [upBusy,setUpBusy]=useState({});
  const [candDocs,setCandDocs]=useState({});
  const [eduData,setEduData]=useState({});
  const setEd=(k,f,v)=>setEduData(p=>({...p,[k]:{...(p[k]||{}),[f]:v}}));

  useEffect(()=>{
    if(!id){setError("Invalid link.");setLoading(false);return;}
    getDoc(doc(db,"candidate_verifications",id)).then(async(snap)=>{
      if(!snap.exists()){setError("This verification link is invalid.");return;}
      const d={id:snap.id,...snap.data()};
      setData(d);
      if(d.status==="draft"||d.status==="pending_approval"){setError("This verification link is not active yet. Please wait for the sender to send it to you.");return;}
      if(d.submittedAt)setDone(true);
      else if(d.expiresAt&&new Date(d.expiresAt)<new Date())setError("This link has expired. Please ask the sender to resend it.");
      else if(!d.openedAt){try{await updateDoc(doc(db,"candidate_verifications",id),{openedAt:new Date().toISOString(),status:"opened"});}catch(e){}}
    }).catch(()=>setError("This link is no longer active. If you have already submitted your details, no further action is needed. Otherwise, please ask the sender to resend the link.")).finally(()=>setLoading(false));
  },[id]);

  const defs=(data?.checks||[]).map(cvCheckDef).filter(Boolean);
  const setV=(k,v)=>setVals(p=>({...p,[k]:v}));
  const refCount=Math.max(1,Math.min(2,data?.refCount||1));
  const setRf=(i,k,v)=>setRefs(p=>p.map((x,j)=>j===i?{...x,[k]:v}:x));
  const pickDoc=async(i,d,file)=>{
    if(!file)return;
    const ok=/\.(pdf|jpe?g|png)$/i.test(file.name);
    const ek="r"+i+"_"+d.k;
    if(!ok){setErrs(e=>({...e,[ek]:"Only PDF, JPG or PNG files"}));return;}
    if(file.size>CV_MAX_BYTES){setErrs(e=>({...e,[ek]:"File is larger than 5 MB"}));return;}
    setErrs(e=>{const n={...e};delete n[ek];return n;});
    setUpBusy(b=>({...b,[ek]:true}));
    try{
      const up=await uploadRefDoc(id,i+1,d.k,file);
      setRefs(p=>p.map((x,j)=>j===i?{...x,docs:{...x.docs,[d.k]:{url:up.url,name:file.name,size:file.size,path:up.path}}}:x));
    }catch(err){setErrs(e=>({...e,[ek]:"Upload failed. Please try again."}));}
    setUpBusy(b=>({...b,[ek]:false}));
  };

  const pickCandDoc=async(d,file)=>{
    if(!file)return;
    const ek="c_"+d.k;
    if(!/\.(pdf|jpe?g|png)$/i.test(file.name)){setErrs(e=>({...e,[ek]:"Only PDF, JPG or PNG files"}));return;}
    if(file.size>CV_MAX_BYTES){setErrs(e=>({...e,[ek]:"File is larger than 5 MB"}));return;}
    setErrs(e=>{const n={...e};delete n[ek];return n;});
    setUpBusy(b=>({...b,[ek]:true}));
    try{
      const up=await uploadRefDoc(id,0,d.k,file);
      setCandDocs(p=>({...p,[d.k]:{url:up.url,name:file.name,size:file.size,path:up.path}}));
    }catch(err){setErrs(e=>({...e,[ek]:"Upload failed. Please try again."}));}
    setUpBusy(b=>({...b,[ek]:false}));
  };

  const submit=async()=>{
    const e={};const out={};
    const hasEdu=(data?.checks||[]).includes("education");
    CV_ID_DOCS.forEach(d=>{if(d.req&&!candDocs[d.k]?.url)e["c_"+d.k]="This document is required";});
    if(hasEdu){
      const eduOut=[];
      CV_EDU_DOCS.forEach(d=>{
        const x=eduData[d.k]||{};
        const touched=CV_EDU_FIELDS.some(f=>(x[f.k]||"").trim())||candDocs[d.k]?.url;
        if(!d.req&&!touched)return;
        const o={k:d.k,level:d.short};
        CV_EDU_FIELDS.forEach(f=>{
          const v=(x[f.k]||"").toString().trim();
          if(f.req&&!v)e["e_"+d.k+"_"+f.k]="Required";
          else if(f.re&&v&&!f.re.test(v))e["e_"+d.k+"_"+f.k]="Enter a valid value";
          else if(v)o[f.k]=v;
        });
        if(!candDocs[d.k]?.url)e["c_"+d.k]="This document is required";
        eduOut.push(o);
      });
      out.education=eduOut;
    }
    out.candDocs=Object.fromEntries(Object.entries(candDocs).filter(([,v])=>v?.url));
    defs.forEach(def=>def.fields.forEach(f=>{
      if(f.type==="addr"){
        const p=addr[f.k]||{};
        if(f.sameAs&&p.same!==false){return;} // same as present
        const pin=String(p.pin||"").replace(/\D/g,"");
        const ok=String(p.line1||"").trim().length>=3&&String(p.city||"").trim().length>=2&&p.state&&/^\d{6}$/.test(pin);
        if(!ok){if(!f.optional||f.sameAs)e[f.k]=f.err||"Required";return;}
        const parts={line1:String(p.line1).trim(),line2:String(p.line2||"").trim(),city:String(p.city).trim(),state:p.state,pin};
        out[f.k]=cvComposeAddr(parts);out[f.k+"_parts"]=parts;
        return;
      }
      const raw=(vals[f.k]||"").toString();
      const v=f.clean?f.clean(raw):raw.trim();
      if(!f.optional&&(!v||(f.re&&!f.re.test(v))))e[f.k]=f.err||"Required";
      else if(v)out[f.k]=v;
    }));
    const refOut=[];
    if((data?.checks||[]).includes("refcheck")){
      for(let i=0;i<refCount;i++){
        const x=refs[i]||{docs:{}};const o={docs:x.docs||{}};
        CV_REF_FIELDS.forEach(f=>{
          const v=(x[f.k]||"").toString().trim();
          if(f.req&&!v)e["r"+i+"_"+f.k]="Required";
          else if(f.re&&v&&!f.re.test(v))e["r"+i+"_"+f.k]="Enter a valid value";
          else if(v)o[f.k]=f.k==="refEmail"?v.toLowerCase():v;
        });
        CV_REF_DOCS.forEach(d=>{if(d.req&&!o.docs[d.k]?.url)e["r"+i+"_"+d.k]="This document is required";});
        refOut.push(o);
      }
      if(refCount===2&&refOut[0]?.refEmail&&refOut[0].refEmail===refOut[1]?.refEmail)e["r1_refEmail"]="Reference 2 must be a different person";
      out.refs=refOut;
    }
    setErrs(e);
    if(Object.keys(e).length){return;}
    if(!consent){setErrs({_c:"Please give your consent to continue"});return;}
    setBusy(true);
    try{
      const at=new Date().toISOString();
      await updateDoc(doc(db,"candidate_verifications",id),{submission:out,consent:{given:true,at,version:CAND_CONSENT_VERSION,company:data.companyName||"",ua:(navigator.userAgent||"").slice(0,180)},submittedAt:at,status:"submitted"});
      const msg=`${data.candidateName} submitted details for ${defs.map(d=>d.label).join(", ")}. Review and run the checks.`;
      createNotification({companyId:data.companyId==="superadmin"?null:data.companyId,targetRole:"admin",targetUserId:data.initiatedBy,type:"candidate_submitted",title:"Candidate submitted verification details",message:msg,link:"candverify"});
      createNotification({companyId:null,targetRole:"superadmin",type:"candidate_submitted",title:"Candidate submitted verification details",message:`${msg} (${data.companyName||"Meitho"})`,link:"candverify"});
      setDone(true);
    }catch(err){setErrs({_c:"Could not submit. Please try again."});}
    setBusy(false);
  };

  const wrap={minHeight:"100vh",background:"#f4f6fb",display:"flex",justifyContent:"center",padding:"24px 14px",fontFamily:FF};
  const card={width:"100%",maxWidth:520,background:"#fff",borderRadius:16,boxShadow:"0 10px 40px rgba(15,23,42,0.08)",padding:"26px 22px",alignSelf:"flex-start"};
  const inp=(bad)=>({width:"100%",padding:"11px 13px",border:"1.5px solid "+(bad?"#ef4444":"#dbe1ea"),borderRadius:9,fontSize:15,fontFamily:FF,outline:"none",background:"#fff",color:"#0f172a"});
  const logo=<div style={{display:"flex",alignItems:"center",gap:10,marginBottom:18}}><img src={LOGO1} alt="Meitho" style={{height:34}}/></div>;

  if(loading)return <div style={wrap}><div style={card}>{logo}<div style={{color:"#64748b"}}>Loading…</div></div></div>;
  if(error)return <div style={wrap}><div style={card}>{logo}<div style={{fontSize:40,marginBottom:8}}>⚠️</div><div style={{fontWeight:700,fontSize:17,color:"#0f172a",marginBottom:6}}>Unable to open</div><div style={{color:"#64748b",fontSize:14}}>{error}</div></div></div>;
  if(done)return <div style={wrap}><div style={card}>{logo}<div style={{fontSize:44,marginBottom:8}}>✅</div><div style={{fontWeight:700,fontSize:18,color:"#0f172a",marginBottom:6}}>Thank you, {data?.candidateName}!</div><div style={{color:"#64748b",fontSize:14,lineHeight:1.6}}>Your details have been submitted securely to {data?.companyName||"the requester"}. You don't need to do anything else.</div></div></div>;

  return(
    <div style={wrap}><div style={card}>
      {logo}
      <div style={{fontWeight:800,fontSize:20,color:"#0f172a",marginBottom:4}}>Hi {data.candidateName}</div>
      <div style={{color:"#64748b",fontSize:14,lineHeight:1.6,marginBottom:20}}>{data.companyName||"Your employer"} has requested a background verification. Please fill in the details below.</div>
      {defs.map(def=>def.key==="education"?(
        <div key={def.key} style={{marginBottom:20}}>
          <div style={{fontWeight:700,fontSize:14,color:"#0f172a",marginBottom:6,paddingBottom:6,borderBottom:"2px solid #ffe3d6"}}>🎓 Education Details</div>
          <div style={{fontSize:12,color:"#64748b",marginBottom:12,lineHeight:1.5}}>Fill the details of each qualification and upload the certificate (PDF, JPG or PNG, max 5 MB each). Only the 10th certificate is required — add 12th, UG or PG only if you have them.</div>
          {CV_EDU_DOCS.map(d=>{const x=eduData[d.k]||{};const ek="c_"+d.k;const up=candDocs[d.k];return(
            <div key={d.k} style={{border:"1.5px solid #e2e8f0",borderRadius:12,padding:"14px 14px 6px",marginBottom:14,background:"#fafbfd"}}>
              <div style={{fontWeight:800,fontSize:13.5,color:"#0f172a",marginBottom:10}}>{d.short}{d.req?"":" (optional)"}</div>
              {CV_EDU_FIELDS.map(f=>{const fk="e_"+d.k+"_"+f.k;return(
                <div key={f.k} style={{marginBottom:11}}>
                  <label style={{display:"block",fontSize:12,fontWeight:600,color:"#475569",marginBottom:4}}>{f.label}{f.req&&d.req?" *":""}</label>
                  <input type="text" value={x[f.k]||""} onChange={e=>setEd(d.k,f.k,e.target.value)} placeholder={f.ph} style={inp(errs[fk])}/>
                  {errs[fk]&&<div style={{color:"#ef4444",fontSize:12,marginTop:3}}>{errs[fk]}</div>}
                </div>);})}
              <label style={{display:"block",fontSize:12,fontWeight:600,color:"#475569",marginBottom:4}}>Certificate{d.req?" *":""}</label>
              <div style={{marginBottom:11}}>
                {up?.url
                  ?<div style={{display:"flex",alignItems:"center",gap:8,fontSize:12.5,color:"#059669"}}>✓ {up.name}<button type="button" onClick={()=>setCandDocs(p=>({...p,[d.k]:undefined}))} style={{background:"none",border:"none",color:"#ef4444",cursor:"pointer",fontSize:12}}>Remove</button></div>
                  :<input type="file" accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png" disabled={!!upBusy[ek]} onChange={e=>{pickCandDoc(d,e.target.files?.[0]);e.target.value="";}} style={{fontSize:12.5,width:"100%"}}/>}
                {upBusy[ek]&&<div style={{fontSize:12,color:"#64748b",marginTop:3}}>Uploading…</div>}
                {errs[ek]&&<div style={{color:"#ef4444",fontSize:12,marginTop:3}}>{errs[ek]}</div>}
              </div>
            </div>);})}
        </div>
      ):def.key==="refcheck"?(
        <div key={def.key} style={{marginBottom:20}}>
          <div style={{fontWeight:700,fontSize:14,color:"#0f172a",marginBottom:6,paddingBottom:6,borderBottom:"2px solid #ffe3d6"}}>{def.icon} Employment &amp; Reference Details</div>
          <div style={{fontSize:12,color:"#64748b",marginBottom:12,lineHeight:1.5}}>Fill the details of {refCount===2?"your last two employers":"your previous employer"} and upload the documents (PDF, JPG or PNG, max 5 MB each). The person you name as reference will confirm these details.</div>
          {Array.from({length:refCount}).map((_,i)=>{const x=refs[i]||{docs:{}};return(
            <div key={i} style={{border:"1.5px solid #e2e8f0",borderRadius:12,padding:"14px 14px 6px",marginBottom:14,background:"#fafbfd"}}>
              <div style={{fontWeight:800,fontSize:13.5,color:"#0f172a",marginBottom:10}}>{refCount===2?`Employer ${i+1} · Reference ${i+1}`:"Previous employer · Reference"}</div>
              {CV_REF_FIELDS.map(f=>(
                <div key={f.k} style={{marginBottom:11}}>
                  <label style={{display:"block",fontSize:12,fontWeight:600,color:"#475569",marginBottom:4}}>{f.label}{f.req?" *":""}</label>
                  <input type={f.type||"text"} value={x[f.k]||""} onChange={e=>setRf(i,f.k,e.target.value)} placeholder={f.ph} style={inp(errs["r"+i+"_"+f.k])}/>
                  {errs["r"+i+"_"+f.k]&&<div style={{color:"#ef4444",fontSize:12,marginTop:3}}>{errs["r"+i+"_"+f.k]}</div>}
                </div>
              ))}
              <div style={{fontWeight:700,fontSize:12.5,color:"#0f172a",margin:"6px 0 8px"}}>Documents</div>
              {CV_REF_DOCS.map(d=>{const ek="r"+i+"_"+d.k;const up=x.docs?.[d.k];return(
                <div key={d.k} style={{marginBottom:11}}>
                  <label style={{display:"block",fontSize:12,fontWeight:600,color:"#475569",marginBottom:4}}>{d.label}{d.req?" *":" (optional)"}</label>
                  {up?.url
                    ?<div style={{display:"flex",alignItems:"center",gap:8,fontSize:12.5,color:"#059669"}}>✓ {up.name}<button type="button" onClick={()=>setRefs(p=>p.map((y,j)=>j===i?{...y,docs:{...y.docs,[d.k]:undefined}}:y))} style={{background:"none",border:"none",color:"#ef4444",cursor:"pointer",fontSize:12}}>Remove</button></div>
                    :<input type="file" accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png" disabled={!!upBusy[ek]} onChange={e=>{pickDoc(i,d,e.target.files?.[0]);e.target.value="";}} style={{fontSize:12.5,width:"100%"}}/>}
                  {upBusy[ek]&&<div style={{fontSize:12,color:"#64748b",marginTop:3}}>Uploading…</div>}
                  {errs[ek]&&<div style={{color:"#ef4444",fontSize:12,marginTop:3}}>{errs[ek]}</div>}
                </div>);})}
            </div>);})}
        </div>
      ):(
        <div key={def.key} style={{marginBottom:20}}>
          <div style={{fontWeight:700,fontSize:14,color:"#0f172a",marginBottom:10,paddingBottom:6,borderBottom:"2px solid #ffe3d6"}}>{def.icon} {def.label}</div>
          {def.fields.map(f=>f.type==="addr"?(
            <div key={f.k} style={{marginBottom:14}}>
              <label style={{display:"block",fontSize:12,fontWeight:600,color:"#475569",marginBottom:6}}>{f.label}{f.sameAs?"":" *"}</label>
              {f.sameAs&&<label style={{display:"flex",alignItems:"center",gap:8,fontSize:12.5,color:"#334155",marginBottom:8,cursor:"pointer"}}>
                <input type="checkbox" checked={(addr[f.k]||{}).same!==false} onChange={e=>setAddrPart(f.k,"same",e.target.checked)}/> Same as present address
              </label>}
              {!(f.sameAs&&(addr[f.k]||{}).same!==false)&&<div style={{display:"grid",gap:8}}>
                <input value={(addr[f.k]||{}).line1||""} onChange={e=>setAddrPart(f.k,"line1",e.target.value)} placeholder="Address line 1 (house / flat no, street)" style={inp(errs[f.k])}/>
                <input value={(addr[f.k]||{}).line2||""} onChange={e=>setAddrPart(f.k,"line2",e.target.value)} placeholder="Address line 2 (area, landmark) — optional" style={inp(false)}/>
                <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
                  <input value={(addr[f.k]||{}).city||""} onChange={e=>setAddrPart(f.k,"city",e.target.value)} placeholder="City / Town" style={inp(errs[f.k])}/>
                  <input value={(addr[f.k]||{}).pin||""} onChange={e=>setAddrPart(f.k,"pin",e.target.value.replace(/\D/g,"").slice(0,6))} placeholder="Pincode" inputMode="numeric" style={inp(errs[f.k])}/>
                </div>
                <select value={(addr[f.k]||{}).state||""} onChange={e=>setAddrPart(f.k,"state",e.target.value)} style={inp(errs[f.k])}>
                  <option value="">Select state</option>
                  {IN_STATES.map(st=><option key={st} value={st}>{st}</option>)}
                </select>
              </div>}
              {errs[f.k]&&<div style={{color:"#ef4444",fontSize:12,marginTop:3}}>{errs[f.k]}</div>}
            </div>
          ):(
            <div key={f.k} style={{marginBottom:12}}>
              <label style={{display:"block",fontSize:12,fontWeight:600,color:"#475569",marginBottom:4}}>{f.label}{f.optional?" (optional)":" *"}</label>
              {f.type==="textarea"
                ?<textarea rows={3} value={vals[f.k]||""} onChange={e=>setV(f.k,e.target.value)} placeholder={f.ph} style={{...inp(errs[f.k]),resize:"vertical"}}/>
                :<input type={f.type||"text"} value={vals[f.k]||""} onChange={e=>setV(f.k,e.target.value)} placeholder={f.ph} style={inp(errs[f.k])} inputMode={def.key==="uan"||def.key==="aadhaar"?"numeric":undefined}/>}
              {errs[f.k]&&<div style={{color:"#ef4444",fontSize:12,marginTop:3}}>{errs[f.k]}</div>}
            </div>
          ))}
        </div>
      ))}
      {[["🪪 Identity documents","Upload clear copies of your Aadhaar and PAN card (PDF, JPG or PNG, max 5 MB each).",CV_ID_DOCS]].map(([title,note,list])=>(
        <div key={title} style={{marginBottom:20}}>
          <div style={{fontWeight:700,fontSize:14,color:"#0f172a",marginBottom:6,paddingBottom:6,borderBottom:"2px solid #ffe3d6"}}>{title}</div>
          <div style={{fontSize:12,color:"#64748b",marginBottom:12,lineHeight:1.5}}>{note}</div>
          {list.map(d=>{const ek="c_"+d.k;const up=candDocs[d.k];return(
            <div key={d.k} style={{marginBottom:11}}>
              <label style={{display:"block",fontSize:12,fontWeight:600,color:"#475569",marginBottom:4}}>{d.label}{d.req?" *":" (optional)"}</label>
              {up?.url
                ?<div style={{display:"flex",alignItems:"center",gap:8,fontSize:12.5,color:"#059669"}}>✓ {up.name}<button type="button" onClick={()=>setCandDocs(p=>({...p,[d.k]:undefined}))} style={{background:"none",border:"none",color:"#ef4444",cursor:"pointer",fontSize:12}}>Remove</button></div>
                :<input type="file" accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png" disabled={!!upBusy[ek]} onChange={e=>{pickCandDoc(d,e.target.files?.[0]);e.target.value="";}} style={{fontSize:12.5,width:"100%"}}/>}
              {upBusy[ek]&&<div style={{fontSize:12,color:"#64748b",marginTop:3}}>Uploading…</div>}
              {errs[ek]&&<div style={{color:"#ef4444",fontSize:12,marginTop:3}}>{errs[ek]}</div>}
            </div>);})}
        </div>
      ))}
      <label style={{display:"flex",gap:10,alignItems:"flex-start",fontSize:12.5,color:"#475569",lineHeight:1.55,marginBottom:6,cursor:"pointer"}}>
        <input type="checkbox" checked={consent} onChange={e=>setConsent(e.target.checked)} style={{marginTop:3}}/>
        <span>I give my free and informed consent to {data.companyName||"the requesting company"} and Goport Delivery Private Limited (Meitho), acting on its behalf, to collect and verify the documents and details I have provided — including my Aadhaar (through DigiLocker), PAN, education, employment, address (with my selfie and location) and court or criminal records, as selected — only for this background verification, as described in the <a href="/privacy" target="_blank" rel="noreferrer" style={{color:"#0cc0df"}}>Privacy Policy</a>. I confirm the information is correct, and I understand I can withdraw my consent at any time by writing to verification@meitho.com.</span>
      </label>
      {errs._c&&<div style={{color:"#ef4444",fontSize:12,marginBottom:8}}>{errs._c}</div>}
      <button onClick={submit} disabled={busy} style={{width:"100%",marginTop:10,padding:"13px",border:"none",borderRadius:10,background:"linear-gradient(90deg,#ff3131,#ff914d)",color:"#fff",fontSize:15,fontWeight:700,cursor:busy?"default":"pointer",opacity:busy?.7:1,fontFamily:FF}}>{busy?"Submitting…":"Submit securely"}</button>
      <div style={{textAlign:"center",fontSize:11,color:"#94a3b8",marginTop:14}}>🔒 Secured by Meitho · help@meitho.com</div>
    </div></div>
  );
}

export default function App() {
  // Route BEFORE hooks using module-level constants
  if(IS_FEEDBACK) return <PanelFeedbackPage/>;
  if(IS_ATS_JOB) return <ATSJobDetail/>;
  if(IS_ATS_CAREER) return <ATSCareerPage/>;
  if(IS_EMPLOYEE_PORTAL) return <EmployeeApp/>;
  if(IS_PUBLIC_PROFILE) return <PublicProfile/>;
  if(IS_TRACK) return <CandidateTrackPage/>;
  if(IS_ADDRESS_VERIFY) return <AddressVerifyPage/>;
  if(IS_CAND_VERIFY) return <CandidateVerifyPublicPage/>;
  if(IS_DEMO) return <DemoPage/>;
  if(IS_DL_COMPLETE) return <div style={{minHeight:"100vh",display:"flex",alignItems:"center",justifyContent:"center",background:"#0b0b10",color:"#fff",fontFamily:"system-ui,sans-serif",padding:24,textAlign:"center"}}><div style={{maxWidth:420}}><div style={{fontSize:56,marginBottom:12}}>✅</div><h2 style={{margin:"0 0 10px"}}>Aadhaar verification submitted</h2><p style={{color:"rgba(255,255,255,0.65)",lineHeight:1.7,fontSize:15}}>Thank you. You have completed the DigiLocker steps. You can safely close this window — the verifying team has been notified and will receive your details shortly.</p></div></div>;
  if(IS_TERMS) return <TermsPage/>;
  if(IS_PRIVACY) return <PrivacyPage/>;
  return <MainApp/>;;
}

function MainApp() {
  const [dark, setDark] = useState(() => {
    try { return localStorage.getItem("bgv_theme") !== "light"; } catch { return false; }
  });
  const toggle = () => setDark(d => { try{localStorage.setItem("bgv_theme",!d?"dark":"light");}catch{}return !d; });

  // Public form pages - no Firebase needed
  if(IS_PUBLIC_FORM) {
    const path = window.location.pathname;
    const refMatch = path.match(/^\/refcheck\/([^/]+)\/(candidate|ref1|ref2)$/);
    if(refMatch) {
      const checkId = refMatch[1];
      const formType = refMatch[2];
      return(
        <PublicFormErrorBoundary>
          {formType==="candidate"
            ? <PublicCandidateForm checkId={checkId}/>
            : <PublicReferenceForm checkId={checkId} refNum={formType==="ref1"?1:2}/>
          }
        </PublicFormErrorBoundary>
      );
    }
  }

  return <ThemeCtx.Provider value={{dark,toggle}}><AppInner/></ThemeCtx.Provider>;
}
